// shortcut: monolithic root component contains all views and modals, extract into sub-views when moving to React Router v7 or when build latency exceeds 15s
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import * as apiClient from './services/apiClient';
import * as syncEngine from './services/syncEngine';
import { calculateSplits, applyVirtualDebtorBalancing } from './domain/splitCalculator';
import { simplifyDebts, calculateNetBalances, resolveGroupDebts } from './domain/debtSimplification';
import SuccessCheckmark from './components/SuccessCheckmark';
import EmptyState from './components/EmptyState';
import { SummaryCardSkeleton, TransactionListSkeleton, ChartSkeleton } from './components/SkeletonLoaders';
import TradingViewChart from './components/TradingViewChart';
import LiveMarketTicker from './components/LiveMarketTicker';
import AssetSearchModal from './components/AssetSearchModal';
import GroupDetailHub, { getCategoryBadge, getDateTokens } from './components/GroupDetailHub';
import GroupBalancesView from './components/GroupBalancesView';
import GroupChartsView from './components/GroupChartsView';
import GroupSettleModal from './components/GroupSettleModal';
import GroupTransactionDetailView from './components/GroupTransactionDetailView';
import CustomAccountSelect from './components/CustomAccountSelect';
import ThinkingOrbLoader from './components/ThinkingOrbLoader';
import MasterCardPicker from './components/MasterCardPicker';
import ExploreCardsModal from './components/ExploreCardsModal';
import CardRewardsAccordion from './components/CardRewardsAccordion';
import CardCommunityView from './components/CardCommunityView';
import ConnectCardModal from './components/ConnectCardModal';
import FriendBalanceChooserModal from './components/FriendBalanceChooserModal';
import {
  CreditCard,
  Landmark,
  Sparkles,
  Plus,
  Minus,
  Users,
  ArrowLeftRight,
  Settings,
  LogOut,
  RefreshCw,
  Wifi,
  WifiOff,
  Calendar,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Trash2,
  Download,
  Lock,
  ChevronLeft,
  Unlock,
  AlertTriangle,
  CheckCircle,
  FileText,
  UserPlus,
  Menu,
  X,
  PlusCircle,
  Edit2,
  Check,
  Upload,
  TrendingUp,
  Bell,
  Star,
  Eye,
  EyeOff,
  Key,
  Save,
  Scale,
  Activity
} from 'lucide-react';
import { trackEvent, identifyUser, resetUser, isTelemetryOptedIn, setTelemetryOptIn } from './services/analytics';


// One-time seamless migration: Copy legacy dhudd_* localStorage keys to tezer_*
if (typeof localStorage !== 'undefined') {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith('dhudd_')) {
        const newK = k.replace(/^dhudd_/, 'tezer_');
        if (localStorage.getItem(newK) === null) {
          localStorage.setItem(newK, localStorage.getItem(k));
        }
      }
    }
  } catch (e) {
    console.warn('[Storage Migration] Non-critical migration warning:', e);
  }
}

const COUNTRY_PRESETS = [
  { country: 'United Arab Emirates', flag: '🇦🇪', currency: 'AED', timezone: 'Asia/Dubai', defaultCurrencies: ['AED'] },
  { country: 'India', flag: '🇮🇳', currency: 'INR', timezone: 'Asia/Kolkata', defaultCurrencies: ['INR'] },
  { country: 'United States', flag: '🇺🇸', currency: 'USD', timezone: 'America/New_York', defaultCurrencies: ['USD'] },
  { country: 'United Kingdom', flag: '🇬🇧', currency: 'GBP', timezone: 'Europe/London', defaultCurrencies: ['GBP'] },
  { country: 'Saudi Arabia', flag: '🇸🇦', currency: 'SAR', timezone: 'Asia/Riyadh', defaultCurrencies: ['SAR'] },
  { country: 'Qatar', flag: '🇶🇦', currency: 'QAR', timezone: 'Asia/Qatar', defaultCurrencies: ['QAR'] },
  { country: 'Oman', flag: '🇴🇲', currency: 'OMR', timezone: 'Asia/Muscat', defaultCurrencies: ['OMR'] },
  { country: 'Kuwait', flag: '🇰🇼', currency: 'KWD', timezone: 'Asia/Kuwait', defaultCurrencies: ['KWD'] },
  { country: 'Bahrain', flag: '🇧🇭', currency: 'BHD', timezone: 'Asia/Bahrain', defaultCurrencies: ['BHD'] },
  { country: 'Singapore', flag: '🇸🇬', currency: 'SGD', timezone: 'Asia/Singapore', defaultCurrencies: ['SGD'] },
  { country: 'Canada', flag: '🇨🇦', currency: 'CAD', timezone: 'America/Toronto', defaultCurrencies: ['CAD'] },
  { country: 'Australia', flag: '🇦🇺', currency: 'AUD', timezone: 'Australia/Sydney', defaultCurrencies: ['AUD'] },
  { country: 'European Union', flag: '🇪🇺', currency: 'EUR', timezone: 'Europe/Paris', defaultCurrencies: ['EUR'] },
  { country: 'Pakistan', flag: '🇵🇰', currency: 'PKR', timezone: 'Asia/Karachi', defaultCurrencies: ['PKR'] },
  { country: 'Philippines', flag: '🇵🇭', currency: 'PHP', timezone: 'Asia/Manila', defaultCurrencies: ['PHP'] }
];

const CURRENCY_FLAGS = {
  AED: '🇦🇪',
  INR: '🇮🇳',
  USD: '🇺🇸',
  EUR: '🇪🇺',
  GBP: '🇬🇧',
  SAR: '🇸🇦',
  QAR: '🇶🇦',
  OMR: '🇴🇲',
  KWD: '🇰🇼',
  BHD: '🇧🇭',
  SGD: '🇸🇬',
  CAD: '🇨🇦',
  AUD: '🇦🇺',
  PKR: '🇵🇰',
  PHP: '🇵🇭'
};

const GLOBAL_INFLOW_CATEGORIES = [
  'Salary', 'Cashback', 'Income from investments', 'Other income',
  'Credit Card Rewards', 'Investment Income', 'External Borrowers',
  'Deposits', 'Crypto Profit', 'Income'
];

const escapeHtml = (str) =>
  String(str ?? '').replace(/[&<>"']/g, (m) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  }[m]));

const findCategoryByName = (cats, catName) => {
  if (!catName || !Array.isArray(cats)) return null;
  const target = catName.trim().toLowerCase();
  return cats.find(c => (c.name || '').trim().toLowerCase() === target) || null;
};

const renderInfoTooltip = (text) => (
  <span 
    title={text} 
    style={{
      cursor: 'pointer',
      fontSize: '10px',
      background: 'rgba(255, 255, 255, 0.08)',
      border: '1px solid var(--border-glass)',
      borderRadius: '50%',
      width: '14px',
      height: '14px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'var(--accent-cyan)',
      fontWeight: 'bold',
      marginLeft: '6px',
      verticalAlign: 'middle'
    }}
  >
    i
  </span>
);

/**
 * Formats friend display name for dropdowns and lists:
 * - Registered male (or unspecified): name 👦
 * - Registered female: name 👧
 * - Placeholder friend: name 👤
 */
const formatFriendLabel = (friend) => {
  if (!friend) return '';
  const isPlaceholder = !!(friend.is_placeholder === true || friend.is_placeholder === 1 || friend.is_placeholder === 'true');
  const baseName = (friend.nickname || friend.display_name || (friend.email ? friend.email.split('@')[0] : 'Friend')).trim();

  if (isPlaceholder) {
    return `${baseName} 👤`;
  }

  const g = (friend.gender || '').toLowerCase().trim();
  let emoji = '👦';
  if (g === 'female' || g === 'f') {
    emoji = '👧';
  } else if (g === 'male' || g === 'm') {
    emoji = '👦';
  }

  return `${baseName} ${emoji}`;
};

/**
 * Sorts friends array alphabetically (A-Z) by effective display name (nickname > display_name > email).
 */
const sortFriendsAZ = (friendsList) => {
  if (!Array.isArray(friendsList)) return [];
  return [...friendsList].sort((a, b) => {
    const nameA = (a.nickname || a.display_name || (a.email ? a.email.split('@')[0] : '')).toLowerCase().trim();
    const nameB = (b.nickname || b.display_name || (b.email ? b.email.split('@')[0] : '')).toLowerCase().trim();
    return nameA.localeCompare(nameB);
  });
};

// A generic custom dropdown multi-select
const MultiSelectDropdown = ({
  options,        // Array of { id, name }
  selectedIds,    // Array of selected ids
  onChange,       // (nextIds) => void
  placeholder = "Select...",
  renderExtra = null // optional function: (id) => ReactNode
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(opt =>
    opt.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const allFilteredSelected = filteredOptions.length > 0 && filteredOptions.every(opt => selectedIds.includes(opt.id));

  const handleSelectAll = () => {
    if (allFilteredSelected) {
      const filteredIds = filteredOptions.map(o => o.id);
      const next = selectedIds.filter(id => !filteredIds.includes(id));
      onChange(next);
    } else {
      const filteredIds = filteredOptions.map(o => o.id);
      const next = Array.from(new Set([...selectedIds, ...filteredIds]));
      onChange(next);
    }
  };

  return (
    <div ref={dropdownRef} className="multiselect-container" style={{ position: 'relative', width: '100%' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: 'var(--bg-input)',
          border: '1px solid var(--border-glass)',
          borderRadius: '12px',
          color: 'var(--text-main)',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '13px',
          transition: 'border-color 0.2s',
        }}
      >
        <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '85%' }}>
          {selectedIds.length === 0
            ? placeholder
            : `${selectedIds.length} selected (${options.filter(o => selectedIds.includes(o.id)).map(o => o.name).join(', ')})`
          }
        </span>
        <span style={{ fontSize: '10px', opacity: 0.6 }}>{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div
          className="glass-panel"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 9999,
            marginTop: '6px',
            padding: '12px',
            maxHeight: '260px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
            borderRadius: '16px',
            background: 'rgba(20, 22, 36, 0.95)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                padding: '8px 12px',
                fontSize: '12px',
                borderRadius: '8px',
                background: 'rgba(0,0,0,0.2)',
                border: '1px solid var(--border-glass)',
                flex: 1,
                margin: 0
              }}
            />
            <button
              type="button"
              onClick={handleSelectAll}
              style={{
                padding: '8px 12px',
                fontSize: '11px',
                borderRadius: '8px',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-glass)',
                color: 'var(--text-main)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                width: 'auto'
              }}
            >
              {allFilteredSelected ? 'Clear' : 'Select All'}
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', overflowY: 'auto', maxHeight: '160px', paddingRight: '4px' }}>
            {filteredOptions.length === 0 ? (
              <div style={{ padding: '8px', textAlign: 'center', fontSize: '11px', color: 'var(--text-muted)' }}>
                No matches found
              </div>
            ) : (
              filteredOptions.map(opt => {
                const isChecked = selectedIds.includes(opt.id);
                return (
                  <label
                    key={opt.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 8px',
                      borderRadius: '6px',
                      background: isChecked ? 'rgba(0, 245, 212, 0.05)' : 'transparent',
                      cursor: 'pointer',
                      margin: 0,
                      fontSize: '12px',
                      transition: 'background 0.2s',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          if (isChecked) {
                            onChange(selectedIds.filter(id => id !== opt.id));
                          } else {
                            onChange([...selectedIds, opt.id]);
                          }
                        }}
                        style={{ width: '14px', height: '14px', margin: 0, flexShrink: 0 }}
                      />
                      <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', color: 'var(--text-main)' }}>
                        {opt.name}
                      </span>
                    </div>
                    {renderExtra && (
                      <div style={{ flexShrink: 0, marginLeft: '8px' }}>
                        {renderExtra(opt.id)}
                      </div>
                    )}
                  </label>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function App() {
  // Desktop layout responsiveness
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 768);
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Authentication & PIN Lock
  const [isAuthenticated, setIsAuthenticated] = useState(!!apiClient.getToken());
  const [isLocked, setIsLocked] = useState(false);
  const [pin, setPin] = useState('');
  const [savedPin, setSavedPin] = useState('1234');
  const [pinInput, setPinInput] = useState('1234');
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [showRegistrationSuccess, setShowRegistrationSuccess] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [authName, setAuthName] = useState('');
  const [authDob, setAuthDob] = useState('');
  const [authGender, setAuthGender] = useState('');
  const [authCountry, setAuthCountry] = useState('United Arab Emirates');
  const [authCountryCode, setAuthCountryCode] = useState('+971');
  const [authCustomCc, setAuthCustomCc] = useState('');
  const [authPhoneDigits, setAuthPhoneDigits] = useState('');

  // Profile Avatar & Security States
  const [profileAvatarUrl, setProfileAvatarUrl] = useState(() => {
    return localStorage.getItem('tezer_avatar_url') || localStorage.getItem('dhudd_avatar_url') || null;
  });
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [unregisteredContacts, setUnregisteredContacts] = useState([]);

  // Forgot Password States (Login Screen)
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);
  const [forgotPwStep, setForgotPwStep] = useState('email'); // 'email' | 'code'
  const [forgotPwEmail, setForgotPwEmail] = useState('');
  const [forgotPwOtp, setForgotPwOtp] = useState('');
  const [forgotPwNewPassword, setForgotPwNewPassword] = useState('');
  const [forgotPwConfirmPassword, setForgotPwConfirmPassword] = useState('');
  const [forgotPwTurnstileToken, setForgotPwTurnstileToken] = useState(null);
  const [isSendingForgotPwOtp, setIsSendingForgotPwOtp] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [forgotPwCooldown, setForgotPwCooldown] = useState(0);

  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setInterval(() => setOtpCountdown(prev => prev - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [otpCountdown]);

  useEffect(() => {
    if (forgotPwCooldown > 0) {
      const timer = setInterval(() => setForgotPwCooldown(prev => prev - 1), 1000);
      return () => clearInterval(timer);
    }
  }, [forgotPwCooldown]);

  // Sync & Network status
  const [online, setOnline] = useState(apiClient.isOnline());
  const [queuedMutationsCount, setQueuedMutationsCount] = useState(0);

  // UI state
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard', 'accounts', 'credit_cards', 'groups', 'friends', 'transfers'
  const [lastBankOrCardTab, setLastBankOrCardTab] = useState('accounts');
  const [lastFriendOrGroupTab, setLastFriendOrGroupTab] = useState('friends');
  const [activePeriod, setActivePeriod] = useState('Month'); // 'Day', 'Week', 'Month', 'Year', 'All', 'Interval'
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [selectedAccountIds, setSelectedAccountIds] = useState(['ALL']);
  const [dashboardTxsLimit, setDashboardTxsLimit] = useState(10);
  const [selectedCategories, setSelectedCategories] = useState(['ALL']);
  const [accountsFilterOpen, setAccountsFilterOpen] = useState(false);
  const [categoriesFilterOpen, setCategoriesFilterOpen] = useState(false);
  const accountsDropdownRef = React.useRef(null);
  const categoriesDropdownRef = React.useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    if (!accountsFilterOpen && !categoriesFilterOpen) return;
    const handleClickOutside = (e) => {
      if (accountsFilterOpen && accountsDropdownRef.current && !accountsDropdownRef.current.contains(e.target)) {
        setAccountsFilterOpen(false);
      }
      if (categoriesFilterOpen && categoriesDropdownRef.current && !categoriesDropdownRef.current.contains(e.target)) {
        setCategoriesFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [accountsFilterOpen, categoriesFilterOpen]);

  // Keep last active bank or card tab in sync
  useEffect(() => {
    if (activeTab === 'accounts' || activeTab === 'credit_cards') {
      setLastBankOrCardTab(activeTab);
    }
  }, [activeTab]);

  const handleCardsAndBanksTabClick = () => {
    if (activeTab === 'accounts') {
      setActiveTab('credit_cards');
    } else if (activeTab === 'credit_cards') {
      setActiveTab('accounts');
    } else {
      setActiveTab(lastBankOrCardTab || 'accounts');
    }
  };

  // Keep last active friend or group tab in sync
  useEffect(() => {
    if (activeTab === 'friends' || activeTab === 'groups') {
      setLastFriendOrGroupTab(activeTab);
    }
  }, [activeTab]);

  const handleFriendsAndGroupsTabClick = () => {
    if (activeTab === 'friends') {
      setActiveTab('groups');
    } else if (activeTab === 'groups') {
      setActiveTab('friends');
    } else {
      setActiveTab(lastFriendOrGroupTab || 'friends');
    }
  };

  const [activeIncomeChartCategory, setActiveIncomeChartCategory] = useState(null);
  const [activeExpenseChartCategory, setActiveExpenseChartCategory] = useState(null);
  const [dashboardChartTab, setDashboardChartTab] = useState('expense'); // 'expense' or 'income'
  const [drillDownCategoryData, setDrillDownCategoryData] = useState(null); // { categoryName, type, total, icon, color, list }
  const [breakdownCurrency, setBreakdownCurrency] = useState('ALL');
  
  // Data lists
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [currencyRates, setCurrencyRates] = useState([]);
  const [categories, setCategories] = useState([]);
  const inflowCats = useMemo(() => categories.filter(c => c.type === 'INFLOW'), [categories]);
  const outflowCats = useMemo(() => categories.filter(c => c.type === 'OUTFLOW'), [categories]);
  const [friends, setFriends] = useState([]);
  const [preferredCurrencies, setPreferredCurrencies] = useState([]);
  const [newPrefCurrency, setNewPrefCurrency] = useState('USD');
  const allCurrenciesList = useMemo(() => {
    return Array.from(new Set([
      ...(preferredCurrencies && preferredCurrencies.length > 0 ? preferredCurrencies : []),
      ...COUNTRY_PRESETS.map(cp => cp.currency)
    ])).filter(Boolean);
  }, [preferredCurrencies]);
  
  // Modals / Sliders
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSheet, setActiveSheet] = useState(null); // 'categories', 'accounts_manage', 'cards_manage', 'currencies_manage', 'settings_manage'
  const [activeModal, setActiveModal] = useState(null); // 'outflow', 'inflow', 'transfer', 'account_create', 'group_create'
  const [alert, setAlert] = useState(null);
  const [successCheckmarkMsg, setSuccessCheckmarkMsg] = useState(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [adjustCcBalanceOpen, setAdjustCcBalanceOpen] = useState(false);
  const [newCcBalanceValue, setNewCcBalanceValue] = useState('');

  // Balance Privacy (Task 1)
  const [hideBalances, setHideBalances] = useState(() => localStorage.getItem('dhudd_hide_balances') === 'true');

  // Duplicate / Similar Transaction Warning Modal (Task 2)
  const [duplicateAlertModal, setDuplicateAlertModal] = useState({ isOpen: false, match: null, onConfirm: null });

  // Manage Accounts & Cards Draft States (Single Save/Discard)
  const [accountsDraft, setAccountsDraft] = useState({});
  const [isSavingAccounts, setIsSavingAccounts] = useState(false);
  const [cardsDraft, setCardsDraft] = useState({});
  const [isSavingCards, setIsSavingCards] = useState(false);

  // Master Credit Cards Catalog & Community States
  const [isExploreCardsOpen, setIsExploreCardsOpen] = useState(false);
  const [activeCommunityCard, setActiveCommunityCard] = useState(null);
  const [activeCatalogRewardsCard, setActiveCatalogRewardsCard] = useState(null);
  const [createCardMasterCardId, setCreateCardMasterCardId] = useState(null);
  const [activeCardSubTab, setActiveCardSubTab] = useState('overview'); // 'overview' | 'rewards'
  const [cardToConnect, setCardToConnect] = useState(null); // { account, targetFeature: 'community' | 'rewards' | 'manage' }

  const handleCardConnected = (updatedAccount, targetFeature) => {
    setAccounts(prev => prev.map(a => a.account_id === updatedAccount.account_id ? { ...a, ...updatedAccount } : a));
    if (activeAccount && activeAccount.account_id === updatedAccount.account_id) {
      setActiveAccount(prev => ({ ...prev, ...updatedAccount }));
    }
    setCardsDraft(prev => {
      const existing = prev[updatedAccount.account_id] || {};
      return {
        ...prev,
        [updatedAccount.account_id]: {
          ...existing,
          master_card_id: updatedAccount.master_card_id,
          master_card_name: updatedAccount.master_card_name,
          master_bank_name: updatedAccount.master_bank_name,
          master_card_tier: updatedAccount.master_card_tier
        }
      };
    });
    if (targetFeature === 'community') {
      setActiveCommunityCard({
        card_id: updatedAccount.master_card_id,
        card_name: updatedAccount.master_card_name || updatedAccount.name,
        bank_name: updatedAccount.master_bank_name || 'Bank',
        card_tier: updatedAccount.master_card_tier,
        is_cardholder: true
      });
    } else if (targetFeature === 'rewards') {
      setActiveCardSubTab('rewards');
    }
    refreshData();
  };

  // Dynamic Category Forms
  const [newCatName, setNewCatName] = useState('');
  const [newCatType, setNewCatType] = useState('OUTFLOW');
  const [newCatSymbol, setNewCatSymbol] = useState('📁');
  const [editingCategory, setEditingCategory] = useState(null);

  // Currency Matrix Form
  const [newRateFrom, setNewRateFrom] = useState('USD');
  const [newRateTo, setNewRateTo] = useState('AED');
  const [newRateValue, setNewRateValue] = useState('');
  const [newRateDate, setNewRateDate] = useState(new Date().toISOString().split('T')[0]);

  // 7+ Age Restriction Helper
  const getMaxDob7YearsAgo = () => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 7);
    return d.toISOString().split('T')[0];
  };

  const COUNTRY_CODES = [
    { code: '+971', label: '+971 (UAE)' },
    { code: '+966', label: '+966 (KSA)' },
    { code: '+965', label: '+965 (Kuwait)' },
    { code: '+974', label: '+974 (Qatar)' },
    { code: '+968', label: '+968 (Oman)' },
    { code: '+973', label: '+973 (Bahrain)' },
    { code: '+91', label: '+91 (India)' },
    { code: '+1', label: '+1 (US/CA)' },
    { code: '+44', label: '+44 (UK)' },
    { code: '+92', label: '+92 (Pakistan)' },
    { code: '+880', label: '+880 (Bangladesh)' },
    { code: '+62', label: '+62 (Indonesia)' },
    { code: '+63', label: '+63 (Philippines)' },
    { code: '+20', label: '+20 (Egypt)' },
    { code: 'CUSTOM', label: 'Other Code' }
  ];

  const formatPhoneWithCountryCode = (cc, customCc, digits) => {
    const cleanDigits = (digits || '').replace(/\D/g, '');
    if (!cleanDigits) return '';
    const selectedCc = cc === 'CUSTOM' ? (customCc || '').trim() : cc;
    if (!selectedCc) return cleanDigits;
    const formattedCc = selectedCc.startsWith('+') ? selectedCc : `+${selectedCc}`;
    return `${formattedCc}${cleanDigits}`;
  };

  const parsePhoneIntoCcAndDigits = (fullPhone = '') => {
    if (!fullPhone) return { countryCode: '+971', customCc: '', digits: '' };
    const str = fullPhone.trim();
    const matchedCc = COUNTRY_CODES.find(c => c.code !== 'CUSTOM' && str.startsWith(c.code));
    if (matchedCc) {
      return { countryCode: matchedCc.code, customCc: '', digits: str.slice(matchedCc.code.length).replace(/\D/g, '') };
    }
    if (str.startsWith('+')) {
      const match = str.match(/^\+(\d{1,4})(.*)$/);
      if (match) {
        return { countryCode: 'CUSTOM', customCc: `+${match[1]}`, digits: match[2].replace(/\D/g, '') };
      }
    }
    return { countryCode: '+971', customCc: '', digits: str.replace(/\D/g, '') };
  };

  // Friends Form
  const [newFriendEmail, setNewFriendEmail] = useState('');
  const [addFriendMethod, setAddFriendMethod] = useState('EMAIL'); // 'EMAIL' | 'PHONE'
  const [newFriendCountryCode, setNewFriendCountryCode] = useState('+971');
  const [customCountryCode, setCustomCountryCode] = useState('');
  const [newFriendPhone, setNewFriendPhone] = useState('');

  // Contact Sync & Friend Scanner States
  const [contactScanModalOpen, setContactScanModalOpen] = useState(false);
  const [isScanningContacts, setIsScanningContacts] = useState(false);
  const [contactScanResults, setContactScanResults] = useState([]);
  const [selectedContactUserIds, setSelectedContactUserIds] = useState([]);
  const [contactManualInput, setContactManualInput] = useState('');
  const [isBulkAddingFriends, setIsBulkAddingFriends] = useState(false);

  // Friends Controls (Search, Filter, Sort)
  const [friendSearchQuery, setFriendSearchQuery] = useState('');
  const [friendFilterType, setFriendFilterType] = useState('ALL');
  const [friendSortOrder, setFriendSortOrder] = useState('NAME_ASC');
  const [friendSortCurrency, setFriendSortCurrency] = useState('ALL');

  // User Profile Auth Provider & Password Settings States
  const [userAuthProvider, setUserAuthProvider] = useState(localStorage.getItem('dhudd_auth_provider') || '');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);
  const [pwResetOtpSent, setPwResetOtpSent] = useState(false);
  const [pwResetOtpCode, setPwResetOtpCode] = useState('');
  const [pwResetTurnstileToken, setPwResetTurnstileToken] = useState(null);
  const [isSendingPwResetOtp, setIsSendingPwResetOtp] = useState(false);
  const [pwResetOtpCooldown, setPwResetOtpCooldown] = useState(0);

  // Delete Friend modal states
  const [deleteFriendModalOpen, setDeleteFriendModalOpen] = useState(false);
  const [deletingFriend, setDeletingFriend] = useState(null);
  const [deleteAssociatedTransactionsCheck, setDeleteAssociatedTransactionsCheck] = useState(false);

  // Edit Nickname modal states
  const [editNicknameModalOpen, setEditNicknameModalOpen] = useState(false);
  const [nicknameEditingFriend, setNicknameEditingFriend] = useState(null);
  const [friendNicknameInput, setFriendNicknameInput] = useState('');

  // Add Account form states
  const [newAccName, setNewAccName] = useState('');
  const [newAccType, setNewAccType] = useState('BANK');
  const [newAccCurrency, setNewAccCurrency] = useState('AED');
  const [newAccBalance, setNewAccBalance] = useState('');
  const [newAccIncludeInTotal, setNewAccIncludeInTotal] = useState(true);

  // Add Card form states
  const [newCardName, setNewCardName] = useState('');
  const [newCardLimit, setNewCardLimit] = useState('');
  const [newCardCurrency, setNewCardCurrency] = useState('AED');
  const [newCardClosing, setNewCardClosing] = useState('15');
  const [newCardDue, setNewCardDue] = useState('25');
  const [newCardBalance, setNewCardBalance] = useState('');
  const [newCardSpendTarget, setNewCardSpendTarget] = useState('');
  const [newCardSpendTargetType, setNewCardSpendTargetType] = useState('CALENDAR');
  const [newCardIncludeInTotal, setNewCardIncludeInTotal] = useState(true);
  const [isAddingCard, setIsAddingCard] = useState(false);
  const [isAddingAccountDirect, setIsAddingAccountDirect] = useState(false);

  useEffect(() => {
    setIsAddingCard(false);
    setIsAddingAccountDirect(false);
  }, [activeSheet]);

  // Transaction Entry Form
  const [txDesc, setTxDesc] = useState('');
  const [txAmount, setTxAmount] = useState('');
  const [txCurrency, setTxCurrency] = useState('AED');
  const [txAccountId, setTxAccountId] = useState('');
  const [txCategory, setTxCategory] = useState('Food');
  const [showInlineCatForm, setShowInlineCatForm] = useState(false);
  const [inlineCatName, setInlineCatName] = useState('');
  const [inlineCatSymbol, setInlineCatSymbol] = useState('');
  const [txDate, setTxDate] = useState(new Date().toISOString().split('T')[0]);
  const [txIsShared, setTxIsShared] = useState(false);
  const [txGroupId, setTxGroupId] = useState('');
  const [txIsRecurring, setTxIsRecurring] = useState(false);
  const [txRecurringPeriod, setTxRecurringPeriod] = useState('MONTH');
  const [showMoreTxOptions, setShowMoreTxOptions] = useState(false);
  const [txCharges, setTxCharges] = useState('');
  
  // Split selection details
  const [splitType, setSplitType] = useState('EQUALLY');
  const [exactAmounts, setExactAmounts] = useState({});
  const [percentages, setPercentages] = useState({});
  const [shares, setShares] = useState({});

  // Group Details Modal
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [groupSimplifications, setGroupSimplifications] = useState([]);
  const [groupBalances, setGroupBalances] = useState({});
  const [groupBalancesByCurrency, setGroupBalancesByCurrency] = useState(null);
  const [groupViewSubTab, setGroupViewSubTab] = useState('hub'); // 'hub', 'balances', 'charts'
  const [groupMemberBreakdown, setGroupMemberBreakdown] = useState([]);
  const [groupRepaymentsSaved, setGroupRepaymentsSaved] = useState(0);
  const [groupSettlePrefill, setGroupSettlePrefill] = useState(null);
  const [isGroupSettleModalOpen, setIsGroupSettleModalOpen] = useState(false);
  const [isGroupBalancesLoading, setIsGroupBalancesLoading] = useState(false);

  const activeGroupRef = useRef(activeGroup);
  useEffect(() => {
    activeGroupRef.current = activeGroup;
  }, [activeGroup]);

  const reloadInProgressRef = useRef(false);
  const deletingTxIdsRef = useRef(new Set());
  const reloadActiveGroupBalances = useCallback(async (targetGroupId) => {
    const gid = targetGroupId || activeGroupRef.current?.group_id;
    if (!gid) return;
    if (reloadInProgressRef.current) return;
    reloadInProgressRef.current = true;
    setIsGroupBalancesLoading(true);
    try {
      const [membersRes, simRes] = await Promise.allSettled([
        apiClient.request(`/groups/${gid}/members`),
        apiClient.request(`/groups/${gid}/simplify`)
      ]);
      if (membersRes.status === 'fulfilled' && Array.isArray(membersRes.value)) {
        setGroupMembers(membersRes.value);
      }
      if (simRes.status === 'fulfilled' && simRes.value) {
        setGroupBalances(simRes.value.balances || {});
        setGroupBalancesByCurrency(simRes.value.by_currency || null);
        setGroupSimplifications(simRes.value.transfers || []);
        setGroupMemberBreakdown(simRes.value.member_breakdown || []);
        setGroupRepaymentsSaved(simRes.value.repayments_saved || 0);
        if (membersRes.status !== 'fulfilled' && simRes.value.members && Array.isArray(simRes.value.members)) {
          setGroupMembers(simRes.value.members);
        }
      }
    } catch (err) {
      console.warn('Could not reload group balances:', err);
    } finally {
      reloadInProgressRef.current = false;
      setIsGroupBalancesLoading(false);
    }
  }, []);

  // Group creation & editing states
  const [groupCreateName, setGroupCreateName] = useState('');
  const [groupCreateType, setGroupCreateType] = useState('TRIP');
  const [groupCreateSimplifyDebts, setGroupCreateSimplifyDebts] = useState(true);
  const [groupCreateSelectedFriends, setGroupCreateSelectedFriends] = useState([]);
  
  const [groupEditName, setGroupEditName] = useState('');
  const [groupEditType, setGroupEditType] = useState('TRIP');
  const [groupEditSimplifyDebts, setGroupEditSimplifyDebts] = useState(true);

  const [selectedFriendIdToAdd, setSelectedFriendIdToAdd] = useState('');
  const [showSimplifyLearnMoreModal, setShowSimplifyLearnMoreModal] = useState(false);

  // Group Opening Balances state
  const [isGroupOpeningBalancesModalOpen, setIsGroupOpeningBalancesModalOpen] = useState(false);
  const [groupOpeningBalancePairs, setGroupOpeningBalancePairs] = useState([]);
  const [loadingOpeningBalances, setLoadingOpeningBalances] = useState(false);
  const [savingOpeningBalances, setSavingOpeningBalances] = useState(false);
  const [openingBalanceError, setOpeningBalanceError] = useState('');

  // Ad-hoc Splits & Custom selections
  const [splitTargetType, setSplitTargetType] = useState('GROUP'); // 'GROUP' or 'FRIENDS'
  const [splitFriendsList, setSplitFriendsList] = useState([]);
  const [splitGroupMembersList, setSplitGroupMembersList] = useState([]);
  const [txPayerUserId, setTxPayerUserId] = useState('self');
  const [txPayerType, setTxPayerType] = useState('you'); // 'you', 'friend', 'both'
  const [txMultiPayers, setTxMultiPayers] = useState({}); // { [userId]: amount }
  const [activeFriend, setActiveFriend] = useState(null);
  const [selectedTxDetail, setSelectedTxDetail] = useState(null);
  const [selectedGroupTx, setSelectedGroupTx] = useState(null);
  const [selectedMemberForRole, setSelectedMemberForRole] = useState(null);

  const [friendSettleOpen, setFriendSettleOpen] = useState(false);
  const [friendBalanceChooserOpen, setFriendBalanceChooserOpen] = useState(false);
  const [friendBalanceChooserData, setFriendBalanceChooserData] = useState(null);
  const [friendBalanceChooserCurrency, setFriendBalanceChooserCurrency] = useState('AED');
  const [friendSettleLoadingCur, setFriendSettleLoadingCur] = useState(null);
  const [friendSettleGroupId, setFriendSettleGroupId] = useState(null);
  const [friendSettleGroupName, setFriendSettleGroupName] = useState('');
  const [friendSettleIsAll, setFriendSettleIsAll] = useState(false);
  const [friendSettleCurrency, setFriendSettleCurrency] = useState('AED');
  const [friendSettleAmount, setFriendSettleAmount] = useState('');
  const [friendSettleAccountId, setFriendSettleAccountId] = useState('');
  const [friendSettleDirection, setFriendSettleDirection] = useState('pay'); // 'pay' or 'receive'
  const [friendSettleDate, setFriendSettleDate] = useState(new Date().toISOString().split('T')[0]);

  const [friendAdjustOpen, setFriendAdjustOpen] = useState(false);
  const [friendAdjustCurrency, setFriendAdjustCurrency] = useState('AED');
  const [friendAdjustAmount, setFriendAdjustAmount] = useState('');
  const [friendMergeOpen, setFriendMergeOpen] = useState(false);
  const [friendMergeEmail, setFriendMergeEmail] = useState('');

  // Notifications State
  const [notifications, setNotifications] = useState([]);

  const triggerPushNotification = (title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const cleanBody = body ? body.replace(/<[^>]+>/g, '') : '';
        if (navigator.serviceWorker && navigator.serviceWorker.controller) {
          navigator.serviceWorker.ready.then(reg => {
            reg.showNotification(title, {
              body: cleanBody,
              icon: '/pwa-192x192.png',
              badge: '/favicon.png',
              vibrate: [100, 50, 100]
            });
          });
        } else {
          new Notification(title, {
            body: cleanBody,
            icon: '/pwa-192x192.png',
            badge: '/favicon.png'
          });
        }
      } catch (e) {
        console.warn('Push notification error:', e);
      }
    }
  };

  // Daily Expense Reminder States & Financial Quotes
  const [dailyReminderEnabled, setDailyReminderEnabled] = useState(() => {
    const cached = localStorage.getItem('dhudd_daily_reminder_enabled');
    return cached !== null ? cached === 'true' : ('Notification' in window && Notification.permission === 'granted');
  });
  const [dailyReminderTime, setDailyReminderTime] = useState(() => {
    return localStorage.getItem('dhudd_daily_reminder_time') || '21:00';
  });
  const [friendSettingsMenuOpen, setFriendSettingsMenuOpen] = useState(false);

  const FINANCIAL_REMINDER_QUOTES = [
    { text: "Beware of little expenses; a small leak will sink a great ship.", author: "Benjamin Franklin" },
    { text: "A budget is telling your money where to go instead of wondering where it went.", author: "John C. Maxwell" },
    { text: "Do not save what is left after spending, but spend what is left after saving.", author: "Warren Buffett" },
    { text: "Financial peace isn't the acquisition of stuff. It's learning to live on less than you make.", author: "Dave Ramsey" },
    { text: "The art is not in making money, but in keeping it.", author: "Proverb" },
    { text: "Every dollar you track is a step closer to financial freedom.", author: "tezer Wisdom" },
    { text: "Small daily disciplines lead to massive financial freedom over time. Track your day!", author: "tezer Wisdom" },
    { text: "A penny saved is a penny earned. Keep your streak alive by logging today's spending.", author: "Benjamin Franklin" },
    { text: "Control your money, or it will control you. Take 30 seconds to log today's expenses.", author: "Dave Ramsey" },
    { text: "Wealth is the ability to fully experience life. Stay on track with your goals!", author: "Henry David Thoreau" },
    { text: "Spend wisely, save systematically, invest smartly. Have you logged your expenses today?", author: "tezer Wisdom" }
  ];

  const handleSaveReminderSettings = async (enabled, time) => {
    try {
      setDailyReminderEnabled(enabled);
      setDailyReminderTime(time);
      localStorage.setItem('dhudd_daily_reminder_enabled', String(enabled));
      localStorage.setItem('dhudd_daily_reminder_time', time);

      if (apiClient.isOnline() && apiClient.getToken()) {
        const userTz = selectedTimezone === 'local' || !selectedTimezone
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : selectedTimezone;
        await apiClient.request('/users/reminder-settings', {
          method: 'PUT',
          body: JSON.stringify({
            daily_reminder_enabled: enabled,
            daily_reminder_time: time,
            daily_reminder_timezone: userTz
          })
        });
      }
    } catch (e) {
      console.warn('Error saving daily reminder settings:', e);
    }
  };

  // Group Settle Up Modal States
  const [groupSettleOpen, setGroupSettleOpen] = useState(false);
  const [groupSettleTransfer, setGroupSettleTransfer] = useState(null);
  const [groupSettleAccountId, setGroupSettleAccountId] = useState('');

  // Credit Card Payment States
  const [payCcModalOpen, setPayCcModalOpen] = useState(false);
  const [payCcCard, setPayCcCard] = useState(null);
  const [payCcAmount, setPayCcAmount] = useState('');
  const [payCcSourceAccountId, setPayCcSourceAccountId] = useState('');
  const [payCcSourceType, setPayCcSourceType] = useState('ACCOUNT'); // 'ACCOUNT' or 'FRIEND'
  const [payCcSourceFriendId, setPayCcSourceFriendId] = useState('');
  const [payCcDate, setPayCcDate] = useState(new Date().toISOString().split('T')[0]);

  // Selected Account details & pagination
  const [activeAccount, setActiveAccount] = useState(null);
  const [activeItemTxLimit, setActiveItemTxLimit] = useState(10);

  // Pending Actions State
  const [pendingActions, setPendingActions] = useState([]);
  const [actionSelections, setActionSelections] = useState({});
  const [pendingActionsAllDone, setPendingActionsAllDone] = useState(false);
  const [resolvingAction, setResolvingAction] = useState(null); // { id: string, op: 'assign' | 'reject' | 'clear' }
  const pendingActionsTrackRef = useRef(null);
  const [activePendingActionIndex, setActivePendingActionIndex] = useState(0);

  const scrollPendingActions = (direction) => {
    if (pendingActionsTrackRef.current) {
      const track = pendingActionsTrackRef.current;
      const cardWidth = track.clientWidth > 360 ? 340 : (track.clientWidth * 0.86);
      track.scrollBy({ left: direction === 'left' ? -cardWidth : cardWidth, behavior: 'smooth' });
    }
  };

  // Custom Transaction Export States
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportTargetType, setExportTargetType] = useState('account'); // 'account' | 'credit_card' | 'friend' | 'group'
  const [exportTargetData, setExportTargetData] = useState(null);
  const [exportFormat, setExportFormat] = useState('pdf'); // 'pdf' | 'excel'
  const [exportPeriod, setExportPeriod] = useState('all'); // 'all' | 'this_month' | 'last_30' | 'custom'
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportLimitType, setExportLimitType] = useState('all'); // 'all' | '10' | '20' | '50' | 'custom'
  const [exportLimitValue, setExportLimitValue] = useState('');

  // PWA Installation States & Handlers
  const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(() => {
    return window.matchMedia('(display-mode: standalone)').matches ||
           window.navigator.standalone === true ||
           document.referrer.includes('android-app://');
  });
  const [showIosInstallModal, setShowIosInstallModal] = useState(false);
  const [showInstallAppPopup, setShowInstallAppPopup] = useState(false);

  useEffect(() => {
    const reportPwaStatus = (isInstalled, displayMode) => {
      try {
        if (apiClient.getToken()) {
          apiClient.post('/users/pwa-status', {
            is_installed: isInstalled,
            display_mode: displayMode
          }).catch(() => {});
        }
      } catch (err) {}
    };

    const checkStandalone = () => {
      const standaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
                             window.navigator.standalone === true ||
                             document.referrer.includes('android-app://');
      setIsStandalone(standaloneMode);
      if (standaloneMode) {
        reportPwaStatus(true, 'standalone');
      } else if (typeof navigator !== 'undefined' && 'getInstalledRelatedApps' in navigator) {
        navigator.getInstalledRelatedApps().then(apps => {
          if (Array.isArray(apps) && apps.length > 0) {
            reportPwaStatus(true, 'browser-installed');
          }
        }).catch(() => {});
      }
    };

    checkStandalone();
    window.addEventListener('resize', checkStandalone);

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredInstallPrompt(e);
    };

    const handleAppInstalled = () => {
      setDeferredInstallPrompt(null);
      setIsStandalone(true);
      setShowInstallAppPopup(false);
      reportPwaStatus(true, 'standalone');
      showToast('Tezer app installed successfully!', 'success');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('resize', checkStandalone);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
    try {
      const dismissedAt = localStorage.getItem('tezer_install_popup_dismissed_at');
      const isRecentlyDismissed = dismissedAt && (Date.now() - parseInt(dismissedAt, 10) < DISMISS_COOLDOWN_MS);

      if (!isStandalone && !isRecentlyDismissed) {
        const timer = setTimeout(() => {
          setShowInstallAppPopup(true);
        }, 2500);
        return () => clearTimeout(timer);
      }
    } catch (e) {}
  }, [isStandalone]);

  const handleDismissInstallPopup = () => {
    setShowInstallAppPopup(false);
    try {
      localStorage.setItem('tezer_install_popup_dismissed_at', Date.now().toString());
    } catch (e) {}
  };

  const handleInstallFromPopup = async () => {
    handleDismissInstallPopup();
    await handleInstallPwa();
  };

  const handleInstallPwa = async () => {
    if (isStandalone) {
      showToast('Tezer is already running as an installed app!', 'info');
      return;
    }

    if (deferredInstallPrompt) {
      try {
        deferredInstallPrompt.prompt();
        const { outcome } = await deferredInstallPrompt.userChoice;
        if (outcome === 'accepted') {
          showToast('Installing tezer...', 'info');
        }
        setDeferredInstallPrompt(null);
      } catch (err) {
        console.error('PWA prompt error:', err);
        setShowIosInstallModal(true);
      }
    } else {
      setShowIosInstallModal(true);
    }
  };

  // First Transaction Push Notification Prompt State
  const [showPushPromptModal, setShowPushPromptModal] = useState(false);

  const checkAndPromptPushNotifications = () => {
    if ('Notification' in window && Notification.permission !== 'granted') {
      const dismissed = (localStorage.getItem('tezer_push_prompt_dismissed') || localStorage.getItem('dhudd_push_prompt_dismissed')) === 'true';
      if (!dismissed) {
        setTimeout(() => {
          setShowPushPromptModal(true);
        }, 650);
      }
    }
  };

  const handleEnablePushFromPrompt = async () => {
    localStorage.setItem('tezer_push_prompt_dismissed', 'true');
    localStorage.setItem('dhudd_push_prompt_dismissed', 'true');
    setShowPushPromptModal(false);
    if ('Notification' in window) {
      try {
        const perm = await Notification.requestPermission();
        if (perm === 'granted') {
          showToast('Push Notifications Enabled!', 'success');
          triggerPushNotification('🔔 tezer Push Active', 'You will now receive notifications for transactions, settlements, and bill reminders!');
          handleSaveReminderSettings(true, dailyReminderTime || '21:00');
        } else {
          showToast('Notifications declined. You can enable them anytime in Settings.', 'info');
        }
      } catch (err) {
        console.error('Error requesting notification permission:', err);
      }
    }
  };

  const handleDismissPushPrompt = () => {
    localStorage.setItem('tezer_push_prompt_dismissed', 'true');
    localStorage.setItem('dhudd_push_prompt_dismissed', 'true');
    setShowPushPromptModal(false);
  };

  // Transaction Edit States
  const [editingTx, setEditingTx] = useState(null);
  const [editTxDesc, setEditTxDesc] = useState('');
  const [descSuggestions, setDescSuggestions] = useState([]);
  const [editTxAmount, setEditTxAmount] = useState('');
  const [editTxCurrency, setEditTxCurrency] = useState('');
  const [editTxAccountId, setEditTxAccountId] = useState('');
  const [editTxTransferToAccountId, setEditTxTransferToAccountId] = useState('');
  const [editTxCategory, setEditTxCategory] = useState('');
  const [editTxDate, setEditTxDate] = useState('');
  const [editTxIsShared, setEditTxIsShared] = useState(false);
  const [editTxPayerUserId, setEditTxPayerUserId] = useState('self');
  const [editTxPayerType, setEditTxPayerType] = useState('you');
  const [editTxMultiPayers, setEditTxMultiPayers] = useState({});
  const [editTxSplitTargetType, setEditTxSplitTargetType] = useState('GROUP');
  const [editTxGroupId, setEditTxGroupId] = useState('');
  const [editTxSplitGroupMembers, setEditTxSplitGroupMembers] = useState([]);
  const [editTxSplitFriends, setEditTxSplitFriends] = useState([]);
  const [editTxSplitType, setEditTxSplitType] = useState('EQUALLY');
  const [editTxExactAmounts, setEditTxExactAmounts] = useState({});
  const [editTxPercentages, setEditTxPercentages] = useState({});
  const [editTxShares, setEditTxShares] = useState({});
  const [editTxIsRecurring, setEditTxIsRecurring] = useState(false);
  const [editTxRecurringPeriod, setEditTxRecurringPeriod] = useState('MONTH');
  const [editTxUseSplitSources, setEditTxUseSplitSources] = useState(false);
  const [editSplitTxSources, setEditSplitTxSources] = useState([{ accountId: '', amount: '' }]);
  const [editTxCharges, setEditTxCharges] = useState('');
  const [editShowMoreTxOptions, setEditShowMoreTxOptions] = useState(false);
  const [selectedTimezone, setSelectedTimezone] = useState(localStorage.getItem('dhudd_timezone') || 'local');

  // WhatsApp Integration States
  const [waPhone, setWaPhone] = useState('');
  const [waNotifyMonthlySummary, setWaNotifyMonthlySummary] = useState(true);
  const [waNotifySplitExpenses, setWaNotifySplitExpenses] = useState(true);
  const [waNotifyDebtReminders, setWaNotifyDebtReminders] = useState(true);
  const [waBotEnabled, setWaBotEnabled] = useState(true);
  const [waQueueList, setWaQueueList] = useState([]);
  const [waRedirectModal, setWaRedirectModal] = useState({ isOpen: false, redirectReason: '', waMeUrl: '', friendName: '' });
  const [waChoiceModal, setWaChoiceModal] = useState({ isOpen: false, friend: null, amount: 0, currency: 'AED' });

  const fetchWhatsAppSettings = async () => {
    try {
      if (apiClient.isOnline() && apiClient.getToken()) {
        const [res, qRes] = await Promise.all([
          apiClient.request('/users/whatsapp-settings').catch(() => null),
          apiClient.request('/whatsapp/queue').catch(() => [])
        ]);
        if (res) {
          setWaPhone(res.whatsapp_phone || '');
          setWaNotifyMonthlySummary(res.notify_monthly_summary ?? true);
          setWaNotifySplitExpenses(res.notify_split_expenses ?? true);
          setWaNotifyDebtReminders(res.notify_debt_reminders ?? true);
          setWaBotEnabled(res.bot_enabled !== false);
        }
        setWaQueueList(Array.isArray(qRes) ? qRes : []);
      }
    } catch (e) {
      console.error('Failed fetching WhatsApp settings:', e);
    }
  };

  const handleSaveWhatsAppSettings = async () => {
    try {
      const res = await apiClient.request('/users/whatsapp-settings', 'PUT', {
        whatsapp_phone: waPhone,
        notify_monthly_summary: waNotifyMonthlySummary,
        notify_split_expenses: waNotifySplitExpenses,
        notify_debt_reminders: waNotifyDebtReminders
      });
      if (res && res.success) {
        showToast('WhatsApp preferences saved!', 'success');
      }
    } catch (err) {
      showToast(err.message || 'Failed saving WhatsApp preferences', 'error');
    }
  };

  // Account Preferences State (Default accounts for Transfers & Expenses per currency)
  const [accountPreferences, setAccountPreferences] = useState({}); // { [currency]: { default_transfer_account_id, default_expense_account_id } }

  const fetchAccountPreferences = async () => {
    try {
      if (apiClient.isOnline() && apiClient.getToken()) {
        const rows = await apiClient.request('/user/account-preferences');
        if (Array.isArray(rows)) {
          const prefMap = {};
          rows.forEach(r => {
            if (r.currency) {
              prefMap[r.currency.toUpperCase().trim()] = {
                default_transfer_account_id: r.default_transfer_account_id || '',
                default_expense_account_id: r.default_expense_account_id || ''
              };
            }
          });
          setAccountPreferences(prefMap);
        }
      }
    } catch (e) {
      console.error('Failed to fetch account preferences:', e);
    }
  };

  const handleUpdateAccountPreference = async (currency, field, accountId) => {
    const val = accountId || null;
    const currNorm = (currency || 'AED').toUpperCase().trim();
    
    setAccountPreferences(prev => ({
      ...prev,
      [currNorm]: {
        ...prev[currNorm],
        [field]: val || ''
      }
    }));

    try {
      const activeCurrencies = (preferredCurrencies && preferredCurrencies.length > 0)
        ? preferredCurrencies
        : Array.from(new Set(accounts.filter(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0').map(a => a.currency).filter(Boolean)));

      const payload = activeCurrencies.map(c => {
        const cNorm = c.toUpperCase().trim();
        const existing = (accountPreferences && accountPreferences[cNorm]) || {};
        return {
          currency: cNorm,
          default_transfer_account_id: (cNorm === currNorm && field === 'default_transfer_account_id')
            ? val
            : (existing.default_transfer_account_id || null),
          default_expense_account_id: (cNorm === currNorm && field === 'default_expense_account_id')
            ? val
            : (existing.default_expense_account_id || null)
        };
      });

      await apiClient.request('/user/account-preferences', 'PUT', { preferences: payload });
      showToast(`Default ${field === 'default_transfer_account_id' ? 'transfer' : 'expense'} account for ${currency} saved!`, 'success');
    } catch (err) {
      console.error('Failed to auto-save account preference:', err);
      showToast('Failed to save preference: ' + (err.message || 'Error'), 'error');
    }
  };

  const handleSaveAccountPreferences = async () => {
    try {
      const activeCurrencies = (preferredCurrencies && preferredCurrencies.length > 0)
        ? preferredCurrencies
        : Array.from(new Set(accounts.filter(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0').map(a => a.currency).filter(Boolean)));

      const payload = activeCurrencies.map(curr => ({
        currency: curr,
        default_transfer_account_id: accountPreferences[curr]?.default_transfer_account_id || null,
        default_expense_account_id: accountPreferences[curr]?.default_expense_account_id || null
      }));

      await apiClient.request('/user/account-preferences', 'PUT', { preferences: payload });
      showToast('Account preferences saved successfully!', 'success');
      await refreshData();
    } catch (e) {
      console.error('Failed to save account preferences:', e);
      showToast('Failed to save account preferences: ' + (e.message || 'Error'), 'error');
    }
  };

  const handleSendWhatsAppReminder = async (friend, amount, currency, mode = 'BOT', balances = null, frequency = 'NOW', dayOfWeek = null, dayOfMonth = null, customDateTime = null) => {
    const friendName = friend.nickname || friend.display_name || friend.email?.split('@')[0] || 'Friend';
    const senderName = profileDisplayName || 'A friend';

    // Format balance lines for multi-currency or single currency
    let balanceLines = '';
    if (Array.isArray(balances) && balances.length > 0) {
      if (balances.length === 1) {
        balanceLines = `📌 Pending Amount: *${parseFloat(balances[0].amount || 0).toFixed(2)} ${balances[0].currency}*`;
      } else {
        balanceLines = `📌 Pending Amounts across Currencies:\n` +
          balances.map(b => `• *${parseFloat(b.amount || 0).toFixed(2)} ${b.currency}*`).join('\n');
      }
    } else {
      const amtStr = parseFloat(amount || 0).toFixed(2);
      balanceLines = `📌 Pending Amount: *${amtStr} ${currency || 'AED'}*`;
    }

    if (mode === 'DIRECT_WA_ME') {
      const reminderText = `👋 Hi ${friendName}!\n\n` +
        `This is a friendly payment reminder from *${senderName}*.\n\n` +
        `${balanceLines}\n\n` +
        `Please settle when convenient. Thank you! 🙏\n\n` +
        `🤖 _This is an app generated message, I use tezer to manage my finances._ 🚀`;

      const friendPhone = friend.phone_number || friend.whatsapp_phone;
      const cleanP = friendPhone ? friendPhone.replace(/[^0-9]/g, '') : '';
      const waUrl = cleanP
        ? `https://wa.me/${cleanP}?text=${encodeURIComponent(reminderText)}`
        : `https://wa.me/?text=${encodeURIComponent(reminderText)}`;

      window.open(waUrl, '_blank');
      setWaChoiceModal({ isOpen: false, friend: null, amount: 0, currency: 'AED', balances: null });
      return;
    }

    try {
      const res = await apiClient.request('/friends/send-whatsapp-reminder', 'POST', {
        friend_user_id: friend.user_id,
        amount,
        currency,
        balances,
        dispatch_mode: 'BOT',
        frequency,
        day_of_week: dayOfWeek,
        day_of_month: dayOfMonth,
        custom_datetime: customDateTime
      });
      setWaChoiceModal({ isOpen: false, friend: null, amount: 0, currency: 'AED', balances: null });
      if (res && res.scheduled) {
        showToast(res.message || `WhatsApp reminder scheduled (${frequency})!`, 'success');
      } else if (res && res.queued) {
        showToast('Bot is currently busy, your message is queued and it will be delivered soon.', 'info');
      } else if (res && res.success && res.method === 'DIRECT') {
        showToast(res.message || `WhatsApp reminder sent to ${friendName}!`, 'success');
      } else if (res && res.method === 'WA_ME_FALLBACK') {
        setWaRedirectModal({
          isOpen: true,
          redirectReason: res.redirectReason,
          waMeUrl: res.waMeUrl,
          friendName: friendName
        });
      } else if (res && res.error) {
        showToast(res.error, 'error');
      }
    } catch (err) {
      setWaChoiceModal({ isOpen: false, friend: null, amount: 0, currency: 'AED', balances: null });
      showToast(err.message, 'error');
    }
  };

  // Investments Portfolio States
  const [investments, setInvestments] = useState([]);
  const [investmentsAnalytics, setInvestmentsAnalytics] = useState(null);
  const [divDimension, setDivDimension] = useState('class'); // 'class', 'sector', 'region', 'cap', 'currency'
  const [invDisplayCurrency, setInvDisplayCurrency] = useState('ALL'); // 'ALL' or specific currency
  const [expandedAssetId, setExpandedAssetId] = useState(null);
  const [expandedAssetTab, setExpandedAssetTab] = useState({}); // { [investment_id]: 'chart' | 'txs' }
  const [selectedChartAsset, setSelectedChartAsset] = useState(null); // Full-screen / modal chart
  const [isAssetSearchOpen, setIsAssetSearchOpen] = useState(false);
  const [isSyncingPrices, setIsSyncingPrices] = useState(false);
  
  // Asset Form fields
  const [newInvName, setNewInvName] = useState('');
  const [newInvSymbol, setNewInvSymbol] = useState('');
  const [newInvCurrency, setNewInvCurrency] = useState('AED');
  const [newInvType, setNewInvType] = useState('EQUITY');
  const [newInvSector, setNewInvSector] = useState('N/A');
  const [newInvRegion, setNewInvRegion] = useState('US');
  const [newInvMarketCap, setNewInvMarketCap] = useState('Large');
  const [newInvTargetPct, setNewInvTargetPct] = useState('');
  const [newInvPrice, setNewInvPrice] = useState('');
  const [customAssetType, setCustomAssetType] = useState('');
  const [customInvSector, setCustomInvSector] = useState('');
  const [customInvRegion, setCustomInvRegion] = useState('');
  const [newInvQty, setNewInvQty] = useState('');
  const [newInvDate, setNewInvDate] = useState(new Date().toISOString().split('T')[0]);
  const [newInvSourceType, setNewInvSourceType] = useState('ACCOUNT');
  const [newInvSourceId, setNewInvSourceId] = useState('');
  const [recordFundingFlow, setRecordFundingFlow] = useState(false);

  // Split Funding / Multiple Source States
  const [txUseSplitSources, setTxUseSplitSources] = useState(false);
  const [splitTxSources, setSplitTxSources] = useState([{ accountId: '', amount: '' }]);

  const [invUseSplitSources, setInvUseSplitSources] = useState(false);
  const [splitInvSources, setSplitInvSources] = useState([{ sourceType: 'ACCOUNT', sourceId: '', amount: '' }]);

  const [txInvUseSplitSources, setTxInvUseSplitSources] = useState(false);
  const [splitTxInvSources, setSplitTxInvSources] = useState([{ sourceType: 'ACCOUNT', sourceId: '', amount: '' }]);

  const [ccUseSplitSources, setCcUseSplitSources] = useState(false);
  const [splitCcSources, setSplitCcSources] = useState([{ sourceType: 'ACCOUNT', sourceId: '', amount: '' }]);

  // Transaction Form fields
  const [selectedAssetTx, setSelectedAssetTx] = useState(null);
  const [invTxType, setInvTxType] = useState('BUY'); // 'BUY', 'SELL', 'DIVIDEND'
  const [invTxUnits, setInvTxUnits] = useState('');
  const [invTxPricePerUnit, setInvTxPricePerUnit] = useState('');
  const [invTxAmount, setInvTxAmount] = useState('');
  const [invTxSourceType, setInvTxSourceType] = useState('ACCOUNT'); // 'ACCOUNT', 'CREDIT_CARD', 'FRIEND'
  const [invTxSourceId, setInvTxSourceId] = useState('');
  const [invTxDate, setInvTxDate] = useState(new Date().toISOString().split('T')[0]);
  const [editingInvTxId, setEditingInvTxId] = useState(null); // null = create mode, string = editing tx ID

  // All Transactions Feed Filters & Sorts
  const [filterSearch, setFilterSearch] = useState('');
  const [filterType, setFilterType] = useState('ALL');
  const [filterAccount, setFilterAccount] = useState('ALL');
  const [filterCategory, setFilterCategory] = useState('ALL');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Inter-Account Transfer Form
  const [tfFromAccountId, setTfFromAccountId] = useState('');
  const [tfToAccountId, setTfToAccountId] = useState('');
  const [tfFromAmount, setTfFromAmount] = useState('');
  const [tfToAmount, setTfToAmount] = useState('');
  const [tfDesc, setTfDesc] = useState('');
  const [tfDescSuggestions, setTfDescSuggestions] = useState([]);
  const [tfFromType, setTfFromType] = useState('ACCOUNT'); // 'ACCOUNT' or 'FRIEND'
  const [tfToType, setTfToType] = useState('ACCOUNT'); // 'ACCOUNT' or 'FRIEND'
  const [tfConvertCurrency, setTfConvertCurrency] = useState(false);
  const [tfFriendCurrency, setTfFriendCurrency] = useState('AED');
  const [tfAutoConvert, setTfAutoConvert] = useState(() => localStorage.getItem('dhudd_tf_auto_convert') !== 'false');
  const [tfDate, setTfDate] = useState(new Date().toISOString().split('T')[0]);
  const [tfCharges, setTfCharges] = useState('');
  const [tfChargePayer, setTfChargePayer] = useState('SOURCE'); // 'SOURCE' or 'DESTINATION'

  // User Profile Settings
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [profileBio, setProfileBio] = useState('');
  const [profileEmail, setProfileEmail] = useState(localStorage.getItem('dhudd_email') || '');
  const [isChangingEmail, setIsChangingEmail] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [changeEmailOtpSent, setChangeEmailOtpSent] = useState(false);
  const [changeEmailOtpCode, setChangeEmailOtpCode] = useState('');
  const [changeEmailTurnstileToken, setChangeEmailTurnstileToken] = useState(null);
  const [isSendingChangeEmailOtp, setIsSendingChangeEmailOtp] = useState(false);
  const [isVerifyingChangeEmail, setIsVerifyingChangeEmail] = useState(false);
  const [changeEmailCooldown, setChangeEmailCooldown] = useState(0);
  const [profileCountry, setProfileCountry] = useState('');
  const [profileDob, setProfileDob] = useState('');
  const [profileGender, setProfileGender] = useState('');
  const [profileCountryCode, setProfileCountryCode] = useState('+971');
  const [profileCustomCc, setProfileCustomCc] = useState('');
  const [profilePhoneDigits, setProfilePhoneDigits] = useState('');
  const profilePhone = formatPhoneWithCountryCode(profileCountryCode, profileCustomCc, profilePhoneDigits);

  // Cooldown timers for OTP resend buttons
  useEffect(() => {
    if (pwResetOtpCooldown <= 0) return;
    const timer = setTimeout(() => {
      setPwResetOtpCooldown(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearTimeout(timer);
  }, [pwResetOtpCooldown]);

  useEffect(() => {
    if (changeEmailCooldown <= 0) return;
    const timer = setTimeout(() => {
      setChangeEmailCooldown(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearTimeout(timer);
  }, [changeEmailCooldown]);

  // Profile Picture Cropper Modal States
  const [avatarCropModalOpen, setAvatarCropModalOpen] = useState(false);
  const [rawCropImageSrc, setRawCropImageSrc] = useState(null);
  const [cropZoom, setCropZoom] = useState(1);
  const [cropOffsetX, setCropOffsetX] = useState(0);
  const [cropOffsetY, setCropOffsetY] = useState(0);
  const [cropRotation, setCropRotation] = useState(0);
  const [isDraggingCrop, setIsDraggingCrop] = useState(false);
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });

  // First-Time Setup / Onboarding States
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [onboardingCountry, setOnboardingCountry] = useState('United Arab Emirates');
  const [onboardingCurrency, setOnboardingCurrency] = useState('AED');
  const [onboardingTimezone, setOnboardingTimezone] = useState('Asia/Dubai');
  const [onboardingCurrencies, setOnboardingCurrencies] = useState(['AED']);
  const [onboardingDob, setOnboardingDob] = useState('');
  const [onboardingGender, setOnboardingGender] = useState('');
  const [onboardingCountryCode, setOnboardingCountryCode] = useState('+971');
  const [onboardingCustomCc, setOnboardingCustomCc] = useState('');
  const [onboardingPhoneDigits, setOnboardingPhoneDigits] = useState('');
  const [onboardingLoading, setOnboardingLoading] = useState(false);

  // Settings & Currency Custom State
  const [customShortCurrencyInput, setCustomShortCurrencyInput] = useState('');
  const [expandedSettingSection, setExpandedSettingSection] = useState(null);
  const [isSubmittingTx, setIsSubmittingTx] = useState(false);

  const handleCompleteOnboarding = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (onboardingDob && onboardingDob > getMaxDob7YearsAgo()) {
      setOnboardingLoading(false);
      return showToast('Invalid date of birth selected.', 'error');
    }
    setOnboardingLoading(true);
    try {
      const fullPhone = formatPhoneWithCountryCode(onboardingCountryCode, onboardingCustomCc, onboardingPhoneDigits);
      const chosenCurrs = Array.from(new Set([onboardingCurrency, ...onboardingCurrencies])).filter(Boolean);
      await apiClient.request('/auth/complete-onboarding', 'POST', {
        country: onboardingCountry,
        currency: onboardingCurrency,
        timezone: onboardingTimezone,
        currencies: chosenCurrs,
        date_of_birth: onboardingDob,
        gender: onboardingGender,
        phone_number: fullPhone
      });

      localStorage.setItem('dhudd_timezone', onboardingTimezone);
      localStorage.setItem('dhudd_onboarded_completed', 'true');
      setSelectedTimezone(onboardingTimezone);
      setProfileCountry(onboardingCountry);
      if (onboardingDob) setProfileDob(onboardingDob);
      if (onboardingGender) setProfileGender(onboardingGender);
      setPreferredCurrencies(chosenCurrs);
      syncEngine.setLocal(syncEngine.KEYS.PREFERRED_CURRENCIES, chosenCurrs);
      if (chosenCurrs.length > 0) {
        setTxCurrency(chosenCurrs[0]);
        setNewAccCurrency(chosenCurrs[0]);
        setNewCardCurrency(chosenCurrs[0]);
      }
      
      showToast(`Welcome! Settings configured for ${onboardingCountry}`, 'success');
      setOnboardingOpen(false);
      await refreshData();
    } catch (err) {
      console.error('Onboarding error:', err);
      showToast(err.message || 'Error completing setup', 'error');
    } finally {
      setOnboardingLoading(false);
    }
  };

  // Placeholder Friend Form States
  const [isPlaceholderFriend, setIsPlaceholderFriend] = useState(false);
  const [placeholderFriendName, setPlaceholderFriendName] = useState('');
  const [placeholderFriendDesc, setPlaceholderFriendDesc] = useState('');
  const [placeholderCountryCode, setPlaceholderCountryCode] = useState('+971');
  const [placeholderCustomCc, setPlaceholderCustomCc] = useState('');
  const [placeholderPhoneDigits, setPlaceholderPhoneDigits] = useState('');
  const [placeholderAdjustBalance, setPlaceholderAdjustBalance] = useState(false);
  const [placeholderInitialBalance, setPlaceholderInitialBalance] = useState('');
  const [placeholderInitialCurrency, setPlaceholderInitialCurrency] = useState('AED');
  const [friendInitialAdjustBalance, setFriendInitialAdjustBalance] = useState(false);
  const [friendInitialBalance, setFriendInitialBalance] = useState('');
  const [friendInitialCurrency, setFriendInitialCurrency] = useState('AED');
  const [isDisabledAccountsExpanded, setIsDisabledAccountsExpanded] = useState(false);

  // WhatsApp Reminder Scheduling States
  const [waReminderFreq, setWaReminderFreq] = useState('NOW');
  const [waReminderDayOfWeek, setWaReminderDayOfWeek] = useState('MONDAY');
  const [waReminderDayOfMonth, setWaReminderDayOfMonth] = useState(1);
  const [waReminderCustomDateTime, setWaReminderCustomDateTime] = useState('');
  
  const [friendMergePhone, setFriendMergePhone] = useState('');
  
  const [editingPlaceholderFriendId, setEditingPlaceholderFriendId] = useState(null);
  const [editPlaceholderName, setEditPlaceholderName] = useState('');
  const [editPlaceholderCountryCode, setEditPlaceholderCountryCode] = useState('+971');
  const [editPlaceholderCustomCc, setEditPlaceholderCustomCc] = useState('');
  const [editPlaceholderPhoneDigits, setEditPlaceholderPhoneDigits] = useState('');

  // Card Reminders Setting (ON by default)
  const [cardRemindersEnabled, setCardRemindersEnabled] = useState(
    localStorage.getItem('dhudd_card_reminders') !== 'false'
  );

  // Credit Card Redesign - Collapsible Sections State
  const [expandedCardsList, setExpandedCardsList] = useState({});
  const [expandedDetailSections, setExpandedDetailSections] = useState({ cycle: false, target: false });

  const toggleCardListExpand = (cardId, e) => {
    if (e) e.stopPropagation();
    setExpandedCardsList(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  const toggleDetailSection = (sectionKey) => {
    setExpandedDetailSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  };



  // Global Settings
  const [settings, setSettings] = useState({
    decimalSeparator: '.',
    delimiter: ',',
    precision: 2
  });

  // Activities, Password Change & Manual Investment price editing states
  const [transactionTabMode, setTransactionTabMode] = useState('transactions');
  const [activityLogs, setActivityLogs] = useState([]);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [editingAssetPriceId, setEditingAssetPriceId] = useState(null);
  const [newAssetPriceInput, setNewAssetPriceInput] = useState('');

  useEffect(() => {
    setDescSuggestions([]);
    setTfDescSuggestions([]);
  }, [activeModal, editingTx, activeTab]);

  const [visibleTransfersLimit, setVisibleTransfersLimit] = useState(10);

  useEffect(() => {
    setVisibleTransfersLimit(10);
  }, [activeTab]);

  // Load configuration on startup
  useEffect(() => {
    const savedSettings = localStorage.getItem('dhudd_settings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  // Fetch initial caches & data
  useEffect(() => {
    if (isAuthenticated) {
      loadCachedData();
      refreshData();

      const cleanup = syncEngine.registerSyncListeners(
        (status) => setOnline(status),
        () => {
          showToast('Offline mutations auto-synced!', 'success');
          refreshData();
        }
      );
      
      const handleAuthRequired = () => {
        setIsAuthenticated(false);
        setIsLocked(true);
        showToast('Auth expired. Please log in.', 'error');
      };
      window.addEventListener('auth_required', handleAuthRequired);

      return () => {
        if (cleanup) cleanup();
        window.removeEventListener('auth_required', handleAuthRequired);
      };
    }
  }, [isAuthenticated]);

  // Google OAuth Initialization
  useEffect(() => {
    if (isAuthenticated) return;

    let initialized = false;

    const handleGoogleCallback = async (response) => {
      console.log('[Google Auth] Credential received from Google');
      try {
        const res = await apiClient.request('/auth/google', 'POST', {
          credential: response.credential
        });
        console.log('[Google Auth] Server login success:', res);
        apiClient.setToken(res.token);
        localStorage.setItem('tezer_user_id', res.user_id);
        localStorage.setItem('dhudd_user_id', res.user_id);
        localStorage.setItem('tezer_email', res.email);
        localStorage.setItem('dhudd_email', res.email);
        const provider = res.auth_provider || 'GOOGLE';
        setUserAuthProvider(provider);
        localStorage.setItem('tezer_auth_provider', provider);
        localStorage.setItem('dhudd_auth_provider', provider);
        if (res.passcode) {
          setSavedPin(res.passcode);
          setPinInput(res.passcode);
        }
        setIsAuthenticated(true);
        setIsLocked(false);
        if (res.is_onboarded === false || res.is_onboarded === 0 || !res.is_onboarded) {
          localStorage.removeItem('dhudd_onboarded_completed');
          setOnboardingOpen(true);
        } else {
          localStorage.setItem('dhudd_onboarded_completed', 'true');
        }
        showToast('Signed in with Google!', 'success');
      } catch (err) {
        console.error('[Google Auth] Failed:', err);
        showToast(err.message || 'Google Sign-In failed', 'error');
      }
    };

    const setupGoogleBtn = () => {
      if (initialized) return;
      if (window.google?.accounts?.id) {
        initialized = true;
        window.google.accounts.id.initialize({
          client_id: '250902860545-frrl7f7lchdknai831uv3g5vo7e1jg0m.apps.googleusercontent.com',
          callback: handleGoogleCallback,
          auto_select: false
        });
        const btnContainer = document.getElementById('googleSignInBtn');
        if (btnContainer) {
          btnContainer.innerHTML = '';
          window.google.accounts.id.renderButton(btnContainer, {
            theme: 'outline',
            size: 'large',
            width: '100%',
            shape: 'rectangular'
          });
        }
      }
    };

    setupGoogleBtn();
    const timer = setInterval(() => {
      if (!initialized && window.google?.accounts?.id) {
        setupGoogleBtn();
        clearInterval(timer);
      }
    }, 250);

    return () => clearInterval(timer);
  }, [isAuthenticated, isRegisterMode]);

  // Last data refresh timestamp for smart visibility & refocus syncing
  const lastRefreshTimeRef = useRef(Date.now());

  // Smart Sync Engine: Process offline queue periodically and sync on visibility/focus
  useEffect(() => {
    if (!isAuthenticated) return;

    // 1. Process offline mutations queue if pending (every 10s)
    const queueInterval = setInterval(async () => {
      const q = syncEngine.getLocal(syncEngine.KEYS.MUTATIONS, []);
      setQueuedMutationsCount(q.length);
      if (q.length > 0 && apiClient.isOnline() && apiClient.getToken()) {
        try {
          const synced = await syncEngine.syncOfflineQueue();
          if (synced) {
            const newQ = syncEngine.getLocal(syncEngine.KEYS.MUTATIONS, []);
            setQueuedMutationsCount(newQ.length);
            lastRefreshTimeRef.current = Date.now();
            refreshData().catch(err => console.error('Queue sync refresh error:', err));
          }
        } catch (e) {
          console.warn('[Sync Engine] Background queue sync failed:', e);
        }
      }
    }, 10000);

    // 2. Refresh data on window focus / tab visibility change (throttled to at most once per 3 minutes)
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible' && apiClient.isOnline() && apiClient.getToken()) {
        const now = Date.now();
        if (now - lastRefreshTimeRef.current > 3 * 60 * 1000) {
          lastRefreshTimeRef.current = now;
          refreshData().catch(err => console.error('Focus refresh error:', err));
        }
      }
    };

    // 3. Relaxed background heartbeat (every 5 minutes, ONLY when tab is actively visible)
    const heartbeatInterval = setInterval(() => {
      if (document.visibilityState === 'visible' && apiClient.isOnline() && apiClient.getToken()) {
        lastRefreshTimeRef.current = Date.now();
        refreshData().catch(err => console.error('Heartbeat refresh error:', err));
      }
    }, 5 * 60 * 1000);

    window.addEventListener('focus', handleVisibilityOrFocus);
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);

    return () => {
      clearInterval(queueInterval);
      clearInterval(heartbeatInterval);
      window.removeEventListener('focus', handleVisibilityOrFocus);
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
    };
  }, [isAuthenticated]);

  // Immediately fetch investment data when switching to the investments tab
  useEffect(() => {
    if (isAuthenticated && activeTab === 'investments' && apiClient.isOnline() && apiClient.getToken()) {
      Promise.allSettled([
        apiClient.request('/investments'),
        apiClient.request('/investments/analytics')
      ]).then(([invsRes, anRes]) => {
        if (invsRes.status === 'fulfilled' && invsRes.value) {
          setInvestments(Array.isArray(invsRes.value) ? invsRes.value : []);
        }
        if (anRes.status === 'fulfilled' && anRes.value) {
          setInvestmentsAnalytics(anRes.value);
        }
      }).catch(err => console.error('Error fetching investments on tab switch:', err));
    }
  }, [activeTab, isAuthenticated]);

  // History API for Mobile Back Button
  useEffect(() => {
    if (isAuthenticated) {
      if (!window.history.state || window.history.state.tab !== activeTab) {
        window.history.pushState({ tab: activeTab }, '', `#${activeTab}`);
      }
    }
  }, [activeTab, isAuthenticated]);

  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.tab) {
        setActiveTab(event.state.tab);
        setActiveSheet(null);
        setIsMenuOpen(false);
      } else {
        setActiveTab('dashboard');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const sanitizeTransactions = (rawTxs) => {
    if (!Array.isArray(rawTxs)) return [];
    const myUserId = localStorage.getItem('dhudd_user_id');
    if (!myUserId) return rawTxs;
    return rawTxs.filter(tx => 
      !(tx.user_id !== myUserId && rawTxs.some(t => t.source_transaction_id === tx.transaction_id && t.user_id === myUserId)) &&
      !(tx.user_id !== myUserId && tx.source_transaction_id && rawTxs.some(t => t.transaction_id === tx.source_transaction_id && t.user_id === myUserId))
    );
  };

  const loadCachedData = () => {
    const accs = syncEngine.getLocal(syncEngine.KEYS.ACCOUNTS, []);
    setAccounts(Array.isArray(accs) ? accs : []);

    const txs = syncEngine.getLocal(syncEngine.KEYS.TRANSACTIONS, []);
    setTransactions(sanitizeTransactions(txs));

    const grps = syncEngine.getLocal(syncEngine.KEYS.GROUPS, []);
    setGroups(Array.isArray(grps) ? grps : []);

    const cats = syncEngine.getLocal(syncEngine.KEYS.CATEGORIES, []);
    setCategories(Array.isArray(cats) ? cats : []);

    const frnds = syncEngine.getLocal(syncEngine.KEYS.FRIENDS, []);
    setFriends(sortFriendsAZ(Array.isArray(frnds) ? frnds : []));

    const pref = syncEngine.getLocal(syncEngine.KEYS.PREFERRED_CURRENCIES, []);
    setPreferredCurrencies(Array.isArray(pref) ? pref : []);

    const rates = syncEngine.getLocal(syncEngine.KEYS.CURRENCY_RATES, []);
    setCurrencyRates(Array.isArray(rates) ? rates : []);

    const notifs = syncEngine.getLocal(syncEngine.KEYS.NOTIFICATIONS, []);
    setNotifications(Array.isArray(notifs) ? notifs : []);

    const pas = syncEngine.getLocal(syncEngine.KEYS.PENDING_ACTIONS, []);
    setPendingActions(Array.isArray(pas) ? pas : []);

    const invs = syncEngine.getLocal(syncEngine.KEYS.INVESTMENTS, []);
    setInvestments(Array.isArray(invs) ? invs : []);

    const invAn = syncEngine.getLocal(syncEngine.KEYS.INVESTMENTS_ANALYTICS, null);
    if (invAn) setInvestmentsAnalytics(invAn);

    const accPrefs = syncEngine.getLocal(syncEngine.KEYS.ACCOUNT_PREFERENCES, {});
    if (accPrefs) setAccountPreferences(accPrefs);

    setProfileDisplayName(localStorage.getItem('dhudd_cache_profile_display_name') || '');
    setProfileBio(localStorage.getItem('dhudd_cache_profile_bio') || '');
    setProfileEmail(localStorage.getItem('dhudd_email') || '');
    setUserAuthProvider(localStorage.getItem('dhudd_auth_provider') || '');
  };

  const refreshData = async () => {
    try {
      if (!apiClient.isOnline() || !apiClient.getToken()) {
        loadCachedData();
        return;
      }

      const [
        accsRes, txsRes, grpsRes,
        ratesRes, catsRes, frndsRes, prefRes, notifsRes, profileRes, invsRes, anRes, pendingActRes
      ] = await Promise.allSettled([
        syncEngine.getAccountsCached(),
        syncEngine.getTransactionsCached(),
        syncEngine.getGroupsCached(),
        apiClient.request('/currency/rates'),
        apiClient.request('/categories'),
        apiClient.request('/friends'),
        apiClient.request('/currencies/preferred'),
        apiClient.request('/notifications'),
        apiClient.request('/users/profile'),
        activeTab === 'investments' ? apiClient.request('/investments') : Promise.resolve(null),
        activeTab === 'investments' ? apiClient.request('/investments/analytics') : Promise.resolve(null),
        apiClient.request('/pending-actions')
      ]);

      if (accsRes.status === 'fulfilled') setAccounts(Array.isArray(accsRes.value) ? accsRes.value : []);
      if (txsRes.status === 'fulfilled') setTransactions(sanitizeTransactions(txsRes.value));
      if (grpsRes.status === 'fulfilled') setGroups(Array.isArray(grpsRes.value) ? grpsRes.value : []);
      if (ratesRes.status === 'fulfilled') {
        const safeRates = Array.isArray(ratesRes.value) ? ratesRes.value : [];
        setCurrencyRates(safeRates);
        syncEngine.setLocal(syncEngine.KEYS.CURRENCY_RATES, safeRates);
      }
      if (catsRes.status === 'fulfilled') {
        const rawCats = Array.isArray(catsRes.value) ? catsRes.value : [];
        const catMap = new Map();
        rawCats.forEach(cat => {
          const key = `${cat.type}_${(cat.name || '').trim().toLowerCase()}`;
          if (!catMap.has(key) || cat.user_id) {
            catMap.set(key, cat);
          }
        });
        const safeCats = Array.from(catMap.values());
        setCategories(safeCats);
        syncEngine.setLocal(syncEngine.KEYS.CATEGORIES, safeCats);
      }
      if (frndsRes.status === 'fulfilled') {
        const safeFrnds = sortFriendsAZ(Array.isArray(frndsRes.value) ? frndsRes.value : []);
        setFriends(safeFrnds);
        syncEngine.setLocal(syncEngine.KEYS.FRIENDS, safeFrnds);
      }
      if (prefRes.status === 'fulfilled') {
        const safePref = Array.isArray(prefRes.value) ? prefRes.value : [];
        setPreferredCurrencies(safePref);
        syncEngine.setLocal(syncEngine.KEYS.PREFERRED_CURRENCIES, safePref);
      }
      if (notifsRes.status === 'fulfilled') {
        const safeNotifs = Array.isArray(notifsRes.value) ? notifsRes.value : [];
        setNotifications(safeNotifs);
        syncEngine.setLocal(syncEngine.KEYS.NOTIFICATIONS, safeNotifs);
      }
      if (profileRes.status === 'fulfilled' && profileRes.value) {
        try {
          const p = profileRes.value;
          if (p.display_name !== undefined) {
            setProfileDisplayName(p.display_name || '');
            localStorage.setItem('dhudd_cache_profile_display_name', p.display_name || '');
          }
          if (p.bio !== undefined) {
            setProfileBio(p.bio || '');
            localStorage.setItem('dhudd_cache_profile_bio', p.bio || '');
          }
          if (p.email !== undefined) {
            setProfileEmail(p.email || '');
            localStorage.setItem('dhudd_email', p.email || '');
          }
          if (p.auth_provider !== undefined) {
            const prov = p.auth_provider || 'EMAIL';
            setUserAuthProvider(prov);
            localStorage.setItem('dhudd_auth_provider', prov);
          }
          if (p.avatar_url !== undefined) {
            setProfileAvatarUrl(p.avatar_url || null);
            if (p.avatar_url) {
              localStorage.setItem('tezer_avatar_url', p.avatar_url);
              localStorage.setItem('dhudd_avatar_url', p.avatar_url);
            } else {
              localStorage.removeItem('tezer_avatar_url');
              localStorage.removeItem('dhudd_avatar_url');
            }
          }
          if (p.daily_reminder_enabled !== undefined) {
            const remEnabled = p.daily_reminder_enabled === true || p.daily_reminder_enabled === 1 || p.daily_reminder_enabled === 'true';
            setDailyReminderEnabled(remEnabled);
            localStorage.setItem('dhudd_daily_reminder_enabled', String(remEnabled));
          }
          if (p.daily_reminder_time) {
            setDailyReminderTime(p.daily_reminder_time);
            localStorage.setItem('dhudd_daily_reminder_time', p.daily_reminder_time);
          }
          identifyUser({
            id: p.user_id || p.id || localStorage.getItem('tezer_user_id') || localStorage.getItem('dhudd_user_id'),
            email: p.email,
            display_name: p.display_name,
            country: p.country,
            primary_currency: p.primary_currency
          });
        } catch (profileErr) {
          console.error('Error processing user profile:', profileErr);
        }
      }
      if (invsRes.status === 'fulfilled' && invsRes.value) {
        const safeInvs = Array.isArray(invsRes.value) ? invsRes.value : [];
        setInvestments(safeInvs);
        syncEngine.setLocal(syncEngine.KEYS.INVESTMENTS, safeInvs);
      }
      if (anRes.status === 'fulfilled' && anRes.value) {
        setInvestmentsAnalytics(anRes.value);
        syncEngine.setLocal(syncEngine.KEYS.INVESTMENTS_ANALYTICS, anRes.value);
      }
      if (pendingActRes.status === 'fulfilled') {
        const safePAs = Array.isArray(pendingActRes.value) ? pendingActRes.value : [];
        setPendingActions(safePAs);
        syncEngine.setLocal(syncEngine.KEYS.PENDING_ACTIONS, safePAs);
        // Reset actionSelections to only include keys that still exist in pending actions
        setActionSelections(prev => {
          const newSel = {};
          safePAs.forEach(pa => { if (prev[pa.action_id]) newSel[pa.action_id] = prev[pa.action_id]; });
          return newSel;
        });
      }

      if (activeGroupRef.current?.group_id) {
        await reloadActiveGroupBalances(activeGroupRef.current.group_id);
      }

      lastRefreshTimeRef.current = Date.now();
    } catch (e) {
      console.error('Failed refreshing data from server', e);
    }
  };


  // Lazy-load settings logs and preferences ONLY when the settings modal is opened
  useEffect(() => {
    if (activeModal === 'settings' && isAuthenticated && apiClient.isOnline() && apiClient.getToken()) {
      Promise.allSettled([
        apiClient.request('/activity-logs'),
        fetchWhatsAppSettings(),
        fetchAccountPreferences()
      ]).then(([logsRes]) => {
        if (logsRes.status === 'fulfilled') setActivityLogs(Array.isArray(logsRes.value) ? logsRes.value : []);
      }).catch(err => console.error('Error lazy-loading settings data:', err));
    }
  }, [activeModal, isAuthenticated]);


  // Sync selected account's currency with the transaction currency when modal is opened or accounts load
  useEffect(() => {
    if (activeModal === 'outflow' || activeModal === 'inflow') {
      const validAccounts = accounts.filter(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts')));
      const isValid = txAccountId && validAccounts.some(a => a.account_id === txAccountId);
      const selectedAccId = isValid ? txAccountId : (validAccounts[0]?.account_id || '');
      if (selectedAccId) {
        setTxAccountId(selectedAccId);
        const acc = accounts.find(a => a.account_id === selectedAccId);
        if (acc) {
          setTxCurrency(acc.currency);
        }
      }
    }
  }, [activeModal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset transfer amount fields when transfer accounts change
  useEffect(() => {
    setTfFromAmount('');
    setTfToAmount('');
  }, [tfFromAccountId, tfToAccountId, tfFromType, tfToType]);

  // Reset active account/group/friend on tab change
  useEffect(() => {
    setActiveAccount(null);
    setActiveGroup(null);
    setActiveFriend(null);
    setActiveItemTxLimit(10);
    setExportModalOpen(false);
    setExportTargetType('account');
    setExportTargetData(null);
    setExportFormat('pdf');
    setExportPeriod('all');
    setExportStartDate('');
    setExportEndDate('');
    setExportLimitType('all');
    setExportLimitValue('');
  }, [activeTab]);

  const showToast = (message, type = 'info') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5500);
  };

  // Auth operations
  const handleSendOtp = async (e) => {
    if (e) e.preventDefault();
    if (!authEmail || !authEmail.includes('@')) {
      showToast('Please enter a valid email address first.', 'error');
      return;
    }

    const tokenToSend = turnstileToken || 'XXXX-fallback-token';

    setIsSendingOtp(true);
    try {
      const sendPromise = apiClient.request('/auth/send-otp', 'POST', {
        email: authEmail.trim(),
        turnstileToken: tokenToSend
      });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out while sending verification code. Please try again.')), 15000)
      );

      const res = await Promise.race([sendPromise, timeoutPromise]);
      setOtpSent(true);
      setOtpCountdown(60);
      showToast(res.message || 'Verification code sent to your email!', 'success');
    } catch (err) {
      console.error('Send OTP Error:', err);
      showToast(err.message || 'Failed to send verification code. Please try again.', 'error');
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleSendForgotPwOtp = async (e) => {
    if (e) e.preventDefault();
    if (!forgotPwEmail || !forgotPwEmail.includes('@')) {
      showToast('Please enter a valid email address first.', 'error');
      return;
    }

    const tokenToSend = forgotPwTurnstileToken || 'XXXX-fallback-token';

    setIsSendingForgotPwOtp(true);
    try {
      const sendPromise = apiClient.request('/auth/forgot-password', 'POST', {
        email: forgotPwEmail.trim(),
        turnstileToken: tokenToSend
      });
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out while sending verification code. Please try again.')), 15000)
      );

      const res = await Promise.race([sendPromise, timeoutPromise]);
      setForgotPwStep('code');
      setForgotPwCooldown(60);
      showToast(res.message || 'You will receive an email with the code if the entered email is correct.', 'info');
    } catch (err) {
      console.error('Forgot Password Send OTP Error:', err);
      showToast(err.message || 'Failed to send verification code. Please try again.', 'error');
    } finally {
      setIsSendingForgotPwOtp(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!forgotPwOtp || forgotPwOtp.trim().length !== 6) {
      showToast('Please enter the 6-digit verification code.', 'error');
      return;
    }
    if (!forgotPwNewPassword) {
      showToast('Please enter a new password.', 'error');
      return;
    }
    if (forgotPwNewPassword.length < 8) {
      showToast('Password must be at least 8 characters long.', 'error');
      return;
    }
    if (forgotPwNewPassword !== forgotPwConfirmPassword) {
      showToast('New passwords do not match.', 'error');
      return;
    }

    setIsResettingPassword(true);
    try {
      const res = await apiClient.request('/auth/reset-password', 'POST', {
        email: forgotPwEmail.trim(),
        otpCode: forgotPwOtp.trim(),
        newPassword: forgotPwNewPassword
      });

      showToast(res.message || 'Password has been reset successfully. Please sign in with your new password.', 'success');
      setIsForgotPasswordMode(false);
      setForgotPwStep('email');
      setForgotPwOtp('');
      setForgotPwNewPassword('');
      setForgotPwConfirmPassword('');
      setAuthEmail(forgotPwEmail.trim());
      setAuthPassword('');
    } catch (err) {
      console.error('Reset Password Error:', err);
      showToast(err.message || 'Failed to reset password. Please check your verification code and try again.', 'error');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    if (!authEmail) return;

    const tokenToSend = turnstileToken || 'XXXX-fallback-token';

    try {
      if (isRegisterMode) {
        if (authDob && authDob > getMaxDob7YearsAgo()) {
          showToast('Invalid date of birth selected.', 'error');
          return;
        }
        if (!otpCode) {
          showToast('Please enter the 6-digit verification code sent to your email.', 'error');
          return;
        }
        if (!authPassword) {
          showToast('Please enter a password for your account.', 'error');
          return;
        }
        const fullAuthPhone = formatPhoneWithCountryCode(authCountryCode, authCustomCc, authPhoneDigits);
        const res = await apiClient.request('/auth/register', 'POST', { 
          email: authEmail.trim(), 
          password: authPassword, 
          otpCode: otpCode.trim(),
          turnstileToken: tokenToSend,
          display_name: authName.trim() || undefined,
          date_of_birth: authDob || undefined,
          gender: authGender || undefined,
          country: authCountry || 'United Arab Emirates',
          phone_number: fullAuthPhone || undefined,
          timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'Asia/Dubai'
        });
        if (res.token) {
          apiClient.setToken(res.token);
          localStorage.setItem('dhudd_user_id', res.user_id);
          localStorage.setItem('dhudd_email', res.email);
          setUserAuthProvider('EMAIL');
          localStorage.setItem('dhudd_auth_provider', 'EMAIL');
          if (res.passcode) {
            setSavedPin(res.passcode);
            setPinInput(res.passcode);
          }
          setIsAuthenticated(true);
          setIsLocked(false);
          identifyUser({
            id: res.user_id,
            email: res.email,
            display_name: authName.trim(),
            country: authCountry
          });
          trackEvent('user_signed_up', {
            email: res.email,
            country: authCountry,
            phone_provided: Boolean(fullAuthPhone)
          });
          showToast('Account registered successfully!', 'success');

          if (res.is_onboarded === false || res.is_onboarded === 0 || !res.is_onboarded) {
            localStorage.removeItem('dhudd_onboarded_completed');
            if (authCountry) {
              setOnboardingCountry(authCountry);
              const preset = COUNTRY_PRESETS.find(p => p.country === authCountry);
              if (preset) {
                setOnboardingCurrency(preset.currency);
                setOnboardingTimezone(preset.timezone);
                setOnboardingCurrencies(preset.defaultCurrencies);
              }
            }
            if (authDob) setProfileDob(authDob);
            if (authDob) setOnboardingDob(authDob);
            if (authGender) setOnboardingGender(authGender);
            if (authCountryCode) setOnboardingCountryCode(authCountryCode);
            if (authPhoneDigits) setOnboardingPhoneDigits(authPhoneDigits);
            setOnboardingStep(3);
            setOnboardingOpen(true);
          } else {
            localStorage.setItem('dhudd_onboarded_completed', 'true');
          }
        } else {
          setShowRegistrationSuccess(true);
          setTurnstileToken(null);
        }
      } else {
        const res = await apiClient.request('/auth/login', 'POST', { 
          email: authEmail.trim(), 
          password: authPassword, 
          turnstileToken: tokenToSend 
        });
        apiClient.setToken(res.token);
        localStorage.setItem('dhudd_user_id', res.user_id);
        localStorage.setItem('dhudd_email', res.email);
        const prov = res.auth_provider || 'EMAIL';
        setUserAuthProvider(prov);
        localStorage.setItem('dhudd_auth_provider', prov);
        if (res.passcode) {
          setSavedPin(res.passcode);
          setPinInput(res.passcode);
        }
        setIsAuthenticated(true);
        setIsLocked(false); 
        if (res.is_onboarded === false || res.is_onboarded === 0) {
          localStorage.removeItem('dhudd_onboarded_completed');
          setOnboardingOpen(true);
        } else {
          localStorage.setItem('dhudd_onboarded_completed', 'true');
        }
        identifyUser({
          id: res.user_id,
          email: res.email
        });
        trackEvent('user_logged_in', {
          email: res.email,
          auth_provider: prov
        });
        showToast('Logged in!', 'success');
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Helper to predict category from description
  const getAutoCategory = (desc, type = 'OUTFLOW') => {
    if (!desc) return '';
    const d = desc.toLowerCase().trim();
    
    // 1. Check transaction history (only if category exists in current categories)
    const match = transactions.find(t => t.description && t.description.toLowerCase().trim() === d);
    if (match && match.category_name) {
      const isValid = categories.some(c => c.name.toLowerCase() === match.category_name.toLowerCase());
      if (isValid) {
        const found = categories.find(c => c.name.toLowerCase() === match.category_name.toLowerCase());
        return found ? found.name : match.category_name;
      }
    }
    
    // 2. Keyword heuristic checks
    let predicted = '';
    if (type === 'OUTFLOW') {
      if (d.includes('starbucks') || d.includes('coffee') || d.includes('cafe') || d.includes('restaurant') || d.includes('lunch') || d.includes('dinner') || d.includes('breakfast') || d.includes('burger') || d.includes('pizza') || d.includes('swiggy') || d.includes('zomato') || d.includes('eat') || d.includes('kfc') || d.includes('mcdonald')) {
        predicted = 'Eating out';
      } else if (d.includes('grocery') || d.includes('groceries') || d.includes('supermarket') || d.includes('vegetable') || d.includes('fruit') || d.includes('market') || d.includes('provisions') || d.includes('milk') || d.includes('bread') || d.includes('lulu') || d.includes('carrefour') || d.includes('hypermarket') || d.includes('nestle') || d.includes('al maya')) {
        predicted = 'Grocery';
      } else if (d.includes('food')) {
        predicted = 'Food';
      } else if (d.includes('rent') || d.includes('room') || d.includes('flat') || d.includes('apartment') || d.includes('mess') || d.includes('pg') || d.includes('hostel') || d.includes('electric') || d.includes('water') || d.includes('electricity') || d.includes('internet') || d.includes('wifi') || d.includes('broadband') || d.includes('bill') || d.includes('utility') || d.includes('utilities') || d.includes('phone') || d.includes('mobile') || d.includes('recharge') || d.includes('gas') || d.includes('sewa') || d.includes('dewa') || d.includes('etisalat') || d.includes('du ') || d.endsWith('du')) {
        predicted = 'Bills';
      } else if (d.includes('uber') || d.includes('fuel') || d.includes('petrol') || d.includes('diesel') || d.includes('car') || d.includes('taxi') || d.includes('transport') || d.includes('metro') || d.includes('train') || d.includes('bus') || d.includes('cab') || d.includes('ola') || d.includes('parking') || d.includes('toll') || d.includes('salik') || d.includes('careem') || d.includes('rta') || d.includes('service') || d.includes('oil change')) {
        predicted = 'Transportation';
      } else if (d.includes('fee') || d.includes('charge') || d.includes('charges') || d.includes('interest') || d.includes('commission') || d.includes('bank fee') || d.includes('annual fee') || d.includes('penalty') || d.includes('late fee') || d.includes('vat')) {
        predicted = 'Bank/Card Charges';
      } else if (d.includes('shirt') || d.includes('jeans') || d.includes('shoes') || d.includes('clothes') || d.includes('shopping') || d.includes('dress') || d.includes('jacket') || d.includes('apparel') || d.includes('fashion') || d.includes('nike') || d.includes('adidas') || d.includes('zara') || d.includes('h&m') || d.includes('cloth') || d.includes('garment')) {
        predicted = 'Apparel';
      } else if (d.includes('doctor') || d.includes('medicine') || d.includes('hospital') || d.includes('spa') || d.includes('salon') || d.includes('makeup') || d.includes('hair') || d.includes('dental') || d.includes('pharmacy') || d.includes('health') || d.includes('beauty') || d.includes('clinic') || d.includes('cosmetic') || d.includes('dentist')) {
        predicted = 'Health & Beauty';
      } else if (d.includes('flight') || d.includes('hotel') || d.includes('vacation') || d.includes('trip') || d.includes('travel') || d.includes('booking') || d.includes('airline') || d.includes('tour') || d.includes('resort') || d.includes('airways') || d.includes('emirates')) {
        predicted = 'Trip';
      } else if (d.includes('movie') || d.includes('ticket') || d.includes('cinema') || d.includes('netflix') || d.includes('spotify') || d.includes('prime') || d.includes('disney') || d.includes('theater') || d.includes('entertainment') || d.includes('game') || d.includes('gaming') || d.includes('event') || d.includes('concert') || d.includes('youtube')) {
        predicted = 'Entertainment';
      } else if (d.includes('school') || d.includes('college') || d.includes('tuition') || d.includes('course') || d.includes('visa') || d.includes('fine') || d.includes('govt') || d.includes('government') || d.includes('education') || d.includes('university') || d.includes('exam') || d.includes('license') || d.includes('passport')) {
        predicted = 'Edu/Govt.';
      } else if (d.includes('family') || d.includes('kids') || d.includes('baby') || d.includes('parents') || d.includes('child')) {
        predicted = 'Family';
      } else if (d.includes('charity') || d.includes('donation') || d.includes('zakat') || d.includes('mosque') || d.includes('sadaqah') || d.includes('donate')) {
        predicted = 'Donation';
      } else if (d.includes('stock loss') || d.includes('crypto loss') || d.includes('investment loss')) {
        predicted = 'Loss from investments';
      }
    } else {
      if (d.includes('salary') || d.includes('payout') || d.includes('payroll') || d.includes('wage') || d.includes('income') || d.includes('salary payment') || d.includes('transfer from employer')) {
        predicted = 'Salary';
      } else if (d.includes('refund') || d.includes('cashback') || d.includes('cash back') || d.includes('reward') || d.includes('gpay reward') || d.includes('bonus') || d.includes('gift')) {
        predicted = 'Cashback';
      } else if (d.includes('dividend') || d.includes('profit') || d.includes('capital gain') || d.includes('investment income') || d.includes('stock') || d.includes('invest') || d.includes('crypto')) {
        predicted = 'Income from investments';
      } else if (d.includes('liquidation') || d.includes('sold stock') || d.includes('sold crypto') || d.includes('redemption')) {
        predicted = 'Investment Liquidation';
      } else if (d.includes('deposit') || d.includes('cash deposit') || d.includes('self transfer') || d.includes('atm deposit')) {
        predicted = 'Deposits';
      } else {
        predicted = 'Other income';
      }
    }

    if (predicted) {
      const matchCat = categories.find(c => c.name.toLowerCase() === predicted.toLowerCase() && (c.type === type || c.type === (type === 'OUTFLOW' ? 'OUTFLOW' : 'INFLOW')));
      if (matchCat) return matchCat.name;
      const anyMatch = categories.find(c => c.name.toLowerCase() === predicted.toLowerCase());
      if (anyMatch) return anyMatch.name;
    }
    return '';
  };


  const handleTxDescChange = (e) => {
    const val = e.target.value;
    setTxDesc(val);
    const predictedCat = getAutoCategory(val, activeModal === 'outflow' ? 'OUTFLOW' : 'INFLOW');
    if (predictedCat) {
      setTxCategory(predictedCat);
    }
    
    // Autocomplete suggestions
    if (val.trim()) {
      const uniqueDescriptions = Array.from(new Set(transactions.map(t => t.description).filter(Boolean)));
      const filtered = uniqueDescriptions.filter(desc => 
        desc.toLowerCase().includes(val.toLowerCase()) && 
        desc.toLowerCase() !== val.toLowerCase()
      ).slice(0, 5);
      setDescSuggestions(filtered);
    } else {
      setDescSuggestions([]);
    }
  };

  const handleTfDescChange = (e) => {
    const val = e.target.value;
    setTfDesc(val);
    
    // Autocomplete suggestions for transfers
    if (val.trim()) {
      const transferTxs = transactions.filter(isTransferTransaction);
      const uniqueDescriptions = Array.from(new Set(transferTxs.map(t => t.description).filter(Boolean)));
      const filtered = uniqueDescriptions.filter(desc => 
        desc.toLowerCase().includes(val.toLowerCase()) && 
        desc.toLowerCase() !== val.toLowerCase()
      ).slice(0, 5);
      setTfDescSuggestions(filtered);
    } else {
      setTfDescSuggestions([]);
    }
  };

  // Get active split members list (either selected group members or selected friends + user themselves)
  const getSplitMembers = () => {
    if (splitTargetType === 'GROUP') {
      const g = groups.find(grp => grp.group_id === txGroupId);
      if (!g || !g.members) return [];
      return g.members.filter(m => splitGroupMembersList.includes(m.user_id));
    } else {
      const userEmail = localStorage.getItem('dhudd_email') || 'You';
      const userId = localStorage.getItem('dhudd_user_id') || 'active_user';
      const userDisplayName = localStorage.getItem('dhudd_display_name') || 'You';
      const selfMember = { user_id: userId, email: userEmail, display_name: userDisplayName };
      const selectedFriendsObjects = friends
        .filter(f => splitFriendsList.includes(f.user_id))
        .map(f => ({ user_id: f.user_id, email: f.email, display_name: f.display_name, is_placeholder: f.is_placeholder }));
      
      const list = [selfMember, ...selectedFriendsObjects];
      if (txPayerUserId && txPayerUserId !== 'self') {
        const payerFriend = friends.find(f => f.user_id === txPayerUserId);
        if (payerFriend && !list.some(m => m.user_id === txPayerUserId)) {
          list.push({ user_id: payerFriend.user_id, email: payerFriend.email, display_name: payerFriend.display_name, is_placeholder: payerFriend.is_placeholder });
        }
      }
      if (txMultiPayers && typeof txMultiPayers === 'object') {
        Object.keys(txMultiPayers).forEach(uid => {
          if (uid !== 'self' && !list.some(m => m.user_id === uid)) {
            const payerFriend = friends.find(f => f.user_id === uid);
            if (payerFriend) {
              list.push({ user_id: payerFriend.user_id, email: payerFriend.email, display_name: payerFriend.display_name, is_placeholder: payerFriend.is_placeholder });
            }
          }
        });
      }
      return list;
    }
  };

  // Calculate real-time split values for preview and validation
  const getRealTimeCalculatedSplits = () => {
    const members = getSplitMembers();
    if (members.length === 0) return [];
    const amt = parseFloat(txAmount) || 0;
    if (amt <= 0) return members.map(m => ({ ...m, owed_amount: 0 }));

    if (splitType === 'EQUALLY') {
      const share = parseFloat((amt / members.length).toFixed(4));
      return members.map((m, idx) => {
        const owed = idx === members.length - 1 
          ? parseFloat((amt - (share * (members.length - 1))).toFixed(4)) 
          : share;
        return { ...m, owed_amount: owed };
      });
    }

    if (splitType === 'EXACT') {
      return members.map(m => {
        const val = parseFloat(exactAmounts[m.user_id]) || 0;
        return { ...m, owed_amount: val };
      });
    }

    if (splitType === 'PERCENT') {
      let runningSum = 0;
      return members.map((m, idx) => {
        const pct = percentages[m.user_id] !== undefined ? percentages[m.user_id] : parseFloat((100 / members.length).toFixed(2));
        const owed = idx === members.length - 1 
          ? parseFloat((amt - runningSum).toFixed(4)) 
          : parseFloat(((amt * pct) / 100).toFixed(4));
        runningSum += owed;
        return { ...m, owed_amount: owed, percent: pct };
      });
    }

    if (splitType === 'SHARES') {
      let totalShares = 0;
      members.forEach(m => {
        totalShares += (shares[m.user_id] !== undefined ? shares[m.user_id] : 1);
      });
      if (totalShares <= 0) {
        return members.map(m => ({ ...m, owed_amount: 0 }));
      }
      let runningSum = 0;
      return members.map((m, idx) => {
        const sh = shares[m.user_id] !== undefined ? shares[m.user_id] : 1;
        const owed = idx === members.length - 1 
          ? parseFloat((amt - runningSum).toFixed(4)) 
          : parseFloat(((amt * sh) / totalShares).toFixed(4));
        runningSum += owed;
        return { ...m, owed_amount: owed, shares: sh };
      });
    }

    return [];
  };

  // Get filtered and sorted transactions for Transactions Feed tab
  const getFilteredTransactions = () => {
    let list = [...transactions];

    // Merge investment transactions as synthetic entries
    const invTxEntries = [];
    for (const inv of investments) {
      for (const t of (inv.transactions || [])) {
        // Find source label
        let srcLabel = 'N/A';
        if (t.source_type === 'ACCOUNT' || t.source_type === 'CREDIT_CARD') {
          const acc = accounts.find(a => a.account_id === t.source_id);
          srcLabel = acc ? `${acc.name} (${acc.currency})` : 'Account';
        } else if (t.source_type === 'FRIEND') {
          const fr = friends.find(f => f.user_id === t.source_id);
          srcLabel = fr ? fr.email : 'Friend';
        } else if (t.source_type === 'NONE') {
          srcLabel = 'Portfolio only';
        }

        const invCurr = inv.currency || 'AED';
        invTxEntries.push({
          transaction_id: `inv_${t.transaction_id}`,
          description: `${t.type === 'BUY' ? '📈 Buy' : t.type === 'SELL' ? '📉 Sell' : '💰 Dividend'} – ${inv.name} (${inv.symbol})`,
          category_name: t.type === 'DIVIDEND' ? 'Income from investments' : t.type === 'SELL' ? (parseFloat(t.realized_pnl) < 0 ? 'Loss from investments' : 'Income from investments') : 'Investments',
          total_amount: parseFloat(t.amount) || 0,
          currency: invCurr,
          date: t.transaction_date,
          paid_by_account_id: (t.source_type === 'ACCOUNT' || t.source_type === 'CREDIT_CARD') ? t.source_id : null,
          user_id: t.user_id,
          _isInvestmentTx: true,
          _invTxType: t.type,
          _invTxData: t,
          _invData: inv,
          _srcLabel: srcLabel
        });
      }
    }

    // Filter out core transactions that are duplicates of investment transactions
    // (investment transactions with source_type ACCOUNT/CREDIT_CARD/FRIEND create core tx records)
    const coreInvTxIds = new Set();
    for (const inv of investments) {
      for (const t of (inv.transactions || [])) {
        if (t.core_transaction_id) {
          coreInvTxIds.add(t.core_transaction_id);
        }
        if (t.pnl_core_transaction_id) {
          coreInvTxIds.add(t.pnl_core_transaction_id);
        }
      }
    }
    list = list.filter(tx => !coreInvTxIds.has(tx.transaction_id));

    // Add investment transactions to the list
    list = [...list, ...invTxEntries];
    
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      list = list.filter(tx => 
        tx.description.toLowerCase().includes(q) || 
        tx.category_name.toLowerCase().includes(q)
      );
    }
    
    if (filterType !== 'ALL') {
      const isOutflowCategory = (catName) => {
        const INFLOWS = ['Salary', 'Cashback', 'Income from investments', 'Other income', 'Credit Card Rewards', 'Investment Income', 'External Borrowers', 'Deposits', 'Crypto Profit'];
        const found = categories.find(c => (c.name || '').trim().toLowerCase() === (catName || '').trim().toLowerCase());
        if (found) return found.type !== 'INFLOW';
        return !INFLOWS.map(n => n.toLowerCase()).includes((catName || '').trim().toLowerCase());
      };
      list = list.filter(tx => {
        if (tx._isInvestmentTx) {
          // BUY = outflow, SELL/DIVIDEND = inflow
          const isOut = tx._invTxType === 'BUY';
          return filterType === 'OUTFLOW' ? isOut : !isOut;
        }
        if (isTransferTransaction(tx)) return false;
        const isOutflow = isOutflowCategory(tx.category_name);
        return filterType === 'OUTFLOW' ? isOutflow : !isOutflow;
      });
    }
    
    if (filterAccount !== 'ALL') {
      list = list.filter(tx => tx.paid_by_account_id === filterAccount);
    }
    
    if (filterCategory !== 'ALL') {
      if (filterCategory === 'Transfer') {
        list = list.filter(tx => !tx._isInvestmentTx && isTransferTransaction(tx));
      } else if (filterCategory === 'Investment') {
        list = list.filter(tx => tx._isInvestmentTx);
      } else {
        list = list.filter(tx => tx.category_name === filterCategory && !isTransferTransaction(tx));
      }
    }
    
    if (filterStartDate) {
      list = list.filter(tx => tx.date.split('T')[0] >= filterStartDate);
    }
    if (filterEndDate) {
      list = list.filter(tx => tx.date.split('T')[0] <= filterEndDate);
    }
    
    list.sort((a, b) => {
      let valA, valB;
      if (sortField === 'amount') {
        valA = parseFloat(a.total_amount);
        valB = parseFloat(b.total_amount);
      } else if (sortField === 'description') {
        valA = a.description.toLowerCase();
        valB = b.description.toLowerCase();
      } else {
        valA = new Date(a.date).getTime();
        valB = new Date(b.date).getTime();
      }
      
      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return list;
  };
  const handleLogout = () => {
    trackEvent('user_logged_out');
    resetUser();
    apiClient.logout();
    localStorage.removeItem('tezer_pin');
    localStorage.removeItem('dhudd_pin');
    setIsAuthenticated(false);
    setIsLocked(true);
    setPin('');
    showToast('Logged out successfully.', 'info');
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  // PIN keypad handling
  const handlePinPress = (num) => {
    if (pin.length < 4) {
      const newPin = pin + num;
      setPin(newPin);
      if (newPin === savedPin) {
        setTimeout(() => {
          setIsLocked(false);
          setPin('');
          showToast('Welcome to tezer', 'success');
        }, 150);
      } else if (newPin.length === 4) {
        setTimeout(() => {
          showToast('Incorrect PIN. Try again.', 'error');
          setPin('');
        }, 300);
      }
    }
  };

  // Formatter: Currency Values
  const getCurrencySymbol = (currency) => {
    const symbols = {
      'USD': '$',
      'INR': '₹',
      'EUR': '€',
      'GBP': '£',
      'AED': '\u20C3',
      'SAR': 'SAR',
      'JPY': '¥',
      'CAD': 'CA$',
      'AUD': 'A$',
      'CHF': 'CHF',
      'CNY': '¥'
    };
    return symbols[currency] || currency;
  };

  const formatInputValue = (val) => {
    if (val === null || val === undefined || val === '') return '';
    const num = parseFloat(val);
    if (isNaN(num)) return val;
    return Number(num.toFixed(2)).toString();
  };

  const formatMoney = (amount, currency = 'AED') => {
    const { decimalSeparator, delimiter, precision } = settings;
    const num = parseFloat(amount || 0);
    const fixed = num.toFixed(precision);
    const parts = fixed.split('.');
    
    let integerPart = parts[0];
    const decimalPart = parts[1] || '';
    
    const regex = /\B(?=(\d{3})+(?!\d))/g;
    integerPart = integerPart.replace(regex, delimiter);
    
    let result = integerPart;
    if (precision > 0) {
      result += decimalSeparator + decimalPart;
    }
    const symbol = getCurrencySymbol(currency);
    return `${symbol} ${result}`;
  };

  const DEFAULT_RATES = {
    USD: 3.6725,
    EUR: 4.0125,
    GBP: 4.7550,
    INR: 0.0441,
    CAD: 2.6850,
    AUD: 2.4210,
    SAR: 0.9793,
    QAR: 1.0089,
    KWD: 11.980,
    BHD: 9.7420,
    OMR: 9.5390,
    JPY: 0.0245,
    AED: 1.0
  };

  const convertCurrency = (amount, fromCurr = 'AED', toCurr = 'AED') => {
    const num = parseFloat(amount) || 0;
    if (!fromCurr || !toCurr || fromCurr.toUpperCase() === toCurr.toUpperCase()) return num;

    const fc = fromCurr.toUpperCase();
    const tc = toCurr.toUpperCase();

    // 1. Direct rate in currencyRates array
    const direct = currencyRates.find(r => r.from_currency === fc && r.to_currency === tc);
    if (direct && direct.rate && parseFloat(direct.rate) > 0) return num * parseFloat(direct.rate);

    // 2. Inverse rate
    const inverse = currencyRates.find(r => r.from_currency === tc && r.to_currency === fc);
    if (inverse && inverse.rate && parseFloat(inverse.rate) > 0) return num / parseFloat(inverse.rate);

    // 3. Via AED base
    const getRateToAed = (c) => {
      if (c === 'AED') return 1.0;
      const r = currencyRates.find(rate => rate.from_currency === c && rate.to_currency === 'AED');
      if (r && r.rate && parseFloat(r.rate) > 0) return parseFloat(r.rate);
      const inv = currencyRates.find(rate => rate.from_currency === 'AED' && rate.to_currency === c);
      if (inv && inv.rate && parseFloat(inv.rate) > 0) return 1 / parseFloat(inv.rate);
      return DEFAULT_RATES[c] || 1.0;
    };

    const rateFrom = getRateToAed(fc);
    const rateTo = getRateToAed(tc);
    if (rateTo === 0) return num;

    return (num * rateFrom) / rateTo;
  };

  const getLatestRecordedRate = (fromCurr, toCurr) => {
    if (!fromCurr || !toCurr || fromCurr.toUpperCase() === toCurr.toUpperCase()) return { rate: 1.0, isDirect: true };
    const fc = fromCurr.toUpperCase();
    const tc = toCurr.toUpperCase();

    // 1. Direct rate in currencyRates array
    const direct = currencyRates.find(r => r.from_currency === fc && r.to_currency === tc);
    if (direct && direct.rate && parseFloat(direct.rate) > 0) {
      return { rate: parseFloat(direct.rate), isDirect: true, date: direct.rate_date };
    }

    // 2. Inverse rate
    const inverse = currencyRates.find(r => r.from_currency === tc && r.to_currency === fc);
    if (inverse && inverse.rate && parseFloat(inverse.rate) > 0) {
      return { rate: 1 / parseFloat(inverse.rate), isInverse: true, date: inverse.rate_date };
    }

    // 3. Fallback conversion
    const converted = convertCurrency(1.0, fc, tc);
    return { rate: converted, isFallback: true };
  };

  // --- NOTIFICATIONS HANDLERS ---
  const handleMarkNotificationRead = async (id) => {
    try {
      if (id.startsWith('cc-')) {
        const dismissedList = JSON.parse(localStorage.getItem('dhudd_dismissed_cc_reminders') || '[]');
        if (!dismissedList.includes(id)) {
          dismissedList.push(id);
          localStorage.setItem('dhudd_dismissed_cc_reminders', JSON.stringify(dismissedList));
        }
        setNotifications(prev => [...prev]); // Trigger re-render
        return;
      }
      await apiClient.request(`/notifications/${id}/read`, 'POST');
      setNotifications(prev => prev.map(n => n.notification_id === id ? { ...n, read: true } : n));
      const cached = syncEngine.getLocal('dhudd_cache_notifications', []);
      const updated = cached.map(n => n.notification_id === id ? { ...n, read: true } : n);
      syncEngine.setLocal('dhudd_cache_notifications', updated);
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleClearNotifications = async () => {
    try {
      await apiClient.request('/notifications', 'DELETE');
      setNotifications([]);
      syncEngine.setLocal('dhudd_cache_notifications', []);
      
      // Clear CC reminders
      const ccAccounts = accounts.filter(a => a.type === 'CREDIT_CARD');
      const dismissedList = JSON.parse(localStorage.getItem('dhudd_dismissed_cc_reminders') || '[]');
      ccAccounts.forEach(card => {
        const stmtInfo = getCardStatementInfo(card, transactions);
        const dueAmt = stmtInfo.remainingStatementDue;
        if (dueAmt > 0 && stmtInfo.dueDaysLeft !== null && stmtInfo.dueDaysLeft >= 0 && stmtInfo.dueDaysLeft <= 5) {
          const reminderKey = `cc-${card.account_id}-${card.due_day}-${stmtInfo.lastStatementDate?.toISOString()?.slice(0, 10)}-${dueAmt.toFixed(2)}`;
          if (!dismissedList.includes(reminderKey)) {
            dismissedList.push(reminderKey);
          }
        }
      });
      localStorage.setItem('dhudd_dismissed_cc_reminders', JSON.stringify(dismissedList));
      
      showToast('Notifications cleared', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };


  // --- MULTI-CURRENCY DASHBOARD BALANCES ---
  const getNetBalancesByCurrency = () => {
    if (!selectedAccountIds.includes('ALL')) {
      if (selectedAccountIds.length === 0) return [];
      const balances = {};
      accounts.forEach(acc => {
        if (selectedAccountIds.includes(acc.account_id)) {
          const factor = acc.type === 'CREDIT_CARD' ? -1 : 1;
          balances[acc.currency] = (balances[acc.currency] || 0) + (factor * parseFloat(acc.current_balance));
        }
      });
      return Object.keys(balances).map(cur => ({
        currency: cur,
        amount: balances[cur]
      }));
    }
    const balances = {};
    accounts.forEach(acc => {
      if (acc.include_in_total && acc.is_active !== false && acc.is_active !== 0 && acc.is_active !== '0') {
        const factor = acc.type === 'CREDIT_CARD' ? -1 : 1;
        balances[acc.currency] = (balances[acc.currency] || 0) + (factor * parseFloat(acc.current_balance));
      }
    });
    return Object.keys(balances).map(cur => ({
      currency: cur,
      amount: balances[cur]
    }));
  };

  const getOverallBalancesByCurrency = () => {
    const balances = {};
    accounts.forEach(acc => {
      if (acc.is_active !== false && acc.is_active !== 0 && acc.is_active !== '0') {
        const factor = acc.type === 'CREDIT_CARD' ? -1 : 1;
        balances[acc.currency] = (balances[acc.currency] || 0) + (factor * parseFloat(acc.current_balance));
      }
    });
    return Object.keys(balances).map(cur => ({
      currency: cur,
      amount: balances[cur]
    }));
  };

  const isTransferTransaction = (tx) => {
    if (!tx) return false;
    return !!tx.transfer_to_account_id || tx.category_name === 'Deposits' || (tx.description && tx.description.toLowerCase().includes('transfer'));
  };

  const getTransferDirection = (tx, accs = accounts) => {
    if (!tx) return 'none';
    const isTransfer = isTransferTransaction(tx) || (tx.description && tx.description.toLowerCase().includes('settlement'));
    if (!isTransfer) return 'none';

    const hasPaidAcc = !!tx.paid_by_account_id;
    const hasToAcc = !!tx.transfer_to_account_id;
    const desc = (tx.description || '').toLowerCase();

    // Check if both accounts belong to current user (self transfer)
    const ownPaidAcc = accs.some(a => a.account_id === tx.paid_by_account_id);
    const ownToAcc = accs.some(a => a.account_id === tx.transfer_to_account_id && a.account_id !== 'VIRTUAL_DEBT');
    if (hasPaidAcc && hasToAcc && ownPaidAcc && ownToAcc) {
      return 'self';
    }

    // Heuristics based on description (prioritized to catch settlements and legacy records)
    if (desc.includes('transferred to you') || desc.includes('received from') || desc.includes('transfer from') || desc.includes('settlement from') || desc.includes('incoming')) {
      return 'inward';
    }
    if (desc.includes('settlement to') || desc.includes('transfer to') || desc.includes('paid to')) {
      return 'outward';
    }

    // If paid_by_account_id is explicitly set and belongs to this user, it's an Outward debit
    if (hasPaidAcc && !hasToAcc) return 'outward';
    // If transfer_to_account_id is explicitly set and no paid_by_account_id, it's an Inward credit
    if (hasToAcc && !hasPaidAcc) return 'inward';

    // Fallback
    if (tx.category_name === 'Deposits' && !hasPaidAcc) return 'inward';
    return 'outward';
  };

  const getTransferCounterparty = (tx, dir = 'none') => {
    if (!tx) return 'Friend';
    const myUserId = localStorage.getItem('dhudd_user_id');

    // 1. Check if there are splits
    if (tx.splits && Array.isArray(tx.splits) && tx.splits.length > 0) {
      const friendSplit = tx.splits.find(s => s.member_user_id !== myUserId);
      if (friendSplit) {
        return resolveMemberDisplayName(friendSplit.member_user_id, friendSplit.display_name, friendSplit.email);
      }
    }

    // 2. Check if paid_by_user_id is a friend
    if (tx.paid_by_user_id && tx.paid_by_user_id !== myUserId) {
      const fr = friends.find(f => f.friend_user_id === tx.paid_by_user_id || f.user_id === tx.paid_by_user_id);
      if (fr) return fr.nickname || fr.display_name || (fr.email ? fr.email.split('@')[0] : 'Friend');
    }

    // 3. Check if linked transactions exist (parent/child)
    const linked = transactions.find(t => 
      t.transaction_id !== tx.transaction_id && (
        t.transaction_id === tx.source_transaction_id || 
        t.source_transaction_id === tx.transaction_id
      )
    );
    if (linked && linked.user_id && linked.user_id !== myUserId) {
      const fr = friends.find(f => f.friend_user_id === linked.user_id || f.user_id === linked.user_id);
      if (fr) return fr.nickname || fr.display_name || (fr.email ? fr.email.split('@')[0] : 'Friend');
    }

    // 4. Extract from description heuristics
    const desc = tx.description || '';
    if (desc.includes(' from ')) {
      const parts = desc.split(/ from /i);
      if (parts[1]) return parts[1].trim();
    }
    if (desc.includes(' to ')) {
      const parts = desc.split(/ to /i);
      if (parts[1]) return parts[1].trim();
    }
    if (desc.includes('Received from ')) {
      return desc.replace(/Received from /i, '').trim();
    }
    if (desc.includes('Paid to ')) {
      return desc.replace(/Paid to /i, '').trim();
    }
    if (desc.includes('Settlement: ')) {
      return desc.replace(/Settlement: /i, '').replace(/Received from /i, '').replace(/Paid to /i, '').trim();
    }

    return 'Friend';
  };

  // Comprehensive multi-currency transfer resolver helper
  const getTransferDetails = useCallback((tx, contextAcc = null) => {
    if (!tx) return null;
    const isTransfer = isTransferTransaction(tx) || (tx.description && (tx.description.toLowerCase().includes('settlement') || tx.description.toLowerCase().includes('transfer')));
    const dir = getTransferDirection(tx, accounts);
    const counterparty = getTransferCounterparty(tx, dir);

    const sAcc = accounts.find(a => a.account_id === tx.paid_by_account_id);
    const dAcc = accounts.find(a => a.account_id === tx.transfer_to_account_id);

    let sourceName = '';
    let sourceAmount = parseFloat(tx.total_amount || 0);
    let sourceCurrency = tx.currency || 'AED';

    let targetName = '';
    let targetAmount = tx.transfer_to_amount ? parseFloat(tx.transfer_to_amount) : parseFloat(tx.total_amount || 0);
    let targetCurrency = dAcc?.currency || tx.currency || 'AED';

    if (dir === 'self') {
      sourceName = sAcc ? sAcc.name : 'Source Account';
      sourceCurrency = sAcc?.currency || tx.currency;
      sourceAmount = parseFloat(tx.total_amount || 0);

      targetName = dAcc ? dAcc.name : 'Target Account';
      targetCurrency = dAcc?.currency || sourceCurrency;
      targetAmount = tx.transfer_to_amount ? parseFloat(tx.transfer_to_amount) : sourceAmount;
    } else if (dir === 'inward') {
      sourceName = counterparty;
      sourceCurrency = tx.currency;
      sourceAmount = parseFloat(tx.total_amount || 0);

      const targetAcc = dAcc || sAcc || (contextAcc && (contextAcc.account_id === tx.transfer_to_account_id || contextAcc.account_id === tx.paid_by_account_id) ? contextAcc : null);
      targetName = targetAcc ? targetAcc.name : 'Your Account';
      targetCurrency = targetAcc?.currency || tx.currency;
      targetAmount = (tx.transfer_to_amount && targetAcc && targetAcc.currency !== tx.currency)
        ? parseFloat(tx.transfer_to_amount)
        : (tx.transfer_to_amount ? parseFloat(tx.transfer_to_amount) : parseFloat(tx.total_amount || 0));
    } else if (dir === 'outward') {
      const srcAcc = sAcc || dAcc || (contextAcc && (contextAcc.account_id === tx.paid_by_account_id || contextAcc.account_id === tx.transfer_to_account_id) ? contextAcc : null);
      sourceName = srcAcc ? srcAcc.name : 'Your Account';
      sourceCurrency = srcAcc?.currency || tx.currency;

      targetName = counterparty;
      targetCurrency = tx.currency;
      targetAmount = parseFloat(tx.total_amount || 0);

      if (tx.transfer_to_amount && srcAcc && srcAcc.currency !== tx.currency) {
        sourceAmount = parseFloat(tx.transfer_to_amount);
      } else {
        sourceAmount = parseFloat(tx.total_amount || 0);
      }
    } else {
      sourceName = sAcc ? sAcc.name : getPayerName(tx);
      targetName = dAcc ? dAcc.name : counterparty;
    }

    const isInterCurrency = sourceCurrency !== targetCurrency || (sourceAmount !== targetAmount && Math.abs(sourceAmount - targetAmount) > 0.001);

    let exchangeRateStr = '';
    if (isInterCurrency && sourceAmount > 0 && targetAmount > 0) {
      const rate1 = (targetAmount / sourceAmount).toFixed(4);
      const rate2 = (sourceAmount / targetAmount).toFixed(2);
      exchangeRateStr = `1 ${sourceCurrency} = ${rate1} ${targetCurrency} (1 ${targetCurrency} = ${rate2} ${sourceCurrency})`;
    }

    let accountDisplayAmount = parseFloat(tx.total_amount || 0);
    let accountDisplayCurrency = tx.currency;
    let accountPrefix = '-';
    let accountColor = 'var(--color-outflow)';

    if (contextAcc) {
      const isTarget = tx.transfer_to_account_id === contextAcc.account_id || (dir === 'inward' && (tx.paid_by_account_id === contextAcc.account_id || !tx.paid_by_account_id));
      if (isTarget) {
        accountPrefix = '+';
        accountColor = 'var(--color-inflow)';
        accountDisplayCurrency = contextAcc.currency || targetCurrency;
        accountDisplayAmount = (tx.transfer_to_amount && contextAcc.currency !== tx.currency)
          ? parseFloat(tx.transfer_to_amount)
          : (dir === 'self' && tx.transfer_to_amount ? parseFloat(tx.transfer_to_amount) : parseFloat(tx.total_amount || 0));
      } else {
        accountPrefix = '-';
        accountColor = 'var(--color-outflow)';
        accountDisplayCurrency = contextAcc.currency || sourceCurrency;
        accountDisplayAmount = (tx.transfer_to_amount && contextAcc.currency !== tx.currency)
          ? parseFloat(tx.transfer_to_amount)
          : parseFloat(tx.total_amount || 0);
      }
    }

    return {
      isTransfer,
      dir,
      counterparty,
      sAcc,
      dAcc,
      sourceName,
      sourceAmount,
      sourceCurrency,
      targetName,
      targetAmount,
      targetCurrency,
      isInterCurrency,
      exchangeRateStr,
      accountDisplayAmount,
      accountDisplayCurrency,
      accountPrefix,
      accountColor
    };
  }, [accounts, friends, transactions]);

  // --- TRANSACTION EDITING HANDLERS ---
  const handleStartEdit = (tx) => {
    setSelectedTxDetail(null);
    setEditingTx(tx);
    setEditTxDesc(tx.description);
    setEditTxAmount(formatInputValue(tx.total_amount));
    setEditTxCurrency(tx.currency);
    
    const myUserId = localStorage.getItem('dhudd_user_id') || 'self';
    const myAcc = accounts.find(a => a.currency === tx.currency && a.is_active !== false && a.is_active !== 0 && a.type !== 'CREDIT_CARD' && !(a.name && a.name.includes('External Debts'))) || accounts.find(a => !(a.name && a.name.includes('External Debts')));
    
    const isTxTransfer = isTransferTransaction(tx) || (tx.description && tx.description.toLowerCase().includes('settlement'));
    const transferDirection = isTxTransfer ? getTransferDirection(tx, accounts) : 'none';

    const isToAccExternal = tx.transfer_to_account_id === 'VIRTUAL_DEBT' || accounts.some(a => a.account_id === tx.transfer_to_account_id && a.name && a.name.includes('External Debts'));
    const isPaidAccExternal = tx.paid_by_account_id === 'VIRTUAL_DEBT' || accounts.some(a => a.account_id === tx.paid_by_account_id && a.name && a.name.includes('External Debts'));

    if (transferDirection === 'inward') {
      setEditTxAccountId('');
      setEditTxTransferToAccountId(isToAccExternal ? 'VIRTUAL_DEBT' : (tx.transfer_to_account_id || ''));
    } else if (transferDirection === 'outward') {
      setEditTxAccountId(isPaidAccExternal ? 'VIRTUAL_DEBT' : (tx.paid_by_account_id || ''));
      setEditTxTransferToAccountId('');
    } else {
      setEditTxAccountId(tx.paid_by_account_id || (myAcc ? myAcc.account_id : ''));
      setEditTxTransferToAccountId(tx.transfer_to_account_id || '');
    }

    const matchedCategory = findCategoryByName(categories, tx.category_name);
    setEditTxCategory(matchedCategory ? matchedCategory.name : (tx.category_name || ''));
    const txDateFormatted = tx.date ? new Date(tx.date).toISOString().split('T')[0] : '';
    setEditTxDate(txDateFormatted);
    setEditTxIsShared(tx.is_shared === true || tx.is_shared === 1);
    setEditTxIsRecurring(tx.is_recurring === true || tx.is_recurring === 1);
    setEditTxRecurringPeriod(tx.recurring_period || 'MONTH');
    
    const payerId = tx.paid_by_user_id || tx.user_id;
    const multiPayers = {};
    let totalPayersCount = 0;

    if (tx.payer_shares && typeof tx.payer_shares === 'object' && Object.keys(tx.payer_shares).length > 0) {
      for (const [uid, val] of Object.entries(tx.payer_shares)) {
        const num = parseFloat(val) || 0;
        if (num > 0) {
          const key = (uid === myUserId || uid === 'self') ? 'self' : uid;
          multiPayers[key] = num;
          totalPayersCount++;
        }
      }
    } else if (tx.splits && Array.isArray(tx.splits) && tx.splits.some(s => parseFloat(s.paid_amount || 0) > 0)) {
      tx.splits.forEach(s => {
        const paidAmt = parseFloat(s.paid_amount || 0);
        if (paidAmt > 0) {
          const key = (s.member_user_id === myUserId) ? 'self' : s.member_user_id;
          multiPayers[key] = paidAmt;
          totalPayersCount++;
        }
      });
    } else {
      const singlePayer = tx.paid_by_user_id || tx.user_id;
      const key = (singlePayer === myUserId || singlePayer === 'self') ? 'self' : singlePayer;
      if (key) {
        const amt = parseFloat(tx.total_amount) || 0;
        multiPayers[key] = amt;
        totalPayersCount = 1;
      }
    }

    setEditTxMultiPayers(multiPayers);

    if (totalPayersCount > 1 || (multiPayers['self'] > 0 && Object.keys(multiPayers).length > 1)) {
      setEditTxPayerType('both');
    } else if (multiPayers['self'] > 0 && Object.keys(multiPayers).length === 1) {
      setEditTxPayerType('you');
      setEditTxPayerUserId('self');
    } else {
      const friendPayerId = Object.keys(multiPayers).find(k => k !== 'self') || tx.paid_by_user_id || tx.user_id;
      setEditTxPayerType('friend');
      setEditTxPayerUserId(friendPayerId);
    }
    
    if (tx.group_id) {
      setEditTxSplitTargetType('GROUP');
      setEditTxGroupId(tx.group_id);
      if (tx.splits) {
        setEditTxSplitGroupMembers(tx.splits.map(s => s.member_user_id));
      }
    } else {
      setEditTxSplitTargetType('FRIENDS');
      setEditTxGroupId('');
      if (tx.splits) {
        const friendIds = tx.splits
          .map(s => s.member_user_id)
          .filter(id => id !== myUserId);
        setEditTxSplitFriends(friendIds);
      }
    }

    setEditTxSplitType(tx.split_type || 'EQUALLY');

    const exacts = {};
    const pcts = {};
    const shrs = {};
    if (tx.splits) {
      tx.splits.forEach(s => {
        exacts[s.member_user_id] = parseFloat(s.owed_amount);
        if (tx.split_type === 'PERCENT') {
          pcts[s.member_user_id] = tx.total_amount > 0 ? Math.round((parseFloat(s.owed_amount) / parseFloat(tx.total_amount)) * 100) : 0;
        } else {
          pcts[s.member_user_id] = 100 / tx.splits.length;
        }
        if (tx.split_type === 'SHARES') {
          shrs[s.member_user_id] = 1;
        } else {
          shrs[s.member_user_id] = 1;
        }
      });
    }
    setEditTxExactAmounts(exacts);
    setEditTxPercentages(pcts);
    setEditTxShares(shrs);

    // Reset advanced options
    setEditTxUseSplitSources(false);
    setEditSplitTxSources([{ accountId: '', amount: '' }]);
    setEditTxCharges('');
    setEditShowMoreTxOptions(false);
  };

  const getEditSplitMembers = () => {
    const myUserId = localStorage.getItem('dhudd_user_id') || '';
    const myEmail = localStorage.getItem('dhudd_email') || 'You';
    const myDisplayName = localStorage.getItem('dhudd_display_name') || 'You';

    if (editTxSplitTargetType === 'GROUP') {
      const g = groups.find(grp => grp.group_id === editTxGroupId);
      if (!g || !g.members) return [];
      return g.members.filter(m => editTxSplitGroupMembers.includes(m.user_id));
    } else {
      const list = [{ user_id: myUserId, email: myEmail, display_name: myDisplayName }];
      editTxSplitFriends.forEach(fid => {
        const f = friends.find(fr => fr.user_id === fid);
        if (f) {
          list.push({ user_id: f.user_id, email: f.email, display_name: f.display_name, is_placeholder: f.is_placeholder });
        } else {
          const splitObj = editingTx?.splits?.find(s => s.member_user_id === fid);
          if (splitObj) {
            list.push({ user_id: fid, email: splitObj.email || '', display_name: splitObj.display_name || splitObj.email || 'Friend', is_placeholder: false });
          }
        }
      });
      return list;
    }
  };

  const getEditRealTimeCalculatedSplits = () => {
    const amt = parseFloat(editTxAmount) || 0;
    const members = getEditSplitMembers();
    if (members.length === 0) return [];

    if (editTxSplitType === 'EQUALLY') {
      const perPerson = amt / members.length;
      return members.map(m => ({
        user_id: m.user_id,
        email: m.email,
        owed_amount: parseFloat(perPerson.toFixed(4))
      }));
    } else if (editTxSplitType === 'EXACT') {
      return members.map(m => ({
        user_id: m.user_id,
        email: m.email,
        owed_amount: parseFloat((editTxExactAmounts[m.user_id] || 0).toFixed(4))
      }));
    } else if (editTxSplitType === 'PERCENT') {
      return members.map(m => {
        const pct = editTxPercentages[m.user_id] || 0;
        const owed = (pct / 100) * amt;
        return {
          user_id: m.user_id,
          email: m.email,
          percent: pct,
          owed_amount: parseFloat(owed.toFixed(4))
        };
      });
    } else if (editTxSplitType === 'SHARES') {
      const totalShares = members.reduce((sum, m) => sum + (editTxShares[m.user_id] !== undefined ? editTxShares[m.user_id] : 1), 0);
      return members.map(m => {
        const sh = editTxShares[m.user_id] !== undefined ? editTxShares[m.user_id] : 1;
        const owed = totalShares > 0 ? (sh / totalShares) * amt : 0;
        return {
          user_id: m.user_id,
          email: m.email,
          share: sh,
          owed_amount: parseFloat(owed.toFixed(4))
        };
      });
    }
    return [];
  };

  const handleEditTxDescChange = (e) => {
    const val = e.target.value;
    setEditTxDesc(val);
    const foundCat = findCategoryByName(categories, editTxCategory);
    const isOutflow = foundCat ? foundCat.type === 'OUTFLOW' : !GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (editTxCategory || '').trim().toLowerCase());
    const predictedCat = getAutoCategory(val, isOutflow ? 'OUTFLOW' : 'INFLOW');
    if (predictedCat) {
      setEditTxCategory(predictedCat);
    }
    
    // Autocomplete suggestions
    if (val.trim()) {
      const uniqueDescriptions = Array.from(new Set(transactions.map(t => t.description).filter(Boolean)));
      const filtered = uniqueDescriptions.filter(desc => 
        desc.toLowerCase().includes(val.toLowerCase()) && 
        desc.toLowerCase() !== val.toLowerCase()
      ).slice(0, 5);
      setDescSuggestions(filtered);
    } else {
      setDescSuggestions([]);
    }
  };

  const handleUpdateTransaction = async (e) => {
    e.preventDefault();
    if (isSubmittingTx) return;
    if (!editingTx) return;

    const amt = parseFloat(editTxAmount);
    if (!amt || amt <= 0) return showToast('Enter a valid amount', 'error');

    setIsSubmittingTx(true);

    const calculated = getEditRealTimeCalculatedSplits();
    if (editTxIsShared) {
      if (calculated.length === 0) {
        return showToast('You must select at least one person to split with', 'error');
      }
      if (editTxSplitType === 'EXACT') {
        const sum = calculated.reduce((sumVal, m) => sumVal + m.owed_amount, 0);
        if (Math.abs(sum - amt) > 0.01) {
          return showToast('Sum of exact amounts must equal the total amount', 'error');
        }
      } else if (editTxSplitType === 'PERCENT') {
        const sumPct = calculated.reduce((sumVal, m) => sumVal + (m.percent || 0), 0);
        if (Math.abs(sumPct - 100) > 0.01) {
          return showToast('Sum of percentages must equal 100%', 'error');
        }
      }
    }

    const isTxTransfer = editingTx && (isTransferTransaction(editingTx) || (editingTx.description && editingTx.description.toLowerCase().includes('settlement')));
    const transferDirection = isTxTransfer ? getTransferDirection(editingTx, accounts) : 'none';
    const isIncoming = transferDirection === 'inward';
    const isOutward = transferDirection === 'outward';

    let splitDetailsPayload = null;
    let friendsToSplit = null;
    const myUserId = localStorage.getItem('dhudd_user_id');

    if (editTxIsShared && !isTxTransfer) {
      if (editTxSplitTargetType === 'GROUP') {
        const memberIds = getEditSplitMembers().map(m => m.user_id);
        splitDetailsPayload = {
          members: memberIds,
          exactAmounts: editTxSplitType === 'EXACT' ? editTxExactAmounts : null,
          percentages: editTxSplitType === 'PERCENT' ? editTxPercentages : null,
          shares: editTxSplitType === 'SHARES' ? editTxShares : null
        };
      } else {
        const friendIds = editTxSplitFriends;
        friendsToSplit = friendIds;
        splitDetailsPayload = {
          members: [myUserId, ...friendIds],
          exactAmounts: editTxSplitType === 'EXACT' ? editTxExactAmounts : null,
          percentages: editTxSplitType === 'PERCENT' ? editTxPercentages : null,
          shares: editTxSplitType === 'SHARES' ? editTxShares : null
        };
      }
    }

    let myPaidAmount = amt;
    let parsedPayerShares = null;
    let finalIsShared = editTxIsShared;
    let finalSplitType = editTxSplitType;

    if (!isTxTransfer && (editTxPayerType === 'both' || editTxPayerType === 'friend')) {
      parsedPayerShares = {};
      let sum = 0;
      for (const [uid, val] of Object.entries(editTxMultiPayers)) {
        const num = parseFloat(val) || 0;
        if (num > 0) {
          const actualUid = uid === 'self' ? myUserId : uid;
          parsedPayerShares[actualUid] = num;
          sum += num;
        }
      }
      myPaidAmount = editTxPayerType === 'friend' ? 0 : parseFloat(editTxMultiPayers['self'] || 0);

      if (Math.abs(sum - amt) > 0.01) {
        return showToast(`Payers sum (${sum.toFixed(2)}) must equal Total Amount (${amt.toFixed(2)})`, 'error');
      }

      if (!editTxIsShared) {
        finalIsShared = true;
        const friendsInvolved = Object.keys(editTxMultiPayers).filter(id => id !== 'self' && parseFloat(editTxMultiPayers[id]) > 0);
        friendsToSplit = friendsInvolved;
        finalSplitType = 'EXACT';
        const exactAmountsMap = { [myUserId]: amt };
        for (const fid of friendsInvolved) {
          exactAmountsMap[fid] = 0;
        }
        splitDetailsPayload = {
          members: [myUserId, ...friendsInvolved],
          exactAmounts: exactAmountsMap
        };
      }
    }

    let singlePayerId = null;
    if (editTxPayerType === 'you') {
      singlePayerId = myUserId;
    } else if (editTxPayerType === 'friend') {
      const friendPayers = Object.keys(editTxMultiPayers).filter(id => id !== 'self' && parseFloat(editTxMultiPayers[id]) > 0);
      if (friendPayers.length === 1 && myPaidAmount === 0) {
        singlePayerId = friendPayers[0];
      } else if (editTxPayerUserId && editTxPayerUserId !== 'self') {
        singlePayerId = editTxPayerUserId;
      }
    }

    const payload = {
      description: editTxDesc,
      total_amount: amt,
      currency: editTxCurrency,
      date: new Date(editTxDate).toISOString(),
      paid_by_account_id: isIncoming ? null : (myPaidAmount > 0 ? (editTxAccountId || null) : null),
      paid_by_user_id: isIncoming ? null : (isTxTransfer ? (editingTx.paid_by_user_id || singlePayerId) : singlePayerId),
      payer_shares: isTxTransfer ? null : parsedPayerShares,
      is_shared: finalIsShared,
      group_id: (!isTxTransfer && finalIsShared && editTxSplitTargetType === 'GROUP') ? editTxGroupId : null,
      category_name: editTxCategory,
      is_recurring: editTxIsRecurring,
      recurring_period: editTxIsRecurring ? editTxRecurringPeriod : null,
      split_type: (isTxTransfer || !finalIsShared) ? null : finalSplitType,
      split_details: isTxTransfer ? null : splitDetailsPayload,
      friends_to_split: isTxTransfer ? null : friendsToSplit,
      transfer_to_account_id: isIncoming ? (editTxTransferToAccountId || null) : (editTxTransferToAccountId || null),
      transfer_to_amount: (isIncoming || editTxTransferToAccountId) ? amt : undefined
    };

    try {
      const res = await syncEngine.updateTransactionCached(editingTx.transaction_id, payload);
      if (res && res.offline) {
        showToast('Updated offline — will sync when reconnected', 'info');
      } else {
        showToast('Transaction updated successfully!', 'success');
      }
      setEditingTx(null);
      refreshData();
      if (payload.group_id || editingTx?.group_id || activeGroupRef.current?.group_id) {
        reloadActiveGroupBalances(payload.group_id || editingTx?.group_id || activeGroupRef.current?.group_id);
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setIsSubmittingTx(false);
    }
  };

  const formatDateDDMMYYYY = (dateVal, timeZone) => {
    if (!dateVal) return '';
    if (typeof dateVal === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateVal.trim())) {
      const [y, m, d] = dateVal.trim().split('-');
      return `${d}/${m}/${y}`;
    }
    const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
    if (isNaN(d.getTime())) return '';

    let tz = timeZone || selectedTimezone;
    if (tz === 'local' || !tz) {
      try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (e) {
        tz = undefined;
      }
    }

    try {
      const opts = { day: '2-digit', month: '2-digit', year: 'numeric' };
      if (tz) opts.timeZone = tz;
      return d.toLocaleDateString('en-GB', opts);
    } catch (e) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    }
  };

  const getRelativeDateStr = (dateVal) => {
    if (!dateVal) return 'Today';
    const txDay = formatDateDDMMYYYY(dateVal, selectedTimezone);
    const todayDay = formatDateDDMMYYYY(new Date(), selectedTimezone);
    if (txDay === todayDay) return 'Today';
    const yest = new Date();
    yest.setDate(yest.getDate() - 1);
    const yestDay = formatDateDDMMYYYY(yest, selectedTimezone);
    if (txDay === yestDay) return 'Yesterday';
    return txDay;
  };

  const isSimilarText = (t1, t2) => {
    const s1 = (t1 || '').toString().toLowerCase().trim();
    const s2 = (t2 || '').toString().toLowerCase().trim();
    if (!s1 || !s2) return false;
    if (s1 === s2) return true;
    if (s1.includes(s2) || s2.includes(s1)) return true;

    // Token set overlap
    const extractTokens = (s) => s.split(/[\s,._\-➔:;/()]+/g).filter(w => w.length > 1);
    const toks1 = new Set(extractTokens(s1));
    const toks2 = new Set(extractTokens(s2));
    if (toks1.size === 0 || toks2.size === 0) return false;

    let common = 0;
    toks1.forEach(t => { if (toks2.has(t)) common++; });
    const jaccard = common / (toks1.size + toks2.size - common);
    return jaccard >= 0.40;
  };

  const findSimilarTransaction = ({
    amount,
    currency,
    date,
    description,
    type, // 'expense' | 'income' | 'transfer'
    excludeTxId,
    ignorePendingActions = false,
    fromFriendId,
    toFriendId
  }) => {
    const targetAmt = parseFloat(amount || 0);
    if (isNaN(targetAmt) || targetAmt <= 0) return null;
    const targetCurr = (currency || preferredCurrencies[0] || 'AED').toUpperCase();
    const targetDay = formatDateDDMMYYYY(date, selectedTimezone);

    // 1. Check in transactions list
    if (Array.isArray(transactions)) {
      for (const tx of transactions) {
        if (excludeTxId && (tx.transaction_id === excludeTxId || tx.source_transaction_id === excludeTxId)) continue;

        // Condition 1: Same amount & currency
        const txAmt = parseFloat(tx.total_amount || 0);
        const txCurr = (tx.currency || 'AED').toUpperCase();
        if (Math.abs(txAmt - targetAmt) > 0.01 || txCurr !== targetCurr) continue;

        // Condition 2: Same date (same calendar day)
        const txDay = formatDateDDMMYYYY(tx.date, selectedTimezone);
        if (txDay !== targetDay) continue;

        // Condition 3: Similar or same description
        const isTxTransfer = isTransferTransaction(tx);
        let descMatches = false;

        if (type === 'transfer' || isTxTransfer) {
          if (isSimilarText(description, tx.description)) {
            descMatches = true;
          } else if (type === 'transfer' && isTxTransfer) {
            const candDesc = (description || '').toLowerCase();
            const txDescLower = (tx.description || '').toLowerCase();
            if (candDesc && txDescLower) {
              const candToks = candDesc.split(/[\s,._\-➔:;/()]+/g).filter(w => w.length > 2 && !['transfer', 'from', 'to'].includes(w));
              const txToks = txDescLower.split(/[\s,._\-➔:;/()]+/g).filter(w => w.length > 2 && !['transfer', 'from', 'to'].includes(w));
              if (candToks.length > 0 && txToks.length > 0 && candToks.some(t => txToks.includes(t))) {
                descMatches = true;
              }
            }
          }
        } else {
          descMatches = isSimilarText(description, tx.description);
        }

        if (descMatches) {
          const isOut = !GLOBAL_INFLOW_CATEGORIES.includes(tx.category_name) && !inflowCats.some(c => c.name === tx.category_name);
          const txTypeLabel = isTxTransfer ? 'transfer' : (isOut ? 'expense' : 'income');
          const relDate = getRelativeDateStr(tx.date);
          const amtFormatted = (txAmt % 1 === 0 ? parseInt(txAmt, 10) : txAmt.toFixed(2));
          let displayDesc = tx.description;
          if (isTxTransfer && (!displayDesc || displayDesc === 'Inter-Account Transfer')) {
            const srcAcc = accounts.find(a => a.account_id === tx.paid_by_account_id)?.name || 'Account';
            const dstAcc = accounts.find(a => a.account_id === tx.transfer_to_account_id)?.name || 'Account';
            displayDesc = `From ${srcAcc} to ${dstAcc}`;
          }
          return {
            matchedTx: tx,
            typeLabel: txTypeLabel,
            amount: amtFormatted,
            currency: txCurr,
            relativeDate: relDate,
            description: displayDesc || '(No description)',
            source: 'transaction'
          };
        }
      }
    }

    // 2. Check in pendingActions list (friend-recorded transactions awaiting user)
    if (!ignorePendingActions && Array.isArray(pendingActions)) {
      for (const pa of pendingActions) {
        // Condition 1: Same amount & currency
        const paAmt = parseFloat(pa.amount || 0);
        const paCurr = (pa.currency || 'AED').toUpperCase();
        if (Math.abs(paAmt - targetAmt) > 0.01 || paCurr !== targetCurr) continue;

        // Condition 2: Same date (same calendar day)
        const paDate = pa.source_tx_date || pa.created_at;
        const paDay = formatDateDDMMYYYY(paDate, selectedTimezone);
        if (paDay !== targetDay) continue;

        // Condition 3: Similar or same description
        const isPaTransfer = pa.type === 'OUTGOING_PAYMENT' || pa.type === 'TRANSFER';
        let descMatches = isSimilarText(description, pa.description);
        if (!descMatches && (type === 'transfer' || isPaTransfer)) {
          const candDesc = (description || '').toLowerCase();
          const srcName = (pa.source_display_name || '').toLowerCase();
          if (srcName && candDesc.includes(srcName)) {
            descMatches = true;
          }
        }

        if (descMatches) {
          const typeLabel = isPaTransfer ? 'transfer' : 'expense';
          const relDate = getRelativeDateStr(paDate);
          const amtFormatted = (paAmt % 1 === 0 ? parseInt(paAmt, 10) : paAmt.toFixed(2));
          return {
            matchedTx: pa,
            typeLabel: typeLabel,
            amount: amtFormatted,
            currency: paCurr,
            relativeDate: relDate,
            description: pa.description || `From ${pa.source_display_name || 'Friend'}`,
            source: 'pending_action'
          };
        }
      }
    }

    return null;
  };

  const getUserReportName = () => {
    if (profileDisplayName && profileDisplayName.trim()) {
      return profileDisplayName.trim();
    }
    const cachedName = localStorage.getItem('dhudd_cache_profile_display_name') || 
                       localStorage.getItem('tezer_display_name') || 
                       localStorage.getItem('dhudd_display_name');
    if (cachedName && cachedName.trim()) {
      return cachedName.trim();
    }
    const email = localStorage.getItem('tezer_email') || localStorage.getItem('dhudd_email') || profileEmail || '';
    if (email && email.includes('@')) {
      const prefix = email.split('@')[0];
      return prefix.charAt(0).toUpperCase() + prefix.slice(1);
    }
    return 'User';
  };

  const handleExportExcel = () => {
    const list = getFilteredTransactions();
    if (list.length === 0) {
      return showToast('No transactions to export', 'error');
    }

    const accRunningMaps = {};
    accounts.forEach(acc => {
      accRunningMaps[acc.account_id] = computeAccountRunningBalances(acc, transactions);
    });

    const headers = ['Date', 'Description', 'Category', 'Account', 'Amount', 'Running Balance', 'Currency', 'Flow Type', 'Details'];
    const escapeCsv = (val) => '"' + String(val || '').replace(/"/g, '""') + '"';

    const INFLOWS = ['Salary', 'Cashback', 'Income from investments', 'Other income', 'Credit Card Rewards', 'Investment Income', 'External Borrowers', 'Deposits', 'Crypto Profit'];

    const rows = list.map(tx => {
      const isTransfer = isTransferTransaction(tx);
      
      const dateStr = formatDateTime(tx.date);
      const desc = tx.description;
      const cat = isTransfer ? 'Transfer' : tx.category_name;
      
      const acc = tx.paid_by_account_id === 'VIRTUAL_DEBT' ? "Friend's Virtual Balance" : (accounts.find(a => a.account_id === tx.paid_by_account_id)?.name || 'Unknown');
      const { amount: displayAmt } = getDisplayAmount(tx);
      const isOut = !INFLOWS.includes(tx.category_name) && !inflowCats.some(c => c.name === tx.category_name);
      
      let flowType = 'Expense';
      let amtFormatted = '';

      if (isTransfer) {
        flowType = 'Transfer';
        amtFormatted = `-${displayAmt.toFixed(2)}`;
      } else if (isOut) {
        flowType = 'Expense';
        amtFormatted = `-${displayAmt.toFixed(2)}`;
      } else {
        flowType = 'Income';
        amtFormatted = `+${displayAmt.toFixed(2)}`;
      }

      const curr = tx.currency;

      const targetAccId = filterAccount !== 'ALL' ? filterAccount : tx.paid_by_account_id;
      const runBal = (targetAccId && accRunningMaps[targetAccId]) ? accRunningMaps[targetAccId][tx.transaction_id] : null;
      const runBalFormatted = runBal !== undefined && runBal !== null ? (runBal < 0 ? `-${Math.abs(runBal).toFixed(2)}` : `+${runBal.toFixed(2)}`) : '-';

      let details = '';
      if (isTransfer) {
        const destAcc = accounts.find(a => a.account_id === tx.transfer_to_account_id)?.name || 'Unknown';
        details = `Transfer to ${destAcc}`;
      } else if (tx.is_shared === true || tx.is_shared === 1) {
        details = 'Shared Split';
      } else if (tx.is_recurring === true || tx.is_recurring === 1) {
        details = 'Recurring';
      }

      return [
        escapeCsv(dateStr),
        escapeCsv(desc),
        escapeCsv(cat),
        escapeCsv(acc),
        amtFormatted,
        escapeCsv(runBalFormatted),
        escapeCsv(curr),
        escapeCsv(flowType),
        escapeCsv(details)
      ].join(',');
    });


    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `tezer_transactions_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('Excel/CSV export completed!', 'success');
  };

  const TEZER_REPORT_LOGO_SVG = `
    <svg class="tezer-report-logo" xmlns="http://www.w3.org/2000/svg" viewBox="172 95 760 760" width="48" height="48" style="width: 48px; height: 48px; margin-bottom: 8px; display: block;" role="img" aria-label="tezer logo">
      <g stroke="none" shape-rendering="geometricPrecision">
        <polygon points="209,402 340,214 420,333" fill="#4D978B"/>
        <polygon points="209,402 420,333 471,403" fill="#175541"/>
        <polygon points="340,214 561,286 420,333" fill="#2E8B72"/>
        <polygon points="340,214 420,333 471,403" fill="#236D56"/>
        <polygon points="420,333 561,286 471,403" fill="#0F4B39"/>
        <polygon points="561,286 894,190 812,400 711,349" fill="#56C58A"/>
        <polygon points="894,190 812,400 648,454 711,349" fill="#197A50"/>
        <polygon points="561,286 711,349 648,454" fill="#2CA66D"/>
        <polygon points="561,286 648,454 471,403" fill="#218A61"/>
        <polygon points="711,349 812,400 648,454" fill="#278C5D"/>
        <polygon points="561,286 711,349 648,454" fill="#34B878"/>
        <polygon points="471,403 561,286 648,454" fill="#238F63"/>
        <polygon points="471,403 648,454 548,552" fill="#4FBD83"/>
        <polygon points="471,403 548,552 450,641" fill="#239B6A"/>
        <polygon points="548,552 648,454 647,759" fill="#238A58"/>
        <polygon points="450,641 548,552 647,759" fill="#124C3B"/>
        <polygon points="450,641 548,552 503,604" fill="#1D6E53"/>
      </g>
    </svg>
  `;

  const REPORT_BASE_STYLES = `
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap');

    @font-face {
      font-family: 'Dirham';
      src: url('data:font/woff2;base64,d09GMgABAAAAAAPwAAsAAAAAB+wAAAOhAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAHFQGYACCcAqEUINdATYCJAMICwYABCAFg34HMBuwBhEVnCfJfibYtha2/ig30q5UWkoWF7/kQ+9lbyboNimUqaDtFfKJXJypP2oZ49rDW3MOlIlQJdnzrR/5R8pnpOyUXgBMXJO/zl8gyR/he9GNRaXD2QAn1tZtLTQa4IB4IBumV740AfuAAJAQhhTQjKyCCph1b+3fEw7goesJEOWS7l06tULFTeArcglUIBCb9qWpZDYEgD6ghwFM995PniOcUIBC4KALSjpmtsdxt1NGD268YbE67inaD1cGu1MAAgAOQNDCLKSi2lUELkgFgYNU5OLhgYp5EMDtlMHg8Mv+4QEEICJ0tgkAwAnyxyRY4qFmUwAADxsvAHjc6bIAP4PJjsO0irKhcsESQNKxtYzpNIBWqyxQNnhro2VUMZ2OMefUxEe8tXIlRs+3u+1nI0/zXXfO1lbvuy9xncsG9sBjoXZ8m17W70KMPlPmtDVrFEWrZ2Qh07GtMazKdpkLk6cpa7wVMoYtTdCtjZmq85verdJtZCyBrb0b42NdS5raeib6zJ007RZlibO9ViOZkLu2RJ7WYfRo2PQkS/WmphVap8V2dhobm+0FmtU2Go3X6tNeiT9J4qqn6cXycYjCNinNy760fd3olvIPiLKyoGbGtp2vea9Xq33ad8G8vrqBA40FNsxmQKlRG9Kpk52bpL4adfJEC7Rjbnx+93DukF/adr/XrXVbd2y4F7UlVv4qStSjQ9x951tVWMmiyBf7KvnjCmDCE7OoSHW0WaWlQ/F+jzWWJSGWfLLfjB7lHW61G9939+M61nasZRmmnxoeURb3vz6N9/a2+dMSIVoYNJhHaFlZp3lcFU8lhJtbCoJ0no6b/2RevdXwT+ETnqndLKuFqXM/Pl8f8A//AWoABpxZvJHc+De8HFqjWm40myd9U8ncewB48zfQ2JM18wCqW+AiwPsMgB9h0LX4kULgfIUJJKG58IckKADJiAIG7bHA2a5lPwgnPEYQIwUXaEwhwCPWmAAZ9grR0HhBTEIgAgbLsEal4ozsCBOdAUaZ6Y9xTIwFeGQmJmB+mMjEdkxS9Z9D5hMr6dCydFHmGRlSwen2UNhdyTAPU+oVu8oKGSkJ/Av9ZRYiDRpLgq1kUjEWqFi8EjRYorqOoy768iBKOkiHlqWLMs/IkFdE6EN92l3JYDaYKoFdlRtQGSm5O/+C/jILIg00lgS2gbiGimikCqjFnbwSNLAdEnU1omq6qWsgBjGfaBoSPQ12l3hsxsMBAAAA') format('woff2');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
      unicode-range: U+20C3;
      size-adjust: 82%;
    }

    @font-face {
      font-family: 'Dirham-Sans';
      src: url('data:font/woff2;base64,d09GMgABAAAAAAPUAAsAAAAAB+QAAAODAAEAAAAAAAAAAAAAAAAAAAAAAAAAAAAAHFQGYACCcAqEKINPATYCJAMICwYABCAFg34HMBuwBhEVnCfJfibYtha2/ig30q5UWkoWF7/kQ+9lbyboNimUqaDtFfKJXJypP2oZ49rDW3MOlIlQJdnzrR/5R8pnpOyUXgBMXJO/zl8gyR/he9GNRaXD2QAn1tZtLTQa4IB4IBumV740AfuAAJAQhhTQjKyCCph1b+3fEw7goesJEOWS7l06tULFTeArcglUIBCb9qWpZDYEgD6ghwFM995PniOcUIBC4KALSjpmtsdxt1NGD268YbE67inaD1cGu1MAAgAOQNDCLKSi2lUELkgFgYNU5OLhgYp5EMDtlMHg8Mv+4QEEICJ0tgkAwAnyxyRY4qFmUwAADxsvAHjc6bIAP4PJjsO0irKhcsESQNKxtYzpNIBWqyxQNnhro2VUMZ2OMefUxEe8tXIlRs+3u+1nI0/zXXfO1lbvuy9xncsG9sBjoXZ8m17W70KMPlPmtDVrFEWrZ2Qh07GtMazKdpkLk6cpa7wVMoYtTdCtjZmq85verdJtZCyBrb0b42NdS5raeib6zJ007RZlibO9ViOZkLu2RJ7WYfRo2PQkS/WmphVap8V2dhobm+0FmtU2Go3X6tNeiT9J4qqn6cXycYjCNinNy760fd3olvIPiLKyoGbGtp2vea9Xq33ad8G8vrqBA40FNsxmQKlRG9Kpk52bpL4adfJEC7Rjbnx+93DukF/adr/XrXVbd2y4F7UlVv4qStSjQ9x951tVWMmiyBf7KvnjCmDCE7OoSHW0WaWlQ/F+jzWWJSGWfLLfjB7lHW61G9939+M61nasZRmmnxoeURb3vz6N9/a2+dMSIVoYNJhHaFlZp3lcFU8lhJtbCoJ0no6b/2RevdXwT+ETnqndLKuFqXM/Pl8f8A//AWoABpxZvJHc+De8HFqjWm40myd9U8ncewB48zfQ2JM18wCqW+AiwPsMgB9h0LX4kULgfIUJJKG58IckKADJiAIG7bHA2a5lPwgnPEYQIwUXaEwhwCPWmAAZ9grR0HhBTEIgAgbLsEal4ozsCBOdAUaZ6Y9xTIwFeGQmJmB+mMjEdkxS9Z9D5hMr6dCydFHmGRlSwen2UNhdyTAPU+oVu8oKGSkJ/Av9ZRYiDRpLgq1kUjEWqFi8EjRYorqOoy768iBKOkiHlqWLMs/IkFdE6EN92l3JYDaYKoFdlRtQGSm5O/+C/jILIg00lgS2gbiGimikCqjFnbwSNLAdEnU1omq6qWsgBjGfaBoSPQ12l3hsxsMBAAAA') format('woff2');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
      unicode-range: U+20C3;
      size-adjust: 82%;
    }

    body, table, th, td, h1, h2, h3, p, span, div, .total-summary, .report-footer {
      font-family: 'Dirham', 'Dirham-Sans', 'Outfit', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    }
    body {
      background-color: #121620;
      color: #e2e8f0;
      padding: 40px;
      margin: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .header {
      border-bottom: 2px solid rgba(255,255,255,0.1);
      padding-bottom: 20px;
      margin-bottom: 30px;
    }
    .tezer-report-logo {
      width: 48px;
      height: 48px;
      margin-bottom: 8px;
      display: block;
    }
    h1 {
      font-size: 28px;
      margin: 0 0 5px 0;
      color: #00f5d4;
      letter-spacing: -0.5px;
    }
    .subtitle {
      font-size: 14px;
      color: #94a3b8;
      margin: 0;
    }
    .app-url-link {
      color: #00f5d4;
      text-decoration: none;
      font-weight: 700;
      font-size: 14px;
    }
    .filter-card {
      background-color: rgba(255,255,255,0.03);
      border: 1px solid rgba(255,255,255,0.08);
      border-radius: 8px;
      padding: 15px;
      margin-bottom: 30px;
      font-size: 13px;
    }
    .filter-title {
      font-weight: 700;
      text-transform: uppercase;
      font-size: 11px;
      color: #94a3b8;
      margin-bottom: 6px;
      letter-spacing: 0.5px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 30px;
    }
    th {
      text-align: left;
      padding: 12px 10px;
      background-color: rgba(255,255,255,0.05);
      border-bottom: 2px solid rgba(255,255,255,0.15);
      font-size: 12px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #94a3b8;
    }
    .balance-cards-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
      gap: 12px;
      margin-bottom: 20px;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    .balance-card {
      background-color: rgba(255, 255, 255, 0.03);
      border: 1px solid rgba(255, 255, 255, 0.08);
      border-radius: 10px;
      padding: 12px 14px;
      box-sizing: border-box;
    }
    .balance-card-label {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #94a3b8;
      font-weight: 700;
      margin-bottom: 4px;
    }
    .balance-card-value {
      font-size: 17px;
      font-weight: 800;
      line-height: 1.2;
      margin-bottom: 4px;
    }
    .balance-card-sub {
      font-size: 11px;
      color: #94a3b8;
    }
    .total-summary {
      display: flex;
      justify-content: flex-end;
      font-size: 15px;
      font-weight: bold;
      padding-top: 15px;
      border-top: 1px solid rgba(255,255,255,0.1);
    }
    .report-meta-footer {
      margin-top: 25px;
      padding-top: 10px;
      border-top: 1px dashed rgba(255, 255, 255, 0.08);
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 11px;
      color: #94a3b8;
    }
    .report-footer {
      margin-top: 15px;
      padding-top: 15px;
      border-top: 1px solid rgba(255,255,255,0.1);
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 12px;
      color: #94a3b8;
    }
    @media print {
      body {
        background-color: #ffffff !important;
        color: #121620 !important;
        padding: 0;
      }
      .tezer-report-logo {
        display: block !important;
        margin-bottom: 8px !important;
      }
      .app-url-link {
        color: #0284c7 !important;
        font-weight: 700 !important;
      }
      .balance-cards-grid {
        gap: 8px !important;
        margin-bottom: 16px !important;
      }
      .balance-card {
        background-color: #f8fafc !important;
        border-color: #cbd5e1 !important;
        padding: 10px 12px !important;
      }
      .balance-card-label {
        color: #64748b !important;
      }
      .balance-card-value {
        color: #0f172a !important;
      }
      .balance-card-sub {
        color: #64748b !important;
      }
      .filter-card {
        border-color: #cbd5e1 !important;
        background-color: #f8fafc !important;
        color: #334155 !important;
      }
      th {
        background-color: #f1f5f9 !important;
        border-bottom-color: #94a3b8 !important;
        color: #475569 !important;
      }
      td {
        border-bottom-color: #e2e8f0 !important;
      }
      h1 {
        color: #0f172a !important;
      }
      .subtitle, .filter-title {
        color: #64748b !important;
      }
      .total-summary {
        border-top-color: #cbd5e1 !important;
      }
      .report-meta-footer {
        border-top-color: #cbd5e1 !important;
        color: #64748b !important;
      }
      .report-footer {
        border-top-color: #cbd5e1 !important;
        color: #64748b !important;
      }
    }
  `;

  const handleExportPdf = () => {
    const list = getFilteredTransactions();
    if (list.length === 0) {
      return showToast('No transactions to export', 'error');
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return showToast('Popup blocked! Please allow popups to export PDF', 'error');
    }

    const userName = getUserReportName();
    const now = new Date();
    const dateStr = formatDateDDMMYYYY(now) + ' ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    let filterDetails = [];
    if (filterSearch) filterDetails.push(`Search: "${filterSearch}"`);
    if (filterType !== 'ALL') filterDetails.push(`Type: ${filterType}`);
    if (filterAccount !== 'ALL') {
      const accName = accounts.find(a => a.account_id === filterAccount)?.name || 'Unknown';
      filterDetails.push(`Account: ${accName}`);
    }
    if (filterCategory !== 'ALL') filterDetails.push(`Category: ${filterCategory}`);
    if (filterStartDate) filterDetails.push(`Start: ${formatDateDDMMYYYY(filterStartDate)}`);
    if (filterEndDate) filterDetails.push(`End: ${formatDateDDMMYYYY(filterEndDate)}`);
    const activeFiltersText = filterDetails.length > 0 ? filterDetails.join(' | ') : 'None';

    const INFLOWS = ['Salary', 'Cashback', 'Income from investments', 'Other income', 'Credit Card Rewards', 'Investment Income', 'External Borrowers', 'Deposits', 'Crypto Profit'];

    const accRunningMaps = {};
    accounts.forEach(acc => {
      accRunningMaps[acc.account_id] = computeAccountRunningBalances(acc, transactions);
    });

    // Compute top summary cards
    let summaryCardsHtml = '';
    if (filterAccount !== 'ALL') {
      const targetAcc = accounts.find(a => a.account_id === filterAccount);
      if (targetAcc) {
        if (targetAcc.type === 'CREDIT_CARD') {
          const cardBal = parseFloat(targetAcc.current_balance || 0);
          const cardLimit = parseFloat(targetAcc.credit_limit || 0);
          const availLimit = Math.max(0, cardLimit - cardBal);
          const stmtInfo = getCardStatementInfo(targetAcc, transactions);
          const stmtDue = stmtInfo.hasStatement ? stmtInfo.remainingStatementDue : 0;
          const dueDateStr = stmtInfo.dueDate ? formatDateDDMMYYYY(stmtInfo.dueDate) : null;
          const utilPct = cardLimit > 0 ? Math.round((cardBal / cardLimit) * 100) : 0;

          summaryCardsHtml = `
            <div class="balance-card">
              <div class="balance-card-label">Total Due (Current Balance)</div>
              <div class="balance-card-value" style="color: #ff6b6b;">${formatMoney(cardBal, targetAcc.currency)}</div>
              <div class="balance-card-sub">Outstanding balance on card</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Available Credit</div>
              <div class="balance-card-value" style="color: #2bcbba;">${formatMoney(availLimit, targetAcc.currency)}</div>
              <div class="balance-card-sub">Limit: ${formatMoney(cardLimit, targetAcc.currency)}</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Last Statement Due</div>
              <div class="balance-card-value" style="color: ${stmtInfo.isStatementDuePaid ? '#2bcbba' : '#ffb03a'};">${formatMoney(stmtDue, targetAcc.currency)}</div>
              <div class="balance-card-sub">${stmtInfo.isStatementDuePaid ? '✓ Fully Paid / Settled' : (dueDateStr ? 'Due by ' + dueDateStr : 'Payment Pending')}</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Credit Limit</div>
              <div class="balance-card-value" style="color: #38bdf8;">${formatMoney(cardLimit, targetAcc.currency)}</div>
              <div class="balance-card-sub">Utilization: ${utilPct}%</div>
            </div>
          `;
        } else {
          const cur = targetAcc.currency || 'AED';
          const currentBal = parseFloat(targetAcc.current_balance || 0);
          let periodInflow = 0;
          let periodOutflow = 0;
          list.forEach(tx => {
            const isTransfer = isTransferTransaction(tx);
            const isOut = !INFLOWS.includes(tx.category_name) && !inflowCats.some(c => c.name === tx.category_name);
            if (isTransfer) {
              if (tx.transfer_to_account_id === targetAcc.account_id) periodInflow += parseFloat(tx.transfer_to_amount || tx.total_amount || 0);
              else if (tx.paid_by_account_id === targetAcc.account_id) periodOutflow += parseFloat(tx.total_amount || 0);
            } else if (!isOut) {
              periodInflow += parseFloat(tx.total_amount || 0);
            } else {
              periodOutflow += parseFloat(tx.total_amount || 0);
            }
          });
          const netPeriod = periodInflow - periodOutflow;

          summaryCardsHtml = `
            <div class="balance-card">
              <div class="balance-card-label">Current Balance</div>
              <div class="balance-card-value" style="color: ${currentBal >= 0 ? '#2bcbba' : '#ff6b6b'};">${formatMoney(currentBal, cur)}</div>
              <div class="balance-card-sub">${targetAcc.type} account balance</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Period Inflow</div>
              <div class="balance-card-value" style="color: #2bcbba;">+${formatMoney(periodInflow, cur)}</div>
              <div class="balance-card-sub">Deposits &amp; transfers in</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Period Outflow</div>
              <div class="balance-card-value" style="color: #ff6b6b;">-${formatMoney(periodOutflow, cur)}</div>
              <div class="balance-card-sub">Expenses &amp; transfers out</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Net Period Change</div>
              <div class="balance-card-value" style="color: ${netPeriod >= 0 ? '#2bcbba' : '#ff6b6b'};">${netPeriod >= 0 ? '+' : '-'}${formatMoney(Math.abs(netPeriod), cur)}</div>
              <div class="balance-card-sub">${targetAcc.minimum_balance > 0 ? 'Min Target: ' + formatMoney(targetAcc.minimum_balance, cur) : (list.length + ' transactions')}</div>
            </div>
          `;
        }
      }
    }

    if (!summaryCardsHtml) {
      const primaryCur = preferredCurrencies[0] || (accounts[0]?.currency) || 'AED';
      let totalInflow = 0;
      let totalOutflow = 0;
      list.forEach(tx => {
        const isTransfer = isTransferTransaction(tx);
        const { amount: displayAmt } = getDisplayAmount(tx);
        if (!isTransfer) {
          if (INFLOWS.includes(tx.category_name) || inflowCats.some(c => c.name === tx.category_name)) {
            totalInflow += displayAmt;
          } else {
            totalOutflow += displayAmt;
          }
        }
      });
      const netFlow = totalInflow - totalOutflow;

      summaryCardsHtml = `
        <div class="balance-card">
          <div class="balance-card-label">Total Inflow (Period)</div>
          <div class="balance-card-value" style="color: #2bcbba;">+${formatMoney(totalInflow, primaryCur)}</div>
          <div class="balance-card-sub">Income across selected scope</div>
        </div>
        <div class="balance-card">
          <div class="balance-card-label">Total Outflow (Period)</div>
          <div class="balance-card-value" style="color: #ff6b6b;">-${formatMoney(totalOutflow, primaryCur)}</div>
          <div class="balance-card-sub">Expenses across selected scope</div>
        </div>
        <div class="balance-card">
          <div class="balance-card-label">Net Cash Flow</div>
          <div class="balance-card-value" style="color: ${netFlow >= 0 ? '#2bcbba' : '#ff6b6b'};">${netFlow >= 0 ? '+' : '-'}${formatMoney(Math.abs(netFlow), primaryCur)}</div>
          <div class="balance-card-sub">${netFlow >= 0 ? 'Net savings surplus' : 'Net deficit'}</div>
        </div>
        <div class="balance-card">
          <div class="balance-card-label">Filtered Records</div>
          <div class="balance-card-value" style="color: #38bdf8;">${list.length}</div>
          <div class="balance-card-sub">Matching transactions</div>
        </div>
      `;
    }

    const tableRowsHtml = list.map((tx, idx) => {
      const isTransfer = isTransferTransaction(tx);
      const dateVal = formatDateTime(tx.date);
      const descVal = tx.description;
      const catVal = isTransfer ? '🔄 Transfer' : tx.category_name;
      const accVal = tx.paid_by_account_id === 'VIRTUAL_DEBT' ? "Friend's Virtual Balance" : (accounts.find(a => a.account_id === tx.paid_by_account_id)?.name || 'Unknown');
      const { amount: displayAmt } = getDisplayAmount(tx);
      
      let flowType = 'Expense';
      let flowColor = '#ff6b6b';
      let amtSign = '-';
      
      if (isTransfer) {
        flowType = 'Transfer';
        flowColor = '#00f5d4';
        amtSign = '';
      } else if (INFLOWS.includes(tx.category_name)) {
        flowType = 'Income';
        flowColor = '#2bcbba';
        amtSign = '+';
      }

      const targetAccId = filterAccount !== 'ALL' ? filterAccount : tx.paid_by_account_id;
      const targetAcc = accounts.find(a => a.account_id === targetAccId);
      const runBal = (targetAccId && accRunningMaps[targetAccId]) ? accRunningMaps[targetAccId][tx.transaction_id] : null;
      let runBalHtml = '-';
      if (runBal !== undefined && runBal !== null) {
        const currToUse = targetAcc ? targetAcc.currency : tx.currency;
        const color = runBal >= 0 ? '#2bcbba' : '#ff6b6b';
        const sign = runBal >= 0 ? '+' : '-';
        runBalHtml = `<span style="color: ${color}; font-weight: 700;">${sign}${formatMoney(Math.abs(runBal), currToUse)}</span>`;
      }

      return `
        <tr style="background-color: ${idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'};">
          <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px;">${escapeHtml(dateVal)}</td>
          <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px; font-weight: 500;">${escapeHtml(descVal)}</td>
          <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px; color: #a55eea;">${escapeHtml(catVal)}</td>
          <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px; color: #54a0ff;">${escapeHtml(accVal)}</td>
          <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px;">${flowType}</td>
          <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px; font-weight: 700; text-align: right; color: ${flowColor};">
            ${amtSign}${formatMoney(displayAmt, tx.currency)}
          </td>
          <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px; text-align: right;">
            ${runBalHtml}
          </td>
        </tr>
      `;
    }).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <base href="${window.location.origin}/">
        <title>tezer - Transaction Report</title>
        <style>
          ${REPORT_BASE_STYLES}
        </style>
      </head>
      <body>
        <div class="header">
          <div style="display: flex; justify-content: space-between; align-items: flex-start;">
            <div>
              ${TEZER_REPORT_LOGO_SVG}
              <h1>tezer</h1>
              <p class="subtitle">Transaction History Report</p>
            </div>
            <div style="text-align: right; padding-top: 4px;">
              <a href="https://tezer.app" target="_blank" rel="noopener noreferrer" class="app-url-link" style="font-size: 15px; font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
                tezer.app
                <span style="font-size: 12px;">↗</span>
              </a>
              <div style="font-size: 11px; color: #94a3b8; margin-top: 2px;">Smart Personal &amp; Shared Finance</div>
            </div>
          </div>
        </div>

        ${summaryCardsHtml ? `
        <div class="balance-cards-grid">
          ${summaryCardsHtml}
        </div>
        ` : ''}

        <div class="filter-card">
          <div class="filter-title">Active Filter Configuration</div>
          <div>${escapeHtml(activeFiltersText)}</div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 11%;">Date</th>
              <th style="width: 29%;">Description</th>
              <th style="width: 13%;">Category</th>
              <th style="width: 14%;">Account</th>
              <th style="width: 10%;">Type</th>
              <th style="width: 11%; text-align: right;">Amount</th>
              <th style="width: 12%; text-align: right;">Running Balance</th>
            </tr>
          </thead>
          <tbody>
            ${tableRowsHtml}
          </tbody>
        </table>

        <div class="total-summary">
          <span>Total Records: ${list.length}</span>
        </div>

        <div class="report-meta-footer">
          <span>Generated by <strong>${escapeHtml(userName)}</strong> on ${escapeHtml(dateStr)}</span>
          <span>tezer.app</span>
        </div>

        <div class="report-footer">
          <span>Powered by <strong>tezer</strong> — Smart Personal &amp; Shared Expense Tracker</span>
          <span>Access the app: <a href="https://tezer.app" target="_blank" rel="noopener noreferrer" class="app-url-link" style="text-decoration: none; font-weight: 600;">https://tezer.app</a></span>
        </div>

        <script>
          (function() {
            function doPrint() {
              setTimeout(function() {
                window.print();
              }, 250);
            }
            if (document.fonts && document.fonts.ready) {
              document.fonts.ready.then(doPrint).catch(doPrint);
            } else if (document.readyState === 'complete') {
              doPrint();
            } else {
              window.addEventListener('load', doPrint);
            }
          })();
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Date, Account & Category filters
  const filterTransactions = () => {
    const myUserId = localStorage.getItem('dhudd_user_id');
    return transactions.filter(tx => {
      // Exclude parent transactions if user already has a mirror child transaction
      if (myUserId && tx.user_id !== myUserId && transactions.some(t => t.source_transaction_id === tx.transaction_id && t.user_id === myUserId)) {
        return false;
      }
      // Exclude child mirror transactions if user is owner of parent transaction
      if (myUserId && tx.user_id !== myUserId && tx.source_transaction_id && transactions.some(t => t.transaction_id === tx.source_transaction_id && t.user_id === myUserId)) {
        return false;
      }

      // 1. Account filter
      if (selectedAccountIds && !selectedAccountIds.includes('ALL')) {
        if (selectedAccountIds.length === 0) return false;
        if (!tx.paid_by_account_id || !selectedAccountIds.includes(tx.paid_by_account_id)) {
          return false;
        }
      }

      // 2. Category filter
      if (selectedCategories && !selectedCategories.includes('ALL')) {
        if (selectedCategories.length === 0) return false;
        if (!tx.category_name || !selectedCategories.includes(tx.category_name)) {
          return false;
        }
      }

      const txDate = new Date(tx.date);
      const now = new Date();
      
      switch (activePeriod) {
        case 'Day':
          return txDate.toDateString() === now.toDateString();
        case 'Week': {
          const pastWeek = new Date();
          pastWeek.setDate(now.getDate() - 7);
          return txDate >= pastWeek;
        }
        case 'Month':
          return txDate.getMonth() === now.getMonth() && txDate.getFullYear() === now.getFullYear();
        case 'Year':
          return txDate.getFullYear() === now.getFullYear();
        case 'Interval': {
          if (!customRange.start || !customRange.end) return true;
          const start = new Date(customRange.start);
          const end = new Date(customRange.end);
          end.setHours(23, 59, 59, 999);
          return txDate >= start && txDate <= end;
        }
        case 'All':
        default:
          return true;
      }
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const renderFriendBalanceTiles = (userId) => {
    const friend = friends.find(fr => fr.user_id === userId);
    if (!friend || !friend.balances || friend.balances.length === 0) return null;
    
    // Check if settled
    const isSettled = friend.balances.every(b => Math.abs(b.amount) < 0.01);
    if (isSettled) {
      return (
        <span key="settled" style={{
          fontSize: '9px',
          fontWeight: 'bold',
          padding: '1px 4px',
          borderRadius: '4px',
          background: 'rgba(255, 255, 255, 0.05)',
          color: 'var(--text-muted)',
          border: '1px solid var(--border-glass)',
          marginLeft: '4px',
          display: 'inline-block',
          verticalAlign: 'middle'
        }}>
          Settled
        </span>
      );
    }

    return friend.balances.map((b, i) => {
      if (Math.abs(b.amount) < 0.01) return null;
      const owesYou = b.amount > 0;
      return (
        <span key={i} style={{
          fontSize: '9px',
          fontWeight: 'bold',
          padding: '1px 4px',
          borderRadius: '4px',
          background: owesYou ? 'rgba(0, 245, 212, 0.15)' : 'rgba(255, 75, 75, 0.15)',
          color: owesYou ? 'var(--accent-cyan)' : 'var(--color-outflow)',
          border: '1px solid var(--border-glass)',
          marginLeft: '4px',
          display: 'inline-block',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap'
        }}>
          {owesYou ? 'Owes' : 'Owes'} {formatMoney(Math.abs(b.amount), b.currency)}
        </span>
      );
    });
  };

  const formatDateTime = (dateVal) => {
    if (!dateVal) return '';
    const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    
    let tz = selectedTimezone;
    if (tz === 'local' || !tz) {
      try {
        tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      } catch (e) {
        tz = undefined;
      }
    }
    
    try {
      const dateStr = d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: tz });
      const timeStr = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', timeZone: tz });
      return `${dateStr} ${timeStr}`;
    } catch (e) {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const dateStr = `${day}/${month}/${year}`;
      const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      return `${dateStr} ${timeStr}`;
    }
  };

  const resolveMemberDisplayName = (userId, fallbackDisplayName, fallbackEmail) => {
    const myUserId = localStorage.getItem('dhudd_user_id');
    if (userId === myUserId) return 'You';
    const f = friends.find(friend => friend.user_id === userId);
    if (f) {
      return f.nickname || f.display_name || f.email?.split('@')[0] || fallbackDisplayName || fallbackEmail?.split('@')[0] || 'Unknown';
    }
    const baseName = fallbackDisplayName || fallbackEmail?.split('@')[0] || 'Deleted User';
    return `${baseName} (deleted user)`;
  };

  const getPayerName = (tx) => {
    if (!tx) return 'Unknown';
    const myUserId = localStorage.getItem('dhudd_user_id');
    const payers = tx.splits?.filter(s => parseFloat(s.paid_amount) > 0) || [];
    
    if (payers.length > 1) {
      return 'Multiple';
    }
    
    if (payers.length === 1) {
       if (payers[0].member_user_id === myUserId) return 'You';
       return resolveMemberDisplayName(payers[0].member_user_id, payers[0].display_name, payers[0].email);
    }

    if (tx.paid_by_user_id === myUserId || (!tx.paid_by_user_id && tx.user_id === myUserId)) {
      return 'You';
    }
    if (tx.paid_by_user_id) {
      const payerSplit = tx.splits?.find(s => s.member_user_id === tx.paid_by_user_id);
      return resolveMemberDisplayName(
        tx.paid_by_user_id,
        payerSplit?.display_name,
        payerSplit?.email
      );
    }
    return 'Unknown';
  };

  const getMonthlySpend = (cardId, targetType = 'CALENDAR', statementDay = 15) => {
    const now = new Date();
    let cycleStart, cycleEnd;

    if (targetType === 'STATEMENT' && statementDay) {
      const day = parseInt(statementDay, 10);
      const today = now.getDate();
      
      let startYear, startMonth, endYear, endMonth;
      
      if (today <= day) {
        startYear = now.getFullYear();
        startMonth = now.getMonth() - 1;
        endYear = now.getFullYear();
        endMonth = now.getMonth();
      } else {
        startYear = now.getFullYear();
        startMonth = now.getMonth();
        endYear = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
        endMonth = now.getMonth() === 11 ? 0 : now.getMonth() + 1;
      }
      
      const getMaxDays = (year, month) => new Date(year, month + 1, 0).getDate();
      
      const startMax = getMaxDays(startYear, startMonth);
      const startDayClamped = Math.min(day + 1, startMax);
      cycleStart = new Date(startYear, startMonth, startDayClamped, 0, 0, 0, 0);
      
      const endMax = getMaxDays(endYear, endMonth);
      const endDayClamped = Math.min(day, endMax);
      cycleEnd = new Date(endYear, endMonth, endDayClamped, 23, 59, 59, 999);
    } else {
      const year = now.getFullYear();
      const month = now.getMonth();
      cycleStart = new Date(year, month, 1, 0, 0, 0, 0);
      cycleEnd = new Date(year, month + 1, 0, 23, 59, 59, 999);
    }

    const inflowCats = ['Salary', 'Cashback', 'Income from investments', 'Other income', 'Credit Card Rewards', 'Investment Income', 'External Borrowers', 'Deposits', 'Crypto Profit'];
    const spend = transactions
      .filter(tx => {
        const txDate = new Date(tx.date);
        const inCycle = txDate >= cycleStart && txDate <= cycleEnd;
        const isCardPayment = tx.paid_by_account_id === cardId;
        const isOutflow = !inflowCats.includes(tx.category_name) || isTransferTransaction(tx);
        return inCycle && isCardPayment && isOutflow;
      })
      .reduce((sum, tx) => sum + parseFloat(tx.total_amount), 0);

    return {
      amount: spend,
      cycleStart,
      cycleEnd
    };
  };

  // Compute credit card statement cycle, last statement due amount, and payments after statement
  const getCardStatementInfo = (card, txList = []) => {
    if (!card || card.type !== 'CREDIT_CARD') {
      return {
        hasStatement: false,
        lastStatementDate: null,
        nextStatementDate: null,
        lastStatementBalance: 0,
        paymentsAfterStatement: 0,
        paymentsAfterCount: 0,
        remainingStatementDue: 0,
        dueDaysLeft: null,
        dueDate: null,
        isStatementDuePaid: true
      };
    }

    const statementDay = parseInt(card.statement_day, 10);
    const dueDay = parseInt(card.due_day, 10);

    if (!statementDay || isNaN(statementDay)) {
      return {
        hasStatement: false,
        lastStatementDate: null,
        nextStatementDate: null,
        lastStatementBalance: 0,
        paymentsAfterStatement: 0,
        paymentsAfterCount: 0,
        remainingStatementDue: 0,
        dueDaysLeft: null,
        dueDate: null,
        isStatementDuePaid: true
      };
    }

    const now = new Date();
    const today = now.getDate();
    const getMaxDays = (y, m) => new Date(y, m + 1, 0).getDate();

    let lastStmtYear, lastStmtMonth, nextStmtYear, nextStmtMonth;

    if (today >= statementDay) {
      // Current month's statement has already closed
      lastStmtYear = now.getFullYear();
      lastStmtMonth = now.getMonth();

      nextStmtYear = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
      nextStmtMonth = now.getMonth() === 11 ? 0 : now.getMonth() + 1;
    } else {
      // Current month's statement hasn't closed yet; last statement closed last month
      lastStmtYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
      lastStmtMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;

      nextStmtYear = now.getFullYear();
      nextStmtMonth = now.getMonth();
    }

    const lastMax = getMaxDays(lastStmtYear, lastStmtMonth);
    const lastClampedDay = Math.min(statementDay, lastMax);
    const lastStatementDate = new Date(lastStmtYear, lastStmtMonth, lastClampedDay, 23, 59, 59, 999);

    const nextMax = getMaxDays(nextStmtYear, nextStmtMonth);
    const nextClampedDay = Math.min(statementDay, nextMax);
    const nextStatementDate = new Date(nextStmtYear, nextStmtMonth, nextClampedDay, 23, 59, 59, 999);

    // Compute Due Date for the last statement
    let dueYear = lastStmtYear;
    let dueMonth = lastStmtMonth;
    if (dueDay && !isNaN(dueDay)) {
      if (dueDay <= statementDay) {
        // Due date is in the month following statement closing
        if (dueMonth === 11) {
          dueYear += 1;
          dueMonth = 0;
        } else {
          dueMonth += 1;
        }
      }
    }
    const dueMax = getMaxDays(dueYear, dueMonth);
    const dueClampedDay = Math.min(dueDay || statementDay, dueMax);
    const dueDate = new Date(dueYear, dueMonth, dueClampedDay, 23, 59, 59, 999);

    // Compute days left until due date
    const msPerDay = 1000 * 60 * 60 * 24;
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfDueDate = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
    const dueDaysLeft = Math.round((startOfDueDate - startOfToday) / msPerDay);

    // Filter transactions touching this card
    const cardTxs = (txList || []).filter(tx => tx.paid_by_account_id === card.account_id || tx.transfer_to_account_id === card.account_id);
    const sortedTxs = [...cardTxs].sort((a, b) => new Date(a.date) - new Date(b.date));

    const INFLOWS = ['Salary', 'Cashback', 'Income from investments', 'Other income', 'Credit Card Rewards', 'Investment Income', 'External Borrowers', 'Deposits', 'Crypto Profit'];

    let statementBalance = 0;
    let hasTxsBeforeStmt = false;

    for (const tx of sortedTxs) {
      const txDate = new Date(tx.date);
      if (txDate <= lastStatementDate) {
        hasTxsBeforeStmt = true;
        const isTransfer = isTransferTransaction(tx);
        const isDeposit = tx.category_name === 'Deposits' || (!isTransfer && (INFLOWS.includes(tx.category_name) || inflowCats.some(c => c.name === tx.category_name)));

        if (tx.transfer_to_account_id === card.account_id) {
          statementBalance -= parseFloat(tx.transfer_to_amount || tx.total_amount || 0);
        } else if (tx.paid_by_account_id === card.account_id) {
          if (isDeposit) {
            statementBalance -= parseFloat(tx.total_amount || 0);
          } else {
            let chargedAmt = parseFloat(tx.total_amount || 0);
            if (tx.is_shared && tx.splits && tx.splits.length > 0) {
              const mySplit = tx.splits.find(s => s.member_user_id === (tx.paid_by_user_id || tx.user_id));
              if (mySplit && parseFloat(mySplit.paid_amount || 0) > 0) {
                chargedAmt = parseFloat(mySplit.paid_amount);
              }
            }
            statementBalance += chargedAmt;
          }
        }
      }
    }

    const lastStatementBalance = Math.max(0, statementBalance);

    let paymentsAfterStatement = 0;
    let paymentsAfterCount = 0;

    for (const tx of sortedTxs) {
      const txDate = new Date(tx.date);
      if (txDate > lastStatementDate) {
        const isTransfer = isTransferTransaction(tx);
        const isDeposit = tx.category_name === 'Deposits' || (!isTransfer && (INFLOWS.includes(tx.category_name) || inflowCats.some(c => c.name === tx.category_name)));

        if (tx.transfer_to_account_id === card.account_id) {
          const amt = parseFloat(tx.transfer_to_amount || tx.total_amount || 0);
          paymentsAfterStatement += amt;
          paymentsAfterCount += 1;
        } else if (tx.paid_by_account_id === card.account_id && isDeposit) {
          const amt = parseFloat(tx.total_amount || 0);
          paymentsAfterStatement += amt;
          paymentsAfterCount += 1;
        }
      }
    }

    const remainingStatementDue = Math.max(0, lastStatementBalance - paymentsAfterStatement);
    const isStatementDuePaid = lastStatementBalance > 0 ? remainingStatementDue === 0 : true;

    return {
      hasStatement: hasTxsBeforeStmt || lastStatementBalance > 0,
      lastStatementDate,
      nextStatementDate,
      lastStatementBalance,
      paymentsAfterStatement,
      paymentsAfterCount,
      remainingStatementDue,
      dueDaysLeft,
      dueDate,
      isStatementDuePaid
    };
  };

  // Compute chronological running post-transaction balances for Bank/Cash and Credit Card accounts
  const computeAccountRunningBalances = useCallback((account, txList) => {
    if (!account || !txList || txList.length === 0) return {};
    const accId = account.account_id;
    const isCc = account.type === 'CREDIT_CARD';
    
    // Filter transactions touching this account
    const accTxs = txList.filter(tx => tx.paid_by_account_id === accId || tx.transfer_to_account_id === accId);
    if (accTxs.length === 0) return {};

    // Sort chronologically (oldest first)
    const sortedChronological = [...accTxs].sort((a, b) => {
      const timeDiff = new Date(a.date) - new Date(b.date);
      if (timeDiff !== 0) return timeDiff;
      return (a.created_at || '').localeCompare(b.created_at || '');
    });

    const deltas = sortedChronological.map(tx => {
      const isTransfer = isTransferTransaction(tx) || (tx.description && (tx.description.toLowerCase().includes('settlement') || tx.description.toLowerCase().includes('transfer')));
      const isDeposit = !isTransfer && (tx.category_name === 'Deposits' || GLOBAL_INFLOW_CATEGORIES.includes(tx.category_name) || inflowCats.some(c => c.name === tx.category_name));
      let delta = 0;

      if (isCc) {
        // For credit cards: current_balance is owed balance (positive number in display)
        // Charging an expense increases owed balance (+)
        // Paying the bill or getting a deposit/refund decreases owed balance (-)
        if (tx.transfer_to_account_id === accId) {
          // Bill payment transferred into card -> reduces owed balance
          const creditAmt = (tx.transfer_to_amount && account.currency !== tx.currency)
            ? parseFloat(tx.transfer_to_amount)
            : parseFloat(tx.transfer_to_amount || tx.total_amount || 0);
          delta = -creditAmt;
        } else if (tx.paid_by_account_id === accId) {
          if (isTransfer) {
            // Transfer OUT of credit card -> increases owed balance
            const debitAmt = (tx.transfer_to_amount && account.currency !== tx.currency)
              ? parseFloat(tx.transfer_to_amount)
              : parseFloat(tx.total_amount || 0);
            delta = debitAmt;
          } else if (isDeposit) {
            // Refund / Cashback on credit card -> reduces owed balance
            delta = -parseFloat(tx.total_amount || 0);
          } else {
            // Expense charged on card -> increases owed balance
            let chargedAmt = parseFloat(tx.total_amount || 0);
            if (tx.is_shared && tx.splits && tx.splits.length > 0) {
              const mySplit = tx.splits.find(s => s.member_user_id === (tx.paid_by_user_id || tx.user_id));
              if (mySplit && parseFloat(mySplit.paid_amount || 0) > 0) {
                chargedAmt = parseFloat(mySplit.paid_amount);
              }
            }
            delta = chargedAmt;
          }
        }
      } else {
        // For bank/cash accounts:
        // Incoming transfer or deposit increases balance (+)
        // Outgoing transfer or expense decreases balance (-)
        if (tx.transfer_to_account_id === accId) {
          const creditAmt = (tx.transfer_to_amount && account.currency !== tx.currency)
            ? parseFloat(tx.transfer_to_amount)
            : parseFloat(tx.transfer_to_amount || tx.total_amount || 0);
          delta = creditAmt;
        } else if (tx.paid_by_account_id === accId) {
          if (isTransfer) {
            // Outgoing transfer decreases balance (-)
            const debitAmt = (tx.transfer_to_amount && account.currency !== tx.currency)
              ? parseFloat(tx.transfer_to_amount)
              : parseFloat(tx.total_amount || 0);
            delta = -debitAmt;
          } else if (isDeposit) {
            delta = parseFloat(tx.total_amount || 0);
          } else {
            let paidAmt = parseFloat(tx.total_amount || 0);
            if (tx.is_shared && tx.splits && tx.splits.length > 0) {
              const mySplit = tx.splits.find(s => s.member_user_id === (tx.paid_by_user_id || tx.user_id));
              if (mySplit && parseFloat(mySplit.paid_amount || 0) > 0) {
                paidAmt = parseFloat(mySplit.paid_amount);
              }
            }
            delta = -paidAmt;
          }
        }
      }
      return { txId: tx.transaction_id, delta };
    });

    const totalNetDelta = deltas.reduce((sum, d) => sum + d.delta, 0);
    const currentVal = isCc ? -parseFloat(account.current_balance || 0) : parseFloat(account.current_balance || 0);
    const startVal = currentVal - totalNetDelta;

    const resultMap = {};
    let running = startVal;
    for (const d of deltas) {
      running += d.delta;
      resultMap[d.txId] = running;
    }
    return resultMap;
  }, [inflowCats]);

  // Compute chronological running post-transaction bilateral balances for Friend detail view
  const computeFriendRunningBalances = useCallback((friend, txList, myUserId) => {
    if (!friend || !txList || txList.length === 0) return {};
    const friendId = friend.user_id;

    const friendTxs = txList.filter(tx => {
      if (!tx.is_shared && !(tx.paid_by_user_id && tx.paid_by_user_id !== tx.user_id)) return false;
      if (tx.source_transaction_id && txList.some(p => p.transaction_id === tx.source_transaction_id && (tx.user_id !== myUserId || p.splits?.some(s => s.member_user_id === friendId) || p.user_id === friendId || p.paid_by_user_id === friendId))) return false;

      const hasSplits = tx.splits && Array.isArray(tx.splits) && tx.splits.length > 0;
      const payers = hasSplits ? tx.splits.filter(s => parseFloat(s.paid_amount || 0) > 0) : [];
      const userIsPayer = payers.some(p => p.member_user_id === myUserId) || tx.paid_by_user_id === myUserId || (!tx.paid_by_user_id && payers.length === 0 && tx.user_id === myUserId);
      const friendIsPayer = payers.some(p => p.member_user_id === friendId) || tx.paid_by_user_id === friendId;

      // Settlement check
      if (tx.category_name === 'Debt Settlement' || (tx.description && tx.description.toLowerCase().includes('settle'))) {
        const mySplit = hasSplits ? tx.splits.find(s => s.member_user_id === myUserId) : null;
        const friendSplit = hasSplits ? tx.splits.find(s => s.member_user_id === friendId) : null;
        const myOwed = parseFloat(mySplit?.owed_amount || 0);
        const friendOwed = parseFloat(friendSplit?.owed_amount || 0);
        if (hasSplits) {
          return (userIsPayer && tx.user_id === friendId) || (friendIsPayer && tx.user_id === myUserId) || (userIsPayer && friendOwed > 0) || (friendIsPayer && myOwed > 0);
        }
        return (tx.paid_by_user_id === myUserId && tx.user_id === friendId) || (tx.paid_by_user_id === friendId && tx.user_id === myUserId);
      }

      // TP1: Third-Party Non-Consumer Payer Classification
      const payerUserId = tx.paid_by_user_id;
      const creatorUserId = tx.user_id;
      const payerSplit = hasSplits && payerUserId ? tx.splits.find(s => s.member_user_id === payerUserId) : null;
      const payerOwed = payerSplit ? parseFloat(payerSplit.owed_amount || 0) : 0;
      const isThirdPartyPayer = !!(payerUserId && creatorUserId && payerUserId !== creatorUserId && payerOwed === 0);

      if (isThirdPartyPayer) {
        if (tx.group_id) return false;

        // TP2: Three-Way Bilateral Debt Routing
        // Debtor vs Payer: Filter out transaction (debtor <-> payer = 0)
        const isDebtorVsPayer = (myUserId === payerUserId && friendId !== creatorUserId) || (friendId === payerUserId && myUserId !== creatorUserId);
        if (isDebtorVsPayer) return false;

        // Creator vs Payer: Creator owes third-party payer full paid amount
        const isCreatorVsPayer = (myUserId === creatorUserId && friendId === payerUserId) || (myUserId === payerUserId && friendId === creatorUserId);
        if (isCreatorVsPayer) return true;

        // Creator vs Debtor: All split debtors owe the expense creator
        const mySplit = hasSplits ? tx.splits.find(s => s.member_user_id === myUserId) : null;
        const friendSplit = hasSplits ? tx.splits.find(s => s.member_user_id === friendId) : null;
        const myOwed = mySplit ? parseFloat(mySplit.owed_amount || 0) : (!hasSplits && myUserId !== payerUserId ? parseFloat(tx.total_amount || 0) : 0);
        const friendOwed = friendSplit ? parseFloat(friendSplit.owed_amount || 0) : (!hasSplits && friendId !== payerUserId ? parseFloat(tx.total_amount || 0) : 0);

        if (myUserId === creatorUserId && friendOwed > 0) return true;
        if (friendId === creatorUserId && myOwed > 0) return true;

        return false;
      }

      if (hasSplits) {
        const mySplit = tx.splits.find(s => s.member_user_id === myUserId);
        const friendSplit = tx.splits.find(s => s.member_user_id === friendId);
        const myOwed = parseFloat(mySplit?.owed_amount || 0);
        const friendOwed = parseFloat(friendSplit?.owed_amount || 0);

        // Direct bilateral involvement:
        if (userIsPayer && friendOwed > 0) return true;
        if (friendIsPayer && myOwed > 0) return true;
        if (!tx.group_id && mySplit && friendSplit) return true;

        return false;
      }

      // No splits (legacy or simple 1-on-1 tx)
      if (tx.group_id) return false;
      return (userIsPayer && tx.user_id === friendId) || (friendIsPayer && tx.user_id === myUserId);
    });

    if (friendTxs.length === 0) return {};

    const sortedChronological = [...friendTxs].sort((a, b) => {
      const timeDiff = new Date(a.date) - new Date(b.date);
      if (timeDiff !== 0) return timeDiff;
      return (a.created_at || '').localeCompare(b.created_at || '');
    });

    // We calculate bilateral delta per currency
    // + delta = friend owes me more (inflow / they owe)
    // - delta = I owe friend more (outflow / I owe)
    const runningPerCurrency = {};
    const resultMap = {};

    for (const tx of sortedChronological) {
      const cur = tx.currency || 'AED';
      if (runningPerCurrency[cur] === undefined) {
        runningPerCurrency[cur] = 0;
      }

      const payers = tx.splits?.filter(s => parseFloat(s.paid_amount) > 0) || [];
      const myPaid = payers.find(p => p.member_user_id === myUserId);
      const friendPaid = payers.find(p => p.member_user_id === friendId);
      const mySplit = tx.splits?.find(s => s.member_user_id === myUserId);
      const friendSplit = tx.splits?.find(s => s.member_user_id === friendId);

      let delta = 0;

      // Settlement check
      if (tx.category_name === 'Debt Settlement' || tx.description.toLowerCase().includes('settle')) {
        const amt = parseFloat(tx.total_amount || 0);
        if (tx.paid_by_user_id === myUserId) {
          // I paid friend -> clears my debt to friend (increases net balance towards positive)
          delta += amt;
        } else if (tx.paid_by_user_id === friendId) {
          // Friend paid me -> clears their debt to me (decreases net balance towards 0)
          delta -= amt;
        }
      } else {
        // TP1: Check if Third-Party Non-Consumer Payer transaction
        const payerUserId = tx.paid_by_user_id;
        const creatorUserId = tx.user_id;
        const payerSplit = tx.splits?.find(s => s.member_user_id === payerUserId);
        const payerOwed = payerSplit ? parseFloat(payerSplit.owed_amount || 0) : 0;
        const isThirdPartyPayer = !!(payerUserId && creatorUserId && payerUserId !== creatorUserId && payerOwed === 0);

        if (isThirdPartyPayer) {
          // TP2: Three-Way Bilateral Debt Routing
          const totalPaidAmt = parseFloat(payerSplit?.paid_amount || tx.total_amount || 0);
          const friendOwedAmt = friendSplit ? parseFloat(friendSplit.owed_amount || 0) : (!tx.splits?.length && friendId !== payerUserId ? parseFloat(tx.total_amount || 0) : 0);
          const myOwedAmt = mySplit ? parseFloat(mySplit.owed_amount || 0) : (!tx.splits?.length && myUserId !== payerUserId ? parseFloat(tx.total_amount || 0) : 0);

          if (myUserId === creatorUserId && friendId === payerUserId) {
            // Creator vs Payer: Creator owes the payer the total amount paid
            delta -= totalPaidAmt;
          } else if (myUserId === payerUserId && friendId === creatorUserId) {
            // Payer vs Creator: Payer is owed the total amount paid
            delta += totalPaidAmt;
          } else if (myUserId === creatorUserId && friendId !== payerUserId) {
            // Creator vs Debtor: Creator is owed the debtor's split amount
            delta += friendOwedAmt;
          } else if (friendId === creatorUserId && myUserId !== payerUserId) {
            // Debtor vs Creator: Debtor owes the creator the split amount
            delta -= myOwedAmt;
          } else {
            // Debtor vs Payer: 0 (and filtered out)
            delta = 0;
          }
        } else {
          // Standard shared expense
          // What I paid for friend:
          const effectiveFriendSplitAmt = friendSplit ? parseFloat(friendSplit.owed_amount || 0) : (!tx.splits?.length && tx.user_id === friendId ? parseFloat(tx.total_amount || 0) : 0);
          if (myPaid && parseFloat(myPaid.paid_amount) > 0 && effectiveFriendSplitAmt > 0) {
            const totalPaid = payers.reduce((sum, p) => sum + parseFloat(p.paid_amount), 0) || parseFloat(tx.total_amount);
            const mySharePaidFraction = parseFloat(myPaid.paid_amount) / (totalPaid || 1);
            delta += effectiveFriendSplitAmt * mySharePaidFraction;
          } else if (tx.paid_by_user_id === myUserId && effectiveFriendSplitAmt > 0) {
            delta += effectiveFriendSplitAmt;
          }

          // What friend paid for me:
          const effectiveMySplitAmt = mySplit ? parseFloat(mySplit.owed_amount || 0) : (!tx.splits?.length && tx.user_id === myUserId ? parseFloat(tx.total_amount || 0) : 0);
          if (friendPaid && parseFloat(friendPaid.paid_amount) > 0 && effectiveMySplitAmt > 0) {
            const totalPaid = payers.reduce((sum, p) => sum + parseFloat(p.paid_amount), 0) || parseFloat(tx.total_amount);
            const friendSharePaidFraction = parseFloat(friendPaid.paid_amount) / (totalPaid || 1);
            delta -= effectiveMySplitAmt * friendSharePaidFraction;
          } else if (tx.paid_by_user_id === friendId && effectiveMySplitAmt > 0) {
            delta -= effectiveMySplitAmt;
          }
        }
      }

      runningPerCurrency[cur] = Math.round((runningPerCurrency[cur] + delta) * 100) / 100;
      resultMap[tx.transaction_id] = {
        balance: runningPerCurrency[cur],
        currency: cur
      };
    }
    return resultMap;
  }, []);

  const CATEGORY_COLORS = [
    '#f78da7', // Soft pink
    '#54a0ff', // Light blue
    '#2bcbba', // Teal/Mint green
    '#a55eea', // Soft purple
    '#ff9f43', // Orange/Coral
    '#fecb2f', // Bright yellow
    '#78e08f', // Lime green
    '#d6a2e8', // Lavender
    '#ff6b6b', // Light coral
    '#00d2d3'  // Cyan
  ];

  // Calculate a user's exact personal consumption/expense share for a transaction
  const getUserExpenseShare = (tx, myUserId) => {
    if (!tx || !myUserId) return 0;
    if (isTransferTransaction(tx) || tx.category_name === 'Debt Settlement' || (tx.description && tx.description.toLowerCase().includes('settle'))) return 0;
    const isShared = tx.is_shared === true || tx.is_shared === 1 || tx.is_shared === 'true';
    if (isShared) {
      if (tx.splits && Array.isArray(tx.splits) && tx.splits.length > 0) {
        const mySplit = tx.splits.find(s => s.member_user_id === myUserId);
        if (mySplit) {
          return parseFloat(mySplit.owed_amount || 0);
        }
        return 0;
      }
      return tx.user_id === myUserId ? parseFloat(tx.total_amount || 0) : 0;
    }
    if (tx.user_id === myUserId) {
      return parseFloat(tx.total_amount || 0);
    }
    return 0;
  };

  // Calculate a user's exact personal income share for a transaction
  const getUserIncomeShare = (tx, myUserId) => {
    if (!tx || !myUserId) return 0;
    if (isTransferTransaction(tx) || tx.category_name === 'Debt Settlement' || (tx.description && tx.description.toLowerCase().includes('settle'))) return 0;
    const isShared = tx.is_shared === true || tx.is_shared === 1 || tx.is_shared === 'true';
    if (isShared) {
      if (tx.splits && Array.isArray(tx.splits) && tx.splits.length > 0) {
        const mySplit = tx.splits.find(s => s.member_user_id === myUserId);
        if (mySplit) {
          return parseFloat(mySplit.owed_amount || 0);
        }
        return 0;
      }
      return tx.user_id === myUserId ? parseFloat(tx.total_amount || 0) : 0;
    }
    if (tx.user_id === myUserId) {
      return parseFloat(tx.total_amount || 0);
    }
    return 0;
  };

  // Category Math
  const getCategorySpendData = () => {
    const myUserId = localStorage.getItem('dhudd_user_id');
    const activeTxs = filterTransactions().filter(tx => {
      if (isTransferTransaction(tx)) return false;
      if (breakdownCurrency !== 'ALL' && tx.currency !== breakdownCurrency) return false;
      const match = findCategoryByName(categories, tx.category_name);
      const isGlobalInflow = GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (tx.category_name || '').trim().toLowerCase());
      const isOut = match ? match.type === 'OUTFLOW' : !isGlobalInflow;
      if (!isOut) return false;
      return getUserExpenseShare(tx, myUserId) > 0;
    });

    const baseCurrency = preferredCurrencies[0] || 'AED';
    let totalSpend = 0;

    if (breakdownCurrency === 'ALL') {
      const groups = {};

      activeTxs.forEach(tx => {
        const amount = getUserExpenseShare(tx, myUserId);
        if (amount <= 0) return;
        const match = findCategoryByName(categories, tx.category_name);
        const resolvedName = match ? match.name : (tx.category_name || '').trim();
        const cCode = tx.currency || baseCurrency;
        const key = `${resolvedName}___${cCode}`;
        if (!groups[key]) {
          groups[key] = {
            category_name: resolvedName,
            currency: cCode,
            amount: 0
          };
        }
        groups[key].amount += amount;
      });

      const rawList = Object.values(groups).map(g => {
        const match = findCategoryByName(categories, g.category_name);
        const converted = convertCurrency(g.amount, g.currency, baseCurrency);
        totalSpend += converted;
        const currSym = getCurrencySymbol(g.currency) || g.currency;
        const catName = match ? match.name : g.category_name;
        return {
          id: `${catName}___${g.currency}`,
          name: catName,
          displayName: `${catName} (${currSym})`,
          currency: g.currency,
          amount: g.amount,
          convertedAmount: converted,
          icon: match ? match.symbol : '💸'
        };
      });

      rawList.sort((a, b) => b.convertedAmount - a.convertedAmount);

      let list = [];
      if (rawList.length > 9) {
        const topCategories = rawList.slice(0, 8);
        const otherCategories = rawList.slice(8);
        const otherConvertedSum = otherCategories.reduce((sum, item) => sum + item.convertedAmount, 0);
        list = [
          ...topCategories,
          {
            id: 'Others___ALL',
            name: 'Others',
            displayName: 'Others',
            currency: baseCurrency,
            amount: otherConvertedSum,
            convertedAmount: otherConvertedSum,
            icon: '📁'
          }
        ];
      } else {
        list = rawList;
      }

      list = list.map((item, idx) => ({
        ...item,
        color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length]
      }));

      return { list, totalSpend, isAllCurrency: true, baseCurrency };
    } else {
      const categoriesSum = {};

      activeTxs.forEach(tx => {
        const amount = getUserExpenseShare(tx, myUserId);
        if (amount <= 0) return;
        const match = findCategoryByName(categories, tx.category_name);
        const resolvedName = match ? match.name : (tx.category_name || '').trim();
        categoriesSum[resolvedName] = (categoriesSum[resolvedName] || 0) + amount;
        totalSpend += amount;
      });

      const sortedList = Object.entries(categoriesSum)
        .map(([name, sum]) => {
          const match = findCategoryByName(categories, name);
          const catName = match ? match.name : name;
          return {
            id: catName,
            name: catName,
            displayName: catName,
            currency: breakdownCurrency,
            amount: sum,
            convertedAmount: sum,
            icon: match ? match.symbol : '💸'
          };
        })
        .sort((a, b) => b.amount - a.amount);

      let list = [];
      if (sortedList.length > 9) {
        const topCategories = sortedList.slice(0, 8);
        const otherCategories = sortedList.slice(8);
        const otherSum = otherCategories.reduce((sum, item) => sum + item.amount, 0);
        list = [
          ...topCategories,
          {
            id: 'Others',
            name: 'Others',
            displayName: 'Others',
            currency: breakdownCurrency,
            amount: otherSum,
            convertedAmount: otherSum,
            icon: '📁'
          }
        ];
      } else {
        list = sortedList;
      }

      list = list.map((item, idx) => ({
        ...item,
        color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length]
      }));

      return { list, totalSpend, isAllCurrency: false, baseCurrency: breakdownCurrency };
    }
  };

  const getCategoryIncomeData = () => {
    const myUserId = localStorage.getItem('dhudd_user_id');
    const activeTxs = filterTransactions().filter(tx => {
      if (isTransferTransaction(tx)) return false;
      if (breakdownCurrency !== 'ALL' && tx.currency !== breakdownCurrency) return false;
      const match = findCategoryByName(categories, tx.category_name);
      const isGlobalInflow = GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (tx.category_name || '').trim().toLowerCase());
      const isIn = match ? match.type === 'INFLOW' : isGlobalInflow;
      if (!isIn) return false;
      return getUserIncomeShare(tx, myUserId) > 0;
    });

    const baseCurrency = preferredCurrencies[0] || 'AED';
    let totalIncome = 0;

    if (breakdownCurrency === 'ALL') {
      const groups = {};

      activeTxs.forEach(tx => {
        const amount = getUserIncomeShare(tx, myUserId);
        if (amount <= 0) return;
        const match = findCategoryByName(categories, tx.category_name);
        const resolvedName = match ? match.name : (tx.category_name || '').trim();
        const cCode = tx.currency || baseCurrency;
        const key = `${resolvedName}___${cCode}`;
        if (!groups[key]) {
          groups[key] = {
            category_name: resolvedName,
            currency: cCode,
            amount: 0
          };
        }
        groups[key].amount += amount;
      });

      const rawList = Object.values(groups).map(g => {
        const match = findCategoryByName(categories, g.category_name);
        const converted = convertCurrency(g.amount, g.currency, baseCurrency);
        totalIncome += converted;
        const currSym = getCurrencySymbol(g.currency) || g.currency;
        const catName = match ? match.name : g.category_name;
        return {
          id: `${catName}___${g.currency}`,
          name: catName,
          displayName: `${catName} (${currSym})`,
          currency: g.currency,
          amount: g.amount,
          convertedAmount: converted,
          icon: match ? match.symbol : '💰'
        };
      });

      rawList.sort((a, b) => b.convertedAmount - a.convertedAmount);

      let list = [];
      if (rawList.length > 9) {
        const topCategories = rawList.slice(0, 8);
        const otherCategories = rawList.slice(8);
        const otherConvertedSum = otherCategories.reduce((sum, item) => sum + item.convertedAmount, 0);
        list = [
          ...topCategories,
          {
            id: 'Others___ALL',
            name: 'Others',
            displayName: 'Others',
            currency: baseCurrency,
            amount: otherConvertedSum,
            convertedAmount: otherConvertedSum,
            icon: '📁'
          }
        ];
      } else {
        list = rawList;
      }

      list = list.map((item, idx) => ({
        ...item,
        color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length]
      }));

      return { list, totalIncome, isAllCurrency: true, baseCurrency };
    } else {
      const categoriesSum = {};

      activeTxs.forEach(tx => {
        const amount = getUserIncomeShare(tx, myUserId);
        if (amount <= 0) return;
        const match = findCategoryByName(categories, tx.category_name);
        const resolvedName = match ? match.name : (tx.category_name || '').trim();
        categoriesSum[resolvedName] = (categoriesSum[resolvedName] || 0) + amount;
        totalIncome += amount;
      });

      const sortedList = Object.entries(categoriesSum)
        .map(([name, sum]) => {
          const match = findCategoryByName(categories, name);
          const catName = match ? match.name : name;
          return {
            id: catName,
            name: catName,
            displayName: catName,
            currency: breakdownCurrency,
            amount: sum,
            convertedAmount: sum,
            icon: match ? match.symbol : '💰'
          };
        })
        .sort((a, b) => b.amount - a.amount);

      let list = [];
      if (sortedList.length > 9) {
        const topCategories = sortedList.slice(0, 8);
        const otherCategories = sortedList.slice(8);
        const otherSum = otherCategories.reduce((sum, item) => sum + item.amount, 0);
        list = [
          ...topCategories,
          {
            id: 'Others',
            name: 'Others',
            displayName: 'Others',
            currency: breakdownCurrency,
            amount: otherSum,
            convertedAmount: otherSum,
            icon: '📁'
          }
        ];
      } else {
        list = sortedList;
      }

      list = list.map((item, idx) => ({
        ...item,
        color: CATEGORY_COLORS[idx % CATEGORY_COLORS.length]
      }));

      return { list, totalIncome, isAllCurrency: false, baseCurrency: breakdownCurrency };
    }
  };

  const handleOpenDrillDown = (catItemOrName, type) => {
    const isOutflow = type === 'outflow';
    const data = isOutflow ? getCategorySpendData() : getCategoryIncomeData();
    const list = data.list;

    const chartCat = typeof catItemOrName === 'object' && catItemOrName !== null
      ? (list.find(c => c.id === catItemOrName.id) || catItemOrName)
      : (list.find(c => c.id === catItemOrName || (c.name || '').trim().toLowerCase() === (catItemOrName || '').trim().toLowerCase()) || list[0]);
    if (!chartCat) return;

    const targetCategoryName = chartCat.name;
    const targetCurrency = chartCat.currency;
    const myUserId = localStorage.getItem('dhudd_user_id');

    // Filter transactions contributing to this segment
    const allTxs = filterTransactions().filter(tx => {
      if (isTransferTransaction(tx)) return false;
      const match = findCategoryByName(categories, tx.category_name);
      const isGlobalInflow = GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (tx.category_name || '').trim().toLowerCase());
      const isCorrectType = match 
        ? (isOutflow ? match.type === 'OUTFLOW' : match.type === 'INFLOW') 
        : (isOutflow ? !isGlobalInflow : isGlobalInflow);
      if (!isCorrectType) return false;

      const userShare = isOutflow ? getUserExpenseShare(tx, myUserId) : getUserIncomeShare(tx, myUserId);
      if (userShare <= 0) return false;

      const txCatResolvedName = match ? match.name : (tx.category_name || '').trim();

      if (targetCategoryName === 'Others') {
        return !list.slice(0, 8).some(c => (c.name || '').toLowerCase() === txCatResolvedName.toLowerCase());
      } else {
        const matchesCat = txCatResolvedName.toLowerCase() === (targetCategoryName || '').trim().toLowerCase() ||
                           (tx.category_name || '').trim().toLowerCase() === (targetCategoryName || '').trim().toLowerCase();
        const matchesCurr = (breakdownCurrency === 'ALL' && targetCurrency)
          ? (tx.currency || preferredCurrencies[0] || 'AED') === targetCurrency
          : (breakdownCurrency !== 'ALL' ? tx.currency === breakdownCurrency : true);
        return matchesCat && matchesCurr;
      }
    });

    setDrillDownCategoryData({
      categoryName: chartCat.displayName || chartCat.name,
      currency: chartCat.currency || breakdownCurrency || preferredCurrencies[0] || 'AED',
      type,
      total: chartCat.amount,
      icon: chartCat.icon,
      color: chartCat.color,
      list: allTxs
    });
  };

  const getDisplayAmount = (tx) => {
    const isOut = !GLOBAL_INFLOW_CATEGORIES.includes(tx.category_name) && !inflowCats.some(c => c.name === tx.category_name);
    if (isOut && tx.is_shared && tx.splits) {
      const myUserId = localStorage.getItem('dhudd_user_id');
      const mySplit = tx.splits.find(s => s.member_user_id === myUserId);
      if (mySplit) {
        return { amount: parseFloat(mySplit.owed_amount), isSplitPortion: true };
      }
    }
    return { amount: parseFloat(tx.total_amount), isSplitPortion: false };
  };

  const getNetBalance = () => {
    if (selectedAccountIds && !selectedAccountIds.includes('ALL')) {
      if (selectedAccountIds.length === 0) return 0;
      return accounts
        .filter(acc => selectedAccountIds.includes(acc.account_id))
        .reduce((sum, acc) => {
          const factor = acc.type === 'CREDIT_CARD' ? -1 : 1;
          return sum + (factor * parseFloat(acc.current_balance));
        }, 0);
    }
    return accounts
      .filter(a => a.include_in_total && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0')
      .reduce((sum, acc) => {
        const factor = acc.type === 'CREDIT_CARD' ? -1 : 1;
        return sum + (factor * parseFloat(acc.current_balance));
      }, 0);
  };

  // Categories CRUD
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newCatName || !newCatSymbol) return;

    try {
      if (editingCategory) {
        await apiClient.request(`/categories/${editingCategory.category_id}`, 'PUT', {
          name: newCatName,
          symbol: newCatSymbol
        });
        showToast('Category updated!', 'success');
        setEditingCategory(null);
      } else {
        await apiClient.request('/categories', 'POST', {
          name: newCatName,
          type: newCatType,
          symbol: newCatSymbol
        });
        showToast('Category created!', 'success');
      }
      setNewCatName('');
      setNewCatSymbol('📁');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleStartEditCategory = (cat) => {
    setEditingCategory(cat);
    setNewCatName(cat.name);
    setNewCatSymbol(cat.symbol);
    setNewCatType(cat.type);
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setNewCatName('');
    setNewCatSymbol('📁');
  };

  const handleCreateInlineCategory = async () => {
    if (!inlineCatName.trim() || !inlineCatSymbol.trim()) {
      showToast('Name and emoji are required', 'error');
      return;
    }
    try {
      const type = activeModal === 'outflow' ? 'OUTFLOW' : 'INFLOW';
      const res = await apiClient.request('/categories', 'POST', {
        name: inlineCatName.trim(),
        symbol: inlineCatSymbol.trim(),
        type
      });
      await refreshData();
      setTxCategory(res.name || inlineCatName.trim());
      setShowInlineCatForm(false);
      setInlineCatName('');
      setInlineCatSymbol('');
      showToast('Category created', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await apiClient.request(`/categories/${catId}`, 'DELETE');
      showToast('Category deleted', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Currency Rates Matrix Addition
  const handleCreateCurrencyRate = async (e) => {
    e.preventDefault();
    if (!newRateValue || !newRateDate) return;

    try {
      await apiClient.request('/currency/rates', 'POST', {
        from_currency: newRateFrom,
        to_currency: newRateTo,
        rate: parseFloat(newRateValue),
        rate_date: newRateDate
      });
      showToast('Conversion rate saved!', 'success');
      setNewRateValue('');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Account CRUD helpers
  const handleCreateBankAccountDirect = async (e) => {
    e.preventDefault();
    if (!newAccName || !newAccBalance) return;
    try {
      await apiClient.request('/accounts', 'POST', {
        name: newAccName,
        type: newAccType,
        currency: newAccCurrency,
        current_balance: parseFloat(newAccBalance),
        include_in_total: newAccIncludeInTotal
      });
      showToast('Bank Account created!', 'success');
      setNewAccName('');
      setNewAccBalance('');
      setNewAccIncludeInTotal(true);
      setIsAddingAccountDirect(false);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleCreateCreditCardDirect = async (e) => {
    e.preventDefault();
    if (!newCardName || !newCardLimit) return;
    try {
      let current_balance = parseFloat(newCardBalance || 0);
      current_balance = -current_balance;

      await apiClient.request('/accounts', 'POST', {
        name: newCardName,
        type: 'CREDIT_CARD',
        currency: newCardCurrency,
        current_balance,
        include_in_total: newCardIncludeInTotal,
        credit_limit: parseFloat(newCardLimit),
        statement_day: parseInt(newCardClosing),
        due_day: parseInt(newCardDue),
        monthly_spend_target: parseFloat(newCardSpendTarget || 0) || null,
        monthly_spend_target_type: newCardSpendTargetType
      });
      showToast('Credit Card added!', 'success');
      setNewCardName('');
      setNewCardLimit('');
      setNewCardBalance('');
      setNewCardSpendTarget('');
      setNewCardSpendTargetType('CALENDAR');
      setNewCardIncludeInTotal(true);
      setIsAddingCard(false);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteAccountDirect = async (accountId, name) => {
    if (!window.confirm(`Delete the account "${name}"? This deletes its configurations and settings.`)) return;
    try {
      await apiClient.request(`/accounts/${accountId}`, 'DELETE');
      showToast('Account deleted successfully', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Synchronize draft states when opening Manage Accounts or Manage Credit Cards
  useEffect(() => {
    if (activeSheet === 'accounts_manage') {
      const draft = {};
      accounts.forEach(a => {
        draft[a.account_id] = {
          name: a.name || '',
          balance: formatInputValue(a.current_balance),
          include_in_total: a.include_in_total === true || a.include_in_total === 1
        };
      });
      setAccountsDraft(draft);
    } else {
      setAccountsDraft({});
    }

    if (activeSheet === 'cards_manage') {
      const draft = {};
      accounts.filter(a => a.type === 'CREDIT_CARD').forEach(c => {
        draft[c.account_id] = {
          name: c.name || '',
          owedBalance: formatInputValue(-parseFloat(c.current_balance || 0)),
          limit: c.credit_limit != null ? String(c.credit_limit) : '',
          statement: c.statement_day != null ? String(c.statement_day) : '',
          due: c.due_day != null ? String(c.due_day) : '',
          spendTarget: c.monthly_spend_target != null ? String(c.monthly_spend_target) : '',
          spendTargetType: c.monthly_spend_target_type || 'CALENDAR',
          include_in_total: c.include_in_total === true || c.include_in_total === 1,
          master_card_id: c.master_card_id || null,
          master_card_name: c.master_card_name || null,
          master_bank_name: c.master_bank_name || null,
          master_card_tier: c.master_card_tier || null
        };
      });
      setCardsDraft(draft);
    } else {
      setCardsDraft({});
    }
  }, [activeSheet]);

  const handleAccountDraftChange = (accountId, field, value) => {
    setAccountsDraft(prev => {
      const acc = accounts.find(a => a.account_id === accountId);
      const existing = prev[accountId] || {
        name: acc?.name || '',
        balance: formatInputValue(acc?.current_balance),
        include_in_total: acc?.include_in_total === true || acc?.include_in_total === 1
      };
      return {
        ...prev,
        [accountId]: {
          ...existing,
          [field]: value
        }
      };
    });
  };

  const handleSaveAllAccounts = async () => {
    setIsSavingAccounts(true);
    try {
      const nonCreditAccounts = accounts.filter(a => 
        a.type !== 'CREDIT_CARD' && 
        a.account_type !== 'CREDIT_CARD' && 
        a.account_type !== 'EXTERNAL_DEBT' && 
        !a.is_external_debt && 
        !(a.name && a.name.toLowerCase().includes('external debt'))
      );

      let saveCount = 0;
      for (const acc of nonCreditAccounts) {
        const draft = accountsDraft[acc.account_id];
        if (!draft) continue;

        const newName = (draft.name || '').trim();
        const newBalance = parseFloat(draft.balance);
        const newInclude = Boolean(draft.include_in_total);

        const origName = (acc.name || '').trim();
        const origBalance = parseFloat(acc.current_balance);
        const origInclude = acc.include_in_total === true || acc.include_in_total === 1;

        const isNameChanged = newName !== origName;
        const isBalChanged = !isNaN(newBalance) && Math.abs(newBalance - origBalance) > 0.0001;
        const isIncludeChanged = newInclude !== origInclude;

        if (isNameChanged || isBalChanged || isIncludeChanged) {
          if (!newName) {
            showToast('Account name cannot be empty', 'error');
            setIsSavingAccounts(false);
            return;
          }
          await apiClient.request(`/accounts/${acc.account_id}`, 'PUT', {
            name: newName,
            current_balance: isNaN(newBalance) ? acc.current_balance : newBalance,
            include_in_total: newInclude,
            credit_limit: acc.credit_limit,
            statement_day: acc.statement_day,
            due_day: acc.due_day,
            is_active: acc.is_active !== false && acc.is_active !== 0 && acc.is_active !== '0'
          });
          saveCount++;
        }
      }

      if (saveCount > 0) {
        showToast(`${saveCount} account${saveCount > 1 ? 's' : ''} saved!`, 'success');
        await refreshData();
      } else {
        showToast('No changes to save', 'info');
      }
      setAccountsDraft({});
      setActiveSheet(null);
    } catch (err) {
      showToast(err.message || 'Failed to save accounts', 'error');
    } finally {
      setIsSavingAccounts(false);
    }
  };

  const handleCardDraftChange = (cardId, field, value) => {
    setCardsDraft(prev => {
      const c = accounts.find(a => a.account_id === cardId);
      const existing = prev[cardId] || {
        name: c?.name || '',
        owedBalance: formatInputValue(-parseFloat(c?.current_balance || 0)),
        limit: c?.credit_limit != null ? String(c.credit_limit) : '',
        statement: c?.statement_day != null ? String(c.statement_day) : '',
        due: c?.due_day != null ? String(c.due_day) : '',
        spendTarget: c?.monthly_spend_target != null ? String(c.monthly_spend_target) : '',
        spendTargetType: c?.monthly_spend_target_type || 'CALENDAR',
        include_in_total: c?.include_in_total === true || c?.include_in_total === 1,
        master_card_id: c?.master_card_id || null,
        master_card_name: c?.master_card_name || null,
        master_bank_name: c?.master_bank_name || null,
        master_card_tier: c?.master_card_tier || null
      };
      return {
        ...prev,
        [cardId]: {
          ...existing,
          [field]: value
        }
      };
    });
  };

  const handleSaveAllCards = async () => {
    setIsSavingCards(true);
    try {
      const cardAccounts = accounts.filter(a => a.type === 'CREDIT_CARD');

      let saveCount = 0;
      for (const card of cardAccounts) {
        const draft = cardsDraft[card.account_id];
        if (!draft) continue;

        const newName = (draft.name || '').trim();
        const newOwedBalance = parseFloat(draft.owedBalance);
        const newLimit = parseFloat(draft.limit);
        const newStatement = parseInt(draft.statement);
        const newDue = parseInt(draft.due);
        const newSpendTarget = parseFloat(draft.spendTarget || 0);
        const newSpendTargetType = draft.spendTargetType || 'CALENDAR';
        const newInclude = Boolean(draft.include_in_total);
        const newMasterCardId = draft.master_card_id !== undefined ? draft.master_card_id : (card.master_card_id || null);

        const origName = (card.name || '').trim();
        const origOwedBalance = -parseFloat(card.current_balance || 0);
        const origLimit = parseFloat(card.credit_limit || 0);
        const origStatement = parseInt(card.statement_day || 1);
        const origDue = parseInt(card.due_day || 1);
        const origSpendTarget = parseFloat(card.monthly_spend_target || 0);
        const origSpendTargetType = card.monthly_spend_target_type || 'CALENDAR';
        const origInclude = card.include_in_total === true || card.include_in_total === 1;
        const origMasterCardId = card.master_card_id || null;

        const isNameChanged = newName !== origName;
        const isBalChanged = !isNaN(newOwedBalance) && Math.abs(newOwedBalance - origOwedBalance) > 0.0001;
        const isLimitChanged = !isNaN(newLimit) && Math.abs(newLimit - origLimit) > 0.0001;
        const isStatementChanged = !isNaN(newStatement) && newStatement !== origStatement;
        const isDueChanged = !isNaN(newDue) && newDue !== origDue;
        const isTargetChanged = Math.abs(newSpendTarget - origSpendTarget) > 0.0001;
        const isTargetTypeChanged = newSpendTargetType !== origSpendTargetType;
        const isIncludeChanged = newInclude !== origInclude;
        const isMasterCardChanged = newMasterCardId !== origMasterCardId;

        if (
          isNameChanged || isBalChanged || isLimitChanged ||
          isStatementChanged || isDueChanged || isTargetChanged ||
          isTargetTypeChanged || isIncludeChanged || isMasterCardChanged
        ) {
          if (!newName) {
            showToast('Card name cannot be empty', 'error');
            setIsSavingCards(false);
            return;
          }
          await apiClient.request(`/accounts/${card.account_id}`, 'PUT', {
            name: newName,
            nickname: newName,
            master_card_id: newMasterCardId,
            current_balance: isNaN(newOwedBalance) ? card.current_balance : -newOwedBalance,
            include_in_total: newInclude,
            credit_limit: isNaN(newLimit) ? card.credit_limit : newLimit,
            statement_day: isNaN(newStatement) ? card.statement_day : newStatement,
            due_day: isNaN(newDue) ? card.due_day : newDue,
            monthly_spend_target: newSpendTarget > 0 ? newSpendTarget : null,
            monthly_spend_target_type: newSpendTargetType,
            is_active: card.is_active !== false && card.is_active !== 0 && card.is_active !== '0'
          });
          saveCount++;
        }
      }

      if (saveCount > 0) {
        showToast(`${saveCount} credit card${saveCount > 1 ? 's' : ''} saved!`, 'success');
        await refreshData();
      } else {
        showToast('No changes to save', 'info');
      }
      setCardsDraft({});
      setActiveSheet(null);
    } catch (err) {
      showToast(err.message || 'Failed to save credit cards', 'error');
    } finally {
      setIsSavingCards(false);
    }
  };

  // Preferred Currencies & Rates
  const handleAddPreferredCurrency = async (e) => {
    e.preventDefault();
    if (!newPrefCurrency) return;
    try {
      await apiClient.request('/currencies/preferred', 'POST', { currency: newPrefCurrency });
      showToast('Preferred currency added!', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleRemovePreferredCurrency = async (curr) => {
    if (!window.confirm(`Remove ${curr} from preferred list?`)) return;
    try {
      await apiClient.request(`/currencies/preferred/${curr}`, 'DELETE');
      showToast('Preferred currency removed', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteCurrencyRate = async (rate) => {
    if (!window.confirm(`Delete rate for ${rate.from_currency} ➔ ${rate.to_currency} on ${rate.rate_date}?`)) return;
    try {
      await apiClient.request(`/currency/rates/${rate.from_currency}/${rate.to_currency}/${rate.rate_date}`, 'DELETE');
      showToast('Rate deleted', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Passcode Saving
  const handleSavePasscode = async () => {
    if (pinInput.length !== 4) {
      return showToast('Passcode must be exactly 4 digits!', 'error');
    }
    try {
      await apiClient.request('/auth/change-passcode', 'POST', { newPin: pinInput });
      setSavedPin(pinInput);
      showToast('Passcode updated successfully!', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleSaveProfile = async () => {
    if (profileDob && profileDob > getMaxDob7YearsAgo()) {
      return showToast('Invalid date of birth selected.', 'error');
    }
    const fullProfilePhone = formatPhoneWithCountryCode(profileCountryCode, profileCustomCc, profilePhoneDigits);
    try {
      await apiClient.request('/users/profile', 'PUT', {
        display_name: profileDisplayName,
        bio: profileBio,
        country: profileCountry,
        date_of_birth: profileDob,
        gender: profileGender,
        phone_number: fullProfilePhone
      });
      showToast('Profile updated successfully!', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Profile Picture File Selection -> Opens Interactive Cropper Editor
  const handleFileSelectedForCropping = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validMimes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validMimes.includes(file.type.toLowerCase())) {
      return showToast('Invalid format. Please select a JPEG, PNG, or WebP image.', 'error');
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      setRawCropImageSrc(evt.target.result);
      setCropZoom(1);
      setCropOffsetX(0);
      setCropOffsetY(0);
      setCropRotation(0);
      setAvatarCropModalOpen(true);
    };
    reader.onerror = () => showToast('Failed to read selected image', 'error');
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  // Render & Compress Cropped Avatar Image onto Canvas (<1MB Guaranteed)
  const handleApplyCrop = async () => {
    if (!rawCropImageSrc) return;
    setIsUploadingAvatar(true);

    try {
      const cleanDataUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const outputDim = 512; // 512x512 crisp square profile canvas
          canvas.width = outputDim;
          canvas.height = outputDim;
          const ctx = canvas.getContext('2d');

          // Dark background for transparent areas
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(0, 0, outputDim, outputDim);

          ctx.save();
          ctx.translate(outputDim / 2, outputDim / 2);
          ctx.rotate((cropRotation * Math.PI) / 180);
          ctx.scale(cropZoom, cropZoom);

          const baseScale = Math.max(outputDim / img.width, outputDim / img.height);
          const drawW = img.width * baseScale;
          const drawH = img.height * baseScale;

          const finalX = -drawW / 2 + cropOffsetX * 1.5;
          const finalY = -drawH / 2 + cropOffsetY * 1.5;

          ctx.drawImage(img, finalX, finalY, drawW, drawH);
          ctx.restore();

          let quality = 0.85;
          let compressedUrl = canvas.toDataURL('image/webp', quality);

          // Guarantee compressed output is under 1MB
          while (compressedUrl.length > 1.33 * 1024 * 1024 && quality > 0.3) {
            quality -= 0.1;
            compressedUrl = canvas.toDataURL('image/jpeg', quality);
          }

          resolve(compressedUrl);
        };
        img.onerror = () => reject(new Error('Failed loading image into editor'));
        img.src = rawCropImageSrc;
      });

      const res = await apiClient.request('/users/avatar', 'POST', { image: cleanDataUrl });
      setProfileAvatarUrl(res.avatar_url);
      if (res.avatar_url) {
        localStorage.setItem('tezer_avatar_url', res.avatar_url);
        localStorage.setItem('dhudd_avatar_url', res.avatar_url);
      }
      setAvatarCropModalOpen(false);
      setRawCropImageSrc(null);
      showToast('Profile picture updated successfully!', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message || 'Failed processing profile picture', 'error');
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      await apiClient.request('/users/avatar', 'DELETE');
      setProfileAvatarUrl(null);
      localStorage.removeItem('tezer_avatar_url');
      localStorage.removeItem('dhudd_avatar_url');
      showToast('Profile picture removed!', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Top 5 categories counter by frequency
  const getTopCategories = (type, allCategories, limit = 5) => {
    const filteredCats = allCategories.filter(c => c.type === type);
    const frequencies = {};
    transactions.forEach(tx => {
      frequencies[tx.category_name] = (frequencies[tx.category_name] || 0) + 1;
    });
    const sorted = [...filteredCats].sort((a, b) => {
      const freqA = frequencies[a.name] || 0;
      const freqB = frequencies[b.name] || 0;
      if (freqB !== freqA) return freqB - freqA;
      return a.name.localeCompare(b.name);
    });
    return sorted.slice(0, limit);
  };

  // Friends Addition
  const handleAddFriend = async (e) => {
    e.preventDefault();
    if (isPlaceholderFriend) {
      if (!placeholderFriendName) return;
      const fullPlaceholderPhone = formatPhoneWithCountryCode(placeholderCountryCode, placeholderCustomCc, placeholderPhoneDigits);
      try {
        const newFriend = await apiClient.request('/friends', 'POST', {
          is_placeholder: true,
          name: placeholderFriendName,
          description: placeholderFriendDesc,
          phone_number: fullPlaceholderPhone || undefined
        });

        const targetFriendId = newFriend?.friend_id || newFriend?.user_id;
        if (placeholderAdjustBalance && placeholderInitialBalance && targetFriendId) {
          const amt = parseFloat(placeholderInitialBalance);
          if (!isNaN(amt) && amt !== 0) {
            await apiClient.request('/friends/adjust-balance', 'POST', {
              friend_user_id: targetFriendId,
              new_balance: amt,
              currency: placeholderInitialCurrency || preferredCurrencies[0] || 'AED'
            });
          }
        }

        showToast('Placeholder friend added!', 'success');
        trackEvent('friend_added', { is_placeholder: true, has_initial_balance: Boolean(placeholderAdjustBalance) });
        setPlaceholderFriendName('');
        setPlaceholderFriendDesc('');
        setPlaceholderPhoneDigits('');
        setPlaceholderAdjustBalance(false);
        setPlaceholderInitialBalance('');
        setPlaceholderInitialCurrency(preferredCurrencies[0] || 'AED');
        setIsPlaceholderFriend(false);
        setActiveModal(null);
        refreshData();
      } catch (err) {
        showToast(err.message, 'error');
      }
    } else {
      if (addFriendMethod === 'PHONE') {
        if (!newFriendPhone) return showToast('Please enter mobile number', 'error');
        const cleanDigits = newFriendPhone.replace(/\D/g, '');
        if (!cleanDigits) return showToast('Please enter a valid mobile number', 'error');
        
        const cc = newFriendCountryCode === 'CUSTOM' ? customCountryCode.trim() : newFriendCountryCode;
        if (!cc) return showToast('Please select or enter a country code', 'error');
        
        const formattedCC = cc.startsWith('+') ? cc : `+${cc}`;
        const fullPhone = `${formattedCC}${cleanDigits}`;

        try {
          const res = await apiClient.request('/friends', 'POST', { phone_number: fullPhone });
          const targetFriendId = res?.friend_id || res?.user_id;
          if (friendInitialAdjustBalance && friendInitialBalance && targetFriendId) {
            const amt = parseFloat(friendInitialBalance);
            if (!isNaN(amt) && amt !== 0) {
              await apiClient.request('/friends/adjust-balance', 'POST', {
                friend_user_id: targetFriendId,
                new_balance: amt,
                currency: friendInitialCurrency || preferredCurrencies[0] || 'AED'
              });
            }
          }

          showToast('Friend added successfully!', 'success');
          trackEvent('friend_added', { is_placeholder: false, method: 'PHONE' });
          setNewFriendPhone('');
          setFriendInitialAdjustBalance(false);
          setFriendInitialBalance('');
          setFriendInitialCurrency(preferredCurrencies[0] || 'AED');
          setActiveModal(null);
          refreshData();
        } catch (err) {
          showToast(err.message, 'error');
        }
      } else {
        if (!newFriendEmail) return;
        try {
          const res = await apiClient.request('/friends', 'POST', { email: newFriendEmail });
          const targetFriendId = res?.friend_id || res?.user_id;
          if (friendInitialAdjustBalance && friendInitialBalance && targetFriendId) {
            const amt = parseFloat(friendInitialBalance);
            if (!isNaN(amt) && amt !== 0) {
              await apiClient.request('/friends/adjust-balance', 'POST', {
                friend_user_id: targetFriendId,
                new_balance: amt,
                currency: friendInitialCurrency || preferredCurrencies[0] || 'AED'
              });
            }
          }

          showToast('Friend added!', 'success');
          trackEvent('friend_added', { is_placeholder: false, method: 'EMAIL' });
          setNewFriendEmail('');
          setFriendInitialAdjustBalance(false);
          setFriendInitialBalance('');
          setFriendInitialCurrency(preferredCurrencies[0] || 'AED');
          setActiveModal(null);
          refreshData();
        } catch (err) {
          showToast(err.message, 'error');
        }
      }
    }
  };

  // Mobile & PWA Contact Sync & Friend Match Scanner
  const sendContactsToScan = async (emailsList = [], phonesList = []) => {
    trackEvent('contact_sync_started', { emails_count: emailsList.length, phones_count: phonesList.length });
    setIsScanningContacts(true);
    try {
      const res = await apiClient.request('/friends/scan-contacts', 'POST', {
        emails: emailsList,
        phone_numbers: phonesList
      });

      if (res && res.matches) {
        setContactScanResults(res.matches);
        if (res.unregistered_contacts) {
          setUnregisteredContacts(res.unregistered_contacts);
        }
        const newMatchIds = res.matches.filter(m => !m.is_already_friend).map(m => m.user_id);
        setSelectedContactUserIds(newMatchIds);
        setContactScanModalOpen(true);

        if (res.total_new_matches > 0) {
          showToast(`Found ${res.total_new_matches} contact(s) registered on tezer!`, 'success');
        } else if (res.total_matches > 0) {
          showToast(`Found ${res.total_matches} contact(s) on tezer (already your friends)!`, 'info');
        } else {
          showToast('No registered tezer users found in scanned contacts.', 'info');
        }
      }
    } catch (err) {
      showToast(err.message || 'Failed scanning contacts', 'error');
    } finally {
      setIsScanningContacts(false);
    }
  };

  const handleAddUnregisteredAsPlaceholder = async (contact) => {
    const contactPhone = contact.phone_number || contact.email || '';
    const confirmAdd = window.confirm(`Add "${contact.name}" (${contactPhone}) as a placeholder friend?`);
    if (!confirmAdd) return;

    try {
      await apiClient.request('/friends', 'POST', {
        name: contact.name,
        phone_number: contact.phone_number || undefined,
        email: contact.email || undefined,
        is_placeholder: true
      });
      showToast(`Added "${contact.name}" as a placeholder friend!`, 'success');
      setUnregisteredContacts(prev => prev.filter(c => c !== contact));
      refreshData();
    } catch (err) {
      showToast(err.message || 'Failed adding placeholder friend', 'error');
    }
  };

  const handleNativeContactSync = async () => {
    if ('contacts' in navigator && 'ContactsManager' in window) {
      try {
        const props = ['name', 'email', 'tel'];
        const selected = await navigator.contacts.select(props, { multiple: true });
        if (selected && selected.length > 0) {
          const emails = [];
          const phones = [];
          selected.forEach(c => {
            if (c.email) c.email.forEach(e => emails.push(e));
            if (c.tel) c.tel.forEach(t => phones.push(t));
          });
          await sendContactsToScan(emails, phones);
          return;
        }
      } catch (err) {
        console.log('Native contact selection cancelled or unavailable:', err);
      }
    }
    // Fallback if native cancelled or unsupported: open scan modal
    setContactScanModalOpen(true);
  };

  const handleScanTextOrFileContacts = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!contactManualInput.trim()) {
      return showToast('Please enter or paste phone numbers or emails', 'error');
    }

    const lines = contactManualInput.split(/[\n,;]+/);
    const emails = [];
    const phones = [];

    lines.forEach(raw => {
      const item = raw.trim();
      if (!item) return;
      if (item.includes('@')) {
        emails.push(item);
      } else {
        phones.push(item);
      }
    });

    await sendContactsToScan(emails, phones);
  };

  const handleBulkAddSelectedFriends = async () => {
    if (selectedContactUserIds.length === 0) {
      return showToast('Please select at least one contact to add', 'error');
    }

    setIsBulkAddingFriends(true);
    try {
      const res = await apiClient.request('/friends/add-bulk', 'POST', {
        friend_user_ids: selectedContactUserIds
      });
      showToast(res.message || 'Friends added successfully!', 'success');
      setContactScanModalOpen(false);
      setContactScanResults([]);
      setSelectedContactUserIds([]);
      refreshData();
    } catch (err) {
      showToast(err.message || 'Failed adding selected friends', 'error');
    } finally {
      setIsBulkAddingFriends(false);
    }
  };

  const handleEditPlaceholderFriend = async (e) => {
    e.preventDefault();
    if (!editPlaceholderName) return;
    const fullEditPhone = formatPhoneWithCountryCode(editPlaceholderCountryCode, editPlaceholderCustomCc, editPlaceholderPhoneDigits);
    try {
      await apiClient.request(`/friends/placeholder/${editingPlaceholderFriendId}`, 'PUT', {
        name: editPlaceholderName,
        phone_number: fullEditPhone || undefined
      });
      showToast('Placeholder friend updated!', 'success');
      setEditingPlaceholderFriendId(null);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // --- INVESTMENTS EVENT HANDLERS ---
  const handleOpenAssetSearch = () => {
    setIsAssetSearchOpen(true);
  };

  const handleSelectAssetFromSearch = (asset) => {
    setIsAssetSearchOpen(false);
    setNewInvName(asset.name || '');
    setNewInvSymbol(asset.symbol || '');
    setNewInvType(asset.type || 'EQUITY');
    setNewInvSector(asset.sector || 'Technology');
    setNewInvRegion(asset.region || 'US');
    setNewInvCurrency(asset.currency || preferredCurrencies[0] || 'AED');
    setNewInvPrice(asset.currentPrice ? asset.currentPrice.toString() : '');
    setNewInvMarketCap(asset.marketCap || 'Large');
    setNewInvQty('');
    setNewInvTargetPct('');
    setActiveModal('add_investment');
  };

  const handleManualCustomAssetFromSearch = (initialQuery = '') => {
    setIsAssetSearchOpen(false);
    setNewInvName(initialQuery || '');
    setNewInvSymbol(initialQuery ? initialQuery.toUpperCase().slice(0, 8) : '');
    setNewInvType('EQUITY');
    setNewInvSector('Technology');
    setNewInvRegion('US');
    setNewInvCurrency(preferredCurrencies[0] || 'AED');
    setNewInvPrice('');
    setNewInvQty('');
    setNewInvTargetPct('');
    setActiveModal('add_investment');
  };

  const handleSyncPortfolioPrices = async () => {
    if (isSyncingPrices) return;
    setIsSyncingPrices(true);
    try {
      const res = await apiClient.request('/market/sync-portfolio', 'POST', {});
      showToast(res.message || 'Portfolio prices synchronized!', 'success');
      // Refresh investments and analytics
      const [invsRes, anRes] = await Promise.all([
        apiClient.request('/investments'),
        apiClient.request('/investments/analytics')
      ]);
      setInvestments(Array.isArray(invsRes) ? invsRes : []);
      setInvestmentsAnalytics(anRes);
    } catch (err) {
      console.error('Error syncing portfolio prices:', err);
      showToast(err.message || 'Failed to sync prices', 'error');
    } finally {
      setIsSyncingPrices(false);
    }
  };

  const handleCreateAsset = async (e) => {
    e.preventDefault();
    try {
      const typeVal = newInvType === 'ADD_NEW' ? customAssetType.trim() : newInvType;
      const sectorVal = newInvSector === 'ADD_NEW' ? customInvSector.trim() : newInvSector;
      const regionVal = newInvRegion === 'ADD_NEW' ? customInvRegion.trim() : newInvRegion;

      if (!typeVal) {
        showToast('Please specify a custom asset type', 'error');
        return;
      }
      if (!sectorVal) {
        showToast('Please specify a custom sector', 'error');
        return;
      }
      if (!regionVal) {
        showToast('Please specify a custom region', 'error');
        return;
      }

      if (newInvType === 'ADD_NEW' && customAssetType.trim()) {
        const val = customAssetType.trim().toUpperCase();
        const saved = JSON.parse(localStorage.getItem('dhudd_custom_inv_types') || '[]');
        if (!saved.includes(val)) {
          localStorage.setItem('dhudd_custom_inv_types', JSON.stringify([...saved, val]));
        }
      }
      if (newInvSector === 'ADD_NEW' && customInvSector.trim()) {
        const val = customInvSector.trim();
        const saved = JSON.parse(localStorage.getItem('dhudd_custom_inv_sectors') || '[]');
        if (!saved.includes(val)) {
          localStorage.setItem('dhudd_custom_inv_sectors', JSON.stringify([...saved, val]));
        }
      }
      if (newInvRegion === 'ADD_NEW' && customInvRegion.trim()) {
        const val = customInvRegion.trim();
        const saved = JSON.parse(localStorage.getItem('dhudd_custom_inv_regions') || '[]');
        if (!saved.includes(val)) {
          localStorage.setItem('dhudd_custom_inv_regions', JSON.stringify([...saved, val]));
        }
      }

      const res = await apiClient.request('/investments', 'POST', {
        name: newInvName,
        symbol: newInvSymbol.toUpperCase(),
        type: typeVal.toUpperCase(),
        sector: sectorVal,
        region: regionVal,
        market_cap: newInvMarketCap,
        target_allocation_pct: parseFloat(newInvTargetPct) || 0,
        current_price: parseFloat(newInvPrice) || 0,
        currency: newInvCurrency || 'AED'
      });

      const qty = parseFloat(newInvQty) || 0;
      if (qty > 0) {
        const totalAmount = qty * (parseFloat(newInvPrice) || 0);

        if (recordFundingFlow && invUseSplitSources) {
          const sumSources = splitInvSources.reduce((sum, src) => sum + (parseFloat(src.amount) || 0), 0);
          if (Math.abs(sumSources - totalAmount) > 0.02) {
            showToast(`Split sources sum (${sumSources.toFixed(2)}) must equal Total Amount (${totalAmount.toFixed(2)})`, 'error');
            return;
          }

          for (const source of splitInvSources) {
            const sourceAmt = parseFloat(source.amount);
            const ratio = totalAmount > 0 ? sourceAmt / totalAmount : 0;
            await apiClient.request(`/investments/${res.investment_id}/transactions`, 'POST', {
              type: 'BUY',
              units: qty * ratio,
              price_per_unit: parseFloat(newInvPrice) || 0,
              amount: sourceAmt,
              transaction_date: newInvDate,
              source_type: source.sourceType,
              source_id: source.sourceId || null
            });
          }
        } else {
          const source = recordFundingFlow ? newInvSourceType : 'NONE';
          const sourceId = recordFundingFlow && newInvSourceType !== 'NONE' ? newInvSourceId : null;

          await apiClient.request(`/investments/${res.investment_id}/transactions`, 'POST', {
            type: 'BUY',
            units: qty,
            price_per_unit: parseFloat(newInvPrice) || 0,
            amount: totalAmount,
            transaction_date: newInvDate,
            source_type: source,
            source_id: sourceId
          });
        }
      }

      showToast('Investment asset created successfully!', 'success');
      setActiveModal(null);
      // Reset form
      setNewInvName('');
      setNewInvSymbol('');
      setNewInvType('EQUITY');
      setNewInvSector('N/A');
      setNewInvRegion('US');
      setNewInvMarketCap('Large');
      setNewInvTargetPct('');
      setNewInvPrice('');
      setCustomAssetType('');
      setCustomInvSector('');
      setCustomInvRegion('');
      setNewInvQty('');
      setNewInvDate(new Date().toISOString().split('T')[0]);
      setRecordFundingFlow(false);
      setNewInvSourceType('ACCOUNT');
      setNewInvSourceId('');
      setInvUseSplitSources(false);
      setSplitInvSources([{ sourceType: 'ACCOUNT', sourceId: '', amount: '' }]);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleCreateAssetTx = async (e) => {
    e.preventDefault();
    if (!selectedAssetTx) return;
    try {
      const totalAmount = parseFloat(invTxAmount);
      const isEditing = !!editingInvTxId;

      if (isEditing) {
        // Edit mode: PUT request to update existing transaction
        await apiClient.request(`/investments/${selectedAssetTx.investment_id}/transactions/${editingInvTxId}`, 'PUT', {
          type: invTxType,
          units: parseFloat(invTxUnits) || 0,
          price_per_unit: parseFloat(invTxPricePerUnit) || 0,
          amount: totalAmount,
          transaction_date: invTxDate,
          source_type: invTxSourceType,
          source_id: invTxSourceType === 'NONE' ? null : invTxSourceId
        });
        showToast('Transaction updated successfully!', 'success');
      } else if (invTxSourceType !== 'NONE' && txInvUseSplitSources) {
        const sumSources = splitTxInvSources.reduce((sum, src) => sum + (parseFloat(src.amount) || 0), 0);
        if (Math.abs(sumSources - totalAmount) > 0.02) {
          showToast(`Split sources sum (${sumSources.toFixed(2)}) must equal Total Amount (${totalAmount.toFixed(2)})`, 'error');
          return;
        }

        const qty = parseFloat(invTxUnits) || 0;
        for (const source of splitTxInvSources) {
          const sourceAmt = parseFloat(source.amount);
          const ratio = totalAmount > 0 ? sourceAmt / totalAmount : 0;
          await apiClient.request(`/investments/${selectedAssetTx.investment_id}/transactions`, 'POST', {
            type: invTxType,
            units: qty * ratio,
            price_per_unit: parseFloat(invTxPricePerUnit) || 0,
            amount: sourceAmt,
            transaction_date: invTxDate,
            source_type: source.sourceType,
            source_id: source.sourceId || null
          });
        }
        showToast('Transaction logged successfully!', 'success');
      } else {
        await apiClient.request(`/investments/${selectedAssetTx.investment_id}/transactions`, 'POST', {
          type: invTxType,
          units: parseFloat(invTxUnits) || 0,
          price_per_unit: parseFloat(invTxPricePerUnit) || 0,
          amount: totalAmount,
          transaction_date: invTxDate,
          source_type: invTxSourceType,
          source_id: invTxSourceType === 'NONE' ? null : invTxSourceId
        });
        showToast('Transaction logged successfully!', 'success');
      }

      setActiveModal(null);
      // Reset form
      setSelectedAssetTx(null);
      setEditingInvTxId(null);
      setInvTxType('BUY');
      setInvTxUnits('');
      setInvTxPricePerUnit('');
      setInvTxAmount('');
      setInvTxSourceType('ACCOUNT');
      setInvTxSourceId('');
      setInvTxDate(new Date().toISOString().split('T')[0]);
      setTxInvUseSplitSources(false);
      setSplitTxInvSources([{ sourceType: 'ACCOUNT', sourceId: '', amount: '' }]);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteAsset = async (id) => {
    if (!window.confirm('Are you sure you want to delete this asset profile? This will delete all logged transactions for this asset from your portfolio. Core cash flow records will be preserved.')) return;
    try {
      await apiClient.request(`/investments/${id}`, 'DELETE');
      showToast('Asset profile deleted', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleStartEditInvTx = (inv, tx) => {
    setSelectedAssetTx(inv);
    setEditingInvTxId(tx.transaction_id);
    setInvTxType(tx.type);
    setInvTxUnits(tx.type === 'DIVIDEND' ? '0' : String(tx.units || ''));
    setInvTxPricePerUnit(tx.type === 'DIVIDEND' ? '0' : String(tx.price_per_unit || ''));
    setInvTxAmount(formatInputValue(tx.amount));
    setInvTxSourceType(tx.source_type || 'NONE');
    setInvTxSourceId(tx.source_id || '');
    setInvTxDate(tx.transaction_date ? new Date(tx.transaction_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    setTxInvUseSplitSources(false);
    setSplitTxInvSources([{ sourceType: 'ACCOUNT', sourceId: '', amount: '' }]);
    setActiveModal('log_investment_tx');
  };

  const handleDeleteAssetTx = async (id, txId) => {
    if (!window.confirm('Are you sure you want to delete this transaction? This will revert any core cash account balance changes or friend split ledgers associated with this transaction.')) return;
    try {
      await apiClient.request(`/investments/${id}/transactions/${txId}`, 'DELETE');
      showToast('Transaction deleted and cash flows reverted', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleSendPasswordResetOtp = async () => {
    if (!pwResetTurnstileToken) {
      return showToast('Please complete the anti-bot verification first.', 'error');
    }
    setIsSendingPwResetOtp(true);
    try {
      const res = await apiClient.request('/auth/password-reset/send-otp', 'POST', {
        turnstileToken: pwResetTurnstileToken
      });
      setPwResetOtpSent(true);
      setPwResetOtpCooldown(60);
      setPwResetTurnstileToken(null);
      showToast(res.message || 'Verification code sent to your registered email!', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to send verification code', 'error');
      setPwResetTurnstileToken(null);
    } finally {
      setIsSendingPwResetOtp(false);
    }
  };

  const handleSavePassword = async () => {
    if (!pwResetOtpCode || pwResetOtpCode.trim().length !== 6) {
      return showToast('Please enter the 6-digit verification code sent to your email!', 'error');
    }
    if (!newPassword || !confirmPassword) {
      return showToast('Please fill in both new password fields!', 'error');
    }
    if (newPassword.length < 8) {
      return showToast('Password must be at least 8 characters long!', 'error');
    }
    if (!/[0-9]/.test(newPassword)) {
      return showToast('Password must contain at least one number (0-9)!', 'error');
    }
    if (!/[A-Z]/.test(newPassword)) {
      return showToast('Password must contain at least one uppercase letter (A-Z)!', 'error');
    }
    if (!/[a-z]/.test(newPassword)) {
      return showToast('Password must contain at least one lowercase letter (a-z)!', 'error');
    }
    if (!/[^A-Za-z0-9]/.test(newPassword)) {
      return showToast('Password must contain at least one special symbol (!@#$%^&*)!', 'error');
    }
    if (newPassword !== confirmPassword) {
      return showToast('New passwords do not match!', 'error');
    }

    setIsSubmittingPassword(true);
    try {
      const res = await apiClient.request('/auth/change-password', 'POST', {
        otpCode: pwResetOtpCode.trim(),
        newPassword
      });
      setNewPassword('');
      setConfirmPassword('');
      setPwResetOtpCode('');
      setPwResetOtpSent(false);
      setPwResetTurnstileToken(null);
      showToast(res.message || 'Password updated successfully!', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to update password', 'error');
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  const handleSendChangeEmailOtp = async () => {
    if (!newEmailInput || !newEmailInput.includes('@')) {
      return showToast('Please enter a valid new email address.', 'error');
    }
    const currentEmail = (profileEmail || localStorage.getItem('dhudd_email') || '').toLowerCase().trim();
    if (newEmailInput.toLowerCase().trim() === currentEmail) {
      return showToast('The new email address is the same as your current email address.', 'error');
    }
    if (!changeEmailTurnstileToken) {
      return showToast('Please complete the anti-bot verification first.', 'error');
    }

    setIsSendingChangeEmailOtp(true);
    try {
      const res = await apiClient.request('/users/email/send-otp', 'POST', {
        newEmail: newEmailInput.trim(),
        turnstileToken: changeEmailTurnstileToken
      });
      setChangeEmailOtpSent(true);
      setChangeEmailCooldown(60);
      setChangeEmailTurnstileToken(null);
      showToast(res.message || 'Verification code sent to your new email!', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to send verification code', 'error');
      setChangeEmailTurnstileToken(null);
    } finally {
      setIsSendingChangeEmailOtp(false);
    }
  };

  const handleVerifyChangeEmail = async () => {
    if (!newEmailInput || !newEmailInput.includes('@')) {
      return showToast('Please enter a valid new email address.', 'error');
    }
    if (!changeEmailOtpCode || changeEmailOtpCode.trim().length !== 6) {
      return showToast('Please enter the 6-digit verification code sent to your new email.', 'error');
    }

    setIsVerifyingChangeEmail(true);
    try {
      const res = await apiClient.request('/users/email/verify', 'POST', {
        newEmail: newEmailInput.trim(),
        otpCode: changeEmailOtpCode.trim()
      });
      if (res.token) {
        apiClient.setToken(res.token);
      }
      if (res.email) {
        localStorage.setItem('dhudd_email', res.email);
        setProfileEmail(res.email);
      }
      setIsChangingEmail(false);
      setNewEmailInput('');
      setChangeEmailOtpCode('');
      setChangeEmailOtpSent(false);
      setChangeEmailTurnstileToken(null);
      showToast(res.message || 'Email address updated successfully!', 'success');
    } catch (err) {
      showToast(err.message || 'Failed to update email address', 'error');
    } finally {
      setIsVerifyingChangeEmail(false);
    }
  };

  const handleSaveAssetPrice = async (id) => {
    if (!newAssetPriceInput || isNaN(parseFloat(newAssetPriceInput))) {
      return showToast('Please enter a valid price!', 'error');
    }
    try {
      await apiClient.request(`/investments/${id}/price`, 'PUT', {
        currentPrice: parseFloat(newAssetPriceInput)
      });
      setEditingAssetPriceId(null);
      showToast('Asset price updated!', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };



  // Submit transaction
  const handleCreateTransaction = async (e, bypassDuplicate = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSubmittingTx) return;
    if (!txDesc || !txAmount || !txCategory) return;

    if (!bypassDuplicate) {
      try {
        const match = findSimilarTransaction({
          amount: txAmount,
          currency: txCurrency,
          date: txDate,
          description: txDesc,
          type: activeModal === 'inflow' ? 'income' : 'expense'
        });
        if (match) {
          setIsSubmittingTx(false);
          setDuplicateAlertModal({
            isOpen: true,
            match,
            onConfirm: () => handleCreateTransaction(null, true)
          });
          return;
        }
      } catch (err) {
        console.error('Error checking duplicate transaction:', err);
      }
    }

    setIsSubmittingTx(true);

    if (txIsShared) {
      if (splitTargetType === 'GROUP' && !txGroupId) {
        return showToast('Please select a ledger group to split with', 'error');
      }
      if (splitTargetType === 'FRIENDS' && splitFriendsList.length === 0) {
        return showToast('Please select at least one friend to split with', 'error');
      }

      // Mathematical validation checks
      const amt = parseFloat(txAmount) || 0;
      if (splitType === 'EXACT') {
        const sum = getSplitMembers().reduce((sumAcc, m) => sumAcc + (parseFloat(exactAmounts[m.user_id]) || 0), 0);
        if (Math.abs(sum - amt) > 0.01) {
          return showToast(`Validation failed: Exact split sum (${sum.toFixed(2)}) must equal Total (${amt.toFixed(2)})`, 'error');
        }
      } else if (splitType === 'PERCENT') {
        const sumPct = getSplitMembers().reduce((sumAcc, m) => sumAcc + (percentages[m.user_id] !== undefined ? percentages[m.user_id] : parseFloat((100 / getSplitMembers().length).toFixed(2))), 0);
        if (Math.abs(sumPct - 100) > 0.01) {
          return showToast(`Validation failed: Split percentages sum (${sumPct}%) must equal 100%`, 'error');
        }
      } else if (splitType === 'SHARES') {
        const totalShares = getSplitMembers().reduce((sumAcc, m) => sumAcc + (shares[m.user_id] !== undefined ? shares[m.user_id] : 1), 0);
        if (totalShares <= 0) {
          return showToast('Validation failed: Total shares must be greater than 0', 'error');
        }
      }
    }

    let splitDetailsPayload = {};
    if (txIsShared) {
      const members = getSplitMembers();
      splitDetailsPayload.members = members.map(m => m.user_id);
      
      if (splitType === 'EXACT') {
        splitDetailsPayload.exactAmounts = exactAmounts;
      } else if (splitType === 'PERCENT') {
        const pcts = {};
        members.forEach(m => {
          pcts[m.user_id] = percentages[m.user_id] !== undefined ? percentages[m.user_id] : parseFloat((100 / members.length).toFixed(2));
        });
        splitDetailsPayload.percentages = pcts;
      } else if (splitType === 'SHARES') {
        const shs = {};
        members.forEach(m => {
          shs[m.user_id] = shares[m.user_id] !== undefined ? shares[m.user_id] : 1;
        });
        splitDetailsPayload.shares = shs;
      }
    }

    try {
      if ((activeModal === 'outflow' && txPayerUserId === 'self' && txUseSplitSources) || (activeModal === 'inflow' && txUseSplitSources)) {
        const totalAmt = parseFloat(txAmount) || 0;
        const sumSources = splitTxSources.reduce((sum, src) => sum + (parseFloat(src.amount) || 0), 0);
        if (Math.abs(sumSources - totalAmt) > 0.02) {
          return showToast(`Split sources sum (${sumSources.toFixed(2)}) must equal Total Amount (${totalAmt.toFixed(2)})`, 'error');
        }

        for (const source of splitTxSources) {
          const sourceAmt = parseFloat(source.amount);
          const ratio = totalAmt > 0 ? sourceAmt / totalAmt : 0;
          
          let sourceSplitDetails = null;
          if (txIsShared && splitDetailsPayload) {
            sourceSplitDetails = { ...splitDetailsPayload };
            if (splitType === 'EXACT') {
              const exact = {};
              Object.entries(exactAmounts).forEach(([uid, val]) => {
                exact[uid] = (parseFloat(val) * ratio).toFixed(2);
              });
              sourceSplitDetails.exactAmounts = exact;
            }
          }

          const sourceTxName = accounts.find(a => a.account_id === source.accountId)?.name || 'Account';
          const splitTx = {
            transaction_id: crypto.randomUUID(),
            description: `${txDesc} (funded by ${sourceTxName})`,
            total_amount: sourceAmt,
            currency: txCurrency,
            date: new Date(txDate).toISOString(),
            paid_by_account_id: source.accountId || null,
            paid_by_user_id: null,
            is_shared: txIsShared,
            group_id: txIsShared && splitTargetType === 'GROUP' ? txGroupId : null,
            friends_to_split: txIsShared && splitTargetType === 'FRIENDS' ? splitFriendsList : null,
            category_name: txCategory,
            is_recurring: txIsRecurring,
            recurring_period: txIsRecurring ? txRecurringPeriod : null,
            split_type: txIsShared ? splitType : null,
            split_details: txIsShared ? sourceSplitDetails : null
          };

          const res = await syncEngine.createTransactionCached(splitTx);
          if (!firstSourceTxId) firstSourceTxId = res.transaction_id || splitTx.transaction_id;
        }

        if (txCharges && parseFloat(txCharges) > 0 && firstSourceTxId) {
          const chargeAmt = parseFloat(txCharges);
          const firstSource = splitTxSources[0];
          const chargeTx = {
            transaction_id: crypto.randomUUID(),
            description: "Transaction Charge: " + txDesc,
            total_amount: chargeAmt,
            currency: txCurrency,
            date: new Date(txDate).toISOString(),
            paid_by_account_id: firstSource.accountId || null,
            paid_by_user_id: null,
            is_shared: false,
            group_id: null,
            friends_to_split: null,
            category_name: "Bank/Card Charges",
            is_recurring: false,
            recurring_period: null,
            split_type: null,
            split_details: null,
            source_transaction_id: firstSourceTxId
          };
          await syncEngine.createTransactionCached(chargeTx);
        }

        showToast('All split transactions logged successfully!', 'success');
      } else {
        const myUserId = localStorage.getItem('dhudd_user_id');
        let parsedPayerShares = null;
        let isActuallyShared = txIsShared;
        let finalSplitTargetType = txIsShared ? splitTargetType : null;
        let finalSplitFriendsList = txIsShared ? splitFriendsList : [];
        let finalSplitType = txIsShared ? splitType : null;
        let finalSplitDetails = txIsShared ? splitDetailsPayload : null;
        let myPaidAmount = parseFloat(txAmount) || 0;

        if (txPayerType === 'both' || txPayerType === 'friend') {
          parsedPayerShares = {};
          let sum = 0;
          for (const [uid, val] of Object.entries(txMultiPayers)) {
            const num = parseFloat(val) || 0;
            if (num > 0) {
              const actualUid = uid === 'self' ? myUserId : uid;
              parsedPayerShares[actualUid] = num;
              sum += num;
            }
          }

          if (txPayerType === 'friend') {
            myPaidAmount = 0;
          } else {
            myPaidAmount = parseFloat(txMultiPayers['self'] || 0);
          }

          if (Math.abs(sum - parseFloat(txAmount)) > 0.01) {
             return showToast(`Payers sum (${sum.toFixed(2)}) must equal Total Amount (${parseFloat(txAmount).toFixed(2)})`, 'error');
          }

          if (!txIsShared) {
            isActuallyShared = true;
            finalSplitTargetType = 'FRIENDS';
            const friendsInvolved = Object.keys(txMultiPayers).filter(id => id !== 'self' && parseFloat(txMultiPayers[id]) > 0);
            finalSplitFriendsList = friendsInvolved;
            finalSplitType = 'EXACT';
            const exactAmountsMap = { [myUserId]: parseFloat(txAmount) };
            for (const fid of friendsInvolved) {
               exactAmountsMap[fid] = 0;
            }
            finalSplitDetails = {
              members: [myUserId, ...friendsInvolved],
              exactAmounts: exactAmountsMap
            };
          }
        }

        let singlePayerId = null;
        if (txPayerType === 'you') {
          singlePayerId = myUserId;
        } else if (txPayerType === 'friend') {
          const friendPayers = Object.keys(txMultiPayers).filter(id => id !== 'self' && parseFloat(txMultiPayers[id]) > 0);
          if (friendPayers.length === 1 && myPaidAmount === 0) {
            singlePayerId = friendPayers[0];
          }
        }

        const newTx = {
          transaction_id: crypto.randomUUID(),
          description: txDesc,
          total_amount: parseFloat(txAmount),
          currency: txCurrency,
          date: new Date(txDate).toISOString(),
          paid_by_account_id: myPaidAmount > 0 ? (txAccountId || null) : null,
          paid_by_user_id: singlePayerId,
          payer_shares: parsedPayerShares,
          is_shared: isActuallyShared,
          group_id: isActuallyShared && finalSplitTargetType === 'GROUP' ? txGroupId : null,
          friends_to_split: isActuallyShared && finalSplitTargetType === 'FRIENDS' ? finalSplitFriendsList : null,
          category_name: txCategory,
          is_recurring: txIsRecurring,
          recurring_period: txIsRecurring ? txRecurringPeriod : null,
          split_type: isActuallyShared ? finalSplitType : null,
          split_details: isActuallyShared ? finalSplitDetails : null
        };
        const res = await syncEngine.createTransactionCached(newTx);
        const mainTxId = res.transaction_id || newTx.transaction_id;

        if (txCharges && parseFloat(txCharges) > 0) {
          const chargeAmt = parseFloat(txCharges);
          const chargeTx = {
            transaction_id: crypto.randomUUID(),
            description: "Transaction Charge: " + txDesc,
            total_amount: chargeAmt,
            currency: txCurrency,
            date: new Date(txDate).toISOString(),
            paid_by_account_id: newTx.paid_by_account_id,
            paid_by_user_id: singlePayerId,
            payer_shares: parsedPayerShares ? { ...parsedPayerShares } : undefined,
            is_shared: isActuallyShared,
            group_id: newTx.group_id,
            friends_to_split: newTx.friends_to_split,
            category_name: "Bank/Card Charges",
            is_recurring: false,
            recurring_period: null,
            split_type: newTx.split_type,
            split_details: newTx.split_details ? { ...newTx.split_details } : null,
            source_transaction_id: mainTxId
          };

          if (isActuallyShared && finalSplitType === 'EXACT' && finalSplitDetails && finalSplitDetails.exactAmounts) {
            const ratio = chargeAmt / parseFloat(txAmount);
            const scaledExact = {};
            for (const [uid, val] of Object.entries(finalSplitDetails.exactAmounts)) {
              scaledExact[uid] = parseFloat((parseFloat(val) * ratio).toFixed(2));
            }
            chargeTx.split_details.exactAmounts = scaledExact;
          }
          if (chargeTx.payer_shares) {
            const ratio = chargeAmt / parseFloat(txAmount);
            const scaledPayerShares = {};
            for (const [uid, val] of Object.entries(chargeTx.payer_shares)) {
              scaledPayerShares[uid] = parseFloat((parseFloat(val) * ratio).toFixed(2));
            }
            chargeTx.payer_shares = scaledPayerShares;
          }
          await syncEngine.createTransactionCached(chargeTx);
        }

        showToast(res.offline ? 'Queued offline!' : 'Transaction saved!', 'success');
        trackEvent('transaction_created', {
          type: activeModal,
          amount: parseFloat(txAmount),
          currency: txCurrency,
          category: txCategory,
          is_shared: Boolean(isActuallyShared),
          is_recurring: Boolean(txIsRecurring),
          has_charges: Boolean(txCharges && parseFloat(txCharges) > 0),
          offline: Boolean(res.offline)
        });
      }

      setTxDesc('');
      setTxAmount('');
      setTxCharges('');
      setTxIsShared(false);
      setTxIsRecurring(false);
      setTxPayerUserId('self');
      setTxPayerType('you');
      setTxMultiPayers({});
      setTxIsShared(false);
      setTxIsRecurring(false);
      setTxPayerUserId('self');
      setSplitFriendsList([]);
      setSplitGroupMembersList([]);
      setExactAmounts({});
      setPercentages({});
      setShares({});
      setTxUseSplitSources(false);
      setSplitTxSources([{ accountId: accounts[0]?.account_id || '', amount: '' }]);
      const msg = activeModal === 'outflow' ? 'Expense Recorded Successfully!' : 'Income Recorded Successfully!';
      setActiveModal(null);
      setSuccessCheckmarkMsg(msg);
      
      refreshData();
      const targetGrpId = (txIsShared && splitTargetType === 'GROUP' ? txGroupId : null) || activeGroupRef.current?.group_id;
      if (targetGrpId) {
        reloadActiveGroupBalances(targetGrpId);
      }
      checkAndPromptPushNotifications();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setIsSubmittingTx(false);
    }
  };

  // Handle Transfer
  const handleTransfer = async (e, bypassDuplicate = false) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSubmittingTx) return;
    if (!tfFromAccountId || !tfToAccountId || !tfFromAmount || !tfToAmount) {
      return showToast('Select source/target and amounts', 'error');
    }

    if (tfFromAccountId === tfToAccountId && tfFromType === tfToType) {
      return showToast('Source and destination cannot be the same', 'error');
    }

    const fromAcc = (tfFromType === 'ACCOUNT' || tfFromType === 'CREDIT_CARD') ? accounts.find(a => a.account_id === tfFromAccountId) : null;
    const toAcc = (tfToType === 'ACCOUNT' || tfToType === 'CREDIT_CARD') ? accounts.find(a => a.account_id === tfToAccountId) : null;
    
    let sourceCurrency = 'AED';
    if (tfFromType === 'ACCOUNT' || tfFromType === 'CREDIT_CARD') {
      sourceCurrency = fromAcc?.currency || preferredCurrencies[0] || 'AED';
    } else if (tfFromType === 'FRIEND') {
      sourceCurrency = tfConvertCurrency ? tfFriendCurrency : (toAcc?.currency || preferredCurrencies[0] || 'AED');
    }

    let targetCurrency = 'AED';
    if (tfToType === 'ACCOUNT' || tfToType === 'CREDIT_CARD') {
      targetCurrency = toAcc?.currency || preferredCurrencies[0] || 'AED';
    } else if (tfToType === 'FRIEND') {
      targetCurrency = tfConvertCurrency ? tfFriendCurrency : (fromAcc?.currency || preferredCurrencies[0] || 'AED');
    }

    const isMultiCurrency = sourceCurrency !== targetCurrency;
    const transferCurrency = (tfFromType === 'FRIEND' && tfToType === 'FRIEND')
      ? (tfConvertCurrency ? tfFriendCurrency : (preferredCurrencies[0] || 'AED'))
      : (tfToType === 'FRIEND' ? (tfConvertCurrency ? tfFriendCurrency : (fromAcc?.currency || 'AED')) : (toAcc?.currency || 'AED'));

    if (!bypassDuplicate) {
      let candidateDesc = tfDesc;
      if (!candidateDesc) {
        if (tfFromType === 'FRIEND' && tfToType === 'FRIEND') {
          const fA = friends.find(f => f.user_id === tfFromAccountId);
          const fB = friends.find(f => f.user_id === tfToAccountId);
          candidateDesc = `From ${fA?.display_name || fA?.email?.split('@')[0] || 'Friend'} to ${fB?.display_name || fB?.email?.split('@')[0] || 'Friend'}`;
        } else if (tfFromType === 'FRIEND') {
          const f = friends.find(f => f.user_id === tfFromAccountId);
          candidateDesc = `From ${f?.display_name || f?.email?.split('@')[0] || 'Friend'} to you`;
        } else if (tfToType === 'FRIEND') {
          const f = friends.find(f => f.user_id === tfToAccountId);
          candidateDesc = `From you to ${f?.display_name || f?.email?.split('@')[0] || 'Friend'}`;
        } else {
          candidateDesc = `From ${fromAcc?.name || 'Account'} to ${toAcc?.name || 'Account'}`;
        }
      }

      try {
        const match = findSimilarTransaction({
          amount: tfToAmount || tfFromAmount,
          currency: transferCurrency,
          date: tfDate,
          description: candidateDesc,
          type: 'transfer',
          fromFriendId: tfFromType === 'FRIEND' ? tfFromAccountId : null,
          toFriendId: tfToType === 'FRIEND' ? tfToAccountId : null
        });

        if (match) {
          setIsSubmittingTx(false);
          setDuplicateAlertModal({
            isOpen: true,
            match,
            onConfirm: () => handleTransfer(null, true)
          });
          return;
        }
      } catch (err) {
        console.error('Error checking duplicate transfer:', err);
      }
    }

    setIsSubmittingTx(true);

    try {
      if (tfFromType === 'FRIEND' && tfToType === 'FRIEND') {
        // Friend B ➔ Friend C (recorded by User A: A owes B, C owes A)
        const selectedFriendA = friends.find(f => f.user_id === tfFromAccountId);
        const friendAName = selectedFriendA ? (selectedFriendA.display_name || selectedFriendA.email?.split('@')[0] || selectedFriendA.nickname) : 'Friend';
        
        const selectedFriendB = friends.find(f => f.user_id === tfToAccountId);
        const friendBName = selectedFriendB ? (selectedFriendB.display_name || selectedFriendB.email?.split('@')[0] || selectedFriendB.nickname) : 'Friend';

        const curr = tfConvertCurrency ? tfFriendCurrency : (preferredCurrencies[0] || 'AED');

        if (tfConvertCurrency && sourceCurrency !== targetCurrency) {
          const sourceAmt = parseFloat(tfFromAmount);
          const targetAmt = parseFloat(tfToAmount);
          if (sourceAmt > 0 && targetAmt > 0) {
            const rate = targetAmt / sourceAmt;
            const invRate = sourceAmt / targetAmt;
            const todayStr = new Date().toISOString().split('T')[0];
            Promise.all([
              apiClient.request('/currency/rates', 'POST', {
                from_currency: sourceCurrency,
                to_currency: targetCurrency,
                rate: rate,
                rate_date: todayStr
              }),
              apiClient.request('/currency/rates', 'POST', {
                from_currency: targetCurrency,
                to_currency: sourceCurrency,
                rate: invRate,
                rate_date: todayStr
              })
            ]).catch(rateErr => console.error('Failed to auto-log transfer exchange rate', rateErr));
          }
        }

        const selectedDate = new Date(tfDate);
        const now = new Date();
        selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

        await apiClient.request('/transfers/friend-to-friend', 'POST', {
          from_friend_id: tfFromAccountId,
          to_friend_id: tfToAccountId,
          amount: parseFloat(tfToAmount || tfFromAmount),
          currency: curr,
          date: selectedDate.toISOString(),
          description: tfDesc || `Transfer: ${friendAName} ➔ ${friendBName}`
        });
      } else if (tfFromType === 'FRIEND') {
        // Friend A ➔ Account B
        const selectedFriend = friends.find(f => f.user_id === tfFromAccountId);
        const friendPrefix = selectedFriend ? (selectedFriend.display_name || selectedFriend.email.split('@')[0]) : 'Friend';
        const toCurrency = toAcc?.currency || 'AED';
        const friendSourceCurrency = tfConvertCurrency ? tfFriendCurrency : toCurrency;
        const sourceAmt = parseFloat(tfFromAmount);
        const targetAmt = parseFloat(tfToAmount);

        if (tfConvertCurrency && friendSourceCurrency !== toCurrency) {
          const rate = targetAmt / sourceAmt;
          const invRate = sourceAmt / targetAmt;
          const todayStr = new Date().toISOString().split('T')[0];

          Promise.all([
            apiClient.request('/currency/rates', 'POST', {
              from_currency: friendSourceCurrency,
              to_currency: toCurrency,
              rate: rate,
              rate_date: todayStr
            }),
            apiClient.request('/currency/rates', 'POST', {
              from_currency: toCurrency,
              to_currency: friendSourceCurrency,
              rate: invRate,
              rate_date: todayStr
            })
          ]).catch(rateErr => console.error('Failed to auto-log transfer exchange rate', rateErr));
        }

        const selectedDate = new Date(tfDate);
        const now = new Date();
        selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

        await apiClient.request('/friends/settle', 'POST', {
          friend_user_id: tfFromAccountId,
          amount: sourceAmt,
          currency: friendSourceCurrency,
          from_amount: targetAmt,
          from_currency: toCurrency,
          direction: 'receive',
          account_id: tfToAccountId,
          description: tfDesc || `Transfer from ${friendPrefix}`,
          date: selectedDate.toISOString()
        });
      } else if (tfToType === 'FRIEND') {
        // Account / Credit Card ➔ Friend B
        const selectedFriend = friends.find(f => f.user_id === tfToAccountId);
        const friendPrefix = selectedFriend ? (selectedFriend.display_name || selectedFriend.email.split('@')[0]) : 'Friend';
        const fromCurrency = fromAcc?.currency || 'AED';
        const targetCurrency = tfConvertCurrency ? tfFriendCurrency : fromCurrency;
        const sourceAmt = parseFloat(tfFromAmount);
        const targetAmt = parseFloat(tfToAmount);

        if (tfConvertCurrency && fromCurrency !== targetCurrency) {
          const rate = targetAmt / sourceAmt;
          const invRate = sourceAmt / targetAmt;
          const todayStr = new Date().toISOString().split('T')[0];

          Promise.all([
            apiClient.request('/currency/rates', 'POST', {
              from_currency: fromCurrency,
              to_currency: targetCurrency,
              rate: rate,
              rate_date: todayStr
            }),
            apiClient.request('/currency/rates', 'POST', {
              from_currency: targetCurrency,
              to_currency: fromCurrency,
              rate: invRate,
              rate_date: todayStr
            })
          ]).catch(rateErr => console.error('Failed to auto-log transfer exchange rate', rateErr));
        }

        const selectedDate = new Date(tfDate);
        const now = new Date();
        selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

        await apiClient.request('/friends/settle', 'POST', {
          friend_user_id: tfToAccountId,
          amount: targetAmt,
          currency: targetCurrency,
          from_amount: sourceAmt,
          from_currency: fromCurrency,
          direction: 'pay',
          account_id: tfFromAccountId,
          description: tfDesc || `Transfer to ${friendPrefix}`,
          date: selectedDate.toISOString()
        });
      } else {
        // Account A ➔ Account B
        if (isMultiCurrency) {
          const rate = parseFloat(tfToAmount) / parseFloat(tfFromAmount);
          const invRate = parseFloat(tfFromAmount) / parseFloat(tfToAmount);
          const todayStr = new Date().toISOString().split('T')[0];

          Promise.all([
            apiClient.request('/currency/rates', 'POST', {
              from_currency: fromAcc.currency,
              to_currency: toAcc.currency,
              rate: rate,
              rate_date: todayStr
            }),
            apiClient.request('/currency/rates', 'POST', {
              from_currency: toAcc.currency,
              to_currency: fromAcc.currency,
              rate: invRate,
              rate_date: todayStr
            })
          ]).catch(rateErr => console.error('Failed to auto-log transfer exchange rate', rateErr));
        }

        const selectedDate = new Date(tfDate);
        const now = new Date();
        selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());

        await apiClient.request('/transfers', 'POST', {
          from_account_id: tfFromAccountId,
          to_account_id: tfToAccountId,
          from_amount: parseFloat(tfFromAmount),
          to_amount: parseFloat(tfToAmount),
          description: tfDesc || 'Inter-Account Transfer',
          date: selectedDate.toISOString()
        });
      }

      // Record Transaction Charge if provided
      if (tfCharges && parseFloat(tfCharges) > 0) {
        const chargeAmt = parseFloat(tfCharges);
        let defaultDesc = 'Inter-Account Transfer';
        if (tfFromType === 'FRIEND' && tfToType === 'FRIEND') {
          const fA = friends.find(f => f.user_id === tfFromAccountId);
          const fB = friends.find(f => f.user_id === tfToAccountId);
          defaultDesc = `Transfer: ${fA?.display_name || 'Friend'} ➔ ${fB?.display_name || 'Friend'}`;
        } else if (tfFromType === 'FRIEND') {
          const f = friends.find(f => f.user_id === tfFromAccountId);
          defaultDesc = `Transfer from ${f?.display_name || 'Friend'}`;
        } else if (tfToType === 'FRIEND') {
          const f = friends.find(f => f.user_id === tfToAccountId);
          defaultDesc = `Transfer to ${f?.display_name || 'Friend'}`;
        }

        const finalTxDesc = tfDesc && tfDesc.trim() ? tfDesc.trim() : defaultDesc;
        const chargeDesc = `Transaction charge for ${finalTxDesc}`;

        let chargeAccountId = null;
        let chargeCurrency = 'AED';

        if (tfChargePayer === 'DESTINATION' && (tfToType === 'ACCOUNT' || tfToType === 'CREDIT_CARD')) {
          chargeAccountId = tfToAccountId;
          chargeCurrency = toAcc?.currency || preferredCurrencies[0] || 'AED';
        } else if (tfFromType === 'ACCOUNT' || tfFromType === 'CREDIT_CARD') {
          chargeAccountId = tfFromAccountId;
          chargeCurrency = fromAcc?.currency || preferredCurrencies[0] || 'AED';
        } else if (tfToType === 'ACCOUNT' || tfToType === 'CREDIT_CARD') {
          chargeAccountId = tfToAccountId;
          chargeCurrency = toAcc?.currency || preferredCurrencies[0] || 'AED';
        } else {
          const defaultUserAcc = accounts.find(a => a.type !== 'CREDIT_CARD') || accounts[0];
          chargeAccountId = defaultUserAcc?.account_id || null;
          chargeCurrency = defaultUserAcc?.currency || preferredCurrencies[0] || 'AED';
        }

        const chargeTx = {
          transaction_id: crypto.randomUUID(),
          description: chargeDesc,
          total_amount: chargeAmt,
          currency: chargeCurrency,
          date: new Date(tfDate).toISOString(),
          paid_by_account_id: chargeAccountId,
          paid_by_user_id: null,
          is_shared: false,
          group_id: null,
          friends_to_split: null,
          category_name: 'Bank/Card Charges',
          is_recurring: false,
          recurring_period: null,
          split_type: null,
          split_details: null
        };
        await syncEngine.createTransactionCached(chargeTx);
      }

      showToast('Transfer completed!', 'success');
      trackEvent('transfer_created', {
        from_type: tfFromType,
        to_type: tfToType,
        from_amount: parseFloat(tfFromAmount),
        to_amount: parseFloat(tfToAmount),
        has_charges: Boolean(tfCharges && parseFloat(tfCharges) > 0)
      });
      setTfFromAmount('');
      setTfToAmount('');
      setTfFromAccountId('');
      setTfToAccountId('');
      setTfDesc('');
      setTfCharges('');
      setTfChargePayer('SOURCE');
      setTfFromType('ACCOUNT');
      setTfToType('ACCOUNT');
      setTfConvertCurrency(false);
      setTfFriendCurrency('AED');
      setTfDate(new Date().toISOString().split('T')[0]);
      setActiveModal(null);
      setSuccessCheckmarkMsg('Transfer Completed Successfully!');
      refreshData();
      checkAndPromptPushNotifications();
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setIsSubmittingTx(false);
    }
  };

  // Handle Credit Card Payment
  const handlePayCreditCard = async (e) => {
    e.preventDefault();
    if (!payCcCard || !payCcAmount) {
      return showToast('Please fill out all required fields', 'error');
    }
    const amountVal = parseFloat(payCcAmount);
    if (!amountVal || amountVal <= 0) {
      return showToast('Enter a valid amount', 'error');
    }

    try {
      const selectedDate = new Date(payCcDate);
      const now = new Date();
      selectedDate.setHours(now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
      const dateObj = selectedDate.toISOString();

      if (ccUseSplitSources) {
        const sumSources = splitCcSources.reduce((sum, src) => sum + (parseFloat(src.amount) || 0), 0);
        if (Math.abs(sumSources - amountVal) > 0.02) {
          return showToast(`Split sources sum (${sumSources.toFixed(2)}) must equal Total Amount (${amountVal.toFixed(2)})`, 'error');
        }

        for (const source of splitCcSources) {
          const sourceAmt = parseFloat(source.amount);
          if (source.sourceType === 'FRIEND') {
            const selectedFriend = friends.find(f => f.user_id === source.sourceId);
            const friendEmail = selectedFriend ? selectedFriend.email : 'Friend';
            const friendEmailPrefix = friendEmail.split('@')[0];

            await apiClient.request('/transactions', 'POST', {
              description: `Credit Card Payment: ${payCcCard.name} (Paid by ${friendEmailPrefix})`,
              total_amount: sourceAmt,
              currency: payCcCard.currency,
              date: dateObj,
              paid_by_account_id: payCcCard.account_id,
              paid_by_user_id: source.sourceId,
              is_shared: true,
              category_name: 'Deposits',
              split_type: 'EXACT',
              split_details: {
                members: [myUserId, source.sourceId],
                exactAmounts: {
                  [myUserId]: sourceAmt,
                  [source.sourceId]: 0.00
                }
              },
              friends_to_split: [source.sourceId]
            });
          } else {
            await apiClient.request('/transfers', 'POST', {
              from_account_id: source.sourceId,
              to_account_id: payCcCard.account_id,
              from_amount: sourceAmt,
              to_amount: sourceAmt,
              description: `Credit Card Payment: ${payCcCard.name}`,
              date: dateObj
            });
          }
        }
      } else {
        if (payCcSourceType === 'FRIEND') {
          if (!payCcSourceFriendId) {
            return showToast('Please select a friend', 'error');
          }
          const selectedFriend = friends.find(f => f.user_id === payCcSourceFriendId);
          const friendEmail = selectedFriend ? selectedFriend.email : 'Friend';
          const friendEmailPrefix = friendEmail.split('@')[0];

          await apiClient.request('/transactions', 'POST', {
            description: `Credit Card Payment: ${payCcCard.name} (Paid by ${friendEmailPrefix})`,
            total_amount: amountVal,
            currency: payCcCard.currency,
            date: dateObj,
            paid_by_account_id: payCcCard.account_id,
            paid_by_user_id: payCcSourceFriendId,
            is_shared: true,
            category_name: 'Deposits',
            split_type: 'EXACT',
            split_details: {
              members: [myUserId, payCcSourceFriendId],
              exactAmounts: {
                [myUserId]: amountVal,
                [payCcSourceFriendId]: 0.00
              }
            },
            friends_to_split: [payCcSourceFriendId]
          });
        } else {
          if (!payCcSourceAccountId) {
            return showToast('Please select source account', 'error');
          }
          await apiClient.request('/transfers', 'POST', {
            from_account_id: payCcSourceAccountId,
            to_account_id: payCcCard.account_id,
            from_amount: amountVal,
            to_amount: amountVal,
            description: `Credit Card Payment: ${payCcCard.name}`,
            date: dateObj
          });
        }
      }

      showToast('Payment recorded successfully!', 'success');
      setPayCcModalOpen(false);
      setPayCcCard(null);
      setPayCcAmount('');
      setPayCcSourceAccountId('');
      setPayCcSourceType('ACCOUNT');
      setPayCcSourceFriendId('');
      setCcUseSplitSources(false);
      setSplitCcSources([{ sourceType: 'ACCOUNT', sourceId: accounts.find(a => a.type !== 'CREDIT_CARD')?.account_id || '', amount: '' }]);
      setPayCcDate(new Date().toISOString().split('T')[0]);
      refreshData();
      checkAndPromptPushNotifications();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // Group views
  const handleViewGroup = async (group, initialSubTab = 'hub') => {
    setActiveGroup(group);
    setGroupViewSubTab(initialSubTab);
    setActiveItemTxLimit(20);

    // Compute OPTIMISTIC balances immediately (<1ms) from transactions already in memory
    const isSimp = group.simplify_debts !== false && group.simplify_debts !== 0 && group.simplify_debts !== 'false';
    const localGroupTxs = transactions.filter(tx => tx.group_id === group.group_id);
    const existingMembers = Array.isArray(group.members) && group.members.length > 0
      ? group.members.map(m => m.user_id || m)
      : groupMembers.map(m => m.user_id);

    if (localGroupTxs.length > 0) {
      try {
        const localRes = resolveGroupDebts(localGroupTxs, existingMembers, isSimp);
        setGroupBalances(localRes.balances || {});
        setGroupBalancesByCurrency(null);
        setGroupSimplifications((localRes.transfers || []).map(t => ({
          from_user_id: t.from,
          from_name: 'Member',
          to_user_id: t.to,
          to_name: 'Member',
          amount: t.amount
        })));
        setGroupRepaymentsSaved(localRes.repayments_saved || 0);
      } catch (optErr) {
        console.warn('Optimistic group balances calculation fallback:', optErr);
      }
    } else {
      setGroupBalances({});
      setGroupBalancesByCurrency(null);
      setGroupSimplifications([]);
      setGroupMemberBreakdown([]);
      setGroupRepaymentsSaved(0);
    }

    setIsGroupBalancesLoading(true);
    try {
      const [membersRes, simRes] = await Promise.allSettled([
        apiClient.request(`/groups/${group.group_id}/members`),
        apiClient.request(`/groups/${group.group_id}/simplify`)
      ]);

      if (membersRes.status === 'fulfilled' && Array.isArray(membersRes.value)) {
        setGroupMembers(membersRes.value);
      }
      if (simRes.status === 'fulfilled' && simRes.value) {
        setGroupBalances(simRes.value.balances || {});
        setGroupBalancesByCurrency(simRes.value.by_currency || null);
        setGroupSimplifications(simRes.value.transfers || []);
        setGroupMemberBreakdown(simRes.value.member_breakdown || []);
        setGroupRepaymentsSaved(simRes.value.repayments_saved || 0);
        if (membersRes.status !== 'fulfilled' && simRes.value.members && Array.isArray(simRes.value.members)) {
          setGroupMembers(simRes.value.members);
        }
      } else if (simRes.status === 'rejected') {
        throw simRes.reason;
      }
    } catch (err) {
      console.warn('Could not fetch online group details:', err);
      if (!navigator.onLine) {
        showToast('Offline - Group details loaded from cache', 'info');
      } else {
        showToast(err.message || 'Could not load group balance details', 'error');
      }
    } finally {
      setIsGroupBalancesLoading(false);
    }
  };

  const handleToggleGroupSimplify = async (newSimplifyState) => {
    if (!activeGroup) return;
    try {
      const res = await apiClient.request(`/groups/${activeGroup.group_id}/toggle-simplify`, 'POST', {
        simplify_debts: newSimplifyState
      });
      const updatedGroup = { ...activeGroup, simplify_debts: res.simplify_debts };
      setActiveGroup(updatedGroup);
      setGroups(prev => prev.map(g => g.group_id === updatedGroup.group_id ? updatedGroup : g));
      
      const simRes = await apiClient.request(`/groups/${activeGroup.group_id}/simplify`);
      setGroupBalances(simRes.balances || {});
      setGroupBalancesByCurrency(simRes.by_currency || null);
      setGroupSimplifications(simRes.transfers || []);
      setGroupMemberBreakdown(simRes.member_breakdown || []);
      setGroupRepaymentsSaved(simRes.repayments_saved || 0);
      showToast(res.message || `Debt simplification is now ${res.simplify_debts ? 'ON' : 'OFF'}`, 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleGroupSettleSubmit = async ({ from_user_id, to_user_id, amount, currency, account_id }) => {
    if (!activeGroup) return;
    try {
      await apiClient.request(`/groups/${activeGroup.group_id}/settle`, 'POST', {
        from_user_id,
        to_user_id,
        amount,
        currency,
        account_id
      });
      showToast('Settlement recorded successfully!', 'success');
      await handleViewGroup(activeGroup, groupViewSubTab);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
      throw err;
    }
  };

  const handleRemindDebt = (debt, member) => {
    const isOwedToMember = debt.type === 'owes_to_member';
    const payerName = isOwedToMember ? debt.other_name : member.display_name;
    const payeeName = isOwedToMember ? member.display_name : debt.other_name;
    const phone = debt.other_phone || member.phone_number;
    const groupCurrency = activeGroup?.currency || 'DH';
    const text = `Hi ${payerName}, gentle reminder regarding group "${activeGroup?.group_name}": you have a balance of ${formatMoney(debt.amount, groupCurrency)} to settle with ${payeeName}.`;

    if (phone) {
      const cleanPhone = phone.replace(/[^0-9]/g, '');
      window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`, '_blank');
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      showToast('Reminder message copied to clipboard!', 'success');
    } else {
      showToast(text, 'info');
    }
  };

  const handleOpenAddGroupExpense = () => {
    if (!activeGroup) return;
    setTxIsShared(true);
    setSplitTargetType('GROUP');
    setTxGroupId(activeGroup.group_id);
    setTxPayerType('you');
    setTxCategory(outflowCats[0]?.name || 'Food');
    setActiveModal('outflow');
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!groupCreateName.trim()) return showToast('Group name is required', 'error');

    try {
      await apiClient.request('/groups', 'POST', {
        group_name: groupCreateName.trim(),
        type: groupCreateType,
        simplify_debts: groupCreateSimplifyDebts,
        member_user_ids: groupCreateSelectedFriends
      });
      showToast('Group created successfully!', 'success');
      trackEvent('group_created', {
        type: groupCreateType,
        simplify_debts: groupCreateSimplifyDebts,
        members_count: groupCreateSelectedFriends.length
      });
      setGroupCreateName('');
      setGroupCreateType('TRIP');
      setGroupCreateSimplifyDebts(true);
      setGroupCreateSelectedFriends([]);
      setActiveModal(null);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();
    if (!activeGroup || !groupEditName.trim()) return showToast('Group name is required', 'error');

    try {
      await apiClient.request(`/groups/${activeGroup.group_id}`, 'PUT', {
        group_name: groupEditName.trim(),
        type: groupEditType,
        simplify_debts: groupEditSimplifyDebts
      });
      showToast('Group settings updated!', 'success');
      const updatedGroup = {
        ...activeGroup,
        group_name: groupEditName.trim(),
        type: groupEditType,
        simplify_debts: groupEditSimplifyDebts
      };
      setActiveGroup(updatedGroup);
      setActiveModal(null);
      await handleViewGroup(updatedGroup);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!group) return;
    const hasUnsettled = Object.values(groupBalances || {}).some(bal => Math.abs(bal) > 0.005);
    if (hasUnsettled) {
      showToast('Group cannot be deleted because there are unsettled balances remaining. All balances must be settled before deleting the group.', 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete group "${group.group_name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await apiClient.request(`/groups/${group.group_id}`, 'DELETE');
      showToast('Group deleted successfully!', 'success');
      setActiveGroup(null);
      setActiveModal(null);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleLeaveGroup = async (group) => {
    if (!group) return;
    const myId = localStorage.getItem('dhudd_user_id');
    const myBal = groupBalances[myId] || 0;
    if (Math.abs(myBal) > 0.005) {
      const actionText = myBal > 0 
        ? `are owed ${formatMoney(myBal, group.currency || 'AED')}` 
        : `owe ${formatMoney(Math.abs(myBal), group.currency || 'AED')}`;
      showToast(`Cannot leave group: you ${actionText}. All balances must be settled before leaving.`, 'error');
      return;
    }
    if (!window.confirm(`Are you sure you want to leave group "${group.group_name}"?`)) {
      return;
    }
    try {
      const res = await apiClient.request(`/groups/${group.group_id}/leave`, 'POST');
      showToast(res.message || 'You have left the group successfully', 'success');
      setActiveGroup(null);
      setActiveModal(null);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const openEditGroupModal = (group) => {
    setGroupEditName(group.group_name);
    setGroupEditType(group.type || 'TRIP');
    setGroupEditSimplifyDebts(group.simplify_debts !== false && group.simplify_debts !== 0 && group.simplify_debts !== 'false');
    setActiveModal('group_edit');
  };

  const handleAddGroupMember = async (e) => {
    e.preventDefault();
    if (!selectedFriendIdToAdd || !activeGroup) return showToast('Select a friend to add', 'error');

    try {
      await apiClient.request(`/groups/${activeGroup.group_id}/members`, 'POST', { friend_user_id: selectedFriendIdToAdd });
      showToast('Member added to group!', 'success');
      setSelectedFriendIdToAdd('');
      await handleViewGroup(activeGroup);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleRemoveGroupMember = async (memberUserId) => {
    if (!activeGroup) return;
    const targetBal = groupBalances[memberUserId] || 0;
    if (Math.abs(targetBal) > 0.005) {
      showToast(`Cannot remove member with unsettled balance (${formatMoney(Math.abs(targetBal), activeGroup?.currency || 'AED')}). All balances must be settled first.`, 'error');
      return;
    }
    if (!window.confirm('Are you sure you want to remove this member from the group?')) return;
    try {
      await apiClient.request(`/groups/${activeGroup.group_id}/members/${memberUserId}`, 'DELETE');
      showToast('Member removed!', 'success');
      await handleViewGroup(activeGroup);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleUpdateMemberRole = async (memberUserId, newRole) => {
    if (!activeGroup) return;
    try {
      const res = await apiClient.request(`/groups/${activeGroup.group_id}/members/${memberUserId}/role`, 'PUT', { role: newRole });
      showToast(res.message || `Member role updated to ${newRole}!`, 'success');
      await handleViewGroup(activeGroup);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleSettleGroupDebt = async (transfer, accountId) => {
    try {
      const acc = accounts.find(a => a.account_id === accountId);
      const cur = acc ? acc.currency : 'AED';

      await apiClient.request(`/groups/${activeGroup.group_id}/settle`, 'POST', {
        from_user_id: transfer.from_user_id,
        to_user_id: transfer.to_user_id,
        amount: transfer.amount,
        currency: cur,
        account_id: accountId || null
      });
      showToast('Debt settled!', 'success');
      await handleViewGroup(activeGroup);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const openGroupOpeningBalancesModal = async (group) => {
    if (!group) return;
    setLoadingOpeningBalances(true);
    setOpeningBalanceError('');
    setIsGroupOpeningBalancesModalOpen(true);
    try {
      const data = await apiClient.request(`/groups/${group.group_id}/opening-balances`);
      if (Array.isArray(data) && data.length > 0) {
        setGroupOpeningBalancePairs(data.map((item, idx) => ({
          id: item.transaction_id || `pair_${idx}_${Date.now()}`,
          debtor_user_id: item.debtor_user_id || '',
          creditor_user_id: item.creditor_user_id || '',
          amount: item.amount ? String(item.amount) : '',
          currency: item.currency || group.currency || 'AED',
          notes: item.notes === 'Opening Balance' ? '' : (item.notes || '')
        })));
      } else {
        setGroupOpeningBalancePairs([
          { id: `pair_${Date.now()}`, debtor_user_id: '', creditor_user_id: '', amount: '', currency: group.currency || 'AED', notes: '' }
        ]);
      }
    } catch (err) {
      setOpeningBalanceError(err.message || 'Failed to load existing opening balances');
      showToast(err.message, 'error');
      setGroupOpeningBalancePairs([
        { id: `pair_${Date.now()}`, debtor_user_id: '', creditor_user_id: '', amount: '', currency: group.currency || 'AED', notes: '' }
      ]);
    } finally {
      setLoadingOpeningBalances(false);
    }
  };

  const handleAddOpeningBalancePair = () => {
    setOpeningBalanceError('');
    setGroupOpeningBalancePairs(prev => [
      ...prev,
      { id: `pair_${Date.now()}_${Math.random()}`, debtor_user_id: '', creditor_user_id: '', amount: '', currency: activeGroup?.currency || 'AED', notes: '' }
    ]);
  };

  const handleRemoveOpeningBalancePair = (id) => {
    setOpeningBalanceError('');
    setGroupOpeningBalancePairs(prev => prev.filter(p => p.id !== id));
  };

  const handleUpdateOpeningBalancePair = (id, field, value) => {
    setOpeningBalanceError('');
    setGroupOpeningBalancePairs(prev => prev.map(p => {
      if (p.id !== id) return p;
      return { ...p, [field]: value };
    }));
  };

  const handleSaveGroupOpeningBalances = async (e) => {
    if (e) e.preventDefault();
    if (!activeGroup) return;
    setOpeningBalanceError('');

    // Filter out empty rows
    const filledPairs = groupOpeningBalancePairs.filter(p => p.debtor_user_id || p.creditor_user_id || p.amount);

    for (let i = 0; i < filledPairs.length; i++) {
      const p = filledPairs[i];
      if (!p.debtor_user_id || !p.creditor_user_id) {
        const msg = `Pair #${i + 1}: Select both debtor and creditor`;
        setOpeningBalanceError(msg);
        return showToast(msg, 'error');
      }
      if (p.debtor_user_id === p.creditor_user_id) {
        const msg = `Pair #${i + 1}: Debtor and creditor cannot be the same member`;
        setOpeningBalanceError(msg);
        return showToast(msg, 'error');
      }
      const amt = parseFloat(p.amount);
      if (isNaN(amt) || amt <= 0) {
        const msg = `Pair #${i + 1}: Enter a valid positive amount`;
        setOpeningBalanceError(msg);
        return showToast(msg, 'error');
      }
    }

    setSavingOpeningBalances(true);
    try {
      await apiClient.request(`/groups/${activeGroup.group_id}/opening-balances`, 'POST', {
        pairs: filledPairs.map(p => ({
          debtor_user_id: p.debtor_user_id,
          creditor_user_id: p.creditor_user_id,
          amount: parseFloat(p.amount),
          currency: p.currency || activeGroup.currency || 'AED',
          notes: p.notes || ''
        }))
      });

      showToast('Opening balances saved successfully!', 'success');
      setIsGroupOpeningBalancesModalOpen(false);
      await handleViewGroup(activeGroup);
      refreshData();
    } catch (err) {
      setOpeningBalanceError(err.message || 'Failed to save opening balances');
      showToast(err.message, 'error');
    } finally {
      setSavingOpeningBalances(false);
    }
  };

  const openingBalanceNetPreview = useMemo(() => {
    const map = {}; // userId -> currency -> net amount
    groupMembers.forEach(m => {
      map[m.user_id] = {};
    });

    groupOpeningBalancePairs.forEach(p => {
      const amt = parseFloat(p.amount);
      if (!amt || amt <= 0 || !p.debtor_user_id || !p.creditor_user_id || p.debtor_user_id === p.creditor_user_id) return;
      const curr = p.currency || activeGroup?.currency || 'AED';

      if (!map[p.creditor_user_id]) map[p.creditor_user_id] = {};
      if (!map[p.debtor_user_id]) map[p.debtor_user_id] = {};

      map[p.creditor_user_id][curr] = (map[p.creditor_user_id][curr] || 0) + amt;
      map[p.debtor_user_id][curr] = (map[p.debtor_user_id][curr] || 0) - amt;
    });

    return map;
  }, [groupMembers, groupOpeningBalancePairs, activeGroup]);

  const computeFriendDetailedBalances = (friend) => {
    if (!friend) return {};
    const myUserId = localStorage.getItem('dhudd_user_id');
    const friendUserId = friend.user_id;

    const res = {}; // currency -> { nonGroup: 0, groupBalances: {}, deletedGroupsBalance: 0, overall: 0 }

    // 1. Calculate non-group transaction balances
    transactions.forEach(tx => {
      if (tx.group_id || tx.is_deleted_group) return;
      if (!tx.is_shared) return;

      const cur = tx.currency || 'AED';
      if (!res[cur]) res[cur] = { nonGroup: 0, groupBalances: {}, deletedGroupsBalance: 0, overall: 0 };

      const payers = tx.splits?.filter(s => parseFloat(s.paid_amount || 0) > 0) || [];
      const isMyPay = payers.some(p => p.member_user_id === myUserId) || tx.paid_by_user_id === myUserId || (!tx.paid_by_user_id && payers.length === 0 && tx.user_id === myUserId);
      const isFriendPay = payers.some(p => p.member_user_id === friendUserId) || tx.paid_by_user_id === friendUserId;

      if (tx.splits && tx.splits.length > 0) {
        tx.splits.forEach(split => {
          if (isMyPay && split.member_user_id === friendUserId) {
            res[cur].nonGroup += parseFloat(split.owed_amount || 0);
          } else if (isFriendPay && split.member_user_id === myUserId) {
            res[cur].nonGroup -= parseFloat(split.owed_amount || 0);
          }
        });
      } else {
        if (isMyPay && tx.user_id === friendUserId) {
          res[cur].nonGroup += parseFloat(tx.total_amount || 0);
        } else if (isFriendPay && tx.user_id === myUserId) {
          res[cur].nonGroup -= parseFloat(tx.total_amount || 0);
        }
      }
    });

    // 2. Calculate group balances across active shared groups AND deleted groups
    const groupTxMap = {};
    transactions.forEach(tx => {
      if ((!tx.group_id && !tx.is_deleted_group) || !tx.is_shared) return;
      const gid = tx.group_id || '__deleted__';
      if (!groupTxMap[gid]) groupTxMap[gid] = [];
      groupTxMap[gid].push(tx);
    });

    Object.entries(groupTxMap).forEach(([gid, txList]) => {
      const activeGroup = gid !== '__deleted__' ? groups.find(g => g.group_id === gid) : null;
      const isGroupActive = !!activeGroup;
      const isSimplified = activeGroup && (
        activeGroup.simplify_debts !== false &&
        activeGroup.simplify_debts !== 0 &&
        activeGroup.simplify_debts !== 'false'
      );

      // Partition txList by currency
      const curMap = {};
      txList.forEach(tx => {
        const cur = tx.currency || 'AED';
        if (!curMap[cur]) curMap[cur] = [];
        curMap[cur].push(tx);
      });

      Object.entries(curMap).forEach(([cur, curTxs]) => {
        if (!res[cur]) res[cur] = { nonGroup: 0, groupBalances: {}, deletedGroupsBalance: 0, overall: 0 };

        let delta = 0;
        if (isGroupActive && isSimplified) {
          const members = activeGroup.members ? activeGroup.members.map(m => m.user_id) : [];
          const resolution = resolveGroupDebts(curTxs, members, true);
          const transfers = resolution.simplifiedTransfers || [];
          transfers.forEach(t => {
            if (t.from === friendUserId && t.to === myUserId) {
              delta += t.amount;
            } else if (t.from === myUserId && t.to === friendUserId) {
              delta -= t.amount;
            }
          });
        } else {
          // Pairwise calculation (for unsimplified active groups or deleted groups)
          curTxs.forEach(tx => {
            const payers = tx.splits?.filter(s => parseFloat(s.paid_amount || 0) > 0) || [];
            const isMyPay = payers.some(p => p.member_user_id === myUserId) || tx.paid_by_user_id === myUserId || (!tx.paid_by_user_id && payers.length === 0 && tx.user_id === myUserId);
            const isFriendPay = payers.some(p => p.member_user_id === friendUserId) || tx.paid_by_user_id === friendUserId;

            if (tx.splits && tx.splits.length > 0) {
              tx.splits.forEach(split => {
                if (isMyPay && split.member_user_id === friendUserId) {
                  delta += parseFloat(split.owed_amount || 0);
                } else if (isFriendPay && split.member_user_id === myUserId) {
                  delta -= parseFloat(split.owed_amount || 0);
                }
              });
            } else {
              if (isMyPay && tx.user_id === friendUserId) {
                delta += parseFloat(tx.total_amount || 0);
              } else if (isFriendPay && tx.user_id === myUserId) {
                delta -= parseFloat(tx.total_amount || 0);
              }
            }
          });
        }

        if (delta !== 0) {
          if (isGroupActive) {
            if (!res[cur].groupBalances[gid]) {
              res[cur].groupBalances[gid] = { name: activeGroup.group_name, amount: 0 };
            }
            res[cur].groupBalances[gid].amount += delta;
          } else {
            res[cur].deletedGroupsBalance += delta;
          }
        }
      });
    });

    // 3. Fallback to friend.balances if any direct balances exist
    if (friend.balances && friend.balances.length > 0) {
      friend.balances.forEach(b => {
        if (!res[b.currency]) {
          res[b.currency] = { nonGroup: parseFloat(b.amount || 0), groupBalances: {}, deletedGroupsBalance: 0, overall: 0 };
        } else if (res[b.currency].nonGroup === 0 && Object.keys(res[b.currency].groupBalances).length === 0 && res[b.currency].deletedGroupsBalance === 0) {
          res[b.currency].nonGroup = parseFloat(b.amount || 0);
        }
      });
    }

    // 4. Compute overall sums per currency and harmonize with friend.balances if available
    Object.keys(res).forEach(cur => {
      let groupSum = 0;
      Object.values(res[cur].groupBalances).forEach(gb => {
        groupSum += gb.amount;
      });
      const deletedSum = res[cur].deletedGroupsBalance || 0;
      res[cur].overall = res[cur].nonGroup + groupSum + deletedSum;

      if (friend.balances && friend.balances.length > 0) {
        const friendBal = friend.balances.find(b => b.currency === cur);
        if (friendBal && Math.abs(res[cur].overall - friendBal.amount) > 0.001) {
          if (Math.abs(res[cur].overall - friendBal.amount) <= 0.05) {
            res[cur].overall = friendBal.amount;
          }
        }
      }
    });

    return res;
  };

  // Aggregated totals across all friends (Total friends owe me & Total I owe friends in each currency)
  const friendsAggregatedBalances = useMemo(() => {
    const friendsOweMe = {}; // currency -> total positive amount
    const iOweFriends = {};  // currency -> total positive amount (user debt)

    (friends || []).forEach(f => {
      const detailedMap = computeFriendDetailedBalances(f);
      Object.keys(detailedMap).forEach(cur => {
        const overall = detailedMap[cur]?.overall || 0;
        if (overall > 0.0001) {
          friendsOweMe[cur] = (friendsOweMe[cur] || 0) + overall;
        } else if (overall < -0.0001) {
          iOweFriends[cur] = (iOweFriends[cur] || 0) + Math.abs(overall);
        }
      });
    });

    const prefs = preferredCurrencies && preferredCurrencies.length > 0 ? preferredCurrencies : syncEngine.getLocal('dhudd_cache_preferred_currencies', ['AED']);
    const preferredCurrencySet = new Set(Array.isArray(prefs) ? prefs : (typeof prefs === 'string' ? JSON.parse(prefs) : ['AED']));

    const sortCurrencies = (currList) => {
      return [...currList].sort((a, b) => {
        if (preferredCurrencySet.has(a) && !preferredCurrencySet.has(b)) return -1;
        if (!preferredCurrencySet.has(a) && preferredCurrencySet.has(b)) return 1;
        return a.localeCompare(b);
      });
    };

    return {
      friendsOweMe,
      iOweFriends,
      oweMeCurrencies: sortCurrencies(Object.keys(friendsOweMe).filter(c => friendsOweMe[c] > 0.001)),
      iOweCurrencies: sortCurrencies(Object.keys(iOweFriends).filter(c => iOweFriends[c] > 0.001))
    };
  }, [friends, transactions, groups, preferredCurrencies]);

  const handleOpenFriendSettleChooser = async (currency, currentOverallAmount) => {
    if (!activeFriend) return;
    setFriendSettleLoadingCur(currency);
    try {
      const res = await apiClient.request(`/friends/${activeFriend.user_id}/balances`);
      setFriendBalanceChooserData(res);
      if (res && res.groups && res.groups.length > 0) {
        setFriendBalanceChooserCurrency(currency || res.groups[0]?.currency || 'AED');
        setFriendBalanceChooserOpen(true);
        return;
      }
    } catch (err) {
      console.warn('Could not fetch friend balance breakdown:', err);
    } finally {
      setFriendSettleLoadingCur(null);
    }

    // Direct fallback if no mutual groups or endpoint error
    setFriendSettleGroupId(null);
    setFriendSettleGroupName('');
    setFriendSettleIsAll(false);
    setFriendSettleCurrency(currency);
    setFriendSettleAmount(formatInputValue(Math.abs(currentOverallAmount || 0)));
    setFriendSettleDirection(currentOverallAmount < 0 ? 'pay' : 'receive');
    setFriendSettleAccountId(accounts[0]?.account_id || '');
    setFriendSettleDate(new Date().toISOString().split('T')[0]);
    setFriendSettleOpen(true);
    setFriendAdjustOpen(false);
  };

  const handleSelectBalanceOption = (option) => {
    setFriendBalanceChooserOpen(false);
    if (!option) return;

    if (option.type === 'group') {
      setFriendSettleGroupId(option.group_id);
      setFriendSettleGroupName(option.group_name || '');
      setFriendSettleIsAll(false);
      setFriendSettleCurrency(option.currency);
      setFriendSettleAmount(formatInputValue(option.amount));
      setFriendSettleDirection(option.direction || 'pay');
      setFriendSettleAccountId(accounts[0]?.account_id || '');
      setFriendSettleDate(new Date().toISOString().split('T')[0]);
      setFriendSettleOpen(true);
      setFriendAdjustOpen(false);
    } else if (option.type === 'settle_all') {
      setFriendSettleGroupId(null);
      setFriendSettleGroupName('');
      setFriendSettleIsAll(true);
      setFriendSettleCurrency(option.currency);
      setFriendSettleAmount(formatInputValue(option.amount));
      setFriendSettleDirection(option.direction || 'pay');
      setFriendSettleAccountId(accounts[0]?.account_id || '');
      setFriendSettleDate(new Date().toISOString().split('T')[0]);
      setFriendSettleOpen(true);
      setFriendAdjustOpen(false);
    } else if (option.type === 'non_group') {
      setFriendSettleGroupId(null);
      setFriendSettleGroupName('Non-group expenses');
      setFriendSettleIsAll(false);
      setFriendSettleCurrency(option.currency);
      setFriendSettleAmount(formatInputValue(option.amount));
      setFriendSettleDirection(option.direction || 'pay');
      setFriendSettleAccountId(accounts[0]?.account_id || '');
      setFriendSettleDate(new Date().toISOString().split('T')[0]);
      setFriendSettleOpen(true);
      setFriendAdjustOpen(false);
    } else {
      // 'more_options' or default
      setFriendSettleGroupId(null);
      setFriendSettleGroupName('');
      setFriendSettleIsAll(false);
      if (option.currency) setFriendSettleCurrency(option.currency);
      if (option.amount) setFriendSettleAmount(formatInputValue(option.amount));
      if (option.direction) setFriendSettleDirection(option.direction);
      setFriendSettleAccountId(accounts[0]?.account_id || '');
      setFriendSettleDate(new Date().toISOString().split('T')[0]);
      setFriendSettleOpen(true);
      setFriendAdjustOpen(false);
    }
  };

  const closeFriendSettleModal = () => {
    setFriendSettleOpen(false);
    setFriendSettleGroupId(null);
    setFriendSettleGroupName('');
    setFriendSettleIsAll(false);
  };

  const handleSettleFriend = async (e) => {
    e.preventDefault();
    if (!activeFriend) return;
    const amt = parseFloat(friendSettleAmount);
    if (!amt || amt <= 0) return showToast('Enter a valid amount', 'error');

    try {
      let settleDateTime = new Date();
      if (friendSettleDate) {
        const [y, m, d] = friendSettleDate.split('-').map(Number);
        const now = new Date();
        settleDateTime = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds());
      }

      await apiClient.request('/friends/settle', 'POST', {
        friend_user_id: activeFriend.user_id,
        amount: amt,
        currency: friendSettleCurrency,
        direction: friendSettleDirection,
        account_id: friendSettleAccountId || null,
        date: settleDateTime.toISOString(),
        group_id: friendSettleGroupId || null,
        settle_all: friendSettleIsAll || false
      });
      
      showToast('Settlement recorded!', 'success');
      closeFriendSettleModal();
      setFriendSettleAmount('');
      setFriendSettleAccountId('');
      setFriendSettleDate(new Date().toISOString().split('T')[0]);
      
      const frnds = await apiClient.request('/friends');
      const sorted = sortFriendsAZ(frnds);
      setFriends(sorted);
      const updatedFriend = sorted.find(f => f.user_id === activeFriend.user_id);
      if (updatedFriend) setActiveFriend(updatedFriend);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteFriend = async (e) => {
    e.preventDefault();
    if (!deletingFriend) return;
    try {
      await apiClient.request(`/friends/${deletingFriend.user_id}?deleteTransactions=${deleteAssociatedTransactionsCheck}`, 'DELETE');
      showToast('Friend deleted successfully!', 'success');
      setDeleteFriendModalOpen(false);
      setDeletingFriend(null);
      setDeleteAssociatedTransactionsCheck(false);
      setActiveFriend(null);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleUpdateNickname = async (e) => {
    e.preventDefault();
    if (!nicknameEditingFriend) return;
    try {
      await apiClient.request(`/friends/${nicknameEditingFriend.user_id}/nickname`, 'PUT', {
        nickname: friendNicknameInput
      });
      showToast('Nickname updated successfully!', 'success');
      setEditNicknameModalOpen(false);
      setNicknameEditingFriend(null);
      setFriendNicknameInput('');
      
      if (activeFriend && activeFriend.user_id === nicknameEditingFriend.user_id) {
        setActiveFriend(prev => ({ ...prev, nickname: friendNicknameInput.trim() || null }));
      }
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleAdjustFriendBalance = async (e) => {
    e.preventDefault();
    if (!activeFriend) return;
    const balanceVal = parseFloat(friendAdjustAmount);
    if (isNaN(balanceVal)) return showToast('Enter a valid balance value', 'error');

    try {
      await apiClient.request('/friends/adjust-balance', 'POST', {
        friend_user_id: activeFriend.user_id,
        new_balance: balanceVal,
        currency: friendAdjustCurrency
      });
      
      showToast('Mutual balance updated manually!', 'success');
      setFriendAdjustOpen(false);
      setFriendAdjustAmount('');
      
      const frnds = await apiClient.request('/friends');
      const sorted = sortFriendsAZ(frnds);
      setFriends(sorted);
      const updatedFriend = sorted.find(f => f.user_id === activeFriend.user_id);
      if (updatedFriend) setActiveFriend(updatedFriend);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleMergeFriend = async (e) => {
    e.preventDefault();
    if (!activeFriend || (!friendMergeEmail && !friendMergePhone)) {
      return showToast('Please select or enter an email or phone number', 'error');
    }
    try {
      await apiClient.request('/friends/merge', 'POST', {
        placeholder_user_id: activeFriend.user_id,
        real_user_email: friendMergeEmail || undefined,
        real_user_phone: friendMergePhone || undefined
      });
      showToast('Placeholder friend successfully merged!', 'success');
      setFriendMergeOpen(false);
      setFriendMergeEmail('');
      setFriendMergePhone('');
      setActiveFriend(null);
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteTx = async (txId) => {
    if (!window.confirm('Delete this transaction?')) return;
    if (deletingTxIdsRef.current.has(txId)) return;
    deletingTxIdsRef.current.add(txId);
    // Optimistic UI update: immediately remove from local transactions state for instant feedback
    setTransactions(prev => prev.filter(t => t.transaction_id !== txId && t.source_transaction_id !== txId));
    if (editingTx && (editingTx.transaction_id === txId || editingTx.source_transaction_id === txId)) {
      setEditTxModalOpen(false);
      setEditingTx(null);
    }
    try {
      const res = await syncEngine.deleteTransactionCached(txId);
      if (res && res.offline) {
        showToast('Deleted offline — will sync when reconnected', 'info');
      } else {
        showToast('Transaction deleted', 'success');
      }
      refreshData();
      if (activeGroupRef.current?.group_id) {
        reloadActiveGroupBalances(activeGroupRef.current.group_id);
      }
    } catch (err) {
      showToast(err.message, 'error');
      refreshData();
      if (activeGroupRef.current?.group_id) {
        reloadActiveGroupBalances(activeGroupRef.current.group_id);
      }
    } finally {
      deletingTxIdsRef.current.delete(txId);
    }
  };

  const handleExportZip = async () => {
    try {
      const token = apiClient.getToken();
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      const response = await fetch(`${apiClient.API_BASE_URL}/backup/zip`, {
        headers
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to download backup ZIP');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
link.setAttribute('href', url);
      link.setAttribute('download', `tezer_backup_${Date.now()}.zip`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast('ZIP backup completed successfully!', 'success');
    } catch (err) {
      showToast(err.message, 'error');
    }
  };



  const openExportModal = (targetType, targetData) => {
    setExportTargetType(targetType);
    setExportTargetData(targetData || null);
    setExportFormat('pdf');
    setExportPeriod('all');
    setExportStartDate('');
    setExportEndDate('');
    setExportLimitType('all');
    setExportLimitValue('');
    setExportModalOpen(true);
  };

  const downloadCsv = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const generatePdfReport = (title, scopeDetails, columns, tableRowsHtml, totalCount, summaryCardsHtml = '') => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      return showToast('Popup blocked! Please allow popups to export PDF', 'error');
    }

    const userName = getUserReportName();
    const now = new Date();
    const dateStr = formatDateDDMMYYYY(now) + ' ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    const headersHtml = columns.map(col => `
      <th style="width: ${col.width || 'auto'}; text-align: ${col.align || 'left'};">${escapeHtml(col.name)}</th>
    `).join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <base href="${window.location.origin}/">
        <title>tezer - ${escapeHtml(title)}</title>
        <style>
          ${REPORT_BASE_STYLES}
        </style>
      </head>
      <body>
        <div class="header">
          <div style="display: flex; justify-content: space-between; align-items: flex-start;">
            <div>
              ${TEZER_REPORT_LOGO_SVG}
              <h1>tezer</h1>
              <p class="subtitle">${escapeHtml(title)}</p>
            </div>
            <div style="text-align: right; padding-top: 4px;">
              <a href="https://tezer.app" target="_blank" rel="noopener noreferrer" class="app-url-link" style="font-size: 15px; font-weight: 700; text-decoration: none; display: inline-flex; align-items: center; gap: 4px;">
                tezer.app
                <span style="font-size: 12px;">↗</span>
              </a>
              <div style="font-size: 11px; color: #94a3b8; margin-top: 2px;">Smart Personal &amp; Shared Finance</div>
            </div>
          </div>
        </div>

        ${summaryCardsHtml ? `
        <div class="balance-cards-grid">
          ${summaryCardsHtml}
        </div>
        ` : ''}

        <div class="filter-card">
          <div class="filter-title">Report Details</div>
          <div>${escapeHtml(scopeDetails)}</div>
        </div>

        <table>
          <thead>
            <tr>
              ${headersHtml}
            </tr>
          </thead>
          <tbody>
            ${tableRowsHtml}
          </tbody>
        </table>

        <div class="total-summary">
          <span>Total Records: ${escapeHtml(totalCount)}</span>
        </div>

        <div class="report-meta-footer">
          <span>Generated by <strong>${escapeHtml(userName)}</strong> on ${escapeHtml(dateStr)}</span>
          <span>tezer.app</span>
        </div>

        <div class="report-footer">
          <span>Powered by <strong>tezer</strong> — Smart Personal &amp; Shared Expense Tracker</span>
          <span>Access the app: <a href="https://tezer.app" target="_blank" rel="noopener noreferrer" class="app-url-link" style="text-decoration: none; font-weight: 600;">https://tezer.app</a></span>
        </div>

        <script>
          (function() {
            function doPrint() {
              setTimeout(function() {
                window.print();
              }, 250);
            }
            if (document.fonts && document.fonts.ready) {
              document.fonts.ready.then(doPrint).catch(doPrint);
            } else if (document.readyState === 'complete') {
              doPrint();
            } else {
              window.addEventListener('load', doPrint);
            }
          })();
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
    showToast('PDF Report generated successfully!', 'success');
  };

  const handleExecuteEntityExport = () => {
    const myUserId = localStorage.getItem('dhudd_user_id');
    const userName = getUserReportName();
    const now = new Date();
    const dateStr = formatDateDDMMYYYY(now) + ' ' + now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    const escapeCsv = (val) => '"' + String(val || '').replace(/"/g, '""') + '"';

    // -------------------------------------------------------------
    // 1. SUMMARY LIST EXPORTS (Accounts, Credit Cards, Friends, Groups)
    // -------------------------------------------------------------
    if (exportTargetType.endsWith('_summary')) {
      setExportModalOpen(false);

      if (exportTargetType === 'accounts_summary') {
        const activeAccs = accounts.filter(acc => acc.type !== 'CREDIT_CARD');
        const title = 'Cash & Bank Accounts Summary';

        if (exportFormat === 'excel') {
          const headers = ['Account Name', 'Type', 'Currency', 'Current Balance', 'Min Balance Target', 'Monthly Spend Target', 'Status', 'Included in Total', 'Default Account'];
          const rows = activeAccs.map(acc => {
            const isDef = acc.is_default ? 'Yes' : 'No';
            const isInc = acc.include_in_total !== false && acc.include_in_total !== 0 && acc.include_in_total !== '0' ? 'Yes' : 'No';
            const status = acc.is_active !== false && acc.is_active !== 0 && acc.is_active !== '0' ? 'Active' : 'Disabled';
            return [
              escapeCsv(acc.name),
              escapeCsv(acc.type),
              escapeCsv(acc.currency),
              parseFloat(acc.current_balance || 0).toFixed(2),
              parseFloat(acc.minimum_balance || 0).toFixed(2),
              parseFloat(acc.monthly_spend_target || 0).toFixed(2),
              escapeCsv(status),
              escapeCsv(isInc),
              escapeCsv(isDef)
            ].join(',');
          });

          const csvContent = [`# ${title} | Generated by: ${userName} on ${dateStr}`, headers.join(','), ...rows].join('\n');
          downloadCsv(csvContent, `tezer_accounts_summary_${Date.now()}.csv`);
          return showToast('Accounts Summary Excel export completed!', 'success');
        } else {
          // PDF Report
          const totalAssetsByCurr = {};
          activeAccs.forEach(acc => {
            const c = acc.currency || 'AED';
            totalAssetsByCurr[c] = (totalAssetsByCurr[c] || 0) + (parseFloat(acc.current_balance) || 0);
          });
          const assetCards = Object.entries(totalAssetsByCurr).map(([c, total]) => `
            <div class="balance-card">
              <div class="balance-card-label">Total Assets (${c})</div>
              <div class="balance-card-value" style="color: ${total >= 0 ? '#2bcbba' : '#ff6b6b'};">${formatMoney(total, c)}</div>
              <div class="balance-card-sub">Sum of cash &amp; bank balances</div>
            </div>
          `).join('');
          const summaryCardsHtml = `
            ${assetCards}
            <div class="balance-card">
              <div class="balance-card-label">Active Accounts</div>
              <div class="balance-card-value" style="color: #38bdf8;">${activeAccs.length}</div>
              <div class="balance-card-sub">Cash &amp; bank accounts</div>
            </div>
          `;

          const tableRowsHtml = activeAccs.map((acc, idx) => {
            const isDef = acc.is_default ? '⭐ Default' : '-';
            const status = acc.is_active !== false && acc.is_active !== 0 && acc.is_active !== '0' ? 'Active' : 'Disabled';
            const bal = parseFloat(acc.current_balance || 0);
            return `
              <tr style="background-color: ${idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'};">
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 600;">${escapeHtml(acc.name)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08);">${escapeHtml(acc.type)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 700; color: #00f5d4;">${escapeHtml(acc.currency)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 700; text-align: right; color: ${bal >= 0 ? '#2bcbba' : '#ff6b6b'};">
                  ${formatMoney(bal, acc.currency)}
                </td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); text-align: right;">${acc.monthly_spend_target > 0 ? formatMoney(acc.monthly_spend_target, acc.currency) : '-'}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08);">${status} ${isDef !== '-' ? `(${isDef})` : ''}</td>
              </tr>
            `;
          }).join('');

          generatePdfReport(title, `Total Accounts: ${activeAccs.length}`, [
            { name: 'Account Name', width: '25%' },
            { name: 'Type', width: '15%' },
            { name: 'Currency', width: '10%' },
            { name: 'Current Balance', width: '20%', align: 'right' },
            { name: 'Spend Target', width: '15%', align: 'right' },
            { name: 'Status', width: '15%' }
          ], tableRowsHtml, activeAccs.length, summaryCardsHtml);
          return;
        }
      }

      if (exportTargetType === 'credit_cards_summary') {
        const cardAccs = accounts.filter(acc => acc.type === 'CREDIT_CARD');
        const title = 'Credit Cards Summary';

        if (exportFormat === 'excel') {
          const headers = ['Card Name', 'Currency', 'Owed Balance', 'Credit Limit', 'Available Credit', 'Utilization %', 'Statement Day', 'Due Day', 'Monthly Spend Target', 'Status', 'Default Card'];
          const rows = cardAccs.map(acc => {
            const bal = parseFloat(acc.current_balance || 0);
            const limit = parseFloat(acc.credit_limit || 0);
            const avail = limit > 0 ? limit - bal : 0;
            const util = limit > 0 ? (bal / limit) * 100 : 0;
            const isDef = acc.is_default ? 'Yes' : 'No';
            const status = acc.is_active !== false && acc.is_active !== 0 && acc.is_active !== '0' ? 'Active' : 'Disabled';

            return [
              escapeCsv(acc.name),
              escapeCsv(acc.currency),
              bal.toFixed(2),
              limit.toFixed(2),
              avail.toFixed(2),
              util.toFixed(1) + '%',
              acc.statement_day || '',
              acc.due_day || '',
              parseFloat(acc.monthly_spend_target || 0).toFixed(2),
              escapeCsv(status),
              escapeCsv(isDef)
            ].join(',');
          });

          const csvContent = [`# ${title} | Generated by: ${userName} on ${dateStr}`, headers.join(','), ...rows].join('\n');
          downloadCsv(csvContent, `tezer_credit_cards_summary_${Date.now()}.csv`);
          return showToast('Credit Cards Summary Excel export completed!', 'success');
        } else {
          // PDF Report
          let totalDue = 0;
          let totalLimit = 0;
          const curr = cardAccs[0]?.currency || 'AED';
          cardAccs.forEach(acc => {
            totalDue += parseFloat(acc.current_balance || 0);
            totalLimit += parseFloat(acc.credit_limit || 0);
          });
          const totalAvail = Math.max(0, totalLimit - totalDue);
          const avgUtil = totalLimit > 0 ? (totalDue / totalLimit) * 100 : 0;
          const summaryCardsHtml = `
            <div class="balance-card">
              <div class="balance-card-label">Total Outstanding Due</div>
              <div class="balance-card-value" style="color: #ff6b6b;">${formatMoney(totalDue, curr)}</div>
              <div class="balance-card-sub">Total owed across cards</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Total Available Credit</div>
              <div class="balance-card-value" style="color: #2bcbba;">${formatMoney(totalAvail, curr)}</div>
              <div class="balance-card-sub">Limit: ${formatMoney(totalLimit, curr)}</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Credit Limit</div>
              <div class="balance-card-value" style="color: #38bdf8;">${formatMoney(totalLimit, curr)}</div>
              <div class="balance-card-sub">Avg Utilization: ${Math.round(avgUtil)}%</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Active Credit Cards</div>
              <div class="balance-card-value" style="color: #a55eea;">${cardAccs.length}</div>
              <div class="balance-card-sub">Cards registered</div>
            </div>
          `;

          const tableRowsHtml = cardAccs.map((acc, idx) => {
            const bal = parseFloat(acc.current_balance || 0);
            const limit = parseFloat(acc.credit_limit || 0);
            const avail = limit > 0 ? limit - bal : 0;
            const util = limit > 0 ? (bal / limit) * 100 : 0;
            const status = acc.is_active !== false && acc.is_active !== 0 && acc.is_active !== '0' ? 'Active' : 'Disabled';

            return `
              <tr style="background-color: ${idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'};">
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 600;">${escapeHtml(acc.name)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 700; color: #a55eea;">${escapeHtml(acc.currency)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 700; text-align: right; color: #ff6b6b;">${formatMoney(-bal, acc.currency)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); text-align: right;">${formatMoney(limit, acc.currency)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); text-align: right; color: #00f5d4;">${formatMoney(avail, acc.currency)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); text-align: right;">${Math.round(util)}%</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08);">${status}</td>
              </tr>
            `;
          }).join('');

          generatePdfReport(title, `Total Credit Cards: ${cardAccs.length}`, [
            { name: 'Card Name', width: '22%' },
            { name: 'Currency', width: '10%' },
            { name: 'Owed Balance', width: '16%', align: 'right' },
            { name: 'Credit Limit', width: '16%', align: 'right' },
            { name: 'Available Limit', width: '16%', align: 'right' },
            { name: 'Utilization', width: '10%', align: 'right' },
            { name: 'Status', width: '10%' }
          ], tableRowsHtml, cardAccs.length, summaryCardsHtml);
          return;
        }
      }

      if (exportTargetType === 'friends_summary') {
        const title = 'Friends & Ledger Balances Summary';

        const preferred = syncEngine.getLocal('dhudd_cache_preferred_currencies', ['AED']);
        const prefArr = Array.isArray(preferred) ? preferred : (typeof preferred === 'string' ? JSON.parse(preferred) : ['AED']);

        const activeCurrenciesSet = new Set([...prefArr]);
        friends.forEach(f => {
          const detailedMap = computeFriendDetailedBalances(f);
          Object.keys(detailedMap).forEach(c => activeCurrenciesSet.add(c));
          (f.balances || []).forEach(b => { if (b.currency) activeCurrenciesSet.add(b.currency); });
        });

        const sortedCurrencies = Array.from(activeCurrenciesSet).filter(Boolean).sort();

        if (exportFormat === 'excel') {
          const headers = [
            'Friend Name',
            'Email',
            'Nickname',
            'Friend Type',
            'Phone Number',
            ...sortedCurrencies.map(curr => `Net Balance (${curr})`),
            'Status'
          ];

          const rows = friends.map(f => {
            const name = f.display_name || f.nickname || f.email.split('@')[0];
            const type = f.is_placeholder ? 'Placeholder' : 'Registered User';
            const detailedMap = computeFriendDetailedBalances(f);

            const currCols = sortedCurrencies.map(cur => {
              const net = detailedMap[cur]?.overall || 0;
              return net.toFixed(2);
            });

            return [
              escapeCsv(name),
              escapeCsv(f.email || ''),
              escapeCsv(f.nickname || ''),
              escapeCsv(type),
              escapeCsv(f.phone_number || ''),
              ...currCols,
              'Active'
            ].join(',');
          });

          const csvContent = [`# ${title} | Generated by: ${userName} | Exported: ${dateStr}`, headers.join(','), ...rows].join('\n');
          downloadCsv(csvContent, `tezer_friends_summary_${Date.now()}.csv`);
          return showToast('Friends Summary Excel export completed!', 'success');
        } else {
          // PDF Report
          const primaryCurr = prefArr[0] || sortedCurrencies[0] || 'AED';
          let totalOwedToYou = 0;
          let totalYouOwe = 0;
          friends.forEach(f => {
            const detailedMap = computeFriendDetailedBalances(f);
            const net = detailedMap[primaryCurr]?.overall || 0;
            if (net > 0) totalOwedToYou += net;
            else if (net < 0) totalYouOwe += Math.abs(net);
          });
          const netPosition = totalOwedToYou - totalYouOwe;
          const netColor = netPosition > 0 ? '#2bcbba' : (netPosition < 0 ? '#ff6b6b' : '#38bdf8');
          const netSign = netPosition > 0 ? '+' : (netPosition < 0 ? '-' : '');

          const summaryCardsHtml = `
            <div class="balance-card">
              <div class="balance-card-label">Owed to You (${primaryCurr})</div>
              <div class="balance-card-value" style="color: #2bcbba;">+${formatMoney(totalOwedToYou, primaryCurr)}</div>
              <div class="balance-card-sub">Friends who owe you</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">You Owe (${primaryCurr})</div>
              <div class="balance-card-value" style="color: #ff6b6b;">-${formatMoney(totalYouOwe, primaryCurr)}</div>
              <div class="balance-card-sub">Debts you owe to friends</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Net Friend Position</div>
              <div class="balance-card-value" style="color: ${netColor};">${netSign}${formatMoney(Math.abs(netPosition), primaryCurr)}</div>
              <div class="balance-card-sub">${netPosition >= 0 ? 'Net Receivable' : 'Net Payable'}</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Total Friends</div>
              <div class="balance-card-value" style="color: #a55eea;">${friends.length}</div>
              <div class="balance-card-sub">${friends.filter(f => !f.is_placeholder).length} registered, ${friends.filter(f => f.is_placeholder).length} other</div>
            </div>
          `;

          const tableRowsHtml = friends.map((f, idx) => {
            const name = f.nickname || f.display_name || f.email.split('@')[0];
            const type = f.is_placeholder ? 'Placeholder' : 'Registered';
            const detailedMap = computeFriendDetailedBalances(f);

            const currTdsHtml = sortedCurrencies.map(cur => {
              const net = detailedMap[cur]?.overall || 0;
              let color = '#94a3b8';
              let valStr = `0.00 ${cur}`;
              if (net > 0) {
                color = '#2bcbba';
                valStr = `+${formatMoney(net, cur)}`;
              } else if (net < 0) {
                color = '#ff6b6b';
                valStr = `-${formatMoney(Math.abs(net), cur)}`;
              }
              return `<td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 700; text-align: right; color: ${color};">${valStr}</td>`;
            }).join('');

            return `
              <tr style="background-color: ${idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'};">
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 600;">${escapeHtml(name)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 12px;">${escapeHtml(f.email || '-')}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08);">${type}</td>
                ${currTdsHtml}
              </tr>
            `;
          }).join('');

          const baseColumns = [
            { name: 'Friend Name', width: '22%' },
            { name: 'Email / Handle', width: '25%' },
            { name: 'Type', width: '13%' }
          ];

          const currColumns = sortedCurrencies.map(cur => ({
            name: `${cur} Net Balance`,
            width: `${Math.floor(40 / (sortedCurrencies.length || 1))}%`,
            align: 'right'
          }));

          generatePdfReport(title, `Total Friends: ${friends.length}`, [...baseColumns, ...currColumns], tableRowsHtml, friends.length, summaryCardsHtml);
          return;
        }
      }

      if (exportTargetType === 'groups_summary') {
        const title = 'Shared Groups Summary';
        const preferred = syncEngine.getLocal('dhudd_cache_preferred_currencies', ['AED']);
        const prefArr = Array.isArray(preferred) ? preferred : (typeof preferred === 'string' ? JSON.parse(preferred) : ['AED']);
        const primaryCurr = prefArr[0] || 'AED';

        if (exportFormat === 'excel') {
          const headers = ['Group Name', 'Group Type', 'Debt Simplification', 'Member Count', 'Member Names', 'Total Group Spending', 'Status'];
          const rows = groups.map(g => {
            const isSimp = g.simplify_debts !== false && g.simplify_debts !== 0 && g.simplify_debts !== 'false' ? 'ON' : 'OFF';
            const gTxs = transactions.filter(tx => tx.group_id === g.group_id);
            
            const currSpendMap = {};
            gTxs.forEach(tx => {
              const c = tx.currency || 'AED';
              currSpendMap[c] = (currSpendMap[c] || 0) + (parseFloat(tx.total_amount) || 0);
            });
            const spendStr = Object.entries(currSpendMap).map(([c, amt]) => `${amt.toFixed(2)} ${c}`).join(' | ') || '0.00 AED';
            const members = (g.members || []).map(m => m.display_name || m.nickname || m.email.split('@')[0]).join(', ');

            return [
              escapeCsv(g.group_name),
              escapeCsv(g.type),
              escapeCsv(isSimp),
              (g.members || []).length,
              escapeCsv(members),
              escapeCsv(spendStr),
              'Active'
            ].join(',');
          });

          const csvContent = [`# ${title} | Generated by: ${userName} | Exported: ${dateStr}`, headers.join(','), ...rows].join('\n');
          downloadCsv(csvContent, `tezer_groups_summary_${Date.now()}.csv`);
          return showToast('Groups Summary Excel export completed!', 'success');
        } else {
          // PDF Report
          let totalGroupSpend = 0;
          let groupsWithSpend = 0;
          groups.forEach(g => {
            const gTxs = transactions.filter(tx => tx.group_id === g.group_id && (tx.currency || 'AED') === primaryCurr);
            const sum = gTxs.reduce((acc, tx) => acc + (parseFloat(tx.total_amount) || 0), 0);
            if (sum > 0) groupsWithSpend++;
            totalGroupSpend += sum;
          });
          const simplifiedCount = groups.filter(g => g.simplify_debts !== false && g.simplify_debts !== 0 && g.simplify_debts !== 'false').length;

          const summaryCardsHtml = `
            <div class="balance-card">
              <div class="balance-card-label">Total Groups Spend (${primaryCurr})</div>
              <div class="balance-card-value" style="color: #00f5d4;">${formatMoney(totalGroupSpend, primaryCurr)}</div>
              <div class="balance-card-sub">Recorded across ${groupsWithSpend} active groups</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Total Groups</div>
              <div class="balance-card-value" style="color: #38bdf8;">${groups.length}</div>
              <div class="balance-card-sub">All shared expense groups</div>
            </div>
            <div class="balance-card">
              <div class="balance-card-label">Debt Simplification</div>
              <div class="balance-card-value" style="color: #a55eea;">${simplifiedCount} / ${groups.length}</div>
              <div class="balance-card-sub">Groups with simplification active</div>
            </div>
          `;

          const tableRowsHtml = groups.map((g, idx) => {
            const isSimp = g.simplify_debts !== false && g.simplify_debts !== 0 && g.simplify_debts !== 'false' ? '⚡ ON' : 'OFF';
            const gTxs = transactions.filter(tx => tx.group_id === g.group_id);
            const currSpendMap = {};
            gTxs.forEach(tx => {
              const c = tx.currency || 'AED';
              currSpendMap[c] = (currSpendMap[c] || 0) + (parseFloat(tx.total_amount) || 0);
            });
            const spendStr = Object.entries(currSpendMap).map(([c, amt]) => formatMoney(amt, c)).join(', ') || '0.00';
            const members = (g.members || []).map(m => m.display_name || m.nickname || m.email.split('@')[0]).join(', ');

            return `
              <tr style="background-color: ${idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'};">
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 600; color: #00f5d4;">${escapeHtml(g.group_name)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08);">${escapeHtml(g.type)}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08);">${isSimp}</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 12px;">${escapeHtml(members)} (${(g.members || []).length})</td>
                <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-weight: 700; text-align: right;">${spendStr}</td>
              </tr>
            `;
          }).join('');

          generatePdfReport(title, `Total Groups: ${groups.length}`, [
            { name: 'Group Name', width: '25%' },
            { name: 'Type', width: '15%' },
            { name: 'Simplification', width: '15%' },
            { name: 'Members', width: '25%' },
            { name: 'Total Expenses', width: '20%', align: 'right' }
          ], tableRowsHtml, groups.length, summaryCardsHtml);
          return;
        }
      }
    }

    // -------------------------------------------------------------
    // 2. TRANSACTION EXPORTS (Specific Account, Credit Card, Friend, Group)
    // -------------------------------------------------------------
    if (!exportTargetData) return;

    let entityTxs = [];
    let title = '';

    if (exportTargetType === 'account' || exportTargetType === 'credit_card') {
      entityTxs = transactions.filter(tx => tx.paid_by_account_id === exportTargetData.account_id || tx.transfer_to_account_id === exportTargetData.account_id);
      title = `${exportTargetData.name} (${exportTargetData.currency})`;
    } else if (exportTargetType === 'group') {
      entityTxs = transactions.filter(tx => tx.group_id === exportTargetData.group_id);
      title = `Group: ${exportTargetData.group_name}`;
    } else if (exportTargetType === 'friend') {
      const friendId = exportTargetData.user_id;
      entityTxs = transactions.filter(tx => {
        if (!tx.is_shared && !(tx.paid_by_user_id && tx.paid_by_user_id !== tx.user_id)) return false;
        const hasSplits = tx.splits && Array.isArray(tx.splits) && tx.splits.length > 0;
        const payers = hasSplits ? tx.splits.filter(s => parseFloat(s.paid_amount || 0) > 0) : [];
        const userIsPayer = payers.some(p => p.member_user_id === myUserId) || tx.paid_by_user_id === myUserId || (!tx.paid_by_user_id && payers.length === 0 && tx.user_id === myUserId);
        const friendIsPayer = payers.some(p => p.member_user_id === friendId) || tx.paid_by_user_id === friendId;

        if (hasSplits) {
          const mySplit = tx.splits.find(s => s.member_user_id === myUserId);
          const friendSplit = tx.splits.find(s => s.member_user_id === friendId);
          const myOwed = parseFloat(mySplit?.owed_amount || 0);
          const friendOwed = parseFloat(friendSplit?.owed_amount || 0);

          if (tx.category_name === 'Debt Settlement' || (tx.description && tx.description.toLowerCase().includes('settle'))) {
            return (userIsPayer && tx.user_id === friendId) || (friendIsPayer && tx.user_id === myUserId) || (userIsPayer && friendOwed > 0) || (friendIsPayer && myOwed > 0);
          }

          if (userIsPayer && friendOwed > 0) return true;
          if (friendIsPayer && myOwed > 0) return true;
          if (!tx.group_id && mySplit && friendSplit) return true;
          return false;
        }

        if (tx.category_name === 'Debt Settlement' || (tx.description && tx.description.toLowerCase().includes('settle'))) {
          return (tx.paid_by_user_id === myUserId && tx.user_id === friendId) || (tx.paid_by_user_id === friendId && tx.user_id === myUserId);
        }

        if (tx.group_id) return false;
        return (userIsPayer && tx.user_id === friendId) || (friendIsPayer && tx.user_id === myUserId);
      });
      title = `Friend: ${exportTargetData.nickname || exportTargetData.display_name || exportTargetData.email}`;
    }

    let filteredTxs = [...entityTxs].sort((a, b) => new Date(b.date) - new Date(a.date));

    let activeFiltersText = `Scope: ${title}`;
    if (exportPeriod === 'this_month') {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      filteredTxs = filteredTxs.filter(tx => new Date(tx.date) >= startOfMonth);
      activeFiltersText += ` | Period: This Month (since ${formatDateDDMMYYYY(startOfMonth)})`;
    } else if (exportPeriod === 'last_30') {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 30);
      filteredTxs = filteredTxs.filter(tx => new Date(tx.date) >= cutoff);
      activeFiltersText += ` | Period: Last 30 Days (since ${formatDateDDMMYYYY(cutoff)})`;
    } else if (exportPeriod === 'custom') {
      if (!exportStartDate || !exportEndDate) {
        return showToast('Please select both start and end dates', 'error');
      }
      const start = new Date(exportStartDate);
      const end = new Date(exportEndDate);
      end.setHours(23, 59, 59, 999);
      filteredTxs = filteredTxs.filter(tx => {
        const d = new Date(tx.date);
        return d >= start && d <= end;
      });
      activeFiltersText += ` | Period: Custom (${formatDateDDMMYYYY(exportStartDate)} - ${formatDateDDMMYYYY(exportEndDate)})`;
    } else {
      activeFiltersText += ` | Period: All Time`;
    }

    let limitNum = null;
    if (exportLimitType === '10' || exportLimitType === '20' || exportLimitType === '50') {
      limitNum = parseInt(exportLimitType);
      filteredTxs = filteredTxs.slice(0, limitNum);
      activeFiltersText += ` | Limit: Last ${limitNum} records`;
    } else if (exportLimitType === 'custom') {
      const maxVal = parseInt(exportLimitValue);
      if (isNaN(maxVal) || maxVal <= 0) {
        return showToast('Please specify a valid limit number', 'error');
      }
      limitNum = maxVal;
      filteredTxs = filteredTxs.slice(0, limitNum);
      activeFiltersText += ` | Limit: Last ${limitNum} records`;
    } else {
      activeFiltersText += ` | Limit: All records`;
    }

    if (filteredTxs.length === 0) {
      return showToast('No transactions match the selected criteria', 'error');
    }

    setExportModalOpen(false);

    const INFLOWS = ['Salary', 'Cashback', 'Income from investments', 'Other income', 'Credit Card Rewards', 'Investment Income', 'External Borrowers', 'Deposits', 'Crypto Profit'];

    if (exportFormat === 'excel') {
      const isFriendExport = exportTargetType === 'friend';
      const isAccountOrCard = exportTargetType === 'account' || exportTargetType === 'credit_card';

      const friendRunningBals = isFriendExport && exportTargetData ? computeFriendRunningBalances(exportTargetData, transactions, myUserId) : {};
      const accRunningBals = isAccountOrCard && exportTargetData ? computeAccountRunningBalances(exportTargetData, transactions) : {};

      const headers = isFriendExport
        ? ['Date', 'Description', 'Category', 'Paid By', 'Total Amount', 'Net Friend Impact', 'Running Balance', 'Currency', 'Flow Type', 'Details']
        : isAccountOrCard
        ? ['Date', 'Description', 'Category', 'Paid By / Account', 'Total Amount', 'Account Impact', 'Running Balance', 'Currency', 'Flow Type', 'Details']
        : ['Date', 'Description', 'Category', 'Paid By / Account', 'Total Amount', 'Your Share', 'Currency', 'Flow Type', 'Details'];

      const rows = filteredTxs.map(tx => {
        const isTransfer = isTransferTransaction(tx);
        const dateStr = formatDateTime(tx.date);
        const desc = tx.description;
        const cat = isTransfer ? 'Transfer' : tx.category_name;
        const isOut = !INFLOWS.includes(tx.category_name) && !inflowCats.some(c => c.name === tx.category_name);
        const acc = tx.paid_by_account_id === 'VIRTUAL_DEBT' ? "Friend's Virtual Balance" : (accounts.find(a => a.account_id === tx.paid_by_account_id)?.name || getPayerName(tx) || 'Unknown');
        const { amount: displayAmt } = getDisplayAmount(tx);
        const curr = tx.currency;

        if (isFriendExport) {
          let flowType = 'Shared Split';
          let sign = '';
          let impactAmt = 0;
          let details = '';

          if (tx.category_name === 'Debt Settlement' || (tx.description && tx.description.toLowerCase().includes('settle'))) {
            if (tx.paid_by_user_id === myUserId) {
              flowType = 'Settlement (You Paid)';
              sign = '+';
              impactAmt = parseFloat(tx.total_amount || 0);
              details = 'You settled debt with friend';
            } else if (tx.paid_by_user_id === exportTargetData.user_id) {
              flowType = 'Settlement (Friend Paid)';
              sign = '-';
              impactAmt = parseFloat(tx.total_amount || 0);
              details = 'Friend settled debt with you';
            } else {
              flowType = 'Settlement';
              sign = '+';
              impactAmt = parseFloat(tx.total_amount || 0);
              details = 'Debt settlement';
            }
          } else {
            const payers = tx.splits?.filter(s => parseFloat(s.paid_amount) > 0) || [];
            const myPaid = payers.find(p => p.member_user_id === myUserId);
            const friendPaid = payers.find(p => p.member_user_id === exportTargetData.user_id);
            const mySplit = tx.splits?.find(s => s.member_user_id === myUserId);
            const friendSplit = tx.splits?.find(s => s.member_user_id === exportTargetData.user_id);

            let delta = 0;
            if (myPaid && parseFloat(myPaid.paid_amount) > 0 && friendSplit) {
              const totalPaid = payers.reduce((sum, p) => sum + parseFloat(p.paid_amount), 0) || parseFloat(tx.total_amount);
              const mySharePaidFraction = parseFloat(myPaid.paid_amount) / (totalPaid || 1);
              delta += parseFloat(friendSplit.owed_amount || 0) * mySharePaidFraction;
            } else if (tx.paid_by_user_id === myUserId && friendSplit) {
              delta += parseFloat(friendSplit.owed_amount || 0);
            }

            if (friendPaid && parseFloat(friendPaid.paid_amount) > 0 && mySplit) {
              const totalPaid = payers.reduce((sum, p) => sum + parseFloat(p.paid_amount), 0) || parseFloat(tx.total_amount);
              const friendSharePaidFraction = parseFloat(friendPaid.paid_amount) / (totalPaid || 1);
              delta -= parseFloat(mySplit.owed_amount || 0) * friendSharePaidFraction;
            } else if (tx.paid_by_user_id === exportTargetData.user_id && mySplit) {
              delta -= parseFloat(mySplit.owed_amount || 0);
            }

            if (delta > 0) {
              flowType = 'Friend Owes You';
              sign = '+';
              impactAmt = delta;
            } else if (delta < 0) {
              flowType = 'You Owe Friend';
              sign = '-';
              impactAmt = Math.abs(delta);
            } else {
              flowType = 'Settled / No Impact';
              sign = '';
              impactAmt = 0;
            }
            details = `Shared Split (Payer: ${getPayerName(tx)})`;
          }

          const runObj = friendRunningBals[tx.transaction_id];
          const runBal = runObj ? runObj.balance : null;
          const runBalStr = runBal !== null && runBal !== undefined ? (runBal > 0 ? `+${runBal.toFixed(2)}` : (runBal < 0 ? `-${Math.abs(runBal).toFixed(2)}` : '0.00')) : '-';
          const impactStr = impactAmt === 0 ? '0.00' : `${sign}${impactAmt.toFixed(2)}`;

          return [
            escapeCsv(dateStr),
            escapeCsv(desc),
            escapeCsv(cat),
            escapeCsv(acc),
            parseFloat(tx.total_amount || 0).toFixed(2),
            impactStr,
            runBalStr,
            escapeCsv(curr),
            escapeCsv(flowType),
            escapeCsv(details)
          ].join(',');
        }

        // Account, Credit Card, Group, or Generic entity
        let flowType = 'Expense';
        let sign = '-';
        let details = '';

        if (isAccountOrCard) {
          if (isTransfer) {
            if (tx.paid_by_account_id === exportTargetData.account_id) {
              flowType = 'Transfer Out';
              sign = '-';
              const destAcc = accounts.find(a => a.account_id === tx.transfer_to_account_id)?.name || 'Unknown';
              details = `Transfer to ${destAcc}`;
            } else if (tx.transfer_to_account_id === exportTargetData.account_id) {
              flowType = exportTargetType === 'credit_card' ? 'Card Payment / Inflow' : 'Transfer In';
              sign = '+';
              const srcAcc = accounts.find(a => a.account_id === tx.paid_by_account_id)?.name || 'Unknown';
              details = `Transfer from ${srcAcc}`;
            } else {
              flowType = 'Transfer';
              sign = '-';
            }
          } else if (!isOut) {
            flowType = exportTargetType === 'credit_card' ? 'Cashback / Refund' : 'Income';
            sign = '+';
          } else {
            flowType = 'Expense';
            sign = '-';
          }
        } else {
          // Group or other entity
          if (isTransfer) {
            flowType = 'Transfer';
            sign = '-';
            const destAcc = accounts.find(a => a.account_id === tx.transfer_to_account_id)?.name || 'Unknown';
            details = `Transfer to ${destAcc}`;
          } else if (!isOut) {
            flowType = 'Income';
            sign = '+';
          } else {
            flowType = 'Expense';
            sign = '-';
          }
        }

        if (!details) {
          if (tx.is_shared) {
            details = `Shared Split (Payer: ${getPayerName(tx)})`;
          } else if (tx.is_recurring) {
            details = 'Recurring';
          }
        }

        let exportAmount = parseFloat(tx.total_amount || 0);
        let exportCurrency = curr;

        if (isAccountOrCard && isTransfer && exportTargetData) {
          const tfD = getTransferDetails(tx, exportTargetData);
          if (tfD) {
            exportAmount = tfD.accountDisplayAmount;
            exportCurrency = tfD.accountDisplayCurrency;
          }
        }

        const totalAmtStr = `${sign}${exportAmount.toFixed(2)}`;
        const shareAmtStr = `${sign}${displayAmt.toFixed(2)}`;

        if (isAccountOrCard) {
          const accRunBal = accRunningBals[tx.transaction_id];
          const accRunBalStr = accRunBal !== undefined && accRunBal !== null ? (accRunBal < 0 ? `-${Math.abs(accRunBal).toFixed(2)}` : `+${accRunBal.toFixed(2)}`) : '-';
          return [
            escapeCsv(dateStr),
            escapeCsv(desc),
            escapeCsv(cat),
            escapeCsv(acc),
            totalAmtStr,
            shareAmtStr,
            escapeCsv(accRunBalStr),
            escapeCsv(exportCurrency),
            escapeCsv(flowType),
            escapeCsv(details)
          ].join(',');
        }

        return [
          escapeCsv(dateStr),
          escapeCsv(desc),
          escapeCsv(cat),
          escapeCsv(acc),
          totalAmtStr,
          shareAmtStr,
          escapeCsv(exportCurrency),
          escapeCsv(flowType),
          escapeCsv(details)
        ].join(',');
      });

      const csvContent = [`# ${activeFiltersText} | Generated by: ${userName} | Exported: ${dateStr}`, headers.join(','), ...rows].join('\n');
      downloadCsv(csvContent, `tezer_export_${exportTargetType}_${Date.now()}.csv`);
      return showToast('Transaction Excel/CSV export completed!', 'success');
    } else {
      // PDF Transaction Report
      const isFriendExport = exportTargetType === 'friend';
      const isAccountOrCard = exportTargetType === 'account' || exportTargetType === 'credit_card';

      const friendRunningBals = isFriendExport && exportTargetData ? computeFriendRunningBalances(exportTargetData, transactions, myUserId) : {};
      const accRunningBals = isAccountOrCard && exportTargetData ? computeAccountRunningBalances(exportTargetData, transactions) : {};

      const tableRowsHtml = filteredTxs.map((tx, idx) => {
        const isTransfer = isTransferTransaction(tx);
        const dateVal = formatDateTime(tx.date);
        const descVal = tx.description;
        const catVal = isTransfer ? '🔄 Transfer' : tx.category_name;
        const accVal = tx.paid_by_account_id === 'VIRTUAL_DEBT' ? "Friend's Virtual Balance" : (accounts.find(a => a.account_id === tx.paid_by_account_id)?.name || getPayerName(tx) || 'Unknown');
        const { amount: displayAmt } = getDisplayAmount(tx);
        const isOut = !INFLOWS.includes(tx.category_name) && !inflowCats.some(c => c.name === tx.category_name);

        let flowType = 'Expense';
        let flowColor = '#ff6b6b';
        let amtSign = '-';
        let finalAmt = displayAmt;

        if (isFriendExport) {
          if (tx.category_name === 'Debt Settlement' || (tx.description && tx.description.toLowerCase().includes('settle'))) {
            if (tx.paid_by_user_id === myUserId) {
              flowType = 'Settlement (You Paid)';
              amtSign = '+';
              flowColor = '#2bcbba';
              finalAmt = parseFloat(tx.total_amount || 0);
            } else {
              flowType = 'Settlement (Friend Paid)';
              amtSign = '-';
              flowColor = '#ff6b6b';
              finalAmt = parseFloat(tx.total_amount || 0);
            }
          } else {
            const payers = tx.splits?.filter(s => parseFloat(s.paid_amount) > 0) || [];
            const myPaid = payers.find(p => p.member_user_id === myUserId);
            const friendPaid = payers.find(p => p.member_user_id === exportTargetData.user_id);
            const mySplit = tx.splits?.find(s => s.member_user_id === myUserId);
            const friendSplit = tx.splits?.find(s => s.member_user_id === exportTargetData.user_id);

            let delta = 0;
            if (myPaid && parseFloat(myPaid.paid_amount) > 0 && friendSplit) {
              const totalPaid = payers.reduce((sum, p) => sum + parseFloat(p.paid_amount), 0) || parseFloat(tx.total_amount);
              const mySharePaidFraction = parseFloat(myPaid.paid_amount) / (totalPaid || 1);
              delta += parseFloat(friendSplit.owed_amount || 0) * mySharePaidFraction;
            } else if (tx.paid_by_user_id === myUserId && friendSplit) {
              delta += parseFloat(friendSplit.owed_amount || 0);
            }

            if (friendPaid && parseFloat(friendPaid.paid_amount) > 0 && mySplit) {
              const totalPaid = payers.reduce((sum, p) => sum + parseFloat(p.paid_amount), 0) || parseFloat(tx.total_amount);
              const friendSharePaidFraction = parseFloat(friendPaid.paid_amount) / (totalPaid || 1);
              delta -= parseFloat(mySplit.owed_amount || 0) * friendSharePaidFraction;
            } else if (tx.paid_by_user_id === exportTargetData.user_id && mySplit) {
              delta -= parseFloat(mySplit.owed_amount || 0);
            }

            if (delta > 0) {
              flowType = 'Friend Owes You';
              amtSign = '+';
              flowColor = '#2bcbba';
              finalAmt = delta;
            } else if (delta < 0) {
              flowType = 'You Owe Friend';
              amtSign = '-';
              flowColor = '#ff6b6b';
              finalAmt = Math.abs(delta);
            } else {
              flowType = 'Settled';
              amtSign = '';
              flowColor = '#94a3b8';
              finalAmt = 0;
            }
          }
        } else if (isAccountOrCard) {
          if (isTransfer) {
            if (tx.paid_by_account_id === exportTargetData.account_id) {
              flowType = 'Transfer Out';
              amtSign = '-';
              flowColor = '#ff6b6b';
            } else if (tx.transfer_to_account_id === exportTargetData.account_id) {
              flowType = exportTargetType === 'credit_card' ? 'Card Payment' : 'Transfer In';
              amtSign = '+';
              flowColor = '#2bcbba';
            } else {
              flowType = 'Transfer';
              amtSign = '-';
              flowColor = '#00f5d4';
            }
          } else if (!isOut) {
            flowType = exportTargetType === 'credit_card' ? 'Cashback / Refund' : 'Income';
            amtSign = '+';
            flowColor = '#2bcbba';
          } else {
            flowType = 'Expense';
            amtSign = '-';
            flowColor = '#ff6b6b';
          }
        } else {
          if (isTransfer) {
            flowType = 'Transfer';
            flowColor = '#00f5d4';
            amtSign = '-';
          } else if (!isOut) {
            flowType = 'Income';
            flowColor = '#2bcbba';
            amtSign = '+';
          } else {
            flowType = 'Expense';
            flowColor = '#ff6b6b';
            amtSign = '-';
          }
        }

        let runBalHtml = '-';
        if (isFriendExport) {
          const runObj = friendRunningBals[tx.transaction_id];
          const runBal = runObj ? runObj.balance : null;
          if (runBal !== null && runBal !== undefined) {
            const color = runBal > 0 ? '#2bcbba' : (runBal < 0 ? '#ff6b6b' : '#94a3b8');
            const sign = runBal > 0 ? '+' : (runBal < 0 ? '-' : '');
            runBalHtml = `<span style="color: ${color}; font-weight: 700;">${sign}${formatMoney(Math.abs(runBal), runObj.currency)}</span>`;
          }
        } else if (isAccountOrCard) {
          const runBal = accRunningBals[tx.transaction_id];
          if (runBal !== undefined && runBal !== null) {
            const color = runBal >= 0 ? '#2bcbba' : '#ff6b6b';
            const sign = runBal >= 0 ? '+' : '-';
            runBalHtml = `<span style="color: ${color}; font-weight: 700;">${sign}${formatMoney(Math.abs(runBal), exportTargetData.currency)}</span>`;
          }
        }

        return `
          <tr style="background-color: ${idx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'};">
            <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px;">${escapeHtml(dateVal)}</td>
            <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px; font-weight: 500;">${escapeHtml(descVal)}</td>
            <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px; color: #a55eea;">${escapeHtml(catVal)}</td>
            <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px; color: #54a0ff;">${escapeHtml(accVal)}</td>
            <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px;">${flowType}</td>
            <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px; font-weight: 700; text-align: right; color: ${flowColor};">
              ${amtSign}${formatMoney(finalAmt, tx.currency)}
            </td>
            <td style="padding: 10px; border-bottom: 1px solid rgba(255,255,255,0.08); font-size: 13px; text-align: right;">
              ${runBalHtml}
            </td>
          </tr>
        `;
      }).join('');

      let summaryCardsHtml = '';

      if (exportTargetType === 'credit_card') {
        const stmtInfo = getCardStatementInfo(exportTargetData, transactions);
        const cur = exportTargetData.currency || 'AED';
        const bal = parseFloat(exportTargetData.current_balance || 0);
        const limit = parseFloat(exportTargetData.credit_limit || 0);
        const avail = limit > 0 ? Math.max(0, limit - bal) : 0;
        const util = limit > 0 ? (bal / limit) * 100 : 0;
        const stmtDue = stmtInfo.hasStatement ? stmtInfo.remainingStatementDue : null;
        const dueDateStr = stmtInfo.dueDate ? formatDateDDMMYYYY(stmtInfo.dueDate) : 'N/A';
        const dueDaysStr = stmtInfo.dueDaysLeft !== null ? (stmtInfo.dueDaysLeft >= 0 ? `${stmtInfo.dueDaysLeft}d left` : `${Math.abs(stmtInfo.dueDaysLeft)}d overdue`) : '';

        summaryCardsHtml = `
          <div class="balance-card">
            <div class="balance-card-label">Total Due / Outstanding</div>
            <div class="balance-card-value" style="color: #ff6b6b;">${formatMoney(bal, cur)}</div>
            <div class="balance-card-sub">Current card balance</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Available Credit</div>
            <div class="balance-card-value" style="color: #2bcbba;">${formatMoney(avail, cur)}</div>
            <div class="balance-card-sub">Limit: ${formatMoney(limit, cur)}</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Last Statement Due</div>
            <div class="balance-card-value" style="color: ${stmtDue > 0 ? '#ff6b6b' : '#38bdf8'};">${stmtDue !== null ? formatMoney(stmtDue, cur) : 'N/A'}</div>
            <div class="balance-card-sub">${dueDateStr !== 'N/A' ? `Due: ${dueDateStr} ${dueDaysStr ? `(${dueDaysStr})` : ''}` : 'No statement cycle'}</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Credit Limit &amp; Utilization</div>
            <div class="balance-card-value" style="color: #a55eea;">${formatMoney(limit, cur)}</div>
            <div class="balance-card-sub">Utilization: ${Math.round(util)}%</div>
          </div>
        `;
      } else if (exportTargetType === 'account') {
        const cur = exportTargetData.currency || 'AED';
        const curBal = parseFloat(exportTargetData.current_balance || 0);

        let periodInflows = 0;
        let periodOutflows = 0;

        filteredTxs.forEach(tx => {
          const isTransfer = isTransferTransaction(tx);
          const isOut = !INFLOWS.includes(tx.category_name) && !inflowCats.some(c => c.name === tx.category_name);
          let amt = parseFloat(tx.total_amount || 0);

          if (isTransfer) {
            const tfD = getTransferDetails(tx, exportTargetData);
            if (tfD) amt = tfD.accountDisplayAmount;
            if (tx.transfer_to_account_id === exportTargetData.account_id) {
              periodInflows += amt;
            } else if (tx.paid_by_account_id === exportTargetData.account_id) {
              periodOutflows += amt;
            }
          } else if (!isOut) {
            periodInflows += amt;
          } else {
            periodOutflows += amt;
          }
        });

        const netChange = periodInflows - periodOutflows;
        const netColor = netChange >= 0 ? '#2bcbba' : '#ff6b6b';
        const netSign = netChange >= 0 ? '+' : '-';
        const balColor = curBal >= 0 ? '#2bcbba' : '#ff6b6b';

        summaryCardsHtml = `
          <div class="balance-card">
            <div class="balance-card-label">Current Balance</div>
            <div class="balance-card-value" style="color: ${balColor};">${formatMoney(curBal, cur)}</div>
            <div class="balance-card-sub">${exportTargetData.type || 'Account'} balance</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Period Inflows</div>
            <div class="balance-card-value" style="color: #2bcbba;">+${formatMoney(periodInflows, cur)}</div>
            <div class="balance-card-sub">Deposits &amp; transfers in</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Period Outflows</div>
            <div class="balance-card-value" style="color: #ff6b6b;">-${formatMoney(periodOutflows, cur)}</div>
            <div class="balance-card-sub">Expenses &amp; transfers out</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Net Period Change</div>
            <div class="balance-card-value" style="color: ${netColor};">${netSign}${formatMoney(Math.abs(netChange), cur)}</div>
            <div class="balance-card-sub">${filteredTxs.length} transactions in period</div>
          </div>
        `;
      } else if (exportTargetType === 'friend') {
        const friendName = exportTargetData.nickname || exportTargetData.display_name || exportTargetData.email.split('@')[0];
        const detailedMap = computeFriendDetailedBalances(exportTargetData);
        const cur = filteredTxs[0]?.currency || Object.keys(detailedMap)[0] || 'AED';
        const balInfo = detailedMap[cur] || { nonGroup: 0, groupBalances: {}, deletedGroupsBalance: 0, overall: 0 };

        const overall = balInfo.overall || 0;
        const directBal = balInfo.nonGroup || 0;
        let groupBal = 0;
        Object.values(balInfo.groupBalances || {}).forEach(gb => { groupBal += gb.amount; });
        groupBal += (balInfo.deletedGroupsBalance || 0);

        let directionalLabel = '👉 Settled Up';
        let directionalVal = `0.00 ${cur}`;
        let directionalColor = '#94a3b8';
        let directionalSub = `All balances clear in ${cur}`;

        if (overall > 0) {
          directionalLabel = `👉 ${escapeHtml(friendName)} owes ${escapeHtml(userName)}`;
          directionalVal = `+${formatMoney(overall, cur)}`;
          directionalColor = '#2bcbba';
          directionalSub = `You are owed ${formatMoney(overall, cur)}`;
        } else if (overall < 0) {
          directionalLabel = `👉 ${escapeHtml(userName)} owes ${escapeHtml(friendName)}`;
          directionalVal = `-${formatMoney(Math.abs(overall), cur)}`;
          directionalColor = '#ff6b6b';
          directionalSub = `You owe ${formatMoney(Math.abs(overall), cur)}`;
        }

        const directColor = directBal > 0 ? '#2bcbba' : (directBal < 0 ? '#ff6b6b' : '#94a3b8');
        const directSign = directBal > 0 ? '+' : (directBal < 0 ? '-' : '');
        const groupColor = groupBal > 0 ? '#2bcbba' : (groupBal < 0 ? '#ff6b6b' : '#94a3b8');
        const groupSign = groupBal > 0 ? '+' : (groupBal < 0 ? '-' : '');

        summaryCardsHtml = `
          <div class="balance-card">
            <div class="balance-card-label">${directionalLabel}</div>
            <div class="balance-card-value" style="color: ${directionalColor};">${directionalVal}</div>
            <div class="balance-card-sub">${directionalSub}</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Direct (Non-Group) Balance</div>
            <div class="balance-card-value" style="color: ${directColor};">${directSign}${formatMoney(Math.abs(directBal), cur)}</div>
            <div class="balance-card-sub">Direct splits &amp; settlements</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Shared Groups Debt</div>
            <div class="balance-card-value" style="color: ${groupColor};">${groupSign}${formatMoney(Math.abs(groupBal), cur)}</div>
            <div class="balance-card-sub">Position from group splits</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Statement Records</div>
            <div class="balance-card-value" style="color: #a55eea;">${filteredTxs.length}</div>
            <div class="balance-card-sub">Shared &amp; settlement entries</div>
          </div>
        `;
      } else if (exportTargetType === 'group') {
        const groupCur = filteredTxs[0]?.currency || 'AED';
        let totalGroupSpend = 0;
        let userPaid = 0;
        let userShare = 0;

        filteredTxs.forEach(tx => {
          const amt = parseFloat(tx.total_amount || 0);
          totalGroupSpend += amt;
          if (tx.splits && tx.splits.length > 0) {
            const mySplit = tx.splits.find(s => s.member_user_id === myUserId);
            if (mySplit) {
              userShare += parseFloat(mySplit.owed_amount || 0);
              userPaid += parseFloat(mySplit.paid_amount || 0);
            }
          } else {
            if (tx.paid_by_user_id === myUserId || (!tx.paid_by_user_id && tx.user_id === myUserId)) {
              userPaid += amt;
            }
          }
        });

        const netGroupPosition = userPaid - userShare;
        let netLabel = '👉 Group Settled';
        let netVal = `0.00 ${groupCur}`;
        let netColor = '#94a3b8';
        let netSub = 'Your group balance is zero';

        if (netGroupPosition > 0) {
          netLabel = `👉 Group owes ${escapeHtml(userName)}`;
          netVal = `+${formatMoney(netGroupPosition, groupCur)}`;
          netColor = '#2bcbba';
          netSub = `You are owed by group members`;
        } else if (netGroupPosition < 0) {
          netLabel = `👉 ${escapeHtml(userName)} owes Group`;
          netVal = `-${formatMoney(Math.abs(netGroupPosition), groupCur)}`;
          netColor = '#ff6b6b';
          netSub = `You owe group members`;
        }

        summaryCardsHtml = `
          <div class="balance-card">
            <div class="balance-card-label">Total Group Spending</div>
            <div class="balance-card-value" style="color: #00f5d4;">${formatMoney(totalGroupSpend, groupCur)}</div>
            <div class="balance-card-sub">Across ${filteredTxs.length} expenses</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">${netLabel}</div>
            <div class="balance-card-value" style="color: ${netColor};">${netVal}</div>
            <div class="balance-card-sub">${netSub}</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Total Paid by You</div>
            <div class="balance-card-value" style="color: #38bdf8;">${formatMoney(userPaid, groupCur)}</div>
            <div class="balance-card-sub">Your upfront payments</div>
          </div>
          <div class="balance-card">
            <div class="balance-card-label">Your Share of Spend</div>
            <div class="balance-card-value" style="color: #a55eea;">${formatMoney(userShare, groupCur)}</div>
            <div class="balance-card-sub">Your portion of expenses</div>
          </div>
        `;
      }

      generatePdfReport(`Transactions Report - ${title}`, activeFiltersText, [
        { name: 'Date', width: '11%' },
        { name: 'Description', width: '29%' },
        { name: 'Category', width: '13%' },
        { name: 'Account / Payer', width: '14%' },
        { name: 'Type', width: '10%' },
        { name: 'Amount', width: '11%', align: 'right' },
        { name: 'Running Balance', width: '12%', align: 'right' }
      ], tableRowsHtml, filteredTxs.length, summaryCardsHtml);
    }
  };


  // Backup file ZIP restore upload
  const handleRestoreBackup = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (evt) => {
      const arrayBuffer = evt.target.result;
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      for (let i = 0; i < bytes.byteLength; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      const base64 = btoa(binary);
      try {
        await apiClient.request('/backup/restore', 'POST', { zipData: base64 });
        showToast('Backup restored successfully!', 'success');
        refreshData();
      } catch (err) {
        showToast(err.message, 'error');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleClearData = async () => {
    if (!window.confirm('WARNING: Wiping data will delete ALL accounts, transactions, and group splits. Proceed?')) return;
    try {
      await apiClient.request('/settings/clear', 'POST');
      localStorage.removeItem('dhudd_cache_accounts');
      localStorage.removeItem('dhudd_cache_transactions');
      localStorage.removeItem('dhudd_cache_groups');
      localStorage.removeItem('dhudd_sync_queue');
      showToast('All personal transaction data cleared!', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm("WARNING: Deleting your account will transition you to a placeholder friend. All your personal accounts, private transactions, and settings will be permanently wiped. Your shared splits and friendships will be kept to avoid breaking your friends' ledgers. This action CANNOT be undone. Proceed?")) return;
    try {
      await apiClient.request('/settings/delete-account', 'POST');
      showToast('Your account has been deleted successfully.', 'success');
      handleLogout();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  const handleSetDefaultAccount = async (accountId) => {
    try {
      await apiClient.request(`/accounts/${accountId}/default`, 'PUT');
      showToast('Default account updated', 'success');
      refreshData();
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  // SVG Donut Chart calculations (Clean & Modern Design)
  const renderPieChart = (type) => {
    const isOutflow = type === 'outflow';
    const data = isOutflow ? getCategorySpendData() : getCategoryIncomeData();
    const list = data.list;
    const total = isOutflow ? data.totalSpend : data.totalIncome;

    if (total === 0) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', minHeight: '220px', color: 'var(--text-muted)' }}>
          <svg viewBox="0 0 120 120" style={{ width: '80px', height: '80px', marginBottom: '12px' }}>
            <circle cx="60" cy="60" r="46" fill="transparent" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
          </svg>
          <span style={{ fontSize: '13px' }}>No {isOutflow ? 'expenses' : 'incomes'} recorded for this period</span>
        </div>
      );
    }

    const activeCatName = isOutflow ? activeExpenseChartCategory : activeIncomeChartCategory;
    const setActiveCat = isOutflow ? setActiveExpenseChartCategory : setActiveIncomeChartCategory;

    const currentActive = list.find(c => (c.id || c.name) === activeCatName || c.name === activeCatName) || list[0];
    const r = 78;
    const cx = 130;
    const cy = 130;
    const strokeWidth = 20;
    const circumference = 2 * Math.PI * r;
    let accumulatedPct = 0;

    return (
      <div style={{ position: 'relative', width: '260px', height: '260px', margin: '0 auto' }}>
        <svg viewBox="0 0 260 260" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
          {/* Donut Background Track */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="transparent"
            stroke="rgba(255, 255, 255, 0.05)"
            strokeWidth={strokeWidth}
          />

          {/* Donut Segments */}
          {list.map((cat, idx) => {
            const itemVal = cat.convertedAmount !== undefined ? cat.convertedAmount : cat.amount;
            const pct = total > 0 ? (itemVal / total) : 0;
            const capPadding = list.length > 1 ? strokeWidth * 0.6 : 0;
            const strokeLength = Math.max(0, (circumference * pct) - capPadding);
            const strokeOffset = -accumulatedPct * circumference;
            accumulatedPct += pct;

            const isActive = (currentActive?.id || currentActive?.name) === (cat.id || cat.name);

            return (
              <circle
                key={cat.id || idx}
                cx={cx}
                cy={cy}
                r={r}
                fill="transparent"
                stroke={cat.color}
                strokeWidth={isActive ? strokeWidth + 6 : strokeWidth}
                strokeDasharray={`${strokeLength} ${circumference}`}
                strokeDashoffset={strokeOffset}
                strokeLinecap="round"
                transform={`rotate(-90 ${cx} ${cy})`}
                style={{
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  filter: isActive ? `drop-shadow(0 0 8px ${cat.color}99)` : 'none',
                  opacity: (currentActive && !isActive) ? 0.6 : 1
                }}
                onMouseEnter={() => setActiveCat(cat.id || cat.name)}
                onClick={() => setActiveCat(cat.id || cat.name)}
              />
            );
          })}
        </svg>

        {/* Center Donut Card Summary */}
        {currentActive && (() => {
          const itemVal = currentActive.convertedAmount !== undefined ? currentActive.convertedAmount : currentActive.amount;
          const activePct = total > 0 ? Math.round((itemVal / total) * 100) : 0;
          const activeCurrency = currentActive.currency || (breakdownCurrency === 'ALL' ? (preferredCurrencies[0] || 'AED') : breakdownCurrency);

          return (
            <div
              onClick={() => handleOpenDrillDown(currentActive, type)}
              title="Click to view category transactions"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '122px',
                height: '122px',
                borderRadius: '50%',
                background: 'rgba(13, 17, 23, 0.94)',
                backdropFilter: 'blur(16px)',
                border: `2px solid ${currentActive.color}`,
                boxShadow: `0 6px 20px ${currentActive.color}33`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '8px',
                boxSizing: 'border-box',
                zIndex: 10,
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              <div
                style={{
                  background: `${currentActive.color}20`,
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  marginBottom: '3px',
                  border: `1px solid ${currentActive.color}44`
                }}
              >
                {currentActive.icon}
              </div>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '700',
                  color: 'var(--text-main)',
                  maxWidth: '95px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  lineHeight: '1.2'
                }}
              >
                {currentActive.displayName || currentActive.name}
              </span>
              <span
                style={{
                  fontSize: '11px',
                  fontWeight: '800',
                  color: currentActive.color,
                  marginTop: '1px'
                }}
              >
                {formatMoney(Math.abs(currentActive.amount || 0), activeCurrency)}
              </span>
              <span
                style={{
                  fontSize: '9px',
                  fontWeight: '700',
                  color: 'var(--text-muted)',
                  marginTop: '2px',
                  background: 'rgba(255,255,255,0.06)',
                  padding: '1px 6px',
                  borderRadius: '10px'
                }}
              >
                {activePct}%
              </span>
            </div>
          );
        })()}
      </div>
    );
  };

  const displayNotifications = (() => {
    const ccReminders = [];
    if (cardRemindersEnabled && accounts.length > 0) {
      const ccAccounts = accounts.filter(a => a.type === 'CREDIT_CARD');
      const dismissedList = JSON.parse(localStorage.getItem('dhudd_dismissed_cc_reminders') || '[]');
      
      ccAccounts.forEach(card => {
        const stmtInfo = getCardStatementInfo(card, transactions);
        const dueAmt = stmtInfo.remainingStatementDue;
        
        if (dueAmt > 0 && stmtInfo.dueDaysLeft !== null) {
          if (stmtInfo.dueDaysLeft >= 0 && stmtInfo.dueDaysLeft <= 5) {
            const reminderKey = `cc-${card.account_id}-${card.due_day}-${stmtInfo.lastStatementDate?.toISOString()?.slice(0, 10)}-${dueAmt.toFixed(2)}`;
            if (!dismissedList.includes(reminderKey)) {
              let dueMsg = '';
              if (stmtInfo.dueDaysLeft === 0) {
                dueMsg = `Urgent: Credit card ${card.name} statement balance of ${formatMoney(dueAmt, card.currency)} is due TODAY!`;
              } else if (stmtInfo.dueDaysLeft === 1) {
                dueMsg = `Reminder: Credit card ${card.name} statement balance of ${formatMoney(dueAmt, card.currency)} is due TOMORROW (Monthly ${card.due_day})!`;
              } else {
                dueMsg = `Reminder: Credit card ${card.name} statement balance of ${formatMoney(dueAmt, card.currency)} is due in ${stmtInfo.dueDaysLeft} days (Monthly ${card.due_day})!`;
              }

              ccReminders.push({
                notification_id: reminderKey,
                message: dueMsg,
                created_at: new Date().toISOString(),
                read: false,
                is_cc_reminder: true
              });
            }
          }
        }
      });
    }
    
    // Filter out notifications that reference transactions that no longer exist
    const txIdSet = new Set(transactions.map(t => t.transaction_id));
    const validNotifications = notifications.filter(n => {
      if (n.transaction_id && !txIdSet.has(n.transaction_id)) {
        return false;
      }
      if (n.message && n.message.includes('#/transactions?edit=')) {
        const match = n.message.match(/#\/transactions\?edit=([a-zA-Z0-9_-]+)/);
        if (match && match[1] && !txIdSet.has(match[1])) {
          return false;
        }
      }
      return true;
    });

    return [
      ...validNotifications,
      ...ccReminders
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  })();

  // Automatically dispatch native Push Notifications whenever any new notification comes into displayNotifications
  useEffect(() => {
    if (!isAuthenticated || displayNotifications.length === 0) return;

    try {
      const notifiedSet = new Set(JSON.parse(localStorage.getItem('dhudd_push_notified_ids') || '[]'));
      let hasNewNotifs = false;

      displayNotifications.forEach(n => {
        const notifId = String(n.notification_id);
        if (!n.read && !notifiedSet.has(notifId)) {
          notifiedSet.add(notifId);
          hasNewNotifs = true;

          triggerPushNotification(
            n.is_cc_reminder ? '💳 Credit Card Reminder' : '🔔 tezer Notification',
            n.message || 'You have a new notification in tezer.'
          );
        }
      });

      if (hasNewNotifs) {
        localStorage.setItem('dhudd_push_notified_ids', JSON.stringify(Array.from(notifiedSet)));
      }
    } catch (err) {
      console.error('Error auto-dispatching push notifications:', err);
    }
  }, [displayNotifications, isAuthenticated]);

  // Local Daily Expense Reminder Interval Check (fires when app/tab is active)
  useEffect(() => {
    if (!isAuthenticated || !dailyReminderEnabled) return;

    const checkReminder = () => {
      try {
        const now = new Date();
        const currentHour = String(now.getHours()).padStart(2, '0');
        const currentMin = String(now.getMinutes()).padStart(2, '0');
        const currentTime = `${currentHour}:${currentMin}`;
        const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

        if (currentTime === dailyReminderTime) {
          const lastSentDate = localStorage.getItem('dhudd_last_daily_reminder_date');
          if (lastSentDate !== todayStr) {
            localStorage.setItem('dhudd_last_daily_reminder_date', todayStr);
            const quote = FINANCIAL_REMINDER_QUOTES[Math.floor(Math.random() * FINANCIAL_REMINDER_QUOTES.length)];
            triggerPushNotification(
              'Time to record your expenses! 📝',
              `"${quote.text}" — ${quote.author}`
            );
          }
        }
      } catch (e) {
        console.warn('Daily reminder interval check error:', e);
      }
    };

    checkReminder();
    const interval = setInterval(checkReminder, 30 * 1000);
    return () => clearInterval(interval);
  }, [isAuthenticated, dailyReminderEnabled, dailyReminderTime]);

  // --- LOGIN / REGISTER TEMPLATES ---
  if (!isAuthenticated) {
    return (
      <div className="animate-fade-in auth-screen-wrapper" style={{
        display: 'flex', flexDirection: 'column', minHeight: '100dvh',
        justifyContent: 'center', alignItems: 'center', padding: '24px', backgroundColor: 'var(--bg-primary, #0a0b10)',
        boxSizing: 'border-box'
      }}>
        <div className="auth-card-container" style={{ width: '100%', maxWidth: '440px', margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img src="/logo.png" alt="tezer" className="logo-reveal-img" style={{ width: '84px', height: '84px' }} />
            <h1 className="logo-reveal-line1" style={{ margin: '12px 0 0 0', fontSize: '36px' }}>
              <span style={{ color: 'var(--accent-cyan)' }}>t</span>ezer
            </h1>
            <p className="logo-reveal-line2" style={{ margin: '4px 0 0 0' }}>money matters!</p>
          </div>

          <div className="glass-panel" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {showRegistrationSuccess ? (
            <SuccessCheckmark 
              message="Account Created! Please Log In." 
              onClose={() => { 
                setShowRegistrationSuccess(false); 
                setIsRegisterMode(false); 
                setAuthPassword(''); // Clear password for security
              }} 
            />
          ) : isForgotPasswordMode ? (
            <form onSubmit={forgotPwStep === 'email' ? handleSendForgotPwOtp : handleResetPasswordSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <h2 style={{ marginBottom: '4px', textAlign: 'center' }}>
                Reset Password
              </h2>

              {forgotPwStep === 'email' ? (
                <>
                  <p style={{ fontSize: '13px', color: 'var(--text-muted)', textAlign: 'center', margin: '0 0 4px 0' }}>
                    Enter your email address to receive a verification code.
                  </p>

                  <div>
                    <label>Email Address</label>
                    <input
                      type="email"
                      placeholder="name@email.com"
                      value={forgotPwEmail}
                      onChange={e => setForgotPwEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
                    <Turnstile 
                      key="forgot_pw_init"
                      siteKey="0x4AAAAAAEBFr3-Dsk6xeSLZ" 
                      onSuccess={(token) => setForgotPwTurnstileToken(token)}
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="btn-primary" 
                    disabled={isSendingForgotPwOtp || !forgotPwEmail || !forgotPwTurnstileToken}
                    style={{ marginTop: '6px' }}
                  >
                    {isSendingForgotPwOtp ? <ThinkingOrbLoader popup state="searching" size={20} color="var(--accent-cyan)" text="Sending Code..." /> : 'Send Verification Code'}
                  </button>
                </>
              ) : (
                <>
                  <div style={{
                    padding: '12px 14px',
                    background: 'rgba(56, 189, 248, 0.1)',
                    border: '1px solid rgba(56, 189, 248, 0.3)',
                    borderRadius: '8px',
                    fontSize: '13px',
                    color: 'var(--accent-cyan)',
                    textAlign: 'center',
                    lineHeight: '1.4'
                  }}>
                    📬 You will receive an email with the code if the entered email is correct.
                  </div>

                  <div>
                    <label>6-Digit Verification Code *</label>
                    <input
                      type="text"
                      maxLength={6}
                      placeholder="123456"
                      value={forgotPwOtp}
                      onChange={e => setForgotPwOtp(e.target.value.replace(/\D/g, ''))}
                      style={{ letterSpacing: '4px', fontSize: '18px', textAlign: 'center', fontWeight: 'bold' }}
                      required
                    />
                  </div>

                  <div>
                    <label>New Password *</label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      value={forgotPwNewPassword}
                      onChange={e => setForgotPwNewPassword(e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <label>Confirm New Password *</label>
                    <input
                      type="password"
                      placeholder="••••••••"
                      value={forgotPwConfirmPassword}
                      onChange={e => setForgotPwConfirmPassword(e.target.value)}
                      required
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="btn-primary" 
                    disabled={isResettingPassword || !forgotPwOtp || !forgotPwNewPassword || !forgotPwConfirmPassword}
                    style={{ marginTop: '6px' }}
                  >
                    {isResettingPassword ? <ThinkingOrbLoader popup state="working" size={20} color="var(--accent-cyan)" text="Resetting Password..." /> : 'Reset Password'}
                  </button>

                  <div style={{ textAlign: 'center', fontSize: '13px', marginTop: '4px' }}>
                    {forgotPwCooldown > 0 ? (
                      <span style={{ color: 'var(--text-muted)' }}>Resend code in {forgotPwCooldown}s</span>
                    ) : (
                      <span 
                        onClick={handleSendForgotPwOtp} 
                        style={{ color: 'var(--accent-cyan)', cursor: 'pointer', textDecoration: 'underline' }}
                      >
                        Resend Code
                      </span>
                    )}
                  </div>
                </>
              )}

              <p
                onClick={() => {
                  setIsForgotPasswordMode(false);
                  setForgotPwStep('email');
                  setForgotPwTurnstileToken(null);
                }}
                style={{
                  color: 'var(--accent-cyan)', fontSize: '14px', textAlign: 'center',
                  cursor: 'pointer', marginTop: '8px', textDecoration: 'underline'
                }}
              >
                ← Back to Sign In
              </p>
            </form>
          ) : (
            <form onSubmit={handleAuthSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <h2 style={{ marginBottom: '4px', textAlign: 'center' }}>
                {isRegisterMode ? (otpSent ? 'Complete Account Registration' : 'Register Account') : 'Sign In'}
              </h2>

              {/* Login Mode or Step 1 of Signup: Ask Email */}
              {(!isRegisterMode || !otpSent) && (
                <div>
                  <label>Email Address</label>
                  <input
                    type="email"
                    placeholder="name@email.com"
                    value={authEmail}
                    onChange={e => setAuthEmail(e.target.value)}
                    required
                  />
                </div>
              )}

              {/* Login Mode: Ask Password */}
              {!isRegisterMode && (
                <div>
                  <label>Password</label>
                  <input
                    type="password"
                    placeholder="••••••••"
                    value={authPassword}
                    onChange={e => setAuthPassword(e.target.value)}
                    required
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                    <button
                      type="button"
                      onClick={() => {
                        setIsForgotPasswordMode(true);
                        setForgotPwEmail(authEmail || '');
                        setForgotPwStep('email');
                        setForgotPwOtp('');
                        setForgotPwNewPassword('');
                        setForgotPwConfirmPassword('');
                        setForgotPwTurnstileToken(null);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--accent-cyan)',
                        fontSize: '12px',
                        cursor: 'pointer',
                        padding: 0,
                        textDecoration: 'underline'
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>
              )}

              {/* Turnstile Anti-Bot Check for Login or Step 1 Signup */}
              {(!isRegisterMode || !otpSent) && (
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
                  <Turnstile 
                    key={isRegisterMode ? 'register_init' : 'login'}
                    siteKey="0x4AAAAAAEBFr3-Dsk6xeSLZ" 
                    onSuccess={(token) => setTurnstileToken(token)}
                  />
                </div>
              )}

              {isRegisterMode ? (
                <>
                  {!otpSent ? (
                    <button 
                      type="button" 
                      onClick={handleSendOtp} 
                      className="btn-primary" 
                      disabled={isSendingOtp || !authEmail || !turnstileToken}
                      style={{ marginTop: '6px' }}
                    >
                      {isSendingOtp ? <ThinkingOrbLoader popup state="searching" size={20} color="var(--accent-cyan)" text="Sending Code..." /> : 'Send Verification Code'}
                    </button>
                  ) : (
                    <>
                      <div style={{
                        padding: '10px 14px', background: 'rgba(56, 189, 248, 0.1)',
                        border: '1px solid rgba(56, 189, 248, 0.3)', borderRadius: '8px',
                        fontSize: '13px', color: 'var(--accent-cyan)', textAlign: 'center'
                      }}>
                        A 6-digit code has been sent to <strong>{authEmail}</strong>
                      </div>

                      <div>
                        <label>6-Digit Verification Code *</label>
                        <input
                          type="text"
                          maxLength={6}
                          placeholder="123456"
                          value={otpCode}
                          onChange={e => setOtpCode(e.target.value.replace(/\D/g, ''))}
                          style={{ letterSpacing: '4px', fontSize: '18px', textAlign: 'center', fontWeight: 'bold' }}
                          required
                        />
                      </div>

                      <div>
                        <label>Create Password *</label>
                        <input
                          type="password"
                          placeholder="••••••••"
                          value={authPassword}
                          onChange={e => setAuthPassword(e.target.value)}
                          required
                        />
                      </div>

                      <div>
                        <label>Full Name / Display Name</label>
                        <input
                          type="text"
                          placeholder="John Doe"
                          value={authName}
                          onChange={e => setAuthName(e.target.value)}
                        />
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label>Date of Birth</label>
                          <input
                            type="date"
                            max={getMaxDob7YearsAgo()}
                            value={authDob}
                            onChange={e => setAuthDob(e.target.value)}
                          />
                        </div>
                        <div>
                          <label>Gender</label>
                          <select 
                            value={authGender} 
                            onChange={e => setAuthGender(e.target.value)}
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border-glass)', color: 'var(--text-main)' }}
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                        <div>
                          <label>Country</label>
                          <input
                            type="text"
                            placeholder="United Arab Emirates"
                            value={authCountry}
                            onChange={e => setAuthCountry(e.target.value)}
                          />
                        </div>
                        <div>
                          <label>Phone Number (Optional)</label>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <select
                              value={authCountryCode}
                              onChange={e => setAuthCountryCode(e.target.value)}
                              style={{ padding: '10px', width: '105px', borderRadius: '8px', background: 'var(--bg-card)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '11px' }}
                            >
                              {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                            </select>
                            {authCountryCode === 'CUSTOM' && (
                              <input
                                type="text"
                                placeholder="+123"
                                value={authCustomCc}
                                onChange={e => setAuthCustomCc(e.target.value)}
                                style={{ padding: '10px', width: '55px', borderRadius: '8px' }}
                              />
                            )}
                            <input
                              type="tel"
                              placeholder="50 123 4567"
                              value={authPhoneDigits}
                              onChange={e => setAuthPhoneDigits(e.target.value)}
                              style={{ padding: '10px', flex: 1, borderRadius: '8px', minWidth: '105px' }}
                            />
                          </div>
                        </div>
                      </div>

                      <button type="submit" className="btn-primary" style={{ marginTop: '8px' }}>
                        Verify & Complete Registration
                      </button>

                      <div style={{ textAlign: 'center', fontSize: '13px', marginTop: '4px' }}>
                        {otpCountdown > 0 ? (
                          <span style={{ color: 'var(--text-muted)' }}>Resend code in {otpCountdown}s</span>
                        ) : (
                          <span 
                            onClick={handleSendOtp} 
                            style={{ color: 'var(--accent-cyan)', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            Resend Code
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <button type="submit" className="btn-primary" style={{ marginTop: '4px' }}>
                  Log In
                </button>
              )}

              <div style={{ display: 'flex', alignItems: 'center', margin: '6px 0', gap: '10px' }}>
                <div style={{ flex: 1, height: '1px', background: 'var(--border-glass)' }}></div>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'bold' }}>OR</span>
                <div style={{ flex: 1, height: '1px', background: 'var(--border-glass)' }}></div>
              </div>

              <div id="googleSignInBtn" style={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: '44px' }}></div>

              <p
                onClick={() => {
                  setIsRegisterMode(!isRegisterMode);
                  setIsForgotPasswordMode(false);
                  setOtpSent(false);
                  setOtpCode('');
                  setTurnstileToken(null);
                }}
                style={{
                  color: 'var(--accent-cyan)', fontSize: '14px', textAlign: 'center',
                  cursor: 'pointer', marginTop: '8px', textDecoration: 'underline'
                }}
              >
                {isRegisterMode ? 'Already have an account? Sign In' : 'Need an account? Register'}
              </p>

              {!isStandalone && (
                <div style={{ marginTop: '10px', paddingTop: '12px', borderTop: '1px solid var(--border-glass)' }}>
                  <button
                    type="button"
                    onClick={handleInstallPwa}
                    className="btn-secondary"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      background: 'rgba(0, 245, 212, 0.08)',
                      border: '1px solid var(--accent-cyan)',
                      color: 'var(--accent-cyan)',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    <Download size={16} /> Install the Tezer App
                  </button>
                </div>
              )}
            </form>
          )}
        </div>
        </div>
      </div>
    );
  }

  // --- PREPARE DATA ---
  const activeTxsFiltered = filterTransactions();
  const spendSummary = getCategorySpendData();
  const incomeSummary = getCategoryIncomeData();
  const activeBalance = getNetBalance();

  const renderActivityDetails = (log) => {
    let details = {};
    try {
      details = typeof log.details === 'string' ? JSON.parse(log.details) : (log.details || {});
    } catch (e) {
      return <span style={{ color: 'var(--text-muted)' }}>Could not parse activity details.</span>;
    }

    const isTx = log.entity_type === 'TRANSACTION';
    
    if (log.action_type === 'CREATE') {
      if (isTx) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span>
              Created transaction <strong>"{details.description}"</strong> of{' '}
              <strong style={{ color: 'var(--color-inflow)' }}>{formatMoney(details.total_amount, details.currency)}</strong>
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Category: {details.category_name} • Date: {formatDateDDMMYYYY(details.date)}
            </span>
          </div>
        );
      } else {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span>
              Created investment transaction <strong>{details.type}</strong> for{' '}
              <strong>{details.symbol}</strong> ({details.name})
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {details.units} units @ {formatMoney(details.price_per_unit, 'AED')} (Total: {formatMoney(details.amount, 'AED')})
            </span>
          </div>
        );
      }
    }

    if (log.action_type === 'DELETE') {
      if (isTx) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span>
              Deleted transaction <strong>"{details.description}"</strong> of{' '}
              <strong style={{ color: 'var(--color-outflow)' }}>{formatMoney(details.total_amount, details.currency)}</strong>
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              Category: {details.category_name} • Date: {formatDateDDMMYYYY(details.date)}
            </span>
          </div>
        );
      } else {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span>
              Deleted investment transaction <strong>{details.type}</strong> for{' '}
              <strong>{details.symbol}</strong> ({details.name})
            </span>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
              {details.units} units @ {formatMoney(details.price_per_unit, 'AED')} (Total: {formatMoney(details.amount, 'AED')})
            </span>
          </div>
        );
      }
    }

    if (log.action_type === 'UPDATE') {
      const prev = details.previous || {};
      
      const renderChange = (label, wasVal, isVal) => {
        if (wasVal === isVal || wasVal === undefined || isVal === undefined) return null;
        return (
          <div style={{ fontSize: '11px', margin: '2px 0 0 10px' }} key={label}>
            <span style={{ color: 'var(--text-muted)' }}>{label}:</span>{' '}
            <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>{wasVal}</span>{' '}
            <span style={{ color: 'var(--accent-cyan)' }}>➔</span>{' '}
            <strong>{isVal}</strong>
          </div>
        );
      };

      if (isTx) {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span>
              Modified transaction <strong>"{details.description || prev.description}"</strong>
            </span>
            <div style={{ borderLeft: '2px solid rgba(255, 255, 255, 0.1)', paddingLeft: '8px', marginTop: '4px' }}>
              {renderChange('Description', prev.description, details.description)}
              {renderChange('Amount', prev.total_amount !== undefined ? formatMoney(prev.total_amount, prev.currency || details.currency) : undefined, formatMoney(details.total_amount, details.currency))}
              {renderChange('Category', prev.category_name, details.category_name)}
              {renderChange('Date', prev.date ? formatDateDDMMYYYY(prev.date) : undefined, formatDateDDMMYYYY(details.date))}
            </div>
          </div>
        );
      } else {
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <span>
              Modified investment transaction for <strong>{details.symbol}</strong> ({details.name})
            </span>
            <div style={{ borderLeft: '2px solid rgba(255, 255, 255, 0.1)', paddingLeft: '8px', marginTop: '4px' }}>
              {renderChange('Type', prev.type, details.type)}
              {renderChange('Units', prev.units, details.units)}
              {renderChange('Price Per Unit', prev.price_per_unit !== undefined ? formatMoney(prev.price_per_unit, 'AED') : undefined, formatMoney(details.price_per_unit, 'AED'))}
              {renderChange('Amount', prev.amount !== undefined ? formatMoney(prev.amount, 'AED') : undefined, formatMoney(details.amount, 'AED'))}
            </div>
          </div>
        );
      }
    }

    return <span style={{ color: 'var(--text-muted)' }}>Unknown activity type</span>;
  };

  return (
    <div className="app-container">
      {/* --- FULLSCREEN ANIMATED LOGO REVEAL LOADER --- */}
      {isLoadingData && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          zIndex: 99999, backgroundColor: 'var(--bg-primary)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '24px'
        }}>
          <img src="/logo.png" alt="tezer" className="logo-reveal-img" style={{ width: '96px', height: '96px' }} />
          <div className="logo-reveal-line1">
            <span style={{ color: 'var(--accent-cyan)' }}>t</span>ezer
          </div>
          <div className="logo-reveal-line2">money matters!</div>
          <ThinkingOrbLoader state="working" size={64} color="var(--accent-cyan)" style={{ marginTop: '24px' }} />
        </div>
      )}

      {/* --- SUCCESS CHECKMARK OVERLAY --- */}
      {successCheckmarkMsg && (
        <div
          className="animate-fade-in modal-backdrop-blur"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            className="glass-panel modal-spring-enter"
            style={{
              width: '100%',
              maxWidth: '380px',
              background: 'var(--bg-secondary)',
              borderRadius: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.8)'
            }}
          >
            <SuccessCheckmark
              message={successCheckmarkMsg}
              onClose={() => setSuccessCheckmarkMsg(null)}
            />
          </div>
        </div>
      )}

      {/* --- WHATSAPP REDIRECT REASON MODAL --- */}
      {waRedirectModal.isOpen && (
        <div
          className="animate-fade-in modal-backdrop-blur"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            className="glass-panel modal-spring-enter"
            style={{
              width: '100%',
              maxWidth: '420px',
              background: 'var(--bg-secondary)',
              borderRadius: '24px',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
              display: 'flex',
              flexDirection: 'column',
              gap: '14px'
            }}
          >
            <h3 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px', color: '#25D366' }}>
              💬 WhatsApp Debt Reminder
            </h3>

            <div style={{ padding: '12px 14px', background: 'rgba(255, 176, 58, 0.1)', borderLeft: '4px solid #ffb03a', borderRadius: '10px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: '#ffb03a', fontWeight: 'bold' }}>
                {waRedirectModal.redirectReason || 'Whatsapp bot is currently unavailable :-('}
              </p>
            </div>

            <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0, lineHeight: '1.5' }}>
              To deliver your debt reminder to <strong>{waRedirectModal.friendName}</strong>, WhatsApp will open on your device with the debt details pre-filled and ready to send.
            </p>

            <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  window.open(waRedirectModal.waMeUrl, '_blank');
                  setWaRedirectModal({ ...waRedirectModal, isOpen: false });
                }}
                style={{ flex: 1, padding: '12px', background: '#25D366', color: '#ffffff', fontSize: '13px', fontWeight: 'bold' }}
              >
                Open WhatsApp
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setWaRedirectModal({ ...waRedirectModal, isOpen: false })}
                style={{ padding: '12px 16px', fontSize: '13px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- WHATSAPP REMINDER METHOD CHOICE MODAL --- */}
      {waChoiceModal.isOpen && (
        <div
          className="animate-fade-in modal-backdrop-blur"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div
            className="glass-panel modal-spring-enter"
            style={{
              width: '100%',
              maxWidth: '420px',
              background: 'var(--bg-secondary)',
              borderRadius: '24px',
              padding: '24px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px', color: '#25D366' }}>
                💬 Send WhatsApp Reminder
              </h3>
              <button
                type="button"
                onClick={() => setWaChoiceModal({ isOpen: false, friend: null, amount: 0, currency: 'AED' })}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
              Choose reminder schedule and delivery method for <strong>{waChoiceModal.friend?.nickname || waChoiceModal.friend?.display_name || 'your friend'}</strong>:
            </p>

            {/* Frequency Selector Tabs */}
            <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', padding: '3px', borderRadius: '10px', border: '1px solid var(--border-glass)', gap: '4px' }}>
              <button
                type="button"
                onClick={() => setWaReminderFreq('NOW')}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  background: waReminderFreq === 'NOW' ? 'var(--accent-purple)' : 'transparent',
                  color: waReminderFreq === 'NOW' ? 'white' : 'var(--text-muted)'
                }}
              >
                ⚡ Now
              </button>
              <button
                type="button"
                onClick={() => setWaReminderFreq('WEEKLY')}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  background: waReminderFreq === 'WEEKLY' ? 'var(--accent-purple)' : 'transparent',
                  color: waReminderFreq === 'WEEKLY' ? 'white' : 'var(--text-muted)'
                }}
              >
                📅 Weekly
              </button>
              <button
                type="button"
                onClick={() => setWaReminderFreq('MONTHLY')}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  background: waReminderFreq === 'MONTHLY' ? 'var(--accent-purple)' : 'transparent',
                  color: waReminderFreq === 'MONTHLY' ? 'white' : 'var(--text-muted)'
                }}
              >
                📆 Monthly
              </button>
              <button
                type="button"
                onClick={() => setWaReminderFreq('CUSTOM')}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  fontSize: '11px',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  border: 'none',
                  cursor: 'pointer',
                  background: waReminderFreq === 'CUSTOM' ? 'var(--accent-purple)' : 'transparent',
                  color: waReminderFreq === 'CUSTOM' ? 'white' : 'var(--text-muted)'
                }}
              >
                ⏰ Custom
              </button>
            </div>

            {waReminderFreq === 'WEEKLY' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Select Day of Week:</label>
                <select
                  value={waReminderDayOfWeek}
                  onChange={e => setWaReminderDayOfWeek(e.target.value)}
                  style={{ padding: '8px', fontSize: '12px', width: '100%' }}
                >
                  <option value="MONDAY">Every Monday</option>
                  <option value="TUESDAY">Every Tuesday</option>
                  <option value="WEDNESDAY">Every Wednesday</option>
                  <option value="THURSDAY">Every Thursday</option>
                  <option value="FRIDAY">Every Friday</option>
                  <option value="SATURDAY">Every Saturday</option>
                  <option value="SUNDAY">Every Sunday</option>
                </select>
              </div>
            )}

            {waReminderFreq === 'MONTHLY' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Select Day of Month (1 - 31):</label>
                <select
                  value={waReminderDayOfMonth}
                  onChange={e => setWaReminderDayOfMonth(parseInt(e.target.value, 10))}
                  style={{ padding: '8px', fontSize: '12px', width: '100%' }}
                >
                  {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                    <option key={day} value={day}>Day {day} of every month</option>
                  ))}
                </select>
              </div>
            )}

            {waReminderFreq === 'CUSTOM' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Select Specific Date & Time:</label>
                <input
                  type="datetime-local"
                  value={waReminderCustomDateTime}
                  onChange={e => setWaReminderCustomDateTime(e.target.value)}
                  style={{ padding: '8px', fontSize: '12px', width: '100%' }}
                />
              </div>
            )}

            {waReminderFreq !== 'NOW' ? (
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  handleSendWhatsAppReminder(
                    waChoiceModal.friend,
                    waChoiceModal.amount,
                    waChoiceModal.currency,
                    'BOT',
                    waChoiceModal.balances,
                    waReminderFreq,
                    waReminderDayOfWeek,
                    waReminderDayOfMonth,
                    waReminderCustomDateTime
                  );
                }}
                style={{ padding: '12px', background: '#25D366', color: 'white', fontWeight: 'bold', fontSize: '13px', border: 'none', borderRadius: '12px' }}
              >
                💾 Save Scheduled WhatsApp Reminder
              </button>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {/* Option 1: Bot message */}
                <div
                  onClick={() => {
                    if (!waBotEnabled) {
                      return showToast('Bot message option unavailable', 'info');
                    }
                    handleSendWhatsAppReminder(waChoiceModal.friend, waChoiceModal.amount, waChoiceModal.currency, 'BOT', waChoiceModal.balances);
                  }}
                  style={{
                    padding: '14px 16px',
                    borderRadius: '16px',
                    background: waBotEnabled ? 'rgba(37, 211, 102, 0.08)' : 'rgba(255, 255, 255, 0.03)',
                    border: waBotEnabled ? '1px solid rgba(37, 211, 102, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
                    cursor: waBotEnabled ? 'pointer' : 'not-allowed',
                    opacity: waBotEnabled ? 1 : 0.6,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px'
                  }}
                >
                  <div style={{ fontSize: '26px', filter: waBotEnabled ? 'none' : 'grayscale(100%)' }}>🤖</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: waBotEnabled ? '#25D366' : 'var(--text-muted)', marginBottom: '3px' }}>
                      1. Bot message (Instant)
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                      {waBotEnabled ? 'Send an automated reminder directly through the tezer WhatsApp Bot.' : 'Bot message option unavailable'}
                    </div>
                  </div>
                </div>

                {/* Option 2: Send from my whatsapp */}
                <div
                  onClick={() => handleSendWhatsAppReminder(waChoiceModal.friend, waChoiceModal.amount, waChoiceModal.currency, 'DIRECT_WA_ME', waChoiceModal.balances)}
                  style={{
                    padding: '14px 16px',
                    borderRadius: '16px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid var(--border-glass)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '14px'
                  }}
                >
                  <div style={{ fontSize: '26px' }}>📱</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main)', marginBottom: '3px' }}>
                      2. Send from my whatsapp
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                      Open WhatsApp on your device with a pre-filled reminder text to send yourself.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}


      
      {/* --- Sliding Side Menu Drawer / Permanent Desktop Sidebar --- */}
      {(isMenuOpen || isDesktop) && (
        <div className={isDesktop ? "desktop-sidebar" : "side-menu-overlay"} onClick={() => !isDesktop && setIsMenuOpen(false)}>
          <div className="side-menu-drawer" onClick={e => e.stopPropagation()}>
            <div className="side-menu-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src="/logo.png" alt="tezer logo" style={{ height: '26px', width: 'auto', borderRadius: '6px' }} />
                <h2 style={{ fontSize: '20px', margin: 0 }}>Menu</h2>
              </div>
              {!isDesktop && (
                <button onClick={() => setIsMenuOpen(false)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <X size={20} />
                </button>
              )}
            </div>
            
            <div className="side-menu-content">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '20px', borderBottom: '1px solid var(--border-glass)', paddingBottom: '16px' }}>
                <h3 style={{ fontSize: '11px', paddingLeft: '16px', marginBottom: '8px' }}>Navigation</h3>
                <button onClick={() => { setActiveTab('dashboard'); setActiveSheet(null); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeTab === 'dashboard' && !activeSheet ? 'active' : ''}`}>
                  📊 Dashboard
                </button>
                <button onClick={() => { setActiveTab('transactions_feed'); setActiveSheet(null); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeTab === 'transactions_feed' && !activeSheet ? 'active' : ''}`}>
                  📝 All Transactions
                </button>
                <button onClick={() => { setActiveTab('accounts'); setActiveSheet(null); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeTab === 'accounts' && !activeSheet ? 'active' : ''}`}>
                  🏦 Cash & Bank Accounts
                </button>
                <button onClick={() => { setActiveTab('credit_cards'); setActiveSheet(null); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeTab === 'credit_cards' && !activeSheet ? 'active' : ''}`}>
                  💳 Credit Cards
                </button>
                <button onClick={() => { setActiveTab('groups'); setActiveSheet(null); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeTab === 'groups' && !activeSheet ? 'active' : ''}`}>
                  👥 Groups
                </button>
                <button onClick={() => { setActiveTab('friends'); setActiveSheet(null); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeTab === 'friends' && !activeSheet ? 'active' : ''}`}>
                  🤝 Friends
                </button>
                <button onClick={() => { setActiveTab('transfers'); setActiveSheet(null); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeTab === 'transfers' && !activeSheet ? 'active' : ''}`}>
                  🔄 Transfers
                </button>
                <button onClick={() => { setActiveTab('investments'); setActiveSheet(null); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeTab === 'investments' && !activeSheet ? 'active' : ''}`}>
                  📈 Investments
                </button>
              </div>

              <h3 style={{ fontSize: '11px', paddingLeft: '16px', marginBottom: '8px' }}>Manage</h3>
              <button onClick={() => { setActiveSheet('categories'); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeSheet === 'categories' ? 'active' : ''}`}>
                📁 Categories
              </button>
              <button onClick={() => { setActiveSheet('accounts_manage'); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeSheet === 'accounts_manage' ? 'active' : ''}`}>
                🏦 Cash and Bank Accounts
              </button>
              <button onClick={() => { setActiveSheet('cards_manage'); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeSheet === 'cards_manage' ? 'active' : ''}`}>
                💳 Credit Cards
              </button>
              <button onClick={() => { setActiveSheet('currencies_manage'); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeSheet === 'currencies_manage' ? 'active' : ''}`}>
                🪙 Currencies
              </button>
              <button onClick={() => { setActiveSheet('settings_manage'); !isDesktop && setIsMenuOpen(false); }} className={`side-menu-item ${activeSheet === 'settings_manage' ? 'active' : ''}`}>
                ⚙️ Settings & Backups
              </button>
            </div>

            <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border-glass)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {/* User Profile Avatar Card near Email at bottom of menu */}
              <div 
                onClick={() => { setActiveSheet('settings_manage'); !isDesktop && setIsMenuOpen(false); }}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '10px', 
                  padding: '10px 12px', 
                  borderRadius: '12px', 
                  background: activeSheet === 'settings_manage' ? 'rgba(0, 245, 212, 0.12)' : 'rgba(255, 255, 255, 0.04)', 
                  border: activeSheet === 'settings_manage' ? '1px solid var(--accent-cyan)' : '1px solid var(--border-glass)', 
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                title="Click to manage profile & settings"
              >
                <div style={{ position: 'relative', width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden', border: '1.5px solid var(--accent-cyan)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ fontSize: '15px', fontWeight: 'bold', color: 'var(--accent-cyan)', position: 'absolute' }}>
                    {(profileDisplayName || 'U')[0].toUpperCase()}
                  </div>
                  {profileAvatarUrl && (
                    <img src={apiClient.getAvatarUrl(profileAvatarUrl)} alt="Avatar" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'relative', zIndex: 1 }} />
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-main)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {profileDisplayName || 'User Profile'}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {localStorage.getItem('dhudd_email') || 'Logged in'}
                  </div>
                </div>
                <span style={{ fontSize: '12px', color: 'var(--accent-cyan)' }}>⚙️</span>
              </div>

              <button onClick={handleLogout} className="btn-secondary" style={{ width: '100%', padding: '8px', fontSize: '12px', color: 'var(--color-outflow)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', borderRadius: '10px', border: '1px solid rgba(255,71,87,0.3)' }}>
                <LogOut size={13} /> Log Out
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="app-body">
        
        {/* --- Header (Hamburger Menu + Account Filters) --- */}
        <header style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '16px 20px', borderBottom: '1px solid var(--border-glass)',
          backgroundColor: 'var(--bg-secondary)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Hamburger Menu trigger */}
            <button
              onClick={() => setIsMenuOpen(true)}
              style={{ background: 'transparent', border: 'none', color: 'var(--text-main)', cursor: 'pointer' }}
            >
              <Menu size={24} />
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <img src="/logo.png" alt="tezer logo" style={{ height: '24px', width: 'auto', borderRadius: '4px' }} />
              <h1 style={{ fontSize: '20px', margin: 0 }}>
                <span style={{ color: 'var(--accent-cyan)' }}>t</span>ezer
              </h1>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '12px' }}>
              {!isStandalone && (
                <button
                  type="button"
                  onClick={handleInstallPwa}
                  style={{
                    background: 'rgba(0, 245, 212, 0.12)',
                    border: '1px solid var(--accent-cyan)',
                    color: 'var(--accent-cyan)',
                    borderRadius: '8px',
                    padding: '4px 10px',
                    fontSize: '11px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out'
                  }}
                  title="Install the Tezer App on your mobile or computer"
                >
                  <Download size={13} /> Install App
                </button>
              )}
              <button
                onClick={() => {
                  setActiveTab('notifications');
                  setActiveSheet(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: activeTab === 'notifications' ? 'var(--accent-cyan)' : 'var(--text-main)',
                  cursor: 'pointer',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '4px'
                }}
              >
                <Bell size={18} />
                {displayNotifications.filter(n => !n.read).length > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: 'var(--color-outflow)',
                    color: 'white',
                    borderRadius: '50%',
                    fontSize: '9px',
                    fontWeight: 'bold',
                    height: '14px',
                    width: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {displayNotifications.filter(n => !n.read).length}
                  </span>
                )}
              </button>
              {queuedMutationsCount > 0 && (
                <button
                  type="button"
                  onClick={() => showToast(`${queuedMutationsCount} transaction(s) queued offline. They will sync automatically when the connection is restored.`, 'info')}
                  style={{
                    fontSize: '9px',
                    background: 'var(--color-outflow)',
                    color: 'white',
                    border: 'none',
                    padding: '2px 6px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    transition: 'opacity 0.2s'
                  }}
                  onMouseOver={e => e.currentTarget.style.opacity = 0.8}
                  onMouseOut={e => e.currentTarget.style.opacity = 1}
                  title="Offline Sync Queue"
                >
                  {queuedMutationsCount}
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Offline Mode Indicator Banner */}
        {!online && (
          <div className="animate-fade-in" style={{
            background: 'linear-gradient(90deg, rgba(255, 171, 0, 0.15), rgba(255, 107, 107, 0.15))',
            borderBottom: '1px solid rgba(255, 171, 0, 0.3)',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            fontSize: '12px',
            fontWeight: '600',
            color: '#ffc107',
            userSelect: 'none'
          }}>
            <span style={{ fontSize: '14px' }}>⚡</span>
            <span>Offline Mode — All changes saved locally and will auto-sync when reconnected{queuedMutationsCount > 0 ? ` (${queuedMutationsCount} queued)` : ''}</span>
          </div>
        )}

        {/* --- Toast Banners --- */}
        {alert && (
          <div className="glass-panel animate-fade-in" style={{
            position: 'fixed', top: '70px', left: '50%', transform: 'translateX(-50%)',
            zIndex: 100002, padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '8px',
            backgroundColor: alert.type === 'error' ? 'rgba(255,71,87,0.95)' : 
                             alert.type === 'success' ? 'rgba(46,213,115,0.95)' : 'rgba(24,28,47,0.95)'
          }}>
            {alert.type === 'success' ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            <span style={{ fontSize: '13px', fontWeight: '500' }}>{alert.message}</span>
          </div>
        )}

      {/* --- Main Contents --- */}
      <main style={{ flex: 1, overflowY: 'auto', paddingBottom: '90px' }}>

        {/* VIEW: DASHBOARD */}
        {activeTab === 'dashboard' && (
          <div className="animate-fade-in" style={{ padding: '20px' }}>


            
            {/* Net Balance Premium Header Card */}
            <div className="glass-panel" style={{ padding: '18px 24px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center', textAlign: 'center', position: 'relative', zIndex: accountsFilterOpen ? 100 : 1 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div ref={accountsDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
                    <div
                      role="button"
                      onClick={() => {
                        setAccountsFilterOpen(!accountsFilterOpen);
                        setCategoriesFilterOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>Net Balance</span>
                      <span style={{
                        fontSize: '11px',
                        background: 'var(--bg-input)',
                        border: '1px solid var(--border-glass)',
                        borderRadius: '12px',
                        padding: '2px 10px',
                        color: 'var(--text-main)',
                        fontWeight: '500',
                        maxWidth: '120px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {selectedAccountIds.includes('ALL')
                          ? 'Included Only'
                          : selectedAccountIds.length === 0
                            ? 'None'
                            : selectedAccountIds.length === 1
                              ? accounts.find(a => a.account_id === selectedAccountIds[0])?.name || '1 Account'
                              : `${selectedAccountIds.length} Accounts`}
                      </span>
                      <ChevronDown size={12} style={{ color: 'var(--text-muted)' }} />
                    </div>

                    {accountsFilterOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '28px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 1000,
                          minWidth: '240px',
                          maxHeight: '300px',
                          overflowY: 'auto',
                          background: 'rgba(27, 30, 50, 0.95)',
                          backdropFilter: 'blur(20px)',
                          border: '1px solid var(--border-glass)',
                          borderRadius: '16px',
                          boxShadow: 'var(--shadow-premium)',
                          padding: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            padding: '6px 8px',
                            borderRadius: '8px',
                            transition: 'background 0.2s',
                            fontSize: '13px',
                            fontWeight: '600',
                            color: 'var(--text-main)'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (selectedAccountIds.includes('ALL')) {
                              setSelectedAccountIds([]);
                            } else {
                              setSelectedAccountIds(['ALL']);
                            }
                          }}
                        >
                          <input
                            type="checkbox"
                            readOnly
                            checked={selectedAccountIds.includes('ALL')}
                            style={{ width: '16px', height: '16px', pointerEvents: 'none' }}
                          />
                          <span>All Included Accounts</span>
                        </div>
                        
                        <div style={{ height: '1px', background: 'var(--border-glass)', margin: '4px 0' }} />

                        {accounts.filter(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0').map(acc => {
                          const isChecked = selectedAccountIds.includes('ALL')
                            ? !!acc.include_in_total
                            : selectedAccountIds.includes(acc.account_id);
                          return (
                            <div
                              key={acc.account_id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '8px',
                                cursor: 'pointer',
                                padding: '6px 8px',
                                borderRadius: '8px',
                                transition: 'background 0.2s',
                                fontSize: '13px',
                                color: 'var(--text-main)'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (selectedAccountIds.includes('ALL')) {
                                  // Switching from ALL to explicit: start with include_in_total accounts, then toggle this one
                                  const includedIds = accounts
                                    .filter(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && a.include_in_total)
                                    .map(a => a.account_id);
                                  if (acc.include_in_total) {
                                    // Unchecking an included account
                                    setSelectedAccountIds(includedIds.filter(id => id !== acc.account_id));
                                  } else {
                                    // Checking a non-included account
                                    setSelectedAccountIds([...includedIds, acc.account_id]);
                                  }
                                } else if (isChecked) {
                                  const next = selectedAccountIds.filter(id => id !== acc.account_id);
                                  setSelectedAccountIds(next);
                                } else {
                                  const next = [...selectedAccountIds, acc.account_id];
                                  // Check if all include_in_total accounts are now selected and no extras
                                  const includedIds = accounts
                                    .filter(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && a.include_in_total)
                                    .map(a => a.account_id);
                                  const isBackToDefault = includedIds.length === next.length && includedIds.every(id => next.includes(id));
                                  setSelectedAccountIds(isBackToDefault ? ['ALL'] : next);
                                }
                              }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                  type="checkbox"
                                  readOnly
                                  checked={isChecked}
                                  style={{ width: '16px', height: '16px', pointerEvents: 'none' }}
                                />
                                <span>{acc.name}</span>
                              </div>
                              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                {hideBalances ? '••••••' : formatMoney(acc.type === 'CREDIT_CARD' ? -parseFloat(acc.current_balance) : parseFloat(acc.current_balance), acc.currency)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const next = !hideBalances;
                      setHideBalances(next);
                      localStorage.setItem('dhudd_hide_balances', next ? 'true' : 'false');
                    }}
                    title={hideBalances ? "Show Balances" : "Hide Balances"}
                    style={{
                      background: hideBalances ? 'rgba(0, 245, 212, 0.12)' : 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid var(--border-glass)',
                      borderRadius: '8px',
                      padding: '4px 6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: hideBalances ? 'var(--accent-cyan)' : 'var(--text-muted)',
                      transition: 'all 0.2s ease',
                      outline: 'none'
                    }}
                  >
                    {hideBalances ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center', alignItems: 'center' }}>
                  {getNetBalancesByCurrency().map((nb, i) => (
                    <span key={i} style={{
                      fontSize: '24px',
                      fontWeight: '800',
                      color: nb.amount >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)',
                      textShadow: nb.amount >= 0 ? '0 0 10px rgba(0,245,212,0.2)' : 'none',
                      letterSpacing: hideBalances ? '3px' : 'normal'
                    }}>
                      {hideBalances ? '••••••' : formatMoney(nb.amount, nb.currency)}
                    </span>
                  ))}
                </div>
              </div>
              <div style={{ width: '80%', height: '1px', background: 'var(--border-glass)' }}></div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Overall Balance (All Accounts)</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', alignItems: 'center' }}>
                  {getOverallBalancesByCurrency().map((nb, i) => (
                    <span key={`ov-${i}`} style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: nb.amount >= 0 ? 'rgba(0, 245, 212, 0.8)' : 'rgba(255, 99, 132, 0.8)',
                      letterSpacing: hideBalances ? '3px' : 'normal'
                    }}>
                      {hideBalances ? '••••••' : formatMoney(nb.amount, nb.currency)}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* ─── Pending Actions Card ─── */}
            <div className="glass-panel animate-fade-in" style={{ padding: '16px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontWeight: '700', fontSize: '14px', color: 'var(--accent-cyan)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  ⏳ Pending Actions
                  {pendingActions.length > 0 && (
                    <span style={{
                      background: 'rgba(255,165,0,0.18)', color: '#FFA500',
                      borderRadius: '999px', padding: '2px 10px', fontSize: '11px', fontWeight: '700'
                    }}>{pendingActions.length}</span>
                  )}
                </span>
                {pendingActions.length > 1 ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>
                      {Math.min(activePendingActionIndex + 1, pendingActions.length)} / {pendingActions.length}
                    </span>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      <button
                        type="button"
                        onClick={() => scrollPendingActions('left')}
                        disabled={activePendingActionIndex === 0}
                        aria-label="Previous pending action"
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid var(--border-glass)',
                          borderRadius: '6px',
                          padding: '3px 6px',
                          color: activePendingActionIndex === 0 ? 'var(--text-muted)' : 'var(--text-primary)',
                          cursor: activePendingActionIndex === 0 ? 'not-allowed' : 'pointer',
                          opacity: activePendingActionIndex === 0 ? 0.35 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'opacity 0.2s'
                        }}
                      >
                        <ChevronLeft size={13} />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollPendingActions('right')}
                        disabled={activePendingActionIndex >= pendingActions.length - 1}
                        aria-label="Next pending action"
                        style={{
                          background: 'rgba(255,255,255,0.06)',
                          border: '1px solid var(--border-glass)',
                          borderRadius: '6px',
                          padding: '3px 6px',
                          color: activePendingActionIndex >= pendingActions.length - 1 ? 'var(--text-muted)' : 'var(--text-primary)',
                          cursor: activePendingActionIndex >= pendingActions.length - 1 ? 'not-allowed' : 'pointer',
                          opacity: activePendingActionIndex >= pendingActions.length - 1 ? 0.35 : 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'opacity 0.2s'
                        }}
                      >
                        <ChevronRight size={13} />
                      </button>
                    </div>
                  </div>
                ) : pendingActions.length === 1 ? (
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Choose an account</span>
                ) : null}
              </div>

              {pendingActionsAllDone && (
                <div className="animate-fade-in" style={{
                  background: 'rgba(0,245,212,0.1)', border: '1px solid var(--accent-cyan)',
                  borderRadius: '10px', padding: '10px 14px', textAlign: 'center',
                  fontSize: '13px', color: 'var(--accent-cyan)', fontWeight: '600'
                }}>
                  🎉 All caught up! Your accounts are up to date.
                </div>
              )}

              {pendingActions.length === 0 && !pendingActionsAllDone && (
                <div style={{
                  textAlign: 'center', padding: '10px 14px',
                  fontSize: '13px', color: 'var(--text-muted)', fontWeight: '500'
                }}>
                  ✅ No pending actions — you're all caught up!
                </div>
              )}

              {pendingActions.length > 0 && (
                <>
                  <div
                    ref={pendingActionsTrackRef}
                    className="pending-actions-carousel"
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      if (el && el.clientWidth) {
                        const cardWidth = el.clientWidth > 360 ? 340 : (el.clientWidth * 0.86);
                        const idx = Math.round(el.scrollLeft / cardWidth);
                        setActivePendingActionIndex(Math.min(Math.max(0, idx), pendingActions.length - 1));
                      }
                    }}
                  >
                    {pendingActions.map(pa => {
                      const isOlderThan7Days = (Date.now() - new Date(pa.created_at).getTime()) > 7 * 24 * 60 * 60 * 1000;
                      const sourceName = pa.source_display_name || (pa.source_email ? pa.source_email.split('@')[0] : 'Someone');
                      const isDebit = pa.type === 'OUTGOING_PAYMENT' || pa.type === 'SPLIT_EXPENSE';
                      const typeLabel = pa.type === 'INCOMING_TRANSFER'
                        ? '💸 Incoming Transfer'
                        : pa.type === 'OUTGOING_PAYMENT'
                        ? '📤 Outgoing Payment'
                        : '🤝 Expense Paid by You';
                      const actionVerb = isDebit ? 'Debit from' : 'Credit to';
                      const chosenAccId = actionSelections[pa.action_id] || '';
                      const isResolvingThis = resolvingAction?.id === pa.action_id;
                      const isAssigningThis = isResolvingThis && resolvingAction?.op === 'assign';
                      const isRejectingThis = isResolvingThis && resolvingAction?.op === 'reject';
                      const isClearingThis = isResolvingThis && resolvingAction?.op === 'clear';

                      return (
                        <div
                          key={pa.action_id}
                          className="pending-action-card"
                          style={{
                            flex: pendingActions.length === 1 ? '0 0 100%' : '0 0 min(330px, 86vw)',
                            minWidth: pendingActions.length === 1 ? '100%' : '270px'
                          }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                              <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>{typeLabel}</span>
                              <span style={{ fontSize: '13px', fontWeight: '800', color: isDebit ? 'var(--color-outflow)' : 'var(--color-inflow)' }}>
                                {isDebit ? '−' : '+'}{parseFloat(pa.amount).toFixed(2)} {pa.currency}
                              </span>
                            </div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', wordBreak: 'break-word' }}>
                              {pa.type === 'SPLIT_EXPENSE' && !pa.description?.includes('recorded')
                                ? `${sourceName} recorded "${pa.description || 'Expense'}" (${parseFloat(pa.amount).toFixed(2)} ${pa.currency}) paid by you`
                                : (pa.description || '(No description)')}
                            </div>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              From <strong style={{ color: 'var(--text-secondary)' }}>{sourceName}</strong>
                              {' · '}{formatDateDDMMYYYY(pa.created_at)}
                            </div>
                            {(() => {
                              const paDate = pa.source_tx_date || pa.created_at;
                              const similarExisting = findSimilarTransaction({
                                amount: pa.amount,
                                currency: pa.currency,
                                date: paDate,
                                description: pa.description || `From ${sourceName}`,
                                type: pa.type === 'OUTGOING_PAYMENT' || pa.type === 'TRANSFER' ? 'transfer' : 'expense',
                                ignorePendingActions: true
                              });
                              if (!similarExisting) return null;
                              return (
                                <div style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: 'rgba(255, 71, 87, 0.15)',
                                  border: '1px solid rgba(255, 71, 87, 0.3)',
                                  color: '#ff4757',
                                  borderRadius: '6px',
                                  padding: '2px 8px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  marginTop: '4px'
                                }}>
                                  <AlertTriangle size={12} />
                                  <span>Similar transaction already recorded</span>
                                </div>
                              );
                            })()}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: 'auto' }}>
                            <CustomAccountSelect
                              value={chosenAccId}
                              onChange={e => setActionSelections(prev => ({ ...prev, [pa.action_id]: e.target.value }))}
                              accounts={accounts}
                              filter={a =>
                                a.currency === pa.currency &&
                                a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' &&
                                !(a.name && a.name.includes('External Debts'))
                              }
                              placeholder={`— ${actionVerb} account —`}
                              formatMoney={formatMoney}
                              style={{ width: '100%' }}
                            />

                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                              <button
                                disabled={!chosenAccId || isResolvingThis}
                                onClick={async () => {
                                  if (!chosenAccId || isResolvingThis) return;
                                  const paDate = pa.source_tx_date || pa.created_at;
                                  const similarExisting = findSimilarTransaction({
                                    amount: pa.amount,
                                    currency: pa.currency,
                                    date: paDate,
                                    description: pa.description || `From ${sourceName}`,
                                    type: pa.type === 'OUTGOING_PAYMENT' || pa.type === 'TRANSFER' ? 'transfer' : 'expense',
                                    ignorePendingActions: true
                                  });

                                  const doAssign = async () => {
                                    setResolvingAction({ id: pa.action_id, op: 'assign' });
                                    try {
                                      await apiClient.request(`/pending-actions/${pa.action_id}/resolve`, 'POST', { account_id: chosenAccId });
                                      showToast('Account assigned successfully!', 'success');
                                      trackEvent('pending_action_resolved', { type: pa.type, action_id: pa.action_id });
                                      await refreshData();
                                      if (pendingActions.length <= 1) {
                                        setPendingActionsAllDone(true);
                                        setTimeout(() => setPendingActionsAllDone(false), 4000);
                                      }
                                    } catch (err) {
                                      showToast('Failed to assign account: ' + (err.message || err), 'error');
                                    } finally {
                                      setResolvingAction(null);
                                    }
                                  };

                                  if (similarExisting) {
                                    setDuplicateAlertModal({
                                      isOpen: true,
                                      match: similarExisting,
                                      onConfirm: doAssign
                                    });
                                    return;
                                  }

                                  await doAssign();
                                }}
                                style={{
                                  padding: '7px 14px', borderRadius: '8px', fontSize: '12px', fontWeight: '700',
                                  background: chosenAccId && !isResolvingThis ? 'var(--accent-cyan)' : 'rgba(255,255,255,0.08)',
                                  color: chosenAccId && !isResolvingThis ? '#000' : 'var(--text-muted)', border: 'none',
                                  cursor: chosenAccId && !isResolvingThis ? 'pointer' : 'not-allowed',
                                  transition: 'background 0.2s',
                                  flex: 1
                                }}
                              >
                                {isAssigningThis ? <ThinkingOrbLoader popup state="solving" size={20} color="var(--accent-cyan)" text="Assigning..." /> : '✓ Assign'}
                              </button>

                              {!isOlderThan7Days && (
                                <button
                                  disabled={isResolvingThis}
                                  onClick={async () => {
                                    if (isResolvingThis) return;
                                    if (!window.confirm(`Reject this ${pa.type === 'SPLIT_EXPENSE' ? 'shared expense entry' : 'transaction'}? This cannot be undone.`)) return;
                                    setResolvingAction({ id: pa.action_id, op: 'reject' });
                                    try {
                                      await apiClient.request(`/pending-actions/${pa.action_id}/reject`, 'POST');
                                      showToast('Transaction rejected', 'info');
                                      trackEvent('pending_action_rejected', { type: pa.type, action_id: pa.action_id });
                                      await refreshData();
                                    } catch (err) {
                                      showToast('Failed to reject: ' + (err.message || err), 'error');
                                    } finally {
                                      setResolvingAction(null);
                                    }
                                  }}
                                  style={{
                                    padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                                    background: 'rgba(255,99,132,0.12)', color: 'rgba(255,99,132,0.9)',
                                    border: '1px solid rgba(255,99,132,0.3)', cursor: isResolvingThis ? 'not-allowed' : 'pointer'
                                  }}
                                >
                                  {isRejectingThis ? <ThinkingOrbLoader popup state="solving" size={20} color="rgba(255,99,132,0.9)" text="Rejecting..." /> : '✕ Reject'}
                                </button>
                              )}

                              <button
                                disabled={isResolvingThis}
                                onClick={async () => {
                                  if (isResolvingThis) return;
                                  setResolvingAction({ id: pa.action_id, op: 'clear' });
                                  try {
                                    await apiClient.request(`/pending-actions/${pa.action_id}/clear`, 'POST');
                                    showToast('Item cleared', 'info');
                                    await refreshData();
                                  } catch (err) {
                                    showToast('Failed to clear: ' + (err.message || err), 'error');
                                  } finally {
                                    setResolvingAction(null);
                                  }
                                }}
                                style={{
                                  padding: '7px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                                  background: 'rgba(255,255,255,0.06)', color: 'var(--text-muted)',
                                  border: '1px solid var(--border-glass)', cursor: isResolvingThis ? 'not-allowed' : 'pointer',
                                  transition: 'background 0.2s, color 0.2s'
                                }}
                                title="Dismiss from pending actions without affecting any balances"
                              >
                                {isClearingThis ? <ThinkingOrbLoader popup state="solving" size={20} color="var(--text-muted)" text="Clearing..." /> : '🧹 Clear'}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {pendingActions.length > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', paddingTop: '2px' }}>
                      {pendingActions.map((_, idx) => (
                        <div
                          key={idx}
                          onClick={() => {
                            if (pendingActionsTrackRef.current) {
                              const track = pendingActionsTrackRef.current;
                              const cardWidth = track.clientWidth > 360 ? 340 : (track.clientWidth * 0.86);
                              track.scrollTo({ left: idx * (cardWidth + 12), behavior: 'smooth' });
                            }
                          }}
                          style={{
                            width: activePendingActionIndex === idx ? '18px' : '6px',
                            height: '6px',
                            borderRadius: '3px',
                            background: activePendingActionIndex === idx ? 'var(--accent-cyan)' : 'rgba(255,255,255,0.2)',
                            transition: 'all 0.25s cubic-bezier(0.16, 1, 0.3, 1)',
                            cursor: 'pointer'
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>


            {/* Selection Elements */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '16px' }}>

            <div className="period-selector" style={{ margin: 0, padding: 0 }}>
              {['Day', 'Week', 'Month', 'Year', 'All', 'Interval'].map(p => (
                <button
                  key={p}

                  onClick={() => setActivePeriod(p)}
                  className={`period-tab ${activePeriod === p ? 'active' : ''}`}
                >
                  {p}
                </button>
              ))}
            </div>
            {activePeriod === 'Interval' && (
              <div className="animate-fade-in" style={{ display: 'flex', gap: '12px', padding: '0 10px', justifyContent: 'center' }}>
                <input
                  type="date"
                  value={customRange.start}
                  onChange={e => setCustomRange({ ...customRange, start: e.target.value })}
                  style={{ padding: '8px', fontSize: '12px' }}
                />
                <input
                  type="date"
                  value={customRange.end}
                  onChange={e => setCustomRange({ ...customRange, end: e.target.value })}
                  style={{ padding: '8px', fontSize: '12px' }}
                />
              </div>
            )}

            </div>

            {/* Segmented Toggle Control for Pie Charts */}
            <div style={{
              display: 'flex',
              background: 'var(--bg-input)',
              border: '1px solid var(--border-glass)',
              borderRadius: '14px',
              padding: '4px',
              marginBottom: '20px',
              boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.2)'
            }}>
              <button
                type="button"
                onClick={() => {
                  setDashboardChartTab('expense');
                  setSelectedCategories(['ALL']);
                  setCategoriesFilterOpen(false);
                }}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: '10px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: dashboardChartTab === 'expense' ? 'var(--color-outflow)' : 'transparent',
                  color: dashboardChartTab === 'expense' ? 'white' : 'var(--text-muted)',
                  boxShadow: dashboardChartTab === 'expense' ? '0 4px 12px rgba(255, 71, 87, 0.25)' : 'none',
                  transition: 'var(--transition-smooth)'
                }}
              >
                💸 Expenses
              </button>
              <button
                type="button"
                onClick={() => {
                  setDashboardChartTab('income');
                  setSelectedCategories(['ALL']);
                  setCategoriesFilterOpen(false);
                }}
                style={{
                  flex: 1,
                  padding: '10px 0',
                  borderRadius: '10px',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: dashboardChartTab === 'income' ? 'var(--color-inflow)' : 'transparent',
                  color: dashboardChartTab === 'income' ? 'white' : 'var(--text-muted)',
                  boxShadow: dashboardChartTab === 'income' ? '0 4px 12px rgba(46, 213, 115, 0.25)' : 'none',
                  transition: 'var(--transition-smooth)'
                }}
              >
                💰 Income
              </button>
            </div>

            {/* Active Pie Chart and Category Breakdown Card */}
            {dashboardChartTab === 'expense' ? (
              <div className="glass-panel animate-fade-in" style={{ padding: '20px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '20px', position: 'relative', zIndex: categoriesFilterOpen ? 100 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <h3 style={{ margin: 0, fontSize: '13px', color: 'var(--color-outflow)', textTransform: 'uppercase', letterSpacing: '1px' }}>Expense Breakdown</h3>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div ref={categoriesDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
                      <div
                        role="button"
                        onClick={() => {
                          setCategoriesFilterOpen(!categoriesFilterOpen);
                          setAccountsFilterOpen(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: 'pointer',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid var(--border-glass)',
                          borderRadius: '6px',
                          color: 'var(--text-main)',
                          fontSize: '11px',
                          padding: '4px 8px',
                          userSelect: 'none'
                        }}
                      >
                        <span style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedCategories.includes('ALL')
                            ? 'All Categories'
                            : selectedCategories.length === 1
                              ? selectedCategories[0]
                              : `${selectedCategories.length} Categories`}
                        </span>
                        <ChevronDown size={10} style={{ color: 'var(--text-muted)' }} />
                      </div>

                      {categoriesFilterOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '28px',
                            left: 0,
                            zIndex: 1000,
                            minWidth: '220px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            background: 'rgba(27, 30, 50, 0.95)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid var(--border-glass)',
                            borderRadius: '16px',
                            boxShadow: 'var(--shadow-premium)',
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                              padding: '6px 8px',
                              borderRadius: '8px',
                              transition: 'background 0.2s',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: 'var(--text-main)'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (selectedCategories.includes('ALL')) {
                                setSelectedCategories([]);
                              } else {
                                setSelectedCategories(['ALL']);
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              readOnly
                              checked={selectedCategories.includes('ALL')}
                              style={{ width: '16px', height: '16px', pointerEvents: 'none' }}
                            />
                            <span>All Categories</span>
                          </div>
                          
                          <div style={{ height: '1px', background: 'var(--border-glass)', margin: '4px 0' }} />

                          {categories.filter(c => c.type === 'OUTFLOW').map(cat => {
                            const isChecked = selectedCategories.includes('ALL') || selectedCategories.includes(cat.name);
                            return (
                              <div
                                key={cat.category_id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  cursor: 'pointer',
                                  padding: '6px 8px',
                                  borderRadius: '8px',
                                  transition: 'background 0.2s',
                                  fontSize: '13px',
                                  color: 'var(--text-main)'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const outflowCats = categories.filter(c => c.type === 'OUTFLOW');
                                  if (selectedCategories.includes('ALL')) {
                                    const allNames = outflowCats.map(c => c.name);
                                    const next = allNames.filter(name => name !== cat.name);
                                    setSelectedCategories(next);
                                  } else if (isChecked) {
                                    const next = selectedCategories.filter(name => name !== cat.name);
                                    setSelectedCategories(next);
                                  } else {
                                    const next = selectedCategories.filter(name => name !== 'ALL');
                                    if (next.length + 1 === outflowCats.length) {
                                      setSelectedCategories(['ALL']);
                                    } else {
                                      setSelectedCategories([...next, cat.name]);
                                    }
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  readOnly
                                  checked={isChecked}
                                  style={{ width: '16px', height: '16px', pointerEvents: 'none' }}
                                />
                                <span>{cat.symbol} {cat.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <select 
                      value={breakdownCurrency} 
                      onChange={e => setBreakdownCurrency(e.target.value)}
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '6px', color: 'var(--text-main)', fontSize: '11px', padding: '4px 8px' }}
                    >
                      <option value="ALL">All Currencies</option>
                      {preferredCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '280px' }}>
                  {renderPieChart('outflow')}
                </div>
                {spendSummary.totalSpend > 0 && (
                  <div style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {spendSummary.list.map((cat, idx) => {
                      const itemVal = cat.convertedAmount !== undefined ? cat.convertedAmount : cat.amount;
                      const pct = spendSummary.totalSpend > 0 ? Math.round((itemVal / spendSummary.totalSpend) * 100) : 0;
                      const isSelected = activeExpenseChartCategory === (cat.id || cat.name) || activeExpenseChartCategory === cat.name;

                      return (
                        <div 
                          key={cat.id || idx} 
                          onClick={() => handleOpenDrillDown(cat, 'outflow')}
                          style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '6px',
                            cursor: 'pointer', 
                            padding: '10px 12px', 
                            borderRadius: '10px', 
                            background: isSelected ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)',
                            border: isSelected ? `1px solid ${cat.color}66` : '1px solid var(--border-glass)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={() => setActiveExpenseChartCategory(cat.id || cat.name)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: 'var(--text-main)' }}>
                              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: cat.color, display: 'inline-block', flexShrink: 0 }} />
                              <span>{cat.icon} {cat.displayName || cat.name}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}>
                              <span style={{ color: 'var(--color-outflow)' }}>
                                {formatMoney(Math.abs(cat.amount || 0), cat.currency || breakdownCurrency)}
                              </span>
                              <span style={{ fontSize: '10px', fontWeight: '700', color: cat.color, background: `${cat.color}18`, padding: '2px 6px', borderRadius: '8px' }}>
                                {pct}%
                              </span>
                            </div>
                          </div>

                          <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                            <div 
                              style={{ 
                                width: `${Math.min(100, pct)}%`, 
                                height: '100%', 
                                background: cat.color, 
                                borderRadius: '2px', 
                                transition: 'width 0.4s ease' 
                              }} 
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className="glass-panel animate-fade-in" style={{ padding: '20px', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '20px', position: 'relative', zIndex: categoriesFilterOpen ? 100 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                  <h3 style={{ margin: 0, fontSize: '13px', color: 'var(--color-inflow)', textTransform: 'uppercase', letterSpacing: '1px' }}>Income Breakdown</h3>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div ref={categoriesDropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
                      <div
                        role="button"
                        onClick={() => {
                          setCategoriesFilterOpen(!categoriesFilterOpen);
                          setAccountsFilterOpen(false);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          cursor: 'pointer',
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid var(--border-glass)',
                          borderRadius: '6px',
                          color: 'var(--text-main)',
                          fontSize: '11px',
                          padding: '4px 8px',
                          userSelect: 'none'
                        }}
                      >
                        <span style={{ maxWidth: '80px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {selectedCategories.includes('ALL')
                            ? 'All Categories'
                            : selectedCategories.length === 1
                              ? selectedCategories[0]
                              : `${selectedCategories.length} Categories`}
                        </span>
                        <ChevronDown size={10} style={{ color: 'var(--text-muted)' }} />
                      </div>

                      {categoriesFilterOpen && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '28px',
                            left: 0,
                            zIndex: 1000,
                            minWidth: '220px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            background: 'rgba(27, 30, 50, 0.95)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid var(--border-glass)',
                            borderRadius: '16px',
                            boxShadow: 'var(--shadow-premium)',
                            padding: '12px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                              padding: '6px 8px',
                              borderRadius: '8px',
                              transition: 'background 0.2s',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: 'var(--text-main)'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (selectedCategories.includes('ALL')) {
                                setSelectedCategories([]);
                              } else {
                                setSelectedCategories(['ALL']);
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              readOnly
                              checked={selectedCategories.includes('ALL')}
                              style={{ width: '16px', height: '16px', pointerEvents: 'none' }}
                            />
                            <span>All Categories</span>
                          </div>
                          
                          <div style={{ height: '1px', background: 'var(--border-glass)', margin: '4px 0' }} />

                          {categories.filter(c => c.type === 'INFLOW').map(cat => {
                            const isChecked = selectedCategories.includes('ALL') || selectedCategories.includes(cat.name);
                            return (
                              <div
                                key={cat.category_id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  cursor: 'pointer',
                                  padding: '6px 8px',
                                  borderRadius: '8px',
                                  transition: 'background 0.2s',
                                  fontSize: '13px',
                                  color: 'var(--text-main)'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const inflowCats = categories.filter(c => c.type === 'INFLOW');
                                  if (selectedCategories.includes('ALL')) {
                                    const allNames = inflowCats.map(c => c.name);
                                    const next = allNames.filter(name => name !== cat.name);
                                    setSelectedCategories(next);
                                  } else if (isChecked) {
                                    const next = selectedCategories.filter(name => name !== cat.name);
                                    setSelectedCategories(next);
                                  } else {
                                    const next = selectedCategories.filter(name => name !== 'ALL');
                                    if (next.length + 1 === inflowCats.length) {
                                      setSelectedCategories(['ALL']);
                                    } else {
                                      setSelectedCategories([...next, cat.name]);
                                    }
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  readOnly
                                  checked={isChecked}
                                  style={{ width: '16px', height: '16px', pointerEvents: 'none' }}
                                />
                                <span>{cat.symbol} {cat.name}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <select 
                      value={breakdownCurrency} 
                      onChange={e => setBreakdownCurrency(e.target.value)}
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', borderRadius: '6px', color: 'var(--text-main)', fontSize: '11px', padding: '4px 8px' }}
                    >
                      <option value="ALL">All Currencies</option>
                      {preferredCurrencies.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '280px' }}>
                  {renderPieChart('inflow')}
                </div>
                {incomeSummary.totalIncome > 0 && (
                  <div style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {incomeSummary.list.map((cat, idx) => {
                      const itemVal = cat.convertedAmount !== undefined ? cat.convertedAmount : cat.amount;
                      const pct = incomeSummary.totalIncome > 0 ? Math.round((itemVal / incomeSummary.totalIncome) * 100) : 0;
                      const isSelected = activeIncomeChartCategory === (cat.id || cat.name) || activeIncomeChartCategory === cat.name;

                      return (
                        <div 
                          key={cat.id || idx} 
                          onClick={() => handleOpenDrillDown(cat, 'inflow')}
                          style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '6px',
                            cursor: 'pointer', 
                            padding: '10px 12px', 
                            borderRadius: '10px', 
                            background: isSelected ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)',
                            border: isSelected ? `1px solid ${cat.color}66` : '1px solid var(--border-glass)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={() => setActiveIncomeChartCategory(cat.id || cat.name)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '600', color: 'var(--text-main)' }}>
                              <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: cat.color, display: 'inline-block', flexShrink: 0 }} />
                              <span>{cat.icon} {cat.displayName || cat.name}</span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700' }}>
                              <span style={{ color: 'var(--color-inflow)' }}>
                                {formatMoney(Math.abs(cat.amount || 0), cat.currency || breakdownCurrency)}
                              </span>
                              <span style={{ fontSize: '10px', fontWeight: '700', color: cat.color, background: `${cat.color}18`, padding: '2px 6px', borderRadius: '8px' }}>
                                {pct}%
                              </span>
                            </div>
                          </div>

                          <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                            <div 
                              style={{ 
                                width: `${Math.min(100, pct)}%`, 
                                height: '100%', 
                                background: cat.color, 
                                borderRadius: '2px', 
                                transition: 'width 0.4s ease' 
                              }} 
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}


            {/* Quick-Tap Expense & Income Tiles at Top of Mobile / Dashboard Screen */}
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ marginBottom: '10px', fontSize: '13px', color: 'var(--color-outflow)' }}>⚡ Quick-Tap Expense Log</h3>
              <div className="quick-tap-grid">
                {getTopCategories('OUTFLOW', categories, 5).map(cat => (
                  <button
                    key={cat.category_id}
                    className="category-btn"
                    onClick={() => {
                      setTxCategory(cat.name);
                      setTxIsShared(false);
                      setActiveModal('outflow');
                    }}
                  >
                    <span style={{ fontSize: '20px', marginBottom: '4px' }}>{cat.symbol}</span>
                    <span style={{ fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'center' }}>
                      {cat.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '10px', fontSize: '13px', color: 'var(--color-inflow)' }}>⚡ Quick-Tap Income Log</h3>
              <div className="quick-tap-grid">
                {getTopCategories('INFLOW', categories, 5).map(cat => (
                  <button
                    key={cat.category_id}
                    className="category-btn"
                    onClick={() => {
                      setTxCategory(cat.name);
                      setTxIsShared(false);
                      setActiveModal('inflow');
                    }}
                  >
                    <span style={{ fontSize: '20px', marginBottom: '4px' }}>{cat.symbol}</span>
                    <span style={{ fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%', textAlign: 'center' }}>
                      {cat.name}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Recent feed */}
            <div>
              <h3>Recent Transactions</h3>
              <div className="glass-panel" style={{ marginTop: '12px', overflow: 'hidden' }}>
                {activeTxsFiltered.length === 0 ? (
                  <EmptyState
                    type="transactions"
                    title="No Transactions Recorded"
                    description="You have no recorded transactions for this period."
                    actionLabel="Add Transaction"
                    onAction={() => setActiveModal('outflow')}
                  />
                ) : (
                  <>
                    {activeTxsFiltered.slice(0, dashboardTxsLimit).map(tx => {
                      const isTransfer = isTransferTransaction(tx);
                      const dir = isTransfer ? getTransferDirection(tx, accounts) : 'none';
                      const isSelfTransfer = dir === 'self';
                      const isInwardTransfer = dir === 'inward';
                      const isOutwardTransfer = dir === 'outward';

                      const isCcPayment = tx.description.toLowerCase().includes('credit card-bill payment') || tx.description.toLowerCase().includes('credit card payment:') || tx.description.toLowerCase().includes('card payment');
                      const matchedCat = findCategoryByName(categories, tx.category_name);
                      const isGlobalInflow = GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (tx.category_name || '').trim().toLowerCase());
                      const isOut = isOutwardTransfer || (!isInwardTransfer && !isSelfTransfer && !isCcPayment && (matchedCat ? matchedCat.type === 'OUTFLOW' : !isGlobalInflow));

                      let txColor = 'var(--text-primary)';
                      let txSign = '';
                      if (isSelfTransfer) {
                        txColor = 'var(--accent-purple)';
                        txSign = '';
                      } else if (isOut) {
                        txColor = 'var(--color-outflow)';
                        txSign = '-';
                      } else {
                        txColor = 'var(--color-inflow)';
                        txSign = '+';
                      }

                      const { amount: displayAmt, isSplitPortion } = getDisplayAmount(tx);
                      const isMirrorTx = !!tx.source_transaction_id;

                      let badgeText = null;
                      let badgeColor = 'var(--accent-cyan)';
                      let badgeBg = 'rgba(0,245,212,0.15)';
                      if (isCcPayment) {
                        badgeText = 'CC Payment';
                        badgeColor = 'var(--accent-purple)';
                        badgeBg = 'rgba(168,85,247,0.15)';
                      } else if (isInwardTransfer) {
                        badgeText = 'Incoming';
                        badgeColor = 'var(--color-inflow)';
                        badgeBg = 'rgba(0,245,212,0.15)';
                      } else if (isOutwardTransfer) {
                        badgeText = 'Outgoing';
                        badgeColor = 'var(--color-outflow)';
                        badgeBg = 'rgba(255,71,87,0.15)';
                      } else if (isSelfTransfer) {
                        badgeText = 'Transfer';
                        badgeColor = 'var(--accent-purple)';
                        badgeBg = 'rgba(168,85,247,0.15)';
                      }

                      return (
                        <div key={tx.transaction_id} className="tx-item" onClick={() => setSelectedTxDetail(tx)} style={{ cursor: 'pointer' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '14px', fontWeight: '600' }}>{tx.description}</span>
                              {badgeText && <span style={{ fontSize: '8px', padding: '1px 4px', background: badgeBg, color: badgeColor, borderRadius: '4px' }}>{badgeText}</span>}
                              {(tx.is_shared === true || tx.is_shared === 1 || tx.is_shared === 'true') && !isCcPayment && !isMirrorTx && !isInwardTransfer && (
                                <span style={{ fontSize: '8px', padding: '1px 4px', background: 'rgba(0,245,212,0.15)', color: 'var(--accent-cyan)', borderRadius: '4px' }}>Shared</span>
                              )}
                            </div>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              {matchedCat?.symbol || (isTransfer ? '🔄' : '💸')} {matchedCat?.name || tx.category_name} • {formatDateTime(tx.date)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontSize: '14px', fontWeight: '700', color: txColor }}>
                                {txSign}{formatMoney(displayAmt, tx.currency)}
                              </span>
                              {isSplitPortion && <span style={{ fontSize: '9px', color: 'var(--text-muted)', display: 'block' }}>your share</span>}
                            </div>
                            <button title="Edit Transaction" onClick={(e) => { e.stopPropagation(); handleStartEdit(tx); }} style={{ background: 'transparent', border: 'none', color: 'var(--accent-cyan)', opacity: 0.8, cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}>
                              <Edit2 size={14} />
                            </button>
                            <button title="Delete Transaction" onClick={(e) => { e.stopPropagation(); handleDeleteTx(tx.transaction_id); }} style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.4)', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {activeTxsFiltered.length > dashboardTxsLimit && (
                      <button
                        onClick={() => setDashboardTxsLimit(l => l + 10)}
                        style={{
                          width: '100%',
                          padding: '14px',
                          background: 'rgba(255,255,255,0.02)',
                          border: 'none',
                          borderTop: '1px solid var(--border-glass)',
                          color: 'var(--accent-cyan)',
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'var(--transition-smooth)'
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                      >
                        Load More ({activeTxsFiltered.length - dashboardTxsLimit} remaining)
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

          </div>
        )}

        {/* VIEW: ACCOUNTS */}
        {activeTab === 'accounts' && (
          activeAccount ? (
            /* Render Account Details View */
            <div className="animate-fade-in" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '10px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px' }}>{activeAccount.name}</h2>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {activeAccount.type === 'CREDIT_CARD' ? 'Credit Card' : activeAccount.type} • {activeAccount.currency}
                  </span>
                </div>
                <button 
                  onClick={() => { setActiveAccount(null); }} 
                  style={{ background: 'transparent', border: 'none', color: 'var(--accent-cyan)', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  ← Back to List
                </button>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <h3 style={{ fontSize: '13px', marginBottom: '4px', margin: 0, color: 'var(--text-muted)' }}>
                    {activeAccount.type === 'CREDIT_CARD' ? 'Owed Balance' : 'Current Balance'}
                  </h3>
                  <span style={{ fontSize: '22px', fontWeight: '800', color: 'var(--text-main)' }}>
                    {formatMoney(
                      activeAccount.type === 'CREDIT_CARD' ? -parseFloat(activeAccount.current_balance) : parseFloat(activeAccount.current_balance),
                      activeAccount.currency
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setTxAccountId(activeAccount.account_id);
                      setTxCurrency(activeAccount.currency);
                      setTxCategory(outflowCats[0]?.name || 'Food');
                      setTxIsShared(false);
                      setTxPayerType('you');
                      setActiveModal('outflow');
                    }}
                    className="btn-primary"
                    style={{
                      padding: '6px 14px',
                      fontSize: '12px',
                      borderRadius: '8px',
                      background: 'var(--color-outflow)',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    ➕ Add Expense
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setTxAccountId(activeAccount.account_id);
                      setTxCurrency(activeAccount.currency);
                      setTxCategory(inflowCats[0]?.name || 'Salary');
                      setTxIsShared(false);
                      setActiveModal('inflow');
                    }}
                    className="btn-primary"
                    style={{
                      padding: '6px 14px',
                      fontSize: '12px',
                      borderRadius: '8px',
                      background: 'var(--color-inflow)',
                      color: '#000000',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    📥 Add Income
                  </button>
                </div>
              </div>

              {activeAccount.type === 'CREDIT_CARD' && activeAccount.credit_limit > 0 && (
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginTop: '4px' }}>
                  Available Limit: {formatMoney(parseFloat(activeAccount.credit_limit) - parseFloat(activeAccount.current_balance), activeAccount.currency)} (Limit: {formatMoney(parseFloat(activeAccount.credit_limit), activeAccount.currency)})
                </span>
              )}

              {activeAccount.name.toLowerCase().includes('external debts') && (
                  <div className="glass-panel" style={{ marginTop: '16px', padding: '16px', borderLeft: '4px solid var(--accent-cyan)' }}>
                    <h4 style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Contributing Members
                    </h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {(() => {
                        const contributors = friends.map(friend => {
                          const bal = friend.balances?.find(b => b.currency === activeAccount.currency);
                          return {
                            friend,
                            amount: bal ? parseFloat(bal.amount) : 0
                          };
                        }).filter(c => Math.abs(c.amount) > 0.0001);

                        if (contributors.length === 0) {
                          return (
                            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, fontStyle: 'italic' }}>
                              No active contributions to this balance.
                            </p>
                          );
                        }

                        return contributors.map(({ friend, amount }) => {
                          const friendName = friend.nickname || friend.display_name || friend.email.split('@')[0];
                          const isOwed = amount > 0;
                          return (
                            <div key={friend.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                              <span style={{ color: 'var(--text-main)', fontWeight: '500' }}>{friendName}</span>
                              <span style={{ fontWeight: '600', color: isOwed ? 'var(--color-inflow)' : 'var(--color-outflow)' }}>
                                {isOwed ? 'owes you ' : 'you owe '}
                                {formatMoney(Math.abs(amount), activeAccount.currency)}
                              </span>
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '13px', margin: 0 }}>Recent Transactions</h3>
                  <button 
                    onClick={() => openExportModal(activeAccount.type === 'CREDIT_CARD' ? 'credit_card' : 'account', activeAccount)} 
                    className="btn-secondary" 
                    style={{ padding: '4px 10px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', height: 'auto', width: 'auto' }}
                  >
                    <Download size={12} /> Export
                  </button>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                  {(() => {
                    const accountTxs = transactions.filter(tx => tx.paid_by_account_id === activeAccount.account_id || tx.transfer_to_account_id === activeAccount.account_id);
                    const sorted = [...accountTxs].sort((a, b) => new Date(b.date) - new Date(a.date));
                    const visible = sorted.slice(0, activeItemTxLimit);
                    const runningBalances = computeAccountRunningBalances(activeAccount, transactions);

                    if (visible.length === 0) {
                      return <p style={{ color: 'var(--text-muted)', fontSize: '12px' }}>No transactions recorded for this account.</p>;
                    }

                    return (
                      <>
                        {visible.map(tx => {
                          const isTransfer = isTransferTransaction(tx) || (tx.description && (tx.description.toLowerCase().includes('settlement') || tx.description.toLowerCase().includes('transfer')));
                          const isCcPayment = tx.description.toLowerCase().includes('credit card-bill payment') || tx.description.toLowerCase().includes('credit card payment:') || tx.description.toLowerCase().includes('card payment');
                          const sAcc = accounts.find(a => a.account_id === tx.paid_by_account_id);
                          const dAcc = accounts.find(a => a.account_id === tx.transfer_to_account_id);
                          const isDeposit = !isTransfer && (tx.category_name === 'Deposits' || GLOBAL_INFLOW_CATEGORIES.includes(tx.category_name) || categories.some(c => c.name === tx.category_name && c.type === 'INFLOW'));
                          
                          const tfDetails = isTransfer ? getTransferDetails(tx, activeAccount) : null;

                          let txColor = 'var(--text-main)';
                          let txPrefix = '';
                          let displayAmount = parseFloat(tx.total_amount || 0);
                          let displayCurrency = tx.currency;

                          if (isTransfer && tfDetails) {
                            txPrefix = tfDetails.accountPrefix;
                            txColor = tfDetails.accountColor;
                            displayAmount = tfDetails.accountDisplayAmount;
                            displayCurrency = tfDetails.accountDisplayCurrency;
                          } else if (isDeposit) {
                            txColor = 'var(--color-inflow)';
                            txPrefix = '+';
                          } else {
                            txColor = 'var(--color-outflow)';
                            txPrefix = '-';
                          }
                          const txRunningBal = runningBalances[tx.transaction_id];
                          const badge = getCategoryBadge(tx.category_name, tx.description);
                          const IconComp = badge.icon;
                          const { monthShort, day } = getDateTokens(tx.date);

                          let subtitleText = '';
                          if (isCcPayment) {
                            subtitleText = `💳 Credit Card Payment${isTransfer && sAcc && dAcc ? ` (${sAcc.name} → ${dAcc.name})` : (!isTransfer && sAcc ? ` (${getPayerName(tx)} → ${sAcc.name})` : '')}`;
                          } else if (isTransfer && tfDetails) {
                            if (sAcc && dAcc) {
                              subtitleText = `🔄 Transfer (${sAcc.name} → ${dAcc.name})`;
                            } else if (tfDetails.dir === 'inward') {
                              subtitleText = `🔄 Transfer (From ${tfDetails.counterparty})`;
                            } else if (tfDetails.dir === 'outward') {
                              subtitleText = `🔄 Transfer (To ${tfDetails.counterparty})`;
                            } else {
                              subtitleText = `🔄 Transfer`;
                            }
                          } else {
                            subtitleText = tx.category_name;
                          }
                          subtitleText += ` • ${formatDateTime(tx.date)}`;

                          return (
                            <div 
                              key={tx.transaction_id} 
                              className="glass-panel" 
                              onClick={() => setSelectedTxDetail(tx)} 
                              style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '12px 14px',
                                borderRadius: '14px',
                                border: '1px solid var(--border-glass)',
                                cursor: 'pointer',
                                gap: '12px',
                                transition: 'transform 0.15s ease, background 0.15s ease'
                              }}
                            >
                              {/* Left: Date + Category Icon */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
                                {/* Date Block */}
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '34px',
                                  flexShrink: 0
                                }}>
                                  <span style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600', lineHeight: 1 }}>
                                    {monthShort}
                                  </span>
                                  <span style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-main)', lineHeight: 1.2 }}>
                                    {day}
                                  </span>
                                </div>

                                {/* Category Square Badge */}
                                <div style={{
                                  width: '42px',
                                  height: '42px',
                                  borderRadius: '12px',
                                  background: badge.bg,
                                  border: `1px solid ${badge.border}`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}>
                                  <IconComp size={20} color={badge.color} />
                                </div>

                                {/* Description & Subtitle */}
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{
                                    fontWeight: '700',
                                    fontSize: '14px',
                                    color: 'var(--text-main)',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>
                                    {tx.description || 'Transaction'}
                                  </div>
                                  <div style={{
                                    fontSize: '11px',
                                    color: 'var(--text-muted)',
                                    marginTop: '2px',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}>
                                    {subtitleText}
                                  </div>
                                </div>
                              </div>

                              {/* Right: Amount & Running Balance */}
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: '2px' }}>
                                <span style={{ fontSize: '14px', fontWeight: '800', color: txColor }}>
                                  {txPrefix}{formatMoney(displayAmount, displayCurrency)}
                                </span>
                                {txRunningBal !== undefined && (
                                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>
                                    Balance: {formatMoney(txRunningBal, activeAccount.currency)}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {sorted.length > activeItemTxLimit && (
                          <button 
                            onClick={() => setActiveItemTxLimit(prev => prev + 10)} 
                            className="btn-secondary" 
                            style={{ marginTop: '10px', padding: '8px', fontSize: '12px', width: '100%' }}
                          >
                            Load More Transactions
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          ) : (
            <div className="animate-fade-in" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <h2>Cash & Bank Accounts</h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => openExportModal('accounts_summary', { name: 'Cash & Bank Accounts' })}
                    className="btn-secondary"
                    style={{ padding: '8px 14px', borderRadius: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Download size={14} /> Export Accounts
                  </button>
                  <button onClick={() => setActiveModal('account_create')} className="btn-primary" style={{ padding: '8px 16px', borderRadius: '12px', fontSize: '13px' }}>
                    Create Account
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {(() => {
                  const activeAccs = accounts.filter(acc => acc.type !== 'CREDIT_CARD' && acc.is_active !== false && acc.is_active !== 0 && acc.is_active !== '0' && !acc.name.toLowerCase().includes('external debts') && !acc.name.toLowerCase().includes('external receivables'));
                  if (activeAccs.length === 0) {
                    return <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No active bank accounts added yet.</p>;
                  }
                  
                  const prefs = preferredCurrencies && preferredCurrencies.length > 0 ? preferredCurrencies : syncEngine.getLocal('dhudd_cache_preferred_currencies', ['AED']);
                  const preferredCurrencySet = new Set(Array.isArray(prefs) ? prefs : (typeof prefs === 'string' ? JSON.parse(prefs) : ['AED']));
                  const currencies = [...new Set(activeAccs.map(a => a.currency))].sort((a, b) => {
                    if (preferredCurrencySet.has(a) && !preferredCurrencySet.has(b)) return -1;
                    if (!preferredCurrencySet.has(a) && preferredCurrencySet.has(b)) return 1;
                    return a.localeCompare(b);
                  });

                  return currencies.map(curr => {
                    const currAccs = activeAccs.filter(a => a.currency === curr);
                    const allAccsInCurr = accounts.filter(a => a.currency === curr && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && a.include_in_total !== false && a.include_in_total !== 0 && a.include_in_total !== '0');
                    const overallBalance = allAccsInCurr.reduce((acc, a) => {
                      const bal = parseFloat(a.current_balance || 0);
                      return acc + (a.type === 'CREDIT_CARD' ? -bal : bal);
                    }, 0);
                    
                    return (
                      <div key={curr} style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <h3 style={{ fontSize: '15px', margin: 0, color: 'var(--text-main)', fontWeight: '600' }}>{curr} Accounts</h3>
                          <div style={{ textAlign: 'right' }}>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>Overall Balance ({curr})</span>
                            <span style={{ fontSize: '14px', fontWeight: 'bold', color: overallBalance >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)' }}>
                              {formatMoney(overallBalance, curr)}
                            </span>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {currAccs.map(acc => {
                            const bal = parseFloat(acc.current_balance);
                            const isDefault = acc.is_default === true || acc.is_default === 1 || acc.is_default === '1';
                            
                            return (
                              <div key={acc.account_id} className="glass-panel" onClick={() => { setActiveAccount(acc); setActiveItemTxLimit(10); }} style={{
                                padding: '16px', borderLeft: `4px solid ${isDefault ? 'var(--accent-cyan)' : 'transparent'}`, cursor: 'pointer', position: 'relative'
                              }}>
                                <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <div>
                                    <span style={{ fontWeight: '700', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      {acc.name}
                                    </span>
                                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>{acc.type}</span>
                                  </div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <span style={{ fontSize: '16px', fontWeight: '700' }}>{formatMoney(bal, acc.currency)}</span>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); openExportModal('account', acc); }}
                                      style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: 'var(--text-muted)', opacity: 0.7,
                                        padding: '4px', display: 'flex', alignItems: 'center'
                                      }}
                                      title="Export Account Transactions"
                                    >
                                      <Download size={16} />
                                    </button>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleSetDefaultAccount(acc.account_id); }}
                                      style={{
                                        background: 'none', border: 'none', cursor: 'pointer',
                                        color: isDefault ? 'var(--accent-cyan)' : 'var(--text-muted)',
                                        opacity: isDefault ? 1 : 0.5,
                                        padding: '4px', display: 'flex', alignItems: 'center'
                                      }}
                                      title={isDefault ? 'Default Account' : 'Set as Default'}
                                    >
                                      <Star size={18} fill={isDefault ? 'var(--accent-cyan)' : 'none'} />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
                {(() => {
                  const disabledAccs = accounts.filter(acc => acc.type !== 'CREDIT_CARD' && (acc.is_active === false || acc.is_active === 0 || acc.is_active === '0'));
                  if (disabledAccs.length > 0) {
                    return (
                      <details style={{ marginTop: '10px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: 'var(--text-muted)' }}>Disabled Accounts ({disabledAccs.length})</summary>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '14px' }}>
                          {disabledAccs.map(acc => {
                            const bal = parseFloat(acc.current_balance);
                            return (
                              <div key={acc.account_id} className="glass-panel" onClick={() => { setActiveAccount(acc); setActiveItemTxLimit(10); }} style={{
                                padding: '16px', borderLeft: '4px solid #666', cursor: 'pointer', opacity: 0.6
                              }}>
                                <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between' }}>
                                  <div>
                                    <span style={{ fontWeight: '700' }}>{acc.name}</span>
                                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>{acc.type} • {acc.currency} (Disabled)</span>
                                  </div>
                                  <span style={{ fontSize: '16px', fontWeight: '700' }}>{formatMoney(bal, acc.currency)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )
        )}

        {/* VIEW: DEDICATED CREDIT CARD COMMUNITY PAGE */}
        {activeCommunityCard && (
          <CardCommunityView
            masterCardId={activeCommunityCard.card_id}
            cardData={activeCommunityCard}
            onBack={() => setActiveCommunityCard(null)}
            showToast={showToast}
          />
        )}

        {/* VIEW: CREDIT CARDS */}
        {activeTab === 'credit_cards' && !activeCommunityCard && (
          activeAccount ? (
            /* Render Credit Card Details View */
            <div className="animate-fade-in" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '10px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px' }}>{activeAccount.name}</h2>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Credit Card • {activeAccount.currency}
                  </span>
                </div>
                <button 
                  onClick={() => { setActiveAccount(null); }} 
                  style={{ background: 'transparent', border: 'none', color: 'var(--accent-cyan)', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  ← Back to List
                </button>
              </div>

              {/* Top Credit Card Status & Target Progress Card */}
              {(() => {
                const bal = parseFloat(activeAccount.current_balance);
                const limit = parseFloat(activeAccount.credit_limit || 0);
                const utilization = limit > 0 ? (bal / limit) * 100 : 0;
                const target = parseFloat(activeAccount.monthly_spend_target || 0);
                const stmtInfo = getCardStatementInfo(activeAccount, transactions);
                const { amount: monthlySpend, cycleStart, cycleEnd } = getMonthlySpend(
                  activeAccount.account_id,
                  activeAccount.monthly_spend_target_type,
                  activeAccount.statement_day
                );
                const targetPct = target > 0 ? (monthlySpend / target) * 100 : 0;
                const targetRemaining = Math.max(0, target - monthlySpend);
                const targetColor = targetPct >= 100 ? 'var(--color-inflow)' : (targetPct >= 50 ? '#ffb03a' : 'var(--color-outflow)');
                const formatDateShort = (date) => {
                  if (!date) return '';
                  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                };

                const isDefault = activeAccount.is_default === true || activeAccount.is_default === 1 || activeAccount.is_default === '1';

                return (
                  <div className="glass-panel" style={{ 
                    padding: '20px', 
                    borderRadius: '16px', 
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    background: 'linear-gradient(145deg, rgba(30, 32, 54, 0.7), rgba(18, 20, 36, 0.85))',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                  }}>
                    {/* Header Row: Card Identity & Quick Action Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, rgba(165,94,234,0.25), rgba(84,160,255,0.25))',
                          border: '1px solid rgba(165,94,234,0.4)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--accent-purple)'
                        }}>
                          <CreditCard size={22} />
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text-main)' }}>
                              {activeAccount.name}
                            </span>
                            {isDefault && (
                              <span style={{ fontSize: '10px', background: 'rgba(165,94,234,0.2)', color: 'var(--accent-purple)', padding: '2px 6px', borderRadius: '4px', fontWeight: '700' }}>
                                DEFAULT
                              </span>
                            )}
                          </div>
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            Credit Card • {activeAccount.currency}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => {
                            setPayCcCard(activeAccount);
                            const suggestedPay = stmtInfo.remainingStatementDue > 0 ? stmtInfo.remainingStatementDue : bal;
                            setPayCcAmount(formatInputValue(suggestedPay));
                            setPayCcSourceAccountId(accounts.find(a => a.type !== 'CREDIT_CARD' && a.currency === activeAccount.currency)?.account_id || accounts.find(a => a.type !== 'CREDIT_CARD')?.account_id || '');
                            setPayCcModalOpen(true);
                          }}
                          className="btn-primary"
                          style={{
                            padding: '8px 16px',
                            fontSize: '12px',
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, var(--accent-purple), #54a0ff)',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            fontWeight: '800',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 4px 14px rgba(165,94,234,0.35)'
                          }}
                        >
                          💳 Pay Bill
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setTxAccountId(activeAccount.account_id);
                            setTxCurrency(activeAccount.currency);
                            setTxCategory(outflowCats[0]?.name || 'Food');
                            setTxIsShared(false);
                            setTxPayerType('you');
                            setActiveModal('outflow');
                          }}
                          style={{
                            padding: '8px 12px',
                            fontSize: '12px',
                            borderRadius: '10px',
                            background: 'rgba(255, 107, 107, 0.12)',
                            color: 'var(--color-outflow)',
                            border: '1px solid rgba(255, 107, 107, 0.25)',
                            cursor: 'pointer',
                            fontWeight: '700',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          ➕ Expense
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setTxAccountId(activeAccount.account_id);
                            setTxCurrency(activeAccount.currency);
                            setTxCategory('Deposits');
                            setTxIsShared(false);
                            setActiveModal('inflow');
                          }}
                          style={{
                            padding: '8px 12px',
                            fontSize: '12px',
                            borderRadius: '10px',
                            background: 'rgba(0, 245, 212, 0.1)',
                            color: 'var(--accent-cyan)',
                            border: '1px solid rgba(0, 245, 212, 0.25)',
                            cursor: 'pointer',
                            fontWeight: '700',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          📥 Income
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setNewCcBalanceValue(formatInputValue(-bal));
                            setAdjustCcBalanceOpen(true);
                          }}
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '10px',
                            padding: '8px 10px',
                            fontSize: '12px',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                          title="Manually adjust balance"
                        >
                          ✏️ Adjust
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (!activeAccount.master_card_id) {
                              setCardToConnect({
                                account: activeAccount,
                                targetFeature: 'community'
                              });
                              return;
                            }
                            setActiveCommunityCard({
                              card_id: activeAccount.master_card_id,
                              card_name: activeAccount.master_card_name || activeAccount.name,
                              bank_name: activeAccount.master_bank_name || 'Bank',
                              card_tier: activeAccount.master_card_tier,
                              is_cardholder: true
                            });
                          }}
                          style={{
                            background: activeAccount.master_card_id ? 'rgba(0, 245, 212, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                            border: activeAccount.master_card_id ? '1px solid rgba(0, 245, 212, 0.3)' : '1px solid var(--border-glass)',
                            borderRadius: '10px',
                            padding: '8px 12px',
                            fontSize: '12px',
                            color: activeAccount.master_card_id ? 'var(--accent-cyan)' : 'var(--text-muted)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontWeight: '700'
                          }}
                          title={activeAccount.master_card_id ? "Open dedicated Cardholder Community" : "Connect to directory to access Community"}
                        >
                          💬 Community {!activeAccount.master_card_id && <span style={{ fontSize: '10px', padding: '1px 5px', borderRadius: '4px', background: 'rgba(255,255,255,0.08)', color: 'var(--text-muted)' }}>Unlinked</span>}
                        </button>
                      </div>
                    </div>

                    {/* Hero Financial Numbers (3-Column Clean Tile Grid) */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: '12px',
                      marginBottom: '16px'
                    }}>
                      {/* 1. Total Current Owed Balance */}
                      <div style={{
                        padding: '14px',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.06)'
                      }}>
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '700', display: 'block' }}>
                          Total Current Owed
                        </span>
                        <span style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-main)', display: 'block', marginTop: '2px' }}>
                          {formatMoney(-bal, activeAccount.currency)}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', display: 'block' }}>
                          All cycles combined
                        </span>
                      </div>

                      {/* 2. Last Statement Due */}
                      <div style={{
                        padding: '14px',
                        borderRadius: '12px',
                        background: stmtInfo.remainingStatementDue === 0 && stmtInfo.lastStatementBalance > 0 ? 'rgba(43, 203, 186, 0.08)' : 'rgba(165, 94, 234, 0.08)',
                        border: stmtInfo.remainingStatementDue === 0 && stmtInfo.lastStatementBalance > 0 ? '1px solid rgba(43, 203, 186, 0.25)' : '1px solid rgba(165, 94, 234, 0.25)'
                      }}>
                        <span style={{ fontSize: '10px', color: stmtInfo.remainingStatementDue === 0 && stmtInfo.lastStatementBalance > 0 ? 'var(--color-inflow)' : 'var(--accent-purple)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '700', display: 'block' }}>
                          Last Statement Due
                        </span>
                        <span style={{ 
                          fontSize: '20px', 
                          fontWeight: '800', 
                          color: stmtInfo.remainingStatementDue === 0 && stmtInfo.lastStatementBalance > 0 ? 'var(--color-inflow)' : (stmtInfo.remainingStatementDue > 0 ? 'var(--accent-purple)' : 'var(--text-main)'),
                          display: 'block',
                          marginTop: '2px'
                        }}>
                          {stmtInfo.remainingStatementDue === 0 && stmtInfo.lastStatementBalance > 0 ? '✓ Paid in Full' : formatMoney(stmtInfo.remainingStatementDue, activeAccount.currency)}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', display: 'block' }}>
                          Due: Monthly {activeAccount.due_day} {stmtInfo.dueDaysLeft !== null ? (stmtInfo.dueDaysLeft >= 0 ? `(${stmtInfo.dueDaysLeft}d left)` : `(${Math.abs(stmtInfo.dueDaysLeft)}d overdue)`) : ''}
                        </span>
                      </div>

                      {/* 3. Available Credit */}
                      <div style={{
                        padding: '14px',
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid rgba(255, 255, 255, 0.06)'
                      }}>
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '700', display: 'block' }}>
                          Available Credit
                        </span>
                        <span style={{ fontSize: '20px', fontWeight: '800', color: 'var(--accent-cyan)', display: 'block', marginTop: '2px' }}>
                          {formatMoney(limit - bal, activeAccount.currency)}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', display: 'block' }}>
                          Limit: {formatMoney(limit, activeAccount.currency)} ({Math.round(utilization)}% used)
                        </span>
                      </div>
                    </div>

                    {/* Sleek Utilization Mini-Bar */}
                    <div style={{ marginBottom: '14px' }}>
                      <div style={{ width: '100%', height: '5px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                        <div style={{ 
                          width: `${Math.min(utilization, 100)}%`, 
                          height: '100%', 
                          background: utilization >= 80 ? 'var(--color-outflow)' : (utilization >= 50 ? '#ffb03a' : 'var(--accent-purple)'),
                          borderRadius: '3px',
                          boxShadow: utilization >= 80 ? '0 0 8px rgba(255, 71, 87, 0.6)' : 'none'
                        }} />
                      </div>
                    </div>

                    {/* Expandable Accordions for Secondary Details */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {/* Accordion 1: Statement & Billing Cycle Breakdown */}
                      <div style={{
                        borderRadius: '10px',
                        border: '1px solid rgba(255, 255, 255, 0.06)',
                        background: 'rgba(255, 255, 255, 0.02)',
                        overflow: 'hidden'
                      }}>
                        <button
                          type="button"
                          onClick={() => toggleDetailSection('cycle')}
                          style={{
                            width: '100%',
                            padding: '10px 14px',
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            color: 'var(--text-main)',
                            fontSize: '12px',
                            fontWeight: '600'
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>💳 Statement & Billing Cycle Breakdown</span>
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.05)', padding: '1px 6px', borderRadius: '4px' }}>
                              Closing: Monthly {activeAccount.statement_day} • Due: Monthly {activeAccount.due_day}
                            </span>
                          </span>
                          <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                            {expandedDetailSections.cycle ? 'Hide' : 'View Details'}
                            {expandedDetailSections.cycle ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                          </span>
                        </button>

                        {expandedDetailSections.cycle && (
                          <div style={{
                            padding: '12px 14px',
                            borderTop: '1px solid rgba(255, 255, 255, 0.04)',
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
                            gap: '12px',
                            background: 'rgba(0, 0, 0, 0.15)'
                          }}>
                            <div>
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block' }}>
                                Amount as per last statement
                              </span>
                              <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-main)', display: 'block', marginTop: '2px' }}>
                                {formatMoney(stmtInfo.lastStatementBalance, activeAccount.currency)}
                              </span>
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginTop: '1px' }}>
                                {stmtInfo.lastStatementDate ? `Closed on ${formatDateShort(stmtInfo.lastStatementDate)}` : `Monthly ${activeAccount.statement_day}`}
                              </span>
                            </div>

                            <div>
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block' }}>
                                Payments after statement
                              </span>
                              <span style={{ fontSize: '15px', fontWeight: '700', color: stmtInfo.paymentsAfterStatement > 0 ? 'var(--color-inflow)' : 'var(--text-main)', display: 'block', marginTop: '2px' }}>
                                {stmtInfo.paymentsAfterStatement > 0 ? `+${formatMoney(stmtInfo.paymentsAfterStatement, activeAccount.currency)}` : formatMoney(0, activeAccount.currency)}
                              </span>
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginTop: '1px' }}>
                                {stmtInfo.paymentsAfterCount > 0 ? `${stmtInfo.paymentsAfterCount} payment${stmtInfo.paymentsAfterCount === 1 ? '' : 's'} since statement` : 'Auto-resets on next statement'}
                              </span>
                            </div>

                            <div>
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', display: 'block' }}>
                                Next Statement Cycle
                              </span>
                              <span style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-main)', display: 'block', marginTop: '2px' }}>
                                {stmtInfo.nextStatementDate ? formatDateShort(stmtInfo.nextStatementDate) : `Monthly ${activeAccount.statement_day}`}
                              </span>
                              <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginTop: '1px' }}>
                                Unbilled spending adds to next statement
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Accordion 2: Monthly Spend Target (If target configured) */}
                      {target > 0 && (
                        <div style={{
                          borderRadius: '10px',
                          border: '1px solid rgba(255, 255, 255, 0.06)',
                          background: 'rgba(255, 255, 255, 0.02)',
                          overflow: 'hidden'
                        }}>
                          <button
                            type="button"
                            onClick={() => toggleDetailSection('target')}
                            style={{
                              width: '100%',
                              padding: '10px 14px',
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              color: 'var(--text-main)',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>🎯 Monthly Spend Target</span>
                              <span style={{ fontSize: '10px', color: targetColor, background: 'rgba(255,255,255,0.05)', padding: '1px 6px', borderRadius: '4px', fontWeight: '700' }}>
                                {Math.round(targetPct)}% • {formatMoney(monthlySpend, activeAccount.currency)} / {formatMoney(target, activeAccount.currency)}
                              </span>
                            </span>
                            <span style={{ color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                              {expandedDetailSections.target ? 'Hide' : 'View Details'}
                              {expandedDetailSections.target ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </span>
                          </button>

                          {expandedDetailSections.target && (
                            <div style={{
                              padding: '12px 14px',
                              borderTop: '1px solid rgba(255, 255, 255, 0.04)',
                              background: 'rgba(0, 0, 0, 0.15)'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '4px' }}>
                                <span>Progress: <strong>{formatMoney(monthlySpend, activeAccount.currency)}</strong> of {formatMoney(target, activeAccount.currency)}</span>
                                <span style={{ color: targetColor, fontWeight: 'bold' }}>
                                  {monthlySpend >= target ? '🎉 Target Achieved' : `${formatMoney(targetRemaining, activeAccount.currency)} Remaining`}
                                </span>
                              </div>
                              <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden', marginBottom: '6px' }}>
                                <div style={{ 
                                  width: `${Math.min(targetPct, 100)}%`, 
                                  height: '100%', 
                                  background: targetColor,
                                  borderRadius: '3px',
                                  transition: 'width 0.3s ease'
                                }} />
                              </div>
                              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                Calculation Mode: {activeAccount.monthly_spend_target_type === 'STATEMENT' ? 'Statement Cycle' : 'Calendar Month'} ({formatDateShort(cycleStart)} - {formatDateShort(cycleEnd)})
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Card Sub-Tabs: Overview & Transactions vs Rewards & Perks */}
              <div style={{ display: 'flex', gap: '8px', background: 'rgba(255, 255, 255, 0.04)', padding: '4px', borderRadius: '12px', width: 'fit-content', marginBottom: '8px' }}>
                <button
                  type="button"
                  onClick={() => setActiveCardSubTab('overview')}
                  style={{
                    padding: '7px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: activeCardSubTab === 'overview' ? 'var(--accent-purple)' : 'transparent',
                    color: activeCardSubTab === 'overview' ? 'white' : 'var(--text-muted)',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer'
                  }}
                >
                  📊 Overview & Transactions
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!activeAccount.master_card_id) {
                      setCardToConnect({
                        account: activeAccount,
                        targetFeature: 'rewards'
                      });
                      return;
                    }
                    setActiveCardSubTab('rewards');
                  }}
                  style={{
                    padding: '7px 16px',
                    borderRadius: '8px',
                    border: 'none',
                    background: activeCardSubTab === 'rewards' ? 'var(--accent-purple)' : 'transparent',
                    color: activeCardSubTab === 'rewards' ? 'white' : 'var(--text-muted)',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                  title={activeAccount.master_card_id ? "View Rewards & Perks" : "Connect to directory to access Rewards & Perks"}
                >
                  🎁 Rewards & Perks {!activeAccount.master_card_id && <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '4px', background: 'rgba(255,255,255,0.08)', color: 'var(--text-muted)' }}>Unlinked</span>}
                </button>
              </div>

              {activeCardSubTab === 'rewards' ? (
                <div className="glass-panel" style={{ padding: '24px', borderRadius: '16px', border: '1px solid var(--border-glass)' }}>
                  {activeAccount.master_card_id ? (
                    <CardRewardsAccordion
                      masterCardId={activeAccount.master_card_id}
                      cardName={activeAccount.master_card_name || activeAccount.name}
                      isCardholder={true}
                      showToast={showToast}
                    />
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', maxWidth: '440px', margin: '0 auto', padding: '16px 0', textAlign: 'center' }}>
                      <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(165, 94, 234, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-purple)' }}>
                        <Sparkles size={24} />
                      </div>
                      <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '800' }}>Directory Connection Required</h4>
                      <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5' }}>
                        This is a custom credit card. Connect "{activeAccount.name}" to the official UAE Credit Cards Directory to automatically view cashback rates, lounge passes, and perks.
                      </p>
                      <button
                        type="button"
                        onClick={() => setCardToConnect({ account: activeAccount, targetFeature: 'rewards' })}
                        className="btn-primary"
                        style={{ padding: '10px 20px', fontSize: '13px', fontWeight: '700', borderRadius: '10px', marginTop: '6px' }}
                      >
                        🔗 Select Card from Directory
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '13px', margin: 0 }}>Recent Transactions</h3>
                    <button 
                      onClick={() => openExportModal('credit_card', activeAccount)} 
                      className="btn-secondary" 
                      style={{ padding: '4px 10px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', height: 'auto', width: 'auto' }}
                    >
                      <Download size={12} /> Export
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                    {(() => {
                      const accountTxs = transactions.filter(tx => tx.paid_by_account_id === activeAccount.account_id || tx.transfer_to_account_id === activeAccount.account_id);
                      const sorted = [...accountTxs].sort((a, b) => new Date(b.date) - new Date(a.date));
                      const visible = sorted.slice(0, activeItemTxLimit);
                      const runningBalances = computeAccountRunningBalances(activeAccount, transactions);

                      if (visible.length === 0) {
                        return <p style={{ color: 'var(--text-muted)', fontSize: '12px' }}>No transactions recorded for this card.</p>;
                      }

                      return (
                        <>
                          {visible.map(tx => {
                            const isTransfer = isTransferTransaction(tx) || (tx.description && (tx.description.toLowerCase().includes('settlement') || tx.description.toLowerCase().includes('transfer')));
                            const isCcPayment = tx.description.toLowerCase().includes('credit card-bill payment') || tx.description.toLowerCase().includes('credit card payment:') || tx.description.toLowerCase().includes('card payment');
                            const sAcc = accounts.find(a => a.account_id === tx.paid_by_account_id);
                            const dAcc = accounts.find(a => a.account_id === tx.transfer_to_account_id);
                            const isDeposit = !isTransfer && tx.category_name === 'Deposits';
                            
                            const tfDetails = isTransfer ? getTransferDetails(tx, activeAccount) : null;

                            let txColor = 'var(--text-main)';
                            let txPrefix = '';
                            let displayAmount = parseFloat(tx.total_amount || 0);
                            let displayCurrency = tx.currency;

                            if (isTransfer && tfDetails) {
                              txPrefix = tfDetails.accountPrefix;
                              txColor = tfDetails.accountColor;
                              displayAmount = tfDetails.accountDisplayAmount;
                              displayCurrency = tfDetails.accountDisplayCurrency;
                            } else if (isDeposit) {
                              txColor = 'var(--color-inflow)';
                              txPrefix = '+';
                            } else {
                              txColor = 'var(--color-outflow)';
                              txPrefix = '-';
                            }
                            const txRunningBal = runningBalances[tx.transaction_id];
                            const badge = getCategoryBadge(tx.category_name, tx.description);
                            const IconComp = badge.icon;
                            const { monthShort, day } = getDateTokens(tx.date);

                            let subtitleText = '';
                            if (isCcPayment) {
                              subtitleText = `💳 Credit Card Payment${isTransfer && sAcc && dAcc ? ` (${sAcc.name} → ${dAcc.name})` : (!isTransfer && sAcc ? ` (${getPayerName(tx)} → ${sAcc.name})` : '')}`;
                            } else if (isTransfer && tfDetails) {
                              if (sAcc && dAcc) {
                                subtitleText = `🔄 Transfer (${sAcc.name} → ${dAcc.name})`;
                              } else if (tfDetails.dir === 'inward') {
                                subtitleText = `🔄 Transfer (From ${tfDetails.counterparty})`;
                              } else if (tfDetails.dir === 'outward') {
                                subtitleText = `🔄 Transfer (To ${tfDetails.counterparty})`;
                              } else {
                                subtitleText = `🔄 Transfer`;
                              }
                            } else {
                              subtitleText = tx.category_name;
                            }
                            subtitleText += ` • ${formatDateTime(tx.date)}`;

                            return (
                              <div 
                                key={tx.transaction_id} 
                                className="glass-panel" 
                                onClick={() => setSelectedTxDetail(tx)} 
                                style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '12px 14px',
                                  borderRadius: '14px',
                                  border: '1px solid var(--border-glass)',
                                  cursor: 'pointer',
                                  gap: '12px',
                                  transition: 'transform 0.15s ease, background 0.15s ease'
                                }}
                              >
                                {/* Left: Date + Category Icon */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0, flex: 1 }}>
                                  {/* Date Block */}
                                  <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '34px',
                                    flexShrink: 0
                                  }}>
                                    <span style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600', lineHeight: 1 }}>
                                      {monthShort}
                                    </span>
                                    <span style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-main)', lineHeight: 1.2 }}>
                                      {day}
                                    </span>
                                  </div>

                                  {/* Category Square Badge */}
                                  <div style={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '12px',
                                    background: badge.bg,
                                    border: `1px solid ${badge.border}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                  }}>
                                    <IconComp size={20} color={badge.color} />
                                  </div>

                                  {/* Description & Subtitle */}
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <div style={{
                                      fontWeight: '700',
                                      fontSize: '14px',
                                      color: 'var(--text-main)',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {tx.description || 'Transaction'}
                                    </div>
                                    <div style={{
                                      fontSize: '11px',
                                      color: 'var(--text-muted)',
                                      marginTop: '2px',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {subtitleText}
                                    </div>
                                  </div>
                                </div>

                                {/* Right: Amount & Running Balance */}
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: '2px' }}>
                                  <span style={{ fontSize: '14px', fontWeight: '800', color: txColor }}>
                                    {txPrefix}{formatMoney(displayAmount, displayCurrency)}
                                  </span>
                                  {txRunningBal !== undefined && (
                                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>
                                      Balance: {formatMoney(txRunningBal, activeAccount.currency)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                          {sorted.length > activeItemTxLimit && (
                            <button 
                              onClick={() => setActiveItemTxLimit(prev => prev + 10)} 
                              className="btn-secondary" 
                              style={{ marginTop: '10px', padding: '8px', fontSize: '12px', width: '100%' }}
                            >
                              Load More Transactions
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="animate-fade-in" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <h2>Credit Cards</h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => setIsExploreCardsOpen(true)}
                    className="btn-secondary"
                    style={{ padding: '8px 14px', borderRadius: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--accent-purple)', borderColor: 'rgba(165,94,234,0.4)', background: 'rgba(165,94,234,0.1)' }}
                  >
                    💳 Explore Cards
                  </button>
                  <button
                    type="button"
                    onClick={() => openExportModal('credit_cards_summary', { name: 'Credit Cards' })}
                    className="btn-secondary"
                    style={{ padding: '8px 14px', borderRadius: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Download size={14} /> Export Credit Cards
                  </button>
                  <button onClick={() => { setCreateCardMasterCardId(null); setActiveModal('account_create'); }} className="btn-primary" style={{ padding: '8px 16px', borderRadius: '12px', fontSize: '13px' }}>
                    Add Credit Card
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {(() => {
                  const activeAccs = accounts.filter(acc => acc.type === 'CREDIT_CARD' && acc.is_active !== false && acc.is_active !== 0 && acc.is_active !== '0');
                  if (activeAccs.length === 0) {
                    return <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No active credit cards added yet.</p>;
                  }
                  
                  const prefs = preferredCurrencies && preferredCurrencies.length > 0 ? preferredCurrencies : syncEngine.getLocal('dhudd_cache_preferred_currencies', ['AED']);
                  const preferredCurrencySet = new Set(Array.isArray(prefs) ? prefs : (typeof prefs === 'string' ? JSON.parse(prefs) : ['AED']));
                  const currencies = [...new Set(activeAccs.map(a => a.currency))].sort((a, b) => {
                    if (preferredCurrencySet.has(a) && !preferredCurrencySet.has(b)) return -1;
                    if (!preferredCurrencySet.has(a) && preferredCurrencySet.has(b)) return 1;
                    return a.localeCompare(b);
                  });

                  return currencies.map(curr => {
                    const currAccs = activeAccs.filter(a => a.currency === curr);
                    
                    return (
                      <div key={curr} style={{ marginBottom: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                          <h3 style={{ fontSize: '15px', margin: 0, color: 'var(--text-main)', fontWeight: '600' }}>{curr} Cards</h3>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                          {currAccs.map(acc => {
                            const bal = parseFloat(acc.current_balance);
                            const limit = parseFloat(acc.credit_limit || 0);
                            const utilization = limit > 0 ? (bal / limit) * 100 : 0;
                            const target = parseFloat(acc.monthly_spend_target || 0);
                            const stmtInfo = getCardStatementInfo(acc, transactions);
                            const { amount: monthlySpend, cycleStart, cycleEnd } = getMonthlySpend(
                              acc.account_id,
                              acc.monthly_spend_target_type,
                              acc.statement_day
                            );
                            const targetPct = target > 0 ? (monthlySpend / target) * 100 : 0;
                            const targetRemaining = Math.max(0, target - monthlySpend);
                            const targetColor = targetPct >= 100 ? 'var(--color-inflow)' : (targetPct >= 50 ? '#ffb03a' : 'var(--color-outflow)');
                            const formatDateShort = (date) => {
                              if (!date) return '';
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            };
                            const isDefault = acc.is_default === true || acc.is_default === 1 || acc.is_default === '1';
                            const isExpanded = !!expandedCardsList[acc.account_id];

                            return (
                              <div key={acc.account_id} className="glass-panel" onClick={() => { setActiveAccount(acc); setActiveItemTxLimit(10); }} style={{
                                padding: '16px 18px',
                                borderRadius: '14px',
                                background: 'linear-gradient(145deg, rgba(28, 30, 50, 0.7), rgba(16, 18, 32, 0.85))',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                borderLeft: isDefault ? '4px solid var(--accent-purple)' : '1px solid rgba(255, 255, 255, 0.08)',
                                boxShadow: utilization >= 80 ? '0 0 20px rgba(255, 71, 87, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.25)',
                                cursor: 'pointer',
                                position: 'relative',
                                transition: 'all 0.2s ease'
                              }}>
                                {/* Top Header: Identity & Balance / Actions */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <div style={{
                                      width: '36px',
                                      height: '36px',
                                      borderRadius: '10px',
                                      background: 'linear-gradient(135deg, rgba(165,94,234,0.2), rgba(84,160,255,0.2))',
                                      border: '1px solid rgba(165,94,234,0.3)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      color: 'var(--accent-purple)',
                                      flexShrink: 0
                                    }}>
                                      <CreditCard size={18} />
                                    </div>
                                    <div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ fontWeight: '800', fontSize: '15px', color: 'var(--text-main)' }}>
                                          {acc.name}
                                        </span>
                                        {isDefault && (
                                          <span style={{ fontSize: '9px', background: 'rgba(165,94,234,0.25)', color: 'var(--accent-purple)', padding: '1px 5px', borderRadius: '4px', fontWeight: '700' }}>
                                            DEFAULT
                                          </span>
                                        )}
                                      </div>
                                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                        Credit Card • {acc.currency}
                                      </span>
                                    </div>
                                  </div>

                                  {/* Right: Balances & Actions */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                      <span style={{ fontSize: '16px', fontWeight: '800', color: 'var(--text-main)' }}>
                                        {formatMoney(-bal, acc.currency)}
                                      </span>
                                      <span style={{ fontSize: '11px', color: 'var(--accent-cyan)', fontWeight: '600' }}>
                                        Avail: {formatMoney(limit - bal, acc.currency)}
                                      </span>
                                    </div>

                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPayCcCard(acc);
                                        const suggestedPay = stmtInfo.remainingStatementDue > 0 ? stmtInfo.remainingStatementDue : bal;
                                        setPayCcAmount(formatInputValue(suggestedPay));
                                        setPayCcSourceAccountId(accounts.find(a => a.type !== 'CREDIT_CARD' && a.currency === acc.currency)?.account_id || accounts.find(a => a.type !== 'CREDIT_CARD')?.account_id || '');
                                        setPayCcModalOpen(true);
                                      }}
                                      className="btn-primary"
                                      style={{
                                        padding: '6px 13px',
                                        fontSize: '11px',
                                        borderRadius: '8px',
                                        background: 'linear-gradient(135deg, var(--accent-purple), #54a0ff)',
                                        color: 'white',
                                        border: 'none',
                                        cursor: 'pointer',
                                        fontWeight: '700',
                                        boxShadow: '0 2px 8px rgba(165,94,234,0.3)'
                                      }}
                                    >
                                      💳 Pay Bill
                                    </button>

                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); openExportModal('credit_card', acc); }}
                                      style={{
                                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer',
                                        color: 'var(--text-muted)', borderRadius: '6px',
                                        padding: '5px', display: 'flex', alignItems: 'center'
                                      }}
                                      title="Export Card Transactions"
                                    >
                                      <Download size={14} />
                                    </button>

                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleSetDefaultAccount(acc.account_id); }}
                                      style={{
                                        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer',
                                        color: isDefault ? 'var(--accent-purple)' : 'var(--text-muted)',
                                        borderRadius: '6px',
                                        padding: '5px', display: 'flex', alignItems: 'center'
                                      }}
                                      title={isDefault ? 'Default Card' : 'Set as Default'}
                                    >
                                      <Star size={14} fill={isDefault ? 'var(--accent-purple)' : 'none'} />
                                    </button>
                                  </div>
                                </div>

                                {/* Utilization Bar */}
                                <div style={{ marginTop: '12px', marginBottom: '8px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', marginBottom: '3px' }}>
                                    <span>Credit Utilization</span>
                                    <span style={{ fontWeight: '700', color: utilization >= 80 ? 'var(--color-outflow)' : 'var(--text-muted)' }}>
                                      {Math.round(utilization)}% ({formatMoney(limit, acc.currency)} limit)
                                    </span>
                                  </div>
                                  <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                                    <div style={{
                                      width: `${Math.min(utilization, 100)}%`,
                                      height: '100%',
                                      background: utilization >= 80 ? 'var(--color-outflow)' : (utilization >= 50 ? '#ffb03a' : 'var(--accent-purple)'),
                                      borderRadius: '2px'
                                    }} />
                                  </div>
                                </div>

                                {/* Summary & Expand Toggle Bar */}
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginTop: '8px',
                                  paddingTop: '8px',
                                  borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                                  fontSize: '11px'
                                }}>
                                  <div>
                                    {stmtInfo.hasStatement ? (
                                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                                        <span style={{ color: 'var(--text-muted)' }}>Last Statement Due:</span>
                                        <strong style={{ color: stmtInfo.remainingStatementDue === 0 && stmtInfo.lastStatementBalance > 0 ? 'var(--color-inflow)' : (stmtInfo.remainingStatementDue > 0 ? 'var(--accent-purple)' : 'var(--text-main)') }}>
                                          {stmtInfo.remainingStatementDue === 0 && stmtInfo.lastStatementBalance > 0 ? '✓ Paid' : formatMoney(stmtInfo.remainingStatementDue, acc.currency)}
                                        </strong>
                                        {stmtInfo.dueDaysLeft !== null && stmtInfo.remainingStatementDue > 0 && (
                                          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                            (Monthly {acc.due_day})
                                          </span>
                                        )}
                                      </span>
                                    ) : (
                                      <span style={{ color: 'var(--text-muted)' }}>
                                        Closing: Monthly {acc.statement_day} • Due: Monthly {acc.due_day}
                                      </span>
                                    )}
                                  </div>

                                  <button
                                    type="button"
                                    onClick={(e) => toggleCardListExpand(acc.account_id, e)}
                                    style={{
                                      background: 'rgba(255,255,255,0.04)',
                                      border: '1px solid rgba(255,255,255,0.08)',
                                      borderRadius: '6px',
                                      padding: '3px 8px',
                                      color: 'var(--accent-cyan)',
                                      fontSize: '11px',
                                      fontWeight: '600',
                                      cursor: 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px'
                                    }}
                                  >
                                    <span>{isExpanded ? 'Less' : 'Details'}</span>
                                    {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                  </button>
                                </div>

                                {/* Expandable Details Drawer */}
                                {isExpanded && (
                                  <div style={{
                                    marginTop: '10px',
                                    padding: '12px',
                                    borderRadius: '10px',
                                    background: 'rgba(0, 0, 0, 0.25)',
                                    border: '1px solid rgba(255, 255, 255, 0.05)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '10px'
                                  }}>
                                    {/* Cycle Details Grid */}
                                    <div style={{
                                      display: 'grid',
                                      gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                                      gap: '8px',
                                      fontSize: '11px'
                                    }}>
                                      <div>
                                        <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '10px' }}>Amount as per last statement</span>
                                        <strong style={{ color: 'var(--text-main)' }}>{formatMoney(stmtInfo.lastStatementBalance, acc.currency)}</strong>
                                        <span style={{ display: 'block', fontSize: '9px', color: 'var(--text-muted)' }}>
                                          {stmtInfo.lastStatementDate ? `Closed ${formatDateShort(stmtInfo.lastStatementDate)}` : `Day ${acc.statement_day}`}
                                        </span>
                                      </div>

                                      <div>
                                        <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '10px' }}>Payments after statement</span>
                                        <strong style={{ color: stmtInfo.paymentsAfterStatement > 0 ? 'var(--color-inflow)' : 'var(--text-main)' }}>
                                          {stmtInfo.paymentsAfterStatement > 0 ? `+${formatMoney(stmtInfo.paymentsAfterStatement, acc.currency)}` : formatMoney(0, acc.currency)}
                                        </strong>
                                        <span style={{ display: 'block', fontSize: '9px', color: 'var(--text-muted)' }}>
                                          {stmtInfo.paymentsAfterCount > 0 ? `${stmtInfo.paymentsAfterCount} payment(s)` : 'Auto-resets next cycle'}
                                        </span>
                                      </div>

                                      <div>
                                        <span style={{ color: 'var(--text-muted)', display: 'block', fontSize: '10px' }}>Closing / Due Dates</span>
                                        <span style={{ color: 'var(--text-main)' }}>Closing: <strong>{acc.statement_day}th</strong></span>
                                        <span style={{ display: 'block', color: 'var(--text-main)' }}>Due: <strong>{acc.due_day}th</strong></span>
                                      </div>
                                    </div>

                                    {/* Target Progress (if configured) */}
                                    {target > 0 && (
                                      <div style={{
                                        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                                        paddingTop: '8px',
                                        fontSize: '11px'
                                      }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                                          <span style={{ color: 'var(--text-muted)' }}>Spend Target: <strong>{formatMoney(monthlySpend, acc.currency)}</strong> / {formatMoney(target, acc.currency)}</span>
                                          <span style={{ color: targetColor, fontWeight: '700' }}>{Math.round(targetPct)}%</span>
                                        </div>
                                        <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                                          <div style={{ width: `${Math.min(targetPct, 100)}%`, height: '100%', background: targetColor, borderRadius: '2px' }} />
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  });
                })()}
                {(() => {
                  const disabledAccs = accounts.filter(acc => acc.type === 'CREDIT_CARD' && (acc.is_active === false || acc.is_active === 0 || acc.is_active === '0'));
                  if (disabledAccs.length > 0) {
                    return (
                      <details style={{ marginTop: '10px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: 'var(--text-muted)' }}>Disabled Credit Cards ({disabledAccs.length})</summary>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '14px' }}>
                          {disabledAccs.map(acc => {
                            const bal = parseFloat(acc.current_balance);
                            return (
                              <div key={acc.account_id} className="glass-panel" onClick={() => { setActiveAccount(acc); setActiveItemTxLimit(10); }} style={{
                                padding: '16px', borderLeft: '4px solid #666', cursor: 'pointer', opacity: 0.6
                              }}>
                                <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between' }}>
                                  <div>
                                    <span style={{ fontWeight: '700' }}>{acc.name}</span>
                                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>{acc.type} • {acc.currency} (Disabled)</span>
                                  </div>
                                  <span style={{ fontSize: '16px', fontWeight: '700' }}>{formatMoney(-bal, acc.currency)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )
        )}

        {/* VIEW: LEDGER GROUPS */}
        {activeTab === 'groups' && (
          activeGroup ? (
            <div className="animate-fade-in">
              {groupViewSubTab === 'balances' ? (
                <GroupBalancesView
                  activeGroup={activeGroup}
                  memberBreakdown={groupMemberBreakdown}
                  byCurrency={groupBalancesByCurrency}
                  simplifyDebts={activeGroup.simplify_debts !== false && activeGroup.simplify_debts !== 0 && activeGroup.simplify_debts !== 'false'}
                  repaymentsSaved={groupRepaymentsSaved}
                  currentUserId={localStorage.getItem('dhudd_user_id')}
                  currency={activeGroup?.currency || 'DH'}
                  formatMoney={formatMoney}
                  isAdmin={groupMembers.find(m => m.user_id === localStorage.getItem('dhudd_user_id'))?.role === 'admin' || activeGroup?.created_by_user_id === localStorage.getItem('dhudd_user_id')}
                  isLoading={isGroupBalancesLoading}
                  onBack={() => setGroupViewSubTab('hub')}
                  onToggleSimplify={handleToggleGroupSimplify}
                  onSettleDebt={(debt, member, cur) => {
                    setGroupSettlePrefill({
                      from_user_id: debt.type === 'owes_to_member' ? debt.other_user_id : member.user_id,
                      to_user_id: debt.type === 'owes_to_member' ? member.user_id : debt.other_user_id,
                      amount: debt.amount,
                      currency: cur || debt.currency || activeGroup?.currency || 'AED'
                    });
                    setIsGroupSettleModalOpen(true);
                  }}
                  onRemindDebt={handleRemindDebt}
                  onRefresh={reloadActiveGroupBalances}
                  apiClient={apiClient}
                  showNotification={showToast}
                />
              ) : groupViewSubTab === 'charts' ? (
                <GroupChartsView
                  activeGroup={activeGroup}
                  groupMembers={groupMembers}
                  transactions={transactions}
                  currency={activeGroup?.currency || 'DH'}
                  formatMoney={formatMoney}
                  onBack={() => setGroupViewSubTab('hub')}
                />
              ) : groupViewSubTab === 'tx_detail' ? (
                <GroupTransactionDetailView
                  transaction={selectedGroupTx}
                  activeGroup={activeGroup}
                  groupMembers={groupMembers}
                  currentUserId={localStorage.getItem('dhudd_user_id')}
                  accounts={accounts}
                  formatMoney={formatMoney}
                  onBack={() => {
                    setSelectedGroupTx(null);
                    setGroupViewSubTab('hub');
                  }}
                  onEditTx={(tx) => handleStartEdit(tx)}
                  onDeleteTx={(tx) => handleDeleteTx(tx.transaction_id)}
                  apiClient={apiClient}
                  showNotification={showToast}
                />
              ) : (
                <GroupDetailHub
                  activeGroup={activeGroup}
                  groupMembers={groupMembers}
                  transactions={transactions}
                  balances={groupBalances}
                  transfers={groupSimplifications}
                  repaymentsSaved={groupRepaymentsSaved}
                  currentUserId={localStorage.getItem('dhudd_user_id')}
                  formatMoney={formatMoney}
                  isBalancesLoading={isGroupBalancesLoading}
                  onBack={() => setActiveGroup(null)}
                  onOpenSettings={() => openEditGroupModal(activeGroup)}
                  onOpenBalances={() => {
                    reloadActiveGroupBalances();
                    setGroupViewSubTab('balances');
                  }}
                  onOpenCharts={() => setGroupViewSubTab('charts')}
                  onOpenSettleUp={() => {
                    setGroupSettlePrefill(null);
                    setIsGroupSettleModalOpen(true);
                  }}
                  onAddExpense={handleOpenAddGroupExpense}
                  onSelectTx={(tx) => {
                    setSelectedGroupTx(tx);
                    setGroupViewSubTab('tx_detail');
                  }}
                />
              )}
            </div>
          ) : (
            <div className="animate-fade-in" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <h2>Shared Groups</h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => openExportModal('groups_summary', { name: 'Shared Groups' })}
                    className="btn-secondary"
                    style={{ padding: '8px 14px', borderRadius: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                  >
                    <Download size={14} /> Export Groups
                  </button>
                  <button onClick={() => setActiveModal('group_create')} className="btn-primary" style={{ padding: '8px 16px', borderRadius: '12px', fontSize: '13px' }}>
                    Create Group
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {groups.length === 0 ? (
                  <div className="glass-panel" style={{ padding: '32px 20px', textAlign: 'center', borderRadius: '16px' }}>
                    <Users size={36} color="var(--text-muted)" style={{ margin: '0 auto 12px auto', display: 'block', opacity: 0.6 }} />
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: 'bold', color: 'var(--text-main)' }}>No shared groups yet</p>
                    <p style={{ margin: '6px 0 16px 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                      Create a group for your trip, apartment, or outing to split bills seamlessly.
                    </p>
                    <button
                      type="button"
                      onClick={() => setActiveModal('group_create')}
                      className="btn-primary"
                      style={{ padding: '8px 18px', borderRadius: '12px', fontSize: '13px' }}
                    >
                      Create First Group
                    </button>
                  </div>
                ) : (
                  groups.map(g => {
                    const isSimp = g.simplify_debts !== false && g.simplify_debts !== 0 && g.simplify_debts !== 'false';
                    return (
                      <div key={g.group_id} className="glass-panel" style={{ padding: '16px', cursor: 'pointer' }} onClick={() => handleViewGroup(g)}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <span style={{ fontWeight: '700', fontSize: '15px' }}>{g.group_name}</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                              Type: {g.type} • {isSimp ? '⚡ Debt Simplification: ON' : 'Debt Simplification: OFF'}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); openExportModal('group', g); }}
                              className="btn-secondary"
                              style={{ padding: '4px 10px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', height: 'auto' }}
                              title="Export Group Transactions"
                            >
                              <Download size={12} /> Export Txs
                            </button>
                            <ChevronRight size={18} color="var(--text-muted)" />
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )
        )}

        {/* VIEW: FRIENDS TAB */}
        {activeTab === 'friends' && (
          <div className="animate-fade-in friends-tab-container">
            {activeFriend ? (
              // Friend Details View
              <div className="glass-panel friend-details-panel">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <div style={{ position: 'relative', width: '52px', height: '52px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden', border: '2px solid var(--accent-cyan)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--accent-cyan)', position: 'absolute' }}>
                        {(activeFriend.nickname || activeFriend.display_name || activeFriend.email || 'F')[0].toUpperCase()}
                      </div>
                      {activeFriend.avatar_url && (
                        <img src={apiClient.getAvatarUrl(activeFriend.avatar_url)} alt="Avatar" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'relative', zIndex: 1 }} />
                      )}
                    </div>
                    <div>
                      <h2 style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                        <span>{activeFriend.nickname || activeFriend.display_name || activeFriend.email.split('@')[0]}</span>
                        <span style={{ fontSize: '14px' }}>
                          {activeFriend.is_placeholder ? '👤' : (activeFriend.gender?.toLowerCase() === 'female' || activeFriend.gender?.toLowerCase() === 'f' ? '👧' : '👦')}
                        </span>
                        {activeFriend.nickname && (
                          <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 'normal' }}>
                            ({activeFriend.display_name})
                          </span>
                        )}
                        {activeFriend.is_hidden && (
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', background: 'rgba(255, 107, 107, 0.15)', color: 'var(--color-outflow)', border: '1px solid rgba(255, 107, 107, 0.3)', fontWeight: '600' }}>
                            🙈 Hidden
                          </span>
                        )}
                      </h2>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                        {activeFriend.is_placeholder ? 'Placeholder Friend' : activeFriend.email}
                        {activeFriend.phone_number && ` • ${activeFriend.phone_number}`}
                      </span>
                      {activeFriend.bio && (
                        <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: 'var(--text-main)', fontStyle: 'italic' }}>
                          "{activeFriend.bio}"
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <button 
                      onClick={() => {
                        const defaultAcc = accounts.find(a => a.type !== 'CREDIT_CARD') || accounts[0];
                        setTfFromType('ACCOUNT');
                        setTfFromAccountId(defaultAcc?.account_id || '');
                        setTfToType('FRIEND');
                        setTfToAccountId(activeFriend.user_id);
                        setTfFriendCurrency(preferredCurrencies[0] || 'AED');
                        setTfFromAmount('');
                        setTfToAmount('');
                        setTfDesc(`Transfer with ${activeFriend.nickname || activeFriend.display_name || 'Friend'}`);
                        setActiveTab('transfers');
                        setActiveSheet(null);
                      }}
                      className="btn-secondary"
                      style={{ padding: '6px 12px', fontSize: '12px', height: '32px', color: 'var(--accent-purple)', borderColor: 'var(--accent-purple)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', borderRadius: '8px', fontWeight: '600', boxSizing: 'border-box' }}
                    >
                      🔄 Transfer
                    </button>
                    {/* Show Nickname button for real users, and for placeholders the current user did NOT create */}
                    {(!activeFriend.is_placeholder || activeFriend.created_by_user_id !== localStorage.getItem('dhudd_user_id')) && (
                    <button 
                      onClick={() => {
                        setNicknameEditingFriend(activeFriend);
                        setFriendNicknameInput(activeFriend.nickname || '');
                        setEditNicknameModalOpen(true);
                      }}
                      className="btn-secondary"
                      style={{ padding: '6px 12px', fontSize: '12px', height: '32px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', borderRadius: '8px', fontWeight: '600', boxSizing: 'border-box' }}
                    >
                      ✏️ Nickname
                    </button>
                    )}
                    <button 
                      onClick={() => {
                        setDeletingFriend(activeFriend);
                        setDeleteAssociatedTransactionsCheck(false);
                        setDeleteFriendModalOpen(true);
                      }}
                      className="btn-secondary"
                      style={{ padding: '6px 12px', fontSize: '12px', height: '32px', borderColor: 'var(--color-outflow)', color: 'var(--color-outflow)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', borderRadius: '8px', fontWeight: '600', boxSizing: 'border-box' }}
                    >
                      🗑️ Delete
                    </button>

                    {/* Friend Settings Dropdown (Hide / Unhide Friend) */}
                    <div style={{ position: 'relative' }}>
                      <button
                        type="button"
                        onClick={() => setFriendSettingsMenuOpen(prev => !prev)}
                        className="btn-secondary"
                        style={{
                          width: '32px',
                          height: '32px',
                          padding: 0,
                          borderRadius: '8px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderColor: activeFriend.is_hidden ? 'var(--color-outflow)' : 'var(--border-glass)',
                          color: activeFriend.is_hidden ? 'var(--color-outflow)' : 'var(--text-main)',
                          boxSizing: 'border-box'
                        }}
                        title="Friend Settings"
                      >
                        <Settings size={16} />
                      </button>

                      {friendSettingsMenuOpen && (
                        <div 
                          className="glass-panel"
                          style={{
                            position: 'absolute',
                            right: 0,
                            top: '38px',
                            zIndex: 50,
                            width: '270px',
                            padding: '12px',
                            borderRadius: '12px',
                            background: 'rgba(18, 22, 34, 0.98)',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.6)',
                            border: '1px solid var(--border-glass)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: '6px' }}>
                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main)' }}>⚙️ Friend Settings</span>
                            <button 
                              type="button" 
                              onClick={() => setFriendSettingsMenuOpen(false)} 
                              style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}
                            >
                              ✕
                            </button>
                          </div>

                          {/* Hide / Unhide option */}
                          <div>
                            <button
                              type="button"
                              onClick={async () => {
                                const nextHidden = !activeFriend.is_hidden;
                                try {
                                  await apiClient.request(`/friends/${activeFriend.user_id}/hide`, {
                                    method: 'PUT',
                                    body: JSON.stringify({ is_hidden: nextHidden })
                                  });
                                  setActiveFriend(prev => ({ ...prev, is_hidden: nextHidden }));
                                  setFriends(prev => prev.map(f => f.user_id === activeFriend.user_id ? { ...f, is_hidden: nextHidden } : f));
                                  setFriendSettingsMenuOpen(false);
                                  if (nextHidden) {
                                    showToast("Friend hidden! To view or unhide, select 'Hidden Friends' in the Friends Filter dropdown.", 'info');
                                  } else {
                                    showToast("Friend unhidden and restored to your main friends list!", 'success');
                                  }
                                } catch (err) {
                                  showToast(err.message || 'Failed to update friend visibility', 'error');
                                }
                              }}
                              className="btn-secondary"
                              style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                padding: '8px',
                                fontSize: '12px',
                                borderRadius: '8px',
                                borderColor: activeFriend.is_hidden ? 'var(--accent-cyan)' : 'var(--color-outflow)',
                                color: activeFriend.is_hidden ? 'var(--accent-cyan)' : 'var(--color-outflow)',
                                fontWeight: 'bold'
                              }}
                            >
                              {activeFriend.is_hidden ? '👁️ Unhide Friend' : '🙈 Hide Friend'}
                            </button>
                            <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: 'var(--text-muted)', lineHeight: '1.4' }}>
                              {activeFriend.is_hidden
                                ? 'Unhide this friend so they appear in your active friends list again.'
                                : "Hide this friend from your main list. To view or unhide, select 'Hidden Friends' in the Friends Filter dropdown."}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    <button 
                      onClick={() => { setActiveFriend(null); setFriendSettleOpen(false); }} 
                      style={{ background: 'transparent', border: 'none', color: 'var(--accent-cyan)', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', padding: '6px 8px', height: '32px', display: 'inline-flex', alignItems: 'center' }}
                    >
                      ← Friends List
                    </button>
                  </div>
                </div>

                {/* Detailed Multi-Level Balances Breakdown */}
                {(() => {
                  const detailedMap = computeFriendDetailedBalances(activeFriend);
                  const currencies = Object.keys(detailedMap);
                  const allOwedBalances = currencies
                    .filter(c => detailedMap[c].overall > 0)
                    .map(c => ({ amount: detailedMap[c].overall, currency: c }));

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      <h3 style={{ fontSize: '13px', margin: 0, color: 'var(--text-main)', fontWeight: 'bold' }}>
                        📊 Comprehensive Balance Breakdown
                      </h3>

                      {currencies.length === 0 ? (
                        <p style={{ color: 'var(--text-muted)', fontSize: '12px', padding: '10px 0', margin: 0 }}>All settled up! No active balances.</p>
                      ) : (
                        currencies.map(cur => {
                          const data = detailedMap[cur];
                          const groupEntries = Object.values(data.groupBalances);
                          const owesYou = data.overall > 0;
                          const youOwe = data.overall < 0;

                          return (
                            <div key={cur} className="glass-panel" style={{ padding: '14px', background: 'rgba(255,255,255,0.02)', borderRadius: '12px', border: '1px solid var(--border-glass)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              
                              {/* 1. Overall Final Balance Header */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                                <div>
                                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '2px' }}>⭐ Overall Final Balance ({cur})</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: '18px', fontWeight: '800', color: owesYou ? 'var(--color-inflow)' : youOwe ? 'var(--color-outflow)' : 'var(--text-muted)' }}>
                                      {owesYou ? `+${formatMoney(data.overall, cur)} (Owes You)` : youOwe ? `-${formatMoney(-data.overall, cur)} (You Owe)` : `Settled (0.00 ${cur})`}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setFriendAdjustCurrency(cur);
                                        setFriendAdjustAmount(formatInputValue(data.overall));
                                        setFriendAdjustOpen(true);
                                        setFriendSettleOpen(false);
                                      }}
                                      className="btn-secondary"
                                      style={{ padding: '3px 8px', fontSize: '11px', height: '26px', borderRadius: '6px', borderColor: 'var(--accent-cyan)', color: 'var(--accent-cyan)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '3px', fontWeight: '600', boxSizing: 'border-box' }}
                                      title={`Adjust balance for ${cur}`}
                                    >
                                      ✏️ Adjust
                                    </button>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                  {owesYou && (
                                    <button
                                      type="button"
                                      onClick={() => setWaChoiceModal({ isOpen: true, friend: activeFriend, amount: data.overall, currency: cur, balances: allOwedBalances })}
                                      style={{
                                        padding: '6px 12px',
                                        fontSize: '12px',
                                        height: '32px',
                                        background: '#25D366',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '5px',
                                        boxShadow: '0 2px 6px rgba(37,211,102,0.3)',
                                        boxSizing: 'border-box'
                                      }}
                                    >
                                      💬 Remind via WhatsApp
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleOpenFriendSettleChooser(cur, data.overall)}
                                    disabled={friendSettleLoadingCur === cur}
                                    className="btn-primary"
                                    style={{
                                      padding: '6px 14px',
                                      fontSize: '12px',
                                      height: '32px',
                                      borderRadius: '8px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 'bold',
                                      boxSizing: 'border-box',
                                      gap: '6px',
                                      opacity: friendSettleLoadingCur === cur ? 0.7 : 1,
                                      cursor: friendSettleLoadingCur === cur ? 'wait' : 'pointer'
                                    }}
                                  >
                                    {friendSettleLoadingCur === cur ? (
                                      <>
                                        <RefreshCw size={12} className="animate-spin" />
                                        <span>Loading...</span>
                                      </>
                                    ) : (
                                      'Settle Up'
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const defaultAcc = accounts.find(a => a.currency === cur && a.type !== 'CREDIT_CARD') || accounts.find(a => a.type !== 'CREDIT_CARD') || accounts[0];
                                      setTfFromType('ACCOUNT');
                                      setTfFromAccountId(defaultAcc?.account_id || '');
                                      setTfToType('FRIEND');
                                      setTfToAccountId(activeFriend.user_id);
                                      setTfFriendCurrency(cur || preferredCurrencies[0] || 'AED');
                                      setTfFromAmount('');
                                      setTfToAmount('');
                                      setTfDesc(`Transfer to ${activeFriend.nickname || activeFriend.display_name || 'Friend'}`);
                                      setActiveTab('transfers');
                                      setActiveSheet(null);
                                    }}
                                    className="btn-secondary"
                                    style={{ padding: '6px 12px', fontSize: '12px', height: '32px', borderRadius: '8px', borderColor: 'var(--accent-purple)', color: 'var(--accent-purple)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontWeight: '600', boxSizing: 'border-box' }}
                                  >
                                    🔄 Transfer
                                  </button>
                                </div>
                              </div>

                              {/* 2. Non-Group Expenses Balance */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', background: 'rgba(0,0,0,0.15)', padding: '8px 10px', borderRadius: '8px' }}>
                                <span>📄 Non-Group Direct Expenses:</span>
                                <span style={{ fontWeight: '700', color: data.nonGroup > 0 ? 'var(--color-inflow)' : data.nonGroup < 0 ? 'var(--color-outflow)' : 'var(--text-main)' }}>
                                  {data.nonGroup > 0 ? `+${formatMoney(data.nonGroup, cur)}` : data.nonGroup < 0 ? `-${formatMoney(-data.nonGroup, cur)}` : `0.00 ${cur}`}
                                </span>
                              </div>

                              {/* 3. Balances in Different Groups */}
                              {groupEntries.length > 0 && (
                                <div style={{ background: 'rgba(0,0,0,0.15)', padding: '8px 10px', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'bold' }}>
                                    👥 Balances Across Shared Groups:
                                  </span>
                                  {groupEntries.map((g, gIdx) => (
                                    <div key={gIdx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', padding: '2px 0 2px 8px', borderLeft: '2px solid var(--accent-cyan)' }}>
                                      <span>{g.name}</span>
                                      <span style={{ fontWeight: '600', color: g.amount > 0 ? 'var(--color-inflow)' : g.amount < 0 ? 'var(--color-outflow)' : 'var(--text-muted)' }}>
                                        {g.amount > 0 ? `+${formatMoney(g.amount, cur)}` : g.amount < 0 ? `-${formatMoney(-g.amount, cur)}` : `0.00 ${cur}`}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* 4. Deleted Groups Balance */}
                              {data.deletedGroupsBalance !== 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', background: 'rgba(255,71,87,0.06)', padding: '8px 10px', borderRadius: '8px', border: '1px solid rgba(255,71,87,0.15)' }}>
                                  <span style={{ color: 'var(--color-outflow)', fontWeight: 'bold' }}>🗑️ Deleted Groups Balance:</span>
                                  <span style={{ fontWeight: '700', color: data.deletedGroupsBalance > 0 ? 'var(--color-inflow)' : 'var(--color-outflow)' }}>
                                    {data.deletedGroupsBalance > 0 ? `+${formatMoney(data.deletedGroupsBalance, cur)}` : `-${formatMoney(-data.deletedGroupsBalance, cur)}`}
                                  </span>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  );
                })()}


                {/* Merge Placeholder Section */}
                {activeFriend.is_placeholder && (
                  <div style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '14px', marginTop: '10px' }}>
                    {!friendMergeOpen && editingPlaceholderFriendId !== activeFriend.user_id ? (
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {/* Only the original creator can edit the placeholder's global name */}
                        {activeFriend.created_by_user_id === localStorage.getItem('dhudd_user_id') && (
                        <button
                          onClick={() => {
                            setEditingPlaceholderFriendId(activeFriend.user_id);
                            setEditPlaceholderName(activeFriend.display_name || activeFriend.email.split('@')[0]);
                            const { countryCode, customCc, digits } = parsePhoneIntoCcAndDigits(activeFriend.phone_number);
                            setEditPlaceholderCountryCode(countryCode);
                            setEditPlaceholderCustomCc(customCc);
                            setEditPlaceholderPhoneDigits(digits);
                          }}
                          className="btn-secondary"
                          style={{ padding: '8px 14px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                        >
                          ✏️ Edit Placeholder
                        </button>
                        )}
                        <button
                          onClick={() => {
                            setFriendMergeEmail('');
                            setFriendMergeOpen(true);
                          }}
                          className="btn-secondary"
                          style={{ padding: '8px 14px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px', borderColor: 'var(--accent-purple)', color: 'var(--accent-purple)' }}
                        >
                          <UserPlus size={14} /> Merge with Registered User
                        </button>
                      </div>
                    ) : editingPlaceholderFriendId === activeFriend.user_id ? (
                      <form onSubmit={handleEditPlaceholderFriend} className="glass-panel animate-fade-in" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <h4 style={{ margin: 0, fontSize: '13px' }}>Edit Placeholder Details</h4>
                        <div>
                          <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Name</label>
                          <input 
                            type="text" 
                            placeholder="Friend Name"
                            value={editPlaceholderName} 
                            onChange={e => setEditPlaceholderName(e.target.value)} 
                            required 
                            style={{ padding: '8px', fontSize: '12px', width: '100%' }}
                          />
                        </div>
                        <div>
                          <label style={{ fontSize: '11px', display: 'block', marginBottom: '4px' }}>Phone Number</label>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <select
                              value={editPlaceholderCountryCode}
                              onChange={e => setEditPlaceholderCountryCode(e.target.value)}
                              style={{ padding: '8px', width: '110px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', borderRadius: '8px', color: 'var(--text-main)', fontSize: '12px' }}
                            >
                              {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                            </select>
                            {editPlaceholderCountryCode === 'CUSTOM' && (
                              <input
                                type="text"
                                placeholder="+123"
                                value={editPlaceholderCustomCc}
                                onChange={e => setEditPlaceholderCustomCc(e.target.value)}
                                style={{ padding: '8px', width: '60px', fontSize: '12px' }}
                              />
                            )}
                            <input 
                              type="tel" 
                              placeholder="50 123 4567"
                              value={editPlaceholderPhoneDigits} 
                              onChange={e => setEditPlaceholderPhoneDigits(e.target.value)} 
                              style={{ padding: '8px', fontSize: '12px', flex: 1, minWidth: '110px' }}
                            />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                          <button type="submit" className="btn-primary" style={{ padding: '8px 12px', fontSize: '11px', height: 'auto', flex: 1 }}>
                            Save Changes
                          </button>
                          <button 
                            type="button" 
                            onClick={() => setEditingPlaceholderFriendId(null)} 
                            className="btn-secondary" 
                            style={{ padding: '8px 12px', fontSize: '11px', height: 'auto', flex: 1 }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : (
                      <form onSubmit={handleMergeFriend} className="glass-panel animate-fade-in" style={{ padding: '16px', background: 'rgba(168,85,247,0.03)', border: '1px solid rgba(168,85,247,0.15)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <h4 style={{ margin: 0, fontSize: '13px', color: 'var(--accent-purple)' }}>
                          Merge Placeholder Friend
                        </h4>
                        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
                          Merge all balance history, split shares, and groups of this placeholder into an active, registered user's account.
                        </p>
                        <div>
                          <label style={{ fontSize: '11px', display: 'block', marginBottom: '6px', fontWeight: 'bold' }}>Choose Existing Registered Friend to Merge Into:</label>
                          <select 
                            value={friendMergeEmail}
                            onChange={e => setFriendMergeEmail(e.target.value)}
                            style={{ width: '100%', padding: '10px', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '12px', marginBottom: '8px' }}
                          >
                            <option value="">Select registered friend...</option>
                            {sortFriendsAZ(friends.filter(f => !f.is_placeholder && f.email)).map(rf => (
                              <option key={rf.user_id} value={rf.email}>
                                {formatFriendLabel(rf)}
                              </option>
                            ))}
                          </select>
                          <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Or enter registered user email manually:</label>
                          <input 
                            type="email" 
                            placeholder="user@email.com"
                            value={friendMergeEmail} 
                            onChange={e => { setFriendMergeEmail(e.target.value); setFriendMergePhone(''); }} 
                            style={{ padding: '8px', fontSize: '12px', width: '100%', marginBottom: '8px' }}
                          />

                          <label style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginBottom: '4px' }}>Or enter registered user mobile phone number:</label>
                          <input 
                            type="tel" 
                            placeholder="+971 50 123 4567"
                            value={friendMergePhone} 
                            onChange={e => { setFriendMergePhone(e.target.value); setFriendMergeEmail(''); }} 
                            style={{ padding: '8px', fontSize: '12px', width: '100%' }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                          <button type="submit" className="btn-primary" style={{ padding: '8px 12px', fontSize: '11px', height: 'auto', flex: 1, background: 'var(--accent-purple)', color: 'white', border: 'none' }}>
                            Confirm Merge
                          </button>
                          <button 
                            type="button" 
                            onClick={() => setFriendMergeOpen(false)} 
                            className="btn-secondary" 
                            style={{ padding: '8px 12px', fontSize: '11px', height: 'auto', flex: 1 }}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {/* Adjust Balance Manually Section */}
                <div style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '14px', marginTop: '10px' }}>
                  {!friendAdjustOpen ? (
                    <button
                      onClick={() => {
                        setFriendAdjustCurrency(preferredCurrencies[0] || 'AED');
                        setFriendAdjustAmount('');
                        setFriendAdjustOpen(true);
                      }}
                      className="btn-secondary"
                      style={{ padding: '8px 14px', fontSize: '12px', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                      <PlusCircle size={14} /> Adjust Balance Manually
                    </button>
                  ) : (
                    <form onSubmit={handleAdjustFriendBalance} className="glass-panel animate-fade-in" style={{ padding: '16px', background: 'rgba(255,176,58,0.03)', border: '1px solid rgba(255,176,58,0.15)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <h4 style={{ margin: 0, fontSize: '13px', color: '#ffb03a' }}>
                        Manually Adjust Balance
                      </h4>
                      <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
                        Specify the net balance between you and {activeFriend.is_placeholder ? (activeFriend.nickname || activeFriend.display_name) : (activeFriend.display_name || activeFriend.email)}. Use positive if they owe you, negative if you owe them.
                      </p>
                      
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '11px' }}>Currency</label>
                          <select 
                            value={friendAdjustCurrency} 
                            onChange={e => setFriendAdjustCurrency(e.target.value)} 
                            style={{ padding: '8px', fontSize: '12px', width: '100%', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)' }}
                          >
                            {preferredCurrencies.map(curr => (
                              <option key={curr} value={curr}>{curr}</option>
                            ))}
                          </select>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '11px' }}>New Net Balance</label>
                          <input 
                            type="number" 
                            step="0.0001" 
                            placeholder="e.g. 100 or -50"
                            value={friendAdjustAmount} 
                            onChange={e => setFriendAdjustAmount(e.target.value)} 
                            required 
                            style={{ padding: '8px', fontSize: '12px', width: '100%' }}
                          />
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                        <button type="submit" className="btn-primary" style={{ padding: '8px 12px', fontSize: '11px', height: 'auto', flex: 1, background: '#ffb03a', color: 'black' }}>
                          Apply Adjustment
                        </button>
                        <button 
                          type="button" 
                          onClick={() => setFriendAdjustOpen(false)} 
                          className="btn-secondary" 
                          style={{ padding: '8px 12px', fontSize: '11px', height: 'auto', flex: 1 }}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                </div>

                {/* Friend bilateral timeline */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h3 style={{ fontSize: '13px', margin: 0 }}>Timeline</h3>
                    <button 
                      onClick={() => openExportModal('friend', activeFriend)} 
                      className="btn-secondary" 
                      style={{ padding: '4px 10px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px', height: 'auto', width: 'auto' }}
                    >
                      <Download size={12} /> Export
                    </button>
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {(() => {
                      const myUserId = localStorage.getItem('dhudd_user_id');
                      const friendId = activeFriend.user_id;
                      const friendTxs = transactions.filter(tx => {
                        if (!tx.is_shared && !(tx.paid_by_user_id && tx.paid_by_user_id !== tx.user_id)) return false;
                        if (tx.source_transaction_id && transactions.some(p => p.transaction_id === tx.source_transaction_id && (tx.user_id !== myUserId || p.splits?.some(s => s.member_user_id === friendId) || p.user_id === friendId || p.paid_by_user_id === friendId))) return false;

                        const hasSplits = tx.splits && Array.isArray(tx.splits) && tx.splits.length > 0;
                        const payers = hasSplits ? tx.splits.filter(s => parseFloat(s.paid_amount || 0) > 0) : [];
                        const userIsPayer = payers.some(p => p.member_user_id === myUserId) || tx.paid_by_user_id === myUserId || (!tx.paid_by_user_id && payers.length === 0 && tx.user_id === myUserId);
                        const friendIsPayer = payers.some(p => p.member_user_id === friendId) || tx.paid_by_user_id === friendId;

                        // Settlement check
                        if (tx.category_name === 'Debt Settlement' || (tx.description && tx.description.toLowerCase().includes('settle'))) {
                          const mySplit = hasSplits ? tx.splits.find(s => s.member_user_id === myUserId) : null;
                          const friendSplit = hasSplits ? tx.splits.find(s => s.member_user_id === friendId) : null;
                          const myOwed = parseFloat(mySplit?.owed_amount || 0);
                          const friendOwed = parseFloat(friendSplit?.owed_amount || 0);
                          if (hasSplits) {
                            return (userIsPayer && tx.user_id === friendId) || (friendIsPayer && tx.user_id === myUserId) || (userIsPayer && friendOwed > 0) || (friendIsPayer && myOwed > 0);
                          }
                          return (tx.paid_by_user_id === myUserId && tx.user_id === friendId) || (tx.paid_by_user_id === friendId && tx.user_id === myUserId);
                        }

                        // TP1: Third-Party Non-Consumer Payer Classification
                        const payerUserId = tx.paid_by_user_id;
                        const creatorUserId = tx.user_id;
                        const payerSplit = hasSplits && payerUserId ? tx.splits.find(s => s.member_user_id === payerUserId) : null;
                        const payerOwed = payerSplit ? parseFloat(payerSplit.owed_amount || 0) : 0;
                        const isThirdPartyPayer = !!(payerUserId && creatorUserId && payerUserId !== creatorUserId && payerOwed === 0);

                        if (isThirdPartyPayer) {
                          if (tx.group_id) return false;

                          // TP2: Three-Way Bilateral Debt Routing
                          // Debtor vs Payer: Filter out transaction (debtor <-> payer = 0)
                          const isDebtorVsPayer = (myUserId === payerUserId && friendId !== creatorUserId) || (friendId === payerUserId && myUserId !== creatorUserId);
                          if (isDebtorVsPayer) return false;

                          // Creator vs Payer: Creator owes third-party payer full paid amount
                          const isCreatorVsPayer = (myUserId === creatorUserId && friendId === payerUserId) || (myUserId === payerUserId && friendId === creatorUserId);
                          if (isCreatorVsPayer) return true;

                          // Creator vs Debtor: All split debtors owe the expense creator
                          const mySplit = hasSplits ? tx.splits.find(s => s.member_user_id === myUserId) : null;
                          const friendSplit = hasSplits ? tx.splits.find(s => s.member_user_id === friendId) : null;
                          const myOwed = mySplit ? parseFloat(mySplit.owed_amount || 0) : (!hasSplits && myUserId !== payerUserId ? parseFloat(tx.total_amount || 0) : 0);
                          const friendOwed = friendSplit ? parseFloat(friendSplit.owed_amount || 0) : (!hasSplits && friendId !== payerUserId ? parseFloat(tx.total_amount || 0) : 0);

                          if (myUserId === creatorUserId && friendOwed > 0) return true;
                          if (friendId === creatorUserId && myOwed > 0) return true;

                          return false;
                        }

                        if (hasSplits) {
                          const mySplit = tx.splits.find(s => s.member_user_id === myUserId);
                          const friendSplit = tx.splits.find(s => s.member_user_id === friendId);
                          const myOwed = parseFloat(mySplit?.owed_amount || 0);
                          const friendOwed = parseFloat(friendSplit?.owed_amount || 0);

                          // Direct bilateral involvement:
                          if (userIsPayer && friendOwed > 0) return true;
                          if (friendIsPayer && myOwed > 0) return true;
                          if (!tx.group_id && mySplit && friendSplit) return true;

                          return false;
                        }

                        // No splits (legacy or simple 1-on-1 tx)
                        if (tx.group_id) return false;
                        return (userIsPayer && tx.user_id === friendId) || (friendIsPayer && tx.user_id === myUserId);
                      });

                      const sorted = [...friendTxs].sort((a, b) => new Date(b.date) - new Date(a.date));
                      const visible = sorted.slice(0, activeItemTxLimit);
                      const runningBalances = computeFriendRunningBalances(activeFriend, transactions, myUserId);

                      if (visible.length === 0) {
                        return <p style={{ color: 'var(--text-muted)', fontSize: '12px', padding: '10px 0' }}>No shared transactions recorded yet.</p>;
                      }

                      return (
                        <>
                          {visible.map(tx => {
                            const matchedCat = findCategoryByName(categories, tx.category_name);
                            const isGlobalInflow = GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (tx.category_name || '').trim().toLowerCase());
                            const isOut = matchedCat ? matchedCat.type === 'OUTFLOW' : !isGlobalInflow;
                            const { amount: displayAmt, isSplitPortion } = getDisplayAmount(tx);
                            const isTransfer = isTransferTransaction(tx);
                            const isCcPayment = tx.description.toLowerCase().includes('credit card-bill payment') || tx.description.toLowerCase().includes('credit card payment:') || tx.description.toLowerCase().includes('card payment');
                            const sAcc = accounts.find(a => a.account_id === tx.paid_by_account_id);
                            const dAcc = accounts.find(a => a.account_id === tx.transfer_to_account_id);
                            const runObj = runningBalances[tx.transaction_id];
                            const txRunningBal = runObj ? runObj.balance : null;
                            const badge = getCategoryBadge(tx.category_name, tx.description);
                            const IconComp = badge.icon;
                            const { monthShort, day } = getDateTokens(tx.date);

                            const friendName = activeFriend.display_name || activeFriend.nickname || activeFriend.name || 'Friend';
                            const friendId = activeFriend.user_id;
                            const payers = tx.splits?.filter(s => parseFloat(s.paid_amount || 0) > 0) || [];
                            const userIsPayer = payers.some(p => p.member_user_id === myUserId) || tx.paid_by_user_id === myUserId || (!tx.paid_by_user_id && payers.length === 0 && tx.user_id === myUserId);
                            const friendIsPayer = payers.some(p => p.member_user_id === friendId) || tx.paid_by_user_id === friendId;

                            const mySplit = tx.splits?.find(s => s.member_user_id === myUserId);
                            const friendSplit = tx.splits?.find(s => s.member_user_id === friendId);
                            const myOwed = mySplit ? parseFloat(mySplit.owed_amount || 0) : (!tx.splits?.length && tx.user_id === myUserId ? parseFloat(tx.total_amount || 0) : 0);
                            const friendOwed = friendSplit ? parseFloat(friendSplit.owed_amount || 0) : (!tx.splits?.length && tx.user_id === friendId ? parseFloat(tx.total_amount || 0) : 0);

                            const payerUserId = tx.paid_by_user_id;
                            const creatorUserId = tx.user_id;
                            const isSettlement = tx.category_name === 'Debt Settlement' || (tx.description && tx.description.toLowerCase().includes('settle'));
                            const payerSplit = tx.splits?.find(s => s.member_user_id === payerUserId);
                            const payerOwed = payerSplit ? parseFloat(payerSplit.owed_amount || 0) : 0;
                            const isThirdPartyPayer = !isSettlement && !!(payerUserId && creatorUserId && payerUserId !== creatorUserId && payerOwed === 0);
                            const totalPaidAmt = parseFloat(payerSplit?.paid_amount || tx.total_amount || 0);

                            let subtitleText = '';
                            if (isThirdPartyPayer) {
                              // TP6: Contextual Ledger Subtitles
                              if (myUserId === creatorUserId && friendId !== payerUserId) {
                                subtitleText = `Split share • ${friendName} owes you ${formatMoney(friendOwed, tx.currency)}`;
                              } else if (friendId === creatorUserId && myUserId !== payerUserId) {
                                subtitleText = `${friendName} paid • You owe ${friendName} ${formatMoney(myOwed, tx.currency)}`;
                              } else if (myUserId === creatorUserId && friendId === payerUserId) {
                                subtitleText = `${friendName} paid on your behalf • You owe ${friendName} ${formatMoney(totalPaidAmt, tx.currency)}`;
                              } else if (myUserId === payerUserId && friendId === creatorUserId) {
                                subtitleText = `You paid for ${friendName} • ${friendName} owes you ${formatMoney(totalPaidAmt, tx.currency)}`;
                              } else {
                                subtitleText = `${matchedCat?.symbol || '💸'} ${matchedCat?.name || tx.category_name}`;
                              }
                            } else if (isCcPayment) {
                              subtitleText = `💳 Credit Card Payment${isTransfer && sAcc && dAcc ? ` (${sAcc.name} → ${dAcc.name})` : (!isTransfer && sAcc ? ` (${getPayerName(tx)} → ${sAcc.name})` : '')}`;
                            } else if (isTransfer) {
                              if (sAcc && dAcc) {
                                subtitleText = `🔄 Transfer (${sAcc.name} → ${dAcc.name})`;
                              } else if (sAcc) {
                                subtitleText = `🔄 Transfer (${sAcc.name} → ${friendName})`;
                              } else if (dAcc) {
                                subtitleText = `🔄 Transfer (${friendName} → ${dAcc.name})`;
                              } else {
                                const dir = getTransferDirection(tx, accounts);
                                subtitleText = dir === 'inward' ? `🔄 Transfer (From ${friendName})` : `🔄 Transfer (To ${friendName})`;
                              }
                            } else {
                              subtitleText = `${matchedCat?.symbol || '💸'} ${matchedCat?.name || tx.category_name}`;
                            }
                            subtitleText += ` • ${formatDateTime(tx.date)}`;

                            return (
                              <div 
                                key={tx.transaction_id} 
                                className="glass-panel" 
                                onClick={() => setSelectedTxDetail(tx)} 
                                style={{ 
                                  display: 'flex',
                                  flexDirection: 'column',
                                  padding: '12px 14px',
                                  borderRadius: '14px',
                                  border: '1px solid var(--border-glass)',
                                  cursor: 'pointer',
                                  gap: '8px',
                                  transition: 'transform 0.15s ease, background 0.15s ease'
                                }}
                              >
                                {/* Top: Main Transaction Row */}
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', width: '100%' }}>
                                  {/* Left: Date + Category Icon */}
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                                    {/* Date Block */}
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      width: '32px',
                                      flexShrink: 0
                                    }}>
                                      <span style={{ fontSize: '10px', textTransform: 'uppercase', color: 'var(--text-muted)', fontWeight: '600', lineHeight: 1 }}>
                                        {monthShort}
                                      </span>
                                      <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-main)', lineHeight: 1.2 }}>
                                        {day}
                                      </span>
                                    </div>

                                    {/* Category Square Badge */}
                                    <div style={{
                                      width: '38px',
                                      height: '38px',
                                      borderRadius: '11px',
                                      background: badge.bg,
                                      border: `1px solid ${badge.border}`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0
                                    }}>
                                      <IconComp size={18} color={badge.color} />
                                    </div>
                                  </div>

                                  {/* Middle: Description & Subtitle */}
                                  <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                                    <div style={{
                                      fontWeight: '700',
                                      fontSize: '14px',
                                      color: 'var(--text-main)',
                                      lineHeight: 1.3,
                                      wordBreak: 'break-word',
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}>
                                      {tx.description || 'Transaction'}
                                    </div>
                                    <div style={{
                                      fontSize: '11px',
                                      color: 'var(--text-muted)',
                                      marginTop: '2px',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis'
                                    }}>
                                      {subtitleText}
                                    </div>
                                  </div>

                                  {/* Right: Amount & Share */}
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, gap: '2px' }}>
                                    {isThirdPartyPayer ? (
                                      myUserId === creatorUserId && friendId !== payerUserId ? (
                                        <>
                                          <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--color-inflow)', whiteSpace: 'nowrap' }}>
                                            +{formatMoney(friendOwed, tx.currency)}
                                          </span>
                                          <span style={{ fontSize: '9px', color: 'var(--color-inflow)', whiteSpace: 'nowrap', fontWeight: '600' }}>
                                            {friendName} owes you
                                          </span>
                                        </>
                                      ) : friendId === creatorUserId && myUserId !== payerUserId ? (
                                        <>
                                          <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--color-outflow)', whiteSpace: 'nowrap' }}>
                                            -{formatMoney(myOwed, tx.currency)}
                                          </span>
                                          <span style={{ fontSize: '9px', color: 'var(--color-outflow)', whiteSpace: 'nowrap', fontWeight: '600' }}>
                                            You owe {friendName}
                                          </span>
                                        </>
                                      ) : myUserId === creatorUserId && friendId === payerUserId ? (
                                        <>
                                          <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--color-outflow)', whiteSpace: 'nowrap' }}>
                                            -{formatMoney(totalPaidAmt, tx.currency)}
                                          </span>
                                          <span style={{ fontSize: '9px', color: 'var(--color-outflow)', whiteSpace: 'nowrap', fontWeight: '600' }}>
                                            You owe {friendName}
                                          </span>
                                        </>
                                      ) : myUserId === payerUserId && friendId === creatorUserId ? (
                                        <>
                                          <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--color-inflow)', whiteSpace: 'nowrap' }}>
                                            +{formatMoney(totalPaidAmt, tx.currency)}
                                          </span>
                                          <span style={{ fontSize: '9px', color: 'var(--color-inflow)', whiteSpace: 'nowrap', fontWeight: '600' }}>
                                            You paid for {friendName}
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                                            {formatMoney(displayAmt, tx.currency)}
                                          </span>
                                        </>
                                      )
                                    ) : userIsPayer && myOwed === 0 ? (
                                      <>
                                        <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--color-inflow)', whiteSpace: 'nowrap' }}>
                                          +{formatMoney(friendOwed > 0 ? friendOwed : parseFloat(tx.total_amount || 0), tx.currency)}
                                        </span>
                                        <span style={{ fontSize: '9px', color: 'var(--color-inflow)', whiteSpace: 'nowrap', fontWeight: '600' }}>
                                          You paid for {friendName}
                                        </span>
                                      </>
                                    ) : friendIsPayer && friendOwed === 0 ? (
                                      <>
                                        <span style={{ fontSize: '15px', fontWeight: '800', color: 'var(--color-outflow)', whiteSpace: 'nowrap' }}>
                                          -{formatMoney(myOwed > 0 ? myOwed : parseFloat(tx.total_amount || 0), tx.currency)}
                                        </span>
                                        <span style={{ fontSize: '9px', color: 'var(--color-outflow)', whiteSpace: 'nowrap', fontWeight: '600' }}>
                                          {friendName} paid for you
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <span style={{ fontSize: '15px', fontWeight: '800', color: isOut ? 'var(--color-outflow)' : 'var(--color-inflow)', whiteSpace: 'nowrap' }}>
                                          {isOut ? '-' : '+'}{formatMoney(displayAmt, tx.currency)}
                                        </span>
                                        {isSplitPortion && <span style={{ fontSize: '9px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>your share</span>}
                                      </>
                                    )}
                                  </div>
                                </div>

                                {/* Bottom Row: Bilateral Running Balance Strip */}
                                {txRunningBal !== null && txRunningBal !== undefined && (
                                  <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    paddingTop: '6px',
                                    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                                    fontSize: '11px',
                                    gap: '8px'
                                  }}>
                                    <span style={{ color: 'var(--text-muted)', fontSize: '10.5px', display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                      ⚖️ Bilateral Balance
                                    </span>
                                    <span style={{ 
                                      fontSize: '11px', 
                                      fontWeight: '700', 
                                      color: txRunningBal > 0 ? 'var(--color-inflow)' : txRunningBal < 0 ? 'var(--color-outflow)' : 'var(--text-muted)',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {txRunningBal > 0 ? `+${formatMoney(txRunningBal, tx.currency)} (Owes you)` : txRunningBal < 0 ? `-${formatMoney(-txRunningBal, tx.currency)} (You owe)` : `0.00 ${tx.currency} (Settled)`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {sorted.length > activeItemTxLimit && (
                            <button 
                              type="button"
                              onClick={() => setActiveItemTxLimit(prev => prev + 10)} 
                              className="btn-secondary" 
                              style={{ marginTop: '10px', padding: '8px', fontSize: '12px', width: '100%' }}
                            >
                              Load More Transactions
                            </button>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ) : (
              // Friends List View
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h2 style={{ margin: 0 }}>Friends</h2>
                    <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '4px 0 0 0' }}>
                      Balances aggregated across all shared transactions and groups.
                    </p>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => setActiveModal('friend_create')}
                      className="btn-primary"
                      style={{
                        padding: '8px 16px',
                        borderRadius: '12px',
                        fontSize: '13px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'linear-gradient(135deg, var(--accent-cyan), #00b4d8)',
                        color: 'black',
                        fontWeight: 'bold',
                        border: 'none',
                        boxShadow: '0 2px 10px rgba(0, 245, 212, 0.25)',
                        cursor: 'pointer'
                      }}
                    >
                      <UserPlus size={15} /> Add Friend
                    </button>
                    <button
                      type="button"
                      onClick={() => openExportModal('friends_summary', { name: 'Friends & Ledger Balances' })}
                      className="btn-secondary"
                      style={{ padding: '8px 14px', borderRadius: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Download size={14} /> Export Friends
                    </button>
                  </div>
                </div>

                {/* --- AGGREGATED MULTI-CURRENCY BALANCE PODS --- */}
                {(() => {
                  const { friendsOweMe, iOweFriends, oweMeCurrencies, iOweCurrencies } = friendsAggregatedBalances;
                  const primaryCurr = preferredCurrencies[0] || 'AED';
                  const isOwesYouActive = friendFilterType === 'OWES_YOU';
                  const isYouOweActive = friendFilterType === 'YOU_OWE';

                  return (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                      gap: '12px',
                      marginTop: '4px',
                      marginBottom: '8px'
                    }}>
                      {/* Card 1: Total Friends Owe Me */}
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setFriendFilterType(prev => prev === 'OWES_YOU' ? 'ALL' : 'OWES_YOU')}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setFriendFilterType(prev => prev === 'OWES_YOU' ? 'ALL' : 'OWES_YOU');
                          }
                        }}
                        className="glass-panel"
                        style={{
                          padding: '16px 18px',
                          borderRadius: '14px',
                          background: isOwesYouActive
                            ? 'linear-gradient(145deg, rgba(43, 203, 186, 0.22), rgba(16, 28, 30, 0.95))'
                            : 'linear-gradient(145deg, rgba(43, 203, 186, 0.1), rgba(16, 28, 30, 0.85))',
                          border: isOwesYouActive
                            ? '2px solid var(--color-inflow)'
                            : '1px solid rgba(43, 203, 186, 0.25)',
                          boxShadow: isOwesYouActive
                            ? '0 0 24px rgba(46, 213, 115, 0.3), 0 4px 20px rgba(0, 0, 0, 0.25)'
                            : '0 4px 20px rgba(0, 0, 0, 0.2)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                          transform: isOwesYouActive ? 'scale(1.015)' : 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--color-inflow)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>📥 Total Friends Owe Me</span>
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {isOwesYouActive && (
                              <span style={{ fontSize: '9.5px', background: 'var(--color-inflow)', color: '#000', padding: '2px 7px', borderRadius: '8px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                ✓ ACTIVE
                              </span>
                            )}
                            <span style={{ fontSize: '10px', background: 'rgba(0, 245, 212, 0.15)', color: 'var(--accent-cyan)', padding: '2px 7px', borderRadius: '10px', fontWeight: '700' }}>
                              {oweMeCurrencies.length} {oweMeCurrencies.length === 1 ? 'Currency' : 'Currencies'}
                            </span>
                          </div>
                        </div>

                        {oweMeCurrencies.length === 0 ? (
                          <span style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-muted)', display: 'block' }}>
                            {formatMoney(0, primaryCurr)}
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {oweMeCurrencies.map(cur => (
                              <div key={cur} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--color-inflow)' }}>
                                  {formatMoney(friendsOweMe[cur], cur)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        <span style={{ fontSize: '10px', color: isOwesYouActive ? 'var(--color-inflow)' : 'var(--text-muted)', marginTop: '4px', display: 'block', fontWeight: isOwesYouActive ? '700' : 'normal' }}>
                          {isOwesYouActive ? '✓ Filtering friends who owe you (Click to reset)' : 'Aggregated receivable balance · Click to filter'}
                        </span>
                      </div>

                      {/* Card 2: Total I Owe Friends */}
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setFriendFilterType(prev => prev === 'YOU_OWE' ? 'ALL' : 'YOU_OWE')}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setFriendFilterType(prev => prev === 'YOU_OWE' ? 'ALL' : 'YOU_OWE');
                          }
                        }}
                        className="glass-panel"
                        style={{
                          padding: '16px 18px',
                          borderRadius: '14px',
                          background: isYouOweActive
                            ? 'linear-gradient(145deg, rgba(255, 107, 107, 0.22), rgba(30, 16, 20, 0.95))'
                            : 'linear-gradient(145deg, rgba(255, 107, 107, 0.1), rgba(30, 16, 20, 0.85))',
                          border: isYouOweActive
                            ? '2px solid var(--color-outflow)'
                            : '1px solid rgba(255, 107, 107, 0.25)',
                          boxShadow: isYouOweActive
                            ? '0 0 24px rgba(255, 71, 87, 0.3), 0 4px 20px rgba(0, 0, 0, 0.25)'
                            : '0 4px 20px rgba(0, 0, 0, 0.2)',
                          cursor: 'pointer',
                          userSelect: 'none',
                          transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
                          transform: isYouOweActive ? 'scale(1.015)' : 'none'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--color-outflow)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>📤 Total I Owe Friends</span>
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {isYouOweActive && (
                              <span style={{ fontSize: '9.5px', background: 'var(--color-outflow)', color: '#fff', padding: '2px 7px', borderRadius: '8px', fontWeight: '800', letterSpacing: '0.5px' }}>
                                ✓ ACTIVE
                              </span>
                            )}
                            <span style={{ fontSize: '10px', background: 'rgba(255, 107, 107, 0.15)', color: 'var(--color-outflow)', padding: '2px 7px', borderRadius: '10px', fontWeight: '700' }}>
                              {iOweCurrencies.length} {iOweCurrencies.length === 1 ? 'Currency' : 'Currencies'}
                            </span>
                          </div>
                        </div>

                        {iOweCurrencies.length === 0 ? (
                          <span style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-muted)', display: 'block' }}>
                            {formatMoney(0, primaryCurr)}
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {iOweCurrencies.map(cur => (
                              <div key={cur} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--color-outflow)' }}>
                                  {formatMoney(iOweFriends[cur], cur)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                        <span style={{ fontSize: '10px', color: isYouOweActive ? 'var(--color-outflow)' : 'var(--text-muted)', marginTop: '4px', display: 'block', fontWeight: isYouOweActive ? '700' : 'normal' }}>
                          {isYouOweActive ? '✓ Filtering friends you owe (Click to reset)' : 'Aggregated payable balance · Click to filter'}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* --- FRIENDS CONTROLS BAR: SEARCH, FILTER, SORT --- */}
                {friends.length > 0 && (() => {
                  const allFriendCurrencies = Array.from(new Set(
                    friends.flatMap(f => (f.balances || []).map(b => b.currency))
                  )).filter(Boolean);

                  return (
                    <div style={{
                      position: 'sticky',
                      top: '10px',
                      zIndex: 20,
                      padding: '12px 14px',
                      marginBottom: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      background: 'rgba(18, 20, 32, 0.95)',
                      backdropFilter: 'blur(16px)',
                      WebkitBackdropFilter: 'blur(16px)',
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.45)',
                      borderRadius: '14px'
                    }}>
                      {/* Row 1: Search Input */}
                      <div style={{ width: '100%', position: 'relative' }}>
                        <input
                          type="text"
                          placeholder="🔍 Search by name, email, bio, phone..."
                          value={friendSearchQuery}
                          onChange={e => setFriendSearchQuery(e.target.value)}
                          style={{ width: '100%', padding: '9px 12px', fontSize: '12px', borderRadius: '8px', boxSizing: 'border-box' }}
                        />
                      </div>

                      {/* Row 2: Filter & Sort Side by Side (Mobile & Desktop) */}
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', width: '100%', flexWrap: 'wrap' }}>
                        {/* Filter Dropdown */}
                        <div style={{ flex: 1, minWidth: '130px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>Filter:</label>
                          <select
                            value={friendFilterType}
                            onChange={e => setFriendFilterType(e.target.value)}
                            style={{ width: '100%', minWidth: 0, padding: '7px 8px', fontSize: '11px', borderRadius: '8px', background: 'var(--bg-input)', color: 'var(--text-main)', border: '1px solid var(--border-glass)', boxSizing: 'border-box' }}
                          >
                            <option value="ALL">All Friends</option>
                            <option value="USER">Registered Users</option>
                            <option value="PLACEHOLDER">Placeholders</option>
                            <option value="OWES_YOU">Owes You</option>
                            <option value="YOU_OWE">You Owe</option>
                            <option value="SETTLED">Settled (0)</option>
                            <option value="HIDDEN">Hidden Friends ({friends.filter(f => f.is_hidden).length})</option>
                          </select>
                        </div>

                        {/* Sort Dropdown */}
                        <div style={{ flex: 1, minWidth: '130px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>Sort:</label>
                          <select
                            value={friendSortOrder}
                            onChange={e => setFriendSortOrder(e.target.value)}
                            style={{ width: '100%', minWidth: 0, padding: '7px 8px', fontSize: '11px', borderRadius: '8px', background: 'var(--bg-input)', color: 'var(--text-main)', border: '1px solid var(--border-glass)', boxSizing: 'border-box' }}
                          >
                            <option value="NAME_ASC">Name (A - Z)</option>
                            <option value="NAME_DESC">Name (Z - A)</option>
                            <option value="AMOUNT_DESC">Amount (High → Low)</option>
                            <option value="AMOUNT_ASC">Amount (Low → High)</option>
                          </select>
                        </div>

                        {/* Currency Selector for Amount Sorting */}
                        {(friendSortOrder === 'AMOUNT_DESC' || friendSortOrder === 'AMOUNT_ASC') && (
                          <div style={{ flex: '1 1 100%', display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                            <label style={{ fontSize: '11px', color: 'var(--accent-cyan)', margin: 0, whiteSpace: 'nowrap', flexShrink: 0 }}>Currency:</label>
                            <select
                              value={friendSortCurrency}
                              onChange={e => setFriendSortCurrency(e.target.value)}
                              style={{ width: '100%', minWidth: 0, padding: '7px 8px', fontSize: '11px', borderRadius: '8px', background: 'var(--bg-input)', color: 'var(--accent-cyan)', border: '1px solid var(--accent-cyan)', boxSizing: 'border-box' }}
                            >
                              <option value="ALL">All Currencies (Net Sum)</option>
                              {allFriendCurrencies.map(curr => (
                                <option key={curr} value={curr}>{curr} Only</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '10px' }}>
                  {(() => {
                    const filteredAndSortedFriends = friends.filter(friend => {
                      // Search Filter
                      if (friendSearchQuery.trim()) {
                        const q = friendSearchQuery.toLowerCase().trim();
                        const nameMatch = (friend.display_name && friend.display_name.toLowerCase().includes(q)) ||
                                          (friend.nickname && friend.nickname.toLowerCase().includes(q));
                        const emailMatch = friend.email && friend.email.toLowerCase().includes(q);
                        const bioMatch = friend.bio && friend.bio.toLowerCase().includes(q);
                        const phoneMatch = friend.phone_number && friend.phone_number.includes(q);
                        if (!nameMatch && !emailMatch && !bioMatch && !phoneMatch) {
                          return false;
                        }
                      }

                      // Hidden Friends Filter
                      if (friendFilterType === 'HIDDEN') {
                        if (!friend.is_hidden) return false;
                      } else {
                        // By default, exclude hidden friends from standard list views unless user is actively searching
                        if (friend.is_hidden && !friendSearchQuery.trim()) return false;
                      }

                      // User vs Placeholder & Balance Filter
                      if (friendFilterType === 'USER') {
                        if (friend.is_placeholder) return false;
                      } else if (friendFilterType === 'PLACEHOLDER') {
                        if (!friend.is_placeholder) return false;
                      } else if (friendFilterType === 'OWES_YOU') {
                        const owesYou = (friend.balances && friend.balances.length > 0)
                          ? friend.balances.some(b => (parseFloat(b.amount) || 0) > 0.001)
                          : Object.values(computeFriendDetailedBalances(friend)).some(db => (db.overall || 0) > 0.001);
                        if (!owesYou) return false;
                      } else if (friendFilterType === 'YOU_OWE') {
                        const youOwe = (friend.balances && friend.balances.length > 0)
                          ? friend.balances.some(b => (parseFloat(b.amount) || 0) < -0.001)
                          : Object.values(computeFriendDetailedBalances(friend)).some(db => (db.overall || 0) < -0.001);
                        if (!youOwe) return false;
                      } else if (friendFilterType === 'SETTLED') {
                        const hasBalance = (friend.balances || []).some(b => Math.abs(b.amount) > 0.001);
                        if (hasBalance) return false;
                      }

                      return true;
                    }).sort((a, b) => {
                      if (friendSortOrder === 'NAME_ASC') {
                        const nameA = (a.nickname || a.display_name || a.email || '').toLowerCase();
                        const nameB = (b.nickname || b.display_name || b.email || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                      } else if (friendSortOrder === 'NAME_DESC') {
                        const nameA = (a.nickname || a.display_name || a.email || '').toLowerCase();
                        const nameB = (b.nickname || b.display_name || b.email || '').toLowerCase();
                        return nameB.localeCompare(nameA);
                      } else if (friendSortOrder === 'AMOUNT_DESC' || friendSortOrder === 'AMOUNT_ASC') {
                        const getBalVal = (frnd) => {
                          if (!frnd.balances || frnd.balances.length === 0) return 0;
                          if (friendSortCurrency === 'ALL') {
                            return frnd.balances.reduce((sum, bal) => sum + (parseFloat(bal.amount) || 0), 0);
                          }
                          const bObj = frnd.balances.find(bal => bal.currency === friendSortCurrency);
                          return bObj ? parseFloat(bObj.amount) || 0 : 0;
                        };
                        const valA = getBalVal(a);
                        const valB = getBalVal(b);
                        return friendSortOrder === 'AMOUNT_DESC' ? valB - valA : valA - valB;
                      }
                      return 0;
                    });

                    if (friends.length === 0) {
                      return (
                        <p style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                          No friends added yet. Enter their email address above.
                        </p>
                      );
                    }

                    if (filteredAndSortedFriends.length === 0) {
                      return (
                        <p style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                          No friends found matching your search and filter criteria.
                        </p>
                      );
                    }

                    return filteredAndSortedFriends.map(friend => {
                      const hasBalances = friend.balances && friend.balances.length > 0;
                      return (
                        <div key={friend.user_id} className="glass-panel" onClick={() => { setActiveFriend(friend); setActiveItemTxLimit(10); }} style={{ padding: '16px', cursor: 'pointer' }}>
                          <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                              <div style={{ position: 'relative', width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden', border: '1.5px solid var(--accent-cyan)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ fontSize: '17px', fontWeight: 'bold', color: 'var(--accent-cyan)', position: 'absolute' }}>
                                  {(friend.nickname || friend.display_name || friend.email || 'F')[0].toUpperCase()}
                                </div>
                                {friend.avatar_url && (
                                  <img src={apiClient.getAvatarUrl(friend.avatar_url)} alt="Avatar" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'relative', zIndex: 1 }} />
                                )}
                              </div>
                              <div>
                                <span style={{ fontWeight: '600', fontSize: '15px' }}>
                                  {friend.nickname || friend.display_name || friend.email.split('@')[0]}
                                  <span style={{ marginLeft: '6px', fontSize: '13px' }}>
                                    {friend.is_placeholder ? '👤' : (friend.gender?.toLowerCase() === 'female' || friend.gender?.toLowerCase() === 'f' ? '👧' : '👦')}
                                  </span>
                                  {friend.nickname && <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'normal', marginLeft: '6px' }}>({friend.display_name})</span>}
                                  {friend.is_hidden && (
                                    <span style={{ marginLeft: '8px', fontSize: '10.5px', padding: '1px 7px', borderRadius: '10px', background: 'rgba(255, 107, 107, 0.15)', color: 'var(--color-outflow)', border: '1px solid rgba(255, 107, 107, 0.3)', fontWeight: '600' }}>
                                      🙈 Hidden
                                    </span>
                                  )}
                                </span>
                                {!friend.is_placeholder ? (
                                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                                    {friend.email}
                                  </span>
                                ) : (
                                  <span style={{ fontSize: '11px', color: 'var(--accent-cyan)', display: 'block', marginTop: '2px' }}>
                                    Placeholder Friend
                                  </span>
                                )}
                                {friend.bio && (
                                  <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginTop: '4px', fontStyle: 'italic' }}>
                                    "{friend.bio}"
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ textAlign: 'right' }}>
                                {!hasBalances ? (
                                  <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 'bold' }}>Settled</span>
                                ) : (
                                  friend.balances.map((b, i) => {
                                    const owesYou = b.amount > 0;
                                    return (
                                      <div key={i} style={{
                                        fontSize: '13px', fontWeight: '700',
                                        color: owesYou ? 'var(--color-inflow)' : 'var(--color-outflow)'
                                      }}>
                                        {owesYou ? 'Owes you: ' : 'You owe: '}
                                        {formatMoney(Math.abs(b.amount), b.currency)}
                                      </div>
                                    );
                                  })
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </>
            )}
          </div>
        )}

        {/* VIEW: TRANSFERS */}
        {activeTab === 'transfers' && (() => {
          const fromAcc = (tfFromType === 'ACCOUNT' || tfFromType === 'CREDIT_CARD') ? accounts.find(a => a.account_id === tfFromAccountId) : null;
          const toAcc = (tfToType === 'ACCOUNT' || tfToType === 'CREDIT_CARD') ? accounts.find(a => a.account_id === tfToAccountId) : null;
          
          let sourceCurrency = 'AED';
          if (tfFromType === 'ACCOUNT' || tfFromType === 'CREDIT_CARD') {
            sourceCurrency = fromAcc?.currency || preferredCurrencies[0] || 'AED';
          } else if (tfFromType === 'FRIEND') {
            sourceCurrency = tfConvertCurrency ? tfFriendCurrency : (toAcc?.currency || preferredCurrencies[0] || 'AED');
          }

          let targetCurrency = 'AED';
          if (tfToType === 'ACCOUNT' || tfToType === 'CREDIT_CARD') {
            targetCurrency = toAcc?.currency || preferredCurrencies[0] || 'AED';
          } else if (tfToType === 'FRIEND') {
            targetCurrency = tfConvertCurrency ? tfFriendCurrency : (fromAcc?.currency || preferredCurrencies[0] || 'AED');
          }

          const isMultiCurrency = sourceCurrency !== targetCurrency;
          const bothSelected = tfFromAccountId && tfToAccountId;
          const myUserIdForTf = localStorage.getItem('dhudd_user_id');
          const transferTxs = transactions.filter(tx => {
            if (!isTransferTransaction(tx)) return false;
            if (myUserIdForTf && tx.user_id !== myUserIdForTf && transactions.some(t => t.source_transaction_id === tx.transaction_id && t.user_id === myUserIdForTf)) {
              return false;
            }
            if (myUserIdForTf && tx.user_id !== myUserIdForTf && tx.source_transaction_id && transactions.some(t => t.transaction_id === tx.source_transaction_id && t.user_id === myUserIdForTf)) {
              return false;
            }
            return true;
          });
          const sortedTransfers = [...transferTxs].sort((a, b) => new Date(b.date) - new Date(a.date));
          const visibleTransfers = sortedTransfers.slice(0, visibleTransfersLimit);

          const txCurrencyLabel = sourceCurrency;

          const handleToggleTfAutoConvert = (checked) => {
            setTfAutoConvert(checked);
            localStorage.setItem('dhudd_tf_auto_convert', checked ? 'true' : 'false');
            if (checked && tfFromAmount && parseFloat(tfFromAmount) > 0) {
              const converted = convertCurrency(parseFloat(tfFromAmount), sourceCurrency, targetCurrency);
              setTfToAmount(converted > 0 ? parseFloat(converted.toFixed(4)).toString() : '');
            }
          };

          const handleTfFromAmountChange = (e) => {
            const val = e.target.value;
            setTfFromAmount(val);
            if (!isMultiCurrency) {
              setTfToAmount(val);
            } else if (tfAutoConvert) {
              if (val === '' || isNaN(parseFloat(val))) {
                setTfToAmount('');
              } else {
                const converted = convertCurrency(parseFloat(val), sourceCurrency, targetCurrency);
                setTfToAmount(converted > 0 ? parseFloat(converted.toFixed(4)).toString() : '');
              }
            }
          };

          const handleTfToAmountChange = (e) => {
            const val = e.target.value;
            setTfToAmount(val);
            if (!isMultiCurrency) {
              setTfFromAmount(val);
            } else if (tfAutoConvert && (!tfFromAmount || tfFromAmount === '')) {
              if (val === '' || isNaN(parseFloat(val))) {
                setTfFromAmount('');
              } else {
                const converted = convertCurrency(parseFloat(val), targetCurrency, sourceCurrency);
                setTfFromAmount(converted > 0 ? parseFloat(converted.toFixed(4)).toString() : '');
              }
            }
          };
          
          return (
            <div className="animate-fade-in" style={{ padding: '20px' }}>
              <h2>Transfers</h2>
              <form onSubmit={handleTransfer} className="glass-panel" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '12px' }}>
                <div>
                  <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ margin: 0 }}>From (Source)</label>
                    {tfFromType === 'ACCOUNT' && fromAcc && (() => {
                      const displayBal = fromAcc.type === 'CREDIT_CARD' ? -parseFloat(fromAcc.current_balance) : parseFloat(fromAcc.current_balance);
                      return (
                        <span style={{ 
                          fontSize: '11px', 
                          background: fromAcc.type === 'CREDIT_CARD' ? 'rgba(123,44,191,0.2)' : 'rgba(0,245,212,0.15)',
                          border: '1px solid var(--border-glass)', 
                          padding: '2px 8px', 
                          borderRadius: '8px', 
                          color: displayBal >= 0 ? 'var(--accent-cyan)' : 'var(--color-outflow)',
                          fontWeight: 'bold'
                        }}>
                          Balance: {formatMoney(displayBal, fromAcc.currency)}
                        </span>
                      );
                    })()}
                  </div>

                  <div style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.05)', padding: '2px', borderRadius: '6px', border: '1px solid var(--border-glass)', gap: '4px', marginBottom: '6px' }}>
                    <button
                      type="button"
                      onClick={() => { setTfFromType('ACCOUNT'); setTfFromAccountId(''); }}
                      style={{
                        flex: 1,
                        padding: '6px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                        background: tfFromType === 'ACCOUNT' ? 'var(--accent-purple)' : 'transparent',
                        color: tfFromType === 'ACCOUNT' ? 'white' : 'var(--text-muted)'
                      }}
                    >
                      🏦 Account
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTfFromType('CREDIT_CARD'); setTfFromAccountId(''); }}
                      style={{
                        flex: 1,
                        padding: '6px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                        background: tfFromType === 'CREDIT_CARD' ? 'var(--accent-purple)' : 'transparent',
                        color: tfFromType === 'CREDIT_CARD' ? 'white' : 'var(--text-muted)'
                      }}
                    >
                      💳 Credit Card
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTfFromType('FRIEND'); setTfFromAccountId(''); }}
                      style={{
                        flex: 1,
                        padding: '6px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                        background: tfFromType === 'FRIEND' ? 'var(--accent-purple)' : 'transparent',
                        color: tfFromType === 'FRIEND' ? 'white' : 'var(--text-muted)'
                      }}
                    >
                      🤝 Friend
                    </button>
                  </div>

                  {tfFromType === 'ACCOUNT' ? (
                    <CustomAccountSelect
                      value={tfFromAccountId}
                      onChange={e => setTfFromAccountId(e.target.value)}
                      accounts={accounts}
                      filter={a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && a.type !== 'CREDIT_CARD' && a.account_type !== 'CREDIT_CARD' && !(a.name && a.name.includes('External Debts'))}
                      placeholder="Select Cash / Bank Account"
                      formatMoney={formatMoney}
                      required
                    />
                  ) : tfFromType === 'CREDIT_CARD' ? (
                    <CustomAccountSelect
                      value={tfFromAccountId}
                      onChange={e => setTfFromAccountId(e.target.value)}
                      accounts={accounts}
                      filter={a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && (a.type === 'CREDIT_CARD' || a.account_type === 'CREDIT_CARD')}
                      placeholder="Select Credit Card"
                      formatMoney={formatMoney}
                      required
                    />
                  ) : (
                    <div>
                      {friends.length === 0 ? (
                        <p style={{ fontSize: '11px', color: 'var(--color-outflow)', fontStyle: 'italic', margin: '4px 0' }}>
                          No friends added yet. Please add friends in the Friends tab first.
                        </p>
                      ) : (
                        <select value={tfFromAccountId} onChange={e => setTfFromAccountId(e.target.value)} required>
                          <option value="">Select Friend</option>
                          {sortFriendsAZ(friends).map(friend => (
                            <option key={friend.user_id} value={friend.user_id}>
                              {formatFriendLabel(friend)}
                            </option>
                          ))}
                        </select>
                      )}
                      {tfFromAccountId && (
                        <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Friend Balance:</span>
                          {renderFriendBalanceTiles(tfFromAccountId)}
                        </div>
                      )}

                      {/* Convert Currency option when Friend is Source */}
                      {tfFromAccountId && (
                        <div style={{
                          marginTop: '8px',
                          padding: '10px 12px',
                          background: 'rgba(255, 255, 255, 0.03)',
                          borderRadius: '10px',
                          border: '1px solid var(--border-glass)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label style={{ margin: 0, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={tfConvertCurrency}
                                onChange={e => {
                                  const checked = e.target.checked;
                                  setTfConvertCurrency(checked);
                                  if (!checked) {
                                    const baseCurr = toAcc?.currency || preferredCurrencies[0] || 'AED';
                                    setTfFriendCurrency(baseCurr);
                                    setTfToAmount(tfFromAmount);
                                  }
                                }}
                                style={{ width: '16px', height: '16px', accentColor: 'var(--accent-cyan)', cursor: 'pointer' }}
                              />
                              <span style={{ fontWeight: '600', color: tfConvertCurrency ? 'var(--accent-cyan)' : 'var(--text-main)' }}>
                                ⇄ Convert Currency
                              </span>
                            </label>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              {tfConvertCurrency ? 'Custom Currency' : `Default: ${toAcc?.currency || preferredCurrencies[0] || 'AED'}`}
                            </span>
                          </div>

                          {tfConvertCurrency && (
                            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                                Friend's Debit Currency:
                              </label>
                              <select
                                value={tfFriendCurrency}
                                onChange={e => {
                                  const newCurr = e.target.value;
                                  setTfFriendCurrency(newCurr);
                                  if (tfAutoConvert && tfFromAmount && parseFloat(tfFromAmount) > 0) {
                                    const converted = convertCurrency(parseFloat(tfFromAmount), newCurr, toAcc?.currency || 'AED');
                                    setTfToAmount(converted > 0 ? parseFloat(converted.toFixed(4)).toString() : '');
                                  }
                                }}
                                style={{
                                  padding: '8px 10px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  background: 'rgba(0, 245, 212, 0.05)',
                                  border: '1px solid var(--accent-cyan)',
                                  borderRadius: '8px',
                                  color: 'var(--accent-cyan)'
                                }}
                              >
                                {preferredCurrencies.map(curr => (
                                  <option key={curr} value={curr}>
                                    {curr} {curr === toAcc?.currency ? '(Same as destination account)' : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                    <label style={{ margin: 0 }}>To (Destination)</label>
                    {(tfToType === 'ACCOUNT' || tfToType === 'CREDIT_CARD') && toAcc && (() => {
                      const displayBal = toAcc.type === 'CREDIT_CARD' ? -parseFloat(toAcc.current_balance) : parseFloat(toAcc.current_balance);
                      return (
                        <span style={{ 
                          fontSize: '11px', 
                          background: toAcc.type === 'CREDIT_CARD' ? 'rgba(123,44,191,0.2)' : 'rgba(0,245,212,0.15)',
                          border: '1px solid var(--border-glass)', 
                          padding: '2px 8px', 
                          borderRadius: '8px', 
                          color: displayBal >= 0 ? 'var(--accent-cyan)' : 'var(--color-outflow)',
                          fontWeight: 'bold'
                        }}>
                          Balance: {formatMoney(displayBal, toAcc.currency)}
                        </span>
                      );
                    })()}
                  </div>

                  <div style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.05)', padding: '2px', borderRadius: '6px', border: '1px solid var(--border-glass)', gap: '4px', marginBottom: '6px' }}>
                    <button
                      type="button"
                      onClick={() => { setTfToType('ACCOUNT'); setTfToAccountId(''); }}
                      style={{
                        flex: 1,
                        padding: '6px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                        background: tfToType === 'ACCOUNT' ? 'var(--accent-purple)' : 'transparent',
                        color: tfToType === 'ACCOUNT' ? 'white' : 'var(--text-muted)'
                      }}
                    >
                      🏦 Account
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTfToType('CREDIT_CARD'); setTfToAccountId(''); }}
                      style={{
                        flex: 1,
                        padding: '6px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                        background: tfToType === 'CREDIT_CARD' ? 'var(--accent-purple)' : 'transparent',
                        color: tfToType === 'CREDIT_CARD' ? 'white' : 'var(--text-muted)'
                      }}
                    >
                      💳 Credit Card
                    </button>
                    <button
                      type="button"
                      onClick={() => { setTfToType('FRIEND'); setTfToAccountId(''); }}
                      style={{
                        flex: 1,
                        padding: '6px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px',
                        border: 'none',
                        cursor: 'pointer',
                        background: tfToType === 'FRIEND' ? 'var(--accent-purple)' : 'transparent',
                        color: tfToType === 'FRIEND' ? 'white' : 'var(--text-muted)'
                      }}
                    >
                      🤝 Friend
                    </button>
                  </div>

                  {tfToType === 'ACCOUNT' ? (
                    <CustomAccountSelect
                      value={tfToAccountId}
                      onChange={e => setTfToAccountId(e.target.value)}
                      accounts={accounts}
                      filter={a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && a.type !== 'CREDIT_CARD' && a.account_type !== 'CREDIT_CARD' && !(a.name && a.name.includes('External Debts'))}
                      placeholder="Select Target Cash / Bank Account"
                      formatMoney={formatMoney}
                      required
                    />
                  ) : tfToType === 'CREDIT_CARD' ? (
                    <CustomAccountSelect
                      value={tfToAccountId}
                      onChange={e => setTfToAccountId(e.target.value)}
                      accounts={accounts}
                      filter={a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && (a.type === 'CREDIT_CARD' || a.account_type === 'CREDIT_CARD')}
                      placeholder="Select Target Credit Card"
                      formatMoney={formatMoney}
                      required
                    />
                  ) : (
                    <div>
                      {friends.length === 0 ? (
                        <p style={{ fontSize: '11px', color: 'var(--color-outflow)', fontStyle: 'italic', margin: '4px 0' }}>
                          No friends added yet. Please add friends in the Friends tab first.
                        </p>
                      ) : (
                        <select value={tfToAccountId} onChange={e => setTfToAccountId(e.target.value)} required>
                          <option value="">Select Friend</option>
                          {sortFriendsAZ(friends).map(friend => (
                            <option key={friend.user_id} value={friend.user_id}>
                              {formatFriendLabel(friend)}
                            </option>
                          ))}
                        </select>
                      )}
                      {tfToAccountId && (
                        <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Friend Balance:</span>
                          {renderFriendBalanceTiles(tfToAccountId)}
                        </div>
                      )}

                      {/* Convert Currency option when Friend is Target */}
                      {tfToAccountId && (
                        <div style={{
                          marginTop: '8px',
                          padding: '10px 12px',
                          background: 'rgba(255, 255, 255, 0.03)',
                          borderRadius: '10px',
                          border: '1px solid var(--border-glass)',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label style={{ margin: 0, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={tfConvertCurrency}
                                onChange={e => {
                                  const checked = e.target.checked;
                                  setTfConvertCurrency(checked);
                                  if (!checked) {
                                    const baseCurr = fromAcc?.currency || preferredCurrencies[0] || 'AED';
                                    setTfFriendCurrency(baseCurr);
                                    setTfToAmount(tfFromAmount);
                                  }
                                }}
                                style={{ width: '16px', height: '16px', accentColor: 'var(--accent-cyan)', cursor: 'pointer' }}
                              />
                              <span style={{ fontWeight: '600', color: tfConvertCurrency ? 'var(--accent-cyan)' : 'var(--text-main)' }}>
                                ⇄ Convert Currency
                              </span>
                            </label>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              {tfConvertCurrency ? 'Custom Currency' : `Default: ${fromAcc?.currency || preferredCurrencies[0] || 'AED'}`}
                            </span>
                          </div>

                          {tfConvertCurrency && (
                            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              <label style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                                Credit Friend's Balance in Currency:
                              </label>
                              <select
                                value={tfFriendCurrency}
                                onChange={e => {
                                  const newCurr = e.target.value;
                                  setTfFriendCurrency(newCurr);
                                  if (tfAutoConvert && tfFromAmount && parseFloat(tfFromAmount) > 0) {
                                    const converted = convertCurrency(parseFloat(tfFromAmount), fromAcc?.currency || 'AED', newCurr);
                                    setTfToAmount(converted > 0 ? parseFloat(converted.toFixed(4)).toString() : '');
                                  }
                                }}
                                style={{
                                  padding: '8px 10px',
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  background: 'rgba(0, 245, 212, 0.05)',
                                  border: '1px solid var(--accent-cyan)',
                                  borderRadius: '8px',
                                  color: 'var(--accent-cyan)'
                                }}
                              >
                                {preferredCurrencies.map(curr => (
                                  <option key={curr} value={curr}>
                                    {curr} {curr === fromAcc?.currency ? '(Same as source account)' : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {bothSelected && (
                  <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {!isMultiCurrency ? (
                      <div>
                        <label>Amount ({txCurrencyLabel})</label>
                        <input 
                          type="number" 
                          step="0.0001" 
                          placeholder="0.00" 
                          value={tfFromAmount} 
                          onChange={handleTfFromAmountChange} 
                          required 
                        />
                      </div>
                    ) : (
                      <>
                        {/* Auto-Convert Toggle */}
                        <div style={{
                          padding: '10px 12px',
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid var(--border-glass)',
                          borderRadius: '10px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <label style={{ margin: 0, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={tfAutoConvert}
                                onChange={e => handleToggleTfAutoConvert(e.target.checked)}
                                style={{ width: '16px', height: '16px', accentColor: 'var(--accent-cyan)', cursor: 'pointer' }}
                              />
                              <span style={{ fontWeight: '600', color: tfAutoConvert ? 'var(--accent-cyan)' : 'var(--text-main)' }}>
                                Convert automatically using last recorded rate
                              </span>
                            </label>
                          </div>
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            {tfAutoConvert
                              ? 'Auto-calculates amounts using your saved conversion rate. You can also customize amounts manually.'
                              : 'Manual custom mode. Enter desired source and target amounts — your custom rate will be saved for future transfers.'}
                          </span>
                        </div>

                        <div>
                          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Source Amount ({sourceCurrency})</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              {tfFromType === 'FRIEND' ? 'Debited from Friend' : `Debited from ${fromAcc?.name || 'Source'}`}
                            </span>
                          </label>
                          <input 
                            type="number" 
                            step="0.0001" 
                            placeholder="0.00" 
                            value={tfFromAmount} 
                            onChange={handleTfFromAmountChange} 
                            required 
                          />
                        </div>
                        <div>
                          <label style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>Target Amount ({targetCurrency}) (After Conversion)</span>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              {tfToType === 'FRIEND' ? "Credited in Friend's Balance" : `Credited to ${toAcc?.name || 'Destination'}`}
                            </span>
                          </label>
                          <input 
                            type="number" 
                            step="0.0001" 
                            placeholder="0.00" 
                            value={tfToAmount} 
                            onChange={handleTfToAmountChange} 
                            required 
                          />
                        </div>
                        {(() => {
                          const latest = getLatestRecordedRate(sourceCurrency, targetCurrency);
                          const hasBoth = parseFloat(tfFromAmount) > 0 && parseFloat(tfToAmount) > 0;
                          const currentRate = hasBoth ? (parseFloat(tfToAmount) / parseFloat(tfFromAmount)) : latest.rate;
                          const isCustom = hasBoth && Math.abs(currentRate - latest.rate) > 0.0001;

                          return (
                            <div style={{
                              fontSize: '11px',
                              padding: '8px 12px',
                              background: isCustom ? 'rgba(192, 132, 252, 0.12)' : 'rgba(0, 245, 212, 0.08)',
                              border: `1px solid ${isCustom ? 'rgba(192, 132, 252, 0.35)' : 'rgba(0, 245, 212, 0.25)'}`,
                              borderRadius: '8px',
                              color: isCustom ? 'var(--accent-purple)' : 'var(--accent-cyan)',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{ fontWeight: '600' }}>
                                {isCustom ? '⭐ Custom Rate (Will be saved on transfer):' : '📌 Last Recorded Rate:'}
                              </span>
                              <span style={{ fontWeight: 'bold' }}>
                                1 {sourceCurrency} = {currentRate.toFixed(4)} {targetCurrency}
                              </span>
                            </div>
                          );
                        })()}
                      </>
                    )}
                    <div>
                      <label>Transfer Date</label>
                      <input 
                        type="date" 
                        value={tfDate} 
                        onChange={e => setTfDate(e.target.value)} 
                        style={{ width: '100%', padding: '8px', fontSize: '13px' }}
                        required 
                      />
                    </div>
                    <div>
                      <label>Description</label>
                      <div style={{ position: 'relative' }}>
                        <input 
                          type="text" 
                          placeholder="e.g. Paid card dues" 
                          value={tfDesc} 
                          onChange={handleTfDescChange} 
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setTfDescSuggestions([]);
                          }}
                          onBlur={() => {
                            setTimeout(() => setTfDescSuggestions([]), 250);
                          }}
                          autoComplete="off"
                        />
                        {tfDescSuggestions.length > 0 && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '44px',
                              left: 0,
                              right: 0,
                              zIndex: 1000,
                              background: 'rgba(20, 24, 40, 0.97)',
                              backdropFilter: 'blur(20px)',
                              border: '1px solid var(--border-glass)',
                              borderRadius: '12px',
                              boxShadow: 'var(--shadow-premium)',
                              padding: '6px',
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '2px',
                              maxHeight: '190px',
                              overflowY: 'auto'
                            }}
                          >
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '4px 8px 6px 8px',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                              fontSize: '11px',
                              color: 'var(--text-muted)'
                            }}>
                              <span>💡 Suggestions</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setTfDescSuggestions([]);
                                }}
                                style={{
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  border: 'none',
                                  color: 'var(--text-main)',
                                  borderRadius: '6px',
                                  padding: '2px 8px',
                                  fontSize: '11px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <X size={12} /> Close
                              </button>
                            </div>
                            {tfDescSuggestions.map((sug, i) => (
                              <div
                                key={i}
                                onClick={() => {
                                  setTfDesc(sug);
                                  setTfDescSuggestions([]);
                                }}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  borderRadius: '8px',
                                  fontSize: '13px',
                                  color: 'var(--text-main)',
                                  transition: 'background 0.2s',
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)'}
                                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                              >
                                {sug}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Transaction Charge Field */}
                    <div style={{
                      padding: '12px 14px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid var(--border-glass)',
                      borderRadius: '10px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px'
                    }}>
                      <div>
                        <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                          Transaction charge
                        </label>
                        <input 
                          type="number" 
                          step="0.0001" 
                          placeholder="0.00" 
                          value={tfCharges} 
                          onChange={e => setTfCharges(e.target.value)} 
                          style={{ width: '100%', padding: '9px 12px', fontSize: '13px', boxSizing: 'border-box' }}
                        />
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginTop: '4px' }}>
                          💡 Recorded as an expense in Bank/Card Charges category with description "Transaction charge for {tfDesc || (tfFromType === 'FRIEND' && tfToType === 'FRIEND' ? 'Transfer' : (tfFromType === 'FRIEND' ? 'Transfer from Friend' : (tfToType === 'FRIEND' ? 'Transfer to Friend' : 'Inter-Account Transfer')))}"
                        </span>
                      </div>

                      {/* Payer Account Radio / Toggle Selection */}
                      {parseFloat(tfCharges || 0) > 0 && (
                        <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '6px', borderTop: '1px solid rgba(255, 255, 255, 0.06)', paddingTop: '8px' }}>
                          <label style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: 'bold', margin: 0 }}>
                            Debit Transaction Charge From:
                          </label>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <label style={{
                              flex: 1,
                              minWidth: '140px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 10px',
                              borderRadius: '8px',
                              background: tfChargePayer === 'SOURCE' ? 'rgba(0, 245, 212, 0.12)' : 'rgba(255, 255, 255, 0.04)',
                              border: `1px solid ${tfChargePayer === 'SOURCE' ? 'var(--accent-cyan)' : 'var(--border-glass)'}`,
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: tfChargePayer === 'SOURCE' ? '700' : '500',
                              color: tfChargePayer === 'SOURCE' ? 'var(--accent-cyan)' : 'var(--text-main)',
                              transition: 'all 0.2s ease'
                            }}>
                              <input
                                type="radio"
                                name="tfChargePayer"
                                value="SOURCE"
                                checked={tfChargePayer === 'SOURCE'}
                                onChange={() => setTfChargePayer('SOURCE')}
                                style={{ accentColor: 'var(--accent-cyan)', cursor: 'pointer' }}
                              />
                              <span>
                                {tfFromType === 'FRIEND' ? 'Source (Friend)' : `Source (${fromAcc?.name || 'Source Account'})`}
                              </span>
                            </label>

                            <label style={{
                              flex: 1,
                              minWidth: '140px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 10px',
                              borderRadius: '8px',
                              background: tfChargePayer === 'DESTINATION' ? 'rgba(0, 245, 212, 0.12)' : 'rgba(255, 255, 255, 0.04)',
                              border: `1px solid ${tfChargePayer === 'DESTINATION' ? 'var(--accent-cyan)' : 'var(--border-glass)'}`,
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: tfChargePayer === 'DESTINATION' ? '700' : '500',
                              color: tfChargePayer === 'DESTINATION' ? 'var(--accent-cyan)' : 'var(--text-main)',
                              transition: 'all 0.2s ease'
                            }}>
                              <input
                                type="radio"
                                name="tfChargePayer"
                                value="DESTINATION"
                                checked={tfChargePayer === 'DESTINATION'}
                                onChange={() => setTfChargePayer('DESTINATION')}
                                style={{ accentColor: 'var(--accent-cyan)', cursor: 'pointer' }}
                              />
                              <span>
                                {tfToType === 'FRIEND' ? 'Destination (Friend)' : `Destination (${toAcc?.name || 'Destination Account'})`}
                              </span>
                            </label>
                          </div>
                        </div>
                      )}
                    </div>

                    <button type="submit" className="btn-primary" disabled={isSubmittingTx}>
                      {isSubmittingTx ? <ThinkingOrbLoader popup state="solving" size={20} color="var(--accent-cyan)" text="Executing Transfer..." /> : 'Execute Transfer'}
                    </button>
                  </div>
                )}
              </form>

              {/* Transfer History Section */}
              <div style={{ marginTop: '30px' }}>
                <h3 style={{ marginBottom: '12px' }}>Transfer History</h3>
                {sortedTransfers.length === 0 ? (
                  <p style={{ padding: '20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }} className="glass-panel">
                    No transfers recorded yet.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div className="glass-panel" style={{ padding: '10px 0' }}>
                      {visibleTransfers.map((tx, idx) => {
                        const tfDetails = getTransferDetails(tx);
                        const dir = tfDetails?.dir || getTransferDirection(tx, accounts);
                        const counterparty = tfDetails?.counterparty || getTransferCounterparty(tx, dir);
                        const dateStr = formatDateTime(tx.date);

                        let labelText = '';
                        let badgeText = 'Transfer';
                        let badgeColor = 'var(--accent-cyan)';
                        let amtColor = 'var(--accent-cyan)';

                        if (dir === 'inward') {
                          labelText = `📥 ${counterparty} → ${tfDetails?.targetName || 'Your Account'}`;
                          badgeText = tfDetails?.isInterCurrency ? 'Incoming (Inter-Currency)' : 'Incoming';
                          badgeColor = 'var(--color-inflow)';
                          amtColor = 'var(--color-inflow)';
                        } else if (dir === 'outward') {
                          labelText = `📤 ${tfDetails?.sourceName || 'Your Account'} → ${counterparty}`;
                          badgeText = tfDetails?.isInterCurrency ? 'Outgoing (Inter-Currency)' : 'Outgoing';
                          badgeColor = 'var(--color-outflow)';
                          amtColor = 'var(--color-outflow)';
                        } else {
                          labelText = `🔄 ${tfDetails?.sourceName || 'Source'} → ${tfDetails?.targetName || 'Target'}`;
                          badgeText = tfDetails?.isInterCurrency ? 'Inter-Currency' : 'Transfer';
                          badgeColor = 'var(--accent-cyan)';
                          amtColor = 'var(--accent-cyan)';
                        }

                        return (
                          <div 
                            key={tx.transaction_id} 
                            onClick={() => setSelectedTxDetail(tx)}
                            style={{ 
                              padding: '12px 16px', 
                              cursor: 'pointer', 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              borderBottom: idx < visibleTransfers.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none'
                            }}
                            className="tx-item"
                          >
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '13px', fontWeight: '600' }}>{tx.description}</span>
                                <span style={{ fontSize: '8px', padding: '1px 4px', background: 'rgba(255,255,255,0.08)', color: badgeColor, borderRadius: '4px', border: '1px solid var(--border-glass)' }}>{badgeText}</span>
                              </div>
                              <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                                {labelText} • {dateStr}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <div style={{ textAlign: 'right' }}>
                                {tfDetails?.isInterCurrency ? (
                                  <>
                                    <span style={{ fontSize: '13px', fontWeight: '700', color: amtColor }}>
                                      {formatMoney(tfDetails.sourceAmount, tfDetails.sourceCurrency)}
                                    </span>
                                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block' }}>
                                      → {formatMoney(tfDetails.targetAmount, tfDetails.targetCurrency)}
                                    </span>
                                  </>
                                ) : (
                                  <span style={{ fontSize: '13px', fontWeight: '700', color: amtColor }}>
                                    {formatMoney(tx.total_amount, tx.currency)}
                                  </span>
                                )}
                              </div>
                              <button 
                                title="Edit Transfer" 
                                onClick={(e) => { e.stopPropagation(); handleStartEdit(tx); }} 
                                style={{ background: 'transparent', border: 'none', color: 'var(--accent-cyan)', opacity: 0.8, cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                              >
                                <Edit2 size={14} />
                              </button>
                              <button 
                                title="Delete Transfer" 
                                onClick={(e) => { e.stopPropagation(); handleDeleteTx(tx.transaction_id); }} 
                                style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.4)', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {sortedTransfers.length > visibleTransfersLimit && (
                      <button 
                        onClick={() => setVisibleTransfersLimit(prev => prev + 10)} 
                        className="btn-secondary" 
                        style={{ alignSelf: 'center', marginTop: '8px', padding: '6px 16px', fontSize: '12px' }}
                      >
                        Load More
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* VIEW: ALL TRANSACTIONS (FEED) */}
        {activeTab === 'transactions_feed' && (
          <div className="animate-fade-in" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', height: 'calc(100dvh - 140px)', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
              <h2>All Transactions</h2>
              {transactionTabMode === 'transactions' ? (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <button 
                    onClick={handleExportExcel} 
                    className="btn-secondary" 
                    style={{ padding: '6px 12px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <Download size={12} /> Excel
                  </button>
                  <button 
                    onClick={handleExportPdf} 
                    className="btn-secondary" 
                    style={{ padding: '6px 12px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    <FileText size={12} /> PDF
                  </button>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '6px' }}>
                    {getFilteredTransactions().length} records
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {activityLogs.length} activity records
                </span>
              )}
            </div>

            {/* Sub-tabs for All Transactions / Activities */}
            <div style={{
              display: 'flex',
              background: 'rgba(255, 255, 255, 0.04)',
              border: '1px solid var(--border-glass)',
              borderRadius: '12px',
              padding: '4px',
              gap: '4px',
              flexShrink: 0
            }}>
              <button
                type="button"
                onClick={() => setTransactionTabMode('transactions')}
                style={{
                  flex: 1,
                  padding: '8px 0',
                  borderRadius: '10px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: transactionTabMode === 'transactions' ? 'rgba(0, 245, 212, 0.15)' : 'transparent',
                  color: transactionTabMode === 'transactions' ? 'var(--accent-cyan)' : 'var(--text-muted)',
                  transition: 'var(--transition-smooth)'
                }}
              >
                📝 Transactions
              </button>
              <button
                type="button"
                onClick={() => setTransactionTabMode('activities')}
                style={{
                  flex: 1,
                  padding: '8px 0',
                  borderRadius: '10px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  background: transactionTabMode === 'activities' ? 'rgba(0, 245, 212, 0.15)' : 'transparent',
                  color: transactionTabMode === 'activities' ? 'var(--accent-cyan)' : 'var(--text-muted)',
                  transition: 'var(--transition-smooth)'
                }}
              >
                📜 Activities
              </button>
            </div>

            {transactionTabMode === 'transactions' ? (
              <>
                {/* Filters panel */}
                <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      placeholder="Search description/category..."
                      value={filterSearch}
                      onChange={e => setFilterSearch(e.target.value)}
                      style={{ padding: '10px', fontSize: '13px' }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '11px', marginBottom: '2px' }}>Flow Type</label>
                      <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ padding: '8px', fontSize: '13px' }}>
                        <option value="ALL">All Types</option>
                        <option value="INFLOW">Income Only</option>
                        <option value="OUTFLOW">Expense Only</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', marginBottom: '2px' }}>Account</label>
                      <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)} style={{ padding: '8px', fontSize: '13px' }}>
                        <option value="ALL">All Accounts</option>
                        {accounts.map(acc => (
                          <option key={acc.account_id} value={acc.account_id}>{acc.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '11px', marginBottom: '2px' }}>Category</label>
                      <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={{ padding: '8px', fontSize: '13px' }}>
                        <option value="ALL">All Categories</option>
                        <option value="Transfer">🔄 Transfer</option>
                        <option value="Investment">📈 Investment</option>
                        {categories.map(cat => (
                          <option key={cat.category_id} value={cat.name}>{cat.symbol} {cat.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', marginBottom: '2px' }}>Sort By</label>
                      <select
                        value={`${sortField}_${sortOrder}`}
                        onChange={e => {
                          const [field, order] = e.target.value.split('_');
                          setSortField(field);
                          setSortOrder(order);
                        }}
                        style={{ padding: '8px', fontSize: '13px' }}
                      >
                        <option value="date_desc">Date: Newest First</option>
                        <option value="date_asc">Date: Oldest First</option>
                        <option value="amount_desc">Amount: Highest First</option>
                        <option value="amount_asc">Amount: Lowest First</option>
                        <option value="description_asc">Name: A to Z</option>
                        <option value="description_desc">Name: Z to A</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '11px', marginBottom: '2px' }}>Start Date</label>
                      <input
                        type="date"
                        value={filterStartDate}
                        onChange={e => setFilterStartDate(e.target.value)}
                        style={{ padding: '8px', fontSize: '13px' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '11px', marginBottom: '2px' }}>End Date</label>
                      <input
                        type="date"
                        value={filterEndDate}
                        onChange={e => setFilterEndDate(e.target.value)}
                        style={{ padding: '8px', fontSize: '13px' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Feed list */}
                <div className="glass-panel" style={{ flex: 1, overflowY: 'auto' }}>
                  {getFilteredTransactions().length === 0 ? (
                    <p style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      No transactions match active filters.
                    </p>
                  ) : (
                    getFilteredTransactions().map(tx => {
                      // Investment transaction rendering
                      if (tx._isInvestmentTx) {
                        const isOut = tx._invTxType === 'BUY';
                        return (
                          <div
                            key={tx.transaction_id}
                            className="tx-item"
                            onClick={() => handleStartEditInvTx(tx._invData, tx._invTxData)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '14px', fontWeight: '600' }}>{tx.description}</span>
                                <span style={{ fontSize: '8px', padding: '1px 4px', background: 'rgba(96,165,250,0.15)', color: '#60a5fa', borderRadius: '4px' }}>Investment</span>
                                {tx._invTxType === 'SELL' && tx._invTxData?.realized_pnl !== undefined && tx._invTxData?.realized_pnl !== null && (
                                  <span style={{
                                    fontSize: '9px',
                                    fontWeight: '700',
                                    padding: '1px 5px',
                                    borderRadius: '4px',
                                    background: parseFloat(tx._invTxData.realized_pnl) >= 0 ? 'rgba(46,213,115,0.15)' : 'rgba(255,71,87,0.15)',
                                    color: parseFloat(tx._invTxData.realized_pnl) >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)'
                                  }}>
                                    {parseFloat(tx._invTxData.realized_pnl) >= 0 ? '+' : ''}{formatMoney(tx._invTxData.realized_pnl, tx.currency)} {parseFloat(tx._invTxData.realized_pnl) >= 0 ? 'Profit' : 'Loss'}
                                  </span>
                                )}
                              </div>
                              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                                {tx._invTxType === 'BUY' ? '📈' : tx._invTxType === 'SELL' ? '📉' : '💰'} {tx._invTxType} • {tx._srcLabel} • {formatDateTime(tx.date)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ textAlign: 'right' }}>
                                <span style={{
                                  fontSize: '14px',
                                  fontWeight: '700',
                                  color: isOut ? 'var(--color-outflow)' : 'var(--color-inflow)'
                                }}>
                                  {isOut ? '-' : '+'}{formatMoney(tx.total_amount, tx.currency)}
                                </span>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); handleDeleteAssetTx(tx._invData.investment_id, tx._invTxData.transaction_id); }} style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.4)', cursor: 'pointer' }}>
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        );
                      }

                      // Standard transaction rendering
                      const isTransfer = isTransferTransaction(tx);
                      const dir = isTransfer ? getTransferDirection(tx, accounts) : 'none';
                      const isSelfTransfer = dir === 'self';
                      const isInwardTransfer = dir === 'inward';
                      const isOutwardTransfer = dir === 'outward';

                      const isCcPayment = tx.description.toLowerCase().includes('credit card-bill payment') || tx.description.toLowerCase().includes('credit card payment:') || tx.description.toLowerCase().includes('card payment');
                      const matchedCat = findCategoryByName(categories, tx.category_name);
                      const isGlobalInflow = GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (tx.category_name || '').trim().toLowerCase());
                      const isOut = isOutwardTransfer || (!isInwardTransfer && !isSelfTransfer && !isCcPayment && (matchedCat ? matchedCat.type === 'OUTFLOW' : !isGlobalInflow));

                      let txColor = 'var(--text-primary)';
                      let txSign = '';
                      if (isSelfTransfer) {
                        txColor = 'var(--accent-cyan)';
                        txSign = '';
                      } else if (isOut) {
                        txColor = 'var(--color-outflow)';
                        txSign = '-';
                      } else {
                        txColor = 'var(--color-inflow)';
                        txSign = '+';
                      }

                      const { amount: displayAmt, isSplitPortion } = getDisplayAmount(tx);

                      const sAcc = accounts.find(a => a.account_id === tx.paid_by_account_id);
                      const dAcc = accounts.find(a => a.account_id === tx.transfer_to_account_id);
                      const isMirrorTx = !!tx.source_transaction_id;

                      let badgeText = null;
                      let badgeColor = 'var(--accent-cyan)';
                      let badgeBg = 'rgba(0,245,212,0.15)';
                      if (isCcPayment) {
                        badgeText = 'CC Payment';
                        badgeColor = 'var(--accent-purple)';
                        badgeBg = 'rgba(168,85,247,0.15)';
                      } else if (isInwardTransfer) {
                        badgeText = 'Incoming';
                        badgeColor = 'var(--color-inflow)';
                        badgeBg = 'rgba(0,245,212,0.15)';
                      } else if (isOutwardTransfer) {
                        badgeText = 'Outgoing';
                        badgeColor = 'var(--color-outflow)';
                        badgeBg = 'rgba(255,71,87,0.15)';
                      } else if (isSelfTransfer) {
                        badgeText = 'Transfer';
                        badgeColor = 'var(--accent-cyan)';
                        badgeBg = 'rgba(0,245,212,0.15)';
                      }

                      return (
                        <div key={tx.transaction_id} className="tx-item" onClick={() => setSelectedTxDetail(tx)} style={{ cursor: 'pointer' }}>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '14px', fontWeight: '600' }}>{tx.description}</span>
                              {badgeText && (
                                <span style={{ fontSize: '8px', padding: '1px 4px', background: badgeBg, color: badgeColor, borderRadius: '4px' }}>{badgeText}</span>
                              )}
                              {(tx.is_shared === true || tx.is_shared === 1 || tx.is_shared === 'true') && !isCcPayment && !isMirrorTx && !isInwardTransfer && (
                                <span style={{ fontSize: '8px', padding: '1px 4px', background: 'rgba(0,245,212,0.15)', color: 'var(--accent-cyan)', borderRadius: '4px' }}>Shared</span>
                              )}
                            </div>
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                              {isCcPayment ? (
                                <>
                                  💳 Credit Card Payment
                                  {isTransfer && sAcc && dAcc ? ` (${sAcc.name} → ${dAcc.name})` : ''}
                                  {!isTransfer && sAcc ? ` (Paid by ${getPayerName(tx)} → ${sAcc.name})` : ''}
                                </>
                              ) : isTransfer ? (
                                <>
                                  🔄 Transfer
                                  {sAcc && dAcc ? ` (${sAcc.name} → ${dAcc.name})` : ''}
                                </>
                              ) : (
                                `${matchedCat?.symbol || '💸'} ${matchedCat?.name || tx.category_name}`
                              )}
                              {` • ${formatDateTime(tx.date)}`}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ 
                                fontSize: '14px', 
                                fontWeight: '700', 
                                color: txColor 
                              }}>
                                {txSign}{formatMoney(displayAmt, tx.currency)}
                              </span>
                              {isSplitPortion && <span style={{ fontSize: '9px', color: 'var(--text-muted)', display: 'block' }}>your share</span>}
                            </div>
                            <button title="Edit Transaction" onClick={(e) => { e.stopPropagation(); handleStartEdit(tx); }} style={{ background: 'transparent', border: 'none', color: 'var(--accent-cyan)', opacity: 0.8, cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}>
                              <Edit2 size={14} />
                            </button>
                            <button title="Delete Transaction" onClick={(e) => { e.stopPropagation(); handleDeleteTx(tx.transaction_id); }} style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.4)', cursor: 'pointer', padding: '2px', display: 'flex', alignItems: 'center' }}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              /* Feed list for Activities */
              <div className="glass-panel" style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {activityLogs.length === 0 ? (
                  <p style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    No activity logs found.
                  </p>
                ) : (
                  activityLogs.map(log => {
                    const actionColors = {
                      CREATE: { bg: 'rgba(46, 213, 115, 0.15)', color: 'var(--color-inflow)' },
                      UPDATE: { bg: 'rgba(255, 165, 0, 0.15)', color: '#ffa500' },
                      DELETE: { bg: 'rgba(255, 71, 87, 0.15)', color: 'var(--color-outflow)' }
                    };
                    const colorSet = actionColors[log.action_type] || { bg: 'rgba(255,255,255,0.1)', color: 'var(--text-muted)' };

                    return (
                      <div
                        key={log.activity_id}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          padding: '12px',
                          borderRadius: '12px',
                          background: 'rgba(255, 255, 255, 0.02)',
                          border: '1px solid rgba(255, 255, 255, 0.03)',
                          fontSize: '12px'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span
                            style={{
                              padding: '2px 6px',
                              borderRadius: '6px',
                              fontSize: '9px',
                              fontWeight: '700',
                              background: colorSet.bg,
                              color: colorSet.color,
                              textTransform: 'uppercase'
                            }}
                          >
                            {log.action_type}
                          </span>
                          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                            {formatDateTime(log.created_at)}
                          </span>
                        </div>
                        <div style={{ color: 'var(--text-main)' }}>
                          {renderActivityDetails(log)}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}

        {/* VIEW: NOTIFICATIONS */}
        {activeTab === 'notifications' && (
          <div className="animate-fade-in" style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '18px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                🔔 Notifications
              </h2>
              {displayNotifications.length > 0 && (
                <button
                  onClick={handleClearNotifications}
                  className="btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px', color: 'var(--color-outflow)', borderColor: 'rgba(255,71,87,0.3)' }}
                >
                  Clear All
                </button>
              )}
            </div>

            {displayNotifications.length === 0 ? (
              <div className="glass-panel" style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>
                No notifications yet.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {displayNotifications.map(n => (
                  <div
                    key={n.notification_id}
                    className="glass-panel"
                    style={{
                      padding: '14px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: n.read ? 'rgba(255,255,255,0.02)' : 'rgba(0,245,212,0.05)',
                      borderLeft: n.read ? '1px solid var(--border-glass)' : '3px solid var(--accent-cyan)'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, marginRight: '10px' }}>
                      <span style={{ fontSize: '13px', color: 'var(--text-main)' }}>
                        {(() => {
                          const parts = n.message.split(/(<a href="[^"]*">[^<]*<\/a>)/g);
                          return parts.map((part, i) => {
                            if (part.startsWith('<a href="')) {
                              const match = part.match(/<a href="([^"]*)">([^<]*)<\/a>/);
                              if (match) {
                                return (
                                  <a 
                                    key={i}
                                    tabIndex={0}
                                    style={{ color: 'var(--accent-cyan)', textDecoration: 'underline', cursor: 'pointer' }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      const url = match[1];
                                      if (url.includes('#/accounts?edit=')) {
                                        const accId = url.split('=')[1];
                                        const acc = accounts.find(a => a.account_id === accId);
                                        if (acc) {
                                          setActiveTab(acc.type === 'CREDIT_CARD' ? 'credit_cards' : 'accounts');
                                          setActiveAccount(acc);
                                        } else {
                                          showToast('Account not found', 'error');
                                        }
                                      } else if (url.includes('#/pending-actions') || url.includes('#/dashboard')) {
                                        setActiveTab('dashboard');
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                      } else if (url.includes('#/transactions?edit=')) {
                                        const txId = url.split('=')[1];
                                        const tx = transactions.find(t => t.transaction_id === txId);
                                        if (tx) {
                                          handleStartEdit(tx);
                                        } else {
                                          showToast('Transaction not found locally. It may be out of sync.', 'error');
                                        }
                                      } else if (url.includes('#/groups')) {
                                        const queryPart = url.includes('?') ? url.split('?')[1] : '';
                                        const params = new URLSearchParams(queryPart);
                                        const grpId = params.get('id');
                                        const txId = params.get('tx');
                                        const grp = groups.find(g => g.group_id === grpId);
                                        if (grp) {
                                          setActiveTab('groups');
                                          handleViewGroup(grp, txId ? 'tx_detail' : 'hub');
                                          if (txId) {
                                            const tx = transactions.find(t => t.transaction_id === txId);
                                            if (tx) {
                                              setSelectedGroupTx(tx);
                                            }
                                          }
                                        } else {
                                          setActiveTab('groups');
                                        }
                                      }
                                    }}
                                  >
                                    {match[2]}
                                  </a>
                                );
                              }
                            }
                            return <span key={i}>{part}</span>;
                          });
                        })()}
                      </span>
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{new Date(n.created_at).toLocaleString()}</span>
                    </div>
                    {!n.read && (
                      <button
                        onClick={() => handleMarkNotificationRead(n.notification_id)}
                        className="btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '10px', whiteSpace: 'nowrap' }}
                      >
                        Mark Read
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'investments' && (() => {
          const targetCurrency = invDisplayCurrency === 'ALL' ? (preferredCurrencies[0] || 'AED') : invDisplayCurrency;

          let totalVal = 0;
          let totalCost = 0;
          let totalDividends = 0;
          let ttmDividends = 0;
          const currencyBreakdown = {};

          const now = new Date();
          const oneYearAgo = new Date();
          oneYearAgo.setFullYear(now.getFullYear() - 1);

          investments.forEach(inv => {
            const invCurr = inv.currency || 'AED';
            const nativeVal = parseFloat(inv.current_value) || 0;
            const nativeCost = parseFloat(inv.total_cost) || 0;
            const nativeDivs = parseFloat(inv.total_dividends) || 0;

            if (nativeVal > 0) {
              currencyBreakdown[invCurr] = (currencyBreakdown[invCurr] || 0) + nativeVal;
            }

            totalVal += convertCurrency(nativeVal, invCurr, targetCurrency);
            totalCost += convertCurrency(nativeCost, invCurr, targetCurrency);
            totalDividends += convertCurrency(nativeDivs, invCurr, targetCurrency);

            const divTxs = (inv.transactions || []).filter(tx => tx.type === 'DIVIDEND');
            divTxs.forEach(tx => {
              const txDate = new Date(tx.transaction_date);
              if (txDate >= oneYearAgo) {
                const divAmt = parseFloat(tx.amount) || 0;
                ttmDividends += convertCurrency(divAmt, invCurr, targetCurrency);
              }
            });
          });

          const netGain = totalVal - totalCost;
          const gainPct = totalCost > 0 ? (netGain / totalCost) * 100 : 0;
          const overallYoc = totalCost > 0 ? (ttmDividends / totalCost) * 100 : 0;
          const eadi = ttmDividends > 0 ? ttmDividends : (totalVal * 0.025);

          // Upcoming Dividend Calendar
          const upcoming = [];
          investments.forEach(inv => {
            const invCurr = inv.currency || 'AED';
            const divTxs = (inv.transactions || []).filter(tx => tx.type === 'DIVIDEND');
            let nextDate = null;
            let estAmt = 0;

            if (divTxs.length > 0) {
              const lastDiv = divTxs[divTxs.length - 1];
              const lastDate = new Date(lastDiv.transaction_date);
              nextDate = new Date(lastDate);
              nextDate.setMonth(nextDate.getMonth() + 3); // quarterly estimate
              estAmt = parseFloat(lastDiv.amount) || 0;
            } else if (['EQUITY', 'ETF', 'MUTUAL_FUND', 'FIXED_INCOME'].includes(inv.type) && inv.total_units > 0) {
              nextDate = new Date();
              nextDate.setDate(nextDate.getDate() + 45); // in 45 days
              estAmt = ((inv.current_value || 0) * 0.03) / 4;
            }

            if (nextDate && nextDate > now) {
              upcoming.push({
                symbol: inv.symbol,
                name: inv.name,
                date: nextDate,
                currency: invCurr,
                amountNative: estAmt,
                amountConverted: convertCurrency(estAmt, invCurr, targetCurrency)
              });
            }
          });
          upcoming.sort((a, b) => a.date - b.date);

          // Drift alerts
          const driftAlerts = [];
          const classValues = {};
          const classTargets = {};
          investments.forEach(inv => {
            const invCurr = inv.currency || 'AED';
            const val = convertCurrency((parseFloat(inv.total_units) || 0) * (parseFloat(inv.current_price) || 0), invCurr, targetCurrency);
            classValues[inv.type] = (classValues[inv.type] || 0) + val;
            classTargets[inv.type] = (classTargets[inv.type] || 0) + (parseFloat(inv.target_allocation_pct) || 0);
          });
          Object.keys(classTargets).forEach(type => {
            const val = classValues[type] || 0;
            const target = parseFloat(classTargets[type]) || 0;
            const currentPct = totalVal > 0 ? (val / totalVal) * 100 : 0;
            const drift = currentPct - target;
            if (Math.abs(drift) > 5.0) {
              driftAlerts.push({
                type: type.replace('_', ' '),
                currentPct: parseFloat(currentPct) || 0,
                target: parseFloat(target) || 0,
                drift: parseFloat(drift) || 0
              });
            }
          });

          // Diversification dimension grouping
          const divData = {};
          investments.forEach(inv => {
            const invCurr = inv.currency || 'AED';
            let key = 'N/A';
            if (divDimension === 'class') key = inv.type.replace('_', ' ');
            else if (divDimension === 'sector') key = inv.sector;
            else if (divDimension === 'region') key = inv.region;
            else if (divDimension === 'cap') key = inv.market_cap;
            else if (divDimension === 'currency') key = invCurr.toUpperCase();

            const val = convertCurrency(inv.current_value || 0, invCurr, targetCurrency);
            if (val > 0) {
              divData[key] = (divData[key] || 0) + val;
            }
          });
          const divItems = Object.entries(divData)
            .map(([key, val]) => ({
              name: key,
              value: val,
              pct: totalVal > 0 ? (val / totalVal) * 100 : 0
            }))
            .sort((a, b) => b.value - a.value);

          return (
            <div className="animate-fade-in" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Live Streaming Market Ticker Tape */}
              <LiveMarketTicker
                onSelectTicker={(item) => setSelectedChartAsset({
                  symbol: item.symbol,
                  name: item.label,
                  currency: item.currency
                })}
              />

              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                <div>
                  <h2 style={{ fontSize: '20px', fontWeight: '800', margin: 0 }}>📈 Investments Portfolio</h2>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Unified multi-currency holdings & live market analytics</span>
                </div>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.04)', padding: '4px 8px', borderRadius: '10px', border: '1px solid var(--border-glass)' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '600' }}>Portfolio Currency:</span>
                    <select
                      value={invDisplayCurrency}
                      onChange={e => setInvDisplayCurrency(e.target.value)}
                      style={{
                        padding: '4px 8px',
                        borderRadius: '8px',
                        background: 'rgba(0,0,0,0.3)',
                        border: '1px solid var(--border-glass)',
                        color: 'var(--accent-cyan)',
                        fontSize: '11px',
                        fontWeight: '700',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="ALL">🌐 Native ({preferredCurrencies[0] || 'AED'})</option>
                      {preferredCurrencies.map(curr => (
                        <option key={curr} value={curr}>💱 Display in {curr}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleSyncPortfolioPrices}
                    disabled={isSyncingPrices}
                    style={{
                      padding: '6px 12px',
                      fontSize: '11px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: '700',
                      cursor: isSyncingPrices ? 'not-allowed' : 'pointer'
                    }}
                    title="Refresh all holding prices from live market data"
                  >
                    {isSyncingPrices ? (
                      <ThinkingOrbLoader popup state="searching" size={20} color="#38bdf8" text="Syncing..." />
                    ) : (
                      <>
                        <span style={{ display: 'inline-block' }}>🔄</span>
                        <span>Sync Live Prices</span>
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleOpenAssetSearch}
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                  >
                    ➕ Add Asset
                  </button>
                </div>
              </div>

              {/* Drift alerts */}
              {driftAlerts.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {driftAlerts.map(alert => (
                    <div
                      key={alert.type}
                      style={{
                        padding: '12px 16px',
                        borderRadius: '12px',
                        background: 'rgba(255, 159, 67, 0.1)',
                        borderLeft: '4px solid #ff9f43',
                        color: '#ff9f43',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}
                    >
                      <span>⚠️</span>
                      <div>
                        <strong>Rebalancing Drift ({alert.type})</strong>: Exposure is{' '}
                        {(parseFloat(alert.currentPct) || 0).toFixed(1)}% against target of {(parseFloat(alert.target) || 0).toFixed(1)}% (Drift:{' '}
                        {(parseFloat(alert.drift) || 0) > 0 ? '+' : ''}
                        {(parseFloat(alert.drift) || 0).toFixed(1)}%)
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Portfolio Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
                <div className="glass-panel" style={{ padding: '14px' }}>
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Portfolio Value ({targetCurrency})</span>
                  <h3 style={{ fontSize: '18px', fontWeight: '800', margin: '4px 0 0 0', color: 'var(--accent-cyan)' }}>
                    {formatMoney(totalVal, targetCurrency)}
                  </h3>
                  {Object.keys(currencyBreakdown).length > 1 && (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                      {Object.entries(currencyBreakdown).map(([c, v]) => (
                        <span key={c} style={{ fontSize: '9px', background: 'rgba(255,255,255,0.06)', padding: '1px 5px', borderRadius: '4px', color: 'var(--text-muted)' }}>
                          {c}: {formatMoney(v, c)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="glass-panel" style={{ padding: '14px' }}>
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Cost Basis ({targetCurrency})</span>
                  <h3 style={{ fontSize: '18px', fontWeight: '800', margin: '4px 0 0 0', color: 'var(--text-main)' }}>
                    {formatMoney(totalCost, targetCurrency)}
                  </h3>
                </div>
                <div className="glass-panel" style={{ padding: '14px' }}>
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Gain / Loss ({targetCurrency})</span>
                  <h3 style={{ fontSize: '18px', fontWeight: '800', margin: '4px 0 0 0', color: netGain >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)' }}>
                    {netGain >= 0 ? '+' : ''}{formatMoney(netGain, targetCurrency)} ({gainPct.toFixed(1)}%)
                  </h3>
                </div>
                <div className="glass-panel" style={{ padding: '14px' }}>
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Dividends Cash ({targetCurrency})</span>
                  <h3 style={{ fontSize: '18px', fontWeight: '800', margin: '4px 0 0 0', color: 'var(--accent-cyan)' }}>
                    {formatMoney(totalDividends, targetCurrency)}
                  </h3>
                </div>
                {(() => {
                  const totalRealizedPnL = investments.reduce((acc, inv) => {
                    const invCurr = inv.currency || 'AED';
                    return acc + convertCurrency(parseFloat(inv.total_realized_pnl) || 0, invCurr, targetCurrency);
                  }, 0);
                  return (
                    <div className="glass-panel" style={{ padding: '14px' }}>
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Realized P&L ({targetCurrency})</span>
                      <h3 style={{ fontSize: '18px', fontWeight: '800', margin: '4px 0 0 0', color: totalRealizedPnL >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)' }}>
                        {totalRealizedPnL >= 0 ? '+' : ''}{formatMoney(totalRealizedPnL, targetCurrency)}
                      </h3>
                    </div>
                  );
                })()}
              </div>

              {/* Allocation and Dividend Split Panel */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                {/* Diversification Matrix */}
                <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h4 style={{ fontSize: '13px', fontWeight: '700', margin: 0 }}>📊 Asset Diversification</h4>
                    <div style={{ display: 'flex', background: 'rgba(255,255,255,0.05)', borderRadius: '10px', padding: '2px' }}>
                      {['class', 'sector', 'region', 'cap', 'currency'].map(dim => (
                        <button
                          key={dim}
                          type="button"
                          onClick={() => setDivDimension(dim)}
                          style={{
                            background: divDimension === dim ? 'var(--accent-cyan)' : 'transparent',
                            color: divDimension === dim ? '#000' : 'var(--text-muted)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '2px 6px',
                            fontSize: '10px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            textTransform: 'capitalize'
                          }}
                        >
                          {dim === 'class' ? 'Class' : dim === 'cap' ? 'Cap' : dim === 'currency' ? 'Currency' : dim}
                        </button>
                      ))}
                    </div>
                  </div>

                  {divItems.length === 0 ? (
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', textAlign: 'center', padding: '10px 0' }}>No allocation data.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {divItems.map((item, idx) => {
                        const colors = ['var(--accent-cyan)', '#9b5de5', '#f15bb5', '#fee440', '#00bbf9', '#00f5d4'];
                        const color = colors[idx % colors.length];
                        return (
                          <div key={item.name} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                              <span style={{ color: 'var(--text-main)', fontWeight: '600' }}>{item.name}</span>
                              <span style={{ color: 'var(--text-muted)' }}>
                                {formatMoney(item.value, targetCurrency)} ({item.pct.toFixed(1)}%)
                              </span>
                            </div>
                            <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.05)', borderRadius: '3px', overflow: 'hidden' }}>
                              <div style={{ width: `${item.pct}%`, height: '100%', background: color, borderRadius: '3px' }}></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Dividend Center */}
                <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <h4 style={{ fontSize: '13px', fontWeight: '700', margin: 0 }}>💰 Dividend Center</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-glass)' }}>
                      <span style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                        Yield on Cost (YOC) {renderInfoTooltip("Yield on Cost (YOC) is the annual dividend income divided by your average cost basis. It shows the dividend yield relative to the price you originally paid.")}
                      </span>
                      <h4 style={{ fontSize: '15px', fontWeight: '700', margin: '2px 0 0 0', color: 'var(--text-main)' }}>
                        {overallYoc > 0 ? `${overallYoc.toFixed(2)}%` : '0.00%'}
                      </h4>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-glass)' }}>
                      <span style={{ fontSize: '9px', color: 'var(--text-muted)' }}>
                        Projected Annual (EADI) {renderInfoTooltip("Estimated Annual Dividend Income (EADI) projects the dividend income you expect to receive over the next year based on trailing payouts or asset value.")}
                      </span>
                      <h4 style={{ fontSize: '15px', fontWeight: '700', margin: '2px 0 0 0', color: 'var(--accent-cyan)' }}>
                        {formatMoney(eadi, targetCurrency)}
                      </h4>
                    </div>
                  </div>

                  <div>
                    <h5 style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)', margin: '0 0 6px 0', textTransform: 'uppercase' }}>
                      📅 Upcoming Distributions
                    </h5>
                    {upcoming.length === 0 ? (
                      <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>No upcoming dividends.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '100px', overflowY: 'auto' }}>
                        {upcoming.slice(0, 3).map((item, idx) => (
                          <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.02)', padding: '6px 10px', borderRadius: '6px', fontSize: '11px' }}>
                            <div>
                              <span style={{ fontWeight: '700', color: 'var(--accent-cyan)' }}>{item.symbol}</span>
                              <span style={{ color: 'var(--text-muted)', marginLeft: '4px', fontSize: '10px' }}>{item.name}</span>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <span style={{ fontWeight: '600', color: 'var(--color-inflow)' }}>+{formatMoney(item.amountNative, item.currency)}</span>
                              {item.currency !== targetCurrency && (
                                <span style={{ color: 'var(--text-muted)', fontSize: '9px', display: 'block' }}>≈ {formatMoney(item.amountConverted, targetCurrency)}</span>
                              )}
                              <span style={{ color: 'var(--text-muted)', fontSize: '9px', display: 'block' }}>{formatDateDDMMYYYY(item.date)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Holdings Table */}
              <div className="glass-panel" style={{ padding: '16px', overflowX: 'auto' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '800', margin: '0 0 12px 0' }}>📁 Portfolio Holdings</h4>
                {investments.length === 0 ? (
                  <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px' }}>
                    No holdings in portfolio. Sync brokerage or add an asset manually.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '600px' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-glass)', color: 'var(--text-muted)', textAlign: 'left' }}>
                        <th style={{ padding: '8px 4px' }}>Asset</th>
                        <th style={{ padding: '8px 4px' }}>Type</th>
                        <th style={{ padding: '8px 4px' }}>Current Price</th>
                        <th style={{ padding: '8px 4px' }}>Units</th>
                        <th style={{ padding: '8px 4px' }}>Avg Cost</th>
                        <th style={{ padding: '8px 4px' }}>Current Value</th>
                        <th style={{ padding: '8px 4px' }}>Gain / Loss</th>
                        <th style={{ padding: '8px 4px', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {investments.map(inv => {
                        const invCurr = inv.currency || 'AED';
                        const isExpanded = expandedAssetId === inv.investment_id;
                        const value = inv.current_value || 0;
                        const cost = inv.total_cost || 0;
                        const gain = value - cost;
                        const pct = cost > 0 ? (gain / cost) * 100 : 0;

                        const convertedValue = convertCurrency(value, invCurr, targetCurrency);
                        const convertedGain = convertCurrency(gain, invCurr, targetCurrency);

                        return (
                          <React.Fragment key={inv.investment_id}>
                            <tr
                              onClick={() => setExpandedAssetId(isExpanded ? null : inv.investment_id)}
                              style={{
                                borderBottom: '1px solid rgba(255,255,255,0.03)',
                                cursor: 'pointer',
                                background: isExpanded ? 'rgba(255,255,255,0.02)' : 'transparent'
                              }}
                              className="hover-row"
                            >
                              <td style={{ padding: '10px 4px', fontWeight: '700' }}>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ color: 'var(--accent-cyan)' }}>{inv.symbol}</span>
                                    <span style={{ background: 'rgba(0,245,212,0.12)', color: 'var(--accent-cyan)', fontSize: '9px', fontWeight: '800', padding: '1px 5px', borderRadius: '4px' }}>{invCurr}</span>
                                  </div>
                                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: '400' }}>{inv.name}</span>
                                </div>
                              </td>
                              <td style={{ padding: '10px 4px', textTransform: 'capitalize' }}>
                                {inv.type.replace('_', ' ').toLowerCase()}
                              </td>
                              <td style={{ padding: '10px 4px' }} onClick={e => e.stopPropagation()}>
                                {editingAssetPriceId === inv.investment_id ? (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input
                                      type="number"
                                      step="0.0001"
                                      value={newAssetPriceInput}
                                      onChange={e => setNewAssetPriceInput(e.target.value)}
                                      style={{
                                        width: '70px',
                                        padding: '4px 6px',
                                        borderRadius: '6px',
                                        background: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid var(--border-glass)',
                                        color: 'var(--text-main)',
                                        fontSize: '11px'
                                      }}
                                      autoFocus
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleSaveAssetPrice(inv.investment_id)}
                                      style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'var(--color-inflow)',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        padding: '0 2px'
                                      }}
                                      title="Save"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setEditingAssetPriceId(null)}
                                      style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: 'var(--color-outflow)',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        padding: '0 2px'
                                      }}
                                      title="Cancel"
                                    >
                                      ✗
                                    </button>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span>{formatMoney(inv.current_price, invCurr)}</span>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingAssetPriceId(inv.investment_id);
                                          setNewAssetPriceInput(inv.current_price.toString());
                                        }}
                                        style={{
                                          background: 'transparent',
                                          border: 'none',
                                          color: 'var(--accent-cyan)',
                                          cursor: 'pointer',
                                          padding: 0,
                                          opacity: 0.6,
                                          fontSize: '11px'
                                        }}
                                        title="Edit Price"
                                      >
                                        ✏️
                                      </button>
                                    </div>
                                    {invCurr !== targetCurrency && (
                                      <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>≈ {formatMoney(convertCurrency(inv.current_price, invCurr, targetCurrency), targetCurrency)}</span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '10px 4px' }}>{inv.total_units}</td>
                              <td style={{ padding: '10px 4px' }}>
                                <div>{formatMoney(inv.average_cost, invCurr)}</div>
                                {invCurr !== targetCurrency && (
                                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>≈ {formatMoney(convertCurrency(inv.average_cost, invCurr, targetCurrency), targetCurrency)}</div>
                                )}
                              </td>
                              <td style={{ padding: '10px 4px', fontWeight: '600' }}>
                                <div>{formatMoney(value, invCurr)}</div>
                                {invCurr !== targetCurrency && (
                                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>≈ {formatMoney(convertedValue, targetCurrency)}</div>
                                )}
                              </td>
                              <td style={{ padding: '10px 4px', color: gain >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)', fontWeight: '600' }}>
                                <div>{gain >= 0 ? '+' : ''}{formatMoney(gain, invCurr)} ({pct.toFixed(1)}%)</div>
                                {invCurr !== targetCurrency && (
                                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>≈ {formatMoney(convertedGain, targetCurrency)}</div>
                                )}
                              </td>
                              <td style={{ padding: '10px 4px', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedChartAsset({
                                      symbol: inv.symbol,
                                      name: inv.name,
                                      currency: invCurr,
                                      avgCost: inv.average_cost,
                                      transactions: inv.transactions
                                    })}
                                    className="btn-secondary"
                                    style={{ padding: '2px 6px', fontSize: '10px', color: 'var(--accent-cyan)' }}
                                    title="Open Full TradingView Chart"
                                  >
                                    📈 Chart
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedAssetTx(inv);
                                      setEditingInvTxId(null);
                                      setInvTxType('BUY');
                                      setInvTxUnits('');
                                      setInvTxPricePerUnit('');
                                      setInvTxAmount('');
                                      setInvTxSourceType('ACCOUNT');
                                      setInvTxSourceId(accounts[0]?.account_id || '');
                                      setInvTxDate(new Date().toISOString().split('T')[0]);
                                      setActiveModal('log_investment_tx');
                                    }}
                                    className="btn-secondary"
                                    style={{ padding: '2px 6px', fontSize: '10px' }}
                                  >
                                    Log Tx
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteAsset(inv.investment_id)}
                                    style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.5)', cursor: 'pointer' }}
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan="8" style={{ padding: '14px', background: 'rgba(0,0,0,0.15)', borderBottom: '1px solid var(--border-glass)' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                    <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: 'var(--text-muted)', flexWrap: 'wrap', background: 'rgba(255,255,255,0.03)', padding: '8px 12px', borderRadius: '8px' }}>
                                      <span>💱 <strong>Asset Currency:</strong> <span style={{ color: 'var(--accent-cyan)' }}>{invCurr}</span></span>
                                      <span>⚡ <strong>Rate:</strong> 1 {invCurr} = {convertCurrency(1, invCurr, targetCurrency).toFixed(4)} {targetCurrency}</span>
                                      <span>📍 <strong>Region:</strong> {inv.region}</span>
                                      <span>🏢 <strong>Sector:</strong> {inv.sector}</span>
                                      <span>📊 <strong>Market Cap:</strong> {inv.market_cap}</span>
                                      <span>🎯 <strong>Target Allocation:</strong> {inv.target_allocation_pct}%</span>
                                    </div>

                                    {/* Sub-tab Navigation: Chart vs Transactions */}
                                    <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '8px' }}>
                                      <button
                                        type="button"
                                        onClick={() => setExpandedAssetTab(prev => ({ ...prev, [inv.investment_id]: 'chart' }))}
                                        style={{
                                          background: (expandedAssetTab[inv.investment_id] || 'chart') === 'chart' ? 'rgba(0, 245, 212, 0.12)' : 'transparent',
                                          color: (expandedAssetTab[inv.investment_id] || 'chart') === 'chart' ? 'var(--accent-cyan)' : 'var(--text-muted)',
                                          border: (expandedAssetTab[inv.investment_id] || 'chart') === 'chart' ? '1px solid var(--accent-cyan)' : '1px solid transparent',
                                          borderRadius: '8px',
                                          padding: '4px 10px',
                                          fontSize: '11px',
                                          fontWeight: '700',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        📊 TradingView Chart
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => setExpandedAssetTab(prev => ({ ...prev, [inv.investment_id]: 'txs' }))}
                                        style={{
                                          background: expandedAssetTab[inv.investment_id] === 'txs' ? 'rgba(0, 245, 212, 0.12)' : 'transparent',
                                          color: expandedAssetTab[inv.investment_id] === 'txs' ? 'var(--accent-cyan)' : 'var(--text-muted)',
                                          border: expandedAssetTab[inv.investment_id] === 'txs' ? '1px solid var(--accent-cyan)' : '1px solid transparent',
                                          borderRadius: '8px',
                                          padding: '4px 10px',
                                          fontSize: '11px',
                                          fontWeight: '700',
                                          cursor: 'pointer'
                                        }}
                                      >
                                        📁 Transactions Ledger ({(inv.transactions || []).length})
                                      </button>
                                    </div>

                                    {(expandedAssetTab[inv.investment_id] || 'chart') === 'chart' ? (
                                      <div style={{ marginTop: '4px' }}>
                                        <TradingViewChart
                                          symbol={inv.symbol}
                                          name={inv.name}
                                          currency={invCurr}
                                          avgCost={inv.average_cost}
                                          userTransactions={inv.transactions}
                                          height={280}
                                        />
                                      </div>
                                    ) : (
                                      <div>
                                        <h5 style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)', margin: '0 0 6px 0', textTransform: 'uppercase' }}>
                                          Transactions Ledger ({invCurr})
                                        </h5>
                                      {(inv.transactions || []).length === 0 ? (
                                        <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>No transactions logged.</p>
                                      ) : (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
                                          <thead>
                                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', color: 'var(--text-muted)', textAlign: 'left' }}>
                                              <th style={{ padding: '4px' }}>Date</th>
                                              <th style={{ padding: '4px' }}>Type</th>
                                              <th style={{ padding: '4px' }}>Units</th>
                                              <th style={{ padding: '4px' }}>Price</th>
                                              <th style={{ padding: '4px' }}>Total Amount</th>
                                              <th style={{ padding: '4px' }}>Source / Destination</th>
                                              <th style={{ padding: '4px', textAlign: 'right' }}>Action</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {inv.transactions.map(t => {
                                              let srcLabel = 'N/A';
                                              if (t.source_type === 'ACCOUNT' || t.source_type === 'CREDIT_CARD') {
                                                const acc = accounts.find(a => a.account_id === t.source_id);
                                                srcLabel = acc ? `${acc.name} (${acc.currency})` : 'Account';
                                              } else if (t.source_type === 'FRIEND') {
                                                const fr = friends.find(f => f.user_id === t.source_id);
                                                srcLabel = fr ? fr.email : 'Friend';
                                              }

                                              return (
                                                <tr key={t.transaction_id} onClick={() => handleStartEditInvTx(inv, t)} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)', cursor: 'pointer', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,245,212,0.04)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                                  <td style={{ padding: '6px 4px', color: 'var(--text-muted)' }}>
                                                    {formatDateDDMMYYYY(t.transaction_date)}
                                                  </td>
                                                  <td style={{ padding: '6px 4px' }}>
                                                    <span style={{
                                                      padding: '2px 4px',
                                                      borderRadius: '4px',
                                                      fontSize: '9px',
                                                      fontWeight: '600',
                                                      background: t.type === 'BUY' ? 'rgba(255,71,87,0.1)' : t.type === 'SELL' ? 'rgba(46,213,115,0.1)' : 'rgba(0,245,212,0.1)',
                                                      color: t.type === 'BUY' ? 'var(--color-outflow)' : t.type === 'SELL' ? 'var(--color-inflow)' : 'var(--accent-cyan)'
                                                    }}>
                                                      {t.type}
                                                    </span>
                                                  </td>
                                                  <td style={{ padding: '6px 4px' }}>{t.type === 'DIVIDEND' ? '-' : t.units}</td>
                                                  <td style={{ padding: '6px 4px' }}>{t.type === 'DIVIDEND' ? '-' : formatMoney(t.price_per_unit, invCurr)}</td>
                                                  <td style={{ padding: '6px 4px', fontWeight: '600' }}>
                                                    {formatMoney(t.amount, invCurr)}
                                                    {t.type === 'SELL' && t.realized_pnl !== undefined && t.realized_pnl !== null && (
                                                      <span style={{
                                                        marginLeft: '6px',
                                                        fontSize: '9px',
                                                        fontWeight: '700',
                                                        padding: '1px 4px',
                                                        borderRadius: '4px',
                                                        background: parseFloat(t.realized_pnl) >= 0 ? 'rgba(46,213,115,0.15)' : 'rgba(255,71,87,0.15)',
                                                        color: parseFloat(t.realized_pnl) >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)'
                                                      }}>
                                                        {parseFloat(t.realized_pnl) >= 0 ? '+' : ''}{formatMoney(t.realized_pnl, invCurr)} {parseFloat(t.realized_pnl) >= 0 ? 'Profit' : 'Loss'}
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td style={{ padding: '6px 4px', color: 'var(--text-muted)' }}>{srcLabel}</td>
                                                  <td style={{ padding: '6px 4px', textAlign: 'right' }}>
                                                    <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                                      <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); handleStartEditInvTx(inv, t); }}
                                                        style={{ background: 'transparent', border: 'none', color: 'var(--accent-cyan)', cursor: 'pointer', opacity: 0.6 }}
                                                        title="Edit transaction"
                                                      >
                                                        ✏️
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={(e) => { e.stopPropagation(); handleDeleteAssetTx(inv.investment_id, t.transaction_id); }}
                                                        style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.4)', cursor: 'pointer' }}
                                                      >
                                                        <Trash2 size={12} />
                                                      </button>
                                                    </div>
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      )}
                                    </div>
                                  )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          );
        })()}

      </main>

      {/* --- A. DYNAMIC CATEGORIES SHEET --- */}
      {activeSheet === 'categories' && (
        <div className="sheet-modal animate-fade-in">
          <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '12px' }}>
            <h2>Manage Categories</h2>
            <button onClick={() => { setActiveSheet(null); refreshData(); }} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontWeight: 'bold' }}>Close</button>
          </div>

          <form onSubmit={handleCreateCategory} className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3>{editingCategory ? 'Edit Custom Category' : 'Create Custom Category'}</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input type="text" placeholder="Category Name" value={newCatName} onChange={e => setNewCatName(e.target.value)} required />
              <input type="text" maxLength={2} placeholder="Emoji" value={newCatSymbol} onChange={e => setNewCatSymbol(e.target.value)} style={{ width: '64px', textAlign: 'center' }} required />
            </div>
            {!editingCategory && (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <label style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                  Category Type:
                  {renderInfoTooltip("Expenses are money you spend (e.g. food, rent). Income is money you receive (e.g. salary, transfers).")}
                </label>
                <select value={newCatType} onChange={e => setNewCatType(e.target.value)} style={{ width: 'auto', padding: '6px' }}>
                  <option value="OUTFLOW">Expense</option>
                  <option value="INFLOW">Income</option>
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="submit" className="btn-primary" style={{ padding: '12px', flex: 1 }}>
                {editingCategory ? 'Update Category' : 'Create Category'}
              </button>
              {editingCategory && (
                <button type="button" className="btn-secondary" onClick={handleCancelEditCategory} style={{ padding: '12px' }}>
                  Cancel
                </button>
              )}
            </div>
          </form>

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <h3 style={{ marginBottom: '10px', color: 'var(--color-outflow)' }}>Expense Categories</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {categories.filter(c => c.type === 'OUTFLOW').sort((a, b) => a.name.localeCompare(b.name)).map(cat => (
                  <div key={cat.category_id} className="glass-panel" style={{ padding: '12px 16px', display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{cat.symbol}</span>
                      <span style={{ fontWeight: '600' }}>{cat.name}</span>
                    </div>
                    {!cat.user_id ? (
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: '6px', fontWeight: 'bold' }}>
                        🔒Default
                      </span>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleStartEditCategory(cat)} style={{ background: 'transparent', border: 'none', color: 'var(--text-main)', cursor: 'pointer' }}>
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDeleteCategory(cat.category_id)} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer' }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 style={{ marginBottom: '10px', color: 'var(--color-inflow)' }}>Income Categories</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {categories.filter(c => c.type === 'INFLOW').sort((a, b) => a.name.localeCompare(b.name)).map(cat => (
                  <div key={cat.category_id} className="glass-panel" style={{ padding: '12px 16px', display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '20px' }}>{cat.symbol}</span>
                      <span style={{ fontWeight: '600' }}>{cat.name}</span>
                    </div>
                    {!cat.user_id ? (
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: '6px', fontWeight: 'bold' }}>
                        🔒Default
                      </span>
                    ) : (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleStartEditCategory(cat)} style={{ background: 'transparent', border: 'none', color: 'var(--text-main)', cursor: 'pointer' }}>
                          <Edit2 size={16} />
                        </button>
                        <button onClick={() => handleDeleteCategory(cat.category_id)} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer' }}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- B. ACCOUNTS MANAGER SHEET --- */}
      {activeSheet === 'accounts_manage' && (
        <div className="sheet-modal animate-fade-in">
          <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '12px' }}>
            <h2>Manage Bank Accounts</h2>
            <button onClick={() => { setAccountsDraft({}); setActiveSheet(null); refreshData(); }} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontWeight: 'bold' }}>Close</button>
          </div>

          {/* Add Cash/Bank Account Form */}
          {!isAddingAccountDirect ? (
            <button
              type="button"
              onClick={() => setIsAddingAccountDirect(true)}
              className="btn-primary"
              style={{ padding: '10px 14px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: 'fit-content', marginBottom: '8px' }}
            >
              <Plus size={16} /> Create New Account
            </button>
          ) : (
            <div className="glass-panel animate-fade-in" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Create New Account</h3>
                <button
                  type="button"
                  onClick={() => setIsAddingAccountDirect(false)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                >
                  Cancel
                </button>
              </div>
              <form onSubmit={handleCreateBankAccountDirect} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input type="text" placeholder="Account Name (e.g. Current Account)" value={newAccName} onChange={e => setNewAccName(e.target.value)} required style={{ flex: 1 }} />
                  <input type="number" step="0.01" placeholder="Starting Balance" value={newAccBalance} onChange={e => setNewAccBalance(e.target.value)} required style={{ width: '130px' }} />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ margin: 0 }}>Account Type:</label>
                    <select value={newAccType} onChange={e => setNewAccType(e.target.value)} style={{ padding: '8px', width: '100%' }}>
                      <option value="BANK">Bank Account</option>
                      <option value="CASH">Cash Wallet</option>
                    </select>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ margin: 0 }}>Currency:</label>
                    <select value={newAccCurrency} onChange={e => setNewAccCurrency(e.target.value)} style={{ padding: '8px', width: '100%' }}>
                      <option value="AED">AED</option>
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="checkbox" checked={newAccIncludeInTotal} onChange={e => setNewAccIncludeInTotal(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <label style={{ margin: 0 }}>Include in net sum</label>
                </div>
                <button type="submit" className="btn-primary" style={{ padding: '10px' }}>Add Bank Account</button>
              </form>
            </div>
          )}

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3>Current Bank Accounts & Cash Wallets</h3>
            {(() => {
              const nonCreditAccounts = accounts.filter(a => 
                a.type !== 'CREDIT_CARD' && 
                a.account_type !== 'CREDIT_CARD' && 
                a.account_type !== 'EXTERNAL_DEBT' && 
                !a.is_external_debt && 
                !(a.name && a.name.toLowerCase().includes('external debt'))
              );
              const activeAccounts = nonCreditAccounts.filter(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0');
              const disabledAccounts = nonCreditAccounts.filter(a => a.is_active === false || a.is_active === 0 || a.is_active === '0');

              const renderAccountCard = (acc) => {
                const draft = accountsDraft[acc.account_id] || {
                  name: acc.name || '',
                  balance: formatInputValue(acc.current_balance),
                  include_in_total: acc.include_in_total === true || acc.include_in_total === 1
                };

                return (
                  <div key={acc.account_id} className="glass-panel" style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <input 
                          name="name" 
                          type="text" 
                          value={draft.name} 
                          onChange={e => handleAccountDraftChange(acc.account_id, 'name', e.target.value)}
                          placeholder="Account Name"
                          required 
                          style={{ padding: '8px', flex: 1 }} 
                        />
                        <input 
                          name="balance" 
                          type="number" 
                          step="any" 
                          value={draft.balance} 
                          onChange={e => handleAccountDraftChange(acc.account_id, 'balance', e.target.value)}
                          placeholder="Balance"
                          required 
                          style={{ width: '110px', padding: '8px' }} 
                        />
                      </div>
                      <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0, cursor: 'pointer' }}>
                          <input 
                            name="include" 
                            type="checkbox" 
                            checked={draft.include_in_total} 
                            onChange={e => handleAccountDraftChange(acc.account_id, 'include_in_total', e.target.checked)}
                            style={{ width: '16px', height: '16px' }} 
                          />
                          Include in net balance
                          {renderInfoTooltip("If checked, this account's balance will be included in the total Net Balance donut visualizer at the top of your dashboard.")}
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          {(() => {
                            const isActive = acc.is_active !== false && acc.is_active !== 0 && acc.is_active !== '0';
                            const canDisable = parseFloat(acc.current_balance) === 0;
                            return (
                              <button type="button" onClick={async () => {
                                if (isActive && !canDisable) {
                                  showToast('Balance must be 0 to disable account.', 'error');
                                  return;
                                }
                                try {
                                  await apiClient.request(`/accounts/${acc.account_id}`, 'PUT', {
                                    name: acc.name,
                                    current_balance: acc.current_balance,
                                    include_in_total: acc.include_in_total,
                                    credit_limit: acc.credit_limit,
                                    statement_day: acc.statement_day,
                                    due_day: acc.due_day,
                                    is_active: !isActive
                                  });
                                  showToast(isActive ? 'Account disabled!' : 'Account enabled!', 'success');
                                  refreshData();
                                } catch (err) {
                                  showToast(err.message, 'error');
                                }
                              }} className="btn-secondary" style={{ padding: '6px 10px', fontSize: '11px', borderRadius: '8px', opacity: (isActive && !canDisable) ? 0.5 : 1 }}>
                                {isActive ? 'Disable' : 'Enable'}
                              </button>
                            );
                          })()}
                          <button type="button" onClick={() => handleDeleteAccountDirect(acc.account_id, acc.name)} className="btn-secondary" style={{ padding: '6px 10px', fontSize: '11px', borderRadius: '8px', color: 'var(--color-outflow)', borderColor: 'rgba(255,71,87,0.3)' }}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              };

              return (
                <>
                  {activeAccounts.length === 0 ? (
                    <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>No active accounts.</p>
                  ) : (
                    activeAccounts.map(acc => renderAccountCard(acc))
                  )}

                  {disabledAccounts.length > 0 && (
                    <div style={{ marginTop: '16px', borderTop: '1px solid var(--border-glass)', paddingTop: '12px' }}>
                      <div 
                        onClick={() => setIsDisabledAccountsExpanded(!isDisabledAccountsExpanded)}
                        style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 4px', userSelect: 'none' }}
                      >
                        <h4 style={{ margin: 0, color: 'var(--text-muted)', fontSize: '13px' }}>
                          🔒 Disabled Accounts ({disabledAccounts.length})
                        </h4>
                        <ChevronDown size={18} style={{ color: 'var(--text-muted)', transform: isDisabledAccountsExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                      </div>

                      {isDisabledAccountsExpanded && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px' }}>
                          {disabledAccounts.map(acc => renderAccountCard(acc))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Bottom Sticky Action Bar: Discard and Single Save Button */}
          <div style={{
            display: 'flex',
            gap: '12px',
            paddingTop: '14px',
            borderTop: '1px solid var(--border-glass)',
            background: 'var(--bg-secondary)',
            position: 'sticky',
            bottom: 0,
            zIndex: 10
          }}>
            <button
              type="button"
              onClick={() => {
                setAccountsDraft({});
                setActiveSheet(null);
              }}
              className="btn-secondary"
              style={{ flex: 1, padding: '12px', fontSize: '13px', borderRadius: '12px' }}
            >
              Discard
            </button>
            <button
              type="button"
              disabled={isSavingAccounts}
              onClick={handleSaveAllAccounts}
              className="btn-primary"
              style={{ flex: 1, padding: '12px', fontSize: '13px', fontWeight: 'bold', borderRadius: '12px' }}
            >
              {isSavingAccounts ? <ThinkingOrbLoader popup state="working" size={20} color="var(--accent-cyan)" text="Saving..." /> : '✓ Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* --- C. CREDIT CARDS MANAGER SHEET --- */}
      {activeSheet === 'cards_manage' && (
        <div className="sheet-modal animate-fade-in">
          <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '12px' }}>
            <h2>Credit Card Manager</h2>
            <button onClick={() => { setCardsDraft({}); setActiveSheet(null); refreshData(); }} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontWeight: 'bold' }}>Close</button>
          </div>

          {/* Add Credit Card Form */}
          {!isAddingCard ? (
            <button
              type="button"
              onClick={() => setIsAddingCard(true)}
              className="btn-primary"
              style={{ padding: '10px 14px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: 'fit-content', marginBottom: '8px' }}
            >
              <Plus size={16} /> Add Credit Card
            </button>
          ) : (
            <div className="glass-panel animate-fade-in" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3>Add Credit Card</h3>
                <button
                  type="button"
                  onClick={() => setIsAddingCard(false)}
                  style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                >
                  Cancel
                </button>
              </div>
              <form onSubmit={handleCreateCreditCardDirect} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input type="text" placeholder="Card Name (e.g. Skywards CC)" value={newCardName} onChange={e => setNewCardName(e.target.value)} required style={{ flex: 1 }} />
                  <input type="number" step="0.01" placeholder="Credit Limit" value={newCardLimit} onChange={e => setNewCardLimit(e.target.value)} required style={{ width: '130px' }} />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input type="number" step="0.01" placeholder="Starting Balance" value={newCardBalance} onChange={e => setNewCardBalance(e.target.value)} style={{ flex: 1 }} />
                  <input type="number" step="0.01" placeholder="Spend Target (Optional)" value={newCardSpendTarget} onChange={e => setNewCardSpendTarget(e.target.value)} style={{ width: '130px' }} />
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ margin: 0 }}>Target Calculation Type:</label>
                    <select value={newCardSpendTargetType} onChange={e => setNewCardSpendTargetType(e.target.value)} style={{ padding: '8px', width: '100%' }}>
                      <option value="CALENDAR">Calendar wise monthly</option>
                      <option value="STATEMENT">Statement date to statement date</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ margin: 0 }}>Closing Day:</label>
                    <input type="number" min="1" max="31" value={newCardClosing} onChange={e => setNewCardClosing(e.target.value)} required style={{ padding: '8px', width: '100%' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ margin: 0 }}>Due Day:</label>
                    <input type="number" min="1" max="31" value={newCardDue} onChange={e => setNewCardDue(e.target.value)} required style={{ padding: '8px', width: '100%' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ margin: 0 }}>Currency:</label>
                    <select value={newCardCurrency} onChange={e => setNewCardCurrency(e.target.value)} style={{ padding: '8px', width: '100%' }}>
                      <option value="AED">AED</option>
                      <option value="INR">INR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input type="checkbox" checked={newCardIncludeInTotal} onChange={e => setNewCardIncludeInTotal(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  <label style={{ margin: 0 }}>Include in net sum</label>
                </div>
                <button type="submit" className="btn-primary" style={{ padding: '10px' }}>Add Credit Card</button>
              </form>
            </div>
          )}

          {/* Payment Reminders Settings */}
          <div className="glass-panel" style={{ padding: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: 0, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={cardRemindersEnabled}
                onChange={e => {
                  setCardRemindersEnabled(e.target.checked);
                  localStorage.setItem('dhudd_card_reminders', e.target.checked);
                  showToast(`Credit Card Due Reminders ${e.target.checked ? 'enabled' : 'disabled'}!`, 'info');
                }}
                style={{ width: '18px', height: '18px' }}
              />
              Enable Timely Credit Payment Reminders
            </label>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3>Current Credit Cards</h3>
            {accounts.filter(a => a.type === 'CREDIT_CARD').map(card => {
              const draft = cardsDraft[card.account_id] || {
                name: card.name || '',
                owedBalance: formatInputValue(-parseFloat(card.current_balance || 0)),
                limit: card.credit_limit != null ? String(card.credit_limit) : '',
                statement: card.statement_day != null ? String(card.statement_day) : '',
                due: card.due_day != null ? String(card.due_day) : '',
                spendTarget: card.monthly_spend_target != null ? String(card.monthly_spend_target) : '',
                spendTargetType: card.monthly_spend_target_type || 'CALENDAR',
                include_in_total: card.include_in_total === true || card.include_in_total === 1,
                master_card_id: card.master_card_id || null,
                master_card_name: card.master_card_name || null,
                master_bank_name: card.master_bank_name || null,
                master_card_tier: card.master_card_tier || null
              };

              const currentMasterId = draft.master_card_id !== undefined ? draft.master_card_id : (card.master_card_id || null);
              const currentMasterName = draft.master_card_name !== undefined ? draft.master_card_name : (card.master_card_name || null);
              const currentMasterBank = draft.master_bank_name !== undefined ? draft.master_bank_name : (card.master_bank_name || null);

              return (
                <div key={card.account_id} className="glass-panel" style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Official Directory Connection Strip */}
                    <div style={{
                      padding: '10px 12px',
                      borderRadius: '10px',
                      background: currentMasterId ? 'rgba(0, 245, 212, 0.05)' : 'rgba(255, 171, 0, 0.07)',
                      border: currentMasterId ? '1px solid rgba(0, 245, 212, 0.25)' : '1px solid rgba(255, 171, 0, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: '8px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                        {currentMasterId ? (
                          <>
                            <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'rgba(0, 245, 212, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent-cyan)', flexShrink: 0, fontWeight: 'bold' }}>
                              ✓
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--accent-cyan)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {currentMasterName || 'Directory Linked'}
                              </div>
                              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                {currentMasterBank ? `${currentMasterBank} • ` : ''}Catalog Verified
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'rgba(255, 171, 0, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-warning, #f59e0b)', flexShrink: 0, fontSize: '12px' }}>
                              ⚠️
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--color-warning, #f59e0b)' }}>
                                Custom Card
                              </div>
                              <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                                Not connected to directory (Community & Rewards disabled)
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        {currentMasterId ? (
                          <>
                            <button
                              type="button"
                              onClick={() => {
                                setCardToConnect({
                                  account: card,
                                  targetFeature: 'manage'
                                });
                              }}
                              className="btn-secondary"
                              style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px' }}
                            >
                              Change
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                handleCardDraftChange(card.account_id, 'master_card_id', null);
                                handleCardDraftChange(card.account_id, 'master_card_name', null);
                                handleCardDraftChange(card.account_id, 'master_bank_name', null);
                                handleCardDraftChange(card.account_id, 'master_card_tier', null);
                              }}
                              className="btn-secondary"
                              style={{ padding: '5px 10px', fontSize: '11px', borderRadius: '6px', color: 'var(--color-outflow)' }}
                              title="Unlink from directory and convert to custom card"
                            >
                              Unlink
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setCardToConnect({
                                account: card,
                                targetFeature: 'manage'
                              });
                            }}
                            className="btn-primary"
                            style={{ padding: '6px 12px', fontSize: '11px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}
                          >
                            🔗 Connect to Directory
                          </button>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                      <div style={{ flex: 1, minWidth: '180px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '11px', fontWeight: 'bold' }}>
                          Card Name
                        </label>
                        <input 
                          name="cardName" 
                          type="text" 
                          value={draft.name} 
                          onChange={e => handleCardDraftChange(card.account_id, 'name', e.target.value)}
                          placeholder="Card Name (e.g. Skywards CC)" 
                          required 
                          style={{ padding: '8px', width: '100%', boxSizing: 'border-box' }} 
                        />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Balance: {formatMoney(-parseFloat(card.current_balance), card.currency)}</span>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveAccount(card);
                            setNewCcBalanceValue(formatInputValue(-parseFloat(card.current_balance || 0)));
                            setAdjustCcBalanceOpen(true);
                          }}
                          style={{
                            background: 'rgba(0,245,212,0.1)',
                            border: '1px solid var(--border-glass)',
                            borderRadius: '6px',
                            padding: '4px 8px',
                            fontSize: '11px',
                            color: 'var(--accent-cyan)',
                            cursor: 'pointer'
                          }}
                        >
                          ✏️ Adjust
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'flex', alignItems: 'center' }}>
                          Owed Balance ({card.currency})
                          {renderInfoTooltip("Manually update the current owed balance for this credit card.")}
                        </label>
                        <input 
                          name="owedBalance" 
                          type="number" 
                          step="any" 
                          value={draft.owedBalance} 
                          onChange={e => handleCardDraftChange(card.account_id, 'owedBalance', e.target.value)}
                          style={{ padding: '8px' }} 
                          required 
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label>Credit Limit ({card.currency})</label>
                        <input 
                          name="limit" 
                          type="number" 
                          step="any" 
                          value={draft.limit} 
                          onChange={e => handleCardDraftChange(card.account_id, 'limit', e.target.value)}
                          style={{ padding: '8px' }} 
                          required 
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ display: 'flex', alignItems: 'center' }}>
                          Spend Target ({card.currency})
                          {renderInfoTooltip("Optional monthly spending target budget for this card. Leaves progress bar hidden if empty.")}
                        </label>
                        <input 
                          name="spendTarget" 
                          type="number" 
                          step="any" 
                          value={draft.spendTarget} 
                          onChange={e => handleCardDraftChange(card.account_id, 'spendTarget', e.target.value)}
                          placeholder="Optional" 
                          style={{ padding: '8px' }} 
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label>Target Calculation Type</label>
                        <select 
                          name="spendTargetType" 
                          value={draft.spendTargetType} 
                          onChange={e => handleCardDraftChange(card.account_id, 'spendTargetType', e.target.value)}
                          style={{ padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', borderRadius: '8px', color: 'var(--text-main)', width: '100%' }}
                        >
                          <option value="CALENDAR">Calendar wise monthly</option>
                          <option value="STATEMENT">Statement date to statement date</option>
                        </select>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <div style={{ flex: 1 }}>
                        <label>Closing Day</label>
                        <input 
                          name="statement" 
                          type="number" 
                          min="1" 
                          max="31" 
                          value={draft.statement} 
                          onChange={e => handleCardDraftChange(card.account_id, 'statement', e.target.value)}
                          style={{ padding: '8px' }} 
                          required 
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label>Due Day</label>
                        <input 
                          name="due" 
                          type="number" 
                          min="1" 
                          max="31" 
                          value={draft.due} 
                          onChange={e => handleCardDraftChange(card.account_id, 'due', e.target.value)}
                          style={{ padding: '8px' }} 
                          required 
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input 
                        name="include" 
                        type="checkbox" 
                        checked={draft.include_in_total} 
                        onChange={e => handleCardDraftChange(card.account_id, 'include_in_total', e.target.checked)}
                        style={{ width: '18px', height: '18px' }} 
                      />
                      <label style={{ margin: 0, cursor: 'pointer' }}>Include in net sum</label>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                      {(() => {
                        const isActive = card.is_active !== false && card.is_active !== 0 && card.is_active !== '0';
                        const canDisable = parseFloat(card.current_balance) === 0;
                        return (
                          <button type="button" onClick={async () => {
                            if (isActive && !canDisable) {
                              showToast('Owed balance must be 0 to disable card.', 'error');
                              return;
                            }
                            try {
                              await apiClient.request(`/accounts/${card.account_id}`, 'PUT', {
                                name: card.name,
                                current_balance: card.current_balance,
                                include_in_total: card.include_in_total,
                                credit_limit: card.credit_limit,
                                statement_day: card.statement_day,
                                due_day: card.due_day,
                                monthly_spend_target: card.monthly_spend_target,
                                monthly_spend_target_type: card.monthly_spend_target_type,
                                is_active: !isActive
                              });
                              showToast(isActive ? 'Card disabled!' : 'Card enabled!', 'success');
                              refreshData();
                            } catch (err) {
                              showToast(err.message, 'error');
                            }
                          }} className="btn-secondary" style={{ flex: 1, padding: '10px', fontSize: '12px', borderRadius: '10px', opacity: (isActive && !canDisable) ? 0.5 : 1 }}>
                            {isActive ? 'Disable' : 'Enable'}
                          </button>
                        );
                      })()}
                      <button type="button" onClick={() => handleDeleteAccountDirect(card.account_id, card.name)} className="btn-secondary" style={{ flex: 1, padding: '10px', fontSize: '12px', borderRadius: '10px', color: 'var(--color-outflow)', borderColor: 'rgba(255,71,87,0.3)' }}>
                        <Trash2 size={16} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom Sticky Action Bar: Discard and Single Save Button */}
          <div style={{
            display: 'flex',
            gap: '12px',
            paddingTop: '14px',
            borderTop: '1px solid var(--border-glass)',
            background: 'var(--bg-secondary)',
            position: 'sticky',
            bottom: 0,
            zIndex: 10
          }}>
            <button
              type="button"
              onClick={() => {
                setCardsDraft({});
                setActiveSheet(null);
              }}
              className="btn-secondary"
              style={{ flex: 1, padding: '12px', fontSize: '13px', borderRadius: '12px' }}
            >
              Discard
            </button>
            <button
              type="button"
              disabled={isSavingCards}
              onClick={handleSaveAllCards}
              className="btn-primary"
              style={{ flex: 1, padding: '12px', fontSize: '13px', fontWeight: 'bold', borderRadius: '12px' }}
            >
              {isSavingCards ? <ThinkingOrbLoader popup state="working" size={20} color="var(--accent-cyan)" text="Saving..." /> : '✓ Save Changes'}
            </button>
          </div>
        </div>
      )}

      {/* --- D. CURRENCIES MATRIX SHEET --- */}
      {activeSheet === 'currencies_manage' && (
        <div className="sheet-modal animate-fade-in">
          <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '12px' }}>
            <h2>Currencies & Rates</h2>
            <button onClick={() => { setActiveSheet(null); refreshData(); }} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontWeight: 'bold' }}>Close</button>
          </div>

          {/* Section 1: Preferred Currencies */}
          <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3>Preferred Currencies</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {preferredCurrencies.map(curr => (
                <div key={curr} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  background: 'rgba(0, 245, 212, 0.1)', border: '1px solid var(--accent-cyan)',
                  borderRadius: '16px', padding: '6px 12px', fontSize: '13px', color: 'var(--text-main)'
                }}>
                  <strong>{curr}</strong>
                  <button onClick={() => handleRemovePreferredCurrency(curr)} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontSize: '14px', padding: 0 }}>×</button>
                </div>
              ))}
            </div>

            <form onSubmit={handleAddPreferredCurrency} style={{ display: 'flex', gap: '8px', marginTop: '6px' }}>
              <select value={newPrefCurrency} onChange={e => setNewPrefCurrency(e.target.value)} style={{ padding: '8px', flex: 1 }}>
                {['USD', 'AED', 'INR', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CNY', 'SAR', 'QAR', 'OMR', 'KWD', 'BHD', 'SGD'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <button type="submit" className="btn-primary" style={{ padding: '8px 16px', fontSize: '12px', borderRadius: '10px' }}>Add Preferred</button>
            </form>

            {/* Custom Short Currency Code Entry */}
            <form onSubmit={async (e) => {
              e.preventDefault();
              const code = customShortCurrencyInput.toUpperCase().trim();
              if (!code || code.length !== 3) {
                showToast('Currency code must be 3 letters (e.g. AED, USD)', 'error');
                return;
              }
              if (preferredCurrencies.includes(code)) {
                showToast('Currency is already in your preferred list', 'warning');
                return;
              }
              const next = [...preferredCurrencies, code];
              try {
                await apiClient.request('/currencies/preferred', 'POST', { currencies: next });
                setPreferredCurrencies(next);
                syncEngine.setLocal('dhudd_cache_preferred_currencies', next);
                setCustomShortCurrencyInput('');
                showToast(`Custom currency ${code} added!`, 'success');
              } catch (err) {
                showToast(err.message, 'error');
              }
            }} style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <input
                type="text"
                placeholder="Enter Short Code (e.g. AED)"
                maxLength={3}
                value={customShortCurrencyInput}
                onChange={e => setCustomShortCurrencyInput(e.target.value.toUpperCase())}
                style={{ padding: '8px', flex: 1, textTransform: 'uppercase' }}
              />
              <button type="submit" className="btn-primary" style={{ padding: '8px 16px', fontSize: '12px', borderRadius: '10px' }}>
                Add Custom Code
              </button>
            </form>
          </div>

          {/* Section 2: Log/Edit Rates */}
          <form onSubmit={handleCreateCurrencyRate} className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <h3>Log/Edit Conversion Rate</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select value={newRateFrom} onChange={e => setNewRateFrom(e.target.value)} style={{ padding: '8px', flex: 1 }}>
                {preferredCurrencies.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <span style={{ display: 'flex', alignItems: 'center' }}>➔</span>
              <select value={newRateTo} onChange={e => setNewRateTo(e.target.value)} style={{ padding: '8px', flex: 1 }}>
                {preferredCurrencies.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label>Multiplier Rate (relative to source)</label>
              <input type="number" step="0.00001" placeholder="e.g. 3.67250" value={newRateValue} onChange={e => setNewRateValue(e.target.value)} required />
            </div>
            <div>
              <label>Conversion Date (Interval Boundary)</label>
              <input type="date" value={newRateDate} onChange={e => setNewRateDate(e.target.value)} required />
            </div>
            <button type="submit" className="btn-primary" style={{ padding: '10px' }}>Log Rate Period</button>
          </form>

          {/* Section 3: Existing Rates index */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            <h3 style={{ marginBottom: '10px' }}>History Rates Index</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {currencyRates.map((r, i) => (
                <div key={i} className="glass-panel" style={{ padding: '12px 16px', display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
                  <div>
                    <span>{r.from_currency} ➔ {r.to_currency}</span>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block' }}>Date: {r.rate_date}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontWeight: '700' }}>{parseFloat(r.rate).toFixed(5)}</span>
                    <button onClick={() => handleDeleteCurrencyRate(r)} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer' }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* --- E. SETTINGS & UTILITIES SHEET --- */}
      {activeSheet === 'settings_manage' && (
        <div className="sheet-modal animate-fade-in">
          <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '12px' }}>
            <h2>Settings</h2>
            <button onClick={() => { setActiveSheet(null); refreshData(); }} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontWeight: 'bold' }}>Close</button>
          </div>

          {/* User Profile Settings */}
          <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div 
              onClick={() => setExpandedSettingSection(expandedSettingSection === 'profile' ? null : 'profile')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
            >
              <h3 style={{ margin: 0 }}>👤 User Profile Settings</h3>
              <ChevronDown size={18} style={{ color: 'var(--accent-cyan)', transform: expandedSettingSection === 'profile' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>

            {expandedSettingSection === 'profile' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' }}>
                <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                  Customize your profile details. Friends will see your display name and bio.
                </p>

            {/* Profile Picture Avatar Section */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', background: 'rgba(255, 255, 255, 0.03)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-glass)', marginBottom: '4px' }}>
              <div style={{ position: 'relative', width: '60px', height: '60px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden', border: '2px solid var(--accent-cyan)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--accent-cyan)', position: 'absolute' }}>
                  {(profileDisplayName || 'U')[0].toUpperCase()}
                </div>
                {profileAvatarUrl && (
                  <img src={apiClient.getAvatarUrl(profileAvatarUrl)} alt="Avatar" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'relative', zIndex: 1 }} />
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1 }}>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main)' }}>Profile Picture</span>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <label className="btn-primary" style={{ padding: '6px 12px', fontSize: '11px', cursor: 'pointer', margin: 0, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                    {isUploadingAvatar ? <ThinkingOrbLoader popup state="composing" size={20} color="#a55eea" text="Uploading..." /> : <>📷 Change Picture</>}
                    <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={handleFileSelectedForCropping} disabled={isUploadingAvatar} style={{ display: 'none' }} />
                  </label>
                  {profileAvatarUrl && (
                    <button
                      type="button"
                      onClick={handleRemoveAvatar}
                      className="btn-secondary"
                      style={{ padding: '6px 10px', fontSize: '11px', color: 'var(--color-outflow)' }}
                    >
                      🗑️ Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Email Address & Change Email Panel */}
            <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '12px 14px', borderRadius: '12px', border: '1px solid var(--border-glass)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>Account Email Address</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                    {profileEmail || localStorage.getItem('dhudd_email') || 'Not Set'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsChangingEmail(!isChangingEmail);
                    setNewEmailInput('');
                    setChangeEmailOtpSent(false);
                    setChangeEmailOtpCode('');
                    setChangeEmailTurnstileToken(null);
                  }}
                  className="btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '11px', color: 'var(--accent-cyan)' }}
                >
                  {isChangingEmail ? '✕ Cancel' : '✉️ Change Email'}
                </button>
              </div>

              {isChangingEmail && (
                <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px', paddingTop: '10px', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
                  <p style={{ fontSize: '11px', color: 'var(--accent-cyan)', margin: 0, lineHeight: '1.4' }}>
                    💡 Enter your new email address. A 6-digit verification code will be sent to the new email to verify ownership.
                  </p>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: '600', display: 'block', marginBottom: '4px' }}>New Email Address</label>
                    <input
                      type="email"
                      placeholder="e.g. new.email@example.com"
                      value={newEmailInput}
                      onChange={e => setNewEmailInput(e.target.value)}
                      disabled={changeEmailOtpSent}
                      style={{ width: '100%', padding: '10px', borderRadius: '10px', background: 'var(--bg-input)', color: 'var(--text-main)', border: '1px solid var(--border-glass)', fontSize: '13px' }}
                    />
                  </div>

                  {!changeEmailOtpSent && (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
                        <Turnstile
                          key="change_email_turnstile"
                          siteKey="0x4AAAAAAEBFr3-Dsk6xeSLZ"
                          onSuccess={(token) => setChangeEmailTurnstileToken(token)}
                          onError={() => setChangeEmailTurnstileToken(null)}
                          onExpire={() => setChangeEmailTurnstileToken(null)}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleSendChangeEmailOtp}
                        disabled={isSendingChangeEmailOtp || !newEmailInput || !changeEmailTurnstileToken || changeEmailCooldown > 0}
                        className="btn-primary"
                        style={{
                          padding: '10px',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          opacity: (isSendingChangeEmailOtp || !newEmailInput || !changeEmailTurnstileToken || changeEmailCooldown > 0) ? 0.6 : 1,
                          cursor: (isSendingChangeEmailOtp || !newEmailInput || !changeEmailTurnstileToken || changeEmailCooldown > 0) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {changeEmailCooldown > 0
                          ? `Resend Code in ${changeEmailCooldown}s`
                          : (isSendingChangeEmailOtp ? <ThinkingOrbLoader popup state="searching" size={20} color="var(--accent-cyan)" text="Sending Verification Code..." /> : '📨 Send Code to New Email')}
                      </button>
                    </>
                  )}

                  {changeEmailOtpSent && (
                    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ background: 'rgba(0, 245, 212, 0.08)', padding: '8px 10px', borderRadius: '8px', border: '1px solid rgba(0, 245, 212, 0.2)', fontSize: '11px', color: 'var(--accent-cyan)' }}>
                        ✓ Verification code sent to <strong>{newEmailInput}</strong>. Check your inbox.
                      </div>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <label style={{ fontSize: '11px', fontWeight: '600' }}>6-Digit Verification Code</label>
                          {changeEmailCooldown > 0 ? (
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Resend in {changeEmailCooldown}s</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setChangeEmailOtpSent(false);
                                setChangeEmailTurnstileToken(null);
                              }}
                              style={{ background: 'none', border: 'none', color: 'var(--accent-cyan)', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                            >
                              Request New Code
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          maxLength={6}
                          placeholder="••••••"
                          value={changeEmailOtpCode}
                          onChange={e => setChangeEmailOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                          style={{ width: '100%', padding: '10px', letterSpacing: '6px', fontSize: '18px', textAlign: 'center', borderRadius: '10px', background: 'var(--bg-input)', color: 'var(--text-main)', border: '1px solid var(--border-glass)', fontWeight: 'bold' }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleVerifyChangeEmail}
                        disabled={isVerifyingChangeEmail || changeEmailOtpCode.length !== 6}
                        className="btn-primary"
                        style={{
                          padding: '10px',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          opacity: (isVerifyingChangeEmail || changeEmailOtpCode.length !== 6) ? 0.6 : 1,
                          cursor: (isVerifyingChangeEmail || changeEmailOtpCode.length !== 6) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {isVerifyingChangeEmail ? <ThinkingOrbLoader popup state="searching" size={20} color="var(--accent-cyan)" text="Verifying & Updating..." /> : '✓ Confirm & Update Email Address'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>Display Name</label>
              <input
                type="text"
                placeholder="e.g. Fahiz"
                value={profileDisplayName}
                onChange={e => setProfileDisplayName(e.target.value)}
                style={{ width: '100%', padding: '10px' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>Bio / Description</label>
              <textarea
                placeholder="Write something about yourself..."
                value={profileBio}
                onChange={e => setProfileBio(e.target.value)}
                rows={2}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid var(--border-glass)',
                  color: 'var(--text-main)',
                  fontFamily: 'inherit',
                  fontSize: '13px',
                  resize: 'none'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '130px' }}>
                <label style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>🎂 Date of Birth</label>
                <input
                  type="date"
                  max={getMaxDob7YearsAgo()}
                  value={profileDob}
                  onChange={e => setProfileDob(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px' }}
                />
              </div>
              <div style={{ flex: 1, minWidth: '130px' }}>
                <label style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>👤 Gender</label>
                <select
                  value={profileGender}
                  onChange={e => setProfileGender(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px' }}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                  <option value="Prefer not to say">Prefer not to say</option>
                </select>
              </div>
            </div>
            <div>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>📱 Phone Number (with Country Code)</label>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                <select
                  value={profileCountryCode}
                  onChange={e => setProfileCountryCode(e.target.value)}
                  style={{ padding: '10px 8px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '12px', width: '110px' }}
                >
                  {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
                {profileCountryCode === 'CUSTOM' && (
                  <input
                    type="text"
                    placeholder="+123"
                    value={profileCustomCc}
                    onChange={e => setProfileCustomCc(e.target.value)}
                    style={{ padding: '10px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '12px', width: '60px' }}
                  />
                )}
                <input
                  type="tel"
                  placeholder="50 123 4567"
                  value={profilePhoneDigits}
                  onChange={e => setProfilePhoneDigits(e.target.value)}
                  style={{ flex: 1, padding: '10px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px', minWidth: '110px' }}
                />
              </div>
            </div>
            <button type="button" onClick={handleSaveProfile} className="btn-primary" style={{ padding: '10px', fontSize: '13px', width: '100%' }}>
              Save Profile Details
            </button>
              </div>
            )}
          </div>

          {/* WhatsApp Integration & Preferences Panel */}
          <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div 
              onClick={() => setExpandedSettingSection(expandedSettingSection === 'whatsapp' ? null : 'whatsapp')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
            >
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '6px', color: '#25D366' }}>
                💬 WhatsApp Integration & Preferences
              </h3>
              <ChevronDown size={18} style={{ color: '#25D366', transform: expandedSettingSection === 'whatsapp' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>

            {expandedSettingSection === 'whatsapp' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '4px' }}>
                <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                  Link your WhatsApp phone number to receive monthly summaries, bill split alerts, and allow friends to send automated debt reminders.
                </p>

            {(profilePhone || waPhone) ? (
              <div style={{ padding: '10px 14px', background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.25)', borderRadius: '12px', fontSize: '12px', color: '#25D366', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>📱 Linked WhatsApp Number:</span>
                <span style={{ fontWeight: 'bold' }}>{profilePhone || waPhone}</span>
              </div>
            ) : (
              <div>
                <label style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>📱 WhatsApp Phone Number (with Country Code)</label>
                <input
                  type="tel"
                  placeholder="e.g. +971501234567"
                  value={waPhone}
                  onChange={e => setWaPhone(e.target.value)}
                  style={{ width: '100%', padding: '10px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px' }}
                />
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px' }}>
              <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={waNotifyMonthlySummary}
                  onChange={e => setWaNotifyMonthlySummary(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span>Receive Monthly Financial Summaries on WhatsApp (1st of month)</span>
              </label>

              <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={waNotifySplitExpenses}
                  onChange={e => setWaNotifySplitExpenses(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span>Receive Instant WhatsApp Alerts for Bill Splits & Settlements</span>
              </label>

              <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={waNotifyDebtReminders}
                  onChange={e => setWaNotifyDebtReminders(e.target.checked)}
                  style={{ width: '16px', height: '16px' }}
                />
                <span>Allow Friends to Send Me Automated WhatsApp Debt Reminders</span>
              </label>
            </div>

            <button
              type="button"
              onClick={handleSaveWhatsAppSettings}
              className="btn-primary"
              style={{ padding: '10px', fontSize: '13px', width: '100%', background: '#25D366', color: '#ffffff' }}
            >
              Save WhatsApp Preferences
            </button>

            {/* Queued WhatsApp Messages List */}
            <div style={{ marginTop: '14px', borderTop: '1px solid var(--border-glass)', paddingTop: '12px' }}>
              <h4 style={{ fontSize: '12px', color: '#25D366', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>📋 Queued Messages ({waQueueList.length})</span>
                <button 
                  type="button" 
                  onClick={async () => {
                    try {
                      const qData = await apiClient.request('/whatsapp/queue');
                      setWaQueueList(Array.isArray(qData) ? qData : []);
                    } catch (e) {
                      console.error('Error fetching queue:', e);
                    }
                  }} 
                  style={{ background: 'transparent', border: 'none', color: 'var(--accent-cyan)', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  🔄 Refresh
                </button>
              </h4>

              {waQueueList.length === 0 ? (
                <p style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
                  No messages currently queued. Sent messages will process automatically via the anti-ban worker.
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '160px', overflowY: 'auto' }}>
                  {waQueueList.map((msg, mIdx) => (
                    <div key={msg.queue_id || msg.id || mIdx} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-glass)', borderRadius: '8px', padding: '8px 10px', fontSize: '11px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontWeight: 'bold', display: 'block' }}>To: {msg.to_phone}</span>
                        <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{msg.message_type || 'REMINDER'} • {formatDateTime(msg.created_at)}</span>
                      </div>
                      <span style={{ padding: '2px 8px', borderRadius: '12px', background: msg.status === 'SENT' ? 'rgba(0,245,212,0.15)' : 'rgba(255,183,3,0.15)', color: msg.status === 'SENT' ? 'var(--accent-cyan)' : '#ffb703', fontWeight: 'bold', fontSize: '10px' }}>
                        {msg.status || 'PENDING'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
              </div>
            )}
          </div>

          {/* Push Notifications Settings */}
          <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div 
              onClick={() => setExpandedSettingSection(expandedSettingSection === 'notifications' ? null : 'notifications')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
            >
              <h3 style={{ margin: 0 }}>🔔 Push Notifications</h3>
              <ChevronDown size={18} style={{ color: 'var(--accent-cyan)', transform: expandedSettingSection === 'notifications' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>

            {expandedSettingSection === 'notifications' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' }}>
                <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                  Enable native browser and mobile web push notifications for transaction updates, debt settlements, and recurring bill alerts.
                </p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
              <button
                type="button"
                onClick={async () => {
                  if ('Notification' in window) {
                    const perm = await Notification.requestPermission();
                    if (perm === 'granted') {
                      showToast('Push Notifications Enabled!', 'success');
                      handleSaveReminderSettings(true, dailyReminderTime || '21:00');
                      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                        navigator.serviceWorker.ready.then(reg => {
                          reg.showNotification('tezer Push Active', {
                            body: 'Push notifications enabled successfully for tezer!',
                            icon: '/pwa-192x192.png',
                            badge: '/favicon.png',
                            vibrate: [100, 50, 100]
                          });
                        });
                      } else {
                        new Notification('tezer Push Active', {
                          body: 'Push notifications enabled successfully for tezer!',
                          icon: '/pwa-192x192.png',
                          badge: '/favicon.png'
                        });
                      }
                    } else {
                      showToast('Push notification permission denied in browser.', 'error');
                    }
                  } else {
                    showToast('Push notifications are not supported by this browser.', 'error');
                  }
                }}
                className="btn-primary"
                style={{ flex: 1, padding: '10px', fontSize: '12px' }}
              >
                Enable Push Notifications
              </button>
              <button
                type="button"
                onClick={() => {
                  if ('Notification' in window && Notification.permission === 'granted') {
                    if (navigator.serviceWorker && navigator.serviceWorker.controller) {
                      navigator.serviceWorker.ready.then(reg => {
                        reg.showNotification('tezer Test Alert', {
                          body: 'This is a test push notification from tezer.',
                          icon: '/pwa-192x192.png',
                          badge: '/favicon.png',
                          vibrate: [100, 50, 100]
                        });
                      });
                    } else {
                      new Notification('tezer Test Alert', {
                        body: 'This is a test push notification from tezer.',
                        icon: '/pwa-192x192.png',
                        badge: '/favicon.png'
                      });
                    }
                    showToast('Test push notification sent!', 'success');
                  } else {
                    showToast('Please enable Push Notifications first.', 'error');
                  }
                }}
                className="btn-secondary"
                style={{ flex: 1, padding: '10px', fontSize: '12px' }}
              >
                Send Test Alert
              </button>
            </div>

            {/* Daily Expense Recording Reminder Card */}
            <div style={{
              marginTop: '12px',
              padding: '14px',
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid var(--border-glass)',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>📝</span>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 'bold', color: 'var(--text-main)' }}>Daily Expense Recording Reminder</h4>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      Receive an inspiring financial quote push notification daily to record your transactions.
                    </span>
                  </div>
                </div>
                <label style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}>
                  <input
                    type="checkbox"
                    checked={dailyReminderEnabled}
                    onChange={e => handleSaveReminderSettings(e.target.checked, dailyReminderTime)}
                    style={{ width: '18px', height: '18px', accentColor: 'var(--accent-cyan)', cursor: 'pointer' }}
                  />
                </label>
              </div>

              {dailyReminderEnabled && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-main)', display: 'block' }}>
                        Reminder Time
                      </label>
                      <span style={{ fontSize: '10.5px', color: 'var(--text-muted)' }}>
                        Default is 9:00 PM (21:00) every day.
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="time"
                        value={dailyReminderTime}
                        onChange={e => handleSaveReminderSettings(true, e.target.value)}
                        style={{
                          padding: '6px 10px',
                          fontSize: '13px',
                          fontWeight: 'bold',
                          borderRadius: '8px',
                          border: '1px solid var(--accent-cyan)',
                          background: 'var(--bg-input)',
                          color: 'var(--text-main)',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      />
                      <span style={{ fontSize: '12px', color: 'var(--accent-cyan)', fontWeight: 'bold' }}>
                        {(() => {
                          const [h, m] = (dailyReminderTime || '21:00').split(':');
                          const hourNum = parseInt(h, 10);
                          const ampm = hourNum >= 12 ? 'PM' : 'AM';
                          const displayHour = hourNum % 12 === 0 ? 12 : hourNum % 12;
                          return `${displayHour}:${m || '00'} ${ampm}`;
                        })()}
                      </span>
                    </div>
                  </div>

                  {/* Quote Preview */}
                  <div style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    background: 'rgba(0, 245, 212, 0.05)',
                    border: '1px dashed rgba(0, 245, 212, 0.3)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '10.5px', color: 'var(--accent-cyan)', fontWeight: '700' }}>
                      <span>💬 SAMPLE DAILY REMINDER NOTIFICATION:</span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-main)', fontStyle: 'italic' }}>
                      "Beware of little expenses; a small leak will sink a great ship." — Benjamin Franklin
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                      Tap to record today's transactions and keep your streak alive!
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={async () => {
                        const randomQuote = FINANCIAL_REMINDER_QUOTES[Math.floor(Math.random() * FINANCIAL_REMINDER_QUOTES.length)];
                        triggerPushNotification(
                          'Time to record your expenses! 📝',
                          `"${randomQuote.text}" — ${randomQuote.author}`
                        );
                        if (apiClient.isOnline() && apiClient.getToken()) {
                          try {
                            await apiClient.request('/notifications/test-reminder', { method: 'POST' });
                            refreshData();
                          } catch (e) {}
                        }
                        showToast('Test daily reminder notification sent!', 'success');
                      }}
                      className="btn-secondary"
                      style={{ flex: 1, padding: '8px', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                    >
                      <span>🔔 Test Daily Reminder</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
          {/* Regional & Timezone Settings */}
          <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div 
              onClick={() => setExpandedSettingSection(expandedSettingSection === 'timezone' ? null : 'timezone')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
            >
              <h3 style={{ margin: 0 }}>🌐 Regional & Timezone</h3>
              <ChevronDown size={18} style={{ color: 'var(--accent-cyan)', transform: expandedSettingSection === 'timezone' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>

            {expandedSettingSection === 'timezone' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' }}>
                <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                  Adjust the display timezone for transaction timestamps.
                </p>
            <div>
              <label style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>Select Timezone</label>
              <select
                value={
                  ['local', 'UTC', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles'].includes(selectedTimezone)
                    ? selectedTimezone
                    : 'custom'
                }
                onChange={e => {
                  const val = e.target.value;
                  if (val !== 'custom') {
                    setSelectedTimezone(val);
                    localStorage.setItem('dhudd_timezone', val);
                    showToast(`Timezone updated to ${val}`, 'success');
                  } else {
                    setSelectedTimezone('Asia/Tokyo');
                    localStorage.setItem('dhudd_timezone', 'Asia/Tokyo');
                  }
                }}
                style={{ width: '100%', padding: '10px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px' }}
              >
                <option value="local">Use Browser Local ({Intl.DateTimeFormat().resolvedOptions().timeZone})</option>
                <option value="UTC">Coordinated Universal Time (UTC)</option>
                <option value="Asia/Dubai">Gulf Standard Time (GST / Dubai)</option>
                <option value="Asia/Kolkata">India Standard Time (IST / India)</option>
                <option value="Asia/Singapore">Singapore Time (SGT)</option>
                <option value="Europe/London">Greenwich Mean Time (GMT / London)</option>
                <option value="Europe/Paris">Central European Time (CET / Paris)</option>
                <option value="America/New_York">Eastern Standard Time (EST / New York)</option>
                <option value="America/Los_Angeles">Pacific Standard Time (PST / Los Angeles)</option>
                <option value="custom">Custom IANA Timezone...</option>
              </select>
            </div>
            {!['local', 'UTC', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Europe/London', 'Europe/Paris', 'America/New_York', 'America/Los_Angeles'].includes(selectedTimezone) && (
              <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>Custom IANA Timezone Name</label>
                <input
                  type="text"
                  placeholder="e.g. Europe/Zurich"
                  value={selectedTimezone}
                  onChange={e => {
                    setSelectedTimezone(e.target.value);
                    localStorage.setItem('dhudd_timezone', e.target.value);
                  }}
                  style={{ width: '100%', padding: '10px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px' }}
                />
                <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                  Please enter a valid IANA timezone name. If invalid, the browser will default to local time.
                </span>
              </div>
            )}
              </div>
            )}
          </div>

          {/* Password & Authentication Settings */}
          <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div 
              onClick={() => setExpandedSettingSection(expandedSettingSection === 'security' ? null : 'security')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Key size={18} style={{ color: 'var(--accent-cyan)' }} />
                <h3 style={{ margin: 0 }}>Password & Authentication</h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '3px 8px',
                  borderRadius: '12px',
                  background: userAuthProvider === 'GOOGLE' ? 'rgba(66, 133, 244, 0.15)' : 'rgba(0, 245, 212, 0.12)',
                  color: userAuthProvider === 'GOOGLE' ? '#4285F4' : 'var(--accent-cyan)',
                  border: userAuthProvider === 'GOOGLE' ? '1px solid rgba(66, 133, 244, 0.3)' : '1px solid rgba(0, 245, 212, 0.25)'
                }}>
                  {userAuthProvider === 'GOOGLE' ? '🌐 Google Account' : '✉️ Email Account'}
                </span>
                <ChevronDown size={18} style={{ color: 'var(--accent-cyan)', transform: expandedSettingSection === 'security' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </div>
            </div>

            {expandedSettingSection === 'security' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '4px' }}>
                {userAuthProvider === 'GOOGLE' ? (
                  <div style={{ fontSize: '12px', color: '#4285F4', background: 'rgba(66, 133, 244, 0.08)', padding: '12px 14px', border: '1px solid rgba(66, 133, 244, 0.25)', borderRadius: '12px', lineHeight: '1.5' }}>
                    💡 <strong>Google + Password Dual Login:</strong> You originally signed up using Google OAuth. Setting a password below enables you to also log in directly using your email and this password on any device without Google Sign-In. You can still sign in using Google anytime!
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: 'var(--accent-cyan)', background: 'rgba(0, 245, 212, 0.08)', padding: '12px 14px', border: '1px solid rgba(0, 245, 212, 0.2)', borderRadius: '12px', lineHeight: '1.5' }}>
                    💡 <strong>Secure Password Reset:</strong> To protect your account, we will send a 6-digit verification code to your registered email address before updating your password.
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {/* Step 1: Send OTP via Turnstile */}
                  {!pwResetOtpSent ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div style={{ background: 'rgba(255, 255, 255, 0.03)', padding: '12px', borderRadius: '10px', border: '1px solid var(--border-glass)' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '2px' }}>Registered Email</span>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-main)' }}>
                          {profileEmail || localStorage.getItem('dhudd_email') || 'Your Email'}
                        </span>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
                        <Turnstile
                          key="pw_reset_turnstile"
                          siteKey="0x4AAAAAAEBFr3-Dsk6xeSLZ"
                          onSuccess={(token) => setPwResetTurnstileToken(token)}
                          onError={() => setPwResetTurnstileToken(null)}
                          onExpire={() => setPwResetTurnstileToken(null)}
                        />
                      </div>

                      <button
                        type="button"
                        onClick={handleSendPasswordResetOtp}
                        disabled={isSendingPwResetOtp || !pwResetTurnstileToken || pwResetOtpCooldown > 0}
                        className="btn-primary"
                        style={{
                          padding: '12px',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          opacity: (isSendingPwResetOtp || !pwResetTurnstileToken || pwResetOtpCooldown > 0) ? 0.6 : 1,
                          cursor: (isSendingPwResetOtp || !pwResetTurnstileToken || pwResetOtpCooldown > 0) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <Key size={16} />
                        {pwResetOtpCooldown > 0
                          ? `Resend Code in ${pwResetOtpCooldown}s`
                          : (isSendingPwResetOtp ? <ThinkingOrbLoader popup state="searching" size={20} color="var(--accent-cyan)" text="Sending Verification Code..." /> : '📨 Send Verification Code to Email')}
                      </button>
                    </div>
                  ) : (
                    /* Step 2: Enter OTP & New Password */
                    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      <div style={{ background: 'rgba(0, 245, 212, 0.08)', padding: '10px 12px', borderRadius: '10px', border: '1px solid rgba(0, 245, 212, 0.2)', fontSize: '12px', color: 'var(--accent-cyan)' }}>
                        ✓ Verification code sent to <strong>{profileEmail || localStorage.getItem('dhudd_email')}</strong>. Check your inbox.
                      </div>

                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <label style={{ fontSize: '12px', fontWeight: '600' }}>6-Digit Verification Code</label>
                          {pwResetOtpCooldown > 0 ? (
                            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Resend in {pwResetOtpCooldown}s</span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => {
                                setPwResetOtpSent(false);
                                setPwResetTurnstileToken(null);
                              }}
                              style={{ background: 'none', border: 'none', color: 'var(--accent-cyan)', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                            >
                              Request New Code
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          maxLength={6}
                          placeholder="••••••"
                          value={pwResetOtpCode}
                          onChange={e => setPwResetOtpCode(e.target.value.replace(/[^0-9]/g, ''))}
                          style={{ width: '100%', padding: '10px', letterSpacing: '6px', fontSize: '18px', textAlign: 'center', borderRadius: '10px', background: 'var(--bg-input)', color: 'var(--text-main)', border: '1px solid var(--border-glass)', fontWeight: 'bold' }}
                        />
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                          New Password
                        </label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <input
                            type={showNewPassword ? 'text' : 'password'}
                            placeholder="Enter new strong password"
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            style={{ width: '100%', padding: '10px 40px 10px 12px', borderRadius: '10px', background: 'var(--bg-input)', color: 'var(--text-main)', border: '1px solid var(--border-glass)', fontSize: '13px' }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            style={{ position: 'absolute', right: '10px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }}
                          >
                            {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>

                        {/* Real-time Password Complexity Requirements Checklist */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '6px', marginTop: '8px', padding: '10px 12px', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid var(--border-glass)', borderRadius: '8px', fontSize: '11px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: newPassword.length >= 8 ? '#00f5d4' : 'var(--text-muted)' }}>
                            <span>{newPassword.length >= 8 ? '✓' : '○'}</span>
                            <span>8+ Characters</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: /[0-9]/.test(newPassword) ? '#00f5d4' : 'var(--text-muted)' }}>
                            <span>{/[0-9]/.test(newPassword) ? '✓' : '○'}</span>
                            <span>1+ Number (0-9)</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: /[A-Z]/.test(newPassword) ? '#00f5d4' : 'var(--text-muted)' }}>
                            <span>{/[A-Z]/.test(newPassword) ? '✓' : '○'}</span>
                            <span>1+ Uppercase (A-Z)</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: /[a-z]/.test(newPassword) ? '#00f5d4' : 'var(--text-muted)' }}>
                            <span>{/[a-z]/.test(newPassword) ? '✓' : '○'}</span>
                            <span>1+ Lowercase (a-z)</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: /[^A-Za-z0-9]/.test(newPassword) ? '#00f5d4' : 'var(--text-muted)' }}>
                            <span>{/[^A-Za-z0-9]/.test(newPassword) ? '✓' : '○'}</span>
                            <span>1+ Symbol (!@#$)</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <label style={{ fontSize: '12px', fontWeight: '600', display: 'block', marginBottom: '6px' }}>
                          Confirm New Password
                        </label>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <input
                            type={showConfirmPassword ? 'text' : 'password'}
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            style={{ width: '100%', padding: '10px 40px 10px 12px', borderRadius: '10px', background: 'var(--bg-input)', color: 'var(--text-main)', border: '1px solid var(--border-glass)', fontSize: '13px' }}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            style={{ position: 'absolute', right: '10px', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }}
                          >
                            {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                        </div>
                        {confirmPassword.length > 0 && (
                          <div style={{ fontSize: '11px', marginTop: '4px', color: newPassword === confirmPassword ? '#00f5d4' : 'var(--color-outflow)' }}>
                            {newPassword === confirmPassword ? '✓ Passwords match' : '✕ Passwords do not match'}
                          </div>
                        )}
                      </div>

                      <button
                        type="button"
                        onClick={handleSavePassword}
                        disabled={isSubmittingPassword || !pwResetOtpCode || pwResetOtpCode.length !== 6 || !newPassword || newPassword.length < 8 || newPassword !== confirmPassword}
                        className="btn-primary"
                        style={{
                          padding: '12px',
                          fontSize: '13px',
                          marginTop: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          opacity: (isSubmittingPassword || !pwResetOtpCode || pwResetOtpCode.length !== 6 || !newPassword || newPassword.length < 8 || newPassword !== confirmPassword) ? 0.6 : 1,
                          cursor: (isSubmittingPassword || !pwResetOtpCode || pwResetOtpCode.length !== 6 || !newPassword || newPassword.length < 8 || newPassword !== confirmPassword) ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <Key size={16} />
                        {isSubmittingPassword ? <ThinkingOrbLoader popup state="working" size={20} color="var(--accent-cyan)" text="Updating Password..." /> : (userAuthProvider === 'GOOGLE' ? 'Set Account Password' : 'Save New Password')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Backup & Data Export */}
          <div className="glass-panel" style={{ padding: '16px' }}>
            <div 
              onClick={() => setExpandedSettingSection(expandedSettingSection === 'backups' ? null : 'backups')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
            >
              <h3 style={{ margin: 0 }}>📦 ZIP Data Backup & Restore</h3>
              <ChevronDown size={18} style={{ color: 'var(--accent-cyan)', transform: expandedSettingSection === 'backups' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>

            {expandedSettingSection === 'backups' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
                <div>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 10px 0' }}>
                    Backup your entire financial history including transactions, categories, and friends list into a compressed ZIP archive.
                  </p>
                  <button onClick={handleExportZip} className="btn-primary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                    <Download size={18} /> Backup Database to ZIP
                  </button>
                </div>

                <div style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '12px' }}>
                  <h4 style={{ margin: '0 0 6px 0', fontSize: '13px' }}>Restore Database ZIP</h4>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '0 0 10px 0' }}>
                    Restore from a previously exported ZIP backup. This will overwrite all your current data.
                  </p>
                  <label className="btn-secondary" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                    <Upload size={18} /> Choose ZIP File
                    <input type="file" accept=".zip" onChange={handleRestoreBackup} style={{ display: 'none' }} />
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Numeric configuration formatting */}
          <div className="glass-panel" style={{ padding: '16px' }}>
            <h3 style={{ marginBottom: '12px' }}>Global Delimiters</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <select
                value={settings.decimalSeparator}
                onChange={e => {
                  const s = { ...settings, decimalSeparator: e.target.value };
                  setSettings(s);
                  localStorage.setItem('dhudd_settings', JSON.stringify(s));
                }}
              >
                <option value=".">Decimal ( . )</option>
                <option value=",">Decimal ( , )</option>
              </select>
              <select
                value={settings.precision}
                onChange={e => {
                  const s = { ...settings, precision: parseInt(e.target.value) };
                  setSettings(s);
                  localStorage.setItem('dhudd_settings', JSON.stringify(s));
                }}
              >
                <option value="2">2 Decimals</option>
                <option value="3">3 Decimals</option>
                <option value="0">0 Decimals</option>
              </select>
            </div>
          </div>

          {/* Privacy, Analytics & Diagnostics */}
          <div className="glass-panel" style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div 
              onClick={() => setExpandedSettingSection(expandedSettingSection === 'privacy' ? null : 'privacy')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
            >
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Activity size={18} style={{ color: 'var(--accent-cyan)' }} /> App Improvement & Diagnostics
              </h3>
              <ChevronDown size={18} style={{ color: 'var(--accent-cyan)', transform: expandedSettingSection === 'privacy' ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </div>

            {expandedSettingSection === 'privacy' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '8px', paddingTop: '10px', borderTop: '1px solid var(--border-glass)' }}>
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, lineHeight: '1.5' }}>
                  Help us improve Tezer by sharing anonymous diagnostic telemetry and session replays. This helps detect UI bugs, performance slowdowns, and crashes. All financial data remains strictly protected.
                </p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.03)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-main)' }}>
                      Optional Data Collection & Session Replays
                    </div>
                    <div style={{ fontSize: '11px', color: isTelemetryOptedIn() ? 'var(--color-inflow)' : 'var(--text-muted)' }}>
                      {isTelemetryOptedIn() ? '✓ Enabled (Helping improve Tezer)' : 'Disabled (No session replays recorded)'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const next = !isTelemetryOptedIn();
                      setTelemetryOptIn(next);
                      showToast(next ? 'Diagnostic replays enabled. Thank you!' : 'Session recording disabled.', 'info');
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      border: 'none',
                      background: isTelemetryOptedIn() ? 'var(--accent-cyan)' : 'rgba(255,255,255,0.1)',
                      color: isTelemetryOptedIn() ? '#000' : 'var(--text-main)',
                      transition: 'all 0.2s'
                    }}
                  >
                    {isTelemetryOptedIn() ? 'Turn Off' : 'Turn On'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <button onClick={handleClearData} className="btn-secondary" style={{ color: 'var(--color-outflow)', borderColor: 'rgba(255,71,87,0.3)', marginTop: '20px' }}>
            Reset App (Wipe database)
          </button>

          <button onClick={handleDeleteAccount} className="btn-secondary" style={{ color: 'var(--color-outflow)', borderColor: 'rgba(255,71,87,0.3)', marginTop: '12px', width: '100%' }}>
            Delete My Account
          </button>
        </div>
      )}

      {/* --- ADD TRANSACTION MODAL --- */}
      {activeModal && (activeModal === 'outflow' || activeModal === 'inflow') && (
        <div className="modal-backdrop modal-backdrop-blur">
          <form
            onSubmit={handleCreateTransaction}
            className={`modal-spring-enter modal-content-panel ${activeModal === 'outflow' ? 'highlight-outflow' : 'highlight-inflow'}`}
            style={{ display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box', background: '#121824', border: '1px solid rgba(255,255,255,0.15)', boxShadow: '0 25px 60px rgba(0,0,0,0.95)' }}
          >
            <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Log {activeModal === 'outflow' ? 'Expense' : 'Income'}</h2>
              <button type="button" onClick={() => { setActiveModal(null); setShowInlineCatForm(false); setInlineCatName(''); setInlineCatSymbol(''); }} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
            </div>

            <div>
              <label>Amount</label>
              <input
                type="number"
                step="0.0001"
                placeholder="0.00"
                value={txAmount}
                onChange={e => setTxAmount(e.target.value)}
                style={{ fontSize: '26px', textAlign: 'center', fontWeight: 'bold', borderColor: activeModal === 'outflow' ? 'var(--color-outflow)' : 'var(--color-inflow)' }}
                required
                autoFocus
              />
            </div>

            <div>
              <label>Description</label>
              <div style={{ position: 'relative' }}>
                <input 
                  type="text" 
                  placeholder="e.g. Starbucks, Taxi ride, Salary payment" 
                  value={txDesc} 
                  onChange={handleTxDescChange} 
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') setDescSuggestions([]);
                  }}
                  onBlur={() => {
                    setTimeout(() => setDescSuggestions([]), 250);
                  }}
                  required 
                  autoComplete="off"
                />
                {descSuggestions.length > 0 && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '44px',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      background: 'rgba(20, 24, 40, 0.97)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid var(--border-glass)',
                      borderRadius: '12px',
                      boxShadow: 'var(--shadow-premium)',
                      padding: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      maxHeight: '190px',
                      overflowY: 'auto'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '4px 8px 6px 8px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                      fontSize: '11px',
                      color: 'var(--text-muted)'
                    }}>
                      <span>💡 Suggestions</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDescSuggestions([]);
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: 'none',
                          color: 'var(--text-main)',
                          borderRadius: '6px',
                          padding: '2px 8px',
                          fontSize: '11px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <X size={12} /> Close
                      </button>
                    </div>
                    {descSuggestions.map((sug, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setTxDesc(sug);
                          setDescSuggestions([]);
                          const predictedCat = getAutoCategory(sug, activeModal === 'outflow' ? 'OUTFLOW' : 'INFLOW');
                          if (predictedCat) {
                            setTxCategory(predictedCat);
                          }
                        }}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          fontSize: '13px',
                          color: 'var(--text-main)',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        {sug}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              {activeModal === 'outflow' && (
                <div style={{ flex: 1 }}>
                  <label>Who Paid?</label>
                  <select 
                    value={txPayerType} 
                    onChange={e => {
                      const val = e.target.value;
                      setTxPayerType(val);
                      if (val === 'friend' || val === 'both') {
                        setTxIsShared(true);
                      }
                    }}
                  >
                    <option value="you">You</option>
                    <option value="friend">Friend</option>
                    <option value="both">You and friend</option>
                  </select>
                </div>
              )}

              {(activeModal === 'inflow' || txPayerType === 'you' || txPayerType === 'both') && (
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ margin: 0 }}>{activeModal === 'inflow' ? 'Funding Account' : 'Paid Account'}</label>
                    {!txUseSplitSources && (() => {
                      const selAcc = accounts.find(a => a.account_id === txAccountId);
                      if (!selAcc) return null;
                      
                      let deduction = 0;
                      if (activeModal === 'outflow') {
                        if (txPayerType === 'you') deduction = parseFloat(txAmount) || 0;
                        else if (txPayerType === 'both') deduction = parseFloat(txMultiPayers['self']) || 0;
                      } else {
                        deduction = -(parseFloat(txAmount) || 0);
                      }
                      
                      const displayBal = selAcc.type === 'CREDIT_CARD' ? -parseFloat(selAcc.current_balance) : parseFloat(selAcc.current_balance);
                      const newDisplayBal = displayBal - deduction;
                      
                      return (
                        <span style={{ 
                          fontSize: '11px', 
                          background: selAcc.type === 'CREDIT_CARD' ? 'rgba(123,44,191,0.2)' : 'rgba(46,213,115,0.15)',
                          border: '1px solid var(--border-glass)', 
                          padding: '2px 8px', 
                          borderRadius: '8px', 
                          color: newDisplayBal >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)',
                          fontWeight: 'bold'
                        }}>
                          New balance: {formatMoney(newDisplayBal, selAcc.currency)}
                        </span>
                      );
                    })()}
                  </div>
                  {!txUseSplitSources ? (
                    <CustomAccountSelect
                      value={txAccountId}
                      onChange={e => {
                        const val = e.target.value;
                        setTxAccountId(val);
                        const acc = accounts.find(a => a.account_id === val);
                        if (acc) setTxCurrency(acc.currency);
                      }}
                      accounts={accounts}
                      filter={a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts'))}
                      placeholder="Select Account / Card"
                      formatMoney={formatMoney}
                      required
                    />
                  ) : (
                    <div className="read-only-badge" style={{
                      padding: '12px', background: 'rgba(255,255,255,0.05)',
                      border: '1px solid var(--border-glass)', borderRadius: '12px',
                      fontSize: '12px', color: 'var(--text-muted)',
                      height: '48px', display: 'flex', alignItems: 'center'
                    }}>
                      Multiple sources selected below
                    </div>
                  )}
                </div>
              )}
            </div>

            {activeModal === 'outflow' && (txPayerType === 'friend' || txPayerType === 'both') && (
              <div style={{ marginTop: '4px' }}>
                <label style={{ fontSize: '12px' }}>Select Friend(s)</label>
                <select 
                  value=""
                  onChange={e => {
                    const val = e.target.value;
                    if (val) {
                      if (!txMultiPayers[val]) {
                        setTxMultiPayers(prev => {
                          const numCurrentPayers = Object.keys(prev).filter(k => k !== 'self').length;
                          let initialAmount = '';
                          if (txPayerType === 'friend' && numCurrentPayers === 0) {
                            initialAmount = txAmount || '';
                          }
                          return { ...prev, [val]: initialAmount };
                        });
                      }
                    }
                  }}
                >
                  <option value="">-- Add Friend to Payers --</option>
                  {sortFriendsAZ(friends).map(f => (
                    <option key={f.user_id} value={f.user_id} disabled={txMultiPayers[f.user_id] !== undefined}>
                      {formatFriendLabel(f)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {activeModal === 'outflow' && (txPayerType === 'friend' || txPayerType === 'both') && (
              <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {txPayerType === 'both' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ flex: 1, fontSize: '13px' }}>You Paid:</span>
                    <input 
                      type="number" 
                      value={txMultiPayers['self'] !== undefined ? txMultiPayers['self'] : ''} 
                      onChange={e => setTxMultiPayers(prev => ({ ...prev, 'self': e.target.value }))}
                      placeholder="0.00"
                      style={{ width: '100px', padding: '6px' }}
                    />
                  </div>
                )}
                {Object.keys(txMultiPayers).map(uid => {
                  if (uid === 'self') return null;
                  const f = friends.find(x => x.user_id === uid);
                  if (!f) return null;
                  const fName = f.nickname || f.display_name || f.email.split('@')[0];
                  return (
                    <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ flex: 1, fontSize: '13px' }}>{fName} Paid:</span>
                      <input 
                        type="number" 
                        value={txMultiPayers[uid]} 
                        onChange={e => setTxMultiPayers(prev => ({ ...prev, [uid]: e.target.value }))}
                        placeholder="0.00"
                        style={{ width: '100px', padding: '6px' }}
                      />
                      <button 
                        type="button"
                        onClick={() => {
                          setTxMultiPayers(prev => {
                            const nw = { ...prev };
                            delete nw[uid];
                            return nw;
                          });
                        }}
                        style={{ background: 'none', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer' }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
              </div>
            )}



            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label>Category</label>
                <select value={txCategory} onChange={e => setTxCategory(e.target.value)}>
                  {txCategory && !(activeModal === 'outflow' ? outflowCats : inflowCats).some(c => c.name === txCategory) && (
                    <option value={txCategory}>{txCategory}</option>
                  )}
                  {activeModal === 'outflow' ? (
                    outflowCats.map(c => <option key={c.category_id} value={c.name}>{c.symbol} {c.name}</option>)
                  ) : (
                    inflowCats.map(c => <option key={c.category_id} value={c.name}>{c.symbol} {c.name}</option>)
                  )}
                </select>
                {!showInlineCatForm ? (
                  <button
                    type="button"
                    onClick={() => setShowInlineCatForm(true)}
                    style={{ marginTop: '4px', background: 'none', border: 'none', color: 'var(--accent-cyan)', fontSize: '11px', cursor: 'pointer', padding: '0', textAlign: 'left' }}
                  >
                    ＋ New category
                  </button>
                ) : (
                  <div className="animate-fade-in" style={{ marginTop: '6px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', padding: '8px', background: 'rgba(0,245,212,0.04)', borderRadius: '10px', border: '1px solid rgba(0,245,212,0.2)' }}>
                    <input
                      type="text"
                      placeholder="Name"
                      value={inlineCatName}
                      onChange={e => setInlineCatName(e.target.value)}
                      style={{ flex: 1, minWidth: '80px', padding: '5px 8px', fontSize: '12px' }}
                    />
                    <input
                      type="text"
                      maxLength={2}
                      placeholder="😀"
                      value={inlineCatSymbol}
                      onChange={e => setInlineCatSymbol(e.target.value)}
                      style={{ width: '44px', padding: '5px 8px', fontSize: '14px', textAlign: 'center' }}
                    />
                    <button type="button" onClick={handleCreateInlineCategory} style={{ padding: '5px 10px', background: 'var(--accent-cyan)', border: 'none', borderRadius: '6px', color: '#000', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>Save</button>
                    <button type="button" onClick={() => { setShowInlineCatForm(false); setInlineCatName(''); setInlineCatSymbol(''); }} style={{ padding: '5px 8px', background: 'transparent', border: 'none', color: 'var(--text-muted)', fontSize: '11px', cursor: 'pointer' }}>✕</button>
                  </div>
                )}
              </div>

              <div style={{ flex: 1 }}>
                <label>Date</label>
                <input type="date" value={txDate} onChange={e => setTxDate(e.target.value)} required />
              </div>
            </div>

            {/* More Options Section */}
            <div style={{ marginTop: '10px' }}>
              <button
                type="button"
                onClick={() => setShowMoreTxOptions(!showMoreTxOptions)}
                style={{ background: 'none', border: 'none', color: 'var(--accent-cyan)', fontSize: '13px', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {showMoreTxOptions ? '▼ Less options' : '▶ More options'}
              </button>
              
              {showMoreTxOptions && (
                <div className="animate-fade-in" style={{ marginTop: '10px', padding: '15px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-glass)', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  
                  {/* Split Sources */}
                  {((activeModal === 'outflow' && txPayerType === 'you') || activeModal === 'inflow') && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="txUseSplitSources"
                          checked={txUseSplitSources}
                          onChange={e => {
                            setTxUseSplitSources(e.target.checked);
                            if (e.target.checked) {
                              setSplitTxSources([{ accountId: txAccountId || accounts[0]?.account_id || '', amount: '' }]);
                            }
                          }}
                          style={{ width: 'auto', cursor: 'pointer' }}
                        />
                        <label htmlFor="txUseSplitSources" style={{ margin: 0, cursor: 'pointer', fontSize: '13px', color: 'var(--text-main)' }}>
                          {activeModal === 'inflow' ? 'Split income across multiple funding accounts' : 'Split payment across multiple source accounts'}
                        </label>
                      </div>
                      
                      {txUseSplitSources && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border-glass)', borderRadius: '12px', marginTop: '8px' }}>
                          <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', fontWeight: 'bold' }}>{activeModal === 'inflow' ? 'Funding Accounts' : 'Source Accounts'}</span>
                            <button
                              type="button"
                              onClick={() => {
                                const validAcc = accounts.find(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts')));
                                setSplitTxSources([...splitTxSources, { accountId: validAcc?.account_id || '', amount: '' }]);
                              }}
                              style={{ fontSize: '9px', padding: '2px 8px', background: 'rgba(0,245,212,0.15)', border: 'none', borderRadius: '4px', color: 'var(--accent-cyan)', cursor: 'pointer' }}
                            >
                              ➕ Add Account
                            </button>
                          </div>
                          {splitTxSources.map((src, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <CustomAccountSelect
                                value={src.accountId}
                                onChange={e => {
                                  const list = [...splitTxSources];
                                  list[idx].accountId = e.target.value;
                                  setSplitTxSources(list);
                                }}
                                accounts={accounts}
                                filter={a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts'))}
                                placeholder="Select Account"
                                formatMoney={formatMoney}
                                style={{ flex: 2, minHeight: '38px', padding: '6px 10px', fontSize: '12px' }}
                                required
                              />
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Amount"
                                value={src.amount}
                                onChange={e => {
                                  const list = [...splitTxSources];
                                  list[idx].amount = e.target.value;
                                  setSplitTxSources(list);
                                }}
                                style={{ flex: 1, padding: '4px 6px', fontSize: '11px', minWidth: '60px' }}
                                required
                              />
                              {splitTxSources.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const list = splitTxSources.filter((_, i) => i !== idx);
                                    setSplitTxSources(list);
                                  }}
                                  style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.7)', cursor: 'pointer', fontSize: '12px' }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                          {(() => {
                            const targetVal = parseFloat(txAmount) || 0;
                            const allocatedVal = splitTxSources.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                            const remainingVal = targetVal - allocatedVal;
                            const remainingColor = remainingVal < 0 ? 'var(--color-outflow)' : remainingVal === 0 ? 'var(--color-inflow)' : 'var(--text-muted)';
                            return (
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', background: 'rgba(0,0,0,0.1)', padding: '6px 10px', borderRadius: '6px', marginTop: '4px', width: '100%' }}>
                                <span>Total: <strong style={{ color: 'var(--text-main)' }}>{formatMoney(targetVal, txCurrency)}</strong></span>
                                <span>Allocated: <strong style={{ color: 'var(--text-main)' }}>{formatMoney(allocatedVal, txCurrency)}</strong></span>
                                <span>Remaining: <strong style={{ color: remainingColor }}>{formatMoney(remainingVal, txCurrency)}</strong></span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Recurring scheduling */}
                  <div style={{ borderTop: txUseSplitSources ? '1px solid var(--border-glass)' : 'none', paddingTop: txUseSplitSources ? '10px' : '0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px' }}>Auto-repeat transaction?</span>
                      <input type="checkbox" checked={txIsRecurring} onChange={e => setTxIsRecurring(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                    </div>
                    {txIsRecurring && (
                      <div className="animate-fade-in" style={{ marginTop: '8px' }}>
                        <label>Recurring Period</label>
                        <select value={txRecurringPeriod} onChange={e => setTxRecurringPeriod(e.target.value)}>
                          <option value="WEEK">Every Week</option>
                          <option value="TWO_WEEKS">Every Two Weeks</option>
                          <option value="FOUR_WEEKS">Every Four Weeks</option>
                          <option value="MONTH">Every Month</option>
                          <option value="THREE_MONTHS">Every Three Months</option>
                          <option value="SIX_MONTHS">Every Six Months</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Transaction Charges */}
                  {activeModal === 'outflow' && (
                    <div style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '10px' }}>
                      <label style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>Add transaction charges</label>
                      <input 
                        type="number" 
                        step="0.01"
                        placeholder="Amount" 
                        value={txCharges} 
                        onChange={e => setTxCharges(e.target.value)} 
                        style={{ width: '100%', padding: '8px', fontSize: '12px' }} 
                      />
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginTop: '4px' }}>This amount will be added as a separate expense linked to this transaction.</span>
                    </div>
                  )}

                </div>
              )}
            </div>

            {/* Splits */}
            {activeModal === 'outflow' && (
              <div style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontSize: '13px' }}>Split this bill?</span>
                  <input type="checkbox" checked={txIsShared} onChange={e => setTxIsShared(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                </div>
                {txIsShared && (
                  <div className="animate-fade-in" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Split Mode Selector */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                      <button 
                        type="button" 
                        onClick={() => setSplitTargetType('GROUP')} 
                        className={`btn-secondary ${splitTargetType === 'GROUP' ? 'active' : ''}`}
                        style={{ flex: 1, padding: '8px', fontSize: '11px', borderRadius: '8px', background: splitTargetType === 'GROUP' ? 'var(--accent-cyan)' : 'transparent', color: splitTargetType === 'GROUP' ? 'black' : 'var(--text-main)', border: '1px solid var(--border-glass)' }}
                      >
                        👥 Group Split
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setSplitTargetType('FRIENDS')} 
                        className={`btn-secondary ${splitTargetType === 'FRIENDS' ? 'active' : ''}`}
                        style={{ flex: 1, padding: '8px', fontSize: '11px', borderRadius: '8px', background: splitTargetType === 'FRIENDS' ? 'var(--accent-cyan)' : 'transparent', color: splitTargetType === 'FRIENDS' ? 'black' : 'var(--text-main)', border: '1px solid var(--border-glass)' }}
                      >
                        🤝 Friends Split
                      </button>
                    </div>

                    {/* Group Selector */}
                    {splitTargetType === 'GROUP' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <select 
                          value={txGroupId} 
                          onChange={e => {
                            const val = e.target.value;
                            setTxGroupId(val);
                            const g = groups.find(grp => grp.group_id === val);
                            if (g && g.members) {
                              setSplitGroupMembersList(g.members.map(m => m.user_id));
                            } else {
                              setSplitGroupMembersList([]);
                            }
                          }} 
                          required
                        >
                          <option value="">Select Group</option>
                          {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.group_name}</option>)}
                        </select>

                        {txGroupId && (() => {
                          const g = groups.find(grp => grp.group_id === txGroupId);
                          if (!g || !g.members) return null;
                          const memberOptions = g.members.map(m => {
                            const isSelf = m.user_id === localStorage.getItem('dhudd_user_id');
                            return {
                              id: m.user_id,
                              name: `${m.display_name || m.email.split('@')[0]}${isSelf ? ' (You)' : ''}`
                            };
                          });
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Select group members to split with:</span>
                              <MultiSelectDropdown
                                options={memberOptions}
                                selectedIds={splitGroupMembersList}
                                onChange={setSplitGroupMembersList}
                                placeholder="Select group members..."
                                renderExtra={(id) => id !== localStorage.getItem('dhudd_user_id') ? renderFriendBalanceTiles(id) : null}
                              />
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      /* Ad-hoc Friends Selector */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Select friends to split with:</span>
                        {friends.length === 0 ? (
                          <p style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', padding: '10px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>No friends added yet. Add them in the Friends tab.</p>
                        ) : (
                          <MultiSelectDropdown
                            options={sortFriendsAZ(friends).map(f => ({
                              id: f.user_id,
                              name: formatFriendLabel(f)
                            }))}
                            selectedIds={splitFriendsList}
                            onChange={setSplitFriendsList}
                            placeholder="Select friends to split with..."
                            renderExtra={renderFriendBalanceTiles}
                          />
                        )}
                      </div>
                    )}

                    {/* Split Type Selector and Breakdown (only show if valid target chosen) */}
                    {((splitTargetType === 'GROUP' && txGroupId) || (splitTargetType === 'FRIENDS' && splitFriendsList.length > 0)) && (
                      <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <label style={{ fontSize: '12px', margin: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                            Split Method:
                            {renderInfoTooltip("Choose how the expense is divided:\n• Split Equally: Divides amount evenly.\n• Custom Exact amounts: Assign fixed amounts to each.\n• Percentage Weighting: Split based on % weights (total 100%).\n• Ratio shares: Split using ratio numbers (e.g. 2 shares to 1).")}
                          </label>
                          <select value={splitType} onChange={e => setSplitType(e.target.value)} style={{ padding: '6px', fontSize: '12px' }}>
                            <option value="EQUALLY">Split Equally</option>
                            <option value="EXACT">Custom Exact amounts</option>
                            <option value="PERCENT">Percentage Weighting</option>
                            <option value="SHARES">Ratio shares (e.g. 2:1)</option>
                          </select>
                        </div>

                        {/* Real-time calculated split inputs list */}
                        <div className="glass-panel" style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(0,0,0,0.15)' }}>
                          <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--accent-cyan)' }}>Split Calculations (Real-time)</span>
                          {getSplitMembers().map(m => {
                            const calcSplit = getRealTimeCalculatedSplits().find(s => s.user_id === m.user_id);
                            const owedVal = calcSplit ? calcSplit.owed_amount : 0.0;
                            const isSelf = m.user_id === (localStorage.getItem('dhudd_user_id') || 'active_user');
                            
                            return (
                              <div key={m.user_id} style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '240px' }}>
                                  {m.display_name || m.email.split('@')[0]} {isSelf ? '(You)' : ''}
                                  {!isSelf && renderFriendBalanceTiles(m.user_id)}
                                </span>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {splitType === 'EXACT' && (
                                    <input 
                                      type="number" 
                                      step="0.01" 
                                      placeholder="0.00" 
                                      value={exactAmounts[m.user_id] !== undefined ? exactAmounts[m.user_id] : ''} 
                                      onChange={e => setExactAmounts({ ...exactAmounts, [m.user_id]: parseFloat(e.target.value) || 0.0 })}
                                      style={{ width: '70px', padding: '4px', textAlign: 'right', fontSize: '11px', borderRadius: '4px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', color: 'white' }}
                                      required
                                    />
                                  )}
                                  {splitType === 'PERCENT' && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      <input 
                                        type="number" 
                                        step="1" 
                                        placeholder="0" 
                                        value={percentages[m.user_id] !== undefined ? percentages[m.user_id] : ''} 
                                        onChange={e => setPercentages({ ...percentages, [m.user_id]: parseFloat(e.target.value) || 0.0 })}
                                        style={{ width: '50px', padding: '4px', textAlign: 'right', fontSize: '11px', borderRadius: '4px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', color: 'white' }}
                                        required
                                      />
                                      <span>%</span>
                                    </div>
                                  )}
                                  {splitType === 'SHARES' && (
                                    <input 
                                      type="number" 
                                      step="1" 
                                      min="0"
                                      placeholder="1" 
                                      value={shares[m.user_id] !== undefined ? shares[m.user_id] : ''} 
                                      onChange={e => setShares({ ...shares, [m.user_id]: parseFloat(e.target.value) || 0.0 })}
                                      style={{ width: '50px', padding: '4px', textAlign: 'right', fontSize: '11px', borderRadius: '4px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', color: 'white' }}
                                      required
                                    />
                                  )}
                                  <span style={{ fontWeight: '700', minWidth: '60px', textAlign: 'right' }}>
                                    {formatMoney(owedVal, txCurrency)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}

                          {/* Live split validation message */}
                          {(() => {
                            const amt = parseFloat(txAmount) || 0;
                            if (splitType === 'EXACT') {
                              const sum = getRealTimeCalculatedSplits().reduce((acc, m) => acc + m.owed_amount, 0);
                              if (Math.abs(sum - amt) > 0.01) {
                                return (
                                  <div style={{ color: 'var(--color-outflow)', fontSize: '11px', textAlign: 'right', marginTop: '4px', fontWeight: 'bold' }}>
                                    ⚠️ Sum ({sum.toFixed(2)}) must equal Total ({amt.toFixed(2)})
                                  </div>
                                );
                              }
                            }
                            if (splitType === 'PERCENT') {
                              const sumPct = getRealTimeCalculatedSplits().reduce((acc, m) => acc + (m.percent || 0), 0);
                              if (Math.abs(sumPct - 100) > 0.01) {
                                  return (
                                    <div style={{ color: 'var(--color-outflow)', fontSize: '11px', textAlign: 'right', marginTop: '4px', fontWeight: 'bold' }}>
                                      ⚠️ Sum ({sumPct}%) must equal 100%
                                    </div>
                                  );
                              }
                            }
                            if (splitType === 'SHARES') {
                              const totalShares = getSplitMembers().reduce((acc, m) => acc + (shares[m.user_id] !== undefined ? shares[m.user_id] : 1), 0);
                              if (totalShares <= 0) {
                                return (
                                  <div style={{ color: 'var(--color-outflow)', fontSize: '11px', textAlign: 'right', marginTop: '4px', fontWeight: 'bold' }}>
                                    ⚠️ Total shares must be &gt; 0
                                  </div>
                                );
                              }
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <button type="submit" className="btn-primary" disabled={isSubmittingTx} style={{ background: activeModal === 'outflow' ? 'var(--color-outflow)' : 'var(--color-inflow)' }}>
              {isSubmittingTx ? (
                <ThinkingOrbLoader
                  popup
                  state="working"
                  size={20}
                  color={activeModal === 'outflow' ? '#ff4757' : '#2ed573'}
                  text={activeModal === 'outflow' ? 'Adding Expense...' : 'Adding Income...'}
                />
              ) : `Add ${activeModal === 'outflow' ? 'Expense' : 'Income'}`}
            </button>
          </form>
        </div>
      )}

      {/* --- ADD FRIEND MODAL --- */}
      {activeModal === 'friend_create' && (
        <div className="modal-backdrop modal-backdrop-blur" style={{ background: 'rgba(6, 8, 16, 0.88)' }}>
          <div className="modal-content-panel modal-spring-enter" style={{
            maxWidth: '520px',
            width: '100%',
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            position: 'relative',
            background: '#131522',
            border: '1px solid rgba(255, 255, 255, 0.15)',
            boxShadow: '0 25px 70px rgba(0, 0, 0, 0.9)',
            borderRadius: '18px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  background: 'linear-gradient(135deg, rgba(0, 245, 212, 0.2), rgba(84, 160, 255, 0.2))',
                  color: 'var(--accent-cyan)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid rgba(0, 245, 212, 0.3)'
                }}>
                  <UserPlus size={20} />
                </div>
                <div>
                  <h2 style={{ margin: 0, fontSize: '18px' }}>Add Friend</h2>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Connect with friends or create placeholder ledgers
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveModal(null)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '20px', cursor: 'pointer', padding: '4px' }}
              >
                ✕
              </button>
            </div>

            {/* Contact Sync Card */}
            <div style={{
              padding: '14px 16px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(0, 245, 212, 0.08), rgba(84, 160, 255, 0.08))',
              border: '1px solid rgba(0, 245, 212, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '8px',
                  background: 'rgba(0, 245, 212, 0.15)',
                  color: 'var(--accent-cyan)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Users size={18} />
                </div>
                <div>
                  <span style={{ fontWeight: '700', fontSize: '13px', display: 'block', color: 'var(--text-main)' }}>
                    📱 Sync Phone Contacts
                  </span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Auto-discover friends registered on Tezer
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  handleNativeContactSync();
                }}
                disabled={isScanningContacts}
                className="btn-primary"
                style={{
                  padding: '7px 13px',
                  fontSize: '11px',
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, var(--accent-cyan), #00b4d8)',
                  color: 'black',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {isScanningContacts ? <ThinkingOrbLoader popup state="searching" size={20} color="#000" text="Scanning..." /> : 'Scan Contacts'}
              </button>
            </div>

            {/* Divider */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.08)' }} />
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 'bold' }}>
                Or Add Manually
              </span>
              <div style={{ flex: 1, height: '1px', background: 'rgba(255, 255, 255, 0.08)' }} />
            </div>

            {/* Mode Switcher Tabs: Registered Friend vs Placeholder Friend */}
            <div style={{ display: 'flex', gap: '8px', background: 'rgba(255,255,255,0.03)', padding: '4px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
              <button
                type="button"
                onClick={() => setIsPlaceholderFriend(false)}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '700',
                  background: !isPlaceholderFriend ? 'var(--accent-cyan)' : 'transparent',
                  color: !isPlaceholderFriend ? 'black' : 'var(--text-muted)',
                  transition: 'all 0.2s ease'
                }}
              >
                👤 Registered User
              </button>
              <button
                type="button"
                onClick={() => setIsPlaceholderFriend(true)}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '700',
                  background: isPlaceholderFriend ? 'var(--accent-purple)' : 'transparent',
                  color: isPlaceholderFriend ? 'white' : 'var(--text-muted)',
                  transition: 'all 0.2s ease'
                }}
              >
                📝 Placeholder Friend
              </button>
            </div>

            {/* Friend Addition Form */}
            <form onSubmit={handleAddFriend} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {isPlaceholderFriend ? (
                <>
                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                      Friend Name / Nickname *
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. John Doe"
                      value={placeholderFriendName}
                      onChange={e => setPlaceholderFriendName(e.target.value)}
                      required
                      style={{ padding: '10px 12px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                      Description / Bio (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Roommate, Colleague"
                      value={placeholderFriendDesc}
                      onChange={e => setPlaceholderFriendDesc(e.target.value)}
                      style={{ padding: '10px 12px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                      Mobile Phone Number (Optional)
                    </label>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <select
                        value={placeholderCountryCode}
                        onChange={e => setPlaceholderCountryCode(e.target.value)}
                        style={{ padding: '10px', width: '110px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', borderRadius: '8px', color: 'var(--text-main)', fontSize: '12px' }}
                      >
                        {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                      </select>
                      {placeholderCountryCode === 'CUSTOM' && (
                        <input
                          type="text"
                          placeholder="+123"
                          value={placeholderCustomCc}
                          onChange={e => setPlaceholderCustomCc(e.target.value)}
                          style={{ padding: '10px', width: '60px', fontSize: '12px' }}
                        />
                      )}
                      <input
                        type="tel"
                        placeholder="50 123 4567"
                        value={placeholderPhoneDigits}
                        onChange={e => setPlaceholderPhoneDigits(e.target.value)}
                        style={{ padding: '10px 12px', fontSize: '13px', flex: 1, minWidth: '110px' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                    <input
                      type="checkbox"
                      id="modalPlaceholderAdjustBalance"
                      checked={placeholderAdjustBalance}
                      onChange={e => setPlaceholderAdjustBalance(e.target.checked)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                    <label htmlFor="modalPlaceholderAdjustBalance" style={{ fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', margin: 0 }}>
                      Adjust initial balance manually
                    </label>
                  </div>

                  {placeholderAdjustBalance && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: 'rgba(165, 94, 234, 0.08)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(165, 94, 234, 0.25)', width: '100%', boxSizing: 'border-box' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ width: '110px' }}>
                          <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Currency</label>
                          <select
                            value={placeholderInitialCurrency}
                            onChange={e => setPlaceholderInitialCurrency(e.target.value)}
                            style={{ padding: '10px', fontSize: '12px', width: '100%', borderRadius: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', color: 'var(--text-main)' }}
                          >
                            {allCurrenciesList.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Initial Starting Balance</label>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={placeholderInitialBalance}
                            onChange={e => setPlaceholderInitialBalance(e.target.value)}
                            style={{ padding: '10px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                      <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                        💡 Positive if they owe you (+), negative if you owe them (-).
                      </p>
                    </div>
                  )}

                  <button type="submit" className="btn-primary" style={{ padding: '12px', background: 'var(--accent-purple)', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', fontSize: '13px', marginTop: '6px', cursor: 'pointer' }}>
                    Add Placeholder Friend
                  </button>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '6px', marginBottom: '2px' }}>
                    <button
                      type="button"
                      onClick={() => setAddFriendMethod('EMAIL')}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        background: addFriendMethod === 'EMAIL' ? 'var(--accent-cyan)' : 'rgba(255, 255, 255, 0.05)',
                        color: addFriendMethod === 'EMAIL' ? 'black' : 'var(--text-muted)'
                      }}
                    >
                      📧 By Email
                    </button>
                    <button
                      type="button"
                      onClick={() => setAddFriendMethod('PHONE')}
                      style={{
                        flex: 1,
                        padding: '6px 10px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        borderRadius: '6px',
                        border: 'none',
                        cursor: 'pointer',
                        background: addFriendMethod === 'PHONE' ? 'var(--accent-cyan)' : 'rgba(255, 255, 255, 0.05)',
                        color: addFriendMethod === 'PHONE' ? 'black' : 'var(--text-muted)'
                      }}
                    >
                      📱 By Mobile Number
                    </button>
                  </div>

                  {addFriendMethod === 'EMAIL' ? (
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                        Friend's Registered Email Address *
                      </label>
                      <input
                        type="email"
                        placeholder="friend@email.com"
                        value={newFriendEmail}
                        onChange={e => setNewFriendEmail(e.target.value)}
                        required
                        style={{ padding: '12px', fontSize: '13px', width: '100%', boxSizing: 'border-box', marginBottom: '10px' }}
                      />
                    </div>
                  ) : (
                    <div>
                      <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: '4px' }}>
                        Friend's Registered Mobile Phone *
                      </label>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                        <select
                          value={newFriendCountryCode}
                          onChange={e => setNewFriendCountryCode(e.target.value)}
                          style={{ padding: '12px', width: '130px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', borderRadius: '8px', color: 'var(--text-main)', fontSize: '13px' }}
                        >
                          <option value="+971">+971 (UAE)</option>
                          <option value="+966">+966 (KSA)</option>
                          <option value="+965">+965 (Kuwait)</option>
                          <option value="+974">+974 (Qatar)</option>
                          <option value="+968">+968 (Oman)</option>
                          <option value="+973">+973 (Bahrain)</option>
                          <option value="+91">+91 (India)</option>
                          <option value="+1">+1 (US/CA)</option>
                          <option value="+44">+44 (UK)</option>
                          <option value="+92">+92 (Pakistan)</option>
                          <option value="+880">+880 (Bangladesh)</option>
                          <option value="+62">+62 (Indonesia)</option>
                          <option value="+63">+63 (Philippines)</option>
                          <option value="+20">+20 (Egypt)</option>
                          <option value="CUSTOM">Other Code</option>
                        </select>
                        {newFriendCountryCode === 'CUSTOM' && (
                          <input
                            type="text"
                            placeholder="+123"
                            value={customCountryCode}
                            onChange={e => setCustomCountryCode(e.target.value)}
                            style={{ padding: '12px', width: '70px' }}
                          />
                        )}
                        <input
                          type="tel"
                          placeholder="501234567"
                          value={newFriendPhone}
                          onChange={e => setNewFriendPhone(e.target.value.replace(/[^\d\s-]/g, ''))}
                          required
                          style={{ padding: '12px', flex: 1, minWidth: '130px' }}
                        />
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px', marginBottom: '6px' }}>
                    <input
                      type="checkbox"
                      id="modalFriendAdjustBalance"
                      checked={friendInitialAdjustBalance}
                      onChange={e => setFriendInitialAdjustBalance(e.target.checked)}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                    <label htmlFor="modalFriendAdjustBalance" style={{ fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', margin: 0 }}>
                      Adjust initial balance manually
                    </label>
                  </div>

                  {friendInitialAdjustBalance && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: 'rgba(0, 245, 212, 0.05)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(0, 245, 212, 0.2)', width: '100%', boxSizing: 'border-box', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ width: '110px' }}>
                          <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Currency</label>
                          <select
                            value={friendInitialCurrency}
                            onChange={e => setFriendInitialCurrency(e.target.value)}
                            style={{ padding: '10px', fontSize: '12px', width: '100%', borderRadius: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', color: 'var(--text-main)' }}
                          >
                            {allCurrenciesList.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                        <div style={{ flex: 1 }}>
                          <label style={{ fontSize: '11px', fontWeight: 'bold' }}>Initial Starting Balance</label>
                          <input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={friendInitialBalance}
                            onChange={e => setFriendInitialBalance(e.target.value)}
                            style={{ padding: '10px', fontSize: '13px', width: '100%', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                      <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                        💡 Positive if they owe you (+), negative if you owe them (-).
                      </p>
                    </div>
                  )}

                  <button type="submit" className="btn-primary" style={{ padding: '12px', width: '100%', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', marginTop: '4px' }}>
                    {addFriendMethod === 'EMAIL' ? 'Add Friend by Email' : 'Add Friend by Mobile'}
                  </button>
                </>
              )}
            </form>
          </div>
        </div>
      )}

      {/* --- ADD ACCOUNT MODAL --- */}
      {activeModal === 'account_create' && (
        <div className="modal-backdrop modal-backdrop-blur">
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const name = e.target.name.value;
              const type = e.target.type.value;
              const currency = e.target.currency.value;
              let current_balance = parseFloat(e.target.balance.value || 0);
              if (type === 'CREDIT_CARD') {
                current_balance = -current_balance;
              }
              const include_in_total = e.target.include.checked;

              const credit_limit = parseFloat(e.target.limit?.value || 0);
              const statement_day = parseInt(e.target.statement?.value || 1);
              const due_day = parseInt(e.target.due?.value || 1);
              const monthly_spend_target = parseFloat(e.target.spendTarget?.value || 0) || null;
              const monthly_spend_target_type = e.target.spendTargetType?.value || 'CALENDAR';

              try {
                await apiClient.request('/accounts', 'POST', {
                  name,
                  nickname: name,
                  master_card_id: type === 'CREDIT_CARD' ? createCardMasterCardId : null,
                  type,
                  currency,
                  current_balance,
                  include_in_total,
                  credit_limit,
                  statement_day,
                  due_day,
                  monthly_spend_target,
                  monthly_spend_target_type
                });
                showToast('Account created!', 'success');
                setActiveModal(null);
                setCreateCardMasterCardId(null);
                refreshData();
              } catch (err) {
                showToast(err.message, 'error');
              }
            }}
            className="glass-panel modal-spring-enter modal-content-panel"
            style={{ display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box' }}
          >
            <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Create Account Profile</h2>
              <button type="button" onClick={() => { setActiveModal(null); setCreateCardMasterCardId(null); }} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer' }}>Cancel</button>
            </div>

            <div>
              <label>Account / Card Nickname</label>
              <input name="name" type="text" placeholder="e.g. My ADCB TouchPoints, Salary Account, Cash" required />
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{ flex: 1 }}>
                <label>Account Type</label>
                <select 
                  id="account-type-select"
                  name="type" 
                  defaultValue={createCardMasterCardId ? 'CREDIT_CARD' : 'BANK'}
                  onChange={(e) => {
                    const ccDiv = document.getElementById('credit-card-inputs-create');
                    if (ccDiv) ccDiv.style.display = e.target.value === 'CREDIT_CARD' ? 'block' : 'none';
                  }} 
                  required
                >
                  <option value="BANK">Bank Account</option>
                  <option value="CASH">Cash Wallet</option>
                  <option value="CREDIT_CARD">Credit Card</option>
                </select>
              </div>

              <div style={{ flex: 1 }}>
                <label>Currency</label>
                <select name="currency" required>
                  <option value="AED">AED</option>
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>

            <div>
              <label style={{ display: 'flex', alignItems: 'center' }}>
                Starting Balance
                {renderInfoTooltip("For bank/cash accounts: positive is money you have, negative is debt. For credit cards: positive is overpaid/credit, negative is money you owe.")}
              </label>
              <input name="balance" type="number" step="0.01" defaultValue="0.00" />
            </div>

            {/* Credit Card Details */}
            <div id="credit-card-inputs-create" style={{ display: createCardMasterCardId ? 'block' : 'none', borderTop: '1px solid var(--border-glass)', paddingTop: '12px' }}>
              <div style={{ marginBottom: '14px' }}>
                <MasterCardPicker
                  selectedCardId={createCardMasterCardId}
                  label="Official Card Catalog (Optional — leave empty for Custom Card)"
                  placeholder="Select directory card (or leave empty for custom card)..."
                  onSelect={(id, cardObj) => {
                    setCreateCardMasterCardId(id);
                    if (cardObj) {
                      const nameInput = document.querySelector('input[name="name"]');
                      if (nameInput && (!nameInput.value || nameInput.value.includes('CC') || nameInput.value.includes('Account'))) {
                        nameInput.value = cardObj.card_name;
                      }
                    }
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label>Total Credit Limit</label>
                  <input name="limit" type="number" step="0.01" defaultValue="0.00" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    Monthly Spend Target
                    {renderInfoTooltip("Optional monthly budget target. Leaves progress bar hidden if empty or 0.")}
                  </label>
                  <input name="spendTarget" type="number" step="0.01" placeholder="Optional" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', marginBottom: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    Target Calculation Type
                  </label>
                  <select name="spendTargetType" style={{ padding: '8px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', borderRadius: '8px', color: 'var(--text-main)', width: '100%' }}>
                    <option value="CALENDAR">Calendar wise monthly</option>
                    <option value="STATEMENT">Statement date to statement date</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    Statement Closing Day
                    {renderInfoTooltip("The day of the month your monthly credit card bill is generated.")}
                  </label>
                  <input name="statement" type="number" min="1" max="31" defaultValue="15" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'flex', alignItems: 'center' }}>
                    Payment Due Day
                    {renderInfoTooltip("The deadline to pay your credit card bill before incurring interest/fees.")}
                  </label>
                  <input name="due" type="number" min="1" max="31" defaultValue="25" />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input name="include" type="checkbox" defaultChecked style={{ width: '20px', height: '20px' }} />
              <label style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                Include in net sum
                {renderInfoTooltip("If checked, this account's balance will be included in the total Net Balance donut visualizer at the top of your dashboard.")}
              </label>
            </div>

            <button type="submit" className="btn-primary">
              Create Account
            </button>
          </form>
        </div>
      )}

      {/* --- ADD / CREATE EXPENSE GROUP MODAL --- */}
      {activeModal === 'group_create' && (
        <div className="modal-backdrop modal-backdrop-blur">
          <form
            onSubmit={handleCreateGroup}
            className="glass-panel modal-spring-enter modal-content-panel"
            style={{ display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>Create Shared Group</h2>
              <button type="button" onClick={() => setActiveModal(null)} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer' }}>Cancel</button>
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Group Name</label>
              <input
                type="text"
                placeholder="e.g. Europe Trip 2026, Flatmates"
                value={groupCreateName}
                onChange={e => setGroupCreateName(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px', boxSizing: 'border-box' }}
                required
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>Group Type</label>
              <select
                value={groupCreateType}
                onChange={e => setGroupCreateType(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px', boxSizing: 'border-box' }}
                required
              >
                <option value="TRIP">✈️ Trip / Holiday</option>
                <option value="HOME">🏠 Home / Roommates</option>
                <option value="COUPLE">❤️ Couple / Partners</option>
                <option value="OTHER">📂 Other / Custom Group</option>
              </select>
            </div>

            {/* Add Friends to Group (From Friends List Only) */}
            <div>
              <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                👥 Add Members from Friends List
              </label>
              {friends.length === 0 ? (
                <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, padding: '10px 12px', background: 'rgba(255,255,255,0.02)', borderRadius: '10px', border: '1px solid var(--border-glass)' }}>
                  No friends in your contacts yet. You can create the group now and add friends later from Settings / Friends!
                </p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '160px', overflowY: 'auto', background: 'rgba(0,0,0,0.25)', padding: '8px', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
                  {sortFriendsAZ(friends).map(f => {
                    const isChecked = groupCreateSelectedFriends.includes(f.user_id);
                    const displayName = formatFriendLabel(f);
                    const initial = (f.nickname || f.display_name || f.email || '?').charAt(0).toUpperCase();
                    return (
                      <label 
                        key={f.user_id} 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '10px', 
                          fontSize: '13px', 
                          cursor: 'pointer', 
                          padding: '8px 10px', 
                          borderRadius: '8px', 
                          background: isChecked ? 'rgba(0, 245, 212, 0.08)' : 'rgba(255, 255, 255, 0.02)', 
                          border: '1px solid ' + (isChecked ? 'var(--accent-cyan)' : 'var(--border-glass)'), 
                          transition: 'all 0.2s ease',
                          margin: 0
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          style={{ width: '18px', height: '18px', margin: 0, flexShrink: 0, cursor: 'pointer' }}
                          onChange={e => {
                            if (e.target.checked) {
                              setGroupCreateSelectedFriends([...groupCreateSelectedFriends, f.user_id]);
                            } else {
                              setGroupCreateSelectedFriends(groupCreateSelectedFriends.filter(id => id !== f.user_id));
                            }
                          }}
                        />
                        <div style={{
                          width: '26px',
                          height: '26px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, var(--accent-purple), #5e17eb)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          flexShrink: 0
                        }}>
                          {initial}
                        </div>
                        <span style={{ color: 'var(--text-main)', fontWeight: isChecked ? '600' : '500', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {displayName}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Simplify Group Debts Toggle */}
            <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-glass)', borderRadius: '12px', padding: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={groupCreateSimplifyDebts}
                    style={{ width: '18px', height: '18px', margin: 0, flexShrink: 0, cursor: 'pointer' }}
                    onChange={e => setGroupCreateSimplifyDebts(e.target.checked)}
                  />
                  <span>⚡ Simplify Group Debts</span>
                </label>
                <span
                  onClick={() => setShowSimplifyLearnMoreModal(true)}
                  style={{ color: 'var(--accent-cyan)', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', flexShrink: 0 }}
                >
                  Learn More
                </span>
              </div>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '6px 0 0 0' }}>
                Auto-combines multilateral debts within the group to minimize total repayment transactions.
              </p>
            </div>

            <button type="submit" className="btn-primary" style={{ padding: '12px', fontSize: '14px', fontWeight: 'bold', borderRadius: '12px', marginTop: '4px' }}>
              Create Group
            </button>
          </form>
        </div>
      )}

      {/* --- EDIT EXPENSE GROUP SETTINGS MODAL --- */}
      {activeModal === 'group_edit' && (() => {
        const myUserId = localStorage.getItem('dhudd_user_id');
        const currentMember = groupMembers.find(m => m.user_id === myUserId);
        const isCurrentGroupAdmin = currentMember?.role === 'admin' || activeGroup?.created_by_user_id === myUserId;

        return (
          <div className="modal-backdrop modal-backdrop-blur">
            <div
              className="glass-panel modal-spring-enter modal-content-panel"
              style={{ display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box', maxWidth: '480px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>Group Settings</h2>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {isCurrentGroupAdmin ? '👑 You are an Admin' : '👤 You are a Member'}
                  </span>
                </div>
                <button type="button" onClick={() => setActiveModal(null)} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer' }}>Close</button>
              </div>

              <form onSubmit={handleUpdateGroup} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                    Group Name {!isCurrentGroupAdmin && <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 'normal' }}>(Admin Only)</span>}
                  </label>
                  <input
                    type="text"
                    value={groupEditName}
                    onChange={e => setGroupEditName(e.target.value)}
                    disabled={!isCurrentGroupAdmin}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '12px',
                      background: isCurrentGroupAdmin ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
                      border: '1px solid var(--border-glass)',
                      color: isCurrentGroupAdmin ? 'var(--text-main)' : 'var(--text-muted)',
                      fontSize: '13px',
                      boxSizing: 'border-box',
                      cursor: isCurrentGroupAdmin ? 'text' : 'not-allowed'
                    }}
                    required
                  />
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                    Group Type {!isCurrentGroupAdmin && <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 'normal' }}>(Admin Only)</span>}
                  </label>
                  <select
                    value={groupEditType}
                    onChange={e => setGroupEditType(e.target.value)}
                    disabled={!isCurrentGroupAdmin}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      borderRadius: '12px',
                      background: isCurrentGroupAdmin ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
                      border: '1px solid var(--border-glass)',
                      color: isCurrentGroupAdmin ? 'var(--text-main)' : 'var(--text-muted)',
                      fontSize: '13px',
                      boxSizing: 'border-box',
                      cursor: isCurrentGroupAdmin ? 'pointer' : 'not-allowed'
                    }}
                    required
                  >
                    <option value="TRIP">✈️ Trip / Holiday</option>
                    <option value="HOME">🏠 Home / Roommates</option>
                    <option value="COUPLE">❤️ Couple / Partners</option>
                    <option value="OTHER">📂 Other / Custom Group</option>
                  </select>
                </div>

                {/* Simplify Group Debts Toggle */}
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-glass)', borderRadius: '12px', padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{
                      fontSize: '13px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: isCurrentGroupAdmin ? 'pointer' : 'not-allowed',
                      margin: 0
                    }}>
                      <input
                        type="checkbox"
                        checked={groupEditSimplifyDebts}
                        disabled={!isCurrentGroupAdmin}
                        style={{ width: '18px', height: '18px', margin: 0, flexShrink: 0, cursor: isCurrentGroupAdmin ? 'pointer' : 'not-allowed' }}
                        onChange={e => setGroupEditSimplifyDebts(e.target.checked)}
                      />
                      <span>⚡ Simplify Group Debts</span>
                      {!isCurrentGroupAdmin && (
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontWeight: 'normal' }}>(Admin Only)</span>
                      )}
                    </label>
                    <span
                      onClick={() => setShowSimplifyLearnMoreModal(true)}
                      style={{ color: 'var(--accent-cyan)', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', flexShrink: 0 }}
                    >
                      Learn More
                    </span>
                  </div>
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '6px 0 0 0' }}>
                    Auto-combines multilateral debts within the group to minimize total repayment transactions.
                  </p>
                </div>

                {isCurrentGroupAdmin ? (
                  <button type="submit" className="btn-primary" style={{ padding: '10px', fontSize: '13px', fontWeight: 'bold', borderRadius: '12px' }}>
                    Save Group Details
                  </button>
                ) : (
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0, fontStyle: 'italic' }}>
                    Only group admins can edit name, type, and debt simplification.
                  </p>
                )}
              </form>

              {/* Member Management: Responsive Member Tiles Grid */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h4 style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', margin: 0, letterSpacing: '0.5px' }}>
                    Group Members ({groupMembers.length})
                  </h4>
                  {isCurrentGroupAdmin && (
                    <span style={{ fontSize: '11px', color: 'var(--accent-cyan)' }}>
                      Click tile to manage role
                    </span>
                  )}
                </div>
                
                {/* Member Tiles Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                  gap: '10px',
                  marginBottom: '14px'
                }}>
                  {groupMembers.map(m => {
                    const isSelf = m.user_id === myUserId;
                    const isAdm = m.role === 'admin' || activeGroup?.created_by_user_id === m.user_id;
                    const name = m.display_name || m.nickname || m.email?.split('@')[0] || 'Member';

                    return (
                      <div
                        key={m.user_id}
                        onClick={() => {
                          if (isCurrentGroupAdmin) {
                            setSelectedMemberForRole(m);
                          }
                        }}
                        style={{
                          padding: '12px 10px',
                          borderRadius: '14px',
                          border: isAdm ? '1px solid rgba(0, 245, 212, 0.3)' : '1px solid var(--border-glass)',
                          background: isAdm ? 'rgba(0, 245, 212, 0.04)' : 'rgba(255, 255, 255, 0.02)',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          textAlign: 'center',
                          gap: '8px',
                          cursor: isCurrentGroupAdmin ? 'pointer' : 'default',
                          transition: 'all 0.15s ease'
                        }}
                        title={isCurrentGroupAdmin ? `Click to manage role for ${name}` : undefined}
                      >
                        {/* Avatar */}
                        {m.avatar_url ? (
                          <img
                            src={apiClient?.getAvatarUrl ? apiClient.getAvatarUrl(m.avatar_url) : m.avatar_url}
                            alt={name}
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: isAdm ? '2px solid var(--accent-cyan)' : '2px solid rgba(255, 255, 255, 0.1)'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            background: isAdm ? 'linear-gradient(135deg, #00f5d4, #0575e6)' : 'rgba(255, 255, 255, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: isAdm ? '#000' : '#fff'
                          }}>
                            {name.charAt(0).toUpperCase()}
                          </div>
                        )}

                        {/* Name & Contact */}
                        <div style={{ minWidth: 0, width: '100%' }}>
                          <div style={{
                            fontSize: '12px',
                            fontWeight: '700',
                            color: 'var(--text-main)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {name} {isSelf && <span style={{ fontSize: '10px', color: 'var(--accent-cyan)' }}>(You)</span>}
                          </div>
                          {m.email && (
                            <div style={{
                              fontSize: '10px',
                              color: 'var(--text-muted)',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {m.email}
                            </div>
                          )}
                        </div>

                        {/* Role Badge */}
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          borderRadius: '8px',
                          fontSize: '10px',
                          fontWeight: '700',
                          background: isAdm ? 'rgba(0, 245, 212, 0.15)' : 'rgba(255, 255, 255, 0.06)',
                          color: isAdm ? 'var(--accent-cyan)' : 'var(--text-muted)',
                          border: isAdm ? '1px solid rgba(0, 245, 212, 0.3)' : '1px solid rgba(255, 255, 255, 0.08)'
                        }}>
                          {isAdm ? '👑 Admin' : '👤 Member'}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Add Friend to Group: Admin Only */}
                {isCurrentGroupAdmin ? (
                  <form onSubmit={handleAddGroupMember} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <select
                      value={selectedFriendIdToAdd}
                      onChange={e => setSelectedFriendIdToAdd(e.target.value)}
                      style={{ flex: 1, padding: '9px 12px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px' }}
                      required
                    >
                      <option value="">-- Add friend to group --</option>
                      {sortFriendsAZ(friends.filter(f => !groupMembers.some(m => m.user_id === f.user_id))).map(f => (
                        <option key={f.user_id} value={f.user_id}>
                          {formatFriendLabel(f)}
                        </option>
                      ))}
                    </select>
                    <button type="submit" className="btn-secondary" style={{ padding: '9px 14px', borderRadius: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
                      <UserPlus size={15} /> Add
                    </button>
                  </form>
                ) : (
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', fontStyle: 'italic', margin: 0 }}>
                    Only group admins can add new members to this group.
                  </p>
                )}
              </div>

              {/* Opening Balances Migration Section */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h4 style={{ fontSize: '12px', color: 'var(--text-muted)', textTransform: 'uppercase', margin: 0, letterSpacing: '0.5px' }}>
                    ⚖️ Carried-Over Opening Balances
                  </h4>
                  {isCurrentGroupAdmin && (
                    <span style={{ fontSize: '10px', background: 'rgba(0, 245, 212, 0.15)', color: 'var(--accent-cyan)', padding: '2px 8px', borderRadius: '10px', fontWeight: 'bold' }}>Admin Control</span>
                  )}
                </div>
                <div style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-glass)', borderRadius: '12px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: 0 }}>
                    Migrate starting balances from Splitwise or previous records before logging new expenses. Carried-over debts automatically allocate across daily transactions and sync to the Friends menu.
                  </p>
                  <button
                    type="button"
                    onClick={() => openGroupOpeningBalancesModal(activeGroup)}
                    className="btn-secondary"
                    style={{
                      padding: '8px 14px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      borderColor: 'rgba(0, 245, 212, 0.3)',
                      color: 'var(--accent-cyan)'
                    }}
                  >
                    <Scale size={14} /> {isCurrentGroupAdmin ? 'Manage Opening Balances' : 'View Opening Balances'}
                  </button>
                </div>
              </div>

              {/* Actions: Export Statement, Leave Group & Delete Group */}
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => { setActiveModal(null); openExportModal('group', activeGroup); }}
                  className="btn-secondary"
                  style={{ padding: '8px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}
                >
                  <Download size={14} /> Export Statement
                </button>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {/* Leave Group Button */}
                  <button
                    type="button"
                    onClick={() => handleLeaveGroup(activeGroup)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '10px',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      background: 'rgba(255, 122, 24, 0.12)',
                      border: '1px solid rgba(255, 122, 24, 0.3)',
                      color: '#ff7a18',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                    title="Leave this group (requires zero balance)"
                  >
                    <LogOut size={14} /> Leave Group
                  </button>

                  {/* Delete Group Button: Admin Only */}
                  {isCurrentGroupAdmin && (
                    <button
                      type="button"
                      onClick={() => handleDeleteGroup(activeGroup)}
                      style={{
                        padding: '8px 14px',
                        borderRadius: '10px',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(255, 71, 87, 0.12)',
                        border: '1px solid rgba(255, 71, 87, 0.3)',
                        color: 'var(--color-outflow)',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                      title="Delete group (requires all balances settled)"
                    >
                      <Trash2 size={14} /> Delete Group
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* --- MEMBER ROLE MANAGEMENT MODAL --- */}
      {selectedMemberForRole && (() => {
        const myUserId = localStorage.getItem('dhudd_user_id');
        const isSelf = selectedMemberForRole.user_id === myUserId;
        const isAdm = selectedMemberForRole.role === 'admin' || activeGroup?.created_by_user_id === selectedMemberForRole.user_id;
        const isCreator = selectedMemberForRole.user_id === activeGroup?.created_by_user_id;
        const name = selectedMemberForRole.display_name || selectedMemberForRole.nickname || selectedMemberForRole.email || 'Member';

        return (
          <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 160 }}>
            <div
              className="glass-panel modal-spring-enter modal-content-panel"
              style={{ display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box', maxWidth: '380px', width: '100%' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: '16px', fontWeight: 'bold', margin: 0 }}>Manage Member Role</h3>
                <button type="button" onClick={() => setSelectedMemberForRole(null)} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '4px' }}>
                  <X size={18} />
                </button>
              </div>

              {/* Member Summary */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                borderRadius: '12px',
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid var(--border-glass)'
              }}>
                {selectedMemberForRole.avatar_url ? (
                  <img
                    src={apiClient?.getAvatarUrl ? apiClient.getAvatarUrl(selectedMemberForRole.avatar_url) : selectedMemberForRole.avatar_url}
                    alt={name}
                    style={{ width: '44px', height: '44px', borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    background: isAdm ? 'linear-gradient(135deg, #00f5d4, #0575e6)' : 'rgba(255, 255, 255, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: isAdm ? '#000' : '#fff'
                  }}>
                    {name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {name} {isSelf && <span style={{ fontSize: '11px', color: 'var(--accent-cyan)' }}>(You)</span>}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                    Current Role: <strong style={{ color: isAdm ? 'var(--accent-cyan)' : 'var(--text-main)' }}>{isAdm ? '👑 Admin' : '👤 Member'}</strong>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {isCreator ? (
                  <div style={{
                    padding: '10px 14px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    textAlign: 'center',
                    background: 'rgba(0, 245, 212, 0.08)',
                    color: 'var(--accent-cyan)',
                    border: '1px solid rgba(0, 245, 212, 0.2)'
                  }}>
                    👑 Group Creator (Permanent Admin)
                  </div>
                ) : isAdm ? (
                  <button
                    type="button"
                    onClick={async () => {
                      const target = selectedMemberForRole;
                      setSelectedMemberForRole(null);
                      await handleUpdateMemberRole(target.user_id, 'member');
                    }}
                    className="btn-secondary"
                    style={{ padding: '10px 14px', borderRadius: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    Change Role to 👤 Member
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={async () => {
                      const target = selectedMemberForRole;
                      setSelectedMemberForRole(null);
                      await handleUpdateMemberRole(target.user_id, 'admin');
                    }}
                    className="btn-primary"
                    style={{ padding: '10px 14px', borderRadius: '12px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    Promote to 👑 Admin
                  </button>
                )}

                {!isSelf && !isCreator && (
                  <button
                    type="button"
                    onClick={async () => {
                      const target = selectedMemberForRole;
                      setSelectedMemberForRole(null);
                      await handleRemoveGroupMember(target.user_id);
                    }}
                    style={{
                      padding: '10px 14px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      background: 'rgba(239, 68, 68, 0.12)',
                      border: '1px solid rgba(239, 68, 68, 0.25)',
                      color: '#f87171',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    <Trash2 size={15} /> Remove from Group
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* --- GROUP OPENING BALANCES BUILDER MODAL --- */}
      {isGroupOpeningBalancesModalOpen && (() => {
        const myUserId = localStorage.getItem('dhudd_user_id');
        const currentMember = groupMembers.find(m => m.user_id === myUserId);
        const isCurrentGroupAdmin = currentMember?.role === 'admin' || activeGroup?.created_by_user_id === myUserId;

        return (
        <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 10001 }}>
          <div
            className="glass-panel modal-spring-enter modal-content-panel"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              boxSizing: 'border-box',
              maxWidth: '620px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Scale size={18} color="var(--accent-cyan)" /> Opening Balances Builder
                </h2>
                <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  {activeGroup?.group_name || 'Group'} • Carried-over debts from previous apps
                </span>
              </div>
              <button
                type="button"
                onClick={() => setIsGroupOpeningBalancesModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer' }}
              >
                Close
              </button>
            </div>

            {loadingOpeningBalances ? (
              <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
                <ThinkingOrbLoader state="listening" size={64} color="var(--accent-cyan)" />
                <span>Loading existing opening balances...</span>
              </div>
            ) : (
              <form onSubmit={handleSaveGroupOpeningBalances} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                {/* Pairs Section */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Pairwise Debts ({groupOpeningBalancePairs.length})
                    </label>
                    {isCurrentGroupAdmin && (
                      <button
                        type="button"
                        onClick={handleAddOpeningBalancePair}
                        style={{
                          background: 'rgba(0, 245, 212, 0.12)',
                          border: '1px solid rgba(0, 245, 212, 0.3)',
                          color: 'var(--accent-cyan)',
                          borderRadius: '16px',
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Plus size={13} /> Add Another Pair
                      </button>
                    )}
                  </div>

                  {groupOpeningBalancePairs.map((pair, pIdx) => (
                    <div
                      key={pair.id || pIdx}
                      style={{
                        background: 'rgba(255, 255, 255, 0.03)',
                        border: '1px solid var(--border-glass)',
                        borderRadius: '12px',
                        padding: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {/* Debtor (Who Owes) */}
                        <select
                          value={pair.debtor_user_id}
                          onChange={e => handleUpdateOpeningBalancePair(pair.id, 'debtor_user_id', e.target.value)}
                          disabled={!isCurrentGroupAdmin}
                          style={{
                            flex: '1 1 130px',
                            padding: '8px 10px',
                            borderRadius: '10px',
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid var(--border-glass)',
                            color: 'var(--text-main)',
                            fontSize: '12px'
                          }}
                          required
                        >
                          <option value="">-- Who owes --</option>
                          {groupMembers.map(m => (
                            <option key={m.user_id} value={m.user_id}>
                              {m.display_name || m.nickname || m.email?.split('@')[0]}
                            </option>
                          ))}
                        </select>

                        <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--color-outflow)', padding: '0 2px' }}>
                          owes
                        </span>

                        {/* Creditor (Owed To) */}
                        <select
                          value={pair.creditor_user_id}
                          onChange={e => handleUpdateOpeningBalancePair(pair.id, 'creditor_user_id', e.target.value)}
                          disabled={!isCurrentGroupAdmin}
                          style={{
                            flex: '1 1 130px',
                            padding: '8px 10px',
                            borderRadius: '10px',
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid var(--border-glass)',
                            color: 'var(--text-main)',
                            fontSize: '12px'
                          }}
                          required
                        >
                          <option value="">-- Owed to --</option>
                          {groupMembers.map(m => (
                            <option key={m.user_id} value={m.user_id}>
                              {m.display_name || m.nickname || m.email?.split('@')[0]}
                            </option>
                          ))}
                        </select>

                        {/* Amount */}
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="Amount"
                          value={pair.amount}
                          onChange={e => handleUpdateOpeningBalancePair(pair.id, 'amount', e.target.value)}
                          disabled={!isCurrentGroupAdmin}
                          style={{
                            width: '90px',
                            padding: '8px 10px',
                            borderRadius: '10px',
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid var(--border-glass)',
                            color: 'var(--text-main)',
                            fontSize: '12px'
                          }}
                          required
                        />

                        {/* Currency */}
                        <select
                          value={pair.currency || activeGroup?.currency || 'AED'}
                          onChange={e => handleUpdateOpeningBalancePair(pair.id, 'currency', e.target.value)}
                          disabled={!isCurrentGroupAdmin}
                          style={{
                            width: '75px',
                            padding: '8px 6px',
                            borderRadius: '10px',
                            background: 'rgba(255,255,255,0.06)',
                            border: '1px solid var(--border-glass)',
                            color: 'var(--text-main)',
                            fontSize: '12px'
                          }}
                        >
                          {['AED', 'INR', 'USD', 'EUR', 'GBP', 'SAR', 'QAR', 'OMR', 'KWD', 'BHD'].map(c => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </select>

                        {/* Delete Button */}
                        {isCurrentGroupAdmin && (
                          <button
                            type="button"
                            onClick={() => handleRemoveOpeningBalancePair(pair.id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'var(--color-outflow)',
                              cursor: 'pointer',
                              padding: '6px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Remove Pair"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>

                      {/* Optional Notes */}
                      <input
                        type="text"
                        placeholder="Note (e.g. Splitwise trip carried over)"
                        value={pair.notes || ''}
                        onChange={e => handleUpdateOpeningBalancePair(pair.id, 'notes', e.target.value)}
                        disabled={!isCurrentGroupAdmin}
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '8px',
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.05)',
                          color: 'var(--text-muted)',
                          fontSize: '11px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Live Net Starting Balance Preview */}
                <div style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border-glass)', borderRadius: '12px', padding: '12px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 'bold', color: 'var(--accent-cyan)', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '8px' }}>
                    📊 Live Net Starting Balance Preview
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {groupMembers.map(m => {
                      const netCurrs = openingBalanceNetPreview[m.user_id] || {};
                      const entries = Object.entries(netCurrs);
                      return (
                        <div key={m.user_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px', padding: '4px 8px', borderRadius: '6px', background: 'rgba(255,255,255,0.02)' }}>
                          <span style={{ fontWeight: '600', color: 'var(--text-main)' }}>
                            {m.display_name || m.nickname || m.email?.split('@')[0]}
                          </span>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {entries.length === 0 ? (
                              <span style={{ color: 'var(--text-muted)' }}>0.00 {activeGroup?.currency || 'AED'} (Settled)</span>
                            ) : (
                              entries.map(([cur, val]) => (
                                <span key={cur} style={{ fontWeight: '700', color: val > 0 ? 'var(--color-inflow)' : val < 0 ? 'var(--color-outflow)' : 'var(--text-muted)' }}>
                                  {val > 0 ? `+${formatMoney(val, cur)} (Gets Back)` : val < 0 ? `-${formatMoney(-val, cur)} (Owes)` : `0.00 ${cur}`}
                                </span>
                              ))
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Inline Error Message */}
                {openingBalanceError && (
                  <div style={{
                    padding: '10px 14px',
                    borderRadius: '10px',
                    background: 'rgba(255, 71, 87, 0.15)',
                    border: '1px solid rgba(255, 71, 87, 0.3)',
                    color: 'var(--color-outflow)',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <AlertTriangle size={15} style={{ flexShrink: 0 }} />
                    <span>{openingBalanceError}</span>
                  </div>
                )}

                {/* Action Buttons */}
                {isCurrentGroupAdmin ? (
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setIsGroupOpeningBalancesModalOpen(false)}
                      className="btn-secondary"
                      style={{ padding: '10px 18px', borderRadius: '12px', fontSize: '13px' }}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={savingOpeningBalances}
                      className="btn-primary"
                      style={{ padding: '10px 20px', borderRadius: '12px', fontSize: '13px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                      <Save size={15} /> {savingOpeningBalances ? <ThinkingOrbLoader popup state="working" size={20} color="var(--accent-cyan)" text="Saving..." /> : 'Save Opening Balances'}
                    </button>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', fontSize: '12px', color: 'var(--text-muted)' }}>
                    Only group admins can edit opening balances.
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
        );
      })()}

      {/* --- GROUP SETTLE MODAL --- */}
      <GroupSettleModal
        isOpen={isGroupSettleModalOpen}
        activeGroup={activeGroup}
        groupMembers={groupMembers}
        accounts={accounts}
        prefill={groupSettlePrefill}
        currentUserId={localStorage.getItem('dhudd_user_id')}
        onClose={() => {
          setIsGroupSettleModalOpen(false);
          setGroupSettlePrefill(null);
        }}
        onSubmit={handleGroupSettleSubmit}
        formatMoney={formatMoney}
      />

      {/* --- FRIEND BALANCE CHOOSER MODAL --- */}
      <FriendBalanceChooserModal
        isOpen={friendBalanceChooserOpen}
        onClose={() => setFriendBalanceChooserOpen(false)}
        friend={activeFriend}
        balancesData={friendBalanceChooserData}
        initialCurrency={friendBalanceChooserCurrency}
        onSelectOption={handleSelectBalanceOption}
        formatMoney={formatMoney}
      />

      {/* --- FRIEND SETTLE UP MODAL --- */}
      {friendSettleOpen && activeFriend && (
        <div 
          className="modal-backdrop modal-backdrop-blur"
          style={{ zIndex: 10000 }}
          onClick={(e) => { if (e.target === e.currentTarget) closeFriendSettleModal(); }}
        >
          <div 
            className="glass-panel modal-spring-enter modal-content-panel" 
            style={{ 
              maxWidth: '440px', 
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}
          >
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '10px',
                  background: friendSettleDirection === 'pay' ? 'rgba(255, 71, 87, 0.15)' : 'rgba(0, 245, 212, 0.15)',
                  border: `1px solid ${friendSettleDirection === 'pay' ? 'rgba(255, 71, 87, 0.3)' : 'rgba(0, 245, 212, 0.3)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: friendSettleDirection === 'pay' ? 'var(--color-outflow)' : 'var(--accent-cyan)'
                }}>
                  <ArrowLeftRight size={18} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--text-main)' }}>
                    Record Settlement
                  </h3>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    With {activeFriend.nickname || activeFriend.display_name || activeFriend.email}
                  </span>
                  {friendSettleGroupId && friendSettleGroupName && (
                    <div style={{ marginTop: '4px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '6px',
                        background: 'rgba(99, 102, 241, 0.15)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        color: '#818cf8',
                        fontWeight: '600'
                      }}>
                        📍 Group: {friendSettleGroupName}
                      </span>
                    </div>
                  )}
                  {friendSettleIsAll && (
                    <div style={{ marginTop: '4px' }}>
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '11px',
                        padding: '2px 8px',
                        borderRadius: '6px',
                        background: 'rgba(16, 185, 129, 0.15)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        color: '#34d399',
                        fontWeight: '600'
                      }}>
                        ✨ Settle All Balances
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button 
                type="button" 
                onClick={closeFriendSettleModal}
                style={{ 
                  background: 'rgba(255, 255, 255, 0.05)', 
                  border: '1px solid var(--border-glass)', 
                  color: 'var(--text-muted)', 
                  borderRadius: '8px',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer' 
                }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Direction Selector (You Pay vs Friend Paid You) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: '600' }}>
                  Payment Direction
                </span>
                <span style={{ fontSize: '11px', color: 'var(--accent-cyan)', fontWeight: '600' }}>
                  Currency: {friendSettleCurrency}
                </span>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
                background: 'rgba(255, 255, 255, 0.03)',
                padding: '4px',
                borderRadius: '12px',
                border: '1px solid var(--border-glass)'
              }}>
                <button
                  type="button"
                  onClick={() => setFriendSettleDirection('pay')}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    padding: '10px 8px',
                    borderRadius: '10px',
                    border: friendSettleDirection === 'pay'
                      ? '1.5px solid rgba(255, 71, 87, 0.6)'
                      : '1px solid transparent',
                    background: friendSettleDirection === 'pay'
                      ? 'rgba(255, 71, 87, 0.15)'
                      : 'rgba(255, 255, 255, 0.02)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '16px' }}>💸</span>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: friendSettleDirection === 'pay' ? 'var(--color-outflow, #ff4757)' : 'var(--text-muted)'
                    }}>
                      You Paid {activeFriend?.nickname || activeFriend?.display_name || activeFriend?.email?.split('@')[0] || 'Friend'}
                    </span>
                  </div>
                  <span style={{ fontSize: '10px', color: friendSettleDirection === 'pay' ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)' }}>
                    You sent money (outflow)
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setFriendSettleDirection('receive')}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                    padding: '10px 8px',
                    borderRadius: '10px',
                    border: friendSettleDirection === 'receive'
                      ? '1.5px solid rgba(0, 245, 212, 0.6)'
                      : '1px solid transparent',
                    background: friendSettleDirection === 'receive'
                      ? 'rgba(0, 245, 212, 0.15)'
                      : 'rgba(255, 255, 255, 0.02)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '16px' }}>💰</span>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: friendSettleDirection === 'receive' ? 'var(--accent-cyan, #00f5d4)' : 'var(--text-muted)'
                    }}>
                      {activeFriend?.nickname || activeFriend?.display_name || activeFriend?.email?.split('@')[0] || 'Friend'} Paid You
                    </span>
                  </div>
                  <span style={{ fontSize: '10px', color: friendSettleDirection === 'receive' ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)' }}>
                    You received money (inflow)
                  </span>
                </button>
              </div>
            </div>

            <form onSubmit={handleSettleFriend} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                  {friendSettleDirection === 'pay' ? 'Deduct from Account (optional)' : 'Deposit to Account (optional)'}
                </label>
                <select 
                  value={friendSettleAccountId} 
                  onChange={e => setFriendSettleAccountId(e.target.value)} 
                  style={{ padding: '10px 12px', fontSize: '13px', width: '100%', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)' }}
                >
                  <option value="">None (Don't affect my accounts)</option>
                  {accounts.filter(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts'))).map(acc => (
                    <option key={acc.account_id} value={acc.account_id}>
                      {acc.name} ({acc.currency})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                  Settlement Date
                </label>
                <input 
                  type="date" 
                  value={friendSettleDate} 
                  onChange={e => setFriendSettleDate(e.target.value)} 
                  required 
                  style={{ padding: '10px 12px', fontSize: '13px', width: '100%', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', boxSizing: 'border-box' }}
                />
              </div>

              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '6px', fontWeight: '500' }}>
                  Settlement Amount ({friendSettleCurrency})
                </label>
                <input 
                  type="number" 
                  step="0.0001" 
                  placeholder="0.00"
                  value={friendSettleAmount} 
                  onChange={e => setFriendSettleAmount(e.target.value)} 
                  required 
                  style={{ padding: '10px 12px', fontSize: '14px', width: '100%', borderRadius: '10px', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', boxSizing: 'border-box' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '6px' }}>
                <button 
                  type="button" 
                  onClick={closeFriendSettleModal} 
                  className="btn-secondary" 
                  style={{ padding: '10px 16px', fontSize: '13px', flex: 1, borderRadius: '10px' }}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn-primary" 
                  style={{ padding: '10px 16px', fontSize: '13px', flex: 1, fontWeight: 'bold', borderRadius: '10px' }}
                >
                  ✓ Confirm Settlement
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- DEBT SIMPLIFICATION LEARN MORE MODAL --- */}
      {showSimplifyLearnMoreModal && (
        <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 200 }}>
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ textAlign: 'center', maxWidth: '420px' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(0,245,212,0.1)', color: 'var(--accent-cyan)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px auto' }}>
              <ArrowLeftRight size={24} />
            </div>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 8px 0' }}>About Debt Simplification</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-main)', lineHeight: '1.5', textAlign: 'left', background: 'rgba(0,0,0,0.2)', padding: '14px', borderRadius: '12px', border: '1px solid var(--border-glass)', margin: '0 0 16px 0' }}>
              Debt simplification is a feature that restructures outstanding balances within a group. It does not change the total amount owed by any individual; instead, it streamlines the repayment process by reducing the total number of necessary transactions.
            </p>
            <button
              type="button"
              onClick={() => setShowSimplifyLearnMoreModal(false)}
              className="btn-primary"
              style={{ width: '100%', padding: '10px', fontSize: '13px', borderRadius: '12px' }}
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* --- ASSET SEARCH & LIVE MARKET DISCOVERY MODAL --- */}
      <AssetSearchModal
        isOpen={isAssetSearchOpen}
        onClose={() => setIsAssetSearchOpen(false)}
        onSelectAsset={handleSelectAssetFromSearch}
        onManualCustomAsset={handleManualCustomAssetFromSearch}
      />

      {/* --- FULL-SCREEN TRADINGVIEW LIGHTWEIGHT CHART MODAL --- */}
      {selectedChartAsset && (
        <TradingViewChart
          symbol={selectedChartAsset.symbol}
          name={selectedChartAsset.name}
          currency={selectedChartAsset.currency}
          avgCost={selectedChartAsset.avgCost}
          userTransactions={selectedChartAsset.transactions}
          height={440}
          isModal={true}
          onClose={() => setSelectedChartAsset(null)}
        />
      )}

      {/* --- ADD INVESTMENT MODAL --- */}
      {activeModal === 'add_investment' && (
        <div className="modal-backdrop modal-backdrop-blur">
          <form
            onSubmit={handleCreateAsset}
            className="glass-panel modal-spring-enter modal-content-panel"
            style={{ display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box' }}
          >
            <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <h2 style={{ margin: 0 }}>Add Investment Asset</h2>
                <button
                  type="button"
                  onClick={() => { setActiveModal(null); setIsAssetSearchOpen(true); }}
                  className="btn-secondary"
                  style={{ padding: '3px 8px', fontSize: '10px', color: 'var(--accent-cyan)' }}
                >
                  🔍 Search Catalog
                </button>
              </div>
              <button type="button" onClick={() => setActiveModal(null)} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
            </div>

            <div>
              <label>Asset Name</label>
              <input type="text" placeholder="e.g. Apple Inc., Bitcoin" value={newInvName} onChange={e => setNewInvName(e.target.value)} required />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label>Symbol / Ticker {renderInfoTooltip("The trading symbol (e.g. AAPL, BTC, GLD) representing the asset.")}</label>
                <input type="text" placeholder="e.g. AAPL, BTC" value={newInvSymbol} onChange={e => setNewInvSymbol(e.target.value)} required />
              </div>
              <div style={{ flex: 1 }}>
                <label>Asset Type {renderInfoTooltip("Select the classification of the asset. Choose 'Add Custom Type...' if yours isn't listed.")}</label>
                <select value={newInvType} onChange={e => setNewInvType(e.target.value)} required>
                  <option value="EQUITY">Equity (Stock)</option>
                  <option value="ETF">ETF</option>
                  <option value="MUTUAL_FUND">Mutual Fund</option>
                  <option value="CRYPTO">Cryptocurrency</option>
                  <option value="REAL_ESTATE">Real Estate</option>
                  <option value="COMMODITY">Commodity (Gold/Silver)</option>
                  <option value="FIXED_INCOME">Fixed Income (Bond)</option>
                  <option value="ALTERNATIVE">Alternative (Collectibles)</option>
                  {JSON.parse(localStorage.getItem('dhudd_custom_inv_types') || '[]').map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                  <option value="ADD_NEW">➕ Add Custom Type...</option>
                </select>
              </div>
            </div>

            {newInvType === 'ADD_NEW' && (
              <div style={{ background: 'rgba(0,245,212,0.05)', padding: '12px', borderRadius: '12px', border: '1px solid var(--accent-cyan)' }}>
                <label>New Custom Asset Type Name</label>
                <input
                  type="text"
                  placeholder="e.g. Vintage Watches, Private Equity"
                  value={customInvTypeInput}
                  onChange={e => setCustomInvTypeInput(e.target.value)}
                  required
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label>Sector / Category {renderInfoTooltip("The industry sector (e.g. Technology, Healthcare) or category for this asset. Helps track diversification.")}</label>
                <select value={newInvSector} onChange={e => setNewInvSector(e.target.value)} required>
                  <option value="Technology">Technology</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Finance">Finance</option>
                  <option value="Energy">Energy</option>
                  <option value="Consumer Discretionary">Consumer Discretionary</option>
                  <option value="Consumer Staples">Consumer Staples</option>
                  <option value="Real Estate">Real Estate</option>
                  <option value="Utilities">Utilities</option>
                  <option value="Materials">Materials</option>
                  <option value="Industrials">Industrials</option>
                  <option value="Telecommunication">Telecommunication</option>
                  <option value="Cryptocurrency">Cryptocurrency</option>
                  <option value="N/A">N/A</option>
                  {JSON.parse(localStorage.getItem('dhudd_custom_inv_sectors') || '[]').map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                  <option value="ADD_NEW">➕ Add Custom Sector...</option>
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label>Geographic Region {renderInfoTooltip("The geographic focus of the asset (e.g. US, EU, Global). Helps track global exposure.")}</label>
                <select value={newInvRegion} onChange={e => setNewInvRegion(e.target.value)} required>
                  <option value="US">United States (US)</option>
                  <option value="EU">Europe (EU)</option>
                  <option value="Asia">Asia</option>
                  <option value="Emerging Markets">Emerging Markets</option>
                  <option value="Global">Global</option>
                  <option value="Middle East">Middle East</option>
                  <option value="N/A">N/A</option>
                  {JSON.parse(localStorage.getItem('dhudd_custom_inv_regions') || '[]').map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                  <option value="ADD_NEW">➕ Add Custom Region...</option>
                </select>
              </div>
            </div>

            {(newInvSector === 'ADD_NEW' || newInvRegion === 'ADD_NEW') && (
              <div style={{ display: 'flex', gap: '10px' }}>
                {newInvSector === 'ADD_NEW' ? (
                  <div style={{ flex: 1 }}>
                    <label>Custom Sector / Category</label>
                    <input type="text" placeholder="e.g. Biotech, AI" value={customInvSector} onChange={e => setCustomInvSector(e.target.value)} required />
                  </div>
                ) : <div style={{ flex: 1 }}></div>}
                {newInvRegion === 'ADD_NEW' ? (
                  <div style={{ flex: 1 }}>
                    <label>Custom Geographic Region</label>
                    <input type="text" placeholder="e.g. LatAm, UK" value={customInvRegion} onChange={e => setCustomInvRegion(e.target.value)} required />
                  </div>
                ) : <div style={{ flex: 1 }}></div>}
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label>Asset Currency {renderInfoTooltip("Select the currency in which this asset is priced and traded.")}</label>
                <select value={newInvCurrency} onChange={e => setNewInvCurrency(e.target.value)} required>
                  {preferredCurrencies.map(curr => (
                    <option key={curr} value={curr}>{curr}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label>Market Cap Size {renderInfoTooltip("The market cap category of the equity. Select N/A for crypto, commodities, or fixed income.")}</label>
                <select value={newInvMarketCap} onChange={e => setNewInvMarketCap(e.target.value)}>
                  <option value="Large">Large-Cap</option>
                  <option value="Mid">Mid-Cap</option>
                  <option value="Small">Small-Cap</option>
                  <option value="N/A">N/A (Crypto/Commodity)</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label>Target Allocation % {renderInfoTooltip("Your desired percentage weight for this asset class. Banners will alert you if your actual holding drifts off by more than 5%.")}</label>
                <input type="number" step="0.01" min="0" max="100" placeholder="e.g. 15" value={newInvTargetPct} onChange={e => setNewInvTargetPct(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label>Current Unit Price ({newInvCurrency}) {renderInfoTooltip("The price of a single unit of the asset in the selected currency.")}</label>
                <input type="number" step="0.0001" placeholder="0.00" value={newInvPrice} onChange={e => setNewInvPrice(e.target.value)} required />
              </div>
            </div>

            <div>
              <label>Initial Quantity {renderInfoTooltip("The number of units you currently hold. Leave blank or 0 if you are just defining the asset profile without any initial holdings.")}</label>
              <input type="number" step="0.000001" placeholder="0" value={newInvQty} onChange={e => setNewInvQty(e.target.value)} />
            </div>

            {parseFloat(newInvQty) > 0 && (
              <>
                <div>
                  <label>Purchase Date</label>
                  <input type="date" value={newInvDate} onChange={e => setNewInvDate(e.target.value)} required />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                  <input
                    type="checkbox"
                    id="recordFundingFlow"
                    checked={recordFundingFlow}
                    onChange={e => setRecordFundingFlow(e.target.checked)}
                    style={{ width: 'auto', cursor: 'pointer' }}
                  />
                  <label htmlFor="recordFundingFlow" style={{ margin: 0, cursor: 'pointer', fontSize: '11px', color: 'var(--text-main)' }}>
                    Deduct purchase amount from an account / credit card / friend split
                  </label>
                </div>

                {recordFundingFlow && (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                      <input
                        type="checkbox"
                        id="invUseSplitSources"
                        checked={invUseSplitSources}
                        onChange={e => {
                          setInvUseSplitSources(e.target.checked);
                          if (e.target.checked) {
                            const initialAcc = accounts.find(a => a.type !== 'CREDIT_CARD' && a.currency === newInvCurrency) || accounts.find(a => a.type !== 'CREDIT_CARD');
                            setSplitInvSources([{ sourceType: 'ACCOUNT', sourceId: initialAcc?.account_id || '', amount: '' }]);
                          }
                        }}
                        style={{ width: 'auto', cursor: 'pointer' }}
                      />
                      <label htmlFor="invUseSplitSources" style={{ margin: 0, cursor: 'pointer', fontSize: '11px', color: 'var(--text-main)' }}>
                        Split funding across multiple source accounts
                      </label>
                    </div>

                    {!invUseSplitSources ? (
                      <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                        <div style={{ flex: 1 }}>
                          <label>Funding Source Type {renderInfoTooltip("How you purchased this initial holding.")}</label>
                          <select
                            value={newInvSourceType}
                            onChange={e => {
                              setNewInvSourceType(e.target.value);
                              if (e.target.value === 'ACCOUNT') {
                                const acc = accounts.find(a => a.type !== 'CREDIT_CARD' && a.currency === newInvCurrency) || accounts.find(a => a.type !== 'CREDIT_CARD');
                                setNewInvSourceId(acc?.account_id || '');
                              } else if (e.target.value === 'CREDIT_CARD') {
                                const acc = accounts.find(a => a.type === 'CREDIT_CARD' && a.currency === newInvCurrency) || accounts.find(a => a.type === 'CREDIT_CARD');
                                setNewInvSourceId(acc?.account_id || '');
                              } else if (e.target.value === 'FRIEND') {
                                setNewInvSourceId(friends[0]?.user_id || '');
                              } else {
                                setNewInvSourceId('');
                              }
                            }}
                            required
                          >
                            <option value="ACCOUNT">Bank Account / Cash Wallet</option>
                            <option value="CREDIT_CARD">Credit Card</option>
                            <option value="FRIEND">Friend (Debt Ledger)</option>
                          </select>
                        </div>

                        <div style={{ flex: 1 }}>
                          <label>Funding Source ({newInvCurrency})</label>
                          {newInvSourceType === 'ACCOUNT' && (
                            <select value={newInvSourceId} onChange={e => setNewInvSourceId(e.target.value)} required>
                              <option value="" disabled>Select Account</option>
                              {(() => {
                                const activeAccs = accounts.filter(a => a.type !== 'CREDIT_CARD' && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts')));
                                const matchedAccs = activeAccs.filter(a => a.currency === newInvCurrency);
                                const displayAccs = matchedAccs.length > 0 ? matchedAccs : activeAccs;
                                return displayAccs.map(a => (
                                  <option key={a.account_id} value={a.account_id}>
                                    {a.name} ({a.currency}) (Balance: {formatMoney(a.current_balance, a.currency)})
                                  </option>
                                ));
                              })()}
                            </select>
                          )}
                          {newInvSourceType === 'CREDIT_CARD' && (
                            <select value={newInvSourceId} onChange={e => setNewInvSourceId(e.target.value)} required>
                              <option value="" disabled>Select Credit Card</option>
                              {(() => {
                                const activeCards = accounts.filter(a => a.type === 'CREDIT_CARD' && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0');
                                const matchedCards = activeCards.filter(c => c.currency === newInvCurrency);
                                const displayCards = matchedCards.length > 0 ? matchedCards : activeCards;
                                return displayCards.map(c => (
                                  <option key={c.account_id} value={c.account_id}>
                                    {c.name} ({c.currency}) (Balance: {formatMoney(c.current_balance, c.currency)})
                                  </option>
                                ));
                              })()}
                            </select>
                          )}
                          {newInvSourceType === 'FRIEND' && (
                            <select value={newInvSourceId} onChange={e => setNewInvSourceId(e.target.value)} required>
                              <option value="" disabled>Select Friend</option>
                              {sortFriendsAZ(friends).map(f => (
                                <option key={f.user_id} value={f.user_id}>
                                  {formatFriendLabel(f)}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-glass)', borderRadius: '12px', marginTop: '4px' }}>
                        <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Split Funding Sources ({newInvCurrency})</span>
                          <button
                            type="button"
                            onClick={() => {
                              const initialAcc = accounts.find(a => a.type !== 'CREDIT_CARD' && a.currency === newInvCurrency) || accounts.find(a => a.type !== 'CREDIT_CARD');
                              setSplitInvSources([...splitInvSources, { sourceType: 'ACCOUNT', sourceId: initialAcc?.account_id || '', amount: '' }]);
                            }}
                            style={{ fontSize: '9px', padding: '2px 8px', background: 'rgba(0,245,212,0.15)', border: 'none', borderRadius: '4px', color: 'var(--accent-cyan)', cursor: 'pointer' }}
                          >
                            ➕ Add Source
                          </button>
                        </div>
                        {splitInvSources.map((src, idx) => (
                          <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <select
                              value={src.sourceType}
                              onChange={e => {
                                const list = [...splitInvSources];
                                list[idx].sourceType = e.target.value;
                                if (e.target.value === 'ACCOUNT') {
                                  const acc = accounts.find(a => a.type !== 'CREDIT_CARD' && a.currency === newInvCurrency) || accounts.find(a => a.type !== 'CREDIT_CARD');
                                  list[idx].sourceId = acc?.account_id || '';
                                } else if (e.target.value === 'CREDIT_CARD') {
                                  const acc = accounts.find(a => a.type === 'CREDIT_CARD' && a.currency === newInvCurrency) || accounts.find(a => a.type === 'CREDIT_CARD');
                                  list[idx].sourceId = acc?.account_id || '';
                                } else if (e.target.value === 'FRIEND') {
                                  list[idx].sourceId = friends[0]?.user_id || '';
                                }
                                setSplitInvSources(list);
                              }}
                              style={{ flex: 1, padding: '4px 6px', fontSize: '10px' }}
                              required
                            >
                              <option value="ACCOUNT">Bank/Cash</option>
                              <option value="CREDIT_CARD">Credit Card</option>
                              <option value="FRIEND">Friend</option>
                            </select>
                            
                            <select
                              value={src.sourceId}
                              onChange={e => {
                                const list = [...splitInvSources];
                                list[idx].sourceId = e.target.value;
                                setSplitInvSources(list);
                              }}
                              style={{ flex: 1.5, padding: '4px 6px', fontSize: '10px' }}
                              required
                            >
                              <option value="" disabled>Select Source</option>
                              {src.sourceType === 'ACCOUNT' && (() => {
                                const activeAccs = accounts.filter(a => a.type !== 'CREDIT_CARD' && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts')));
                                const matchedAccs = activeAccs.filter(a => a.currency === newInvCurrency);
                                const displayAccs = matchedAccs.length > 0 ? matchedAccs : activeAccs;
                                return displayAccs.map(a => (
                                  <option key={a.account_id} value={a.account_id}>{a.name} ({a.currency}) ({formatMoney(a.current_balance, a.currency)})</option>
                                ));
                              })()}
                              {src.sourceType === 'CREDIT_CARD' && (() => {
                                const activeCards = accounts.filter(a => a.type === 'CREDIT_CARD' && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0');
                                const matchedCards = activeCards.filter(c => c.currency === newInvCurrency);
                                const displayCards = matchedCards.length > 0 ? matchedCards : activeCards;
                                return displayCards.map(c => (
                                  <option key={c.account_id} value={c.account_id}>{c.name} ({c.currency}) ({formatMoney(-c.current_balance, c.currency)})</option>
                                ));
                              })()}
                              {src.sourceType === 'FRIEND' && sortFriendsAZ(friends).map(f => (
                                <option key={f.user_id} value={f.user_id}>{formatFriendLabel(f)}</option>
                              ))}
                            </select>

                            <input
                              type="number"
                              step="0.01"
                              placeholder="Amount"
                              value={src.amount}
                              onChange={e => {
                                const list = [...splitInvSources];
                                list[idx].amount = e.target.value;
                                setSplitInvSources(list);
                              }}
                              style={{ width: '60px', padding: '4px 6px', fontSize: '10px' }}
                              required
                            />

                            {splitInvSources.length > 1 && (
                              <button
                                type="button"
                                onClick={() => {
                                  const list = splitInvSources.filter((_, i) => i !== idx);
                                  setSplitInvSources(list);
                                }}
                                style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.7)', cursor: 'pointer', fontSize: '12px' }}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        ))}
                        {(() => {
                          const targetVal = (parseFloat(newInvQty) || 0) * (parseFloat(newInvPrice) || 0);
                          const allocatedVal = splitInvSources.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                          const remainingVal = targetVal - allocatedVal;
                          const remainingColor = remainingVal < 0 ? 'var(--color-outflow)' : remainingVal === 0 ? 'var(--color-inflow)' : 'var(--text-muted)';
                          return (
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', background: 'rgba(0,0,0,0.1)', padding: '6px 10px', borderRadius: '6px', marginTop: '4px', width: '100%' }}>
                              <span>Total: <strong style={{ color: 'var(--text-main)' }}>{formatMoney(targetVal, newInvCurrency)}</strong></span>
                              <span>Allocated: <strong style={{ color: 'var(--text-main)' }}>{formatMoney(allocatedVal, newInvCurrency)}</strong></span>
                              <span>Remaining: <strong style={{ color: remainingColor }}>{formatMoney(remainingVal, newInvCurrency)}</strong></span>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            <button type="submit" className="btn-primary" style={{ marginTop: '10px' }}>
              Create Asset Profile
            </button>
          </form>
        </div>
      )}

      {/* --- LOG INVESTMENT TRANSACTION MODAL --- */}
      {activeModal === 'log_investment_tx' && selectedAssetTx && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 100, display: 'flex',
          alignItems: 'flex-end', justifyContent: 'center'
        }}>
          <form
            onSubmit={handleCreateAssetTx}
            className="glass-panel animate-slide-up"
            style={{
              width: '100%', maxWidth: '460px', background: 'var(--bg-secondary)',
              borderTopLeftRadius: '30px', borderTopRightRadius: '30px', borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
              padding: '24px', display: 'flex', flexDirection: 'column', gap: '14px', maxHeight: 'calc(85dvh - env(safe-area-inset-bottom, 0px))', overflowY: 'auto'
            }}
          >
            <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>{editingInvTxId ? 'Edit' : 'Log'} Transaction: {selectedAssetTx.symbol}</h2>
              <button type="button" onClick={() => { setActiveModal(null); setEditingInvTxId(null); }} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
            </div>

            <div>
              <label>Transaction Type</label>
              <div style={{ display: 'flex', gap: '8px', background: 'rgba(255,255,255,0.05)', padding: '3px', borderRadius: '10px' }}>
                {['BUY', 'SELL', 'DIVIDEND'].map(t => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setInvTxType(t);
                      if (t === 'DIVIDEND') {
                        setInvTxUnits('0');
                        setInvTxPricePerUnit('0');
                      } else {
                        setInvTxUnits('');
                        setInvTxPricePerUnit('');
                      }
                    }}
                    style={{
                      flex: 1,
                      background: invTxType === t ? (t === 'BUY' ? 'var(--color-outflow)' : t === 'SELL' ? 'var(--color-inflow)' : 'var(--accent-cyan)') : 'transparent',
                      color: invTxType === t ? '#000' : 'var(--text-muted)',
                      border: 'none',
                      padding: '6px 0',
                      borderRadius: '8px',
                      fontSize: '11px',
                      fontWeight: '700',
                      cursor: 'pointer'
                    }}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <div style={{ flex: 1 }}>
                <label>Funding / Destination Type {renderInfoTooltip("Specifies where the cash comes from or goes. Choose 'None (No cash flow)' to bypass standard cash accounting and simply update portfolio metrics.")}</label>
                <select
                  value={invTxSourceType}
                  onChange={e => {
                    setInvTxSourceType(e.target.value);
                    if (e.target.value === 'ACCOUNT' || e.target.value === 'CREDIT_CARD') {
                      const relevantAcc = accounts.find(a => e.target.value === 'ACCOUNT' ? a.type !== 'CREDIT_CARD' : a.type === 'CREDIT_CARD');
                      setInvTxSourceId(relevantAcc?.account_id || '');
                    } else if (e.target.value === 'FRIEND') {
                      setInvTxSourceId(friends[0]?.user_id || '');
                    } else {
                      setInvTxSourceId('');
                    }
                  }}
                  required
                >
                  <option value="NONE">None (No cash flow)</option>
                  <option value="ACCOUNT">Bank Account / Cash Wallet</option>
                  <option value="CREDIT_CARD">Credit Card</option>
                  <option value="FRIEND">Friend (Debt Ledger)</option>
                </select>
              </div>
              {invTxSourceType === 'NONE' && <div style={{ flex: 1 }}></div>}
            </div>

            {invTxSourceType !== 'NONE' && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                  <input
                    type="checkbox"
                    id="txInvUseSplitSources"
                    checked={txInvUseSplitSources}
                    onChange={e => {
                      setTxInvUseSplitSources(e.target.checked);
                      if (e.target.checked) {
                        setSplitTxInvSources([{ sourceType: 'ACCOUNT', sourceId: accounts.find(a => a.type !== 'CREDIT_CARD')?.account_id || '', amount: '' }]);
                      }
                    }}
                    style={{ width: 'auto', cursor: 'pointer' }}
                  />
                  <label htmlFor="txInvUseSplitSources" style={{ margin: 0, cursor: 'pointer', fontSize: '11px', color: 'var(--text-main)' }}>
                    Split funding across multiple source accounts
                  </label>
                </div>

                {!txInvUseSplitSources ? (
                  <div>
                    <label>Funding / Destination Source ({(selectedAssetTx?.currency || 'AED')})</label>
                    {invTxSourceType === 'ACCOUNT' && (
                      <select value={invTxSourceId} onChange={e => setInvTxSourceId(e.target.value)} required>
                        <option value="" disabled>Select Account</option>
                        {(() => {
                          const assetCurr = selectedAssetTx?.currency || 'AED';
                          const activeAccs = accounts.filter(a => a.type !== 'CREDIT_CARD' && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts')));
                          const matchedAccs = activeAccs.filter(a => a.currency === assetCurr);
                          const displayAccs = matchedAccs.length > 0 ? matchedAccs : activeAccs;
                          return displayAccs.map(a => (
                            <option key={a.account_id} value={a.account_id}>
                              {a.name} ({a.currency}) (Balance: {formatMoney(a.current_balance, a.currency)})
                            </option>
                          ));
                        })()}
                      </select>
                    )}
                    {invTxSourceType === 'CREDIT_CARD' && (
                      <select value={invTxSourceId} onChange={e => setInvTxSourceId(e.target.value)} required>
                        <option value="" disabled>Select Credit Card</option>
                        {(() => {
                          const assetCurr = selectedAssetTx?.currency || 'AED';
                          const activeCards = accounts.filter(a => a.type === 'CREDIT_CARD' && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0');
                          const matchedCards = activeCards.filter(c => c.currency === assetCurr);
                          const displayCards = matchedCards.length > 0 ? matchedCards : activeCards;
                          return displayCards.map(c => (
                            <option key={c.account_id} value={c.account_id}>
                              {c.name} ({c.currency}) (Balance: {formatMoney(c.current_balance, c.currency)})
                            </option>
                          ));
                        })()}
                      </select>
                    )}
                    {invTxSourceType === 'FRIEND' && (
                      <select value={invTxSourceId} onChange={e => setInvTxSourceId(e.target.value)} required>
                        <option value="" disabled>Select Friend</option>
                        {sortFriendsAZ(friends).map(f => (
                          <option key={f.user_id} value={f.user_id}>
                            {formatFriendLabel(f)}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-glass)', borderRadius: '12px', marginTop: '4px' }}>
                    <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Split Funding Sources ({(selectedAssetTx?.currency || 'AED')})</span>
                      <button
                        type="button"
                        onClick={() => {
                          const assetCurr = selectedAssetTx?.currency || 'AED';
                          const initialAcc = accounts.find(a => a.type !== 'CREDIT_CARD' && a.currency === assetCurr) || accounts.find(a => a.type !== 'CREDIT_CARD');
                          setSplitTxInvSources([...splitTxInvSources, { sourceType: 'ACCOUNT', sourceId: initialAcc?.account_id || '', amount: '' }]);
                        }}
                        style={{ fontSize: '9px', padding: '2px 8px', background: 'rgba(0,245,212,0.15)', border: 'none', borderRadius: '4px', color: 'var(--accent-cyan)', cursor: 'pointer' }}
                      >
                        ➕ Add Source
                      </button>
                    </div>
                    {splitTxInvSources.map((src, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <select
                          value={src.sourceType}
                          onChange={e => {
                            const list = [...splitTxInvSources];
                            list[idx].sourceType = e.target.value;
                            const assetCurr = selectedAssetTx?.currency || 'AED';
                            if (e.target.value === 'ACCOUNT') {
                              const acc = accounts.find(a => a.type !== 'CREDIT_CARD' && a.currency === assetCurr) || accounts.find(a => a.type !== 'CREDIT_CARD');
                              list[idx].sourceId = acc?.account_id || '';
                            } else if (e.target.value === 'CREDIT_CARD') {
                              const acc = accounts.find(a => a.type === 'CREDIT_CARD' && a.currency === assetCurr) || accounts.find(a => a.type === 'CREDIT_CARD');
                              list[idx].sourceId = acc?.account_id || '';
                            } else if (e.target.value === 'FRIEND') {
                              list[idx].sourceId = friends[0]?.user_id || '';
                            }
                            setSplitTxInvSources(list);
                          }}
                          style={{ flex: 1, padding: '4px 6px', fontSize: '10px' }}
                          required
                        >
                          <option value="ACCOUNT">Bank/Cash</option>
                          <option value="CREDIT_CARD">Credit Card</option>
                          <option value="FRIEND">Friend</option>
                        </select>
                        
                        <select
                          value={src.sourceId}
                          onChange={e => {
                            const list = [...splitTxInvSources];
                            list[idx].sourceId = e.target.value;
                            setSplitTxInvSources(list);
                          }}
                          style={{ flex: 1.5, padding: '4px 6px', fontSize: '10px' }}
                          required
                        >
                          <option value="" disabled>Select Source</option>
                          {src.sourceType === 'ACCOUNT' && (() => {
                            const assetCurr = selectedAssetTx?.currency || 'AED';
                            const activeAccs = accounts.filter(a => a.type !== 'CREDIT_CARD' && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts')));
                            const matchedAccs = activeAccs.filter(a => a.currency === assetCurr);
                            const displayAccs = matchedAccs.length > 0 ? matchedAccs : activeAccs;
                            return displayAccs.map(a => (
                              <option key={a.account_id} value={a.account_id}>{a.name} ({a.currency}) ({formatMoney(a.current_balance, a.currency)})</option>
                            ));
                          })()}
                          {src.sourceType === 'CREDIT_CARD' && (() => {
                            const assetCurr = selectedAssetTx?.currency || 'AED';
                            const activeCards = accounts.filter(a => a.type === 'CREDIT_CARD' && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0');
                            const matchedCards = activeCards.filter(c => c.currency === assetCurr);
                            const displayCards = matchedCards.length > 0 ? matchedCards : activeCards;
                            return displayCards.map(c => (
                              <option key={c.account_id} value={c.account_id}>{c.name} ({c.currency}) ({formatMoney(-c.current_balance, c.currency)})</option>
                            ));
                          })()}
                          {src.sourceType === 'FRIEND' && sortFriendsAZ(friends).map(f => (
                            <option key={f.user_id} value={f.user_id}>{formatFriendLabel(f)}</option>
                          ))}
                        </select>

                        <input
                          type="number"
                          step="0.01"
                          placeholder="Amount"
                          value={src.amount}
                          onChange={e => {
                            const list = [...splitTxInvSources];
                            list[idx].amount = e.target.value;
                            setSplitTxInvSources(list);
                          }}
                          style={{ width: '60px', padding: '4px 6px', fontSize: '10px' }}
                          required
                        />

                        {splitTxInvSources.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const list = splitTxInvSources.filter((_, i) => i !== idx);
                              setSplitTxInvSources(list);
                            }}
                            style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.7)', cursor: 'pointer', fontSize: '12px' }}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    {(() => {
                      const assetCurr = selectedAssetTx?.currency || 'AED';
                      const targetVal = parseFloat(invTxAmount) || 0;
                      const allocatedVal = splitTxInvSources.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                      const remainingVal = targetVal - allocatedVal;
                      const remainingColor = remainingVal < 0 ? 'var(--color-outflow)' : remainingVal === 0 ? 'var(--color-inflow)' : 'var(--text-muted)';
                      return (
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', background: 'rgba(0,0,0,0.1)', padding: '6px 10px', borderRadius: '6px', marginTop: '4px', width: '100%' }}>
                          <span>Total: <strong style={{ color: 'var(--text-main)' }}>{formatMoney(targetVal, assetCurr)}</strong></span>
                          <span>Allocated: <strong style={{ color: 'var(--text-main)' }}>{formatMoney(allocatedVal, assetCurr)}</strong></span>
                          <span>Remaining: <strong style={{ color: remainingColor }}>{formatMoney(remainingVal, assetCurr)}</strong></span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </>
            )}

            <div>
              <label>Total Amount ({(selectedAssetTx?.currency || 'AED')})</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={invTxAmount}
                onChange={e => {
                  setInvTxAmount(e.target.value);
                  const amt = parseFloat(e.target.value) || 0;
                  const qty = parseFloat(invTxUnits) || 0;
                  if (qty > 0) {
                    setInvTxPricePerUnit((amt / qty).toFixed(4));
                  }
                }}
                style={{ fontSize: '20px', fontWeight: 'bold', textAlign: 'center' }}
                required
              />
              {!txInvUseSplitSources && invTxSourceType !== 'NONE' && (() => {
                const selectedAcc = accounts.find(a => a.account_id === invTxSourceId);
                return selectedAcc ? (
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px', textAlign: 'right' }}>
                    Available Balance: <span style={{ color: 'var(--accent-cyan)', fontWeight: '600' }}>{formatMoney(selectedAcc.current_balance, selectedAcc.currency)}</span>
                  </div>
                ) : null;
              })()}
            </div>

            {invTxType !== 'DIVIDEND' && (
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label>Units / Quantity {renderInfoTooltip("The number of units bought or sold.")}</label>
                  <input
                    type="number"
                    step="0.000001"
                    placeholder="0.00"
                    value={invTxUnits}
                    onChange={e => {
                      setInvTxUnits(e.target.value);
                      const qty = parseFloat(e.target.value) || 0;
                      const amt = parseFloat(invTxAmount) || 0;
                      if (qty > 0) {
                        setInvTxPricePerUnit((amt / qty).toFixed(4));
                      }
                    }}
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label>Price per Unit ({(selectedAssetTx?.currency || 'AED')}) {renderInfoTooltip("The price paid or received per unit. Auto-computed if Amount & Units are set.")}</label>
                  <input
                    type="number"
                    step="0.0001"
                    placeholder="0.00"
                    value={invTxPricePerUnit}
                    onChange={e => {
                      setInvTxPricePerUnit(e.target.value);
                      const price = parseFloat(e.target.value) || 0;
                      const qty = parseFloat(invTxUnits) || 0;
                      if (qty > 0) {
                        setInvTxAmount((qty * price).toFixed(2));
                      } else {
                        const amt = parseFloat(invTxAmount) || 0;
                        if (price > 0) {
                          setInvTxUnits((amt / price).toFixed(6));
                        }
                      }
                    }}
                    required
                  />
                </div>
              </div>
            )}

            {invTxType === 'SELL' && (() => {
              const assetCurr = selectedAssetTx?.currency || 'AED';
              const avgCost = parseFloat(selectedAssetTx?.average_cost) || 0;
              const unitsToSell = parseFloat(invTxUnits) || 0;
              const sellAmt = parseFloat(invTxAmount) || 0;
              const costBasis = unitsToSell * avgCost;
              const estRealizedPnL = sellAmt - costBasis;
              const estPnLPct = costBasis > 0 ? (estRealizedPnL / costBasis) * 100 : 0;

              if (unitsToSell <= 0 || sellAmt <= 0) return null;

              return (
                <div style={{
                  background: estRealizedPnL > 0.005 ? 'rgba(46,213,115,0.08)' : estRealizedPnL < -0.005 ? 'rgba(255,71,87,0.08)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${estRealizedPnL > 0.005 ? 'rgba(46,213,115,0.3)' : estRealizedPnL < -0.005 ? 'rgba(255,71,87,0.3)' : 'var(--border-glass)'}`,
                  borderRadius: '10px',
                  padding: '10px 12px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Avg Cost Basis:</span>
                    <span style={{ fontWeight: '600' }}>{formatMoney(costBasis, assetCurr)} ({formatMoney(avgCost, assetCurr)}/unit)</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Gross Proceeds:</span>
                    <span style={{ fontWeight: '600' }}>{formatMoney(sellAmt, assetCurr)}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: '6px',
                    borderTop: '1px solid rgba(255,255,255,0.06)'
                  }}>
                    <span style={{ fontSize: '12px', fontWeight: '700' }}>Realized P&L:</span>
                    <span style={{
                      fontSize: '13px',
                      fontWeight: '800',
                      color: estRealizedPnL > 0.005 ? 'var(--color-inflow)' : estRealizedPnL < -0.005 ? 'var(--color-outflow)' : 'var(--text-main)'
                    }}>
                      {estRealizedPnL > 0.005 ? `+${formatMoney(estRealizedPnL, assetCurr)} (+${estPnLPct.toFixed(2)}% Profit)` :
                       estRealizedPnL < -0.005 ? `${formatMoney(estRealizedPnL, assetCurr)} (${estPnLPct.toFixed(2)}% Loss)` :
                       `0.00 ${assetCurr} (Breakeven)`}
                    </span>
                  </div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic', textAlign: 'right' }}>
                    {estRealizedPnL > 0.005 ? `Recorded as Income from investments` :
                     estRealizedPnL < -0.005 ? `Recorded as Loss from investments` :
                     `Full amount recorded as capital return`}
                  </div>
                </div>
              );
            })()}

            <div>
              <label>Transaction Date</label>
              <input type="date" value={invTxDate} onChange={e => setInvTxDate(e.target.value)} required />
            </div>

            <button type="submit" className="btn-primary" style={{ marginTop: '10px' }}>
              {editingInvTxId ? 'Update Transaction' : 'Log Transaction Flow'}
            </button>
          </form>
        </div>
      )}

      {/* --- FULL-SCREEN STEP-BY-STEP ONBOARDING WIZARD --- */}
      {onboardingOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 999999,
          background: '#090d16',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px 16px',
          boxSizing: 'border-box',
          overflowY: 'auto'
        }}>
          <div className="animate-fade-in" style={{
            width: '100%',
            maxWidth: '520px',
            background: '#141a29',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8)',
            padding: '28px 24px',
            borderRadius: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
            boxSizing: 'border-box'
          }}>
            {/* Header & Step Indicator */}
            <div style={{ textAlign: 'center' }}>
              <img src="/logo.png" alt="tezer" style={{ width: '48px', height: '48px', marginBottom: '8px', filter: 'drop-shadow(0 0 12px rgba(0,245,212,0.5))' }} />
              <h2 style={{ fontSize: '22px', margin: '4px 0', background: 'linear-gradient(135deg, #fff 0%, var(--accent-cyan) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                Welcome to tezer!
              </h2>
              <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '4px 0 14px 0' }}>
                Step {onboardingStep} of 4: {
                  onboardingStep === 1 ? 'Mobile Number' :
                  onboardingStep === 2 ? 'Gender & Date of Birth' :
                  onboardingStep === 3 ? 'Country, Currencies & Timezone' : 'Review & Launch App'
                }
              </p>

              {/* Progress Bar */}
              <div style={{ width: '100%', height: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${(onboardingStep / 4) * 100}%`,
                  background: 'linear-gradient(90deg, var(--accent-cyan), var(--accent-purple))',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>

            {/* STEP 1: Mobile Number */}
            {onboardingStep === 1 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                    📱 Enter Your Mobile Number
                  </label>
                  <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0 0 12px 0' }}>
                    Used for WhatsApp bill settlement alerts and friend contact discovery.
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <select
                      value={onboardingCountryCode}
                      onChange={e => setOnboardingCountryCode(e.target.value)}
                      style={{ padding: '12px 8px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px', width: '120px' }}
                    >
                      {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                    {onboardingCountryCode === 'CUSTOM' && (
                      <input
                        type="text"
                        placeholder="+123"
                        value={onboardingCustomCc}
                        onChange={e => setOnboardingCustomCc(e.target.value)}
                        style={{ padding: '12px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px', width: '70px' }}
                      />
                    )}
                    <input
                      type="tel"
                      placeholder="50 123 4567"
                      value={onboardingPhoneDigits}
                      onChange={e => setOnboardingPhoneDigits(e.target.value)}
                      style={{ flex: 1, padding: '12px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '14px', minWidth: '120px' }}
                    />
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setOnboardingStep(2)}
                  className="btn-primary"
                  style={{ width: '100%', padding: '12px', fontSize: '14px', borderRadius: '12px', marginTop: '10px' }}
                >
                  Proceed to Personal Details ➔
                </button>
              </div>
            )}

            {/* STEP 2: Gender & DOB */}
            {onboardingStep === 2 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                    👤 Gender
                  </label>
                  <select
                    value={onboardingGender}
                    onChange={e => setOnboardingGender(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px', boxSizing: 'border-box' }}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '13px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                    🎂 Date of Birth
                  </label>
                  <input
                    type="date"
                    max={getMaxDob7YearsAgo()}
                    value={onboardingDob}
                    onChange={e => setOnboardingDob(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setOnboardingStep(1)}
                    className="btn-secondary"
                    style={{ flex: 1, padding: '12px', fontSize: '13px', borderRadius: '12px' }}
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setOnboardingStep(3)}
                    className="btn-primary"
                    style={{ flex: 2, padding: '12px', fontSize: '14px', borderRadius: '12px' }}
                  >
                    Proceed to Country & Currencies ➔
                  </button>
                </div>
              </div>
            )}

            {/* STEP 3: Country, Timezone, Selection of Multi-Currencies */}
            {onboardingStep === 3 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                    🌍 Select Your Current Country
                  </label>
                  <select
                    value={onboardingCountry}
                    onChange={e => {
                      const countryName = e.target.value;
                      setOnboardingCountry(countryName);
                      const preset = COUNTRY_PRESETS.find(p => p.country === countryName);
                      if (preset) {
                        setOnboardingCurrency(preset.currency);
                        setOnboardingTimezone(preset.timezone);
                        setOnboardingCurrencies(preset.defaultCurrencies);
                      }
                    }}
                    style={{ width: '100%', padding: '12px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px' }}
                  >
                    {COUNTRY_PRESETS.map((p, idx) => (
                      <option key={idx} value={p.country}>
                        {p.flag} {p.country} ({p.currency})
                      </option>
                    ))}
                    <option value="Other">🌍 Other / Custom Location</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px' }}>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                      💵 Primary Base Currency
                    </label>
                    <select
                      value={onboardingCurrency}
                      onChange={e => {
                        const newBase = e.target.value.toUpperCase();
                        setOnboardingCurrency(newBase);
                        if (!onboardingCurrencies.includes(newBase)) {
                          setOnboardingCurrencies([...onboardingCurrencies, newBase]);
                        }
                      }}
                      style={{ width: '100%', padding: '12px 8px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '13px' }}
                    >
                      {['AED', 'INR', 'USD', 'EUR', 'GBP', 'SAR', 'QAR', 'OMR', 'KWD', 'BHD', 'SGD', 'CAD', 'AUD', 'PKR', 'PHP'].map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                      🕒 Timezone
                    </label>
                    <select
                      value={onboardingTimezone}
                      onChange={e => setOnboardingTimezone(e.target.value)}
                      style={{ width: '100%', padding: '12px 8px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.05)', border: '1px solid var(--border-glass)', color: 'var(--text-main)', fontSize: '12px' }}
                    >
                      <option value="Asia/Dubai">GST (Dubai)</option>
                      <option value="Asia/Kolkata">IST (India)</option>
                      <option value="America/New_York">EST (NY)</option>
                      <option value="Europe/London">GMT (London)</option>
                      <option value="Asia/Riyadh">AST (Riyadh)</option>
                      <option value="Asia/Qatar">AST (Qatar)</option>
                      <option value="Asia/Muscat">GST (Muscat)</option>
                      <option value="Asia/Kuwait">AST (Kuwait)</option>
                      <option value="Asia/Singapore">SGT (Singapore)</option>
                      <option value="Europe/Paris">CET (Paris)</option>
                      <option value="Asia/Karachi">PKT (Pakistan)</option>
                      <option value="Asia/Manila">PHT (Philippines)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '6px' }}>
                    💱 Active Multi-Currencies
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {['AED', 'INR', 'USD', 'EUR', 'GBP', 'SAR', 'QAR', 'OMR', 'KWD', 'SGD', 'CAD', 'AUD'].map(c => {
                      const isSelected = onboardingCurrencies.includes(c);
                      return (
                        <button
                          type="button"
                          key={c}
                          onClick={() => {
                            if (isSelected) {
                              setOnboardingCurrencies(onboardingCurrencies.filter(item => item !== c));
                            } else {
                              setOnboardingCurrencies([...onboardingCurrencies, c]);
                            }
                          }}
                          style={{
                            padding: '6px 12px',
                            borderRadius: '16px',
                            border: isSelected ? '1px solid var(--accent-cyan)' : '1px solid var(--border-glass)',
                            background: isSelected ? 'rgba(0,245,212,0.15)' : 'rgba(255,255,255,0.03)',
                            color: isSelected ? 'var(--accent-cyan)' : 'var(--text-muted)',
                            fontSize: '11px',
                            fontWeight: isSelected ? '700' : '500',
                            cursor: 'pointer'
                          }}
                        >
                          {isSelected ? '✓ ' : '+ '}{c}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setOnboardingStep(2)}
                    className="btn-secondary"
                    style={{ flex: 1, padding: '12px', fontSize: '13px', borderRadius: '12px' }}
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setOnboardingStep(4)}
                    className="btn-primary"
                    style={{ flex: 2, padding: '12px', fontSize: '14px', borderRadius: '12px' }}
                  >
                    Review & Complete ➔
                  </button>
                </div>
              </div>
            )}

            {/* STEP 4: Launch */}
            {onboardingStep === 4 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '14px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <h4 style={{ margin: 0, fontSize: '14px', color: 'var(--accent-cyan)' }}>📋 Review Your Account Setup</h4>
                  
                  <div style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Mobile Number:</span>
                    <span style={{ fontWeight: 'bold' }}>{onboardingPhoneDigits ? `${onboardingCountryCode} ${onboardingPhoneDigits}` : 'Not specified'}</span>
                  </div>
                  <div style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Gender / DOB:</span>
                    <span style={{ fontWeight: 'bold' }}>{onboardingGender || 'N/A'} • {onboardingDob || 'N/A'}</span>
                  </div>
                  <div style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Country:</span>
                    <span style={{ fontWeight: 'bold' }}>{onboardingCountry}</span>
                  </div>
                  <div style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Primary Currency:</span>
                    <span style={{ fontWeight: 'bold' }}>{onboardingCurrency} ({onboardingTimezone})</span>
                  </div>
                  <div style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Active Currencies:</span>
                    <span style={{ fontWeight: 'bold' }}>{Array.from(new Set([onboardingCurrency, ...onboardingCurrencies])).join(', ')}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setOnboardingStep(3)}
                    className="btn-secondary"
                    disabled={onboardingLoading}
                    style={{ flex: 1, padding: '12px', fontSize: '13px', borderRadius: '12px' }}
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={handleCompleteOnboarding}
                    disabled={onboardingLoading}
                    className="btn-primary"
                    style={{ flex: 2, padding: '14px', fontSize: '15px', fontWeight: 'bold', background: 'linear-gradient(135deg, var(--accent-cyan) 0%, var(--accent-purple) 100%)', color: '#fff', borderRadius: '12px' }}
                  >
                    {onboardingLoading ? <ThinkingOrbLoader popup state="shaping" size={20} color="#fff" text="Setting Up..." /> : '🚀 Launch tezer'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* --- Persistent Quick Action Footer Buttons --- */}
      {activeTab === 'dashboard' && (
        <div className="quick-actions-bar">
          <button className="action-btn-minus" onClick={() => { setTxCategory(outflowCats[0]?.name || 'Food'); setTxIsShared(false); setActiveModal('outflow'); }}>
            <Minus size={20} /> Expense
          </button>
          <button className="action-btn-plus" onClick={() => { setTxCategory(inflowCats[0]?.name || 'Salary'); setTxIsShared(false); setActiveModal('inflow'); }}>
            <Plus size={20} /> Income
          </button>
        </div>
      )}

      {/* --- Footer Tab Navigation Bar --- */}
      <footer style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: '68px', backgroundColor: 'var(--bg-secondary)',
        borderTop: '1px solid var(--border-glass)', display: 'flex',
        justifyContent: 'space-around', alignItems: 'center', zIndex: 5
      }}>
        {/* 1. Feed */}
        <button
          onClick={() => setActiveTab('transactions_feed')}
          style={{
            background: 'transparent', border: 'none', color: activeTab === 'transactions_feed' ? 'var(--accent-cyan)' : 'var(--text-muted)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer'
          }}
        >
          <FileText size={18} />
          <span style={{ fontSize: '10px', fontWeight: activeTab === 'transactions_feed' ? '600' : '400' }}>Feed</span>
        </button>

        {/* 2. Cards & Banks (Switchable) */}
        <button
          onClick={handleCardsAndBanksTabClick}
          style={{
            background: 'transparent', border: 'none',
            color: (activeTab === 'accounts' || activeTab === 'credit_cards') ? 'var(--accent-cyan)' : 'var(--text-muted)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', cursor: 'pointer',
            position: 'relative', padding: '2px 4px'
          }}
          title={activeTab === 'accounts' ? 'Active: Bank Accounts (Tap to switch to Cards)' : activeTab === 'credit_cards' ? 'Active: Credit Cards (Tap to switch to Banks)' : 'Cards & Banks (Tap to switch)'}
        >
          {/* Switchable Dual Icon */}
          <div style={{
            position: 'relative',
            width: '28px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Bank Landmark Icon */}
            <Landmark
              size={15}
              style={{
                position: 'absolute',
                left: '1px',
                top: '1px',
                color: activeTab === 'accounts'
                  ? 'var(--accent-cyan)'
                  : (activeTab === 'credit_cards' ? 'rgba(255,255,255,0.25)' : 'var(--text-muted)'),
                transform: activeTab === 'accounts' ? 'scale(1.2)' : 'scale(0.85)',
                filter: activeTab === 'accounts' ? 'drop-shadow(0 0 6px rgba(0,245,212,0.5))' : 'none',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: activeTab === 'accounts' ? 2 : 1
              }}
            />

            {/* Credit Card Icon */}
            <CreditCard
              size={15}
              style={{
                position: 'absolute',
                right: '1px',
                bottom: '1px',
                color: activeTab === 'credit_cards'
                  ? 'var(--accent-purple)'
                  : (activeTab === 'accounts' ? 'rgba(255,255,255,0.25)' : 'var(--text-muted)'),
                transform: activeTab === 'credit_cards' ? 'scale(1.2)' : 'scale(0.85)',
                filter: activeTab === 'credit_cards' ? 'drop-shadow(0 0 6px rgba(192,132,252,0.5))' : 'none',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: activeTab === 'credit_cards' ? 2 : 1
              }}
            />

            {/* Switch / Swap Badge */}
            <div style={{
              position: 'absolute',
              top: '-3px',
              right: '-5px',
              background: (activeTab === 'accounts' || activeTab === 'credit_cards')
                ? (activeTab === 'credit_cards' ? 'rgba(123,44,191,0.35)' : 'rgba(0,245,212,0.25)')
                : 'rgba(255, 255, 255, 0.08)',
              border: `1px solid ${
                activeTab === 'credit_cards'
                  ? 'var(--accent-purple)'
                  : (activeTab === 'accounts' ? 'var(--accent-cyan)' : 'var(--border-glass)')
              }`,
              borderRadius: '6px',
              padding: '0px 2px',
              height: '11px',
              minWidth: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (activeTab === 'accounts' || activeTab === 'credit_cards') ? '0 0 6px rgba(0,245,212,0.2)' : 'none',
              transition: 'all 0.2s ease',
              zIndex: 3
            }}>
              <ArrowLeftRight
                size={7}
                style={{
                  color: activeTab === 'credit_cards'
                    ? 'var(--accent-purple)'
                    : (activeTab === 'accounts' ? 'var(--accent-cyan)' : 'var(--text-muted)'),
                  strokeWidth: 2.5
                }}
              />
            </div>
          </div>

          {/* Dynamic Switchable Label */}
          <span style={{
            fontSize: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            whiteSpace: 'nowrap',
            lineHeight: 1
          }}>
            <span style={{
              color: activeTab === 'credit_cards' ? 'var(--accent-purple)' : 'var(--text-muted)',
              fontWeight: activeTab === 'credit_cards' ? '700' : '400',
              fontSize: activeTab === 'credit_cards' ? '10.5px' : '9.5px',
              display: 'inline-block',
              transition: 'all 0.2s ease',
              textShadow: activeTab === 'credit_cards' ? '0 0 8px rgba(192, 132, 252, 0.4)' : 'none'
            }}>
              Cards
            </span>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '9px' }}>&</span>
            <span style={{
              color: activeTab === 'accounts' ? 'var(--accent-cyan)' : 'var(--text-muted)',
              fontWeight: activeTab === 'accounts' ? '700' : '400',
              fontSize: activeTab === 'accounts' ? '10.5px' : '9.5px',
              display: 'inline-block',
              transition: 'all 0.2s ease',
              textShadow: activeTab === 'accounts' ? '0 0 8px rgba(0, 245, 212, 0.4)' : 'none'
            }}>
              Banks
            </span>
          </span>
        </button>

        {/* 3. Dashboard (Center) */}
        <button
          onClick={() => setActiveTab('dashboard')}
          style={{
            background: 'transparent', border: 'none', color: activeTab === 'dashboard' ? 'var(--accent-cyan)' : 'var(--text-muted)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer'
          }}
        >
          <Plus size={18} />
          <span style={{ fontSize: '10px', fontWeight: activeTab === 'dashboard' ? '600' : '400' }}>Dashboard</span>
        </button>

        {/* 4. Friends & Groups (Switchable) */}
        <button
          onClick={handleFriendsAndGroupsTabClick}
          style={{
            background: 'transparent', border: 'none',
            color: (activeTab === 'friends' || activeTab === 'groups') ? 'var(--accent-cyan)' : 'var(--text-muted)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px', cursor: 'pointer',
            position: 'relative', padding: '2px 4px'
          }}
          title={activeTab === 'friends' ? 'Active: Friends (Tap to switch to Groups)' : activeTab === 'groups' ? 'Active: Groups (Tap to switch to Friends)' : 'Friends & Groups (Tap to switch)'}
        >
          {/* Switchable Dual Icon */}
          <div style={{
            position: 'relative',
            width: '28px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Friends Icon */}
            <UserPlus
              size={15}
              style={{
                position: 'absolute',
                left: '1px',
                top: '1px',
                color: activeTab === 'friends'
                  ? 'var(--accent-cyan)'
                  : (activeTab === 'groups' ? 'rgba(255,255,255,0.25)' : 'var(--text-muted)'),
                transform: activeTab === 'friends' ? 'scale(1.2)' : 'scale(0.85)',
                filter: activeTab === 'friends' ? 'drop-shadow(0 0 6px rgba(0,245,212,0.5))' : 'none',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: activeTab === 'friends' ? 2 : 1
              }}
            />

            {/* Groups Icon */}
            <Users
              size={15}
              style={{
                position: 'absolute',
                right: '1px',
                bottom: '1px',
                color: activeTab === 'groups'
                  ? 'var(--accent-cyan)'
                  : (activeTab === 'friends' ? 'rgba(255,255,255,0.25)' : 'var(--text-muted)'),
                transform: activeTab === 'groups' ? 'scale(1.2)' : 'scale(0.85)',
                filter: activeTab === 'groups' ? 'drop-shadow(0 0 6px rgba(0,245,212,0.5))' : 'none',
                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: activeTab === 'groups' ? 2 : 1
              }}
            />

            {/* Switch / Swap Badge */}
            <div style={{
              position: 'absolute',
              top: '-3px',
              right: '-5px',
              background: (activeTab === 'friends' || activeTab === 'groups')
                ? 'rgba(0,245,212,0.25)'
                : 'rgba(255, 255, 255, 0.08)',
              border: `1px solid ${
                (activeTab === 'friends' || activeTab === 'groups') ? 'var(--accent-cyan)' : 'var(--border-glass)'
              }`,
              borderRadius: '6px',
              padding: '0px 2px',
              height: '11px',
              minWidth: '13px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (activeTab === 'friends' || activeTab === 'groups') ? '0 0 6px rgba(0,245,212,0.2)' : 'none',
              transition: 'all 0.2s ease',
              zIndex: 3
            }}>
              <ArrowLeftRight
                size={7}
                style={{
                  color: (activeTab === 'friends' || activeTab === 'groups') ? 'var(--accent-cyan)' : 'var(--text-muted)',
                  strokeWidth: 2.5
                }}
              />
            </div>
          </div>

          {/* Dynamic Switchable Label */}
          <span style={{
            fontSize: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '2px',
            whiteSpace: 'nowrap',
            lineHeight: 1
          }}>
            <span style={{
              color: activeTab === 'friends' ? 'var(--accent-cyan)' : 'var(--text-muted)',
              fontWeight: activeTab === 'friends' ? '700' : '400',
              fontSize: activeTab === 'friends' ? '10.5px' : '9.5px',
              display: 'inline-block',
              transition: 'all 0.2s ease',
              textShadow: activeTab === 'friends' ? '0 0 8px rgba(0, 245, 212, 0.4)' : 'none'
            }}>
              Friends
            </span>
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '9px' }}>&</span>
            <span style={{
              color: activeTab === 'groups' ? 'var(--accent-cyan)' : 'var(--text-muted)',
              fontWeight: activeTab === 'groups' ? '700' : '400',
              fontSize: activeTab === 'groups' ? '10.5px' : '9.5px',
              display: 'inline-block',
              transition: 'all 0.2s ease',
              textShadow: activeTab === 'groups' ? '0 0 8px rgba(0, 245, 212, 0.4)' : 'none'
            }}>
              Groups
            </span>
          </span>
        </button>

        {/* 5. Transfers */}
        <button
          onClick={() => setActiveTab('transfers')}
          style={{
            background: 'transparent', border: 'none', color: activeTab === 'transfers' ? 'var(--accent-cyan)' : 'var(--text-muted)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', cursor: 'pointer'
          }}
        >
          <ArrowLeftRight size={18} />
          <span style={{ fontSize: '10px', fontWeight: activeTab === 'transfers' ? '600' : '400' }}>Transfers</span>
        </button>
      </footer>

      {/* --- TRANSACTION DETAILS MODAL --- */}
      {selectedTxDetail && (() => {
        const tx = selectedTxDetail;
        const isTransfer = isTransferTransaction(tx) || (tx.description && (tx.description.toLowerCase().includes('settlement') || tx.description.toLowerCase().includes('transfer')));
        const tfDetails = isTransfer ? getTransferDetails(tx) : null;
        const isInterTransfer = tfDetails?.isInterCurrency;
        const matchedCat = findCategoryByName(categories, tx.category_name);
        const isGlobalInflow = GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (tx.category_name || '').trim().toLowerCase());
        const isOut = !isTransfer && (matchedCat ? matchedCat.type === 'OUTFLOW' : !isGlobalInflow);
        
        const myUserId = localStorage.getItem('dhudd_user_id');
        const userEmail = localStorage.getItem('dhudd_email') || 'You';
        
        let payerName = getPayerName(tx);
        
        const matchedAccount = accounts.find(a => a.account_id === tx.paid_by_account_id);
        const targetAccount = accounts.find(a => a.account_id === tx.transfer_to_account_id);
        const isCcPayment = tx.description.toLowerCase().includes('credit card-bill payment') || tx.description.toLowerCase().includes('credit card payment:') || tx.description.toLowerCase().includes('card payment');

        const linkedTransactions = transactions.filter(t => 
          t.transaction_id !== tx.transaction_id && (
            (t.source_transaction_id === tx.transaction_id) || 
            (tx.source_transaction_id && t.transaction_id === tx.source_transaction_id) ||
            (tx.source_transaction_id && t.source_transaction_id === tx.source_transaction_id)
          )
        );

        return (
          <div 
            className="modal-backdrop modal-backdrop-blur modal-backdrop-elevated"
            onClick={(e) => { if (e.target === e.currentTarget) setSelectedTxDetail(null); }}
          >
            <div className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box' }}>
              <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '10px', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flex: 1 }}>
                  <span style={{ fontSize: '24px', flexShrink: 0 }}>{isCcPayment ? '💳' : (isTransfer ? '🔄' : (matchedCat?.symbol || '💸'))}</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <h2 style={{ fontSize: '18px', margin: 0, wordBreak: 'break-word', lineHeight: 1.25 }}>{tx.description}</h2>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                      {isCcPayment 
                        ? 'Credit Card Payment' 
                        : (isTransfer ? (isInterTransfer ? 'Inter-Currency Transfer' : 'Transfer') : (matchedCat?.name || tx.category_name))
                      } • {formatDateTime(tx.date)}
                    </span>
                  </div>
                </div>
                <button type="button" onClick={() => setSelectedTxDetail(null)} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontWeight: 'bold', flexShrink: 0, padding: '4px 6px' }}>Close</button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {isCcPayment ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Payment Type:</span>
                      <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--accent-purple)' }}>
                        Credit Card Payment
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Paid From:</span>
                      <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--color-outflow)' }}>
                        {isTransfer 
                          ? (matchedAccount ? `${matchedAccount.name} (${matchedAccount.currency})` : 'Unknown')
                          : payerName
                        }
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Card Credited:</span>
                      <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--color-inflow)' }}>
                        {isTransfer 
                          ? (targetAccount ? `${targetAccount.name} (${targetAccount.currency})` : 'Unknown')
                          : (matchedAccount ? `${matchedAccount.name} (${matchedAccount.currency})` : 'Unknown')
                        }
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Amount Paid:</span>
                      <span style={{ fontWeight: '700', fontSize: '14px', color: 'var(--color-inflow)' }}>
                        {formatMoney(tx.total_amount, tx.currency)}
                      </span>
                    </div>

                    {!isTransfer && (tx.is_shared === true || tx.is_shared === 1 || tx.is_shared === 'true') && tx.splits && (
                      <div style={{ marginTop: '10px' }}>
                        <h3 style={{ fontSize: '13px', marginBottom: '8px' }}>Debt Split Details</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '150px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-glass)' }}>
                          {tx.splits.map(split => {
                            const isSelf = split.member_user_id === myUserId;
                            return (
                              <div key={split.split_id} style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}>
                                <span>{isSelf ? 'You (Debt)' : resolveMemberDisplayName(split.member_user_id, split.display_name, split.email)}</span>
                                <span style={{ fontWeight: '600' }}>
                                  {formatMoney(split.owed_amount, tx.currency)}
                                  {(split.is_settled === true || split.is_settled === 1 || split.is_settled === 'true') && <span style={{ marginLeft: '6px', fontSize: '10px', color: 'var(--accent-cyan)', background: 'rgba(0,245,212,0.1)', padding: '1px 4px', borderRadius: '3px' }}>Settled</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                ) : isTransfer ? (() => {
                  const dir = tfDetails?.dir || getTransferDirection(tx, accounts);
                  const isInter = tfDetails?.isInterCurrency;

                  let transferBadge = '🔄 Transfer';
                  let badgeColor = 'var(--accent-cyan)';
                  if (dir === 'inward') {
                    transferBadge = '📥 Incoming Transfer';
                    badgeColor = 'var(--color-inflow)';
                  } else if (dir === 'outward') {
                    transferBadge = '📤 Outgoing Transfer';
                    badgeColor = 'var(--color-outflow)';
                  } else {
                    transferBadge = '🔄 Inter-Account Transfer';
                    badgeColor = 'var(--accent-cyan)';
                  }

                  return (
                    <>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Transfer Type:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontWeight: '600', fontSize: '13px', color: badgeColor }}>
                            {transferBadge}
                          </span>
                          {isInter && (
                            <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 6px', borderRadius: '4px', background: 'rgba(0,245,212,0.15)', color: 'var(--accent-cyan)', border: '1px solid rgba(0,245,212,0.3)' }}>
                              Inter-Currency
                            </span>
                          )}
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>From (Source):</span>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--color-outflow)' }}>
                            {tfDetails?.sAcc ? `🏦 ${tfDetails.sourceName} (${tfDetails.sourceCurrency})` : `🤝 ${tfDetails?.sourceName}`}
                          </div>
                          <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--color-outflow)', marginTop: '2px' }}>
                            - {formatMoney(tfDetails?.sourceAmount, tfDetails?.sourceCurrency)}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>To (Destination):</span>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: '600', fontSize: '13px', color: 'var(--color-inflow)' }}>
                            {tfDetails?.dAcc ? `🏦 ${tfDetails.targetName} (${tfDetails.targetCurrency})` : `🤝 ${tfDetails?.targetName}`}
                          </div>
                          <div style={{ fontWeight: '700', fontSize: '13px', color: 'var(--color-inflow)', marginTop: '2px' }}>
                            + {formatMoney(tfDetails?.targetAmount, tfDetails?.targetCurrency)}
                          </div>
                        </div>
                      </div>

                      {isInter && tfDetails?.exchangeRateStr && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                          <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Exchange Rate:</span>
                          <span style={{ fontWeight: '600', fontSize: '12px', color: 'var(--accent-cyan)' }}>
                            {tfDetails.exchangeRateStr}
                          </span>
                        </div>
                      )}
                    </>
                  );
                })() : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Payer:</span>
                      <span style={{ fontWeight: '600', fontSize: '13px' }}>{payerName}</span>
                    </div>

                    {matchedAccount && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Paid via Account:</span>
                        <span style={{ fontWeight: '600', fontSize: '13px' }}>{matchedAccount.name} ({matchedAccount.currency})</span>
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Total Amount:</span>
                      <span style={{ fontWeight: '700', fontSize: '14px', color: isOut ? 'var(--color-outflow)' : 'var(--color-inflow)' }}>
                        {formatMoney(tx.total_amount, tx.currency)}
                      </span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', paddingBottom: '8px' }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Your Share:</span>
                      <span style={{ fontWeight: '700', fontSize: '14px', color: isOut ? 'var(--color-outflow)' : 'var(--color-inflow)' }}>
                        {formatMoney(getDisplayAmount(tx).amount, tx.currency)}
                      </span>
                    </div>

                    {(tx.is_shared === true || tx.is_shared === 1 || tx.is_shared === 'true') && tx.splits && (
                      <div style={{ marginTop: '10px' }}>
                        <h3 style={{ fontSize: '13px', marginBottom: '8px' }}>Split Details ({tx.split_type || 'EQUALLY'})</h3>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '150px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-glass)' }}>
                          {tx.splits.map(split => {
                            const isSelf = split.member_user_id === myUserId;
                            return (
                              <div key={split.split_id} style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', fontSize: '12px' }}>
                                <span>{isSelf ? 'You' : resolveMemberDisplayName(split.member_user_id, split.display_name, split.email)}</span>
                                <span style={{ fontWeight: '600' }}>
                                  {formatMoney(split.owed_amount, tx.currency)}
                                  {(split.is_settled === true || split.is_settled === 1 || split.is_settled === 'true') && <span style={{ marginLeft: '6px', fontSize: '10px', color: 'var(--accent-cyan)', background: 'rgba(0,245,212,0.1)', padding: '1px 4px', borderRadius: '3px' }}>Settled</span>}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {linkedTransactions.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <h3 style={{ fontSize: '13px', marginBottom: '8px' }}>Linked Transactions</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '150px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-glass)' }}>
                      {linkedTransactions.map(ltx => (
                        <div 
                          key={ltx.transaction_id} 
                          style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', fontSize: '12px', cursor: 'pointer', padding: '6px', borderRadius: '4px', background: 'rgba(255,255,255,0.02)' }}
                          onClick={() => setSelectedTxDetail(ltx)}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.02)'}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0, flex: 1, marginRight: '8px' }}>
                            <span style={{ wordBreak: 'break-word', fontWeight: '600' }}>{ltx.description}</span>
                            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{formatDateTime(ltx.date)}</span>
                          </div>
                          <span style={{ fontWeight: '600', color: ltx.category_name === 'Bank/Card Charges' ? 'var(--color-outflow)' : 'inherit', display: 'flex', alignItems: 'center' }}>
                            {formatMoney(ltx.total_amount, ltx.currency)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px', borderTop: '1px solid var(--border-glass)', paddingTop: '12px' }}>
                    <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
                      <button
                        type="button"
                        onClick={() => {
                          handleStartEdit(tx);
                          setSelectedTxDetail(null);
                        }}
                        className="btn-secondary"
                        style={{ flex: 1, padding: '8px', fontSize: '12px', borderColor: 'var(--accent-cyan)', color: 'var(--accent-cyan)' }}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          handleDeleteTx(tx.transaction_id);
                          setSelectedTxDetail(null);
                        }}
                        className="btn-secondary"
                        style={{ flex: 1, padding: '8px', fontSize: '12px', borderColor: 'var(--color-outflow)', color: 'var(--color-outflow)' }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* --- EDIT TRANSACTION MODAL --- */}
      {editingTx && (() => {
        const myUserId = localStorage.getItem('dhudd_user_id') || '';
        const isTxTransferOrSettlement = editingTx && (isTransferTransaction(editingTx) || (editingTx.description && editingTx.description.toLowerCase().includes('settlement')));
        const transferDirection = isTxTransferOrSettlement ? getTransferDirection(editingTx, accounts) : 'none';
        
        // Inward transfer: received from someone else
        const isTxInwardTransfer = transferDirection === 'inward';

        // Outward friend transfer: sent to a friend
        const isTxOutwardFriendTransfer = transferDirection === 'outward';

        // Self transfer between own accounts
        const isTxSelfTransfer = transferDirection === 'self';

        return (
          <div className="modal-backdrop modal-backdrop-blur modal-backdrop-edit">
            <form
              onSubmit={handleUpdateTransaction}
              className="glass-panel modal-spring-enter modal-content-panel"
              style={{ display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box' }}
            >
              <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>Edit Transaction</h2>
                <button type="button" onClick={() => setEditingTx(null)} style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontWeight: 'bold' }}>Cancel</button>
              </div>

              <div>
                <label>Amount</label>
                <input
                  type="number"
                  step="0.0001"
                  placeholder="0.00"
                  value={editTxAmount}
                  onChange={e => setEditTxAmount(e.target.value)}
                  style={{ fontSize: '26px', textAlign: 'center', fontWeight: 'bold', borderColor: 'var(--accent-cyan)' }}
                  required
                  autoFocus
                />
              </div>

              <div>
                <label>Description</label>
                <div style={{ position: 'relative' }}>
                  <input 
                    type="text" 
                    placeholder="e.g. Starbucks, Taxi ride" 
                    value={editTxDesc} 
                    onChange={handleEditTxDescChange} 
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setDescSuggestions([]);
                    }}
                    onBlur={() => {
                      setTimeout(() => setDescSuggestions([]), 250);
                    }}
                    required 
                    autoComplete="off"
                  />
                  {descSuggestions.length > 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '44px',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        background: 'rgba(20, 24, 40, 0.97)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid var(--border-glass)',
                        borderRadius: '12px',
                        boxShadow: 'var(--shadow-premium)',
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        maxHeight: '190px',
                        overflowY: 'auto'
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '4px 8px 6px 8px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                        fontSize: '11px',
                        color: 'var(--text-muted)'
                      }}>
                        <span>💡 Suggestions</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDescSuggestions([]);
                          }}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            color: 'var(--text-main)',
                            borderRadius: '6px',
                            padding: '2px 8px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}
                        >
                          <X size={12} /> Close
                        </button>
                      </div>
                      {descSuggestions.map((sug, i) => (
                        <div
                          key={i}
                          onClick={() => {
                            setEditTxDesc(sug);
                            setDescSuggestions([]);
                            const foundCat = findCategoryByName(categories, editTxCategory);
                            const isOutflow = foundCat ? foundCat.type === 'OUTFLOW' : !GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (editTxCategory || '').trim().toLowerCase());
                            const predictedCat = getAutoCategory(sug, isOutflow ? 'OUTFLOW' : 'INFLOW');
                            if (predictedCat) {
                              setEditTxCategory(predictedCat);
                            }
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            fontSize: '13px',
                            color: 'var(--text-main)',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          {sug}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {!isTxTransferOrSettlement && (
                <div>
                  <label>Who Paid?</label>
                  <select 
                    value={editTxPayerType} 
                    onChange={e => {
                      const val = e.target.value;
                      setEditTxPayerType(val);
                      if (val === 'friend' || val === 'both') {
                        setEditTxIsShared(true);
                      }
                      if (val === 'friend') {
                        setEditTxMultiPayers(prev => {
                          const nw = { ...prev };
                          delete nw['self'];
                          return nw;
                        });
                      }
                      if (val === 'you') {
                        setEditTxMultiPayers({});
                      }
                    }}
                  >
                    <option value="you">You</option>
                    <option value="friend">Friend</option>
                    <option value="both">You and friend</option>
                  </select>
                </div>
              )}

                {(editTxPayerType === 'friend' || editTxPayerType === 'both') && !isTxTransferOrSettlement && (
                  <div style={{ marginTop: '10px' }}>
                    <label style={{ fontSize: '12px' }}>Select Friend(s)</label>
                    <select 
                      value=""
                      onChange={e => {
                        const val = e.target.value;
                        if (val) {
                          if (!editTxMultiPayers[val]) {
                            setEditTxMultiPayers(prev => {
                              const numCurrentPayers = Object.keys(prev).filter(k => k !== 'self').length;
                              let initialAmount = '';
                              if (editTxPayerType === 'friend' && numCurrentPayers === 0) {
                                initialAmount = editTxAmount || '';
                              }
                              return { ...prev, [val]: initialAmount };
                            });
                          }
                        }
                      }}
                    >
                      <option value="">-- Add Friend to Payers --</option>
                      {(() => {
                        const availableFriends = [...friends];
                        if (editingTx && editingTx.splits) {
                          editingTx.splits.forEach(s => {
                            if (s.member_user_id !== myUserId && !availableFriends.some(f => f.user_id === s.member_user_id)) {
                              availableFriends.push({
                                user_id: s.member_user_id,
                                display_name: s.display_name || s.email,
                                email: s.email || s.member_user_id
                              });
                            }
                          });
                        }
                        return sortFriendsAZ(availableFriends).map(f => (
                          <option key={f.user_id} value={f.user_id} disabled={editTxMultiPayers[f.user_id] !== undefined}>
                            {formatFriendLabel(f)}
                          </option>
                        ));
                      })()}
                    </select>
                  </div>
                )}

                {(editTxPayerType === 'friend' || editTxPayerType === 'both') && !isTxTransferOrSettlement && (
                  <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {editTxPayerType === 'both' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ flex: 1, fontSize: '13px' }}>You Paid:</span>
                        <input 
                          type="number" 
                          value={editTxMultiPayers['self'] !== undefined ? editTxMultiPayers['self'] : ''} 
                          onChange={e => setEditTxMultiPayers(prev => ({ ...prev, 'self': e.target.value }))}
                          placeholder="0.00"
                          style={{ width: '100px', padding: '6px' }}
                        />
                      </div>
                    )}
                    {Object.keys(editTxMultiPayers).map(uid => {
                      if (uid === 'self') return null;
                      const f = friends.find(x => x.user_id === uid);
                      const splitUser = editingTx?.splits?.find(s => s.member_user_id === uid);
                      const fName = f ? (f.nickname || f.display_name || (f.email ? f.email.split('@')[0] : 'Friend'))
                                    : (splitUser ? (splitUser.display_name || (splitUser.email ? splitUser.email.split('@')[0] : 'Friend')) : 'Friend');
                      return (
                        <div key={uid} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ flex: 1, fontSize: '13px' }}>{fName} Paid:</span>
                          <input 
                            type="number" 
                            value={editTxMultiPayers[uid] !== undefined ? editTxMultiPayers[uid] : ''} 
                            onChange={e => setEditTxMultiPayers(prev => ({ ...prev, [uid]: e.target.value }))}
                            placeholder="0.00"
                            style={{ width: '100px', padding: '6px' }}
                          />
                          <button 
                            type="button"
                            onClick={() => {
                              setEditTxMultiPayers(prev => {
                                const nw = { ...prev };
                                delete nw[uid];
                                return nw;
                              });
                            }}
                            style={{ background: 'none', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer' }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

            <div style={{ display: 'flex', gap: '10px' }}>
                {(editTxPayerType === 'you' || editTxPayerType === 'both' || isTxTransferOrSettlement) ? (
                  <div style={{ flex: 1 }}>
                    <label>Currency (Auto)</label>
                    <div className="read-only-badge" style={{
                      padding: '12px', background: 'rgba(255,255,255,0.05)',
                      border: '1px solid var(--border-glass)', borderRadius: '12px',
                      fontSize: '14px', fontWeight: 'bold', color: 'var(--accent-cyan)',
                      height: '48px', display: 'flex', alignItems: 'center'
                    }}>
                      {editTxCurrency}
                    </div>
                  </div>
                ) : (
                  <div style={{ flex: 1 }}>
                    <label>Currency</label>
                    <select value={editTxCurrency} onChange={e => setEditTxCurrency(e.target.value)}>
                      <option value="AED">AED (DH)</option>
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                    </select>
                  </div>
                )}

                {(editTxPayerType === 'you' || editTxPayerType === 'both' || isTxTransferOrSettlement) && (
                  <div style={{ flex: 1 }}>
                    {isTxInwardTransfer ? (
                      /* Inward Transfer: Only 1 selection: Credit the balance to */
                      <div>
                        <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                          <label style={{ margin: 0, color: 'var(--accent-cyan)' }}>🏦 Credit the balance to</label>
                          {(() => {
                            const selAcc = accounts.find(a => a.account_id === editTxTransferToAccountId);
                            if (!selAcc) return null;
                            const displayBal = selAcc.type === 'CREDIT_CARD' ? -parseFloat(selAcc.current_balance) : parseFloat(selAcc.current_balance);
                            return (
                              <span style={{ 
                                fontSize: '11px', 
                                background: 'rgba(46,213,115,0.15)',
                                border: '1px solid var(--border-glass)', 
                                padding: '2px 8px', 
                                borderRadius: '8px', 
                                color: 'var(--color-inflow)',
                                fontWeight: 'bold'
                              }}>
                                Balance: {formatMoney(displayBal, selAcc.currency)}
                              </span>
                            );
                          })()}
                        </div>
                        <CustomAccountSelect
                          value={editTxTransferToAccountId}
                          onChange={e => {
                            const val = e.target.value;
                            setEditTxTransferToAccountId(val);
                            const acc = accounts.find(a => a.account_id === val);
                            if (acc) setEditTxCurrency(acc.currency);
                          }}
                          accounts={accounts}
                          filter={a => !(a.name && a.name.includes('External Debts'))}
                          includeVirtualDebt={true}
                          virtualDebtLabel="🤝 Credit to Friend's Balance / Add to Debt"
                          placeholder="Select Account"
                          formatMoney={formatMoney}
                          required
                        />
                      </div>
                    ) : isTxOutwardFriendTransfer ? (
                      /* Outward Friend Transfer: Only 1 selection: Debit the balance from */
                      <div>
                        <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                          <label style={{ margin: 0, color: 'var(--accent-cyan)' }}>🏦 Debit the balance from</label>
                          {(() => {
                            const selAcc = accounts.find(a => a.account_id === editTxAccountId);
                            if (!selAcc) return null;
                            const displayBal = selAcc.type === 'CREDIT_CARD' ? -parseFloat(selAcc.current_balance) : parseFloat(selAcc.current_balance);
                            return (
                              <span style={{ 
                                fontSize: '11px', 
                                background: 'rgba(255,71,87,0.15)',
                                border: '1px solid var(--border-glass)', 
                                padding: '2px 8px', 
                                borderRadius: '8px', 
                                color: 'var(--color-outflow)',
                                fontWeight: 'bold'
                              }}>
                                Balance: {formatMoney(displayBal, selAcc.currency)}
                              </span>
                            );
                          })()}
                        </div>
                        <CustomAccountSelect
                          value={editTxAccountId}
                          onChange={e => {
                            const val = e.target.value;
                            setEditTxAccountId(val);
                            const acc = accounts.find(a => a.account_id === val);
                            if (acc) setEditTxCurrency(acc.currency);
                          }}
                          accounts={accounts}
                          filter={a => !(a.name && a.name.includes('External Debts'))}
                          includeVirtualDebt={true}
                          virtualDebtLabel="🤝 Debit from Friend's Balance / Add to Debt"
                          placeholder="Select Account"
                          formatMoney={formatMoney}
                          required
                        />
                      </div>
                    ) : (
                      /* Self transfer between own accounts OR standard expense/income */
                      <div>
                        <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                          <label style={{ margin: 0 }}>
                            {isTxSelfTransfer ? '🏦 Debit the balance from' : (() => {
                              const catObj = findCategoryByName(categories, editTxCategory);
                              const isInflow = catObj ? catObj.type === 'INFLOW' : GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (editTxCategory || '').trim().toLowerCase());
                              return isInflow ? 'Deposit Account' : 'Paid Account';
                            })()}
                          </label>
                          {!editTxUseSplitSources && (() => {
                            const selAcc = accounts.find(a => a.account_id === editTxAccountId);
                            if (!selAcc) return null;
                            
                            let displayBal = selAcc.type === 'CREDIT_CARD' ? -parseFloat(selAcc.current_balance) : parseFloat(selAcc.current_balance);
                            const originalTx = editingTx ? (transactions.find(t => t.transaction_id === editingTx.transaction_id) || editingTx) : null;
                            
                            const origAccId = originalTx ? (originalTx.paid_by_account_id || originalTx.account_id) : null;
                            if (originalTx && origAccId === selAcc.account_id) {
                                const origCatObj = findCategoryByName(categories, originalTx.category_name);
                                const isOrigInflow = origCatObj ? origCatObj.type === 'INFLOW' : GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (originalTx.category_name || '').trim().toLowerCase());
                                const isOriginalOutflow = !isOrigInflow;
                                const origAmount = parseFloat(originalTx.total_amount || originalTx.amount) || 0;
                                if (isOriginalOutflow) displayBal += origAmount;
                                else displayBal -= origAmount;
                            }
                            
                            const newCatObj = findCategoryByName(categories, editTxCategory);
                            const isNewInflow = newCatObj ? newCatObj.type === 'INFLOW' : GLOBAL_INFLOW_CATEGORIES.some(gic => gic.toLowerCase() === (editTxCategory || '').trim().toLowerCase());
                            const isNewOutflow = !isNewInflow;
                            let deduction = parseFloat(editTxAmount) || 0;
                            if (editTxPayerType === 'both') {
                              deduction = parseFloat(editTxMultiPayers['self']) || 0;
                            }
                            if (isNewOutflow) displayBal -= deduction;
                            else displayBal += deduction;

                            return (
                              <span style={{ 
                                fontSize: '11px', 
                                background: selAcc.type === 'CREDIT_CARD' ? 'rgba(123,44,191,0.2)' : 'rgba(46,213,115,0.15)',
                                border: '1px solid var(--border-glass)', 
                                padding: '2px 8px', 
                                borderRadius: '8px', 
                                color: displayBal >= 0 ? 'var(--color-inflow)' : 'var(--color-outflow)',
                                fontWeight: 'bold'
                              }}>
                                New balance: {formatMoney(displayBal, selAcc.currency)}
                              </span>
                            );
                          })()}
                        </div>
                        {!editTxUseSplitSources ? (
                        <CustomAccountSelect
                          value={editTxAccountId}
                          onChange={e => {
                            const val = e.target.value;
                            setEditTxAccountId(val);
                            const acc = accounts.find(a => a.account_id === val);
                            if (acc) setEditTxCurrency(acc.currency);
                          }}
                          accounts={accounts}
                          filter={a => !(a.name && a.name.includes('External Debts'))}
                          placeholder="Select Account"
                          formatMoney={formatMoney}
                          required
                        />
                        ) : (
                          <div className="read-only-badge" style={{
                            padding: '12px', background: 'rgba(255,255,255,0.05)',
                            border: '1px solid var(--border-glass)', borderRadius: '12px',
                            fontSize: '12px', color: 'var(--text-muted)',
                            height: '48px', display: 'flex', alignItems: 'center'
                          }}>
                            Multiple sources selected below
                          </div>
                        )}

                        {isTxSelfTransfer && (
                          <div style={{ marginTop: '10px' }}>
                            <label style={{ fontSize: '12px', display: 'block', marginBottom: '4px', color: 'var(--accent-cyan)' }}>🏦 Credit the balance to</label>
                            <CustomAccountSelect
                              value={editTxTransferToAccountId}
                              onChange={e => setEditTxTransferToAccountId(e.target.value)}
                              accounts={accounts}
                              filter={a => !(a.name && a.name.includes('External Debts'))}
                              placeholder="Select Target Account"
                              formatMoney={formatMoney}
                              required
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                {!isTxTransferOrSettlement && (
                  <div style={{ flex: 1 }}>
                    <label>Category</label>
                    <select value={editTxCategory} onChange={e => setEditTxCategory(e.target.value)}>
                      {editTxCategory && !categories.some(c => (c.name || '').trim().toLowerCase() === editTxCategory.trim().toLowerCase()) && (
                        <option value={editTxCategory}>{editTxCategory}</option>
                      )}
                      {categories
                        .filter(c => c.type === (findCategoryByName(categories, editingTx?.category_name || editTxCategory)?.type || 'OUTFLOW'))
                        .map(c => <option key={c.category_id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                )}

                <div style={{ flex: 1 }}>
                  <label>Date</label>
                  <input type="date" value={editTxDate} onChange={e => setEditTxDate(e.target.value)} required />
                </div>
              </div>

              {/* More Options Accordion */}
              <button
                type="button"
                onClick={() => setEditShowMoreTxOptions(!editShowMoreTxOptions)}
                style={{ background: 'none', border: 'none', color: 'var(--accent-cyan)', fontSize: '13px', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                {editShowMoreTxOptions ? '▼ Less options' : '▶ More options'}
              </button>

              {editShowMoreTxOptions && (
                <div className="animate-fade-in" style={{ marginTop: '10px', padding: '15px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-glass)', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '15px' }}>

                  {/* Split Sources */}
                  {(editTxPayerType === 'you' && !isTxTransferOrSettlement) && (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          id="editTxUseSplitSources"
                          checked={editTxUseSplitSources}
                          onChange={e => {
                            setEditTxUseSplitSources(e.target.checked);
                            if (e.target.checked) {
                              setEditSplitTxSources([{ accountId: editTxAccountId || accounts[0]?.account_id || '', amount: '' }]);
                            }
                          }}
                          style={{ width: 'auto', cursor: 'pointer' }}
                        />
                        <label htmlFor="editTxUseSplitSources" style={{ margin: 0, cursor: 'pointer', fontSize: '13px', color: 'var(--text-main)' }}>
                          Split payment across multiple source accounts
                        </label>
                      </div>
                      
                      {editTxUseSplitSources && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border-glass)', borderRadius: '12px', marginTop: '8px' }}>
                          <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Source Accounts</span>
                            <button
                              type="button"
                              onClick={() => {
                                const validAcc = accounts.find(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts')));
                                setEditSplitTxSources([...editSplitTxSources, { accountId: validAcc?.account_id || '', amount: '' }]);
                              }}
                              style={{ fontSize: '9px', padding: '2px 8px', background: 'rgba(0,245,212,0.15)', border: 'none', borderRadius: '4px', color: 'var(--accent-cyan)', cursor: 'pointer' }}
                            >
                              ➕ Add Account
                            </button>
                          </div>
                          {editSplitTxSources.map((src, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <CustomAccountSelect
                                value={src.accountId}
                                onChange={e => {
                                  const list = [...editSplitTxSources];
                                  list[idx].accountId = e.target.value;
                                  setEditSplitTxSources(list);
                                }}
                                accounts={accounts}
                                filter={a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts'))}
                                placeholder="Select Account"
                                formatMoney={formatMoney}
                                style={{ flex: 2, minHeight: '38px', padding: '6px 10px', fontSize: '12px' }}
                                required
                              />
                              <input
                                type="number"
                                step="0.01"
                                placeholder="Amount"
                                value={src.amount}
                                onChange={e => {
                                  const list = [...editSplitTxSources];
                                  list[idx].amount = e.target.value;
                                  setEditSplitTxSources(list);
                                }}
                                style={{ flex: 1, padding: '4px 6px', fontSize: '11px', minWidth: '60px' }}
                                required
                              />
                              {editSplitTxSources.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    const list = editSplitTxSources.filter((_, i) => i !== idx);
                                    setEditSplitTxSources(list);
                                  }}
                                  style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.7)', cursor: 'pointer', fontSize: '12px' }}
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          ))}
                          {(() => {
                            const targetVal = parseFloat(editTxAmount) || 0;
                            const allocatedVal = editSplitTxSources.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                            const remainingVal = targetVal - allocatedVal;
                            const remainingColor = remainingVal < 0 ? 'var(--color-outflow)' : remainingVal === 0 ? 'var(--color-inflow)' : 'var(--text-muted)';
                            return (
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', background: 'rgba(0,0,0,0.1)', padding: '6px 10px', borderRadius: '6px', marginTop: '4px', width: '100%' }}>
                                <span>Total: <strong style={{ color: 'var(--text-main)' }}>{formatMoney(targetVal, editTxCurrency)}</strong></span>
                                <span>Allocated: <strong style={{ color: 'var(--text-main)' }}>{formatMoney(allocatedVal, editTxCurrency)}</strong></span>
                                <span>Remaining: <strong style={{ color: remainingColor }}>{formatMoney(remainingVal, editTxCurrency)}</strong></span>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Recurring scheduling */}
                  <div style={{ borderTop: editTxUseSplitSources ? '1px solid var(--border-glass)' : 'none', paddingTop: editTxUseSplitSources ? '10px' : '0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px' }}>Auto-repeat transaction?</span>
                      <input type="checkbox" checked={editTxIsRecurring} onChange={e => setEditTxIsRecurring(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                    </div>
                    {editTxIsRecurring && (
                      <div className="animate-fade-in" style={{ marginTop: '8px' }}>
                        <label>Recurring Period</label>
                        <select value={editTxRecurringPeriod} onChange={e => setEditTxRecurringPeriod(e.target.value)}>
                          <option value="WEEK">Every Week</option>
                          <option value="TWO_WEEKS">Every Two Weeks</option>
                          <option value="FOUR_WEEKS">Every Four Weeks</option>
                          <option value="MONTH">Every Month</option>
                          <option value="THREE_MONTHS">Every Three Months</option>
                          <option value="SIX_MONTHS">Every Six Months</option>
                        </select>
                      </div>
                    )}
                  </div>

                  {/* Transaction Charges */}
                  {!isTxTransferOrSettlement && (
                    <div style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '10px' }}>
                      <label style={{ fontSize: '13px', display: 'block', marginBottom: '8px' }}>Add transaction charges</label>
                      <input 
                        type="number" 
                        step="0.01"
                        placeholder="Amount" 
                        value={editTxCharges} 
                        onChange={e => setEditTxCharges(e.target.value)} 
                        style={{ width: '100%', padding: '8px', fontSize: '12px' }} 
                      />
                      <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginTop: '4px' }}>This amount will be added as a separate expense linked to this transaction.</span>
                    </div>
                  )}

                </div>
              )}

              {/* Splits */}
              {!isTxTransferOrSettlement && (
                <div style={{ borderTop: '1px solid var(--border-glass)', paddingTop: '10px' }}>
                  <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontSize: '13px' }}>Split this bill?</span>
                    <input type="checkbox" checked={editTxIsShared} onChange={e => setEditTxIsShared(e.target.checked)} style={{ width: '18px', height: '18px' }} />
                  </div>
                {editTxIsShared && (
                  <div className="animate-fade-in" style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {/* Split Mode Selector */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                      <button 
                        type="button" 
                        onClick={() => setEditTxSplitTargetType('GROUP')} 
                        className={`btn-secondary ${editTxSplitTargetType === 'GROUP' ? 'active' : ''}`}
                        style={{ flex: 1, padding: '8px', fontSize: '11px', borderRadius: '8px', background: editTxSplitTargetType === 'GROUP' ? 'var(--accent-cyan)' : 'transparent', color: editTxSplitTargetType === 'GROUP' ? 'black' : 'var(--text-main)', border: '1px solid var(--border-glass)' }}
                      >
                        👥 Group Split
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setEditTxSplitTargetType('FRIENDS')} 
                        className={`btn-secondary ${editTxSplitTargetType === 'FRIENDS' ? 'active' : ''}`}
                        style={{ flex: 1, padding: '8px', fontSize: '11px', borderRadius: '8px', background: editTxSplitTargetType === 'FRIENDS' ? 'var(--accent-cyan)' : 'transparent', color: editTxSplitTargetType === 'FRIENDS' ? 'black' : 'var(--text-main)', border: '1px solid var(--border-glass)' }}
                      >
                        🤝 Friends Split
                      </button>
                    </div>

                    {/* Group Selector */}
                    {editTxSplitTargetType === 'GROUP' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <select 
                          value={editTxGroupId} 
                          onChange={e => {
                            const val = e.target.value;
                            setEditTxGroupId(val);
                            const g = groups.find(grp => grp.group_id === val);
                            if (g && g.members) {
                              setEditTxSplitGroupMembers(g.members.map(m => m.user_id));
                            } else {
                              setEditTxSplitGroupMembers([]);
                            }
                          }} 
                          required
                        >
                          <option value="">Select Group</option>
                          {groups.map(g => <option key={g.group_id} value={g.group_id}>{g.group_name}</option>)}
                        </select>

                        {editTxGroupId && (() => {
                          const g = groups.find(grp => grp.group_id === editTxGroupId);
                          if (!g || !g.members) return null;
                          const memberOptions = g.members.map(m => {
                            const isSelf = m.user_id === myUserId;
                            return {
                              id: m.user_id,
                              name: `${m.display_name || m.email.split('@')[0]}${isSelf ? ' (You)' : ''}`
                            };
                          });
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Select group members to split with:</span>
                              <MultiSelectDropdown
                                options={memberOptions}
                                selectedIds={editTxSplitGroupMembers}
                                onChange={setEditTxSplitGroupMembers}
                                placeholder="Select group members..."
                                renderExtra={(id) => id !== myUserId ? renderFriendBalanceTiles(id) : null}
                              />
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      /* Ad-hoc Friends Selector */
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Select friends to split with:</span>
                        {friends.length === 0 ? (
                          <p style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center', padding: '10px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>No friends added yet. Add them in the Friends tab.</p>
                        ) : (
                          <MultiSelectDropdown
                            options={sortFriendsAZ(friends).map(f => ({
                              id: f.user_id,
                              name: formatFriendLabel(f)
                            }))}
                            selectedIds={editTxSplitFriends}
                            onChange={setEditTxSplitFriends}
                            placeholder="Select friends to split with..."
                            renderExtra={renderFriendBalanceTiles}
                          />
                        )}
                      </div>
                    )}

                    {/* Split Type Selector and Breakdown */}
                    {((editTxSplitTargetType === 'GROUP' && editTxGroupId) || (editTxSplitTargetType === 'FRIENDS' && editTxSplitFriends.length > 0)) && (
                      <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '4px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <label style={{ fontSize: '12px', margin: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' }}>
                            Split Method:
                            {renderInfoTooltip("Choose how the expense is divided:\n• Split Equally: Divides amount evenly.\n• Custom Exact amounts: Assign fixed amounts to each.\n• Percentage Weighting: Split based on % weights (total 100%).\n• Ratio shares: Split using ratio numbers (e.g. 2 shares to 1).")}
                          </label>
                          <select value={editTxSplitType} onChange={e => setEditTxSplitType(e.target.value)} style={{ padding: '6px', fontSize: '12px' }}>
                            <option value="EQUALLY">Split Equally</option>
                            <option value="EXACT">Custom Exact amounts</option>
                            <option value="PERCENT">Percentage Weighting</option>
                            <option value="SHARES">Ratio shares (e.g. 2:1)</option>
                          </select>
                        </div>

                        {/* Real-time calculated split inputs list */}
                        <div className="glass-panel" style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px', background: 'rgba(0,0,0,0.15)' }}>
                          <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--accent-cyan)' }}>Split Calculations (Real-time)</span>
                          {getEditSplitMembers().map(m => {
                            const calcSplit = getEditRealTimeCalculatedSplits().find(s => s.user_id === m.user_id);
                            const owedVal = calcSplit ? calcSplit.owed_amount : 0.0;
                            const isSelf = m.user_id === myUserId;
                            
                            return (
                              <div key={m.user_id} style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '240px' }}>
                                  {m.display_name || m.email.split('@')[0]} {isSelf ? '(You)' : ''}
                                  {!isSelf && renderFriendBalanceTiles(m.user_id)}
                                </span>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {editTxSplitType === 'EXACT' && (
                                    <input 
                                      type="number" 
                                      step="0.01" 
                                      placeholder="0.00" 
                                      value={editTxExactAmounts[m.user_id] !== undefined ? editTxExactAmounts[m.user_id] : ''} 
                                      onChange={e => setEditTxExactAmounts({ ...editTxExactAmounts, [m.user_id]: parseFloat(e.target.value) || 0.0 })}
                                      style={{ width: '70px', padding: '4px', textAlign: 'right', fontSize: '11px', borderRadius: '4px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', color: 'white' }}
                                      required
                                    />
                                  )}
                                  {editTxSplitType === 'PERCENT' && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      <input 
                                        type="number" 
                                        step="1" 
                                        placeholder="0" 
                                        value={editTxPercentages[m.user_id] !== undefined ? editTxPercentages[m.user_id] : ''} 
                                        onChange={e => setEditTxPercentages({ ...editTxPercentages, [m.user_id]: parseFloat(e.target.value) || 0.0 })}
                                        style={{ width: '50px', padding: '4px', textAlign: 'right', fontSize: '11px', borderRadius: '4px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', color: 'white' }}
                                        required
                                      />
                                      <span>%</span>
                                    </div>
                                  )}
                                  {editTxSplitType === 'SHARES' && (
                                    <input 
                                      type="number" 
                                      step="1" 
                                      min="0"
                                      placeholder="1" 
                                      value={editTxShares[m.user_id] !== undefined ? editTxShares[m.user_id] : ''} 
                                      onChange={e => setEditTxShares({ ...editTxShares, [m.user_id]: parseFloat(e.target.value) || 0.0 })}
                                      style={{ width: '50px', padding: '4px', textAlign: 'right', fontSize: '11px', borderRadius: '4px', background: 'var(--bg-input)', border: '1px solid var(--border-glass)', color: 'white' }}
                                      required
                                    />
                                  )}
                                  <span style={{ fontWeight: '700', minWidth: '60px', textAlign: 'right' }}>
                                    {formatMoney(owedVal, editTxCurrency)}
                                  </span>
                                </div>
                              </div>
                            );
                          })}

                          {/* Live split validation message */}
                          {(() => {
                            const amt = parseFloat(editTxAmount) || 0;
                            if (editTxSplitType === 'EXACT') {
                              const sum = getEditRealTimeCalculatedSplits().reduce((acc, m) => acc + m.owed_amount, 0);
                              if (Math.abs(sum - amt) > 0.01) {
                                return (
                                  <div style={{ color: 'var(--color-outflow)', fontSize: '11px', textAlign: 'right', marginTop: '4px', fontWeight: 'bold' }}>
                                    ⚠️ Sum ({sum.toFixed(2)}) must equal Total ({amt.toFixed(2)})
                                  </div>
                                );
                              }
                            }
                            if (editTxSplitType === 'PERCENT') {
                              const sumPct = getEditRealTimeCalculatedSplits().reduce((acc, m) => acc + (m.percent || 0), 0);
                              if (Math.abs(sumPct - 100) > 0.01) {
                                  return (
                                    <div style={{ color: 'var(--color-outflow)', fontSize: '11px', textAlign: 'right', marginTop: '4px', fontWeight: 'bold' }}>
                                      ⚠️ Sum ({sumPct}%) must equal 100%
                                    </div>
                                  );
                              }
                            }
                            if (editTxSplitType === 'SHARES') {
                              const totalShares = getEditSplitMembers().reduce((acc, m) => acc + (editTxShares[m.user_id] !== undefined ? editTxShares[m.user_id] : 1), 0);
                              if (totalShares <= 0) {
                                return (
                                  <div style={{ color: 'var(--color-outflow)', fontSize: '11px', textAlign: 'right', marginTop: '4px', fontWeight: 'bold' }}>
                                    ⚠️ Total shares must be &gt; 0
                                  </div>
                                );
                              }
                            }
                            return null;
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}

              <button type="submit" className="btn-primary" disabled={isSubmittingTx} style={{ background: 'var(--accent-cyan)', color: 'black' }}>
                {isSubmittingTx ? <ThinkingOrbLoader popup state="working" size={20} color="#000" text="Saving Changes..." /> : 'Save Changes'}
              </button>
            </form>
          </div>
        );
      })()}

      {/* --- CREDIT CARD BILL PAYMENT MODAL --- */}
      {payCcModalOpen && payCcCard && (
        <div className="modal-backdrop modal-backdrop-blur">
          <form onSubmit={handlePayCreditCard} className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '16px', margin: 0, color: 'var(--accent-purple)' }}>Pay Credit Card Bill</h3>
              <button
                type="button"
                onClick={() => {
                  setPayCcModalOpen(false);
                  setPayCcCard(null);
                  setPayCcSourceType('ACCOUNT');
                  setPayCcSourceFriendId('');
                  setPayCcSourceAccountId('');
                }}
                style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontSize: '12px' }}
              >
                Cancel
              </button>
            </div>

            <p style={{ fontSize: '13px', margin: 0, lineHeight: '1.4' }}>
              Record a payment towards <strong>{payCcCard.name}</strong> card balance (Card balance: <strong>{formatMoney(-parseFloat(payCcCard.current_balance), payCcCard.currency)}</strong>).
            </p>

            {!ccUseSplitSources ? (
              <>
                <div style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.05)', padding: '4px', borderRadius: '8px', border: '1px solid var(--border-glass)', gap: '4px' }}>
                  <button
                    type="button"
                    onClick={() => { setPayCcSourceType('ACCOUNT'); setPayCcSourceFriendId(''); }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      background: payCcSourceType === 'ACCOUNT' ? 'var(--accent-purple)' : 'transparent',
                      color: payCcSourceType === 'ACCOUNT' ? 'white' : 'var(--text-muted)'
                    }}
                  >
                    🏦 Bank Account
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPayCcSourceType('FRIEND'); setPayCcSourceAccountId(''); }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      background: payCcSourceType === 'FRIEND' ? 'var(--accent-purple)' : 'transparent',
                      color: payCcSourceType === 'FRIEND' ? 'white' : 'var(--text-muted)'
                    }}
                  >
                    🤝 Friends
                  </button>
                </div>

                {payCcSourceType === 'ACCOUNT' ? (
                  <div>
                    <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <label style={{ margin: 0 }}>Select Bank/Cash Account to Pay From</label>
                      {(() => {
                        const selAcc = accounts.find(a => a.account_id === payCcSourceAccountId);
                        if (!selAcc) return null;
                        return (
                          <span style={{ 
                            fontSize: '11px', 
                            background: 'rgba(0,245,212,0.15)',
                            border: '1px solid var(--border-glass)', 
                            padding: '2px 8px', 
                            borderRadius: '8px', 
                            color: 'var(--accent-cyan)',
                            fontWeight: 'bold'
                          }}>
                            Balance: {formatMoney(selAcc.current_balance, selAcc.currency)}
                          </span>
                        );
                      })()}
                    </div>
                    <CustomAccountSelect
                      value={payCcSourceAccountId}
                      onChange={e => setPayCcSourceAccountId(e.target.value)}
                      accounts={accounts}
                      filter={a => a.type !== 'CREDIT_CARD' && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts'))}
                      placeholder="Select Account"
                      formatMoney={formatMoney}
                      required
                    />
                  </div>
                ) : (
                  <div>
                    <label style={{ margin: 0, display: 'block', marginBottom: '6px' }}>Select Friend Who Paid</label>
                    {friends.length === 0 ? (
                      <p style={{ fontSize: '12px', color: 'var(--color-outflow)', margin: '8px 0 0 0', fontStyle: 'italic' }}>
                        No friends added yet. Please add friends in the Friends tab first.
                      </p>
                    ) : (
                      <select
                        value={payCcSourceFriendId}
                        onChange={e => setPayCcSourceFriendId(e.target.value)}
                        style={{ width: '100%', padding: '8px', fontSize: '12px' }}
                        required
                      >
                        <option value="">Select Friend</option>
                        {sortFriendsAZ(friends).map(friend => (
                          <option key={friend.user_id} value={friend.user_id}>
                            {formatFriendLabel(friend)}
                          </option>
                        ))}
                      </select>
                    )}
                    {payCcSourceFriendId && (
                      <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Friend Balance:</span>
                        {renderFriendBalanceTiles(payCcSourceFriendId)}
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="read-only-badge" style={{
                padding: '12px', background: 'rgba(255,255,255,0.05)',
                border: '1px solid var(--border-glass)', borderRadius: '12px',
                fontSize: '12px', color: 'var(--text-muted)',
                display: 'flex', alignItems: 'center'
              }}>
                Multiple payment source accounts configured below
              </div>
            )}

            <div>
              <label style={{ display: 'block', marginBottom: '6px' }}>Payment Amount ({payCcCard.currency})</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={payCcAmount}
                onChange={e => setPayCcAmount(e.target.value)}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '6px' }}>Payment Date</label>
              <input
                type="date"
                value={payCcDate}
                onChange={e => setPayCcDate(e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '13px' }}
                required
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
              <input
                type="checkbox"
                id="ccUseSplitSources"
                checked={ccUseSplitSources}
                onChange={e => {
                  setCcUseSplitSources(e.target.checked);
                  if (e.target.checked) {
                    setSplitCcSources([{ sourceType: 'ACCOUNT', sourceId: accounts.find(a => a.type !== 'CREDIT_CARD')?.account_id || '', amount: '' }]);
                  }
                }}
                style={{ width: 'auto', cursor: 'pointer' }}
              />
              <label htmlFor="ccUseSplitSources" style={{ margin: 0, cursor: 'pointer', fontSize: '11px', color: 'var(--text-main)' }}>
                Split payment across multiple source accounts
              </label>
            </div>

            {ccUseSplitSources && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '10px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border-glass)', borderRadius: '12px' }}>
                <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', fontWeight: 'bold' }}>Split Sources</span>
                  <button
                    type="button"
                    onClick={() => setSplitCcSources([...splitCcSources, { sourceType: 'ACCOUNT', sourceId: accounts.find(a => a.type !== 'CREDIT_CARD')?.account_id || '', amount: '' }])}
                    style={{ fontSize: '9px', padding: '2px 8px', background: 'rgba(0,245,212,0.15)', border: 'none', borderRadius: '4px', color: 'var(--accent-cyan)', cursor: 'pointer' }}
                  >
                    ➕ Add Source
                  </button>
                </div>
                {splitCcSources.map((src, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <select
                      value={src.sourceType}
                      onChange={e => {
                        const list = [...splitCcSources];
                        list[idx].sourceType = e.target.value;
                        if (e.target.value === 'ACCOUNT') {
                          list[idx].sourceId = accounts.find(a => a.type !== 'CREDIT_CARD')?.account_id || '';
                        } else if (e.target.value === 'FRIEND') {
                          list[idx].sourceId = friends[0]?.user_id || '';
                        }
                        setSplitCcSources(list);
                      }}
                      style={{ flex: 1, padding: '4px 6px', fontSize: '10px' }}
                      required
                    >
                      <option value="ACCOUNT">Bank/Cash</option>
                      <option value="FRIEND">Friend</option>
                    </select>
                    
                    {src.sourceType === 'ACCOUNT' ? (
                      <CustomAccountSelect
                        value={src.sourceId}
                        onChange={e => {
                          const list = [...splitCcSources];
                          list[idx].sourceId = e.target.value;
                          setSplitCcSources(list);
                        }}
                        accounts={accounts}
                        filter={a => a.type !== 'CREDIT_CARD' && a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts'))}
                        placeholder="Select Source Account"
                        formatMoney={formatMoney}
                        style={{ flex: 1.5, minHeight: '38px', padding: '6px 10px', fontSize: '12px' }}
                        required
                      />
                    ) : (
                      <select
                        value={src.sourceId}
                        onChange={e => {
                          const list = [...splitCcSources];
                          list[idx].sourceId = e.target.value;
                          setSplitCcSources(list);
                        }}
                        style={{ flex: 1.5, padding: '4px 6px', fontSize: '10px' }}
                        required
                      >
                        <option value="" disabled>Select Friend</option>
                        {sortFriendsAZ(friends).map(f => (
                          <option key={f.user_id} value={f.user_id}>{formatFriendLabel(f)}</option>
                        ))}
                      </select>
                    )}

                    <input
                      type="number"
                      step="0.01"
                      placeholder="Amount"
                      value={src.amount}
                      onChange={e => {
                        const list = [...splitCcSources];
                        list[idx].amount = e.target.value;
                        setSplitCcSources(list);
                      }}
                      style={{ width: '60px', padding: '4px 6px', fontSize: '10px' }}
                      required
                    />

                    {splitCcSources.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const list = splitCcSources.filter((_, i) => i !== idx);
                          setSplitCcSources(list);
                        }}
                        style={{ background: 'transparent', border: 'none', color: 'rgba(255,71,87,0.7)', cursor: 'pointer', fontSize: '12px' }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                {(() => {
                  const targetVal = parseFloat(payCcAmount) || 0;
                  const allocatedVal = splitCcSources.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
                  const remainingVal = targetVal - allocatedVal;
                  const remainingColor = remainingVal < 0 ? 'var(--color-outflow)' : remainingVal === 0 ? 'var(--color-inflow)' : 'var(--text-muted)';
                  return (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: 'var(--text-muted)', background: 'rgba(0,0,0,0.1)', padding: '6px 10px', borderRadius: '6px', marginTop: '4px', width: '100%' }}>
                      <span>Total: <strong style={{ color: 'var(--text-main)' }}>{formatMoney(targetVal, payCcCard.currency)}</strong></span>
                      <span>Allocated: <strong style={{ color: 'var(--text-main)' }}>{formatMoney(allocatedVal, payCcCard.currency)}</strong></span>
                      <span>Remaining: <strong style={{ color: remainingColor }}>{formatMoney(remainingVal, payCcCard.currency)}</strong></span>
                    </div>
                  );
                })()}
              </div>
            )}

            <button type="submit" className="btn-primary" style={{ padding: '10px', background: 'var(--accent-purple)', color: 'white' }}>
              Confirm CC Payment
            </button>
          </form>
        </div>
      )}

      {/* --- GROUP SETTLE CONFIRMATION MODAL --- */}
      {groupSettleOpen && groupSettleTransfer && (
        <div className="modal-backdrop modal-backdrop-blur">
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box' }}>
            <h3 style={{ fontSize: '16px', margin: 0, color: 'var(--accent-cyan)' }}>Confirm Group Settlement</h3>
            
            <p style={{ fontSize: '13px', margin: 0, lineHeight: '1.4' }}>
              You are recording a payment of <strong>{formatMoney(groupSettleTransfer.amount)}</strong> from <strong>{groupSettleTransfer.from_email.split('@')[0]}</strong> to <strong>{groupSettleTransfer.to_email.split('@')[0]}</strong>.
            </p>
            
            <div>
              <label style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>Reflect to Bank/Card Account (optional)</label>
              <select
                value={groupSettleAccountId}
                onChange={e => setGroupSettleAccountId(e.target.value)}
                style={{ width: '100%', padding: '8px', fontSize: '12px' }}
              >
                <option value="">Select Account</option>
                {accounts.filter(a => a.is_active !== false && a.is_active !== 0 && a.is_active !== '0' && !(a.name && a.name.includes('External Debts'))).map(acc => (
                  <option key={acc.account_id} value={acc.account_id}>{acc.name} ({acc.currency})</option>
                ))}
              </select>
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginTop: '4px' }}>
                If selected, this will automatically update your account balance.
              </span>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button
                onClick={async () => {
                  await handleSettleGroupDebt(groupSettleTransfer, groupSettleAccountId);
                  setGroupSettleOpen(false);
                  setGroupSettleTransfer(null);
                  setGroupSettleAccountId('');
                }}
                className="btn-primary"
                style={{ flex: 1, padding: '8px 12px', fontSize: '12px', height: 'auto' }}
              >
                Confirm
              </button>
              <button
                onClick={() => {
                  setGroupSettleOpen(false);
                  setGroupSettleTransfer(null);
                  setGroupSettleAccountId('');
                }}
                className="btn-secondary"
                style={{ flex: 1, padding: '8px 12px', fontSize: '12px', height: 'auto' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- CUSTOM TRANSACTION & SUMMARY EXPORT MODAL --- */}
      {exportModalOpen && (exportTargetData || exportTargetType.endsWith('_summary')) && (
        <div className="modal-backdrop modal-backdrop-blur">
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '16px', margin: 0, color: 'var(--accent-cyan)' }}>
                {exportTargetType.endsWith('_summary') ? 'Export Summary Report' : 'Export Transactions'}
              </h3>
              <button
                type="button"
                onClick={() => setExportModalOpen(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--color-outflow)', cursor: 'pointer', fontSize: '12px' }}
              >
                Cancel
              </button>
            </div>

            <p style={{ fontSize: '12px', margin: 0, color: 'var(--text-muted)' }}>
              Choose details and format for generating your report.
            </p>

            {/* Target Info */}
            <div style={{ background: 'rgba(255, 255, 255, 0.02)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-glass)', fontSize: '13px' }}>
              <strong>Scope:</strong> {
                exportTargetType === 'accounts_summary' ? 'All Cash & Bank Accounts Summary' :
                exportTargetType === 'credit_cards_summary' ? 'All Credit Cards Summary' :
                exportTargetType === 'friends_summary' ? 'All Friends & Ledger Balances Summary' :
                exportTargetType === 'groups_summary' ? 'All Shared Groups Summary' :
                exportTargetType === 'group' ? `Group: ${exportTargetData?.group_name || 'Group'}` :
                exportTargetType === 'friend' ? `Friend: ${exportTargetData?.nickname || exportTargetData?.display_name || exportTargetData?.email || 'Friend'}` :
                `${exportTargetData?.name || 'Account'} (${exportTargetData?.type === 'CREDIT_CARD' ? 'Credit Card' : 'Cash/Bank Account'})`
              }
            </div>

            {/* Format Selection */}
            <div>
              <label style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>Export Format</label>
              <div style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.05)', padding: '4px', borderRadius: '8px', border: '1px solid var(--border-glass)', gap: '4px' }}>
                <button
                  type="button"
                  onClick={() => setExportFormat('pdf')}
                  className={exportFormat === 'pdf' ? 'btn-primary' : 'btn-secondary'}
                  style={{ flex: 1, padding: '6px 12px', fontSize: '12px', height: 'auto', border: 'none', background: exportFormat === 'pdf' ? 'var(--accent-cyan)' : 'transparent', color: exportFormat === 'pdf' ? 'black' : 'var(--text-main)', cursor: 'pointer' }}
                >
                  📄 PDF Document
                </button>
                <button
                  type="button"
                  onClick={() => setExportFormat('excel')}
                  className={exportFormat === 'excel' ? 'btn-primary' : 'btn-secondary'}
                  style={{ flex: 1, padding: '6px 12px', fontSize: '12px', height: 'auto', border: 'none', background: exportFormat === 'excel' ? 'var(--accent-cyan)' : 'transparent', color: exportFormat === 'excel' ? 'black' : 'var(--text-main)', cursor: 'pointer' }}
                >
                  📊 Excel (CSV)
                </button>
              </div>
            </div>

            {/* Period & Limit Selection (Only for Transaction Exports) */}
            {!exportTargetType.endsWith('_summary') && (
              <>
                {/* Period Selection */}
                <div>
                  <label style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>Select Period</label>
                  <select
                    value={exportPeriod}
                    onChange={e => setExportPeriod(e.target.value)}
                    style={{ width: '100%', padding: '10px', fontSize: '13px' }}
                  >
                    <option value="all">All Time</option>
                    <option value="this_month">This Month</option>
                    <option value="last_30">Last 30 Days</option>
                    <option value="custom">Custom Date Range</option>
                  </select>
                </div>

                {/* Custom Date Range Fields */}
                {exportPeriod === 'custom' && (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>Start Date</label>
                      <input
                        type="date"
                        value={exportStartDate}
                        onChange={e => setExportStartDate(e.target.value)}
                        required
                        style={{ width: '100%', padding: '10px', fontSize: '13px' }}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>End Date</label>
                      <input
                        type="date"
                        value={exportEndDate}
                        onChange={e => setExportEndDate(e.target.value)}
                        required
                        style={{ width: '100%', padding: '10px', fontSize: '13px' }}
                      />
                    </div>
                  </div>
                )}

                {/* Record Limit Selection */}
                <div>
                  <label style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>Record Limit</label>
                  <select
                    value={exportLimitType}
                    onChange={e => setExportLimitType(e.target.value)}
                    style={{ width: '100%', padding: '10px', fontSize: '13px' }}
                  >
                    <option value="all">All Records</option>
                    <option value="10">Last 10 Records</option>
                    <option value="20">Last 20 Records</option>
                    <option value="50">Last 50 Records</option>
                    <option value="custom">Custom Maximum</option>
                  </select>
                </div>

                {/* Custom Limit Field */}
                {exportLimitType === 'custom' && (
                  <div>
                    <label style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>Maximum Records Count</label>
                    <input
                      type="number"
                      min="1"
                      placeholder="e.g. 150"
                      value={exportLimitValue}
                      onChange={e => setExportLimitValue(e.target.value)}
                      required
                      style={{ width: '100%', padding: '10px', fontSize: '13px' }}
                    />
                  </div>
                )}
              </>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
              <button
                type="button"
                onClick={handleExecuteEntityExport}
                className="btn-primary"
                style={{ flex: 1, padding: '10px', fontSize: '13px', background: 'var(--accent-cyan)', color: 'black', fontWeight: 'bold' }}
              >
                Generate Export
              </button>
              <button
                type="button"
                onClick={() => setExportModalOpen(false)}
                className="btn-secondary"
                style={{ flex: 1, padding: '10px', fontSize: '13px' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- CATEGORY TRANSACTIONS DRILL-DOWN MODAL --- */}
      {drillDownCategoryData && (
        <div 
          className="modal-backdrop modal-backdrop-blur"
          onClick={(e) => { if (e.target === e.currentTarget) setDrillDownCategoryData(null); }}
        >
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box' }}>
            {/* Modal Header */}
            <div style={{ display: 'flex', justifyItems: 'center', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{
                  fontSize: '28px',
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: `${drillDownCategoryData.color}22`,
                  border: `1.5px solid ${drillDownCategoryData.color}55`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {drillDownCategoryData.icon}
                </span>
                <div>
                  <h2 style={{ fontSize: '18px', margin: 0 }}>{drillDownCategoryData.categoryName}</h2>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    {drillDownCategoryData.type === 'outflow' ? 'Expense' : 'Income'} Category
                  </span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{
                  fontSize: '18px',
                  fontWeight: '800',
                  color: drillDownCategoryData.type === 'outflow' ? 'var(--color-outflow)' : 'var(--color-inflow)'
                }}>
                  {formatMoney(Math.abs(drillDownCategoryData.total || 0), drillDownCategoryData.currency || preferredCurrencies[0] || 'AED')}
                </span>
              </div>
            </div>

            {/* Modal Scrollable Body */}
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px', marginRight: '-6px', paddingRight: '6px' }}>
              {(() => {
                const activeList = drillDownCategoryData.list
                  .map(tx => transactions.find(t => t.transaction_id === tx.transaction_id) || tx)
                  .filter(tx => transactions.some(t => t.transaction_id === tx.transaction_id));
                if (activeList.length === 0) {
                  return (
                    <p style={{ padding: '24px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic' }}>
                      No transactions found in this category.
                    </p>
                  );
                }
                return activeList.map(tx => {
                  const { amount: displayAmt, isSplitPortion } = getDisplayAmount(tx);
                  const displayLabelColor = drillDownCategoryData.type === 'outflow' ? 'var(--color-outflow)' : 'var(--color-inflow)';
                  return (
                    <div 
                      key={tx.transaction_id} 
                      className="tx-item" 
                      onClick={() => {
                        setSelectedTxDetail(tx);
                      }}
                      style={{ 
                        cursor: 'pointer', 
                        borderRadius: '10px', 
                        padding: '12px 14px', 
                        borderBottom: 'none',
                        margin: '2px 0',
                        background: 'rgba(255, 255, 255, 0.01)'
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0, paddingRight: '12px' }}>
                        <span style={{ fontSize: '13.5px', fontWeight: '600', display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {tx.description}
                        </span>
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          {tx.paid_by_account_id === 'VIRTUAL_DEBT' ? "Friend's Virtual Balance" : (accounts.find(a => a.account_id === tx.paid_by_account_id)?.name || 'Unknown Account')} • {formatDateTime(tx.date)}
                        </span>
                      </div>
                      <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <span style={{ fontSize: '13.5px', fontWeight: '700', color: displayLabelColor }}>
                          {formatMoney(Math.abs(displayAmt || 0), tx.currency)}
                        </span>
                        {isSplitPortion && <span style={{ fontSize: '8px', color: 'var(--text-muted)', display: 'block' }}>your share</span>}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Modal Actions */}
            <div style={{ display: 'flex', gap: '10px', borderTop: '1px solid var(--border-glass)', paddingTop: '14px' }}>
              <button
                type="button"
                onClick={() => setDrillDownCategoryData(null)}
                className="btn-secondary"
                style={{ flex: 1, padding: '12px', borderRadius: '12px', fontSize: '13px', fontWeight: '600' }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- CREDIT CARD MANUAL BALANCE ADJUSTMENT MODAL --- */}
      {adjustCcBalanceOpen && activeAccount && (
        <div className="modal-backdrop modal-backdrop-blur">
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box' }}>
            <h3 style={{ fontSize: '16px', margin: 0, color: 'var(--accent-cyan)' }}>Adjust CC Balance</h3>
            
            <p style={{ fontSize: '12px', margin: 0, color: 'var(--text-muted)', lineHeight: '1.4' }}>
              Manually change the current owed balance for <strong>{activeAccount.name}</strong>. This changes the balance directly without adding any transaction records.
            </p>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const newAmt = parseFloat(newCcBalanceValue);
              if (isNaN(newAmt)) {
                return showToast('Please enter a valid balance amount', 'error');
              }
              try {
                await apiClient.request(`/accounts/${activeAccount.account_id}`, 'PUT', {
                  name: activeAccount.name,
                  current_balance: -newAmt,
                  include_in_total: activeAccount.include_in_total,
                  credit_limit: activeAccount.credit_limit,
                  statement_day: activeAccount.statement_day,
                  due_day: activeAccount.due_day,
                  monthly_spend_target: activeAccount.monthly_spend_target,
                  monthly_spend_target_type: activeAccount.monthly_spend_target_type
                });
                
                showToast('Balance updated successfully!', 'success');
                setAdjustCcBalanceOpen(false);
                setNewCcBalanceValue('');
                refreshData();
                setActiveAccount(prev => prev ? { ...prev, current_balance: -newAmt } : null);
              } catch (err) {
                showToast(err.message, 'error');
              }
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '11px', display: 'block', color: 'var(--text-muted)' }}>New Owed Balance ({activeAccount.currency})</label>
                <input
                  type="number"
                  step="any"
                  value={newCcBalanceValue}
                  onChange={e => setNewCcBalanceValue(e.target.value)}
                  required
                  style={{ width: '100%', padding: '10px', fontSize: '13px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border-glass)', borderRadius: '8px', color: 'var(--text-main)' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ flex: 1, padding: '10px', fontSize: '13px', background: 'var(--accent-cyan)', color: 'black', fontWeight: 'bold' }}
                >
                  Save Changes
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAdjustCcBalanceOpen(false);
                    setNewCcBalanceValue('');
                  }}
                  className="btn-secondary"
                  style={{ flex: 1, padding: '10px', fontSize: '13px' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- DELETE FRIEND CONFIRMATION MODAL --- */}
      {deleteFriendModalOpen && deletingFriend && (
        <div className="modal-backdrop modal-backdrop-blur">
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box' }}>
            <h3 style={{ fontSize: '16px', margin: 0, color: 'var(--color-outflow)' }}>Delete Friend</h3>
            
            <p style={{ fontSize: '13px', margin: 0, lineHeight: '1.4' }}>
              Are you sure you want to delete <strong>{deletingFriend.nickname || deletingFriend.display_name || deletingFriend.email}</strong>?
            </p>

            <form onSubmit={handleDeleteFriend} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.02)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-glass)' }}>
                <input
                  type="checkbox"
                  id="deleteAssociatedTransactionsCheck"
                  checked={deleteAssociatedTransactionsCheck}
                  onChange={e => setDeleteAssociatedTransactionsCheck(e.target.checked)}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
                <label htmlFor="deleteAssociatedTransactionsCheck" style={{ margin: 0, cursor: 'pointer', fontSize: '12px', color: 'var(--text-main)' }}>
                  Delete all associated transactions also
                </label>
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ flex: 1, padding: '10px', fontSize: '13px', background: 'var(--color-outflow)', color: 'white', fontWeight: 'bold' }}
                >
                  Confirm Delete
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteFriendModalOpen(false);
                    setDeletingFriend(null);
                    setDeleteAssociatedTransactionsCheck(false);
                  }}
                  className="btn-secondary"
                  style={{ flex: 1, padding: '10px', fontSize: '13px' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- EDIT NICKNAME MODAL --- */}
      {editNicknameModalOpen && nicknameEditingFriend && (
        <div className="modal-backdrop modal-backdrop-blur">
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', boxSizing: 'border-box' }}>
            <h3 style={{ fontSize: '16px', margin: 0, color: 'var(--accent-cyan)' }}>Edit Friend Nickname</h3>
            
            <p style={{ fontSize: '12px', margin: 0, color: 'var(--text-muted)' }}>
              Set a custom nickname for <strong>{nicknameEditingFriend.display_name}</strong> to make them easier to identify.
            </p>

            <form onSubmit={handleUpdateNickname} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '11px', display: 'block', color: 'var(--text-muted)' }}>Nickname</label>
                <input
                  type="text"
                  placeholder="e.g. Bro, Roommate, Mom"
                  value={friendNicknameInput}
                  onChange={e => setFriendNicknameInput(e.target.value)}
                  style={{ width: '100%', padding: '10px', fontSize: '13px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border-glass)', borderRadius: '8px', color: 'var(--text-main)' }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button
                  type="submit"
                  className="btn-primary"
                  style={{ flex: 1, padding: '10px', fontSize: '13px', background: 'var(--accent-cyan)', color: 'black', fontWeight: 'bold' }}
                >
                  Save Nickname
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditNicknameModalOpen(false);
                    setNicknameEditingFriend(null);
                    setFriendNicknameInput('');
                  }}
                  className="btn-secondary"
                  style={{ flex: 1, padding: '10px', fontSize: '13px' }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- FIRST TRANSACTION PUSH NOTIFICATION PROMPT MODAL --- */}
      {showPushPromptModal && (
        <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 10000 }}>
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '400px', textAlign: 'center', alignItems: 'center', boxSizing: 'border-box' }}>
            <div style={{
              background: 'rgba(0, 245, 212, 0.12)',
              border: '1px solid var(--accent-cyan)',
              borderRadius: '50%',
              width: '64px',
              height: '64px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: '4px'
            }}>
              <Bell size={32} color="var(--accent-cyan)" />
            </div>

            <div>
              <h3 style={{ fontSize: '18px', margin: '0 0 6px 0', color: 'var(--text-main)' }}>
                Enable Push Notifications
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0, lineHeight: '1.5' }}>
                Get instant alerts for shared bill splits, friend debt settlements, credit card reminders, and offline sync updates directly on your device.
              </p>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: '6px' }}>
              <button
                type="button"
                className="btn-primary"
                onClick={handleEnablePushFromPrompt}
                style={{ width: '100%', padding: '12px', fontSize: '13px', fontWeight: 'bold' }}
              >
                🔔 Enable Notifications
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleDismissPushPrompt}
                style={{ width: '100%', padding: '10px', fontSize: '12px' }}
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- CONTACT SCAN & FRIEND MATCH MODAL --- */}
      {contactScanModalOpen && (
        <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 10000 }}>
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '440px', maxHeight: '85vh', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '17px', margin: 0, color: 'var(--accent-cyan)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📱 Sync Contacts & Find Friends
              </h3>
              <button
                type="button"
                onClick={() => {
                  setContactScanModalOpen(false);
                  setContactScanResults([]);
                  setSelectedContactUserIds([]);
                }}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, lineHeight: '1.4' }}>
              Scan phonebook contacts to discover friends who are already registered on <strong>tezer</strong>!
            </p>

            {/* iPhone / iOS Notice */}
            <div style={{ background: 'rgba(0, 245, 212, 0.08)', border: '1px solid var(--accent-cyan)', padding: '10px 12px', borderRadius: '10px', fontSize: '11px', color: 'var(--text-main)', lineHeight: '1.4' }}>
              📱 Contact Sync not supported on iOS now, please manually paste email or mobile numbers below
            </div>

            {/* Scanning Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: 'rgba(255, 255, 255, 0.02)', padding: '12px', borderRadius: '10px', border: '1px solid var(--border-glass)' }}>
              {('contacts' in navigator && 'ContactsManager' in window) && (
                <button
                  type="button"
                  onClick={handleNativeContactSync}
                  disabled={isScanningContacts}
                  className="btn-primary"
                  style={{ width: '100%', padding: '10px 12px', fontSize: '13px', fontWeight: 'bold' }}
                >
                  📱 Choose Phonebook Contacts
                </button>
              )}

              <form onSubmit={handleScanTextOrFileContacts} style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Or paste phone numbers / emails (separated by line or comma):</label>
                <textarea
                  rows="2"
                  placeholder="+971501234567, friend@email.com..."
                  value={contactManualInput}
                  onChange={e => setContactManualInput(e.target.value)}
                  style={{ width: '100%', padding: '8px', fontSize: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border-glass)', borderRadius: '6px', color: 'var(--text-main)', resize: 'none' }}
                />
                <button type="submit" disabled={isScanningContacts} className="btn-secondary" style={{ padding: '6px 12px', fontSize: '11px', alignSelf: 'flex-end' }}>
                  {isScanningContacts ? <ThinkingOrbLoader popup state="searching" size={20} color="var(--accent-cyan)" text="Scanning..." /> : '🔍 Scan Input Contacts'}
                </button>
              </form>
            </div>

            {/* Results Section */}
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '260px', paddingRight: '2px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-main)' }}>
                  Matched tezer Users ({contactScanResults.length})
                </span>
                {contactScanResults.some(m => !m.is_already_friend) && (
                  <button
                    type="button"
                    onClick={() => {
                      const newIds = contactScanResults.filter(m => !m.is_already_friend).map(m => m.user_id);
                      if (selectedContactUserIds.length === newIds.length) {
                        setSelectedContactUserIds([]);
                      } else {
                        setSelectedContactUserIds(newIds);
                      }
                    }}
                    style={{ background: 'transparent', border: 'none', color: 'var(--accent-cyan)', fontSize: '11px', cursor: 'pointer', padding: 0 }}
                  >
                    {selectedContactUserIds.length === contactScanResults.filter(m => !m.is_already_friend).length ? 'Deselect All' : 'Select All New'}
                  </button>
                )}
              </div>

              {contactScanResults.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '12px' }}>
                  {isScanningContacts ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                      <ThinkingOrbLoader state="searching" size={32} color="var(--accent-cyan)" />
                      <span>Scanning your contacts...</span>
                    </div>
                  ) : 'No scan performed yet. Select contacts above to search for friends!'}
                </div>
              ) : (
                contactScanResults.map(user => {
                  const isChecked = selectedContactUserIds.includes(user.user_id);
                  return (
                    <div
                      key={user.user_id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        background: user.is_already_friend ? 'rgba(255, 255, 255, 0.02)' : (isChecked ? 'rgba(0, 245, 212, 0.08)' : 'rgba(255, 255, 255, 0.04)'),
                        border: isChecked ? '1px solid var(--accent-cyan)' : '1px solid var(--border-glass)'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {!user.is_already_friend && (
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              if (isChecked) {
                                setSelectedContactUserIds(selectedContactUserIds.filter(id => id !== user.user_id));
                              } else {
                                setSelectedContactUserIds([...selectedContactUserIds, user.user_id]);
                              }
                            }}
                            style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                          />
                        )}
                        <div style={{ position: 'relative', width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0, overflow: 'hidden', border: '1px solid var(--accent-cyan)', background: 'var(--bg-card)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--accent-cyan)', position: 'absolute' }}>
                            {(user.display_name || user.email || 'U')[0].toUpperCase()}
                          </div>
                          {user.avatar_url && (
                            <img src={apiClient.getAvatarUrl(user.avatar_url)} alt="Avatar" onError={(e) => { e.currentTarget.style.display = 'none'; }} style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'relative', zIndex: 1 }} />
                          )}
                        </div>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-main)' }}>
                            {user.display_name}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                            {user.email || user.phone_number} • Matched by {user.matched_by}
                          </div>
                        </div>
                      </div>

                      {user.is_already_friend ? (
                        <span style={{ fontSize: '10px', color: 'var(--color-inflow-emerald)', background: 'rgba(16, 185, 129, 0.1)', padding: '3px 8px', borderRadius: '12px', fontWeight: 'bold' }}>
                          Already Friends ✓
                        </span>
                      ) : (
                        <span style={{ fontSize: '11px', color: 'var(--accent-cyan)', fontWeight: 'bold' }}>
                          Ready to Add
                        </span>
                      )}
                    </div>
                  );
                })
              )}

              {/* Unregistered Contacts Section (Option to Add as Placeholder Friend) */}
              {unregisteredContacts.length > 0 && (
                <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--text-muted)' }}>
                    Not on tezer yet ({unregisteredContacts.length}) - Add as Placeholder Friend
                  </span>
                  {unregisteredContacts.map((contact, idx) => (
                    <div
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '10px 12px',
                        borderRadius: '8px',
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px dashed var(--border-glass)'
                      }}
                    >
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: 'bold', color: 'var(--text-main)' }}>
                          👤 {contact.name}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          {contact.phone_number || contact.email || 'No phone number'}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleAddUnregisteredAsPlaceholder(contact)}
                        className="btn-secondary"
                        style={{ padding: '4px 10px', fontSize: '11px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px', height: 'auto', width: 'auto' }}
                      >
                        ➕ Add Placeholder
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '8px', borderTop: '1px solid var(--border-glass)', paddingTop: '12px' }}>
              <button
                type="button"
                disabled={isBulkAddingFriends || selectedContactUserIds.length === 0}
                onClick={handleBulkAddSelectedFriends}
                className="btn-primary"
                style={{ flex: 1, padding: '12px', fontSize: '13px', fontWeight: 'bold' }}
              >
                {isBulkAddingFriends ? <ThinkingOrbLoader popup state="working" size={20} color="#000" text="Adding Friends..." /> : `➕ Add Selected Friends (${selectedContactUserIds.length})`}
              </button>
              <button
                type="button"
                onClick={() => setContactScanModalOpen(false)}
                className="btn-secondary"
                style={{ padding: '12px 16px', fontSize: '12px' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- INSTALL THE TEZER APP POPUP MODAL --- */}
      {showInstallAppPopup && !isStandalone && (
        <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 10000 }}>
          <div
            className="glass-panel modal-spring-enter modal-content-panel"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              maxWidth: '390px',
              width: '92%',
              textAlign: 'center',
              alignItems: 'center',
              boxSizing: 'border-box',
              position: 'relative'
            }}
          >
            <button
              type="button"
              onClick={handleDismissInstallPopup}
              style={{
                position: 'absolute',
                top: '14px',
                right: '14px',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-muted)',
                cursor: 'pointer',
                fontSize: '18px',
                padding: '4px',
                lineHeight: 1
              }}
              aria-label="Close"
            >
              ✕
            </button>

            <div
              style={{
                background: 'rgba(0, 245, 212, 0.12)',
                border: '1px solid var(--accent-cyan)',
                borderRadius: '50%',
                width: '60px',
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: '6px',
                boxShadow: '0 0 20px rgba(0, 245, 212, 0.25)'
              }}
            >
              <Download size={28} color="var(--accent-cyan)" />
            </div>

            <div>
              <h3 style={{ fontSize: '19px', margin: '0 0 8px 0', color: 'var(--text-main)', fontWeight: '700' }}>
                Install the Tezer App
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0, lineHeight: '1.5' }}>
                Install Tezer on your device for fast access, full-screen view, and an ultra-smooth experience.
              </p>
            </div>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                width: '100%',
                background: 'rgba(255, 255, 255, 0.03)',
                padding: '12px 14px',
                borderRadius: '12px',
                border: '1px solid var(--border-glass)',
                textAlign: 'left'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: 'var(--text-main)' }}>
                <span>⚡</span>
                <span><strong>Instant Launch</strong> — One tap from home screen or dock</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: 'var(--text-main)' }}>
                <span>📱</span>
                <span><strong>Full Screen</strong> — Distraction-free native experience</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: 'var(--text-main)' }}>
                <span>🔔</span>
                <span><strong>Instant Alerts</strong> — Stay updated on debts & reminders</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', marginTop: '4px' }}>
              <button
                type="button"
                className="btn-primary"
                onClick={handleInstallFromPopup}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <Download size={16} /> Install the Tezer App
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleDismissInstallPopup}
                style={{ width: '100%', padding: '10px', fontSize: '13px' }}
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- APP INSTALLATION INSTRUCTIONS MODAL --- */}
      {showIosInstallModal && (
        <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 10000 }}>
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '420px', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '17px', margin: 0, color: 'var(--accent-cyan)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📲 Install the Tezer App
              </h3>
              <button
                type="button"
                onClick={() => setShowIosInstallModal(false)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px' }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: '13px', color: 'var(--text-main)', margin: 0, lineHeight: '1.5' }}>
              Follow these simple steps to install <strong>tezer</strong> on your phone or computer for a fast, standalone app experience:
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', background: 'rgba(255, 255, 255, 0.03)', padding: '14px', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ background: 'var(--accent-cyan)', color: 'black', fontWeight: 'bold', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>1</div>
                <div style={{ fontSize: '13px', color: 'var(--text-main)' }}>
                  <strong>iOS Safari:</strong> Tap the <strong>Share</strong> button (square icon with up arrow ⎋ at bottom/top of Safari).<br/>
                  <strong>Chrome / Mobile / Desktop:</strong> Tap the browser menu button (<strong>⋮</strong> or <strong>⊕</strong> in address bar).
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ background: 'var(--accent-cyan)', color: 'black', fontWeight: 'bold', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>2</div>
                <div style={{ fontSize: '13px', color: 'var(--text-main)' }}>
                  Scroll down and select <strong>"Add to Home Screen"</strong> or <strong>"Install App"</strong>.
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <div style={{ background: 'var(--accent-cyan)', color: 'black', fontWeight: 'bold', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>3</div>
                <div style={{ fontSize: '13px', color: 'var(--text-main)' }}>
                  Launch <strong>tezer</strong> directly from your home screen or app launcher!
                </div>
              </div>
            </div>

            <button
              type="button"
              className="btn-primary"
              onClick={() => setShowIosInstallModal(false)}
              style={{ padding: '12px', fontSize: '13px', width: '100%', marginTop: '4px' }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
      {/* --- PROFILE PICTURE CROPPER & ADJUSTER MODAL --- */}
      {avatarCropModalOpen && rawCropImageSrc && (
        <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 10000 }}>
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ maxWidth: '420px', width: '90%', display: 'flex', flexDirection: 'column', gap: '14px', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ fontSize: '16px', margin: 0, color: 'var(--accent-cyan)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                🖼️ Adjust Profile Picture
              </h3>
              <button
                type="button"
                onClick={() => { setAvatarCropModalOpen(false); setRawCropImageSrc(null); }}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '18px', fontWeight: 'bold' }}
              >
                ✕
              </button>
            </div>

            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
              Drag to position, use the slider to zoom, or rotate to fit your profile avatar.
            </p>

            {/* Interactive Crop Viewport with Circular Mask */}
            <div
              style={{
                position: 'relative',
                width: '240px',
                height: '240px',
                margin: '0 auto',
                borderRadius: '50%',
                overflow: 'hidden',
                border: '3px solid var(--accent-cyan)',
                boxShadow: '0 0 20px rgba(0, 245, 212, 0.25)',
                cursor: isDraggingCrop ? 'grabbing' : 'grab',
                background: '#090d16',
                touchAction: 'none'
              }}
              onMouseDown={e => {
                setIsDraggingCrop(true);
                setDragStartPos({ x: e.clientX - cropOffsetX, y: e.clientY - cropOffsetY });
              }}
              onMouseMove={e => {
                if (!isDraggingCrop) return;
                setCropOffsetX(e.clientX - dragStartPos.x);
                setCropOffsetY(e.clientY - dragStartPos.y);
              }}
              onMouseUp={() => setIsDraggingCrop(false)}
              onMouseLeave={() => setIsDraggingCrop(false)}
              onTouchStart={e => {
                if (e.touches.length === 1) {
                  setIsDraggingCrop(true);
                  setDragStartPos({ x: e.touches[0].clientX - cropOffsetX, y: e.touches[0].clientY - cropOffsetY });
                }
              }}
              onTouchMove={e => {
                if (!isDraggingCrop || e.touches.length !== 1) return;
                setCropOffsetX(e.touches[0].clientX - dragStartPos.x);
                setCropOffsetY(e.touches[0].clientY - dragStartPos.y);
              }}
              onTouchEnd={() => setIsDraggingCrop(false)}
            >
              <img
                src={rawCropImageSrc}
                alt="Crop preview"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  maxWidth: 'none',
                  transform: `translate(-50%, -50%) translate(${cropOffsetX}px, ${cropOffsetY}px) scale(${cropZoom}) rotate(${cropRotation}deg)`,
                  transition: isDraggingCrop ? 'none' : 'transform 0.1s ease-out',
                  userSelect: 'none',
                  pointerEvents: 'none',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            </div>

            {/* Editor Controls: Zoom & Rotate */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: 'rgba(255, 255, 255, 0.03)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border-glass)' }}>
              {/* Zoom Control */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-main)', minWidth: '45px', fontWeight: 'bold' }}>Zoom</span>
                <button
                  type="button"
                  onClick={() => setCropZoom(prev => Math.max(0.5, prev - 0.1))}
                  className="btn-secondary"
                  style={{ padding: '4px 8px', fontSize: '12px', height: 'auto' }}
                >
                  -
                </button>
                <input
                  type="range"
                  min="0.5"
                  max="3"
                  step="0.05"
                  value={cropZoom}
                  onChange={e => setCropZoom(parseFloat(e.target.value))}
                  style={{ flex: 1, cursor: 'pointer' }}
                />
                <button
                  type="button"
                  onClick={() => setCropZoom(prev => Math.min(3, prev + 0.1))}
                  className="btn-secondary"
                  style={{ padding: '4px 8px', fontSize: '12px', height: 'auto' }}
                >
                  +
                </button>
              </div>

              {/* Action Buttons: Rotate & Reset */}
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                <button
                  type="button"
                  onClick={() => setCropRotation(prev => (prev + 90) % 360)}
                  className="btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '11px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                >
                  🔄 Rotate 90°
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCropZoom(1);
                    setCropOffsetX(0);
                    setCropOffsetY(0);
                    setCropRotation(0);
                  }}
                  className="btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '11px', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}
                >
                  ↩️ Reset
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
              <button
                type="button"
                onClick={() => { setAvatarCropModalOpen(false); setRawCropImageSrc(null); }}
                className="btn-secondary"
                style={{ flex: 1, padding: '10px', fontSize: '13px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isUploadingAvatar}
                onClick={handleApplyCrop}
                className="btn-primary"
                style={{ flex: 1, padding: '10px', fontSize: '13px', fontWeight: 'bold' }}
              >
                {isUploadingAvatar ? <ThinkingOrbLoader popup state="composing" size={20} color="#000" text="Saving..." /> : '✓ Apply Picture'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- MASTER CREDIT CARDS DIRECTORY / EXPLORE CARDS MODAL --- */}
      <ExploreCardsModal
        isOpen={isExploreCardsOpen}
        onClose={() => setIsExploreCardsOpen(false)}
        onOpenCommunity={(c) => {
          setIsExploreCardsOpen(false);
          setActiveCommunityCard(c);
        }}
        onOpenRewards={(c) => {
          setIsExploreCardsOpen(false);
          setActiveCatalogRewardsCard(c);
        }}
        onLinkCard={(c) => {
          setIsExploreCardsOpen(false);
          setCreateCardMasterCardId(c.card_id);
          setActiveModal('account_create');
          setTimeout(() => {
            const nameInput = document.querySelector('input[name="name"]');
            if (nameInput) nameInput.value = c.card_name;
            const typeSelect = document.getElementById('account-type-select');
            if (typeSelect) typeSelect.value = 'CREDIT_CARD';
            const ccDiv = document.getElementById('credit-card-inputs-create');
            if (ccDiv) ccDiv.style.display = 'block';
          }, 50);
        }}
      />

      {/* --- STANDALONE CATALOG CARD REWARDS MODAL --- */}
      {activeCatalogRewardsCard && (
        <div className="modal-backdrop modal-backdrop-blur" style={{ zIndex: 9999 }}>
          <div className="glass-panel modal-spring-enter modal-content-panel" style={{ maxWidth: '850px', width: '92%', maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-glass)', paddingBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🎁 {activeCatalogRewardsCard.card_name}
                </h3>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                  {activeCatalogRewardsCard.bank_name} • {activeCatalogRewardsCard.card_tier || 'Rewards & Benefits'}
                </span>
              </div>
              <button
                type="button"
                onClick={() => setActiveCatalogRewardsCard(null)}
                style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '20px', fontWeight: 'bold' }}
              >
                ✕
              </button>
            </div>

            <CardRewardsAccordion
              masterCardId={activeCatalogRewardsCard.card_id}
              cardName={activeCatalogRewardsCard.card_name}
              isCardholder={activeCatalogRewardsCard.is_cardholder || false}
              showToast={showToast}
            />
          </div>
        </div>
      )}

      {/* --- CONNECT CUSTOM CARD TO DIRECTORY MODAL --- */}
      {cardToConnect && (
        <ConnectCardModal
          isOpen={Boolean(cardToConnect)}
          account={cardToConnect.account}
          targetFeature={cardToConnect.targetFeature}
          onClose={() => setCardToConnect(null)}
          onConnected={handleCardConnected}
          showToast={showToast}
        />
      )}

      {/* --- DUPLICATE / SIMILAR TRANSACTION ALERT MODAL --- */}
      {duplicateAlertModal.isOpen && duplicateAlertModal.match && (
        <div className="modal-backdrop modal-backdrop-blur modal-backdrop-alert" style={{ zIndex: 25000 }}>
          <div
            className="glass-panel modal-spring-enter modal-content-panel"
            style={{
              maxWidth: '420px',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              textAlign: 'center',
              border: '1px solid rgba(255, 71, 87, 0.4)',
              boxShadow: '0 16px 40px rgba(0, 0, 0, 0.7)',
              boxSizing: 'border-box'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  background: 'rgba(255, 71, 87, 0.15)',
                  border: '1px solid rgba(255, 71, 87, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#ff4757'
                }}
              >
                <AlertTriangle size={28} />
              </div>
            </div>

            <h3 style={{ fontSize: '18px', margin: 0, fontWeight: '700', color: 'var(--text-main)' }}>
              Similar Transaction Detected
            </h3>

            <div
              style={{
                background: 'rgba(255, 255, 255, 0.04)',
                border: '1px solid var(--border-glass)',
                borderRadius: '12px',
                padding: '14px 16px',
                fontSize: '13px',
                lineHeight: '1.5',
                color: 'var(--text-muted)'
              }}
            >
              There is one similar {duplicateAlertModal.match.typeLabel} recorded{" "}
              <strong style={{ color: 'var(--text-main)', display: 'block', marginTop: '6px', fontSize: '14px' }}>
                "{duplicateAlertModal.match.amount} {duplicateAlertModal.match.currency}, {duplicateAlertModal.match.relativeDate}, {duplicateAlertModal.match.description}"
              </strong>
            </div>

            <p style={{ fontSize: '15px', margin: 0, fontWeight: '600', color: 'var(--text-main)' }}>
              Do you want to continue ?
            </p>

            <div style={{ display: 'flex', gap: '12px', marginTop: '6px' }}>
              <button
                type="button"
                onClick={() => {
                  const proceedFn = duplicateAlertModal.onConfirm;
                  setDuplicateAlertModal({ isOpen: false, match: null, onConfirm: null });
                  if (proceedFn) proceedFn();
                }}
                style={{
                  flex: 1,
                  padding: '12px 18px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  background: '#ff4757', // RED
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(255, 71, 87, 0.4)',
                  transition: 'transform 0.15s ease, filter 0.15s ease'
                }}
                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
                onMouseLeave={e => e.currentTarget.style.filter = 'none'}
              >
                Yes
              </button>

              <button
                type="button"
                onClick={() => {
                  setDuplicateAlertModal({ isOpen: false, match: null, onConfirm: null });
                  setIsSubmittingTx(false);
                }}
                style={{
                  flex: 1,
                  padding: '12px 18px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '700',
                  background: '#2ed573', // GREEN
                  color: '#0b141a',
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(46, 213, 115, 0.4)',
                  transition: 'transform 0.15s ease, filter 0.15s ease'
                }}
                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
                onMouseLeave={e => e.currentTarget.style.filter = 'none'}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      </div> {/* Close app-body */}
    </div>
  );
}
